import React, { useState, useRef, useEffect, useMemo, useCallback, Component, ReactNode, ErrorInfo, Suspense, lazy } from 'react';
import { Camera, Database, Sunrise, Sun, Moon, Upload, Loader2, Utensils, Info, ChevronRight, PieChart as PieChartIcon, History as HistoryIcon, ArrowLeft, Trash2, Calendar, Home as HomeIcon, Settings, Target, CheckCircle2, XCircle, TrendingUp, Search, Flame, Dumbbell, Wheat, Candy, CircleDot, Droplets, Beef, Apple, Ban, MessageSquare, Mic, MicOff, Carrot, Milk, Coffee, Cookie, Plus, PlusCircle, Zap, HelpCircle, X, ChevronLeft, ChevronDown, ChevronUp, Leaf, Waves, Menu, Star, Heart, LayoutTemplate, User, Users, Bot, Barcode, ClipboardList, Image, Monitor, Crop, Bug, Sparkles, RotateCcw, RefreshCw, RotateCw, Croissant, Tag, Bell, Clock, Scale, Activity, Sliders, Volume2, Palette, CornerDownLeft, Download, Brush, Copy, AlertCircle, AlertTriangle, Eraser, UserCircle, Share2, Mail, ExternalLink, ArrowLeftRight, FileText, FileSpreadsheet, FileJson, WifiOff, PlayCircle, ShoppingCart, Save, Smartphone, Shield, ShieldAlert, Trophy, Crown, Medal, MoreVertical, Lock, Award, BookOpen, Edit, Check, Gift, Eye, EyeOff, LogOut, Cpu, Scan, Truck } from 'lucide-react';
import { motion, AnimatePresence, useSpring, useMotionValue } from 'motion/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, AreaChart, Area, ReferenceLine } from 'recharts';
import { Toaster, toast } from 'sonner';
import canvasConfetti from 'canvas-confetti';
const confetti = canvasConfetti.create(null, { useWorker: false, resize: true });
import { analyzeFoodImage, analyzeMealText, getFoodExplanation, generateMealImage, processAIChat, type ChatResponse, verifyBugReportImage, generateMealSuggestions, postProcessNutrition, enrichMealMicronutrients, buildDetailedDynamicInsight, isGenericInsight, determineTimeOfDay, cleanBarcodeMealName, getIngredientsForMeal, checkSafeChoiceForProduct, getAiSuggestedPortions, parseNaturalLanguagePortion, getRecipeUrl, generateWeeklyProgressVideo, generateAutonomousMealFallback, checkIfMessageMentionsFood, checkIfUserMentionedEating } from './services/nutritionService';
import { MealAnalysis, HistoryEntry, UserGoals, MealType, WaterLog, WeightLog, CombinedEntry, TotalNutrition, FoodItem, Language, Vibe, DeclineStyle, ScannedProduct, SafeChoiceProfile, GroceryItem, CustomDrinkOption, BiomarkerLog, NutriAgentChatOrder, NutriAgentChatOrderItem } from './types';
import { MASTER_DRINK_BRANDS, getDrinksForResidence, getDefaultSelectedDrinkIds, BrandLogoCircle, calculateWaterLogNutrients, type DrinkBrandItem } from './data/drinkBrandsDatabase';
import { cn, formatDate, formatDateTime, safeClone, safeStringify, ensureTimestampMillis, getEveningWrapUpSessionDate, getPreviousSessionDate, getEveningWrapUpStatus, safeJsonParse, isValidMealId, cleanInsightQuotes, type EveningWrapUpState } from './lib/utils';
import MealCropper from './components/MealCropper';
import { ChartCard } from './components/ChartCard';
import { WeightLogModal } from './components/WeightLogModal';
import { MealNotesEditor } from './components/MealNotesEditor';
import { SafeChoiceInlineConfig } from './components/SafeChoiceConfig';
import { NutriAgentSettingsConfig } from './components/NutriAgentSettingsConfig';
import { NutriAgentConfigModal, type NutriAgentLaunchConfig } from './components/NutriAgentConfigModal';
import { NutriAgentProgress } from './components/NutriAgentProgress';
import { NutriAgentApprovalModal } from './components/NutriAgentApprovalModal';
import { StorePushNotificationBanner } from './components/StorePushNotificationBanner';
import { HealthSyncConfig } from "./components/HealthSyncConfig";
import { GoogleFitWidget } from "./components/GoogleFitWidget";
import { AchievementsPanel } from "./components/AchievementsPanel";
import { EcosystemDashboard } from "./components/EcosystemDashboard";
import { DEFAULT_BIOMARKER_LOGS } from "./services/longevityEngine";
import { queueHealthSync } from "./services/healthSync";
import { FastingTimer, type FastingState } from './components/FastingTimer';
import { resizeImage } from './lib/imageUtils';
import { RiskComponentsList } from './components/RiskComponentsList';
import { fetchWrapper, getSession, saveSession, clearSession, safeJsonResponse, asyncGetAccessToken, asyncGetGuestSession, forceRecreateSessionWithFirebase, createStripeCheckoutSession, createStripePortalSession, getStripeSubscription } from './lib/api';
import { sounds } from './lib/sound';
import ReactMarkdown from 'react-markdown';
import { GamificationNotifications } from './components/GamificationNotifications';
import { evaluateGamificationState } from './services/gamificationEngine';
import { getUserBioBoxes, getGamificationProfile, DEFAULT_GAMIFICATION_PROFILE } from './services/bioBoxService';
import { BioBoxInventoryItem, UserConsumableItem } from './types';
import { BioBoxUnboxingModal } from './components/BioBoxUnboxingModal';
import { sfx } from './services/audioSynthesis';
import { triggerHaptic } from './lib/haptic';
import { AiConcierge } from './components/AiConcierge';
import { localDb } from './services/localDbService';
import { CollapsibleSection } from './components/CollapsibleSection';
import { DailyWrapUpCard } from './components/DailyWrapUpCard';
import { computeProgressChartsData, clearProgressChartsCache } from './lib/chartCache';
import { isEligibleForRestoration, restoreUserDataIfEligible, applyRestoredUserData, forceRestoreUserData } from './services/dataRestorationService';

// Lazy-loaded heavy and on-demand modal/admin components for high performance
const EDecoderModal = lazy(() => import('./components/EDecoderModal').then(m => ({ default: m.EDecoderModal })));
const SafeChoiceDetailModal = lazy(() => import('./components/SafeChoiceDetailModal').then(m => ({ default: m.SafeChoiceDetailModal })));
const SubscriptionBillingModal = lazy(() => import('./components/SubscriptionBillingModal').then(m => ({ default: m.SubscriptionBillingModal })));
const RecipeModal = lazy(() => import('./components/RecipeModal'));
const AdminTesterPanel = lazy(() => import('./components/AdminTesterPanel').then(m => ({ default: m.AdminTesterPanel })));
const AdminAnalyticsPanel = lazy(() => import('./components/AdminAnalyticsPanel').then(m => ({ default: m.AdminAnalyticsPanel })));
const GeminiModelDiagnosticPanel = lazy(() => import('./components/GeminiModelDiagnosticPanel').then(m => ({ default: m.GeminiModelDiagnosticPanel })));
const AdminGeminiChat = lazy(() => import('./components/AdminGeminiChat').then(m => ({ default: m.AdminGeminiChat })));
import { spoilerManager } from './lib/spoilerManager';
import { OccasionalScreenGlitch } from './components/OccasionalScreenGlitch';
import { SkinTransitionOverlay } from './components/SkinTransitionOverlay';

const AnimatedSuccessIcon = () => (
  <motion.div
    initial={{ scale: 0.3, rotate: -180 }}
    animate={{ scale: 1, rotate: 0 }}
    transition={{
      type: "spring",
      stiffness: 400,
      damping: 15,
      duration: 0.65
    }}
  >
    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
  </motion.div>
);

const isKimAdmin = (emailToCheck: string | null | undefined): boolean => {
  if (!emailToCheck) return false;
  const l = emailToCheck.toLowerCase();
  return l === 'kim41k@gmail.com' || l === 'mr.gray';
};

const PortionPlateGraphic = ({ ratio, active }: { ratio: number; active: boolean }) => {
  const activeColor = active ? '#10B981' : 'currentColor';
  const fillColor = active ? '#10B981' : '#a1a1aa';
  
  if (ratio <= 0.25) {
    return (
      <svg className="w-6 h-6 transition-transform duration-300 group-hover:scale-110 shrink-0" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="13" stroke={activeColor} strokeWidth="2.5" opacity={active ? 1 : 0.35} />
        <path d="M 16 16 L 16 3 A 13 13 0 0 1 29 16 Z" fill={fillColor} opacity={active ? 1 : 0.5} />
      </svg>
    );
  }
  if (ratio <= 0.5) {
    return (
      <svg className="w-6 h-6 transition-transform duration-300 group-hover:scale-110 shrink-0" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="13" stroke={activeColor} strokeWidth="2.5" opacity={active ? 1 : 0.35} />
        <path d="M 16 16 L 16 3 A 13 13 0 0 1 16 29 Z" fill={fillColor} opacity={active ? 1 : 0.5} />
      </svg>
    );
  }
  if (ratio <= 0.75) {
    return (
      <svg className="w-6 h-6 transition-transform duration-300 group-hover:scale-110 shrink-0" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="13" stroke={activeColor} strokeWidth="2.5" opacity={active ? 1 : 0.35} />
        <path d="M 16 16 L 16 3 A 13 13 0 1 1 3 16 Z" fill={fillColor} opacity={active ? 1 : 0.5} />
      </svg>
    );
  }
  if (ratio <= 1.0) {
    return (
      <svg className="w-6 h-6 transition-transform duration-300 group-hover:scale-110 shrink-0" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="13" stroke={activeColor} strokeWidth="2.5" />
        <circle cx="16" cy="16" r="9.5" fill={fillColor} opacity={active ? 1 : 0.5} />
      </svg>
    );
  }
  if (ratio <= 1.5) {
    return (
      <div className="relative w-6 h-6 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shrink-0">
        <svg className="w-6 h-6" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="13" stroke={activeColor} strokeWidth="2.5" />
          <circle cx="16" cy="16" r="9.5" fill={fillColor} opacity={active ? 1 : 0.5} />
        </svg>
        <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[8px] font-black rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-xs">
          +
        </span>
      </div>
    );
  }
  return (
    <div className="relative w-6 h-6 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shrink-0">
      <svg className="w-6 h-6" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="13" stroke={activeColor} strokeWidth="2.5" />
        <circle cx="16" cy="16" r="9.5" fill={fillColor} opacity={active ? 1 : 0.5} />
      </svg>
      <span className="absolute -top-1 -right-1.5 bg-amber-500 text-white text-[7px] font-black rounded-full px-1 py-0.2 shadow-xs">
        2x
      </span>
    </div>
  );
};

const PortionPickerSpoiler = ({
  food,
  originalFood,
  idx,
  lang,
  theme,
  updatePendingFood,
  onUpdateFood,
  t
}: {
  food: any;
  originalFood: any;
  idx: number;
  lang: string;
  theme: string;
  updatePendingFood?: (index: number, fieldOrUpdates: any, value?: any) => void;
  onUpdateFood?: (index: number, updates: any) => void;
  t: any;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const origAmt = parseFloat(String(originalFood?.amount || food?.amount)) || 100;
  const currAmt = parseFloat(String(food?.amount)) || 0;
  const ratio = origAmt > 0 ? currAmt / origAmt : 1;
  const currentCals = originalFood?.calories_kcal !== undefined ? Math.round((originalFood.calories_kcal || 0) * ratio) : Math.round(food?.calories_kcal || 0);
  const currentProtein = originalFood?.protein_g !== undefined ? ((originalFood.protein_g || 0) * ratio).toFixed(1) : Number(food?.protein_g || 0).toFixed(1);

  const portionLabel = ratio <= 0.25 ? (lang.startsWith('ua') ? "1/4 тарілки" : "1/4 Plate") :
                       ratio <= 0.5 ? (lang.startsWith('ua') ? "1/2 тарілки" : "1/2 Plate") :
                       ratio <= 0.75 ? (lang.startsWith('ua') ? "3/4 тарілки" : "3/4 Plate") :
                       ratio <= 1.0 ? (lang.startsWith('ua') ? "Ціла тарілка (1x)" : "Full Plate (1x)") :
                       ratio <= 1.5 ? (lang.startsWith('ua') ? "Півтори порції (1.5x)" : "1.5x Portion") :
                       (lang.startsWith('ua') ? "Подвійна порція (2x)" : "2x Portion");

  return (
    <div className="mt-2 border border-zinc-200/80 dark:border-zinc-800/80 rounded-2xl overflow-hidden transition-all">
      <button
        type="button"
        onClick={() => {
          triggerHaptic('SUCCESS');
          setIsOpen(!isOpen);
        }}
        className={cn(
          "w-full px-3.5 py-2.5 flex items-center justify-between text-left transition-colors cursor-pointer select-none",
          theme === 'light' 
            ? "bg-zinc-50 hover:bg-zinc-100/80 text-zinc-800" 
            : "bg-zinc-900/60 hover:bg-zinc-800/60 text-zinc-200"
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500">
            {lang.startsWith('ua') ? "Частка на тарілці" : "Plate Coverage"}
          </span>
          <span className="text-xs font-bold text-emerald-500 truncate">
            {portionLabel}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400">
            {currentCals} kcal
          </span>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-zinc-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          )}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden px-3.5 pb-3.5 pt-2.5 border-t border-zinc-200/60 dark:border-zinc-800/60 space-y-3"
          >
            <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-zinc-500">
              <span>{lang.startsWith('ua') ? "Частка на тарілці" : "Plate Coverage"}</span>
              <span className="text-emerald-500">{portionLabel}</span>
            </div>
            {/* Graphical portion size cards */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 py-1">
              {[
                { r: 0.25, label: '1/4', descEn: 'Snack', descUa: 'Перекус' },
                { r: 0.5, label: '1/2', descEn: 'Half', descUa: 'Пів тарілки' },
                { r: 0.75, label: '3/4', descEn: '3/4', descUa: '3/4 Тарілки' },
                { r: 1.0, label: '1x', descEn: 'Full', descUa: 'Ціла' },
                { r: 1.5, label: '1.5x', descEn: '1.5x Large', descUa: '1.5x Ситна' },
                { r: 2.0, label: '2x', descEn: '2x Double', descUa: '2x Подвійна' },
              ].map(({ r, label, descEn, descUa }) => {
                const isActive = Math.abs(ratio - r) < 0.05;
                const subLabel = lang.startsWith('ua') ? descUa : descEn;
                return (
                  <motion.button
                    key={r}
                    type="button"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      triggerHaptic('SUCCESS');
                      sounds.playPop();
                      const newAmount = Math.round(origAmt * r);
                      const portionStr = `${newAmount}${food.unit || originalFood?.unit || 'g'}`;
                      if (onUpdateFood) {
                        onUpdateFood(idx, { amount: newAmount, portion: portionStr });
                      } else if (updatePendingFood) {
                        updatePendingFood(idx, { amount: newAmount, portion: portionStr });
                      }
                    }}
                    className={cn(
                      "relative p-2 rounded-2xl border transition-all flex flex-col items-center justify-center gap-1.5 text-center group cursor-pointer overflow-hidden",
                      isActive
                        ? (theme === 'light'
                            ? "bg-emerald-500/10 border-emerald-500 text-emerald-950 shadow-md shadow-emerald-500/10 ring-2 ring-emerald-500/20"
                            : "bg-emerald-500/20 border-emerald-500 text-white shadow-lg shadow-emerald-500/20 ring-2 ring-emerald-500/30")
                        : (theme === 'light'
                            ? "bg-zinc-50/80 border-zinc-200/80 text-zinc-700 hover:bg-zinc-100 hover:border-zinc-300"
                            : "bg-zinc-900/60 border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-700")
                    )}
                  >
                    {isActive && (
                      <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-xs" />
                    )}
                    <PortionPlateGraphic ratio={r} active={isActive} />
                    <div className="flex flex-col items-center leading-none">
                      <span className={cn(
                        "text-xs font-black tracking-tight",
                        isActive ? "text-emerald-500" : (theme === 'light' ? "text-zinc-800" : "text-zinc-200")
                      )}>
                        {label}
                      </span>
                      <span className={cn(
                        "text-[8px] font-extrabold uppercase tracking-wider mt-0.5 truncate max-w-full px-0.5",
                        isActive ? "text-emerald-600 dark:text-emerald-400" : "text-zinc-400 dark:text-zinc-500"
                      )}>
                        {subLabel}
                      </span>
                    </div>
                  </motion.button>
                );
              })}
            </div>
            <div className="flex justify-between items-center pt-1 text-[11px] font-bold text-zinc-400">
              <span className="text-emerald-600 dark:text-emerald-400">{currentCals} kcal</span>
              <span>{currentProtein}g {lang.startsWith('ua') ? "білка" : "protein"}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const WeightUnitSpoiler = ({
  food,
  idx,
  lang,
  theme,
  updatePendingFood,
  onUpdateFood,
  currentCals,
  currentProtein,
}: {
  food: any;
  idx: number;
  lang: string;
  theme: string;
  updatePendingFood?: (index: number, fieldOrUpdates: any, value?: any) => void;
  onUpdateFood?: (index: number, updates: any) => void;
  currentCals: number;
  currentProtein: number | string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mt-2 border border-zinc-200/80 dark:border-zinc-800/80 rounded-2xl overflow-hidden transition-all">
      <button
        type="button"
        onClick={() => {
          triggerHaptic('SUCCESS');
          setIsOpen(!isOpen);
        }}
        className={cn(
          "w-full px-3.5 py-2.5 flex items-center justify-between text-left transition-colors cursor-pointer select-none",
          theme === 'light'
            ? "bg-zinc-50 hover:bg-zinc-100/80 text-zinc-800"
            : "bg-zinc-900/60 hover:bg-zinc-800/60 text-zinc-200"
        )}
      >
        <div className="flex items-center gap-2">
          <Scale className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
          <span className={cn(
            "text-[10px] font-black uppercase tracking-[0.15em]",
            theme === 'light' ? "text-zinc-700" : "text-zinc-300"
          )}>
            {lang.startsWith('ua') ? "Вага та одиниця" : "Item Weight & Unit"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] font-extrabold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-md border border-emerald-500/20">
            {food.amount || 100}{food.unit || 'g'} • {currentCals} kcal
          </span>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-zinc-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          )}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden px-3.5 pb-3.5 pt-2.5 border-t border-zinc-200/60 dark:border-zinc-800/60 space-y-2.5"
          >
            <div className="flex items-center justify-between text-[10px] text-zinc-400">
              <span>{lang.startsWith('ua') ? 'Введіть вагу за порцію:' : 'Enter weight per serving:'}</span>
              <span className="font-bold text-emerald-500">{currentProtein}g {lang.startsWith('ua') ? "білка" : "protein"}</span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {/* Number Input with +/- step buttons */}
              <div className="flex items-center gap-1 flex-1">
                <button
                  type="button"
                  onClick={() => {
                    const current = parseFloat(String(food.amount)) || 100;
                    const updated = Math.max(1, current - 25);
                    const portionStr = `${updated}${food.unit || 'g'}`;
                    if (onUpdateFood) {
                      onUpdateFood(idx, { amount: updated, portion: portionStr });
                    } else if (updatePendingFood) {
                      updatePendingFood(idx, { amount: updated, portion: portionStr });
                    }
                  }}
                  className={cn(
                    "px-2.5 py-2 rounded-xl border text-xs font-black transition-colors shrink-0 cursor-pointer",
                    theme === 'light' 
                      ? "bg-zinc-100 hover:bg-zinc-200 border-zinc-200 text-zinc-700" 
                      : "bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-zinc-200"
                  )}
                >
                  -25
                </button>

                <div className="relative flex-1 min-w-[90px]">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={food.amount !== undefined && food.amount !== null ? food.amount : ''}
                    onChange={(e) => {
                      const valStr = e.target.value;
                      const num = valStr === '' ? '' : parseFloat(valStr);
                      const portionStr = num !== '' ? `${num}${food.unit || 'g'}` : '';
                      if (onUpdateFood) {
                        onUpdateFood(idx, { amount: num, portion: portionStr });
                      } else if (updatePendingFood) {
                        updatePendingFood(idx, { amount: num, portion: portionStr });
                      }
                    }}
                    className={cn(
                      "w-full border rounded-xl py-2 px-3 text-xs font-bold focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all text-center",
                      theme === 'light' 
                        ? "bg-zinc-50 border-zinc-200 text-zinc-900" 
                        : "bg-zinc-900 border-zinc-800 text-zinc-100"
                    )}
                    placeholder="100"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const current = parseFloat(String(food.amount)) || 100;
                    const updated = current + 25;
                    const portionStr = `${updated}${food.unit || 'g'}`;
                    if (onUpdateFood) {
                      onUpdateFood(idx, { amount: updated, portion: portionStr });
                    } else if (updatePendingFood) {
                      updatePendingFood(idx, { amount: updated, portion: portionStr });
                    }
                  }}
                  className={cn(
                    "px-2.5 py-2 rounded-xl border text-xs font-black transition-colors shrink-0 cursor-pointer",
                    theme === 'light' 
                      ? "bg-zinc-100 hover:bg-zinc-200 border-zinc-200 text-zinc-700" 
                      : "bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-zinc-200"
                  )}
                >
                  +25
                </button>
              </div>

              {/* Unit Selector Quick Chips */}
              <div className="flex items-center gap-1 overflow-x-auto py-0.5 no-scrollbar shrink-0">
                {['g', 'ml', 'pcs', 'oz', 'serving'].map((u) => {
                  const currentUnit = String(food.unit || 'g').toLowerCase().trim();
                  const isG = u === 'g' && ['g', 'gr', 'grams', 'г', 'гр'].includes(currentUnit);
                  const isPcs = u === 'pcs' && ['pcs', 'pc', 'piece', 'pieces', 'шт', 'шт.'].includes(currentUnit);
                  const isMl = u === 'ml' && ['ml', 'мл'].includes(currentUnit);
                  const isOz = u === 'oz' && ['oz', 'оз'].includes(currentUnit);
                  const isServing = u === 'serving' && ['serving', 'servings', 'порція', 'порция', 'порц'].includes(currentUnit);
                  const isActive = currentUnit === u || isG || isPcs || isMl || isOz || isServing;

                  return (
                    <button
                      key={u}
                      type="button"
                      onClick={() => {
                        triggerHaptic('SUCCESS');
                        sounds.playPop();
                        const portionStr = `${food.amount || 100}${u}`;
                        if (onUpdateFood) {
                          onUpdateFood(idx, { unit: u, portion: portionStr });
                        } else if (updatePendingFood) {
                          updatePendingFood(idx, { unit: u, portion: portionStr });
                        }
                      }}
                      className={cn(
                        "px-2.5 py-1.5 rounded-xl text-[10px] font-extrabold uppercase tracking-wider transition-all border shrink-0 cursor-pointer",
                        isActive
                          ? "bg-emerald-500 border-emerald-500 text-white font-black shadow-xs"
                          : (theme === 'light'
                              ? "bg-zinc-50 border-zinc-200 hover:bg-zinc-100 text-zinc-600"
                              : "bg-zinc-800/50 border-zinc-700 hover:bg-zinc-800 text-zinc-300")
                      )}
                    >
                      {u}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const NutrientDonutChart = ({
  data,
  centerLabel,
  centerSublabel,
  theme,
  lang,
}: {
  data: Array<{ name: string; value: number; color: string }>;
  centerLabel?: string;
  centerSublabel?: string;
  theme: string;
  lang: string;
}) => {
  const [activeItem, setActiveItem] = useState<{ name: string; value: number; color: string; pct?: number } | null>(null);

  const validData = data.filter(d => (d.value || 0) > 0);
  const total = validData.reduce((sum, item) => sum + (item.value || 0), 0);

  if (validData.length === 0 || total <= 0) {
    return (
      <div className="h-52 w-full flex flex-col items-center justify-center border-2 border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl p-6 text-center">
        <PieChartIcon className="w-10 h-10 text-zinc-400 dark:text-zinc-600 mb-2 opacity-50" />
        <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
          {lang.startsWith('ua') ? 'Немає даних нутрієнтів' : 'No Nutrient Data'}
        </p>
      </div>
    );
  }

  // SVG Donut Math
  const R = 60;
  const C = 2 * Math.PI * R;
  const gap = validData.length > 1 ? 4 : 0;

  let cumulativeOffset = 0;
  const slices = validData.map((item) => {
    const pct = item.value / total;
    const sliceLen = Math.max(0, pct * C - gap);
    const dashArray = `${sliceLen} ${C}`;
    const dashOffset = -cumulativeOffset;
    cumulativeOffset += pct * C;

    return {
      ...item,
      pct,
      dashArray,
      dashOffset,
    };
  });

  return (
    <div className="flex flex-col items-center w-full">
      <div className="relative w-56 h-56 flex items-center justify-center my-2">
        <svg viewBox="0 0 160 160" className="w-full h-full overflow-visible">
          <g transform="rotate(-90 80 80)">
            {/* Background Track */}
            <circle
              cx="80"
              cy="80"
              r={R}
              fill="transparent"
              stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'}
              strokeWidth="20"
            />
            {/* Colorful Nutrient Slices */}
            {slices.map((s, idx) => (
              <circle
                key={`donut-slice-${idx}`}
                cx="80"
                cy="80"
                r={R}
                fill="transparent"
                stroke={s.color}
                strokeWidth="20"
                strokeDasharray={s.dashArray}
                strokeDashoffset={s.dashOffset}
                strokeLinecap="round"
                className="transition-all duration-300 hover:opacity-85 cursor-pointer"
                onMouseEnter={() => setActiveItem(s)}
                onMouseLeave={() => setActiveItem(null)}
              />
            ))}
          </g>
        </svg>

        {/* Center Donut Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center p-2">
          {activeItem ? (
            <div className="flex flex-col items-center">
              <span className="text-[11px] font-black uppercase tracking-wider line-clamp-1" style={{ color: activeItem.color }}>
                {activeItem.name}
              </span>
              <span className={cn("text-xl font-black tracking-tight", theme === 'light' ? "text-zinc-900" : "text-white")}>
                {activeItem.value >= 10 ? Math.round(activeItem.value) : activeItem.value.toFixed(1)}g
              </span>
              <span className="text-[9px] font-extrabold text-zinc-400">
                {Math.round((activeItem.pct ?? 0) * 100)}%
              </span>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <span className={cn("text-2xl font-black tracking-tight", theme === 'light' ? "text-zinc-900" : "text-white")}>
                {centerLabel || `${total >= 10 ? Math.round(total) : total.toFixed(1)}g`}
              </span>
              <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mt-0.5">
                {centerSublabel || (lang.startsWith('ua') ? 'Всього макро' : 'Total Macros')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Legend Chips */}
      <div className="flex flex-wrap items-center justify-center gap-2 mt-2 px-1 w-full">
        {slices.map((item, idx) => {
          const pctStr = `${Math.round(item.pct * 100)}%`;
          const valStr = item.value >= 10 ? `${Math.round(item.value)}g` : `${item.value.toFixed(1)}g`;
          const isHovered = activeItem?.name === item.name;

          return (
            <button
              key={`legend-chip-${idx}`}
              type="button"
              onClick={() => setActiveItem(activeItem?.name === item.name ? null : item)}
              onMouseEnter={() => setActiveItem(item)}
              onMouseLeave={() => setActiveItem(null)}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1 rounded-xl border text-[11px] transition-all cursor-pointer select-none",
                isHovered
                  ? "ring-2 ring-emerald-500/50 scale-105"
                  : "",
                theme === 'light'
                  ? "bg-zinc-50/90 border-zinc-200/80 text-zinc-800 hover:bg-zinc-100"
                  : "bg-zinc-800/80 border-zinc-700/80 text-zinc-200 hover:bg-zinc-800"
              )}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
              <span className="font-extrabold uppercase text-[10px] tracking-wider">{item.name}</span>
              <span className="font-black text-[10px] text-zinc-500 dark:text-zinc-400">{valStr} ({pctStr})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LiquidSplash = ({ beverageType }: { beverageType: 'water' | 'coffee' | 'tea' }) => {
  const color = beverageType === 'coffee' ? '#78350f' : beverageType === 'tea' ? '#15803d' : '#3b82f6';
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
    >
      <div className="relative w-full h-full overflow-hidden">
        {/* Main Splash */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ 
              scale: 0,
              x: 0,
              y: 0,
              opacity: 1
            }}
            animate={{ 
              scale: [0, 1.5, 0.5, 0],
              x: Math.cos(i * 30 * (Math.PI / 180)) * 250,
              y: Math.sin(i * 30 * (Math.PI / 180)) * 250,
              opacity: [1, 0.8, 0]
            }}
            transition={{ 
              duration: 1.2,
              ease: "easeOut",
              delay: i * 0.02
            }}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 20 + Math.random() * 40,
              height: 20 + Math.random() * 40,
              borderRadius: '50%',
              backgroundColor: color,
              filter: 'blur(8px)',
              marginLeft: -20,
              marginTop: -20,
            }}
          />
        ))}

        {/* Ripples */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`ripple-${i}`}
            initial={{ scale: 0, opacity: 0.5 }}
            animate={{ scale: 4, opacity: 0 }}
            transition={{ 
              duration: 1.5,
              ease: "easeOut",
              delay: i * 0.3
            }}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 100,
              height: 100,
              border: `4px solid ${color}`,
              borderRadius: '50%',
              marginLeft: -50,
              marginTop: -50,
            }}
          />
        ))}

        {/* Droplets rising up */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`drop-${i}`}
            initial={{ 
              y: '100vh',
              x: `${Math.random() * 100}vw`,
              scale: 0.5 + Math.random(),
              opacity: 0
            }}
            animate={{ 
              y: '-20vh',
              opacity: [0, 0.6, 0]
            }}
            transition={{ 
              duration: 1.5 + Math.random(),
              ease: "linear",
              delay: Math.random() * 0.5
            }}
            style={{
              position: 'absolute',
              width: 4 + Math.random() * 6,
              height: 4 + Math.random() * 6,
              borderRadius: '50%',
              backgroundColor: color,
              filter: 'blur(2px)',
            }}
          />
        ))}

        {/* Rising waves from bottom */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: ['100%', '80%', '100%'] }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100%',
            backgroundColor: color,
            opacity: 0.1,
            zIndex: -1
          }}
        />
      </div>
    </motion.div>
  );
};

import { 
  onAuthStateChanged, 
  signInWithPopup, 
  signInWithRedirect,
  signInWithCredential,
  getRedirectResult,
  GoogleAuthProvider, 
  signOut, 
  deleteUser,
  signInAnonymously,
  EmailAuthProvider,
  linkWithCredential,
  linkWithPopup,
  User as FirebaseUser
} from 'firebase/auth';
import { auth, db, getCandidateFirestoreDatabases, setDocWithQueue, deleteDocWithQueue, batchDeleteDocsWithQueue, batchSetDocsWithQueue, batchWriteOperationsWithQueue, processOfflineQueue, getOfflineQueueLength, setIsQuotaExceeded, checkFirestoreConnection, isFirestoreQuotaExhaustedError } from './services/firebase';

import { doc, getDoc, setDoc, collection, query, getDocs, deleteDoc, getDocFromServer, addDoc, orderBy, limit, onSnapshot, startAfter, getCountFromServer, where } from 'firebase/firestore';
import firebaseConfig from '../firebase-applet-config.json';
import { StalkerProgressCharts } from './components/StalkerProgressCharts';
import { LiquidBreakdownChart } from './components/LiquidBreakdownChart';
import { MealTimeClustering } from './components/MealTimeClustering';
import { HowItWorksPage } from './components/HowItWorksPage';

type View = 'home' | 'analyze' | 'history' | 'details' | 'goals' | 'edit' | 'manual' | 'chat' | 'add_meal_options' | 'progress' | 'favorites' | 'gallery' | 'nutrient_breakdown' | 'water' | 'login' | 'onboarding' | 'skins' | 'admin_dashboard' | 'safe_choice' | 'grocery' | 'settings' | 'edecoder' | 'how_it_works';
type Skin = 'default' | 'cyberpunk' | 'anime' | 'stalker';

const getStalkerClassification = (name: string): string => {
  const n = (name || '').toLowerCase();
  if (n.includes('coffee') || n.includes('tea') || n.includes('energy')) return 'NON-RADIOACTIVE STIMULANT';
  if (n.includes('water')) return 'SAFE HYDRATION RATION';
  if (n.includes('meat') || n.includes('beef') || n.includes('pork') || n.includes('chicken') || n.includes('sausage')) return 'PSEUDO-DOG PROTEIN EXTRACT';
  if (n.includes('bread') || n.includes('rice') || n.includes('pasta') || n.includes('potato')) return 'STARCH SYNTHETIC RATION';
  if (n.includes('sugar') || n.includes('cake') || n.includes('sweet') || n.includes('candy')) return 'HIGH-GLUCOSE ANOMALY';
  if (n.includes('salad') || n.includes('veg')) return 'MUTATED FLORA RATION';
  return 'UNKNOWN BIOMATTER';
};

const hasDrinkKeywords = (str: string): boolean => {
  const s = str.toLowerCase();
  const drinkWords = [
    'water', 'juice', 'tea', 'coffee', 'soda', 'beer', 'wine', 'milkshake', 'beverage', 'milk', 'ayran', 'kefir', 'yogurt', 'compote', 'lemonade', 'smoothie', 'cacao', 'cola', 'pepsi', 'coke', 'sprite', 'fanta',
    'вода', 'сік', 'сок', 'чай', 'кава', 'кофе', 'кола', 'пепсі', 'пепси', 'спрайт', 'фанта', 'пиво', 'вино', 'коктейль', 'напій', 'напиток', 'молоко', 'айран', 'кефір', 'кефир', 'йогурт', 'компот', 'лимонад', 'смузі', 'смузи', 'какао', 'айраном', 'кавою', 'кофеєм', 'чаєм', 'соком', 'напоєм'
  ];
  return drinkWords.some(w => s.includes(w));
};

const hasFoodKeywords = (str: string): boolean => {
  const s = str.toLowerCase();
  const foodWords = [
    'pasta', 'chicken', 'meat', 'beef', 'pork', 'steak', 'rice', 'salad', 'bread', 'sandwich', 'egg', 'eggs', 'fish', 'salmon', 'pizza', 'burger', 'soup', 'cheese', 'potato', 'vegetable', 'fruit', 'apple', 'banana', 'croissant', 'cookie', 'cake', 'chocolate', 'nut', 'nuts', 'shrimp', 'sausage', 'grain', 'oat', 'oatmeal', 'porridge', 'pancake', 'waffle', 'toast', 'avocado', 'tomato', 'cucumber', 'garlic', 'onion', 'pepper',
    'паста', 'курка', 'куркою', 'м\'ясо', 'мясо', 'яловичина', 'свинина', 'стейк', 'рис', 'салат', 'хліб', 'хлеб', 'сендвіч', 'бутерброд', 'яйце', 'яйця', 'яйца', 'риба', 'рыба', 'лосось', 'піца', 'пицца', 'бургер', 'суп', 'сир', 'сыр', 'картопля', 'картошка', 'овочі', 'овощи', 'фрукти', 'фрукты', 'яблуко', 'яблоко', 'банан', 'круасан', 'печиво', 'печенье', 'торт', 'тістечко', 'шоколад', 'горіхи', 'орехи', 'креветки', 'ковбаса', 'колбаса', 'каша', 'вівсянка', 'овсянка', 'млинці', 'блины', 'вафлі', 'тост', 'авокадо', 'помідор', 'помидор', 'огірок', 'огурец', 'часник', 'чеснок', 'цибуля', 'лук', 'перець', 'перец'
  ];
  return foodWords.some(w => s.includes(w));
};

const isFoodItemLiquid = (f: any): boolean => {
  const cat = (f.category || '').toLowerCase();
  const name = (f.name || '').toLowerCase();
  if (cat === 'drink') return true;
  if (f.water_ml !== undefined && f.water_ml > 0) return true;
  return hasDrinkKeywords(name);
};

const isBothFoodAndDrink = (item: any): boolean => {
  if (!item) return false;
  if (item.type === 'water') return false;

  const mealName = (item.meal_name || '').toLowerCase();
  if (hasFoodKeywords(mealName) && hasDrinkKeywords(mealName)) {
    return true;
  }

  const foods = item.foods || [];
  if (foods.length === 0) return false;

  let hasFood = false;
  let hasDrink = false;

  for (const f of foods) {
    const name = (f.name || '').toLowerCase();
    if (hasFoodKeywords(name) && hasDrinkKeywords(name)) {
      return true;
    }
    if (isFoodItemLiquid(f)) {
      hasDrink = true;
    } else {
      hasFood = true;
    }
  }

  return hasFood && hasDrink;
};

const getIlluminationClass = (item: any): string => {
  if (!item) return '';
  if (item.type === 'water') return 'illumination-water';
  
  if (isBothFoodAndDrink(item)) return 'illumination-food';
  
  const foods = item.foods || [];
  if (foods.length === 0) return 'illumination-food';
  
  let hasDrink = false;
  for (const f of foods) {
    if (isFoodItemLiquid(f)) {
      hasDrink = true;
      break;
    }
  }
  
  if (hasDrink) return 'illumination-water';
  return 'illumination-food';
};

const ILLUMINATION_COLORS = [
  { id: 'original', name: 'Original', rgb: '16, 185, 129', color: '#10b981' },
  { id: 'purple', name: 'Magic Purple', rgb: '168, 85, 247', color: '#a855f7' },
  { id: 'emerald', name: 'Emerald Green', rgb: '16, 185, 129', color: '#10b981' },
  { id: 'amber', name: 'Dark Amber Coffee', rgb: '180, 83, 9', color: '#b45309' },
  { id: 'pink', name: 'Cyber Pink', rgb: '236, 72, 153', color: '#ec4899' },
  { id: 'glacier', name: 'Glacier White', rgb: '248, 250, 252', color: '#f8fafc' },
];

const playScanCompleteSound = () => {
  sounds.playFoodSuccess();
};

let isFlushingAuthLogs = false;

export const flushAuthLogQueue = async () => {
  if (isFlushingAuthLogs) return;
  if (typeof window === 'undefined' || !navigator.onLine) return;

  const rawQueue = localStorage.getItem('auth_error_logs_queue') || '[]';
  let queue: any[] = [];
  try {
    queue = JSON.parse(rawQueue);
  } catch (_) {
    queue = [];
  }

  if (!Array.isArray(queue) || queue.length === 0) return;

  isFlushingAuthLogs = true;
  const remainingQueue: any[] = [];

  for (const logItem of queue) {
    try {
      const authUser = auth?.currentUser;
      const payload: Record<string, any> = {
        method: logItem.method || 'unknown',
        errorCode: logItem.errorCode || 'unknown',
        errorMessage: logItem.errorMessage || 'unknown',
        timestamp: logItem.timestamp || new Date().toISOString(),
        userAgent: logItem.userAgent || (typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown'),
        userId: authUser?.uid || logItem.userId || 'unauthenticated',
        userEmail: authUser?.email || logItem.userEmail || '',
        url: typeof window !== 'undefined' ? window.location.href : '',
        queuedAt: logItem.queuedAt || logItem.timestamp || new Date().toISOString()
      };
      await addDoc(collection(db, 'auth_error_logs'), payload);
    } catch (err: any) {
      console.warn("[AUTH] Failed to flush queued auth log to Firestore:", err);
      const retryCount = (logItem.retryCount || 0) + 1;
      if (retryCount < 5 && !err?.message?.includes('RESOURCE_EXHAUSTED')) {
        remainingQueue.push({ ...logItem, retryCount });
      }
    }
  }

  try {
    localStorage.setItem('auth_error_logs_queue', safeStringify(remainingQueue));
  } catch (e) {
    console.warn("[AUTH] Failed to update auth log queue in storage:", e);
  } finally {
    isFlushingAuthLogs = false;
  }
};

export const addAuthLog = (method: string, errorCode: any, errorMessage: any) => {
  try {
    const timestamp = new Date().toISOString();
    const strErrorCode = typeof errorCode === 'string' ? errorCode : safeStringify(errorCode);
    const strErrorMessage = typeof errorMessage === 'string' ? errorMessage : safeStringify(errorMessage);
    const currentUser = auth?.currentUser;

    const logEntry = {
      id: `${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      method,
      errorCode: strErrorCode,
      errorMessage: strErrorMessage,
      timestamp,
      queuedAt: timestamp,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
      userId: currentUser?.uid || 'unauthenticated',
      userEmail: currentUser?.email || '',
      retryCount: 0
    };

    // 1. Always maintain local history for immediate in-app diagnostics
    const logsStr = localStorage.getItem('auth_logs') || '[]';
    let logs: any[] = [];
    try {
      logs = JSON.parse(logsStr);
      if (!Array.isArray(logs)) logs = [];
    } catch (_) {
      logs = [];
    }

    logs.unshift(safeClone(logEntry));
    if (logs.length > 50) logs.length = 50;
    localStorage.setItem('auth_logs', safeStringify(logs));

    // 2. Add to persistent queue for guaranteed capture
    const queueStr = localStorage.getItem('auth_error_logs_queue') || '[]';
    let queue: any[] = [];
    try {
      queue = JSON.parse(queueStr);
      if (!Array.isArray(queue)) queue = [];
    } catch (_) {
      queue = [];
    }
    queue.push(safeClone(logEntry));
    if (queue.length > 40) queue = queue.slice(-40);
    localStorage.setItem('auth_error_logs_queue', safeStringify(queue));

    // 3. Attempt to flush queue asynchronously
    flushAuthLogQueue().catch(e => console.warn("[AUTH] Immediate flush failed, preserved in queue:", e));
  } catch (e) {
    console.error("Failed to write auth log", e);
  }
};

interface ChatMessage {
  role: 'user' | 'ai';
  content: string;
  image?: string;
  mealAnalysis?: MealAnalysis;
  timestamp: number;
  animating?: boolean;
  logged?: boolean;
  declined?: boolean;
  declinedText?: string;
  isLiked?: boolean;
  aiLiked?: boolean;
  isPending?: boolean;
  nutriAgentOrder?: NutriAgentChatOrder | null;
}

interface ChatThread {
  id: string;
  title: string;
  timestamp: number;
  messages: ChatMessage[];
}

const chatMarkdownComponents: any = {
  p: ({ children }: any) => <p className="mb-2 last:mb-0 leading-relaxed text-sm whitespace-pre-wrap">{children}</p>,
  ul: ({ children }: any) => <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }: any) => <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }: any) => <li className="text-sm leading-relaxed mb-1">{children}</li>,
  h1: ({ children }: any) => <h1 className="text-lg font-bold mt-4 mb-2 text-emerald-500">{children}</h1>,
  h2: ({ children }: any) => <h2 className="text-base font-semibold mt-3 mb-2 text-emerald-500">{children}</h2>,
  h3: ({ children }: any) => <h3 className="text-sm font-semibold mt-2 mb-1 text-emerald-500">{children}</h3>,
  strong: ({ children }: any) => <strong className="font-extrabold text-[var(--text)]">{children}</strong>,
  em: ({ children }: any) => <em className="italic">{children}</em>,
  blockquote: ({ children }: any) => <blockquote className="border-l-4 border-emerald-500/50 pl-3 italic my-2 text-zinc-400">{children}</blockquote>,
  a: ({ href, children }: any) => {
    if (href === '#login' || href === '#open-login') {
      return (
        <button
          type="button"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('open-login-modal'));
          }}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 my-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white font-extrabold text-xs shadow-md shadow-emerald-500/20 transition-all cursor-pointer"
        >
          {children}
        </button>
      );
    }
    if (href === '#launch-nutri-agent' || href === '#open-nutriagent') {
      return (
        <button
          type="button"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('open-nutriagent', { detail: { store: 'silpo' } }));
          }}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 my-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white font-extrabold text-xs shadow-md shadow-emerald-500/20 transition-all cursor-pointer"
        >
          {children}
        </button>
      );
    }
    if (href === '#open-subscription' || href === '#subscribe' || href === '#upgrade') {
      return (
        <button
          type="button"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('open-subscription-modal'));
          }}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 my-2 rounded-xl bg-gradient-to-r from-amber-500 to-emerald-500 hover:brightness-110 active:scale-95 text-white font-extrabold text-xs shadow-md transition-all cursor-pointer"
        >
          {children}
        </button>
      );
    }
    return (
      <a href={href} target="_blank" rel="noreferrer" className="text-emerald-500 hover:underline font-semibold inline-flex items-center gap-0.5">
        {children}
      </a>
    );
  },
  code: ({ className, children, ...props }: any) => {
    const inline = !className;
    return inline ? (
      <code className="px-1.5 py-0.5 rounded bg-zinc-800/10 dark:bg-zinc-800/50 text-emerald-600 dark:text-emerald-400 font-mono text-xs" {...props}>
        {children}
      </code>
    ) : (
      <pre className="p-3 rounded-lg bg-zinc-100 dark:bg-zinc-900 overflow-x-auto my-2 border border-zinc-200 dark:border-zinc-800">
        <code className="font-mono text-xs text-zinc-800 dark:text-zinc-300" {...props}>
          {children}
        </code>
      </pre>
    );
  },
};

const TypewriterText = ({ text, onComplete }: { text: string, onComplete?: () => void }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 10);
      return () => clearTimeout(timeout);
    } else if (onComplete) {
      onComplete();
    }
  }, [currentIndex, text, onComplete]);

  return (
    <div className="markdown-body">
      <ReactMarkdown components={chatMarkdownComponents}>{displayedText}</ReactMarkdown>
    </div>
  );
};

const CyclingGreeting = ({ greetings }: { greetings: { title: string; subtitle: string }[] }) => {
  const [index, setIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [displaySubtitle, setDisplaySubtitle] = useState('');
  const [phase, setPhase] = useState<'typing' | 'waiting' | 'deleting'>('typing');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    if (greetings.length === 0) return;
    
    const currentGreeting = greetings[index % greetings.length];
    const fullTitle = (currentGreeting?.title || '').replace(/^\s+/, '');
    const fullSubtitle = (currentGreeting?.subtitle || '').replace(/^\s+/, '');

    if (phase === 'typing') {
      setShowCursor(true);
      if (displayText.length < fullTitle.length) {
        const timeout = setTimeout(() => {
          setDisplayText(fullTitle.slice(0, displayText.length + 1));
        }, 30);
        return () => clearTimeout(timeout);
      } else if (displaySubtitle.length < fullSubtitle.length) {
         const timeout = setTimeout(() => {
          setDisplaySubtitle(fullSubtitle.slice(0, displaySubtitle.length + 1));
        }, 15);
        return () => clearTimeout(timeout);
      } else {
        setPhase('waiting');
      }
    } else if (phase === 'waiting') {
      const waitTimeout = setTimeout(() => {
        setPhase('deleting');
      }, 15000);
      
      return () => clearTimeout(waitTimeout);
    } else if (phase === 'deleting') {
      setShowCursor(true);
      if (displaySubtitle.length > 0) {
        const timeout = setTimeout(() => {
          setDisplaySubtitle(displaySubtitle.slice(0, -1));
        }, 7);
        return () => clearTimeout(timeout);
      } else if (displayText.length > 0) {
        const timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 10);
        return () => clearTimeout(timeout);
      } else {
        setIndex(prev => (prev + 1) % greetings.length);
        setPhase('typing');
      }
    }
  }, [displayText, displaySubtitle, phase, index, greetings]);

  useEffect(() => {
    if (phase !== 'waiting') {
      setShowCursor(true);
      return;
    }

    // Blink exactly twice at the end: 400ms off, 400ms on, 400ms off, 400ms on, then off
    const b1 = setTimeout(() => setShowCursor(false), 400);
    const b2 = setTimeout(() => setShowCursor(true), 800);
    const b3 = setTimeout(() => setShowCursor(false), 1200);
    const b4 = setTimeout(() => setShowCursor(true), 1600);
    const b5 = setTimeout(() => setShowCursor(false), 2000);

    return () => {
      clearTimeout(b1);
      clearTimeout(b2);
      clearTimeout(b3);
      clearTimeout(b4);
      clearTimeout(b5);
    };
  }, [phase]);

  const currentGreeting = greetings[index % greetings.length];
  const fullTitleText = (currentGreeting?.title || '').replace(/^\s+/, '');
  const showTitleCursor = showCursor && (displayText.length < fullTitleText.length || (displaySubtitle.length === 0 && phase === 'deleting'));
  const showSubtitleCursor = showCursor && !showTitleCursor;

  return (
    <div className="flex flex-col justify-center min-w-0 pr-2">
      <h2 className={cn(
        "font-bold whitespace-pre-line tracking-tight leading-[1.25] text-[var(--text)] line-clamp-2 overflow-hidden",
        "text-[15px] sm:text-[15px] md:text-[15px]"
      )}>
        <span className="inline-block">
          {displayText}
          <span className={cn(
            "ml-0.5 inline-block w-0.5 h-[1em] align-middle bg-emerald-500",
            showTitleCursor ? "opacity-100" : "opacity-0 hidden"
          )} />
        </span>
      </h2>
      <p className="text-zinc-500 text-[11px] sm:text-xs md:text-sm mt-0.5 leading-tight max-w-full truncate line-clamp-1 flex items-center">
        <span className="inline-block truncate">
          {displaySubtitle}
          <span className={cn(
            "ml-0.5 inline-block w-0.5 h-[1em] bg-emerald-500 align-middle",
            showSubtitleCursor ? "opacity-100" : "opacity-0"
          )} />
        </span>
      </p>
    </div>
  );
};

const TRANSLATIONS = {
  'en-US': {
    bro: {
      signOut: 'Sign Out',
      home: 'MAIN',
      takePhoto: 'SNAP MEAL',
      uploadFromGallery: 'PICK MEAL',
      analyze: 'SCAN MEAL',
      history: 'LOG BOOK',
      details: 'MEAL INFO',
      aiNutritionist: 'MAIN Nutrition',
      todaysProgress: "TODAY'S GRIND",
      progressCharts: "PROGRESS CHARTS",
      trackingGoals: "Keepin' tabs on your daily goals, bro",
      active: "CRUSHIN' IT",
      dailyStreak: 'DAILY STREAK 🔥',
      loginSlogan: "Your AI-powered nutrition companion.\nTrack your gains with a snap!",
      signInWithGoogle: "Sign in with Google",
      agreementText: "By signing in, you agree to our terms and privacy policy.",
      logMeals: 'Log your fuel, homie',
      back: 'Back',
      next: 'Next',
      letsGo: "Let's Go!",
      skipOnboarding: 'Skip Onboarding',
      onboardingWelcome: 'Welcome to MAIN Nutrition!',
      onboardingPersonalization: 'Personalization',
      onboardingCoreFeatures: 'Core Features',
      onboardingBodyMetrics: 'Body Metrics',
      onboardingActivityGoal: 'Activity & Goal',
      onboardingGettingStarted: "Let's Get Started!",
      onboardingReady: 'Ready to Start?',
      onboardingWelcomeDesc: "Hi! I'm your new AI nutritionist.\nReady to crush your goals together?",
      onboardingGettingStartedDesc: "First things first, what should I call you?",
      onboardingNamePlaceholder: "Your Name",
      onboardingCoreFeaturesDesc: "Track your nutrition effortlessly with our AI-powered tools.",
      onboardingScanMealsTitle: "Scan Meals",
      onboardingScanMealsDesc: "Just snap a photo of your plate. Our AI identifies ingredients and estimates portions instantly.",
      onboardingAiChatTitle: "AI Chat",
      onboardingAiChatDesc: "Tell me what you ate in plain text or ask for nutritional advice. It's like having a nutritionist in your pocket.",
      onboardingAnalyticsTitle: "Detailed Analytics",
      onboardingAnalyticsDesc: "Visualize your progress with comprehensive charts tracking macros, micros, and long-term health trends.",
      onboardingBodyMetricsDesc: "Let's calculate your needs. Tell me a bit about yourself.",
      onboardingAgeLabel: "Age",
      onboardingWeightLabel: "Weight (kg)",
      onboardingHeightLabel: "Height (cm)",
      onboardingGenderLabel: "Gender",
      onboardingMaleLabel: "Male",
      onboardingFemaleLabel: "Female",
      onboardingActivityGoalDesc: "How active are you and what do you want to achieve?",
      onboardingActivityLabel: "Activity Level",
      onboardingGoalLabel: "Goal",
      onboardingGoalLose: "Lose weight",
      onboardingGoalMaintain: "Maintain",
      onboardingGoalGain: "Gain weight",
      onboardingGoalLoseDesc: "Sustainable fat loss",
      onboardingGoalMaintainDesc: "Keep current balance",
      onboardingGoalGainDesc: "Build muscle mass",
      onboardingTargetMetricsDesc: "Set your daily goals. Leave blank for auto-calculation based on your metrics.",
      onboardingPersonalizationDesc: "Choose your preferred language and theme.",
      onboardingVibeLabel: "Vibe",
      onboardingVibeRegular: "Regular",
      onboardingVibeCasual: "Casual",
      onboardingReadyDesc: "We've calculated everything for you.\nLet's make every calorie count!",
      activitySedentary: 'Sedentary',
      activityLight: 'Lightly Active',
      activityModerate: 'Moderately Active',
      activityActive: 'Very Active',
      activityVeryActive: 'Extra Active',
      activitySedentaryDesc: 'Office job, little exercise',
      activityLightDesc: '1-3 days/week exercise',
      activityModerateDesc: '3-5 days/week exercise',
      activityActiveDesc: '6-7 days/week exercise',
      activityVeryActiveDesc: 'Physical job + hard training',
      breakfast: 'BREAKFAST',
      lunch: 'LUNCH',
      dinner: 'DINNER',
      dailyProgress: 'DAILY GRIND',
      progressTargets: 'How close you are to your targets, dawg',
      starsAchievements: 'Stars Achievements',
      stars: 'Stars',
      calories: 'CALORIES',
      protein: 'Protein',
      carbs: 'Carbs',
      fat: 'Fats',
      totalCalories: 'Total calories',
      nutritionalSummary: 'Macro breakdown',
      macrosPieChart: 'Macro Pie Chart',
      foodItems: 'MEAL ITEMS',
      sugar: 'Sugar',
      fiber: 'Fiber',
      sodium: 'Salt',
      potassium: 'Potassium',
      cholesterol: 'Cholesterol',
      vitaminC: 'Vitamin C',
      calcium: 'Calcium',
      iron: 'Iron',
      updatedProgress: 'NEW GAINS',
      analyzeMeal: 'Analyze',
      editMeal: 'FIX MEAL',
      saveChanges: 'Done',
      mealName: 'MEAL NAME',
      mealType: 'MEAL TYPE',
      foodName: 'FOOD NAME',
      portion: 'PORTION',
      amount: 'AMOUNT',
      unit: 'UNIT',
      addItem: 'ADD MORE',
      remove: 'WIPE',
      deleteMeal: 'TRASH MEAL',
      dateTime: 'WHEN',
      mealLoggedSuccess: 'Meal logged! Nice work, bro!👊',
      mealUpdatedSuccess: 'Looking good, homie!👍',
      viewHistory: 'CHECK PAST GAINS',
      recentMeals: "TODAY'S MEALS",
      noMealsLogged: 'No meal logged yet. Scan your first meal, dawg!',
      uploadPhoto: 'UPLOAD MEAL',
      snapMeal: 'Snap a meal to see the macros',
      uploadSnapMeal: 'Upload your meal to see the macros',
      changePhoto: 'SWAP PHOTO',
      analyzingMeal: 'Analyzing, bro...',
      secFocusing: "Sec, figuring it out... focusing...",
      countingMacros: "Counting the macros...",
      generatingVisual: "Crafting the visual...",
      bringingTogether: "Bringing it all together...",
      almostReady: "Almost ready...",
      noodling: 'Noodling...',
      daydreaming: 'Daydreaming...',
      scanningImage: 'Scanning image pixels...',
      detectingFood: 'Seeing what\'s on the plate',
      identifyingItems: 'Identifying food items...',
      estimatingPortions: 'Calculating the size',
      checkingDatabases: 'Checking nutritional databases...',
      calculatingMacros: 'Logging, hold tight...',
      balancingMacros: 'Balancing macro estimates...',
      generatingInsights: 'Generating nutritional insights...',
      finalizingResults: 'Finalyzing...',
      reviewAnalysis: 'REVIEW MEAL',
      confirmItems: 'CONFIRM',
      editItems: 'EDIT',
      isThisCorrect: 'Is this what you ate, bro?',
      mealHistory: 'LOG BOOK',
      entries: 'logs',
      noHistory: 'Empty log, bro',
      historyAppear: 'Your meal logs will show up here',
      startAnalyzing: 'LET\'S SCAN',
      skins: 'SKINS',
      cyberpunk: 'Neon Protocol',
      anime: 'Anime',
      stalker: 'S.T.A.L.K.E.R.',
      bioSyncStatus: 'BIO-SYNC STATUS',
      coolantLevel: 'COOLANT LEVEL',
      setGoals: 'TARGETS & SETTINGS',
      settings: 'MY ACCOUNT',
      yourTargetsAndSettings: 'Your Targets and Settings',
      saveGoals: 'Done',
      cancel: 'NAH',
      reset: 'WIPE',
      personalization: 'PERSONALIZATION',
      goalsAndTargets: 'GOALS & TARGETS',
      personalData: 'BODY INTEL',
      weightToLose: 'Target to lose (kg)',
      loseWeightTooltip: 'No cheat codes or shortcuts here, bro! Hard work takes time, but this formula is 100% foolproof if you stay consistent!',
      kcal: 'kcal',
      proteinShort: 'P',
      carbsShort: 'C',
      fatShort: 'F',
      snack: 'SNACK',
      retry: 'RETRY',
      errorFailed: 'Failed to scan, bro. Give it another shot.',
      errorNoFood: 'Bruh..( That\'s not food! ',
      startCamera: 'OPEN CAM',
      capturePhoto: 'SNAP IT',
      stopCamera: 'CLOSE CAM',
      theme: 'THEME',
      light: 'LIGHT',
      dark: 'DARK',
      resetHistory: 'WIPE LOGS',
      confirmReset: 'You sure you want to wipe all your logs, bro? No going back!',
      yesReset: 'YEAH, WIPE IT',
      noCancel: 'NAH, KEEP IT',
      confirmRemoveItem: 'Ditch this item, bro?',
      nutritionalInsight: 'Nutritional insight',
      portionAnalysis: 'Portion Recommendation',
      askAI: 'Ask AI',
      addMeal: 'Add meal',
      manualEntry: 'Type yourself',
      water: 'Water',
      waterGoal: 'Water goal',
      addWater: 'Add Liquid',
      ml: 'ml',
      waterLogged: 'Logged! Stay hydrated, bro!💧',
      progressOverview: 'Progress overview',
      addMealManually: 'Type your meal manually',
      uploadImage: 'Upload pic',
      replaceImage: 'Swap pic',
      removeImage: 'Ditch pic',
      noImage: 'No pic',
      mealNamePlaceholder: 'e.g., Massive Steak',
      enterNutrients: 'Meal macros',
      chatWithAI: 'CHAT WITH AI',
      chatPlaceholder: 'Tell me what you ate (e.g., 3 eggs and a protein shake)',
      chatPrompt: 'Tell me what you ate',
      listening: 'Listening, homie...',
      stopListening: 'Stop',
      voiceError: 'Can\'t hear you, bro. Check app permissions.',
      analyzingText: 'Analyzing, bro...',
      notFoodError: 'Bruh..( That\'s not food! ',
      goToToday: 'Back to today',
      reminderTitle: 'Bro!',
      reminderMessage: "Don't forget to log your fuel today, homie!🥗",
      progressReminder: "You're under 50% of your calorie goal, keep it up! ⚡",
      logNow: 'Log Now',
      dailyReminder: 'Daily Reminder',
      reminderTime: 'Reminder Time',
      days: 'Days',
      streakSuffix: 'Streak',
      category: 'Category',
      language: 'Language',
      vibe: 'Vibe',
      standard: 'Regular',
      casual: 'Casual',
      timeFormat: 'Time format',
      timeFormat12h: '12h',
      timeFormat24h: '24h',
      settingsSaved: 'Settings saved!👊',
      historyResetSuccess: 'History wiped!👊',
      undo: 'UNDO',
      actionUndone: 'Action undone!👊',
      permissions: 'APP PERMISSIONS',
      cameraPermission: 'Camera Permission',
      micPermission: 'Microphone Permission',
      requestPermission: 'Request Access',
      permissionGranted: 'Granted',
      permissionDenied: 'Denied',
      checkPermissions: 'Check Permissions',
      searchPlaceholder: 'Search meals or foods...',
      sortBy: 'Sort by',
      sortDate: 'Date',
      sortCalories: 'Calories',
      sortName: 'Name',
      sortTag: 'Tag',
      viewProgress: 'View progress',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      progressChart: 'Progress chart',
      calorieProgress: 'Calorie progress',
      proteinProgress: 'Protein progress',
      carbsProgress: 'Carbs progress',
      fatProgress: 'Fat progress',
      waterProgress: 'Liquid intake',
      microProgress: 'Other nutrients',
      macroDistribution: 'Macro distribution',
      foodExplanation: 'Food explanation',
      whyThisFood: 'Why this food?',
      fairEnough: 'Done',
      logThisMeal: 'Log this meal',
      saveToFavorites: 'To Favorites',
      adjustAmount: 'ADJUST AMOUNT',
      adjustPortion: 'ADJUST PORTION',
      amountPlaceholder: 'e.g., 250',
      totalAmountPlaceholder: 'Total amount (e.g., 2 pieces, 300g)',
      barcodeMode: 'BARCODE',
      photoMode: 'PHOTO',
      scanningBarcode: 'Scanning barcode...',
      productFound: 'Product found!👊',
      productNotFound: 'Product not found. Try photo mode!',
      analyzeWithAI: 'Analyze with AI',
      aiConfidence: 'AI Confidence',
      recognition: 'Recognition',
      portionScore: 'Portion',
      nutritionScore: 'Nutrition',
      mealGallery: 'GALLERY',
      totalToday: 'TOTAL TODAY',
      mealBreakdown: "TODAY'S BREAKDOWN",
      all: 'ALL',
      startDate: 'START DATE',
      endDate: 'END DATE',
      illumination: 'ILLUMINATION',
      on: 'ON',
      off: 'OFF',
      switchCamera: 'SWITCH CAM',
      mealReview: 'MEAL REVIEW',
      mealReviewTooltip: "When enabled, you can review and edit the AI's analysis before logging the meal.",
      confirm: 'Confirm',
      searchFavorites: 'Search favorite meals...',
      noFavorites: 'No favorite meals yet',
      saveFavoritesDesc: 'Save your favorite meals to reuse them later!',
      noMatches: 'No matches found',
      quickLog: 'Quick Log',
      hideTagFilter: 'Hide Tag Filter',
      showTagFilter: 'Show Tag Filter',
      hideDateFilters: 'Hide Date Filters',
      showDateFilters: 'Show Date Filters',
      otherTags: 'Other Tags',
      noMealsYet: 'No meals yet',
      logFirstMeal: 'Log your first meal to see it here!',
      viewDetails: 'View Details',
      sortByPercent: 'Sort by %',
      noDataNutrient: 'No data for this nutrient today',
      salt: 'Salt',
      caffeine: 'Caffeine',
      generateAIImage: 'Generate AI Image',
      expandedNutritionalFacts: 'Expanded Nutritional Facts',
      reanalyze: 'Re-analyze with different AI model',
      tagMode: 'Tag Mode',
      soundEffects: 'Sound Effects',
      confirmClearChat: 'Are you sure you want to clear the chat history?',
      clearChat: 'Clear Chat',
      reviewAndLog: 'Review & Log',
      generate: 'Generate',
      crop: 'Crop',
      itsAMatch: "IT'S A MATCH!",
      mutualLike: 'Mutual Like',
      awwAmazing: 'Aww, amazing!👊',
      imageAttached: 'PHOTO ATTACHED',
      sentAnImage: '[Sent an image]',
      aiMealSuggestions: 'AI MEAL SUGGESTIONS',
      aiSuggestionsLoading: 'AI is crafting recipes for you...',
      aiAnalysis: 'Analysis',
      aiSearchingIdeal: 'AI is crafting suggestions for you...',
      aiUpdating: 'Updating...',
      patiencePlea: 'Hanging in there, just a bit more patience...',
      almostThere: 'Almost there, homie...',
      aiSuggestionsToggle: 'AI Meal Suggestions',
      tripleChecking: 'Triple checking everything, bro...',
      wrappingUp: 'Wrapping up now, homie...',
      duplicateMeal: 'DUPLICATE',
      vibrationOnLogging: 'Log Haptics',
      ignoreMealTimeLogging: 'Ignore Meal Time',
      ignoreMealTimeLoggingDesc: 'Stop AI considering real day time when providing nutritional advice.',
      safeChoiceStop: 'Safe Choice: STOP',
      safeChoiceReview: 'Safe Choice: REVIEW',
      safeChoicePassed: 'Safe Choice: PASSED',
      safeChoiceStopDesc: 'Warning: A medical allergen or lifestyle restriction has been detected in this meal!',
      safeChoiceReviewDesc: 'Possible cross-contamination or unverified ingredients. Please check the label carefully.',
      safeChoicePassedDesc: 'This meal is completely safe and aligns perfectly with your active profile.',
      safeChoiceAlert: 'Safe Choice Alert',
      safeChoiceCheck: 'Safe Choice Check',
      favorites: 'My Besties',
      device: 'Device',
      choose: 'Choose',
    },
    standard: {
      signOut: 'Sign Out',
      home: 'HOME',
      takePhoto: 'Take Photo',
      uploadFromGallery: 'Pick Meal',
      analyze: 'ANALYZE MEAL',
      history: 'LOG BOOK',
      details: 'MEAL INFO',
      aiNutritionist: 'MAIN Nutrition',
      todaysProgress: "TODAY'S PROGRESS",
      trackingGoals: 'Tracking your daily goals',
      active: 'ACTIVE',
      dailyStreak: 'DAILY STREAK 🔥',
      logMeals: 'Log your meals',
      onboardingWelcome: 'Welcome to MAIN Nutrition!',
      onboardingPersonalization: 'Personalization',
      onboardingCoreFeatures: 'Core Features',
      onboardingBodyMetrics: 'Body Metrics',
      onboardingActivityGoal: 'Activity & Goal',
      onboardingGettingStarted: "Let's Get Started!",
      onboardingReady: 'Ready to Start?',
      onboardingWelcomeDesc: "Hi! I'm your new AI nutritionist.\nReady to crush your goals together?",
      onboardingGettingStartedDesc: "First things first, what should I call you?",
      onboardingNamePlaceholder: "Your Name",
      onboardingCoreFeaturesDesc: "Track your nutrition effortlessly with our AI-powered tools.",
      onboardingScanMealsTitle: "Scan Meals",
      onboardingScanMealsDesc: "Just snap a photo of your plate. Our AI identifies ingredients and estimates portions instantly.",
      onboardingAiChatTitle: "AI Chat",
      onboardingAiChatDesc: "Tell me what you ate in plain text or ask for nutritional advice. It's like having a nutritionist in your pocket.",
      onboardingAnalyticsTitle: "Detailed Analytics",
      onboardingAnalyticsDesc: "Visualize your progress with comprehensive charts tracking macros, micros, and long-term health trends.",
      onboardingBodyMetricsDesc: "Let's calculate your needs. Tell me a bit about yourself.",
      onboardingAgeLabel: "Age",
      onboardingWeightLabel: "Weight (kg)",
      onboardingHeightLabel: "Height (cm)",
      onboardingGenderLabel: "Gender",
      onboardingMaleLabel: "Male",
      onboardingFemaleLabel: "Female",
      onboardingActivityGoalDesc: "How active are you and what do you want to achieve?",
      onboardingActivityLabel: "Activity Level",
      onboardingGoalLabel: "Goal",
      onboardingGoalLose: "Lose weight",
      onboardingGoalMaintain: "Maintain",
      onboardingGoalGain: "Gain weight",
      onboardingGoalLoseDesc: "Sustainable fat loss",
      onboardingGoalMaintainDesc: "Keep current balance",
      onboardingGoalGainDesc: "Build muscle mass",
      onboardingTargetMetricsDesc: "Set your daily goals. Leave blank for auto-calculation based on your metrics.",
      onboardingPersonalizationDesc: "Choose your preferred language and theme.",
      loginSlogan: "Your AI-powered nutrition companion.\nTrack your gains with a snap.",
      signInWithGoogle: "Sign in with Google",
      agreementText: "By signing in, you agree to our terms and privacy policy.",
      onboardingVibeLabel: "Vibe",
      onboardingVibeRegular: "Regular",
      onboardingVibeCasual: "Casual",
      onboardingReadyDesc: "We've calculated everything for you.\nLet's make every calorie count!",
      breakfast: 'BREAKFAST',
      lunch: 'LUNCH',
      dinner: 'DINNER',
      dailyProgress: 'DAILY PROGRESS',
      progressTargets: 'Your progress towards daily targets',
      starsAchievements: 'Stars Achievements',
      stars: 'Stars',
      calories: 'CALORIES',
      protein: 'Protein',
      carbs: 'Carbs',
      fat: 'Fat',
      totalCalories: 'Total calories',
      nutritionalSummary: 'Nutritional summary',
      macrosPieChart: 'Macros Pie Chart',
      foodItems: 'MEAL ITEMS',
      sugar: 'Sugar',
      fiber: 'Fiber',
      sodium: 'Salt',
      potassium: 'Potassium',
      cholesterol: 'Cholesterol',
      vitaminC: 'Vitamin C',
      calcium: 'Calcium',
      iron: 'Iron',
      water: 'Water',
      ml: 'ml',
      updatedProgress: 'NEW GAINS',
      analyzeMeal: 'ANALYZE',
      editMeal: 'EDIT MEAL',
      saveChanges: 'Done',
      mealName: 'MEAL NAME',
      mealType: 'MEAL TYPE',
      foodName: 'FOOD NAME',
      portion: 'PORTION',
      amount: 'Amount',
      unit: 'Unit',
      addItem: 'ADD ITEM',
      remove: 'REMOVE',
      deleteMeal: 'DELETE MEAL',
      dateTime: 'DATE & TIME',
      mealLoggedSuccess: 'Meal logged successfully!',
      mealUpdatedSuccess: 'Meal updated successfully!',
      viewHistory: 'VIEW HISTORY',
      recentMeals: "TODAY'S RECENT MEALS",
      noMealsLogged: 'No meals logged yet. Analyze your first meal!',
      uploadPhoto: 'UPLOAD MEAL',
      snapMeal: 'Snap a meal to see the macros',
      uploadSnapMeal: 'Upload your meal to see the macros',
      changePhoto: 'CHANGE PHOTO',
      analyzingMeal: 'Analyzing',
      secFocusing: "One second, analyzing... focusing...",
      countingMacros: "Calculating nutritional values...",
      generatingVisual: "Generating meal image...",
      bringingTogether: "Consolidating all results...",
      almostReady: "Almost ready...",
      noodling: 'Noodling...',
      daydreaming: 'Daydreaming...',
      scanningImage: 'Scanning image pixels...',
      detectingFood: 'Detecting food items',
      estimatingPortions: 'Estimating portions',
      identifyingItems: 'Identifying food items...',
      checkingDatabases: 'Checking nutritional databases...',
      sendingToAI: 'Sending to AI for wisdom...',
      analyzingNutrition: 'Analyzing nutrition values...',
      calculatingMacros: 'Logging your meal',
      calculatingFinalStats: 'Calculating final stats...',
      finalizingResults: 'Finalizing results',
      reviewAnalysis: 'REVIEW MEAL',
      confirmItems: 'CONFIRM',
      editItems: 'EDIT',
      isThisCorrect: 'Is this what you ate?',
      mealHistory: 'LOG BOOK',
      entries: 'entries',
      noHistory: 'No history yet',
      historyAppear: 'Your meal history will appear here',
      startAnalyzing: 'START ANALYZING',
      skins: 'SKINS',
      cyberpunk: 'Neon Protocol',
      anime: 'Anime',
      stalker: 'S.T.A.L.K.E.R.',
      bioSyncStatus: 'BIO-SYNC STATUS',
      coolantLevel: 'COOLANT LEVEL',
      setGoals: 'TARGETS & SETTINGS',
      settings: 'MY ACCOUNT',
      yourTargetsAndSettings: 'Your Targets and Settings',
      saveGoals: 'Done',
      cancel: 'CANCEL',
      reset: 'RESET',
      personalization: 'Personalization',
      goalsAndTargets: 'Goals & Targets',
      personalData: 'Personal Data',
      weightToLose: 'How much weight to lose (kg)',
      loseWeightTooltip: 'No fast results! Real effort takes time, but this system works 100% of the time.',
      kcal: 'kcal',
      proteinShort: 'P',
      carbsShort: 'C',
      fatShort: 'F',
      snack: 'SNACK',
      retry: 'RETRY',
      errorFailed: 'Failed to analyze meal. Please try again.',
      errorNoFood: 'This does not look like food.',
      startCamera: 'OPEN CAM',
      capturePhoto: 'CAPTURE',
      stopCamera: 'CLOSE CAMERA',
      theme: 'THEME',
      light: 'LIGHT',
      dark: 'DARK',
      resetHistory: 'RESET HISTORY',
      confirmReset: 'Are you sure you want to reset all history? This cannot be undone.',
      yesReset: 'YES, RESET',
      noCancel: 'NO, CANCEL',
      confirmRemoveItem: 'Remove this item?',
      nutritionalInsight: 'Nutritional insight',
      portionAnalysis: 'Portion Analysis',
      askAI: 'Ask AI',
      addMeal: 'Add meal',
      manualEntry: 'Manual entry',
      waterGoal: 'Water Goal',
      addWater: 'Add Water',
      waterLogged: 'Water logged successfully!💧',
      addMealManually: 'Type your meal manually',
      uploadImage: 'Upload image',
      replaceImage: 'Replace image',
      removeImage: 'Remove image',
      noImage: 'No image',
      mealNamePlaceholder: 'e.g., Grilled Chicken Salad',
      enterNutrients: 'Enter nutrients manually',
      chatWithAI: 'Chat with AI',
      chatPlaceholder: 'Tell me what you ate (e.g., 3 eggs and a protein shake)',
      chatPrompt: 'Tell me what you ate',
      listening: 'Listening...',
      stopListening: 'Stop',
      voiceError: 'Voice recognition failed. Check permissions.',
      analyzingText: 'Analyzing',
      notFoodError: 'This does not look like food.',
      goToToday: 'Back to today',
      reminderTitle: 'Reminder',
      reminderMessage: "Don't forget to log your meals today! 🥗",
      progressReminder: "Daily Progress: You have logged less than 50% of your calorie goal. 🍏",
      logNow: 'Log now',
      dailyReminder: 'Daily Reminder',
      reminderTime: 'Reminder Time',
      category: 'Category',
      language: 'Language',
      vibe: 'Vibe',
      standard: 'Standard',
      casual: 'Casual',
      settingsSaved: 'Settings saved successfully!',
      historyResetSuccess: 'History reset successfully!',
      undo: 'UNDO',
      actionUndone: 'Action undone!',
      searchPlaceholder: 'Search meals or foods...',
      sortBy: 'Sort by',
      sortDate: 'Date',
      sortCalories: 'Calories',
      sortName: 'Name',
      sortTag: 'Tag',
      viewProgress: 'View progress',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      progressChart: 'Progress chart',
      calorieProgress: 'Calorie progress',
      proteinProgress: 'Protein progress',
      carbsProgress: 'Carbs progress',
      fatProgress: 'Fat progress',
      waterProgress: 'Liquid intake',
      microProgress: 'Other nutrients',
      macroDistribution: 'Macro distribution',
      foodExplanation: 'Food explanation',
      whyThisFood: 'Why this food?',
      fairEnough: 'Done',
      logThisMeal: 'Log this meal',
      saveToFavorites: 'To Favorites',
      adjustAmount: 'Adjust Amount',
      adjustPortion: 'Adjust Portion',
      amountPlaceholder: 'e.g., 250',
      totalAmountPlaceholder: 'Total amount (e.g., 2 pieces, 300g)',
      barcodeMode: 'Barcode',
      photoMode: 'Photo',
      scanningBarcode: 'Scanning barcode...',
      productFound: 'Product found!',
      productNotFound: 'Product not found. Try photo mode!',
      analyzeWithAI: 'Analyze with AI',
      aiConfidence: 'AI Confidence',
      recognition: 'Recognition',
      portionScore: 'Portion',
      nutritionScore: 'Nutrition',
      mealGallery: 'GALLERY',
      totalToday: 'Total Today',
      mealBreakdown: "TODAY'S MEALS",
      all: 'All',
      startDate: 'Start Date',
      endDate: 'End Date',
      illumination: 'ILLUMINATION',
      on: 'ON',
      off: 'OFF',
      switchCamera: 'SWITCH CAMERA',
      mealReview: 'Meal Review',
      confirm: 'Confirm',
      searchFavorites: 'Search favorite meals...',
      noFavorites: 'No favorite meals yet',
      saveFavoritesDesc: 'Save your favorite meals to reuse them later!',
      noMatches: 'No matches found',
      quickLog: 'Quick Log',
      hideTagFilter: 'Hide Tag Filter',
      showTagFilter: 'Show Tag Filter',
      hideDateFilters: 'Hide Date Filters',
      showDateFilters: 'Show Date Filters',
      otherTags: 'Other Tags',
      noMealsYet: 'No meals yet',
      logFirstMeal: 'Log your first meal to see it here!',
      viewDetails: 'View Details',
      sortByPercent: 'Sort by %',
      noDataNutrient: 'No data for this nutrient today',
      salt: 'Salt',
      caffeine: 'Caffeine',
      generateAIImage: 'Generate AI Image',
      expandedNutritionalFacts: 'Expanded Nutritional Facts',
      reanalyze: 'Re-analyze with different AI model',
      tagMode: 'Tag Mode',
      soundEffects: 'Sound Effects',
      confirmClearChat: 'Are you sure you want to clear the chat history?',
      clearChat: 'Clear Chat',
      reviewAndLog: 'Review & Log',
      generate: 'Generate',
      crop: 'Crop',
      adminBugs: 'Bug Reports',
      noBugs: 'No bug reports found.',
      adminDashboard: 'Admin Console',
      users: 'Users',
      stats: 'Stats',
      bugs: 'Bugs',
      noUsers: 'No users found yet.',
      totalUsers: 'Total Users',
      regToday: 'Registered Today',
      latestUser: 'Latest User',
      aiMealSuggestions: 'AI Meal Suggestions',
      aiSuggestionsLoading: 'AI is crafting suggestions for you...',
      vibrationOnLogging: 'Haptic Feedback',
      ignoreMealTimeLogging: 'Ignore Meal Time',
      ignoreMealTimeLoggingDesc: 'Stop AI considering real day time when providing nutritional advice.',
      safeChoiceStop: 'Safe Choice: STOP',
      safeChoiceReview: 'Safe Choice: REVIEW',
      safeChoicePassed: 'Safe Choice: PASSED',
      safeChoiceStopDesc: 'Warning: A medical allergen or lifestyle restriction has been detected in this meal!',
      safeChoiceReviewDesc: 'Possible cross-contamination or unverified ingredients. Please check the label carefully.',
      safeChoicePassedDesc: 'This meal is completely safe and aligns perfectly with your active profile.',
      safeChoiceAlert: 'Safe Choice Alert',
      safeChoiceCheck: 'Safe Choice Check',
      favorites: 'FAVORITES',
      device: 'Device',
      choose: 'Choose',
    }
  },
  'en-GB': {
    bro: {
      signOut: 'Sign Out',
      home: 'MAIN',
      takePhoto: 'SNAP MEAL',
      uploadFromGallery: 'PICK MEAL',
      analyze: 'SCAN MEAL',
      history: 'LOG BOOK',
      details: 'MEAL INFO',
      aiNutritionist: 'MAIN Nutrition',
      todaysProgress: "TODAY'S GRIND",
      trackingGoals: 'Keepin\' tabs on your daily goals, mate',
      active: 'CRUSHIN\' IT',
      dailyStreak: 'DAILY STREAK 🔥',
      logMeals: 'Log your fuel, mate',
      back: 'Back',
      next: 'Next',
      letsGo: "Let's Go!",
      skipOnboarding: 'Skip Onboarding',
      onboardingWelcome: 'Welcome to MAIN Nutrition, mate!',
      onboardingPersonalization: 'Personalisation',
      onboardingCoreFeatures: 'Core Features',
      onboardingBodyMetrics: 'Body Metrics',
      onboardingActivityGoal: 'Activity & Goal',
      onboardingGettingStarted: "Let's Get Started, mate!",
      onboardingReady: 'Ready to Start, mate?',
      onboardingWelcomeDesc: "Hi! I'm your new AI nutritionist.\nReady to crush your goals together?",
      onboardingGettingStartedDesc: "First things first, what should I call you?",
      onboardingNamePlaceholder: "Your Name",
      onboardingCoreFeaturesDesc: "Track your nutrition effortlessly with our AI-powered tools.",
      onboardingScanMealsTitle: "Scan Meals",
      onboardingScanMealsDesc: "Just snap a photo of your plate. Our AI identifies ingredients and estimates portions instantly.",
      onboardingAiChatTitle: "AI Chat",
      onboardingAiChatDesc: "Tell me what you ate in plain text or ask for nutritional advice. It's like having a nutritionist in your pocket.",
      onboardingAnalyticsTitle: "Detailed Analytics",
      onboardingAnalyticsDesc: "Visualize your progress with comprehensive charts tracking macros, micros, and long-term health trends.",
      onboardingBodyMetricsDesc: "Let's calculate your needs. Tell me a bit about yourself.",
      onboardingAgeLabel: "Age",
      onboardingWeightLabel: "Weight (kg)",
      onboardingHeightLabel: "Height (cm)",
      onboardingGenderLabel: "Gender",
      onboardingMaleLabel: "Male",
      onboardingFemaleLabel: "Female",
      onboardingActivityGoalDesc: "How active are you and what do you want to achieve?",
      onboardingActivityLabel: "Activity Level",
      onboardingGoalLabel: "Goal",
      onboardingGoalLose: "Lose weight",
      onboardingGoalMaintain: "Maintain",
      onboardingGoalGain: "Gain weight",
      onboardingGoalLoseDesc: "Sustainable fat loss",
      onboardingGoalMaintainDesc: "Keep current balance",
      onboardingGoalGainDesc: "Build muscle mass",
      onboardingTargetMetricsDesc: "Set your daily goals. Leave blank for auto-calculation based on your metrics.",
      onboardingPersonalizationDesc: "Choose your preferred language and theme.",
      onboardingVibeLabel: "Vibe",
      onboardingVibeRegular: "Regular",
      onboardingVibeCasual: "Casual",
      onboardingReadyDesc: "We've calculated everything for you.\nLet's make every calorie count!",
      activitySedentary: 'Sedentary',
      activityLight: 'Lightly Active',
      activityModerate: 'Moderately Active',
      activityActive: 'Very Active',
      activityVeryActive: 'Extra Active',
      activitySedentaryDesc: 'Office job, little exercise',
      activityLightDesc: '1-3 days/week exercise',
      activityModerateDesc: '3-5 days/week exercise',
      activityActiveDesc: '6-7 days/week exercise',
      activityVeryActiveDesc: 'Physical job + hard training',
      breakfast: 'BREAKFAST',
      lunch: 'LUNCH',
      dinner: 'DINNER',
      dailyProgress: 'DAILY GRIND',
      progressTargets: 'How close you are to your targets, mate',
      starsAchievements: 'Stars Achievements',
      stars: 'Stars',
      calories: 'CALORIES',
      protein: 'Protein',
      carbs: 'Carbs',
      fat: 'Fats',
      totalCalories: 'Total calories',
      nutritionalSummary: 'Macro breakdown',
      macrosPieChart: 'Macros Pie Chart',
      foodItems: 'MEAL ITEMS',
      sugar: 'Sugar',
      fiber: 'Fiber',
      sodium: 'Salt',
      potassium: 'Potassium',
      cholesterol: 'Cholesterol',
      vitaminC: 'Vitamin C',
      calcium: 'Calcium',
      iron: 'Iron',
      updatedProgress: 'NEW GAINS',
      analyzeMeal: 'Analyse',
      editMeal: 'FIX MEAL',
      saveChanges: 'Done',
      mealName: 'MEAL NAME',
      mealType: 'MEAL TYPE',
      foodName: 'FOOD NAME',
      portion: 'PORTION',
      amount: 'AMOUNT',
      unit: 'UNIT',
      addItem: 'ADD MORE',
      remove: 'WIPE',
      deleteMeal: 'TRASH MEAL',
      dateTime: 'WHEN',
      mealLoggedSuccess: 'Meal logged! Nice work, mate!👊',
      mealUpdatedSuccess: 'Looking good, mate!👍',
      viewHistory: 'CHECK PAST GAINS',
      recentMeals: "TODAY'S MEALS",
      noMealsLogged: 'No meal logged yet. Scan your first meal, mate!',
      uploadPhoto: 'UPLOAD MEAL',
      snapMeal: 'Snap a meal to see the macros',
      uploadSnapMeal: 'Upload your meal to see the macros',
      changePhoto: 'SWAP PHOTO',
      analyzingMeal: 'Analysing, mate...',
      secFocusing: "Sec, figuring it out... focusing, mate...",
      countingMacros: "Counting the macros...",
      generatingVisual: "Crafting the visual...",
      bringingTogether: "Bringing it all together...",
      almostReady: "Almost ready, mate...",
      noodling: 'Noodling...',
      daydreaming: 'Daydreaming...',
      scanningImage: 'Scanning image pixels...',
      detectingFood: 'Seeing what\'s on the plate',
      estimatingPortions: 'Calculating the size',
      identifyingItems: 'Identifying food items...',
      checkingDatabases: 'Checking nutritional databases...',
      calculatingMacros: 'Logging, hold tight...',
      balancingMacros: 'Balancing macro estimates...',
      generatingInsights: 'Generating nutritional insights...',
      finalizingResults: 'Finalising...',
      reviewAnalysis: 'REVIEW MEAL',
      confirmItems: 'CONFIRM',
      editItems: 'EDIT',
      isThisCorrect: 'Is this what you ate, mate?',
      mealHistory: 'LOG BOOK',
      entries: 'logs',
      noHistory: 'Empty log, mate',
      historyAppear: 'Your meal logs will show up here',
      startAnalyzing: 'LET\'S SCAN',
      skins: 'SKINS',
      cyberpunk: 'Neon Protocol',
      anime: 'Anime',
      stalker: 'S.T.A.L.K.E.R.',
      bioSyncStatus: 'BIO-SYNC STATUS',
      coolantLevel: 'COOLANT LEVEL',
      setGoals: 'TARGETS & SETTINGS',
      settings: 'MY ACCOUNT',
      yourTargetsAndSettings: 'Your Targets and Settings',
      saveGoals: 'Done',
      cancel: 'NAH',
      reset: 'WIPE',
      personalization: 'PERSONALISATION',
      goalsAndTargets: 'GOALS & TARGETS',
      personalData: 'BODY INTEL',
      weightToLose: 'Target to lose (kg)',
      loseWeightTooltip: 'No shortcuts here, mate! Real hard work takes time, but this formula is 100% foolproof if you stick to it!',
      kcal: 'kcal',
      proteinShort: 'P',
      carbsShort: 'C',
      fatShort: 'F',
      snack: 'SNACK',
      retry: 'RETRY',
      errorFailed: 'Failed to scan, mate. Give it another shot.',
      errorNoFood: 'Bruh..( That\'s not food! ',
      startCamera: 'OPEN CAM',
      capturePhoto: 'SNAP IT',
      stopCamera: 'CLOSE CAM',
      theme: 'THEME',
      light: 'LIGHT',
      dark: 'DARK',
      resetHistory: 'WIPE LOGS',
      confirmReset: 'You sure you want to wipe all your logs, mate? No going back!',
      yesReset: 'YEAH, WIPE IT',
      noCancel: 'NAH, KEEP IT',
      confirmRemoveItem: 'Ditch this item, mate?',
      nutritionalInsight: 'Nutritional insight',
      portionAnalysis: 'Portion Recommendation',
      askAI: 'Ask AI',
      addMeal: 'Add meal',
      manualEntry: 'Type yourself',
      water: 'Water',
      waterGoal: 'Water goal',
      addWater: 'Add Liquid',
      ml: 'ml',
      waterLogged: 'Water logged! Stay hydrated, mate!💧',
      progressOverview: 'Progress overview',
      addMealManually: 'Type your meal manually',
      uploadImage: 'Upload pic',
      replaceImage: 'Swap pic',
      removeImage: 'Ditch pic',
      noImage: 'No pic',
      mealNamePlaceholder: 'e.g., Massive Steak',
      enterNutrients: 'Meal macros',
      chatWithAI: 'CHAT WITH AI',
      chatPlaceholder: 'Tell me what you ate (e.g., 3 eggs and a protein shake)',
      chatPrompt: 'Tell me what you ate',
      listening: 'Listening, mate...',
      stopListening: 'Stop',
      voiceError: 'Can\'t hear you, mate. Check permissions.',
      analyzingText: 'Analysing, mate...',
      notFoodError: 'Bruh..( That\'s not food! ',
      goToToday: 'Back to today',
      reminderTitle: 'Yo, mate!',
      reminderMessage: "Don't forget to log your fuel today, mate! 🥗",
      progressReminder: "Under 50% of your gains for today, mate. Fuel up! ⚡",
      logNow: 'Log Now',
      dailyReminder: 'Daily Reminder',
      reminderTime: 'Reminder Time',
      category: 'Category',
      language: 'Language',
      vibe: 'Vibe',
      standard: 'Regular',
      casual: 'Casual',
      timeFormat: 'Time format',
      timeFormat12h: '12h',
      timeFormat24h: '24h',
      settingsSaved: 'Settings saved!👊',
      historyResetSuccess: 'History wiped!👊',
      undo: 'UNDO',
      actionUndone: 'Action undone!👊',
      permissions: 'APP PERMISSIONS',
      cameraPermission: 'Camera Permission',
      micPermission: 'Microphone Permission',
      requestPermission: 'Request Access',
      permissionGranted: 'Granted',
      permissionDenied: 'Denied',
      checkPermissions: 'Check Permissions',
      searchPlaceholder: 'Search meals or foods...',
      sortBy: 'Sort by',
      sortDate: 'Date',
      sortCalories: 'Calories',
      sortName: 'Name',
      sortTag: 'Tag',
      viewProgress: 'View progress',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      progressChart: 'Progress chart',
      calorieProgress: 'Calorie progress',
      proteinProgress: 'Protein progress',
      carbsProgress: 'Carbs progress',
      fatProgress: 'Fat progress',
      waterProgress: 'Liquid intake',
      microProgress: 'Other nutrients',
      macroDistribution: 'Macro distribution',
      foodExplanation: 'Food explanation',
      whyThisFood: 'Why this food?',
      fairEnough: 'Done',
      logThisMeal: 'Log this meal',
      saveToFavorites: 'To Favourites',
      adjustAmount: 'ADJUST AMOUNT',
      adjustPortion: 'ADJUST PORTION',
      amountPlaceholder: 'e.g., 250',
      totalAmountPlaceholder: 'Total amount (e.g., 2 pieces, 300g)',
      barcodeMode: 'BARCODE',
      photoMode: 'PHOTO',
      scanningBarcode: 'Scanning barcode...',
      productFound: 'Product found!👊',
      productNotFound: 'Product not found. Try photo mode!',
      analyzeWithAI: 'Analyse with AI',
      aiConfidence: 'AI Confidence',
      recognition: 'Recognition',
      portionScore: 'Portion',
      nutritionScore: 'Nutrition',
      mealGallery: 'GALLERY',
      totalToday: 'TOTAL TODAY',
      mealBreakdown: "TODAY'S BREAKDOWN",
      all: 'ALL',
      startDate: 'START DATE',
      endDate: 'END DATE',
      illumination: 'ILLUMINATION',
      on: 'ON',
      off: 'OFF',
      switchCamera: 'SWITCH CAM',
      mealReview: 'MEAL REVIEW',
      mealReviewTooltip: "When enabled, you can review and edit the AI's analysis before logging the meal.",
      confirm: 'Confirm',
      searchFavorites: 'Search favourite meals...',
      noFavorites: 'No favourite meals yet',
      saveFavoritesDesc: 'Save your favourite meals to reuse them later!',
      noMatches: 'No matches found',
      quickLog: 'Quick Log',
      hideTagFilter: 'Hide Tag Filter',
      showTagFilter: 'Show Tag Filter',
      hideDateFilters: 'Hide Date Filters',
      showDateFilters: 'Show Date Filters',
      otherTags: 'Other Tags',
      noMealsYet: 'No meals yet',
      logFirstMeal: 'Log your first meal to see it here!',
      viewDetails: 'View Details',
      sortByPercent: 'Sort by %',
      noDataNutrient: 'No data for this nutrient today',
      salt: 'Salt',
      caffeine: 'Caffeine',
      generateAIImage: 'Generate AI Image',
      expandedNutritionalFacts: 'Expanded Nutritional Facts',
      reanalyze: 'Re-analyse with different AI model',
      tagMode: 'Tag Mode',
      soundEffects: 'Sound Effects',
      confirmClearChat: 'Are you sure you want to clear the chat history?',
      clearChat: 'Clear Chat',
      reviewAndLog: 'Review & Log',
      generate: 'Generate',
      crop: 'Crop',
      itsAMatch: "IT'S A MATCH!",
      mutualLike: 'Mutual Like',
      awwAmazing: 'Aww, amazing!👊',
      imageAttached: 'Photo attached',
      sentAnImage: '[Sent an image]',
      vibrationOnLogging: 'Log Haptics',
      ignoreMealTimeLogging: 'Ignore Meal Time',
      ignoreMealTimeLoggingDesc: 'Stop AI considering real day time when providing nutritional advice.',
      safeChoiceStop: 'Safe Choice: STOP',
      safeChoiceReview: 'Safe Choice: REVIEW',
      safeChoicePassed: 'Safe Choice: PASSED',
      safeChoiceStopDesc: 'Warning: A medical allergen or lifestyle restriction has been detected in this meal!',
      safeChoiceReviewDesc: 'Possible cross-contamination or unverified ingredients. Please check the label carefully.',
      safeChoicePassedDesc: 'This meal is completely safe and aligns perfectly with your active profile.',
      safeChoiceAlert: 'Safe Choice Alert',
      safeChoiceCheck: 'Safe Choice Check',
      favorites: 'Besties',
      device: 'Device',
      choose: 'Choose',
    },
    standard: {
      signOut: 'Sign Out',
      home: 'HOME',
      takePhoto: 'Take Photo',
      uploadFromGallery: 'Pick Meal',
      analyze: 'ANALYSE MEAL',
      history: 'LOG BOOK',
      details: 'MEAL INFO',
      aiNutritionist: 'MAIN Nutrition',
      todaysProgress: "TODAY'S PROGRESS",
      trackingGoals: 'Tracking your daily goals',
      active: 'ACTIVE',
      dailyStreak: 'DAILY STREAK 🔥',
      logMeals: 'Log your meals',
      onboardingWelcome: 'Welcome to MAIN Nutrition!',
      onboardingPersonalization: 'Personalization',
      onboardingCoreFeatures: 'Core Features',
      onboardingBodyMetrics: 'Body Metrics',
      onboardingActivityGoal: 'Activity & Goal',
      onboardingGettingStarted: "Let's Get Started!",
      onboardingReady: 'Ready to Start?',
      onboardingWelcomeDesc: "Hi! I'm your new AI nutritionist.\nReady to crush your goals together?",
      onboardingGettingStartedDesc: "First things first, what should I call you?",
      onboardingNamePlaceholder: "Your Name",
      onboardingCoreFeaturesDesc: "Track your nutrition effortlessly with our AI-powered tools.",
      onboardingScanMealsTitle: "Scan Meals",
      onboardingScanMealsDesc: "Just snap a photo of your plate. Our AI identifies ingredients and estimates portions instantly.",
      onboardingAiChatTitle: "AI Chat",
      onboardingAiChatDesc: "Tell me what you ate in plain text or ask for nutritional advice. It's like having a nutritionist in your pocket.",
      onboardingAnalyticsTitle: "Detailed Analytics",
      onboardingAnalyticsDesc: "Visualize your progress with comprehensive charts tracking macros, micros, and long-term health trends.",
      onboardingBodyMetricsDesc: "Let's calculate your needs. Tell me a bit about yourself.",
      onboardingAgeLabel: "Age",
      onboardingWeightLabel: "Weight (kg)",
      onboardingHeightLabel: "Height (cm)",
      onboardingGenderLabel: "Gender",
      onboardingMaleLabel: "Male",
      onboardingFemaleLabel: "Female",
      onboardingActivityGoalDesc: "How active are you and what do you want to achieve?",
      onboardingActivityLabel: "Activity Level",
      onboardingGoalLabel: "Goal",
      onboardingGoalLose: "Lose weight",
      onboardingGoalMaintain: "Maintain",
      onboardingGoalGain: "Gain weight",
      onboardingGoalLoseDesc: "Sustainable fat loss",
      onboardingGoalMaintainDesc: "Keep current balance",
      onboardingGoalGainDesc: "Build muscle mass",
      onboardingTargetMetricsDesc: "Set your daily goals. Leave blank for auto-calculation based on your metrics.",
      onboardingPersonalizationDesc: "Choose your preferred language and theme.",
      loginSlogan: "Your AI-powered nutrition companion.\nTrack your gains with a snap.",
      signInWithGoogle: "Sign in with Google",
      agreementText: "By signing in, you agree to our terms and privacy policy.",
      onboardingVibeLabel: "Vibe",
      onboardingVibeRegular: "Regular",
      onboardingVibeCasual: "Casual",
      onboardingReadyDesc: "We've calculated everything for you.\nLet's make every calorie count!",
      breakfast: 'BREAKFAST',
      lunch: 'LUNCH',
      dinner: 'DINNER',
      dailyProgress: 'DAILY PROGRESS',
      progressTargets: 'Your progress towards daily targets',
      calories: 'CALORIES',
      protein: 'Protein',
      carbs: 'Carbohydrates',
      fat: 'Fat',
      totalCalories: 'Total calories',
      nutritionalSummary: 'Nutritional summary',
      macrosPieChart: 'Macro Pie Chart',
      foodItems: 'MEAL ITEMS',
      sugar: 'Sugar',
      fiber: 'Fiber',
      sodium: 'Salt',
      potassium: 'Potassium',
      cholesterol: 'Cholesterol',
      vitaminC: 'Vitamin C',
      calcium: 'Calcium',
      iron: 'Iron',
      water: 'Water',
      ml: 'ml',
      updatedProgress: 'NEW GAINS',
      analyzeMeal: 'ANALYSE',
      editMeal: 'EDIT MEAL',
      saveChanges: 'Done',
      mealName: 'MEAL NAME',
      mealType: 'MEAL TYPE',
      foodName: 'FOOD NAME',
      portion: 'PORTION',
      amount: 'Amount',
      unit: 'Unit',
      addItem: 'ADD ITEM',
      remove: 'REMOVE',
      deleteMeal: 'DELETE MEAL',
      dateTime: 'DATE & TIME',
      mealLoggedSuccess: 'Meal logged successfully!',
      mealUpdatedSuccess: 'Meal updated successfully!',
      viewHistory: 'VIEW HISTORY',
      recentMeals: "TODAY'S RECENT MEALS",
      noMealsLogged: 'No meals logged yet. Analyse your first meal!',
      uploadPhoto: 'UPLOAD MEAL',
      snapMeal: 'Snap a meal to see the macros',
      uploadSnapMeal: 'Upload your meal to see the macros',
      changePhoto: 'CHANGE PHOTO',
      analyzingMeal: 'Analysing',
      secFocusing: "One second, analyzing... focusing...",
      countingMacros: "Calculating nutritional values...",
      generatingVisual: "Generating meal image...",
      bringingTogether: "Consolidating all results...",
      almostReady: "Almost ready...",
      noodling: 'Noodling...',
      daydreaming: 'Daydreaming...',
      scanningImage: 'Scanning image pixels...',
      detectingFood: 'Detecting food items',
      estimatingPortions: 'Estimating portions',
      identifyingItems: 'Identifying food items...',
      checkingDatabases: 'Checking nutritional databases...',
      calculatingMacros: 'Logging your meal',
      balancingMacros: 'Balancing macro estimates...',
      generatingInsights: 'Generating nutritional insights...',
      finalizingResults: 'Finalising...',
      reviewAnalysis: 'REVIEW MEAL',
      confirmItems: 'CONFIRM',
      editItems: 'EDIT',
      isThisCorrect: 'Is this what you ate?',
      mealHistory: 'LOG BOOK',
      entries: 'entries',
      noHistory: 'No history yet',
      historyAppear: 'Your meal history will appear here',
      startAnalyzing: 'START ANALYSING',
      skins: 'SKINS',
      cyberpunk: 'Neon Protocol',
      anime: 'Anime',
      stalker: 'S.T.A.L.K.E.R.',
      bioSyncStatus: 'BIO-SYNC STATUS',
      coolantLevel: 'COOLANT LEVEL',
      setGoals: 'TARGETS & SETTINGS',
      settings: 'MY ACCOUNT',
      yourTargetsAndSettings: 'Your Targets and Settings',
      saveGoals: 'Done',
      cancel: 'CANCEL',
      reset: 'RESET',
      personalization: 'Personalisation',
      goalsAndTargets: 'Goals & Targets',
      personalData: 'Personal Data',
      weightToLose: 'How much weight to lose (kg)',
      loseWeightTooltip: 'No fast results! Real effort takes time, but this system works 100% of the time.',
      kcal: 'kcal',
      proteinShort: 'P',
      carbsShort: 'C',
      fatShort: 'F',
      snack: 'SNACK',
      retry: 'RETRY',
      errorFailed: 'Failed to analyse meal. Please try again.',
      errorNoFood: 'This does not look like food.',
      startCamera: 'OPEN CAM',
      capturePhoto: 'CAPTURE',
      stopCamera: 'CLOSE CAMERA',
      theme: 'THEME',
      light: 'LIGHT',
      dark: 'DARK',
      resetHistory: 'RESET HISTORY',
      confirmReset: 'Are you sure you want to reset all history? This cannot be undone.',
      yesReset: 'YES, RESET',
      noCancel: 'NO, CANCEL',
      confirmRemoveItem: 'Remove this item?',
      nutritionalInsight: 'Nutritional insight',
      portionAnalysis: 'Portion Analysis',
      askAI: 'Ask AI',
      addMeal: 'Add meal',
      manualEntry: 'Manual entry',
      waterGoal: 'Water Goal',
      addWater: 'Add Water',
      waterLogged: 'Water logged successfully!💧',
      addMealManually: 'Type your meal manually',
      uploadImage: 'Upload image',
      replaceImage: 'Replace image',
      removeImage: 'Remove image',
      noImage: 'No image',
      mealNamePlaceholder: 'e.g., Grilled Chicken Salad',
      enterNutrients: 'Enter nutrients manually',
      chatWithAI: 'Chat with AI',
      chatPlaceholder: 'Tell me what you ate (e.g., 3 eggs and a protein shake)',
      chatPrompt: 'Tell me what you ate',
      listening: 'Listening...',
      stopListening: 'Stop',
      voiceError: 'Voice recognition failed. Check permissions.',
      analyzingText: 'Analysing',
      notFoodError: 'This does not look like food.',
      goToToday: 'Back to today',
      reminderTitle: 'Reminder',
      reminderMessage: "Don't forget to log your meals today!🥗",
      progressReminder: "Progress Update: You have achieved less than 50% of your calorie target. ⚡",
      logNow: 'Log now',
      dailyReminder: 'Daily Reminder',
      reminderTime: 'Reminder Time',
      category: 'Category',
      language: 'Language',
      vibe: 'Vibe',
      standard: 'Standard',
      casual: 'Casual',
      settingsSaved: 'Settings saved successfully!',
      historyResetSuccess: 'History reset successfully!',
      undo: 'UNDO',
      actionUndone: 'Action undone!',
      searchPlaceholder: 'Search meals or foods...',
      sortBy: 'Sort by',
      sortDate: 'Date',
      sortCalories: 'Calories',
      sortName: 'Name',
      sortTag: 'Tag',
      viewProgress: 'View progress',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      progressChart: 'Progress chart',
      calorieProgress: 'Calorie progress',
      proteinProgress: 'Protein progress',
      carbsProgress: 'Carbs progress',
      fatProgress: 'Fat progress',
      waterProgress: 'Liquid intake',
      microProgress: 'Other nutrients',
      macroDistribution: 'Macro distribution',
      foodExplanation: 'Food explanation',
      whyThisFood: 'Why this food?',
      fairEnough: 'Done',
      logThisMeal: 'Log this meal',
      saveToFavorites: 'To Favourites',
      adjustAmount: 'Adjust Amount',
      adjustPortion: 'Adjust Portion',
      amountPlaceholder: 'e.g., 250',
      totalAmountPlaceholder: 'Total amount (e.g., 2 pieces, 300g)',
      barcodeMode: 'Barcode',
      photoMode: 'Photo',
      scanningBarcode: 'Scanning barcode...',
      productFound: 'Product found!',
      productNotFound: 'Product not found. Try photo mode!',
      analyzeWithAI: 'Analyse with AI',
      aiConfidence: 'AI Confidence',
      recognition: 'Recognition',
      portionScore: 'Portion',
      nutritionScore: 'Nutrition',
      mealGallery: 'GALLERY',
      totalToday: 'Total Today',
      mealBreakdown: "TODAY'S MEALS",
      all: 'All',
      startDate: 'Start Date',
      endDate: 'End Date',
      illumination: 'ILLUMINATION',
      on: 'ON',
      off: 'OFF',
      switchCamera: 'SWITCH CAMERA',
      mealReview: 'Meal Review',
      confirm: 'Confirm',
      searchFavorites: 'Search favourite meals...',
      noFavorites: 'No favourite meals yet',
      saveFavoritesDesc: 'Save your favourite meals to reuse them later!',
      noMatches: 'No matches found',
      quickLog: 'Quick Log',
      hideTagFilter: 'Hide Tag Filter',
      showTagFilter: 'Show Tag Filter',
      hideDateFilters: 'Hide Date Filters',
      showDateFilters: 'Show Date Filters',
      otherTags: 'Other Tags',
      noMealsYet: 'No meals yet',
      logFirstMeal: 'Log your first meal to see it here!',
      viewDetails: 'View Details',
      sortByPercent: 'Sort by %',
      noDataNutrient: 'No data for this nutrient today',
      salt: 'Salt',
      caffeine: 'Caffeine',
      generateAIImage: 'Generate AI Image',
      expandedNutritionalFacts: 'Expanded Nutritional Facts',
      reanalyze: 'Re-analyse with different AI model',
      tagMode: 'Tag Mode',
      soundEffects: 'Sound Effects',
      confirmClearChat: 'Are you sure you want to clear the chat history?',
      clearChat: 'Clear Chat',
      reviewAndLog: 'Review & Log',
      generate: 'Generate',
      crop: 'Crop',
      adminBugs: 'Bug Reports',
      noBugs: 'No bug reports found.',
      adminDashboard: 'Admin Console',
      users: 'Users',
      stats: 'Stats',
      bugs: 'Bugs',
      noUsers: 'No users found yet.',
      totalUsers: 'Total Users',
      regToday: 'Registered Today',
      latestUser: 'Latest User',
      aiMealSuggestions: 'AI Meal Suggestions',
      aiSuggestionsLoading: 'AI is crafting suggestions for you...',
      aiAnalysis: 'Analysis',
      aiSearchingIdeal: 'AI is crafting suggestions for you...',
      aiUpdating: 'Updating...',
      patiencePlea: 'Hanging in there, just a bit more patience...',
      almostThere: 'Almost there...',
      aiSuggestionsToggle: 'AI Meal Suggestions',
      tripleChecking: 'Triple checking everything...',
      wrappingUp: 'Wrapping up now...',
      duplicateMeal: 'DUPLICATE',
      vibrationOnLogging: 'Haptic Feedback',
      ignoreMealTimeLogging: 'Ignore Meal Time',
      ignoreMealTimeLoggingDesc: 'Stop AI considering real day time when providing nutritional advice.',
      safeChoiceStop: 'Safe Choice: STOP',
      safeChoiceReview: 'Safe Choice: REVIEW',
      safeChoicePassed: 'Safe Choice: PASSED',
      safeChoiceStopDesc: 'Warning: A medical allergen or lifestyle restriction has been detected in this meal!',
      safeChoiceReviewDesc: 'Possible cross-contamination or unverified ingredients. Please check the label carefully.',
      safeChoicePassedDesc: 'This meal is completely safe and aligns perfectly with your active profile.',
      safeChoiceAlert: 'Safe Choice Alert',
      safeChoiceCheck: 'Safe Choice Check',
      favorites: 'FAVOURITES',
      device: 'Device',
      choose: 'Choose',
    }
  },
  'ua': {
    bro: {
        signOut: 'Вийти',
        macrosPieChart: 'Діаграма макросів',
        home: 'ГОЛОВНА',
        takePhoto: 'Фото',
        uploadFromGallery: 'Обрати страву',
        analyze: 'Скануй',
        history: 'ЖУРНАЛ',
        details: 'ІНФО ПРО СТРАВУ',
        aiNutritionist: 'MAIN Nutrition',
        todaysProgress: 'ДЕННИЙ ДВІЖ',
        progressCharts: 'Твій прогрес',
        trackingGoals: 'Стежимо за твоїми цілями, бро',
        active: 'АКТИВНИЙ',
        dailyStreak: 'СЕРІЯ ПОТУГ 🔥',
        idle: 'ЛІНИВИЙ',
        logMeals: 'Запиши чим заправився, бро',
        back: 'Назад',
        next: 'Далі',
        letsGo: 'Погнали!',
        skipOnboarding: 'Пропустити',
        onboardingWelcome: 'Ласкаво просимо!',
        onboardingPersonalization: 'Персоналізація',
        onboardingCoreFeatures: 'Основні функції',
        onboardingBodyMetrics: 'Параметри тіла',
        onboardingActivityGoal: 'Активність та твої цілі',
        onboardingGettingStarted: 'Почнемо!',
        onboardingReady: 'Готовий почати?',
        water: 'Вода',
        waterGoal: 'Ціль по воді',
        addWater: 'Додати пити',
        ml: 'мл',
        waterLogged: 'Заправився водою, чіна!💧',
        progressOverview: 'Огляд прогресу',
        onboardingWelcomeDesc: "Привіт! Я твій новий ШІ-дієтолог.\nГотовий перемагати цілі разом?",
        onboardingGettingStartedDesc: "Перш за все, як мені до тебе звертатися?",
        onboardingNamePlaceholder: "Твоє ім'я",
        onboardingCoreFeaturesDesc: "Легко відстежуй своє харчування за допомогою наших інструментів на базі ШІ.",
        onboardingScanMealsTitle: "Сканування страв",
        onboardingScanMealsDesc: "Просто сфотографуй свою тарілку. Наш ШІ розпізнає інгредієнти та миттєво оцінить порції.",
        onboardingAiChatTitle: "ШІ Чат",
        onboardingAiChatDesc: "Розкажи мені, що ти з'їв, або запитай поради щодо харчування. Це як мати дієтолога у кишені.",
        onboardingAnalyticsTitle: "Детальна аналітика",
        onboardingAnalyticsDesc: "Візуалізуй свій прогрес за допомогою розширених графіків, що відстежують макроси, мікроси та тенденції здоров'я.",
        onboardingBodyMetricsDesc: "Давай розрахуємо твої потреби. Розкажи трохи про себе.",
        onboardingAgeLabel: "Вік",
        onboardingWeightLabel: "Вага (кг)",
        onboardingHeightLabel: "Зріст (см)",
        onboardingGenderLabel: "Стать",
        onboardingMaleLabel: "Чоловік",
        onboardingFemaleLabel: "Жінка",
        onboardingActivityGoalDesc: "Наскільки ти активний і чого хочеш досягти?",
        onboardingActivityLabel: "Рівень активності",
        onboardingGoalLabel: "Ціль",
        onboardingGoalLose: "Схуднути",
        onboardingGoalMaintain: "Підтримувати вагу",
        onboardingGoalGain: "Набрати вагу",
        onboardingGoalLoseDesc: "Стійка втрата жиру",
        onboardingGoalMaintainDesc: "Збереження поточного балансу",
        onboardingGoalGainDesc: "Набір м'язової маси",
        onboardingTargetMetricsDesc: "Встанови свої денні цілі. Залиш порожнім для автоматичного розрахунку.",
        onboardingPersonalizationDesc: "Обери мову та тему застосунку.",
        loginSlogan: "Твій ШІ-дієтолог. Трекуй свої здобутки в один клік.",
        signInWithGoogle: "Увійти через Google",
        agreementText: "Входячи, ти погоджуєшся з нашими умовами та політикою конфіденційності.",
        onboardingVibeLabel: "Вайб",
        onboardingVibeRegular: "Стандартний",
        onboardingVibeCasual: "Казуальний",
        onboardingReadyDesc: "Ми все розрахували для тебе.\nДавай зробимо кожну калорію важливою!",
        activitySedentary: 'Сидячий',
        activityLight: 'Легкий',
        activityModerate: 'Помірний',
        activityActive: 'Активний',
        activityVeryActive: 'Дуже активний',
        activitySedentaryDesc: 'Офісна робота, мало вправ або їх відсутність',
        activityLightDesc: '1-3 дні вправ на тиждень',
        activityModerateDesc: '3-5 днів вправ на тиждень',
        activityActiveDesc: '6-7 днів вправ на тиждень',
        activityVeryActiveDesc: 'Фізична робота або інтенсивні тренування',
        breakfast: 'СНІДАНОК',
        lunch: 'ОБІД',
        dinner: 'ВЕЧЕРЯ',
        dailyProgress: 'ПРОГРЕС ЗА ДЕНЬ',
        progressTargets: 'Як ти наближаєшся до цілей, друже',
        starsAchievements: 'Зоряні Досягнення',
        stars: 'Зірки',
        calories: 'Калорії',
        protein: 'Білки',
        carbs: "Вуглеводи",
        fat: 'Жири',
        totalCalories: 'Всього Калорій',
        nutritionalSummary: 'Баланс макросів',
        foodItems: 'СКЛАД СТРАВИ',
        sugar: 'Цукор',
        fiber: "Клітковина",
        sodium: 'Сіль',
        potassium: 'Калій',
        cholesterol: 'Холестерин',
        vitaminC: 'Вітамін C',
        calcium: 'Кальцій',
        iron: 'Залізо',
        nutritionFacts: 'ХАРЧОВА ЦІННІСТЬ',
        amountPerServing: 'Вміст на порцію',
        totalFat: 'Всього жирів',
        saturatedFat: 'Насичені жири',
        totalCarbohydrate: 'Всього вуглеводів',
        dvNote: '* Відсоток денної норми (ДН) показує, скільки поживних речовин обчислюється від загального раціону (2000 калорій на день).',
        updatedProgress: 'ОНОВЛЕНИЙ ДВІЖ',
        analyzeMeal: 'Скануй',
        editMeal: 'ПРАВИТИ СТРАВУ',
        saveChanges: 'Готово',
        mealName: 'НАЗВА',
        mealType: 'ТИП',
        foodName: 'ПРОДУКТ',
        portion: 'ПОРЦІЯ',
        amount: 'КІЛЬКІСТЬ',
        unit: 'ОД. ВИМІРУ',
        addItem: 'ДОДАТИ ЩЕ',
        remove: 'ВИКИНУТИ',
        deleteMeal: 'ВИДАЛИТИ',
        dateTime: 'КОЛИ',
        mealLoggedSuccess: 'Гарна робота, бро!👊',
        mealUpdatedSuccess: 'Оновлено! Так тримати, бро!👍',
        viewHistory: 'ЖУРНАЛ СТРАВ',
        recentMeals: 'ЩО ВЖЕ ЗАКИНУВ',
        noMealsLogged: 'Ще не логував їжу. Зафоткай першу страву, бро!',
        uploadPhoto: 'ЗАВАНТАЖУЙ ФОТО',
        snapMeal: 'Фоткай їжу, щоб побачити макроси',
        uploadSnapMeal: 'Завантажуй фото їжі, щоб побачити макроси',
        changePhoto: 'ЗМІНИТИ ФОТО',
        analyzingMeal: 'Аналізую, бро...',
        secFocusing: "Сек, фокусуюсь...",
        countingMacros: "Рахую макроси...",
        generatingVisual: "Генерую зображення...",
        bringingTogether: "Зводжу результати...",
        almostReady: "Майже готово...",
        noodling: 'Починаю двіж...',
        daydreaming: 'Дрімаю...',
        scanningImage: 'Сканую пікселі на фото...',
        detectingFood: 'Дивлюсь, що в тарілці',
        estimatingPortions: 'Прикидаю розмір',
        identifyingItems: 'Визначаю продукти...',
        checkingDatabases: 'Перевіряю бази...',
        calculatingMacros: 'Логую, тримайся...',
        balancingMacros: 'Балансую оцінку макросів...',
        generatingInsights: 'Готую бро-поради...',
        finalizingResults: 'Звожу до купи результати...',
        reviewAnalysis: 'ПЕРЕВІРКА СТРАВИ',
        confirmItems: 'ПІДТВЕРДИТИ',
        editItems: 'РЕДАГУВАТИ',
        isThisCorrect: 'Це те, що ти з\'їв, бро?',
        mealHistory: 'ЖУРНАЛ',
        entries: 'записів',
        noHistory: 'Порожньо, бро',
        historyAppear: 'Тут буде твоя їжа, бро',
        startAnalyzing: 'ДАВАЙ СКАНУВАТИ',
        skins: 'СКІНИ',
        cyberpunk: 'Neon Protocol',
        anime: 'Anime',
        stalker: 'S.T.A.L.K.E.R.',
        bioSyncStatus: 'БІО-СИНХРОНІЗАЦІЯ',
        coolantLevel: 'РІВЕНЬ ОХОЛОДЖУВАЧА',
        setGoals: 'СТАВ ЦІЛІ',
        settings: 'МІЙ АКАУНТ',
        yourTargetsAndSettings: 'Ваші цілі та налаштування',
        saveGoals: 'Готово',
        cancel: 'Та ну',
        reset: 'СКИНУТИ',
        personalization: 'ПЕРСОНАЛІЗАЦІЯ',
        goalsAndTargets: 'ЦІЛІ ТА ЗАВДАННЯ',
        personalData: 'Особисті дані',
        weightToLose: 'Скільки кг скинути',
        loseWeightTooltip: 'Жодних легких шляхів, бро! Справжня праця потребує часу, але ця схема паше на всі 100% завжди!',
        kcal: 'ккал',
        proteinShort: 'Б',
        carbsShort: 'В',
        fatShort: 'Ж',
        snack: 'ПЕРЕКУС',
        retry: 'СЕКУНДУ, ЩЕ РАЗ',
        errorFailed: 'Не вдалося сканувати, бро. Спробуй ще раз.',
        errorNoFood: 'Бро..( Це не їжа! ',
        startCamera: 'ВІДКРИЙ КАМЕРУ, БРО',
        capturePhoto: 'ФОТО',
        stopCamera: 'ЗАКРИТИ КАМЕРУ',
        theme: 'ТЕМА',
        light: 'СВІТЛА',
        dark: 'ТЕМНА',
        resetHistory: 'ОЧИСТИТИ ЖУРНАЛ',
        confirmReset: 'Впевнений, що хочеш очистити журнал, бро?',
        yesReset: 'ТАК, ВИДАЛЯЙ',
        noCancel: 'НІ, ЗАЛИШ',
        confirmRemoveItem: 'Викинути це, бро?',
        nutritionalInsight: 'Порада бро-дієтолога',
        portionAnalysis: 'Порада щодо порції',
        askAI: 'Запитати ШІ',
        addMeal: 'Додати їсти',
        manualEntry: 'Напиши сам',
        addMealManually: 'Напиши страву вручну',
        uploadImage: 'Завантажуй фото',
        replaceImage: 'Замінити фото',
        removeImage: 'Видалити фото',
        noImage: 'Без фото',
        mealNamePlaceholder: 'напр., Величезний стейк',
        enterNutrients: 'Напиши макроси, бро',
        chatWithAI: 'Давай поговоримо',
        chatPlaceholder: 'Напиши, що ти з’їв (напр., 3 яйця і протеїн)...',
        chatPrompt: 'Напиши, що ти з’їв',
        listening: 'Слухаю, бро...',
        stopListening: 'Стоп',
        voiceError: 'Не чую тебе, бро. Перевір дозволи.',
        analyzingText: 'Аналізую, бро...',
        notFoodError: 'Бро..( Це не їжа! ',
        goToToday: 'На сьогодні',
        reminderTitle: 'ЕЙ, БРО!',
        reminderMessage: 'Запиши чим заправився, бро 🥗',
        progressReminder: 'Бро, ти закинув менше 50% калорій за сьогодні. Час підзарядитись! ⚡',
        logNow: 'Записати зараз',
        dailyReminder: 'Щоденне нагадування',
        reminderTime: 'Час нагадування',
        days: 'днів',
        streakSuffix: 'серія',
        category: 'Категорія',
        language: 'Мова',
        vibe: 'Вайб',
        standard: 'Регулярний',
        casual: 'Казуальний',
        timeFormat: 'Формат часу',
        timeFormat12h: '12г',
        timeFormat24h: '24г',
        settingsSaved: 'Налаштування збережено!👊',
        historyResetSuccess: 'Журнал очищено!👊',
        undo: 'СКАСУВАТИ',
        actionUndone: 'Дію скасовано!👊',
        permissions: 'ДОЗВОЛИ ДОДАТКУ',
        cameraPermission: 'Дозвіл на камеру',
        micPermission: 'Дозвіл на мікрофон',
        requestPermission: 'Запитати доступ',
        permissionGranted: 'Надано',
        permissionDenied: 'Відмовлено',
        checkPermissions: 'Перевірити дозволи',
        searchPlaceholder: 'Шукати страви або продукти...',
        sortBy: 'Сорт.',
        sortDate: 'Дата',
        sortCalories: 'Калорії',
        sortName: 'Назва',
        sortTag: 'Тег',
        viewProgress: 'Переглянути прогрес',
        daily: 'День',
        weekly: 'Тиждень',
        monthly: 'Місяць',
        progressChart: 'Графік прогресу',
        calorieProgress: 'Прогрес по калоріях',
        proteinProgress: 'Прогрес по білках',
        carbsProgress: 'Прогрес по вуглеводах',
        fatProgress: 'Прогрес по жирах',
        waterProgress: 'Споживання рідини',
        microProgress: 'Інші нутрієнти',
        macroDistribution: 'Розподіл макросів',
        foodExplanation: 'Пояснення про продукт',
        whyThisFood: 'Чому саме цей продукт?',
        fairEnough: 'Добре',
        logThisMeal: 'Записати',
        saveToFavorites: 'ДО УЛЮБЛЕНИХ',
        adjustAmount: 'ЗМІНИТИ ПОРЦІЮ',
        adjustPortion: 'НАЛАШТУВАТИ ПОРЦІЮ',
        amountPlaceholder: 'напр., 250',
        totalAmountPlaceholder: 'Загальна кількість (напр., 2 штуки, 300г)',
        barcodeMode: 'ШТРИХ-КОД',
        photoMode: 'ФОТО',
        scanningBarcode: 'Скануємо штрих-код...',
        productFound: 'Продукт знайдено!👊',
        productNotFound: 'Продукт не знайдено. Спробуй через фото!',
        analyzeWithAI: 'Аналізувати з ШІ',
        mealGallery: 'ГАЛЕРЕЯ СТРАВ',
        totalToday: 'Всього за сьогодні',
        mealBreakdown: 'ДЕННИЙ РОЗКЛАД',
        all: 'УСЕ',
        startDate: 'ПОЧАТКОВА ДАТА',
        endDate: 'КІНЦЕВА ДАТА',
        illumination: 'Ілюмінація',
        on: 'УВІМК',
        off: 'ВИМК',
        switchCamera: 'ЗМІНИТИ КАМЕРУ',
        mealReview: 'ПЕРЕВІРКА СТРАВИ',
        mealReviewTooltip: 'Коли увімкнено, ти можеш перевірити та підправити аналіз ШІ перед записом.',
        confirm: 'Підтвердити',
        searchFavorites: 'Шукати в обраному...',
        noFavorites: 'Ще немає обраних страв',
        saveFavoritesDesc: 'Зберігай улюблені страви, щоб додавати їх швидше!',
        noMatches: 'Нічого не знайдено',
        quickLog: 'Швидкий запис',
        hideTagFilter: 'Сховати фільтр тегів',
        showTagFilter: 'Фільтр тегів',
        hideDateFilters: 'Сховати фільтр дат',
        showDateFilters: 'Фільтр дат',
        otherTags: 'Інші теги',
        noMealsYet: 'Ще немає страв',
        logFirstMeal: 'Запиши першу страву, бро!',
        viewDetails: 'Глянути деталі',
        sortByPercent: 'Сорт. за %',
        noDataNutrient: 'Немає даних за цей день',
        salt: 'Сіль',
        caffeine: 'Кофеїн',
        macro: 'Макро',
        micro: 'Мікро',
        generateAIImage: 'ШІ-Генерація фото',
        expandedNutritionalFacts: 'Детальна цінність',
        reanalyze: 'Пересканувати ШІ',
        tagMode: 'Режим тегів',
        soundEffects: 'Звукові ефекти',
        confirmClearChat: 'Впевнений, що хочеш очистити історію чату?',
        clearChat: 'Очистити чат',
        reviewAndLog: 'Перевірити та зберегти',
        generate: 'Згенерувати',
        crop: 'Обрізати',
        itsAMatch: "ВОУ, ЦЕ МЕТЧ!",
        mutualLike: 'Взаємний лайк у чаті',
        awwAmazing: 'Круто! Ви на одній хвилі👊',
        imageAttached: 'ФОТО ДОДАНО',
        sentAnImage: '[Надіслав фото]',
        aiMealSuggestions: 'ІДЕЇ ПОЇСТИ ВІД ШІ',
        aiSuggestionsLoading: 'Зараз накидаю тобі варіанти, бро...',
        aiAnalysis: 'Аналіз',
        aiSearchingIdeal: 'Шукаю ідеальні варіанти для тебе...',
        aiUpdating: 'Оновлюю...',
        aiConfidence: 'Впевненість ШІ',
        recognition: 'Розпізнавання',
        portionScore: 'Порція',
        nutritionScore: 'Цінність',
        patiencePlea: 'Ще секунду, маю знайти найкраще...',
        almostThere: 'Майже готово, бро...',
        aiSuggestionsToggle: 'ІДЕЇ ПОЇСТИ ВІД ШІ',
        tripleChecking: 'Перевіряю все тричі, бро...',
        wrappingUp: 'Ну все, фіналізую, бро...',
        duplicateMeal: 'ДУБЛЮВАТИ',
        vibrationOnLogging: 'Вібрація',
        ignoreMealTimeLogging: 'Ігнорувати час страви',
        ignoreMealTimeLoggingDesc: 'ШІ не зважатиме на реальний час для порад.',
        safeChoiceStop: 'Safe Choice: СТОП',
        safeChoiceReview: 'Safe Choice: УВАГА',
        safeChoicePassed: 'Safe Choice: ПРОЙДЕНО',
        safeChoiceStopDesc: 'Увага: У цій страві виявлено медичний алерген або обмеження способу життя!',
        safeChoiceReviewDesc: 'Можлива перехресна контамінація. Будь ласка, перевірте етикетку.',
        safeChoicePassedDesc: 'Ця страва повністю безпечна та чудово відповідає вашому активному профілю.',
        safeChoiceAlert: 'Safe Choice Увага',
        safeChoiceCheck: 'Safe Choice Чек',
        favorites: 'Улюблені страви',
        device: 'Девайс',
        choose: 'Вибери',
        adminDashboard: 'АДМІН ПАНЕЛЬ',
        users: 'Користувачі',
        stats: 'Статистика',
        bugs: 'Баги',
        adminBugs: 'Звіти про баги',
        noUsers: 'Користувачів ще не зареєстровано, бро.',
        noBugs: 'Багів не знайдено, все чітко!👊',
        totalUsers: 'Всього юзерів',
        regToday: 'Залетіли сьогодні',
        latestUser: 'Свіжий бро',
        sendingToAI: 'Закидаю в ШІ...',
        analyzingNutrition: 'Розбираю нутрієнти...',
        calculatingFinalStats: 'Підбиваю підсумки...',
      },
      standard: {
        signOut: 'Вийти',
        onboardingGoalGain: "Набрати вагу",
        onboardingGoalLoseDesc: "Стійка втрата жиру",
        onboardingGoalMaintainDesc: "Збереження поточного балансу",
        onboardingGoalGainDesc: "Набір м'язової маси",
        onboardingTargetMetricsDesc: "Встановіть свої денні цілі. Залиште порожнім для автоматичного розрахунку.",
        onboardingPersonalizationDesc: "Оберіть мову та тему застосунку.",
        loginSlogan: "Ваш помічник з харчування.\nВідстежуйте свої здобутки одним фото.",
        signInWithGoogle: "Увійти через Google",
        agreementText: "Входячи, ви погоджуєтеся з нашими умовами та політикою конфіденційності.",
        onboardingVibeLabel: "Вайб",
        onboardingVibeRegular: "Стандартний",
        onboardingVibeCasual: "Казуальний",
        onboardingReadyDesc: "Ми все розрахували для вас.\nДавайте зробимо кожну калорію важливою!",
        active: 'АКТИВНИЙ',
        dailyStreak: 'СЕРІЯ 🔥',
        idle: 'В ОЧІКУВАННІ',
        breakfast: 'СНІДАНОК',
        lunch: 'ОБІД',
        dinner: 'ВЕЧЕРЯ',
        todaysProgress: 'ДЕННИЙ ПРОГРЕС',
        dailyProgress: 'ДЕННИЙ ПРОГРЕС',
        progressTargets: 'Ваш прогрес до денних цілей',
        starsAchievements: 'Зоряні досягнення',
        stars: 'Зірки',
        calories: 'КАЛОРІЇ',
        protein: 'Білки',
        carbs: 'Вуглеводи',
        fat: 'Жири',
        totalCalories: 'Всього калорій',
        nutritionalSummary: 'Харчова цінність',
        foodItems: 'СКЛАД СТРАВИ',
        sugar: 'Цукор',
        fiber: 'Клітковина',
        sodium: 'Сіль',
        potassium: 'Калій',
        cholesterol: 'Холестерин',
        vitaminC: 'Вітамін C',
        calcium: 'Кальцій',
        iron: 'Залізо',
        nutritionFacts: 'ХАРЧОВА ЦІННІСТЬ',
        amountPerServing: 'Вміст на порцію',
        totalFat: 'Всього жирів',
        saturatedFat: 'Насичені жири',
        totalCarbohydrate: 'Всього вуглеводів',
        dvNote: '* Відсоток денної норми (ДН) показує, скільки поживних речовин обчислюється від загального раціону (2000 калорій на день).',
        water: 'Вода',
        ml: 'мл',
        updatedProgress: 'ОНОВЛЕНИЙ ПРОГРЕС',
        analyzeMeal: 'АНАЛІЗУВАТИ',
        editMeal: 'РЕДАГУВАТИ СТРАВУ',
        saveChanges: 'Готово',
        mealName: 'НАЗВА СТРАВИ',
        mealType: 'ТИП СТРАВИ',
        foodName: 'ПРОДУКТ',
        portion: 'ПОРЦІЯ',
        amount: 'Кількість',
        unit: 'Одиниця',
        logThisMeal: 'Записати страву',
        saveToFavorites: 'ДО УЛЮБЛЕНИХ',
        adjustPortion: 'Налаштувати порцію',
        nutritionalInsight: 'Порада ШІ нутриціолога',
        portionAnalysis: 'Аналіз порції',
        addItem: 'ДОДАТИ ЕЛЕМЕНТ',
        remove: 'ВИДАЛИТИ',
        deleteMeal: 'ВИДАЛИТИ СТРАВУ',
        dateTime: 'ДАТА ТА ЧАС',
        mealLoggedSuccess: 'Страву успішно записано!',
        mealUpdatedSuccess: 'Страву успішно оновлено!',
        viewHistory: 'ПЕРЕГЛЯНУТИ ЖУРНАЛ',
        recentMeals: 'Страви за сьогодні',
        noMealsLogged: 'Ще немає записів. Проаналізуйте свою першу страву!',
        uploadPhoto: 'ЗАВАНТАЖИТИ ФОТО',
        snapMeal: 'Сфотографуйте страву',
        uploadSnapMeal: 'Завантажте фото страви',
        changePhoto: 'ЗМІНИТИ ФОТО',
        analyzingMeal: 'Аналіз',
        secFocusing: "Секунду, аналізую та фокусуюсь...",
        countingMacros: "Розраховую нутрієнти...",
        generatingVisual: "Генерую зображення...",
        bringingTogether: "Об'єдную результати...",
        almostReady: "Майже готово...",
        noodling: 'Обробка...',
        daydreaming: 'В роздумах...',
        scanningImage: 'Сканую...',
        detectingFood: 'Шукаю їжу...',
        estimatingPortions: 'Оцінюю порцію...',
        identifyingItems: 'Ідентифікація...',
        checkingDatabases: 'Шукаю в базі...',
        calculatingMacros: 'Записую вашу страву...',
        balancingMacros: 'Балансую...',
        generatingInsights: 'Генерую поради...',
        finalizingResults: 'Завершую...',
        reviewAnalysis: 'ПЕРЕВІРКА СТРАВИ',
        confirmItems: 'ПІДТВЕРДИТИ',
        editItems: 'РЕДАГУВАТИ',
        isThisCorrect: 'Це те, що ви з\'їли?',
        mealHistory: 'ЖУРНАЛ',
        entries: 'записів',
        noHistory: 'Журнал порожній',
        historyAppear: 'Ваш журнал страв з\'явиться тут',
        addMeal: 'Додати',
        startAnalyzing: 'ПОЧАТИ АНАЛІЗ',
        settings: 'МІЙ АКАУНТ',
        saveGoals: 'Готово',
        cancel: 'СКАСУВАТИ',
        reset: 'СКИНУТИ',
        chatWithAI: 'Спілкування',
        chatPlaceholder: 'Що ви з\'їли (напр., 3 яйця і протеїн)?',
        chatPrompt: 'Розкажіть про ваш прийом їжі',
        analyzingText: 'Аналіз тексту...',
        notFoodError: 'Це не схоже на їжу.',
        errorFailed: 'Помилка аналізу. Спробуйте ще раз.',
        aiMealSuggestions: 'РЕКОМЕНДАЦІЇ СТРАВ ВІД ШІ',
        aiSuggestionsLoading: 'AI готує пропозиції для вас...',
        aiAnalysis: 'Аналіз',
        aiSearchingIdeal: 'Шукаю ідеальні варіанти для вас...',
        aiUpdating: 'Оновлення...',
        patiencePlea: 'Ще секунду, маю знайти найкраще...',
        almostThere: 'Майже готово...',
        aiSuggestionsToggle: 'РЕКОМЕНДАЦІЇ СТРАВ ВІД ШІ',
        tripleChecking: 'Перевіряю все тричі...',
        aiConfidence: 'Впевненість ШІ',
        recognition: 'Розпізнавання',
        portionScore: 'Порція',
        nutritionScore: 'Цінність',
        wrappingUp: 'Завершення...',
        duplicateMeal: 'ДУБЛЮВАТИ',
        sentAnImage: '[Надіслано зображення]',
        clearChat: 'Очистити чат',
        confirmClearChat: 'Ви впевнені, що хочете очистити чат?',
        macro: 'Макро',
        micro: 'Мікро',
        vibrationOnLogging: 'Вібрація',
        ignoreMealTimeLogging: 'Ігнорувати час прийому',
        ignoreMealTimeLoggingDesc: 'ШІ не зважатиме на реальний час для порад.',
        safeChoiceStop: 'Safe Choice: СТОП',
        safeChoiceReview: 'Safe Choice: УВАГА',
        safeChoicePassed: 'Safe Choice: ПРОЙДЕНО',
        safeChoiceStopDesc: 'Увага: У цій страві виявлено медичний алерген або обмеження способу життя!',
        safeChoiceReviewDesc: 'Можлива перехресна контамінація або неперевірені інгредієнти. Будь ласка, уважно перевірте етикетку.',
        safeChoicePassedDesc: 'Ця страва повністю безпечна та чудово відповідає вашому активному профілю.',
        safeChoiceAlert: 'Попередження Safe Choice',
        safeChoiceCheck: 'Перевірка Safe Choice',
        home: 'ГОЛОВНА',
        takePhoto: 'Фото',
        uploadFromGallery: 'Обрати з галереї',
        analyze: 'АНАЛІЗУВАТИ',
        history: 'ЖУРНАЛ',
        details: 'ДЕТАЛІ',
        aiNutritionist: 'MAIN Nutrition',
        trackingGoals: 'Відстежуємо ваші щоденні цілі',
        logMeals: 'Записуйте свої прийоми їжі',
        onboardingWelcome: 'Ласкаво просимо до MAIN Nutrition!',
        onboardingPersonalization: 'Персоналізація',
        onboardingCoreFeatures: 'Основні функції',
        onboardingBodyMetrics: 'Параметри тіла',
        onboardingActivityGoal: 'Активність та цілі',
        onboardingGettingStarted: 'Почнемо!',
        onboardingReady: 'Готові почати?',
        onboardingWelcomeDesc: 'Привіт! Я ваш новий ШІ-дієтолог.\nГотові досягати цілей разом?',
        onboardingGettingStartedDesc: 'Перш за все, як до вас звертатися?',
        onboardingNamePlaceholder: "Ваше ім'я",
        onboardingCoreFeaturesDesc: 'Легко відстежуйте своє харчування за допомогою наших інструментів на базі ШІ.',
        onboardingScanMealsTitle: 'Сканування страв',
        onboardingScanMealsDesc: 'Просто сфотографуйте свою тарілку. Наш ШІ розпізнає інгредієнти та оцінить порції миттєво.',
        onboardingAiChatTitle: 'ШІ Чат',
        onboardingAiChatDesc: "Напишіть, що ви з'їли, або запитайте поради щодо харчування. Це як мати дієтолога у кишені.",
        onboardingAnalyticsTitle: 'Детальна аналітика',
        onboardingAnalyticsDesc: "Візуалізуйте свій прогрес за допомогою розширених графіків, що відстежують макроси, мікроелементи та тенденції здоров'я.",
        onboardingBodyMetricsDesc: 'Давайте розрахуємо ваші потреби. Розкажіть трохи про себе.',
        onboardingAgeLabel: 'Вік',
        onboardingWeightLabel: 'Вага (кг)',
        onboardingHeightLabel: 'Зріст (см)',
        onboardingGenderLabel: 'Стать',
        onboardingMaleLabel: 'Чоловік',
        onboardingFemaleLabel: 'Жінка',
        onboardingActivityGoalDesc: 'Наскільки ви активні і чого хочете досягти?',
        onboardingActivityLabel: 'Рівень активності',
        onboardingGoalLabel: 'Ціль',
        onboardingGoalLose: 'Схуднути',
        onboardingGoalMaintain: 'Підтримувати вагу',
        macrosPieChart: 'Діаграма макросів',
        sendingToAI: 'Відправляємо до ШІ...',
        analyzingNutrition: 'Аналізуємо харчову цінність...',
        calculatingFinalStats: 'Розраховуємо фінальні показники...',
        skins: 'СКІНИ',
        cyberpunk: 'Neon Protocol',
        anime: 'Аніме',
        stalker: 'S.T.A.L.K.E.R.',
        bioSyncStatus: 'БІО-СИНХРОНІЗАЦІЯ',
        coolantLevel: 'РІВЕНЬ ВОДИ',
        setGoals: 'ЦІЛІ ТА НАЛАШТУВАННЯ',
        yourTargetsAndSettings: 'Ваші цілі та налаштування',
        personalization: 'Персоналізація',
        goalsAndTargets: 'Цілі',
        personalData: 'Особисті дані',
        weightToLose: 'Скільки кг скинути',
        loseWeightTooltip: 'Швидких результатів не буває. Справжні зусилля потребують часу, але цей метод працює на 100% завжди.',
        kcal: 'ккал',
        proteinShort: 'Б',
        carbsShort: 'В',
        fatShort: 'Ж',
        snack: 'ПЕРЕКУС',
        retry: 'ПОВТОРИТИ',
        errorNoFood: 'Це не схоже на їжу.',
        startCamera: 'ВІДКРИТИ КАМЕРУ',
        capturePhoto: 'ФОТО',
        stopCamera: 'ЗАКРИТИ КАМЕРУ',
        theme: 'ТЕМА',
        light: 'СВІТЛА',
        dark: 'ТЕМНА',
        resetHistory: 'ОЧИСТИТИ ЖУРНАЛ',
        confirmReset: 'Ви впевнені, що хочете очистити журнал? Цю дію неможливо скасувати.',
        yesReset: 'ТАК, ОЧИСТИТИ',
        noCancel: 'НІ, СКАСУВАТИ',
        confirmRemoveItem: 'Видалити цей елемент?',
        askAI: 'Запитати ШІ',
        manualEntry: 'Ручне введення',
        waterGoal: 'Ціль води',
        addWater: 'Додати воду',
        waterLogged: 'Воду успішно записано!💧',
        addMealManually: 'Введіть страву вручну',
        uploadImage: 'Завантажити зображення',
        replaceImage: 'Замінити зображення',
        removeImage: 'Видалити зображення',
        noImage: 'Немає зображення',
        mealNamePlaceholder: 'напр., Салат з куркою',
        enterNutrients: 'Ввести дані вручну',
        listening: 'Слухаю...',
        stopListening: 'Зупинити',
        voiceError: 'Помилка розпізнавання голосу. Перевірте дозволи.',
        goToToday: 'Повернутися до сьогодні',
        reminderTitle: 'Нагадування',
        reminderMessage: 'Не забудьте записати свої прийоми їжі сьогодні! 🥗',
        progressReminder: 'Денний прогрес: Ви записали менше 50% своєї денної цілі калорій. 🍏',
        logNow: 'Записати зараз',
        dailyReminder: 'Щоденне нагадування',
        reminderTime: 'Час нагадування',
        category: 'Категорія',
        language: 'Мова',
        vibe: 'Вайб',
        standard: 'Стандартний',
        casual: 'Казуальний',
        settingsSaved: 'Налаштування успішно збережено!',
        historyResetSuccess: 'Журнал успішно очищено!',
        undo: 'СКАСУВАТИ',
        actionUndone: 'Дію скасовано!',
        searchPlaceholder: 'Пошук страв...',
        sortBy: 'Сортувати за',
        sortDate: 'Датою',
        sortCalories: 'Калоріями',
        sortName: 'Назвою',
        sortTag: 'Тегом',
        viewProgress: 'Переглянути прогрес',
        daily: 'День',
        weekly: 'Тиждень',
        monthly: 'Місяць',
        progressChart: 'Графік прогресу',
        calorieProgress: 'Прогрес калорій',
        proteinProgress: 'Прогрес білків',
        carbsProgress: 'Прогрес вуглеводів',
        fatProgress: 'Прогрес жирів',
        waterProgress: 'Споживання рідини',
        microProgress: 'Інші нутрієнти',
        macroDistribution: 'Розподіл макросів',
        foodExplanation: 'Пояснення',
        whyThisFood: 'Чому ця їжа?',
        fairEnough: 'Добре',
        adjustAmount: 'Налаштувати кількість',
        amountPlaceholder: 'напр., 250',
        totalAmountPlaceholder: 'Загальна кількість (напр., 2 шматки, 300г)',
        barcodeMode: 'Штрих-код',
        photoMode: 'Фото',
        scanningBarcode: 'Сканування штрих-коду...',
        productFound: 'Продукт знайдено!',
        productNotFound: 'Продукт не знайдено. Спробуйте режим фото!',
        analyzeWithAI: 'Аналізувати за допомогою ШІ',
        mealGallery: 'ГАЛЕРЕЯ СТРАВ',
        totalToday: 'Всього сьогодні',
        mealBreakdown: 'СТРАВИ ЗА СЬОГОДНІ',
        all: 'Всі',
        startDate: 'Початкова дата',
        endDate: 'Кінцева дата',
        illumination: 'Ілюмінація',
        on: 'УВІМК.',
        off: 'ВИМК.',
        switchCamera: 'ЗМІНИТИ КАМЕРУ',
        mealReview: 'Перевірка страви',
        confirm: 'Підтвердити',
        searchFavorites: 'Пошук улюблених страв...',
        noFavorites: 'Ще немає улюблених страв',
        saveFavoritesDesc: 'Зберігайте свої улюблені страви, щоб використовувати їх пізніше!',
        noMatches: 'Збігів не знайдено',
        quickLog: 'Швидкий запис',
        hideTagFilter: 'Сховати фільтр тегів',
        showTagFilter: 'Фільтр тегів',
        hideDateFilters: 'Сховати фільтр дат',
        showDateFilters: 'Фільтр дат',
        otherTags: 'Інші теги',
        noMealsYet: 'Ще немає страв',
        logFirstMeal: 'Запишіть свою першу страву, щоб побачити її тут!',
        viewDetails: 'Деталі',
        sortByPercent: 'Сортувати за %',
        noDataNutrient: 'Немає даних для цього нутрієнта сьогодні',
        salt: 'Сіль',
        caffeine: 'Кофеїн',
        generateAIImage: 'Згенерувати зображення ШІ',
        expandedNutritionalFacts: 'Розширена харчова цінність',
        reanalyze: 'Переаналізувати з іншою ШІ моделлю',
        tagMode: 'Режим тегів',
        soundEffects: 'Звукові ефекти',
        reviewAndLog: 'Перевірити та зберегти',
        generate: 'Згенерувати',
        crop: 'Обрізати',
        adminBugs: 'Звіти про помилки',
        noBugs: 'Звітів про помилки не знайдено.',
        adminDashboard: 'Адмін Консоль',
        users: 'Користувачі',
        stats: 'Статистика',
        bugs: 'Помилки',
        noUsers: 'Користувачів ще не знайдено.',
        totalUsers: 'Всього користувачів',
        regToday: 'Зареєстровано сьогодні',
        latestUser: 'Останній користувач',
        favorites: 'УЛЮБЛЕНІ',
        device: 'Завантажити',
        choose: 'Виберіть',
        progressCharts: 'Графіки прогресу',
        back: 'Назад',
        next: 'Далі',
        letsGo: 'Розпочати',
        skipOnboarding: 'Пропустити знайомство',
        activitySedentary: 'Сидячий',
        activityLight: 'Помірно активний',
        activityModerate: 'Середня активність',
        activityActive: 'Висока активність',
        activityVeryActive: 'Дуже висока активність',
        activitySedentaryDesc: 'Офісна робота, мінімум фізичних навантажень',
        activityLightDesc: 'Легкі тренування 1-3 рази на тиждень',
        activityModerateDesc: 'Тренування 3-5 разів на тиждень',
        activityActiveDesc: 'Інтенсивні тренування 6-7 разів на тиждень',
        activityVeryActiveDesc: 'Важка фізична праця або щоденні інтенсивні тренування',
        progressOverview: 'Огляд прогресу',
        days: 'днів',
        streakSuffix: 'серія',
        timeFormat: 'Формат часу',
        timeFormat12h: '12 год',
        timeFormat24h: '24 год',
        permissions: 'ДОЗВОЛИ ДОДАТКА',
        cameraPermission: 'Дозвіл на доступ до камери',
        micPermission: 'Дозвіл на доступ до мікрофона',
        requestPermission: 'Запитати доступ',
        permissionGranted: 'Надано',
        permissionDenied: 'Відхилено',
        checkPermissions: 'Перевірити дозволи',
        mealReviewTooltip: 'Коли увімкнено, ви можете переглянути та відредагувати аналіз ШІ перед збереженням страви.',
        itsAMatch: 'СПІВПАДІННЯ ЗНАЙДЕНО!',
        mutualLike: 'Взаємне вподобання',
        awwAmazing: 'Чудово! Спільні вподобання знайдено',
        imageAttached: 'ФОТО ПРИКРІПЛЕНО',
      }
    }
};

const DEFAULT_GOALS: UserGoals = {
  calories: 2000,
  protein: 150,
  carbs: 250,
  fat: 70,
  water_ml: 2500,
  sugar_g: 50,
  fiber_g: 20,
  sodium_mg: 2000,
  caffeine_mg: 400
};

const DEFAULT_CONFETTI_COLORS = ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff'];

const MASTERY_BADGES = [
  { days: 3, icon: Flame, name: '3-Day Fire', color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  { days: 7, icon: Zap, name: '7-Day Spark', color: 'text-yellow-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  { days: 14, icon: Star, name: '14-Day Star', color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  { days: 21, icon: Target, name: '21-Day Focus', color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  { days: 30, icon: Shield, name: '30-Day Shield', color: 'text-indigo-500', bg: 'bg-indigo-500/10', border: 'border-indigo-500/20' },
  { days: 60, icon: Trophy, name: '60-Day Champion', color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  { days: 90, icon: Award, name: '90-Day Royal', color: 'text-indigo-500 dark:text-indigo-400', bg: 'animate-premium-gradient-bg', border: 'border-indigo-500/20 dark:border-purple-500/20' },
  { days: 100, icon: Sparkles, name: '100-Day Legend', color: 'text-rose-500', bg: 'bg-rose-500/10', border: 'border-rose-500/20' }
];

const AnimatedValue = ({ 
  value, 
  format 
}: { 
  value: number; 
  format: (v: number) => string; 
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const prevValueRef = useRef(value);
  const [isUpdating, setIsUpdating] = useState(false);
  const [direction, setDirection] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    const prev = prevValueRef.current;
    if (prev === value) return;

    setDirection(value > prev ? 'up' : 'down');
    setIsUpdating(true);

    const startTime = performance.now();
    const duration = 300; // 300ms duration for rapid and highly responsive update transitions

    let animationFrameId: number;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeProgress = progress * (2 - progress); // Ease out quad
      const currentValue = prev + (value - prev) * easeProgress;
      
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
        setIsUpdating(false);
        setDirection(null);
      }
    };

    animationFrameId = requestAnimationFrame(animate);
    prevValueRef.current = value;

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [value]);

  const targetColor = direction === 'up' ? '#10b981' : direction === 'down' ? '#ef4444' : undefined;

  return (
    <motion.span
      className="inline-block"
      animate={isUpdating ? {
        scale: [1, 1.2, 0.95, 1.05, 1],
        color: targetColor ? [undefined, targetColor, undefined] : undefined,
        y: direction === 'up' ? [0, -3, 0] : direction === 'down' ? [0, 3, 0] : 0
      } : {}}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {format(displayValue)}
    </motion.span>
  );
};

const EditableNutrient = ({ 
  value, 
  unit, 
  onChange, 
  className, 
  format = (v: number) => (v || 0).toFixed(2) 
}: { 
  value: number, 
  unit: string, 
  onChange: (val: number) => void, 
  className?: string,
  format?: (v: number) => string
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(format(value));

  useEffect(() => {
    if (!isEditing) {
      setTempValue(format(value));
    }
  }, [value, isEditing]); // Removed format, added isEditing check

  if (isEditing) {
    return (
      <span className="flex items-center">
        <input
          type="number"
          autoFocus
          onFocus={(e) => e.target.select()}
          className={cn("bg-transparent outline-none w-16 sm:w-20 border-b border-current", className)}
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onBlur={() => {
            setIsEditing(false);
            const num = parseFloat(tempValue);
            if (!isNaN(num)) {
              onChange(num);
            } else if (tempValue === '') {
              onChange(0);
            } else {
              setTempValue(format(value));
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setIsEditing(false);
              const num = parseFloat(tempValue);
              if (!isNaN(num)) {
                onChange(num);
              } else if (tempValue === '') {
                onChange(0);
              } else {
                setTempValue(format(value));
              }
            }
          }}
        />
        {unit && <span className={className}>{unit}</span>}
      </span>
    );
  }

  return (
    <span onClick={() => setIsEditing(true)} className={cn("cursor-pointer hover:opacity-80 transition-opacity inline-flex items-center gap-0.5", className)}>
      <AnimatedValue value={value} format={format} />
      {unit && <span>{unit}</span>}
    </span>
  );
}

const Spark = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0, rotate: -20 }}
    animate={{ opacity: [0, 1, 1, 0], scale: [0, 1, 1, 0], rotate: 20 }}
    transition={{ duration: 2.5, ease: "easeInOut" }}
    className="absolute -top-1 -right-2 text-emerald-500"
  >
    <Sparkles className="w-3.5 h-3.5 fill-emerald-500" />
  </motion.div>
);

const SpringBurgerIcon = ({ isOpen }: { isOpen: boolean }) => {
  return (
    <div className="relative w-5 h-4 flex flex-col justify-between items-center">
      {/* Top Line */}
      <motion.span
        animate={isOpen ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
        transition={{ type: "spring", stiffness: 350, damping: 22 }}
        className="w-full h-[2px] bg-zinc-500 dark:bg-zinc-400 [.skin-stalker_&]:bg-[#4ade80] [.skin-stalker_&]:shadow-[0_0_8px_#4ade80] skin-stalker-burger-line rounded-full origin-center"
      />
      {/* Middle Line */}
      <motion.span
        animate={isOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.2 }}
        className="w-full h-[2px] bg-zinc-500 dark:bg-zinc-400 [.skin-stalker_&]:bg-[#4ade80] [.skin-stalker_&]:shadow-[0_0_8px_#4ade80] skin-stalker-burger-line rounded-full origin-center"
      />
      {/* Bottom Line */}
      <motion.span
        animate={isOpen ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
        transition={{ type: "spring", stiffness: 350, damping: 22 }}
        className="w-full h-[2px] bg-zinc-500 dark:bg-zinc-400 [.skin-stalker_&]:bg-[#4ade80] [.skin-stalker_&]:shadow-[0_0_8px_#4ade80] skin-stalker-burger-line rounded-full origin-center"
      />
    </div>
  );
};

const burgerMenuVariants = {
  hidden: { opacity: 0, scale: 0.88, y: -10, originX: 0.95, originY: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      stiffness: 380,
      damping: 24,
      staggerChildren: 0.05,
      delayChildren: 0.03
    }
  },
  exit: {
    opacity: 0,
    scale: 0.88,
    y: -8,
    transition: { duration: 0.15 }
  }
};

const burgerMenuItemVariants = {
  hidden: { opacity: 0, x: -14, scale: 0.96 },
  visible: { 
    opacity: 1, 
    x: 0, 
    scale: 1, 
    transition: { type: "spring" as const, stiffness: 420, damping: 26 } 
  }
};

const AnimatedMainTitle = ({ 
  text, 
  secondaryText, 
  trigger, 
  isLooping,
  className, 
  skin, 
  lang = "en-US" 
}: { 
  text: string, 
  secondaryText: string, 
  trigger?: boolean, 
  isLooping?: boolean,
  className?: string, 
  skin?: Skin, 
  lang?: string 
}) => {
  const [step, setStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showSpark, setShowSpark] = useState(false);
  const [showAiGlare, setShowAiGlare] = useState(false);

  const isLoopingRef = useRef(!!isLooping);
  isLoopingRef.current = !!isLooping;
  const isRunningRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const waitMs = (ms: number) => new Promise(r => setTimeout(r, ms));

  const startAnimation = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    setIsAnimating(true);

    try {
      do {
        if (!isMountedRef.current) break;
        const delay = 1100;

        setStep(1); // YOUR FUEL
        await waitMs(delay);
        if (!isMountedRef.current) break;

        setStep(2); // YOUR BALANCE
        await waitMs(delay);
        if (!isMountedRef.current) break;

        setStep(3); // YOUR HEALTH
        await waitMs(delay);
        if (!isMountedRef.current) break;

        setStep(4); // YOUR LIFE
        await waitMs(delay);
        if (!isMountedRef.current) break;

        setStep(5); // YOUR Nutrition (YOUR is white)
        await waitMs(delay);
        if (!isMountedRef.current) break;

        setStep(6); // Morphs "Your" -> "Main" (MAIN Nutrition)
        setShowSpark(true);
        await waitMs(1500);
        if (!isMountedRef.current) break;
        setShowSpark(false);
        await waitMs(150);
        if (!isMountedRef.current) break;

        setShowAiGlare(true); // Emerald motion glare sweeps from left to right ONCE
        await waitMs(700);
        if (!isMountedRef.current) break;
        setShowAiGlare(false);

        if (isLoopingRef.current) {
          // Pause briefly between loops while meal analysis is actively in progress
          await waitMs(700);
        } else {
          await waitMs(1000);
          if (isMountedRef.current) setStep(0);
          await waitMs(300);
        }
      } while (isLoopingRef.current && isMountedRef.current);

      if (isMountedRef.current) {
        setStep(0);
      }
    } finally {
      if (isMountedRef.current) {
        setIsAnimating(false);
        setShowSpark(false);
        setShowAiGlare(false);
      }
      isRunningRef.current = false;
    }
  }, []);

  // Trigger when explicit trigger occurs (e.g. user taps header logo or meal analysis starts)
  useEffect(() => {
    if (trigger || isLooping) {
      startAnimation();
    }
  }, [trigger, isLooping, startAnimation]);

  // Run animation strictly once on initial app load in this browser session
  useEffect(() => {
    try {
      const hasAnimated = sessionStorage.getItem('main_logo_animated_on_load');
      if (!hasAnimated) {
        sessionStorage.setItem('main_logo_animated_on_load', 'true');
        const timer = setTimeout(() => {
          startAnimation();
        }, 600);
        return () => clearTimeout(timer);
      }
    } catch {
      const timer = setTimeout(() => {
        startAnimation();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [startAnimation]);

  const handleClick = () => {
    startAnimation();
  };

  const isFirstWordYour = step >= 1 && step <= 5;
  const firstWordKey = isFirstWordYour ? "your" : "main";
  const firstWordText = isFirstWordYour ? (lang === 'ua' ? "ТВІЙ" : "YOUR") : "MAIN";

  let secondWord = "Nutrition";
  if (step === 1) secondWord = "FUEL";
  if (step === 2) secondWord = "BALANCE";
  if (step === 3) secondWord = "HEALTH";
  if (step === 4) secondWord = "LIFE";
  if (step === 5 || step === 6 || step === 0) secondWord = "Nutrition";

  return (
    <div className={cn("relative cursor-pointer select-none h-10 flex items-center overflow-visible", className)} onClick={handleClick}>
      {showSpark && <Spark />}
      <motion.div 
        className="flex font-bold items-center"
        animate={step > 0 && step < 6 ? { scale: 1.05 } : { scale: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
      >
        {/* First word container (YOUR -> MAIN) */}
        <div className="relative flex items-center" style={{ perspective: "1000px" }}>
          <AnimatePresence mode="popLayout">
            <motion.div
              key={firstWordKey}
              initial={step === 6 ? { 
                opacity: 0, 
                y: 16, 
                rotateX: -50,
                filter: "blur(4px)"
              } : (step === 1 ? { 
                opacity: 0, 
                width: 0, 
                x: -10, 
                filter: "blur(8px)" 
              } : false)}
              animate={{ 
                opacity: 1, 
                width: "auto", 
                y: 0, 
                x: 0, 
                rotateX: 0,
                filter: "blur(0px)" 
              }}
              exit={step === 6 ? { 
                opacity: 0, 
                y: -16, 
                rotateX: 50,
                filter: "blur(4px)" 
              } : { 
                opacity: 0, 
                width: 0, 
                x: -10, 
                filter: "blur(8px)" 
              }}
              transition={{ 
                type: "spring", 
                stiffness: 450, 
                damping: 30 
              }}
              className="whitespace-nowrap relative flex items-center"
            >
              {firstWordKey === 'your' ? (
                <span className={cn(
                  "mr-1.5 tracking-tighter",
                  step === 5 
                    ? (skin === 'stalker' ? "text-[#4ade80]" : skin === 'cyberpunk' ? "text-[#FCEE0A]" : "text-white")
                    : "text-emerald-500"
                )}>
                  {firstWordText}
                </span>
              ) : (
                <span className={cn(
                  "font-bold tracking-tight mr-1.5 flex items-center transition-all duration-300",
                  skin === 'stalker' ? "text-[#4ade80]" : skin === 'anime' ? "text-pink-400 drop-shadow-[0_0_10px_rgba(244,114,182,0.6)]" : skin === 'cyberpunk' ? "text-[#FCEE0A] font-mono tracking-widest drop-shadow-[0_0_8px_rgba(252,238,10,0.7)]" : "text-zinc-900 dark:text-white"
                )}>
                  <span>M</span>
                  <span className="relative inline-block overflow-hidden px-px">
                    <span className={cn(
                      "relative z-10 transition-colors duration-300",
                      showAiGlare && "text-emerald-400 dark:text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.9)]"
                    )}>
                      AI
                    </span>
                    {showAiGlare && (
                      <motion.span
                        key="ai-glare"
                        initial={{ x: "-130%", opacity: 0 }}
                        animate={{ x: "130%", opacity: [0, 1, 1, 0] }}
                        transition={{ duration: 0.65, ease: "easeInOut" }}
                        className="absolute inset-0 z-20 pointer-events-none overflow-hidden"
                      >
                        <span 
                          className="absolute inset-y-0 w-full bg-gradient-to-r from-transparent via-emerald-400 to-transparent opacity-90"
                          style={{
                            transform: "skewX(-20deg)",
                            filter: "drop-shadow(0 0 6px #10B981)"
                          }}
                        />
                      </motion.span>
                    )}
                  </span>
                  <span>N</span>
                </span>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Second word container (FUEL -> BALANCE -> HEALTH -> LIFE -> Nutrition) */}
        <div className="relative flex items-center" style={{ perspective: "1000px" }}>
          <AnimatePresence mode="popLayout">
            <motion.div
              key={secondWord}
              initial={step !== 0 && step !== 6 && step !== 5 ? { 
                opacity: 0, 
                y: 20, 
                rotateX: -60,
                scale: 0.9,
                filter: "blur(4px)"
              } : (step === 5 ? {
                opacity: 0, 
                y: 20, 
                rotateX: -60,
                scale: 0.9,
                filter: "blur(4px)"
              } : false)}
              animate={{ 
                opacity: 1, 
                y: 0, 
                rotateX: 0, 
                scale: 1,
                filter: "blur(0px)"
              }}
              exit={{ 
                opacity: 0, 
                y: -20, 
                rotateX: 60,
                scale: 0.9,
                filter: "blur(4px)"
              }}
              transition={{ 
                type: "spring", 
                stiffness: 450, 
                damping: 30
              }}
              className="whitespace-nowrap relative flex items-center"
            >
              {secondWord === 'Nutrition' ? (
                <span className={cn(
                  "font-bold", 
                  skin === 'stalker' ? "text-[#4ade80]" : skin === 'anime' ? "text-purple-300 drop-shadow-[0_0_10px_rgba(168,85,247,0.6)]" : skin === 'cyberpunk' ? "text-[#00ffcc] font-mono tracking-widest drop-shadow-[0_0_8px_rgba(0,255,204,0.6)]" : "text-[#10B981]"
                )}>
                  Nutrition
                </span>
              ) : (
                <span className={cn(
                  "font-bold",
                  skin === 'stalker' ? "text-[#4ade80]" : skin === 'anime' ? "text-white" : skin === 'cyberpunk' ? "text-[#00ffcc] font-mono" : "text-zinc-900 dark:text-white"
                )}>
                  {secondWord}
                </span>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

const AnimeSakuraOverlay: React.FC = () => {
  const petals = useMemo(() => [
    { id: 1, left: '6%', width: 12, height: 16, delay: 0, duration: 8.5, isAlt: false },
    { id: 2, left: '20%', width: 14, height: 18, delay: 2.2, duration: 11, isAlt: true },
    { id: 3, left: '35%', width: 10, height: 13, delay: 4.8, duration: 9, isAlt: false },
    { id: 4, left: '52%', width: 15, height: 19, delay: 1.0, duration: 12, isAlt: true },
    { id: 5, left: '68%', width: 11, height: 15, delay: 3.6, duration: 9.5, isAlt: false },
    { id: 6, left: '84%', width: 13, height: 17, delay: 6.0, duration: 10.5, isAlt: true },
    { id: 7, left: '12%', width: 10, height: 14, delay: 7.5, duration: 10, isAlt: false },
    { id: 8, left: '60%', width: 12, height: 16, delay: 2.8, duration: 8.8, isAlt: true },
    { id: 9, left: '92%', width: 14, height: 18, delay: 6.8, duration: 11.2, isAlt: false },
  ], []);

  const stars = useMemo(() => [
    { id: 1, top: '10%', left: '15%', size: 10, delay: 0 },
    { id: 2, top: '22%', left: '85%', size: 13, delay: 1.4 },
    { id: 3, top: '45%', left: '8%', size: 8, delay: 2.1 },
    { id: 4, top: '62%', left: '92%', size: 12, delay: 0.7 },
    { id: 5, top: '78%', left: '25%', size: 10, delay: 3.0 },
    { id: 6, top: '32%', left: '72%', size: 9, delay: 1.7 },
  ], []);

  return (
    <div className="anime-sakura-overlay" aria-hidden="true">
      {petals.map((petal) => (
        <div
          key={petal.id}
          className={cn("anime-sakura-petal", petal.isAlt && "alt")}
          style={{
            left: petal.left,
            width: `${petal.width}px`,
            height: `${petal.height}px`,
            animationDelay: `${petal.delay}s`,
            animationDuration: `${petal.duration}s`,
          }}
        />
      ))}
      {stars.map((star) => (
        <div
          key={star.id}
          className="anime-star-particle"
          style={{
            top: star.top,
            left: star.left,
            fontSize: `${star.size}px`,
            animationDelay: `${star.delay}s`,
          }}
        >
          ✦
        </div>
      ))}
    </div>
  );
};

const ACTIVITY_LEVELS = [
  { label: 'Sedentary', description: 'Office job, little exercise', value: 1.2 },
  { label: 'Lightly Active', description: '1-3 days/week exercise', value: 1.375 },
  { label: 'Moderately Active', description: '3-5 days/week exercise', value: 1.55 },
  { label: 'Very Active', description: '6-7 days/week exercise', value: 1.725 },
  { label: 'Extra Active', description: 'Physical job + hard training', value: 1.9 },
];

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}



const getMessageIlluminationType = (content: string, image?: string | null): 'food' | 'water' | 'both' => {
  if (!content) return 'both';
  const text = content.toLowerCase();
  
  const hasWater = text.includes('water') || text.includes('вода') || text.includes('води') || text.includes('воду');
  const hasCoffeeOrTea = text.includes('coffee') || text.includes('tea') || text.includes('кава') || text.includes('чай') || text.includes('кави') || text.includes('чаю');
  const hasDrink = text.includes('drink') || text.includes('beverage') || text.includes('пляшка') || text.includes('стакан') || text.includes('мл') || text.includes('ml');
  
  const hasFood = image || text.includes('eat') || text.includes('ate') || text.includes('food') || text.includes('meal') || text.includes('іж') || text.includes('їж') || text.includes('їжа') || text.includes('їжу') || text.includes('калорії') || text.includes('calories') || text.includes('протеїн') || text.includes('protein');
  
  if ((hasWater || hasCoffeeOrTea || hasDrink) && hasFood) {
    return 'both';
  } else if (hasWater || hasCoffeeOrTea || hasDrink) {
    return 'water';
  } else {
    return 'food';
  }
};

let isQuotaExceeded = false;
const daySyncCache = new Map<string, string>();

const saveChatHistory = (messages: ChatMessage[]) => {
  try {
    localDb.setItem('chat_history', JSON.stringify(safeClone(messages)));
  } catch (e: any) {
    console.error('[LocalDB] Critical error saving chat history:', e);
  }
  return messages;
};

const saveMealHistory = (newHistory: HistoryEntry[]) => {
  const normalizedHistory = newHistory.map(entry => ({
    ...entry,
    timestamp: ensureTimestampMillis(entry.timestamp)
  }));
  try {
    localDb.setItem('meal_history', JSON.stringify(safeClone(normalizedHistory)));
  } catch (e: any) {
    console.error('[LocalDB] Critical error saving meal history:', e);
  }
  return normalizedHistory;
};

const handleFirestoreError = (error: unknown, operationType: OperationType, path: string | null) => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  if (errorMessage.includes('exceeds the maximum allowed size')) {
    console.warn(`Firestore Error: Document too large to sync (${path}). Saving locally only.`);
    toast.error('Item is too large to sync to cloud, saving locally only.', { id: 'firestore-error' });
    return;
  }

  const isQuotaExhausted = isFirestoreQuotaExhaustedError(error);
  if (isQuotaExhausted) {
    isQuotaExceeded = true;
    console.warn(`Firestore quota exceeded on [${operationType}] ${path || 'unknown'}:`, errorMessage);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('firestore-quota-exceeded'));
    }
    return;
  }

  const isPermissionError = errorMessage.toLowerCase().includes('missing or insufficient permissions') || errorMessage.toLowerCase().includes('permission-denied');
  
  if (isPermissionError) {
    const errInfo: FirestoreErrorInfo = {
      error: errorMessage,
      authInfo: {
        userId: auth.currentUser?.uid,
        email: auth.currentUser?.email,
        emailVerified: auth.currentUser?.emailVerified,
        isAnonymous: auth.currentUser?.isAnonymous,
        tenantId: auth.currentUser?.tenantId,
        providerInfo: auth.currentUser?.providerData.map(provider => ({
          providerId: provider.providerId,
          displayName: provider.displayName,
          email: provider.email,
          photoUrl: provider.photoURL
        })) || []
      },
      operationType,
      path
    };
    console.error('Firestore Error: ', safeStringify(errInfo));
    throw new Error(safeStringify(errInfo));
  } else {
    console.error('Firestore Error: ', errorMessage);
    throw new Error(errorMessage);
  }
};

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn("ErrorBoundary caught an error:", error?.message, error?.stack, errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      let errorMessage = "Something went wrong.";
      let isFirestoreError = false;

      try {
        if (this.state.error?.message) {
          const parsed = JSON.parse(this.state.error.message);
          if (parsed.error && parsed.operationType) {
            errorMessage = `Firestore Error (${parsed.operationType}): ${parsed.error}`;
            isFirestoreError = true;
          }
        }
      } catch (e) {
        errorMessage = this.state.error?.message || errorMessage;
      }

      const isQuotaError = errorMessage.toLowerCase().includes('quota') || errorMessage.toLowerCase().includes('resource-exhausted');

      return (
        <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-6 text-center">
          <div className="max-w-md w-full glass p-8 rounded-3xl space-y-6 text-white">
            <div className="bg-red-500/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white">
                {isQuotaError ? "Daily Limit Reached" : "Oops! Something went wrong"}
              </h2>
              <p className="text-zinc-400 text-sm leading-relaxed">
                {isQuotaError ? (
                  <>
                    You have reached your daily free quota for database operations. 
                    <br /><br />
                    The quota will <b>reset tomorrow</b>. Detailed information can be found in the Firebase Console.
                  </>
                ) : isFirestoreError ? (
                  "We encountered a database error. Please check your connection or try again later."
                ) : (
                  "An unexpected error occurred in the application."
                )}
              </p>
              <div className="mt-4 p-4 bg-red-500/5 border border-red-500/10 rounded-xl text-xs font-mono text-red-400 text-left overflow-auto max-h-40">
                {errorMessage}
              </div>
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="w-full bg-emerald-500 text-white py-3 rounded-2xl font-bold uppercase tracking-widest text-sm hover:bg-emerald-600 transition-all"
            >
              Refresh App
            </button>
          </div>
        </div>
      );
    }

    // @ts-ignore
    return this.props.children;
  }
}

async function testConnection() {
  if (isQuotaExceeded) return;
  try {
    const connDoc = doc(db, 'test', 'connection');
    await getDocFromServer(connDoc);
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('the client is offline')) {
        console.error("Please check your Firebase configuration. The client is offline.");
      } else if (error.message.includes('quota') || error.message.includes('resource-exhausted')) {
        handleFirestoreError(error, OperationType.GET, 'test/connection');
      }
    }
  }
}

const AdminDashboard = ({ 
  user, 
  t, 
  setView,
  setShowAdminPurgeConfirm,
  theme,
  lang,
  timeFormat,
  illuminationColor,
  setPreviewIlluminationColor,
  isEveningWrapUpTestEnabled = false,
  setIsEveningWrapUpTestEnabled,
  setIsDismissedWrapUp,
  subscriptionTier,
  handleUpgradeSubscription
}: { 
  user: any, 
  t: any, 
  setView: (v: any) => void,
  setShowAdminPurgeConfirm: (v: boolean) => void,
  theme: 'light' | 'dark',
  lang: any,
  timeFormat: '12h' | '24h',
  illuminationColor: string,
  setPreviewIlluminationColor: (v: string) => void,
  isEveningWrapUpTestEnabled?: boolean,
  setIsEveningWrapUpTestEnabled?: (v: boolean) => void,
  setIsDismissedWrapUp?: (v: boolean) => void,
  subscriptionTier?: any,
  handleUpgradeSubscription?: (tier: any) => void
}) => {
  const [activeTab, setActiveTab] = useState<'ai_chat' | 'stat' | 'users' | 'bugs' | 'logs' | 'tester' | 'activity' | 'analytics' | 'diagnostics'>('stat');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [reports, setReports] = useState<any[]>([]);
  const [usersList, setUsersList] = useState<any[]>([]);
  const [authLogs, setAuthLogs] = useState<any[]>([]);
  const [activityFeed, setActivityFeed] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination states for users
  const [lastUserDoc, setLastUserDoc] = useState<any>(null);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const [totalUserCount, setTotalUserCount] = useState<number | null>(null);
  const [registeredTodayCount, setRegisteredTodayCount] = useState<number | null>(null);
  const [latestUser, setLatestUser] = useState<any>(null);

  // New detailed diagnostics & counts states
  const [bugsCount, setBugsCount] = useState<number | null>(null);
  const [logsCount, setLogsCount] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // User subcollection logs states
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [expandedUserMeals, setExpandedUserMeals] = useState<any[]>([]);
  const [expandedUserWater, setExpandedUserWater] = useState<any[]>([]);
  const [expandedUserGrocery, setExpandedUserGrocery] = useState<any[]>([]);
  const [loadingExpandedLogs, setLoadingExpandedLogs] = useState(false);

  const stats = useMemo(() => {
    return {
      total: totalUserCount !== null ? totalUserCount : usersList.length,
      today: registeredTodayCount !== null ? registeredTodayCount : 0,
      latest: latestUser
    };
  }, [totalUserCount, registeredTodayCount, latestUser, usersList.length]);

  const deviceBreakdown = useMemo(() => {
    let ios = 0;
    let android = 0;
    let desktop = 0;
    let pwaCount = 0;
    let webCount = 0;
    let grantedCount = 0;

    usersList.forEach(u => {
      if (u.deviceOS === 'iOS') ios++;
      else if (u.deviceOS === 'Android') android++;
      else if (u.deviceOS === 'Desktop/Web') desktop++;

      if (u.isPWA) pwaCount++;
      else if (u.isPWA === false) webCount++;

      if (u.notificationPermission === 'granted') grantedCount++;
    });

    return { ios, android, desktop, pwaCount, webCount, grantedCount };
  }, [usersList]);

  const loadMoreUsers = async () => {
    if (loadingMoreUsers || !hasMoreUsers || !lastUserDoc || searchTerm.trim()) return;
    setLoadingMoreUsers(true);
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'), startAfter(lastUserDoc), limit(15));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.docs.length === 0) {
        setHasMoreUsers(false);
        setLoadingMoreUsers(false);
        return;
      }
      
      const fetchedUsers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      setUsersList(prev => [...prev, ...fetchedUsers]);
      setLastUserDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMoreUsers(querySnapshot.docs.length === 15);
    } catch (error) {
      console.error("Error loading more users:", error);
      toast.error("Failed to load more users");
    } finally {
      setLoadingMoreUsers(false);
    }
  };

  const handleSearch = async (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      // Re-run standard fetch
      setLoading(true);
      try {
        const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(15));
        const querySnapshot = await getDocs(q);
        const fetchedUsers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsersList(fetchedUsers);
        setLastUserDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMoreUsers(querySnapshot.docs.length === 15);
      } catch (error) {
        console.error("Error resetting users list:", error);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      const normalizedTerm = term.trim().toLowerCase();
      let results: any[] = [];

      // 1. Try exact email query first
      const qEmail = query(collection(db, 'users'), where('email', '==', normalizedTerm));
      const snapEmail = await getDocs(qEmail);
      results = snapEmail.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 2. Try prefix email search
      if (results.length === 0) {
        const qEmailPrefix = query(
          collection(db, 'users'), 
          where('email', '>=', normalizedTerm), 
          where('email', '<=', normalizedTerm + '\uf8ff'),
          limit(30)
        );
        const snapEmailPrefix = await getDocs(qEmailPrefix);
        results = snapEmailPrefix.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      // 3. Try exact username
      if (results.length === 0) {
        const qName = query(collection(db, 'users'), where('userName', '==', term.trim()));
        const snapName = await getDocs(qName);
        results = snapName.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      // 4. Try prefix username search
      if (results.length === 0) {
        const qNamePrefix = query(
          collection(db, 'users'), 
          where('userName', '>=', term.trim()), 
          where('userName', '<=', term.trim() + '\uf8ff'),
          limit(30)
        );
        const snapNamePrefix = await getDocs(qNamePrefix);
        results = snapNamePrefix.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      setUsersList(results);
      setHasMoreUsers(false);
    } catch (error) {
      console.error("Search failed:", error);
      toast.error("Failed to search users");
    } finally {
      setLoading(false);
    }
  };

  const toggleUserExpand = async (userId: string) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    setLoadingExpandedLogs(true);
    setExpandedUserMeals([]);
    setExpandedUserWater([]);
    setExpandedUserGrocery([]);
    try {
      // 1. Define queries for days, history, water, and grocery list
      const daysQ = query(collection(db, 'users', userId, 'days'), orderBy('date', 'desc'), limit(30));
      const historyQ = query(collection(db, 'users', userId, 'history'), orderBy('timestamp', 'desc'), limit(50));
      const waterQ = query(collection(db, 'users', userId, 'water'), orderBy('timestamp', 'desc'), limit(50));
      const groceryQ = query(collection(db, 'users', userId, 'grocery_list'), orderBy('timestamp', 'desc'), limit(50));

      // 2. Fetch all subcollection snapshots concurrently
      const [daysSnapshot, historySnapshot, waterSnapshot, grocerySnapshot] = await Promise.all([
        getDocs(daysQ).catch(() => ({ docs: [] })),
        getDocs(historyQ).catch(() => ({ docs: [] })),
        getDocs(waterQ).catch(() => ({ docs: [] })),
        getDocs(groceryQ).catch(() => ({ docs: [] }))
      ]);

      let mealsFromDays: any[] = [];
      let waterFromDays: any[] = [];

      daysSnapshot.docs.forEach(docSnap => {
        const d = docSnap.data();
        if (Array.isArray(d.history)) {
          mealsFromDays.push(...d.history);
        }
        if (Array.isArray(d.water)) {
          waterFromDays.push(...d.water);
        }
      });

      const mealsFromSub = historySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Merge and deduplicate meals by id
      const mealMap = new Map<string, any>();
      [...mealsFromDays, ...mealsFromSub].forEach(m => {
        if (m && m.id) mealMap.set(m.id, m);
      });
      const meals = Array.from(mealMap.values()).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

      const waterFromSub = waterSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const waterMap = new Map<string, any>();
      [...waterFromDays, ...waterFromSub].forEach(w => {
        if (w && w.id) waterMap.set(w.id, w);
      });
      const water = Array.from(waterMap.values()).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

      const grocery = grocerySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setExpandedUserMeals(meals);
      setExpandedUserWater(water);
      setExpandedUserGrocery(grocery);
    } catch (error) {
      console.error("Failed to load user logs:", error);
      toast.error("Failed to load user logs");
    } finally {
      setLoadingExpandedLogs(false);
    }
  };

  useEffect(() => {
    let unsubscribeUsers: (() => void) | null = null;
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeTab === 'bugs') {
          const q = query(collection(db, 'bug_reports'), orderBy('timestamp', 'desc'), limit(50));
          const querySnapshot = await getDocs(q);
          if (isMounted) {
            const fetchedBugs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setReports(fetchedBugs);
            setBugsCount(fetchedBugs.length);
          }
        } else if (activeTab === 'users' || activeTab === 'stat' || activeTab === 'tester') {
          if (activeTab === 'stat') {
            // Step 1: Get total user count safely
            try {
              const countSnapshot = await getCountFromServer(collection(db, 'users'));
              if (isMounted) setTotalUserCount(countSnapshot.data().count);
            } catch (e) { console.warn('[ADMIN] Could not get total user count:', e); }

            // Step 2: Get bug & log counts independently
            try {
              const bugsCountSnapshot = await getCountFromServer(collection(db, 'bug_reports'));
              if (isMounted) setBugsCount(bugsCountSnapshot.data().count);
            } catch (e) { console.warn('[ADMIN] Could not get bugs count:', e); }

            try {
              const logsCountSnapshot = await getCountFromServer(collection(db, 'auth_error_logs'));
              if (isMounted) setLogsCount(logsCountSnapshot.data().count);
            } catch (e) { console.warn('[ADMIN] Could not get logs count:', e); }

            // Step 3: Get registrations for today (createdAt stored as ISO string)
            try {
              const todayStr = new Date().toISOString().split('T')[0];
              const todayQuery = query(collection(db, 'users'), where('createdAt', '>=', todayStr));
              const todayCountSnapshot = await getCountFromServer(todayQuery);
              if (isMounted) setRegisteredTodayCount(todayCountSnapshot.data().count);
            } catch (e) { console.warn('[ADMIN] Could not get today registration count:', e); }
          }

          // Step 4: Fetch paginated users — try with orderBy, fall back to unordered
          let fetchedUsers: any[] = [];
          let lastDoc: any = null;
          let moreAvailable = false;

          try {
            const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(15));
            const querySnapshot = await getDocs(q);
            fetchedUsers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
            moreAvailable = querySnapshot.docs.length === 15;
          } catch (indexErr: any) {
            console.warn('[ADMIN] orderBy createdAt query failed (index may be building), falling back:', indexErr?.message);
            try {
              const qFallback = query(collection(db, 'users'), limit(50));
              const fallbackSnap = await getDocs(qFallback);
              fetchedUsers = fallbackSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              fetchedUsers.sort((a, b) => {
                const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return bTime - aTime;
              });
              lastDoc = null;
              moreAvailable = false;
            } catch (fallbackErr) {
              console.error('[ADMIN] Fallback user fetch also failed:', fallbackErr);
              if (isMounted) toast.error('Failed to load users list');
            }
          }

          if (isMounted) {
            setUsersList(fetchedUsers);
            setLastUserDoc(lastDoc);
            setHasMoreUsers(moreAvailable);
            setLatestUser(fetchedUsers.length > 0 ? fetchedUsers[0] : null);
          }
        } else if (activeTab === 'logs') {
          const q = query(collection(db, 'auth_error_logs'), orderBy('timestamp', 'desc'), limit(50));
          const querySnapshot = await getDocs(q);
          if (isMounted) {
            const fetchedLogs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAuthLogs(fetchedLogs);
            setLogsCount(fetchedLogs.length);
          }
        } else if (activeTab === 'activity') {
          // Try orderBy lastUpdatedAt — fall back to unordered if index not ready
          let recentUsers: any[] = [];
          try {
            const q = query(collection(db, 'users'), orderBy('lastUpdatedAt', 'desc'), limit(20));
            const querySnapshot = await getDocs(q);
            recentUsers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          } catch (idxErr: any) {
            console.warn('[ADMIN] orderBy lastUpdatedAt failed, falling back:', idxErr?.message);
            try {
              const qFallback = query(collection(db, 'users'), limit(20));
              const snap = await getDocs(qFallback);
              recentUsers = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              recentUsers.sort((a, b) => (b.lastUpdatedAt || 0) - (a.lastUpdatedAt || 0));
            } catch (e) { console.error('[ADMIN] Could not fetch users for activity feed:', e); }
          }

          const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          // Only fetch activity for users who were active in the last 7 days
          const activeUsers = recentUsers.filter(u => (u.lastUpdatedAt || 0) >= sevenDaysAgo);

          const activityPromises = activeUsers.flatMap(u => {
            const historyPromise = getDocs(query(
              collection(db, 'users', u.id, 'history'),
              where('timestamp', '>=', sevenDaysAgo),
              orderBy('timestamp', 'desc')
            )).then(snap => snap.docs.map(d => ({
              ...d.data(),
              id: d.id,
              userId: u.id,
              userName: (u as any).userName,
              email: (u as any).email,
              _type: 'meal'
            }))).catch(() => []);

            const scanPromise = getDocs(query(
              collection(db, 'users', u.id, 'scanned_products'),
              where('timestamp', '>=', sevenDaysAgo),
              orderBy('timestamp', 'desc')
            )).then(snap => snap.docs.map(d => ({
              ...d.data(),
              id: d.id,
              userId: u.id,
              userName: (u as any).userName,
              email: (u as any).email,
              _type: 'scan'
            }))).catch(() => []);

            return [historyPromise, scanPromise];
          });

          const results = await Promise.all(activityPromises);
          const activities = results.flat().sort((a, b) => b.timestamp - a.timestamp);

          if (isMounted) setActivityFeed(activities);
        }
      } catch (error) {
        console.error("Error fetching admin data:", error);
        toast.error("Failed to load data");
      } finally {
        // Always clear loading state for all tabs
        if (isMounted) setLoading(false);
      }
    };

    const adminEmail = user?.email || auth.currentUser?.email || user?.providerData?.find((p: any) => p?.email)?.email || localStorage.getItem('user_email');

    if (isKimAdmin(adminEmail)) {
      fetchData();
    }

    return () => {
      isMounted = false;
      if (unsubscribeUsers) unsubscribeUsers();
    };
  }, [activeTab, user, refreshTrigger]);

  const adminEmail = user?.email || auth.currentUser?.email || user?.providerData?.find((p: any) => p?.email)?.email || localStorage.getItem('user_email');
  if (!isKimAdmin(adminEmail)) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-4">
          <button onClick={() => { setView('goals'); setPreviewIlluminationColor(illuminationColor); }} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors">
            <ArrowLeft className="w-6 h-6 text-[var(--text)]" />
          </button>
          <h2 className={cn(
            "text-xl font-bold uppercase tracking-widest",
            theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
          )}>{t.adminDashboard || 'Admin Console'}</h2>
        </div>
        <button
          onClick={() => setRefreshTrigger(prev => prev + 1)}
          className="p-2.5 hover:bg-zinc-200/60 dark:hover:bg-zinc-800/60 rounded-xl transition-all flex items-center justify-center border border-[var(--card-border)] bg-[var(--card-bg)] text-zinc-500 hover:text-emerald-500 active:scale-95 cursor-pointer shadow-sm animate-pulse-subtle"
          title={lang === 'ua' ? 'Оновити дані' : 'Refresh Data'}
        >
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin text-emerald-500")} />
        </button>
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar bg-[var(--input-bg)] p-1.5 rounded-2xl border border-[var(--card-border)] card-shadow scroll-smooth select-none">
        <button 
          onClick={() => activeTab === 'ai_chat' ? setRefreshTrigger(prev => prev + 1) : setActiveTab('ai_chat')}
          className={cn(
            "px-4 py-2.5 rounded-xl shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1.5",
            activeTab === 'ai_chat' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" : "text-zinc-500 hover:text-[var(--text)] hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
          )}
        >
          <Bot className="w-3.5 h-3.5" />
          {lang === 'ua' ? 'AI Чат' : 'AI Chat'}
        </button>
        <button 
          onClick={() => activeTab === 'stat' ? setRefreshTrigger(prev => prev + 1) : setActiveTab('stat')}
          className={cn(
            "px-4 py-2.5 rounded-xl shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all cursor-pointer",
            activeTab === 'stat' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" : "text-zinc-500 hover:text-[var(--text)] hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
          )}
        >
          {t.stats || 'Stats'}
        </button>
        <button 
          onClick={() => activeTab === 'users' ? setRefreshTrigger(prev => prev + 1) : setActiveTab('users')}
          className={cn(
            "px-4 py-2.5 rounded-xl shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all cursor-pointer",
            activeTab === 'users' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" : "text-zinc-500 hover:text-[var(--text)] hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
          )}
        >
          {t.users || 'Users'}
        </button>
        <button 
          onClick={() => activeTab === 'bugs' ? setRefreshTrigger(prev => prev + 1) : setActiveTab('bugs')}
          className={cn(
            "px-4 py-2.5 rounded-xl shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all cursor-pointer",
            activeTab === 'bugs' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" : "text-zinc-500 hover:text-[var(--text)] hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
          )}
        >
          {t.bugs || 'Bugs'}
        </button>
        <button 
          onClick={() => activeTab === 'logs' ? setRefreshTrigger(prev => prev + 1) : setActiveTab('logs')}
          className={cn(
            "px-4 py-2.5 rounded-xl shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all cursor-pointer",
            activeTab === 'logs' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" : "text-zinc-500 hover:text-[var(--text)] hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
          )}
        >
          Logs
        </button>
        <button 
          onClick={() => activeTab === 'activity' ? setRefreshTrigger(prev => prev + 1) : setActiveTab('activity')}
          className={cn(
            "px-4 py-2.5 rounded-xl shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all cursor-pointer",
            activeTab === 'activity' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" : "text-zinc-500 hover:text-[var(--text)] hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
          )}
        >
          Activity
        </button>
        <button 
          onClick={() => activeTab === 'analytics' ? setRefreshTrigger(prev => prev + 1) : setActiveTab('analytics')}
          className={cn(
            "px-4 py-2.5 rounded-xl shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all cursor-pointer",
            activeTab === 'analytics' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" : "text-zinc-500 hover:text-[var(--text)] hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
          )}
        >
          {lang === 'ua' ? 'Аналітика' : 'Analytics'}
        </button>
        <button 
          onClick={() => activeTab === 'diagnostics' ? setRefreshTrigger(prev => prev + 1) : setActiveTab('diagnostics')}
          className={cn(
            "px-4 py-2.5 rounded-xl shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all cursor-pointer",
            activeTab === 'diagnostics' ? "bg-amber-500 text-white shadow-lg shadow-amber-500/20" : "text-zinc-500 font-semibold hover:text-[var(--text)] hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
          )}
        >
          {lang === 'ua' ? 'Діагностика Моделей' : 'Model Diagnostics'}
        </button>
        <button 
          onClick={() => activeTab === 'tester' ? setRefreshTrigger(prev => prev + 1) : setActiveTab('tester')}
          className={cn(
            "px-4 py-2.5 rounded-xl shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-all cursor-pointer",
            activeTab === 'tester' ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" : "text-zinc-500 hover:text-[var(--text)] hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
          )}
        >
          {lang === 'ua' ? 'Тестер' : 'Tester'}
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input 
              type="text"
              placeholder={lang === 'ua' ? 'Пошук користувачів за ім\'ям чи email...' : 'Search users by name or email...'}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl text-xs font-bold uppercase tracking-wider text-[var(--text)] outline-none focus:border-emerald-500 transition-colors"
            />
          </div>
          {usersList.length > 0 && (
            <button
              onClick={() => setShowAdminPurgeConfirm(true)}
              className="w-full flex items-center justify-center gap-2 py-4 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-2xl border border-red-500/20 transition-all font-bold uppercase tracking-widest text-[10px]"
            >
              <Trash2 className="w-4 h-4" />
              Purge All Users
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : activeTab === 'ai_chat' ? (
          <AdminGeminiChat lang={lang} userEmail={adminEmail} isFloatingBubble={false} defaultOpen={true} />
        ) : activeTab === 'analytics' ? (
          <AdminAnalyticsPanel key={refreshTrigger} lang={lang} timeFormat={timeFormat} />
        ) : activeTab === 'diagnostics' ? (
          <GeminiModelDiagnosticPanel key={refreshTrigger} lang={lang} timeFormat={timeFormat} />
        ) : activeTab === 'stat' ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] p-5 rounded-3xl text-center space-y-2 card-shadow">
                <Users className="w-8 h-8 text-emerald-500 mx-auto" />
                <p className="text-2xl font-bold text-[var(--text)]">{stats.total}</p>
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{t.totalUsers || 'Total Users'}</p>
              </div>
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] p-5 rounded-3xl text-center space-y-2 card-shadow">
                <Zap className="w-8 h-8 text-yellow-500 mx-auto" />
                <p className="text-2xl font-bold text-[var(--text)]">{stats.today}</p>
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{t.regToday || 'Registered Today'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] p-5 rounded-3xl text-center space-y-2 card-shadow">
                <Bug className="w-8 h-8 text-amber-500 mx-auto" />
                <p className="text-2xl font-bold text-[var(--text)]">{bugsCount !== null ? bugsCount : '0'}</p>
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{t.bugs || 'Bugs'}</p>
              </div>
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] p-5 rounded-3xl text-center space-y-2 card-shadow">
                <Lock className="w-8 h-8 text-red-500 mx-auto" />
                <p className="text-2xl font-bold text-[var(--text)]">{logsCount !== null ? logsCount : '0'}</p>
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Auth Logs</p>
              </div>
            </div>

            {stats.latest && (
              <CollapsibleSection
                icon={<User className="w-5 h-5 text-emerald-500" />}
                title={t.latestUser || 'Latest User'}
                badge={
                  <span className="bg-emerald-500/10 text-emerald-600 text-[8px] font-bold px-2 py-0.5 rounded-full uppercase">
                    {lang === 'ua' ? "НОВЕ" : "NEW"}
                  </span>
                }
              >
                <div className="flex items-center gap-4">
                  <div className="bg-emerald-100 dark:bg-emerald-900/30 p-3 rounded-2xl text-emerald-600">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold text-[var(--text)]">{stats.latest.userName || 'New User'}</p>
                    <div className="flex items-center gap-1.5 group">
                      <p className="text-xs text-zinc-500 lowercase">{stats.latest.email || 'no email provided'}</p>
                      {stats.latest.email && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(stats.latest.email);
                            toast.success('Email copied!');
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded cursor-pointer"
                        >
                          <Copy className="w-3 h-3 text-emerald-500" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
            )}

            {/* Live Ecosystem Metrics */}
            <CollapsibleSection
              icon={<Smartphone className="w-5 h-5 text-blue-500" />}
              title={lang === 'ua' ? "Огляд пристроїв тестувальників" : "Tester Device Breakdown"}
            >
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-zinc-50 dark:bg-zinc-900/40 p-3 rounded-2xl border border-[var(--card-border)]">
                  <p className="text-sm font-black text-blue-500">{deviceBreakdown.ios}</p>
                  <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">iOS</p>
                </div>
                <div className="bg-zinc-50 dark:bg-zinc-900/40 p-3 rounded-2xl border border-[var(--card-border)]">
                  <p className="text-sm font-black text-emerald-500">{deviceBreakdown.android}</p>
                  <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">Android</p>
                </div>
                <div className="bg-zinc-50 dark:bg-zinc-900/40 p-3 rounded-2xl border border-[var(--card-border)]">
                  <p className="text-sm font-black text-purple-500">{deviceBreakdown.desktop}</p>
                  <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">{lang === 'ua' ? "Веб" : "Web"}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center pt-1">
                <div className="bg-zinc-50 dark:bg-zinc-900/40 p-3 rounded-2xl border border-[var(--card-border)] flex items-center justify-between">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">{lang === 'ua' ? "Встановлено PWA" : "PWA Installs"}</span>
                  <span className="text-xs font-black text-emerald-500">{deviceBreakdown.pwaCount}</span>
                </div>
                <div className="bg-zinc-50 dark:bg-zinc-900/40 p-3 rounded-2xl border border-[var(--card-border)] flex items-center justify-between">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">{lang === 'ua' ? "Дозвіл сповіщень" : "Notif Granted"}</span>
                  <span className="text-xs font-black text-emerald-500">{deviceBreakdown.grantedCount}</span>
                </div>
              </div>
            </CollapsibleSection>
          </div>
        ) : activeTab === 'users' ? (
          usersList.length === 0 ? (
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-12 text-center text-zinc-500 font-bold uppercase tracking-widest card-shadow">
              {t.noUsers || 'No users found'}
            </div>
          ) : (
            <div className="space-y-3">
              {usersList.map((u: any) => {
                const isExpanded = expandedUserId === u.id;
                return (
                  <div 
                    key={u.id} 
                    className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 card-shadow transition-all space-y-3 cursor-pointer hover:border-emerald-500/30"
                    onClick={() => toggleUserExpand(u.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-zinc-100 dark:bg-zinc-800 p-2 rounded-full">
                          <User className="w-5 h-5 text-zinc-500" />
                        </div>
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-[var(--text)]">{u.userName || 'New User'}</p>
                            {u.isTester && (
                              <span className="bg-amber-500/10 text-amber-500 text-[8px] font-bold px-1.5 py-0.5 rounded border border-amber-500/10 uppercase">Tester</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 group" onClick={(e) => e.stopPropagation()}>
                            <p className="text-[11px] text-zinc-500 font-medium lowercase tracking-tight leading-none">{u.email || 'no email'}</p>
                            {u.email && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(u.email);
                                  toast.success('Email copied!');
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
                              >
                                <Copy className="w-2.5 h-2.5 text-emerald-500" />
                              </button>
                            )}
                          </div>
                          {u.lastActiveDay === new Date().toISOString().split('T')[0] && u.todayCalories > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <Zap className="w-3 h-3 text-emerald-500 fill-emerald-500" />
                              <span className="text-[10px] font-extrabold text-emerald-500 uppercase tracking-widest">
                                {Math.round(u.todayCalories)} kcal today
                              </span>
                            </div>
                          )}
                          
                          {/* Live Diagnostic Badges */}
                          <div className="flex flex-wrap gap-1 mt-1.5 items-center">
                            {u.deviceOS && (
                              <span className={cn(
                                "text-[8px] font-black uppercase px-1.5 py-0.5 rounded tracking-wider leading-none border",
                                u.deviceOS === 'iOS' ? "bg-blue-500/10 text-blue-500 border-blue-500/10" :
                                u.deviceOS === 'Android' ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/10" :
                                "bg-zinc-500/10 text-zinc-500 border-zinc-500/10"
                              )}>
                                {u.deviceOS}
                              </span>
                            )}
                            {u.isPWA !== undefined && (
                              <span className={cn(
                                "text-[8px] font-black uppercase px-1.5 py-0.5 rounded tracking-wider leading-none border",
                                u.isPWA ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/10" : "bg-amber-500/10 text-amber-500 border-amber-500/10"
                              )}>
                                {u.isPWA ? "PWA" : "WEB"}
                              </span>
                            )}
                            {u.notificationPermission && (
                              <span className={cn(
                                "text-[8px] font-black uppercase px-1.5 py-0.5 rounded tracking-wider leading-none border",
                                u.notificationPermission === 'granted' ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/10" : "bg-red-500/10 text-red-500 border-red-500/10"
                              )}>
                                Notif: {u.notificationPermission}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col items-end gap-1">
                          <div className="text-right">
                            <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{lang === 'ua' ? "Приєднався" : "Joined"}</p>
                            <p className="text-[10px] font-bold text-zinc-500">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : 'Unknown'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{lang === 'ua' ? "Активність" : "Activity"}</p>
                            <p className="text-[10px] font-bold text-emerald-500">
                              {u.lastUpdatedAt ? new Date(u.lastUpdatedAt).toLocaleDateString() === new Date().toLocaleDateString() 
                                ? new Date(u.lastUpdatedAt).toLocaleTimeString(lang === 'ua' ? 'uk-UA' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h' })
                                : new Date(u.lastUpdatedAt).toLocaleDateString()
                              : 'None'}
                            </p>
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                      </div>
                    </div>

                    {/* Expandable subcollection data container */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="pt-4 border-t border-[var(--card-border)] space-y-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {loadingExpandedLogs ? (
                            <div className="flex items-center justify-center py-6 gap-2">
                              <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                              <span className="text-xs font-bold text-zinc-500 uppercase tracking-widest">{lang === 'ua' ? 'Завантаження...' : 'Loading user logs...'}</span>
                            </div>
                          ) : (
                            <div className="space-y-4 text-xs">
                              {/* 1. Goals & Profile Details */}
                              <div className="bg-zinc-50 dark:bg-zinc-900/40 p-3 rounded-xl border border-[var(--card-border)] space-y-2">
                                <h4 className="font-extrabold uppercase tracking-wider text-[10px] text-emerald-500">
                                  {lang === 'ua' ? 'Профіль та Цілі' : 'Profile & Goals'}
                                </h4>
                                <div className="grid grid-cols-2 gap-2 text-[11px]">
                                  <div>
                                    <span className="text-zinc-400 font-medium">Daily Calorie Goal:</span>{' '}
                                    <span className="font-bold text-[var(--text)]">{u.goals?.calories_kcal ? `${u.goals.calories_kcal} kcal` : 'Not Set'}</span>
                                  </div>
                                  <div>
                                    <span className="text-zinc-400 font-medium">Daily Water Goal:</span>{' '}
                                    <span className="font-bold text-[var(--text)]">{u.goals?.water_ml ? `${u.goals.water_ml} ml` : 'Not Set'}</span>
                                  </div>
                                  <div>
                                    <span className="text-zinc-400 font-medium">Weight Goal:</span>{' '}
                                    <span className="font-bold text-[var(--text)]">{u.goals?.weight_target_kg ? `${u.goals.weight_target_kg} kg` : 'Not Set'}</span>
                                  </div>
                                  <div>
                                    <span className="text-zinc-400 font-medium">Theme/Vibe:</span>{' '}
                                    <span className="font-bold text-[var(--text)] uppercase">{u.theme || 'Default'} / {u.vibe || 'Default'}</span>
                                  </div>
                                  <div>
                                    <span className="text-zinc-400 font-medium">Language:</span>{' '}
                                    <span className="font-bold text-[var(--text)] uppercase">{u.language || 'en'}</span>
                                  </div>
                                  <div>
                                    <span className="text-zinc-400 font-medium">Calorie Streak:</span>{' '}
                                    <span className="font-bold text-[var(--text)]">{u.calorieStreak || 0} days</span>
                                  </div>
                                </div>
                              </div>

                              {/* 2. Logged Meals (History) */}
                              <div className="space-y-1.5">
                                <h4 className="font-extrabold uppercase tracking-wider text-[10px] text-emerald-500 flex items-center justify-between">
                                  <span>{lang === 'ua' ? 'Журнал записів' : 'Log Book'}</span>
                                  <span className="bg-emerald-500/10 text-emerald-500 font-bold px-1.5 py-0.2 rounded text-[9px]">{expandedUserMeals.length} logged</span>
                                </h4>
                                {expandedUserMeals.length === 0 ? (
                                  <p className="text-zinc-500 italic pl-1">No meals logged yet.</p>
                                ) : (
                                  <div className="max-h-48 overflow-y-auto space-y-2 border border-[var(--card-border)] rounded-xl p-2 bg-zinc-50 dark:bg-zinc-900/20">
                                    {expandedUserMeals.map((meal: any) => (
                                      <div key={meal.id} className="p-2 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg space-y-1">
                                        <div className="flex justify-between font-bold">
                                          <span className="text-[11px] text-[var(--text)]">{meal.meal_name}</span>
                                          <span className="text-[10px] text-emerald-500">{Math.round(meal.total_nutrition?.calories_kcal || 0)} kcal</span>
                                        </div>
                                        <div className="flex justify-between text-[9px] text-zinc-400">
                                          <span>Type: <strong className="uppercase">{meal.meal_type}</strong></span>
                                          <span>{meal.timestamp ? new Date(meal.timestamp).toLocaleString() : ''}</span>
                                        </div>
                                        {meal.total_nutrition && (
                                          <div className="flex gap-2 text-[9px] text-zinc-500 font-mono">
                                            <span>P: {Math.round(meal.total_nutrition.protein_g || 0)}g</span>
                                            <span>C: {Math.round(meal.total_nutrition.carbs_g || 0)}g</span>
                                            <span>F: {Math.round(meal.total_nutrition.fat_g || 0)}g</span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* 3. Water Logs */}
                              <div className="space-y-1.5">
                                <h4 className="font-extrabold uppercase tracking-wider text-[10px] text-emerald-500 flex items-center justify-between">
                                  <span>{lang === 'ua' ? 'Логи Води' : 'Water Logs'}</span>
                                  <span className="bg-emerald-500/10 text-emerald-500 font-bold px-1.5 py-0.2 rounded text-[9px]">{expandedUserWater.length} logs</span>
                                </h4>
                                {expandedUserWater.length === 0 ? (
                                  <p className="text-zinc-500 italic pl-1">No liquid logged yet.</p>
                                ) : (
                                  <div className="max-h-36 overflow-y-auto space-y-1.5 border border-[var(--card-border)] rounded-xl p-2 bg-zinc-50 dark:bg-zinc-900/20">
                                    {expandedUserWater.map((wl: any) => (
                                      <div key={wl.id} className="p-1.5 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg flex justify-between items-center">
                                        <div className="flex items-center gap-1.5">
                                          <Coffee className="w-3 h-3 text-blue-400" />
                                          <span className="font-bold text-[11px] uppercase text-[var(--text)]">{wl.beverage_type || 'water'}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                          <span className="font-bold text-emerald-500">{wl.amount_ml} ml</span>
                                          <span className="text-[9px] text-zinc-400">{wl.timestamp ? new Date(wl.timestamp).toLocaleTimeString() : ''}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* 4. AI Concierge Items */}
                              <div className="space-y-1.5">
                                <h4 className="font-extrabold uppercase tracking-wider text-[10px] text-emerald-500 flex items-center justify-between">
                                  <span>{lang === 'ua' ? 'Список Продуктів' : 'Concierge'}</span>
                                  <span className="bg-emerald-500/10 text-emerald-500 font-bold px-1.5 py-0.2 rounded text-[9px]">{expandedUserGrocery.length} items</span>
                                </h4>
                                {expandedUserGrocery.length === 0 ? (
                                  <p className="text-zinc-500 italic pl-1">Concierge is empty.</p>
                                ) : (
                                  <div className="max-h-36 overflow-y-auto space-y-1.5 border border-[var(--card-border)] rounded-xl p-2 bg-zinc-50 dark:bg-zinc-900/20">
                                    {expandedUserGrocery.map((item: any) => (
                                      <div key={item.id} className="p-1.5 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg flex justify-between items-center">
                                        <span className={cn("font-bold text-[11px]", item.checked ? "line-through text-zinc-500 font-medium" : "text-[var(--text)]")}>{item.name} {item.amount ? `(${item.amount})` : ''}</span>
                                        <span className="text-[9px] text-zinc-400">{item.checked ? 'Checked' : 'Pending'}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              
              {hasMoreUsers && lastUserDoc && (
                <div className="flex justify-center pt-2">
                  <button
                    onClick={loadMoreUsers}
                    disabled={loadingMoreUsers}
                    className="w-full flex items-center justify-center gap-2 py-4 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 rounded-2xl border border-emerald-500/20 transition-all font-bold uppercase tracking-widest text-[10px] disabled:opacity-50"
                  >
                    {loadingMoreUsers ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 text-emerald-500" />
                        Load More Users
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )
        ) : activeTab === 'logs' ? (
            authLogs.length === 0 ? (
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-12 text-center card-shadow flex flex-col items-center">
                 <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-4" />
                 <p className="text-zinc-500 font-bold uppercase tracking-widest">{lang === 'ua' ? "Журнал автентифікації порожній" : "No auth logs found"}</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center px-2 mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">{lang === 'ua' ? "Останні помилки входу" : "Recent Login Errors"}</h3>
                </div>
                {authLogs.map((log: any, idx: number) => (
                  <div key={log.id || idx} className="bg-[var(--card-bg)] border border-red-500/20 rounded-2xl p-4 card-shadow space-y-2 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-red-500" />
                    <div className="flex justify-between items-start">
                       <span className="text-xs font-black uppercase text-[var(--text)] bg-red-500/10 text-red-500 px-2 py-0.5 rounded-md">{log.method}</span>
                       <span className="text-[10px] text-zinc-400 font-medium">{new Date(log.timestamp).toLocaleString()}</span>
                    </div>
                    <div className="space-y-1 mt-2 bg-zinc-50 dark:bg-zinc-900/50 p-2 rounded-xl">
                      <p className="text-[10px] font-bold text-red-400 uppercase break-all">{log.errorCode}</p>
                      <p className="text-xs text-zinc-600 dark:text-zinc-300 break-words">{log.errorMessage}</p>
                      {log.userAgent && (
                        <p className="text-[9px] text-zinc-400 mt-2 pt-2 border-t border-zinc-200 dark:border-zinc-800 break-words font-mono line-clamp-2">
                          {log.userAgent}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : activeTab === 'activity' ? (
            activityFeed.length === 0 ? (
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-12 text-center card-shadow flex flex-col items-center">
                 <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-4" />
                 <p className="text-zinc-500 font-bold uppercase tracking-widest">{lang === 'ua' ? "Журнал активності порожній" : "No recent activity found"}</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center px-2 mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">{lang === 'ua' ? "Остання активність" : "Recent Activity Feed"}</h3>
                </div>
                
                {(() => {
                  const chartDataMap = new Map<string, number>();
                  const now = new Date();
                  for (let i = 6; i >= 0; i--) {
                    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
                    chartDataMap.set(d.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' }), 0);
                  }
                  
                  activityFeed.forEach(item => {
                     if (item._type === 'meal') {
                       const d = new Date(item.timestamp);
                       const dateStr = d.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                       if (chartDataMap.has(dateStr)) {
                         chartDataMap.set(dateStr, chartDataMap.get(dateStr)! + 1);
                       }
                     }
                  });
                  
                  const chartData = Array.from(chartDataMap.entries()).map(([name, logs]) => ({ name, logs }));
                  
                  return (
                    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 card-shadow mb-4">
                      <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-4">{lang === 'ua' ? "Журнали прийомів їжі за тиждень" : "Meal Logs Last 7 Days"}</h4>
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: 15 }}
                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="h-32"
                      >
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData}>
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#71717a' }} />
                            <Tooltip 
                               cursor={{ fill: 'rgba(16, 185, 129, 0.1)' }}
                               contentStyle={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)', borderRadius: '12px', fontSize: '12px', color: 'var(--text)' }}
                               itemStyle={{ color: '#10b981', fontWeight: 'bold' }}
                             />
                            <Bar dataKey="logs" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={32} isAnimationActive={true} animationDuration={1000} animationEasing="ease-out" />
                          </BarChart>
                        </ResponsiveContainer>
                      </motion.div>
                    </div>
                  );
                })()}

                {activityFeed.map((item: any, idx: number) => (
                  <div key={item.id || idx} className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 card-shadow space-y-2 relative overflow-hidden transition-all hover:border-emerald-500/30">
                    <div className="flex justify-between items-start">
                       <div className="flex items-center gap-2">
                         <span className={cn(
                           "text-[9px] font-black uppercase px-2 py-0.5 rounded-md",
                           item._type === 'meal' ? "bg-emerald-500/10 text-emerald-500" : "bg-blue-500/10 text-blue-500"
                         )}>
                           {item._type === 'meal' ? (lang === 'ua' ? 'Прийом їжі' : 'Meal Log') : (lang === 'ua' ? 'Сканування' : 'Product Scan')}
                         </span>
                         <span className="text-[10px] font-bold text-[var(--text)]">{item.userName || 'Unknown'}</span>
                       </div>
                       <span className="text-[10px] text-zinc-400 font-medium">{new Date(item.timestamp).toLocaleString()}</span>
                    </div>
                    
                    <div className="space-y-1 mt-2 bg-zinc-50 dark:bg-zinc-900/50 p-2.5 rounded-xl border border-zinc-100 dark:border-zinc-800/50">
                      {item._type === 'meal' ? (
                        <>
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-[var(--text)]">{item.meal_name}</p>
                            <p className="text-xs font-bold text-emerald-500">{Math.round(item.total_nutrition?.calories_kcal || 0)} kcal</p>
                          </div>
                          <div className="flex justify-between text-[10px] text-zinc-500 font-medium">
                            <span className="uppercase">{item.meal_type}</span>
                            <div className="flex gap-2 text-[9px] font-mono">
                              <span>P: {Math.round(item.total_nutrition?.protein_g || 0)}g</span>
                              <span>C: {Math.round(item.total_nutrition?.carbs_g || 0)}g</span>
                              <span>F: {Math.round(item.total_nutrition?.fat_g || 0)}g</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-[var(--text)]">{item.name || item.barcode || 'Unknown Product'}</p>
                            {item.nutrition?.calories_kcal && (
                              <p className="text-xs font-bold text-blue-500">{Math.round(item.nutrition.calories_kcal)} kcal <span className="text-[9px] text-zinc-400">/100g</span></p>
                            )}
                          </div>
                          <div className="flex justify-between text-[10px] text-zinc-500 font-medium">
                            <span className="font-mono">{item.barcode}</span>
                            {item.nutrition && (
                              <div className="flex gap-2 text-[9px] font-mono">
                                <span>P: {Math.round(item.nutrition.protein_g || 0)}g</span>
                                <span>C: {Math.round(item.nutrition.carbs_g || 0)}g</span>
                                <span>F: {Math.round(item.nutrition.fat_g || 0)}g</span>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <p className="text-[9px] text-zinc-400 pl-1">{item.email || 'No email'}</p>
                  </div>
                ))}
              </div>
            )
          ) : activeTab === 'tester' ? (
          <AdminTesterPanel
            usersList={usersList}
            lang={lang}
            t={t}
            timeFormat={timeFormat}
            currentAdminTier={subscriptionTier}
            onSwitchAdminTier={(tier) => handleUpgradeSubscription(tier)}
            isEveningWrapUpTestEnabled={isEveningWrapUpTestEnabled}
            onRefreshUsers={() => handleSearch("")}
            onToggleEveningWrapUpTest={() => {
              if (setIsEveningWrapUpTestEnabled) {
                setIsEveningWrapUpTestEnabled(!isEveningWrapUpTestEnabled);
              }
              if (setIsDismissedWrapUp) {
                setIsDismissedWrapUp(false);
              }
            }}
            onLogPastMeal={(data) => {
              const targetDateObj = new Date(data.targetDate);
              const analysis: MealAnalysis = {
                meal_name: data.mealName,
                meal_type: data.mealType as any,
                foods: [
                  {
                    name: data.mealName,
                    category: 'Protein' as any,
                    portion: '1 serving',
                    amount: 1,
                    unit: 'serving',
                    calories_kcal: data.calories,
                    protein_g: data.protein,
                    carbs_g: data.carbs,
                    fat_g: data.fat,
                    sugar_g: 0,
                    fiber_g: 0,
                    sodium_mg: 0
                  }
                ],
                total_nutrition: {
                  calories_kcal: data.calories,
                  protein_g: data.protein,
                  carbs_g: data.carbs,
                  fat_g: data.fat,
                  sugar_g: 0,
                  fiber_g: 0,
                  sodium_mg: 0
                },
                notes: `Back-dated meal logged by Admin for ${data.targetDate}`,
                insight: `Admin back-dated meal record (${data.calories} kcal).`
              };

              if (typeof (window as any).__appSaveToHistory === 'function') {
                (window as any).__appSaveToHistory(analysis, '', 'Past meal logged for admin stats!', false, targetDateObj);
              }
            }}
          />
        ) : (
          reports.length === 0 ? (
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-12 text-center card-shadow">
              <motion.div
                initial={{ scale: 0, rotate: -45 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
              </motion.div>
              <p className="text-zinc-500 font-bold uppercase tracking-widest">{t.noBugs || 'No bug reports found'}</p>
            </div>
          ) : (
            reports.map((report: any) => (
              <div key={report.id} className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 space-y-3 card-shadow">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest">{report.userEmail || 'Anonymous'}</p>
                    <p className="text-[10px] text-zinc-400">{new Date(report.timestamp).toLocaleString()}</p>
                  </div>
                  <button 
                    onClick={async () => {
                      if (confirm('Delete this report?')) {
                        await deleteDocWithQueue(doc(db, 'bug_reports', report.id));
                        setReports(prev => prev.filter(r => r.id !== report.id));
                        toast.success('Report deleted');
                      }
                    }}
                    className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-[var(--text)] leading-relaxed whitespace-pre-wrap">{report.description}</p>
                {report.image && (
                  <div className="relative group/img">
                    <img 
                      src={report.image} 
                      alt="Bug Screenshot" 
                      loading="lazy"
                      decoding="async"
                      className="w-full max-h-64 object-contain rounded-xl border border-[var(--card-border)] bg-zinc-50 dark:bg-zinc-800/50" 
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                      <button 
                        onClick={() => window.open(report.image, '_blank')}
                        className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest text-white border border-white/20"
                      >
                        Open Full Size
                      </button>
                    </div>
                  </div>
                )}
                <div className="pt-3 border-t border-[var(--card-border)]">
                  <p className="text-[9px] text-zinc-400 font-mono break-all line-clamp-2">{report.userAgent}</p>
                </div>
              </div>
            ))
          )
        )}
      </div>
    </motion.div>
  );
};

const MICRONUTRIENT_TARGETS: { [key: string]: { name: string; target: number; unit: string } } = {
  potassium_mg: { name: 'Potassium', target: 4700, unit: 'mg' },
  calcium_mg: { name: 'Calcium', target: 1300, unit: 'mg' },
  iron_mg: { name: 'Iron', target: 18, unit: 'mg' },
  vitamin_c_mg: { name: 'Vitamin C', target: 90, unit: 'mg' },
  vitamin_a_mcg: { name: 'Vitamin A', target: 900, unit: 'mcg' },
  vitamin_d_mcg: { name: 'Vitamin D', target: 20, unit: 'mcg' },
  vitamin_b12_mcg: { name: 'Vitamin B12', target: 2.4, unit: 'mcg' },
  magnesium_mg: { name: 'Magnesium', target: 420, unit: 'mg' },
  zinc_mg: { name: 'Zinc', target: 11, unit: 'mg' }
};

const MICRONUTRIENT_FOODS: { [key: string]: { en: string[]; ua: string[] } } = {
  potassium_mg: {
    en: ['bananas', 'potatoes', 'spinach', 'avocados', 'white beans', 'salmon'],
    ua: ['банани', 'картоплю', 'шпинат', 'авокадо', 'білу квасолю', 'лосось']
  },
  calcium_mg: {
    en: ['yogurt', 'milk', 'cheese', 'tofu', 'almonds', 'kale', 'broccoli'],
    ua: ['йогурт', 'молоко', 'твердий сир', 'тофу', 'мигдаль', 'капусту кале', 'броколі']
  },
  iron_mg: {
    en: ['red meat', 'spinach', 'lentils', 'pumpkin seeds', 'quinoa', 'dark chocolate'],
    ua: ['червоне м’ясо', 'шпинат', 'сочевицю', 'гарбузове насіння', 'кіноа', 'чорний шоколад']
  },
  vitamin_c_mg: {
    en: ['oranges', 'strawberries', 'bell peppers', 'broccoli', 'kiwi'],
    ua: ['апельсини', 'полуницю', 'болгарський перець', 'броколі', 'ківі']
  },
  vitamin_a_mcg: {
    en: ['carrots', 'sweet potatoes', 'spinach', 'kale', 'cantaloupe', 'eggs'],
    ua: ['моркву', 'солодку картоплю (батат)', 'шпинат', 'капусту кале', 'диню', 'яйця']
  },
  vitamin_d_mcg: {
    en: ['salmon', 'mackerel', 'fortified milk', 'eggs', 'mushrooms'],
    ua: ['лосось', 'скумбрію', 'збагачене молоко', 'яйця', 'гриби']
  },
  vitamin_b12_mcg: {
    en: ['beef', 'salmon', 'tuna', 'milk', 'cheese', 'eggs'],
    ua: ['яловичину', 'лосось', 'тунець', 'молоко', 'сир', 'яйця']
  },
  magnesium_mg: {
    en: ['spinach', 'pumpkin seeds', 'almonds', 'dark chocolate', 'black beans'],
    ua: ['шпинат', 'гарбузове насіння', 'мигдаль', 'чорний шоколад', 'чорну квасолю']
  },
  zinc_mg: {
    en: ['beef', 'chickpeas', 'pumpkin seeds', 'lentils', 'cashews'],
    ua: ['яловичину', 'нут', 'гарбузове насіння', 'сочевицю', 'кеш’ю']
  }
};

const MACRO_FOODS: { [key: string]: { en: string[]; ua: string[] } } = {
  protein_g: {
    en: ['chicken breast', 'eggs', 'tofu', 'beef', 'salmon', 'tuna', 'greek yogurt', 'lentils'],
    ua: ['куряче філе', 'яйця', 'тофу', 'яловичина', 'лосось', 'тунець', 'грецький йогурт', 'сочевиця']
  },
  carbs_g: {
    en: ['rice', 'oats', 'pasta', 'sweet potatoes', 'quinoa', 'bananas', 'bread', 'potatoes'],
    ua: ['рис', 'вівсянка', 'макарони', 'солодка картопля', 'кіноа', 'банани', 'хліб', 'картопля']
  },
  fat_g: {
    en: ['avocados', 'almonds', 'walnuts', 'olive oil', 'pumpkin seeds', 'chia seeds', 'peanut butter', 'salmon'],
    ua: ['авокадо', 'мигдаль', 'волоські горіхи', 'оливкова олія', 'гарбузове насіння', 'насіння чіа', 'арахісова паста', 'лосось']
  }
};

interface DeficiencyAlert {
  nutrientKey: string;
  nutrientName: string;
  daysCount: number;
  consecutiveDates: string[];
  intakePercentages: number[];
  recommendedFoods: string[];
  severity: 'warning' | 'info';
  isMacro?: boolean;
}

const ProgressBar = ({ 
  label, 
  current, 
  goal, 
  colorClass, 
  unit = 'g', 
  isNutrient, 
  isSaltSugar, 
  isFat, 
  isCarbs, 
  noSuccessColor, 
  onClick, 
  projected, 
  gradientClass, 
  lang 
}: { 
  label: string, 
  current: number, 
  goal: number, 
  colorClass?: string, 
  unit?: string, 
  isNutrient?: boolean, 
  isSaltSugar?: boolean, 
  isFat?: boolean, 
  isCarbs?: boolean, 
  noSuccessColor?: boolean, 
  onClick?: () => void, 
  projected?: number, 
  gradientClass?: string, 
  lang: string 
}) => {
  const percentage = Math.min((current / goal) * 100, 100);
  const projectedPercentage = projected ? Math.min((projected / goal) * 100, 100) : 0;
  
  const labelLower = label.toLowerCase();
  const unitLower = (unit || '').toLowerCase();

  // Identify specific nutrients precisely to align with user rules
  const isThisWater = labelLower.includes('water') || labelLower.includes('вода') || unitLower.includes('ml') || unitLower.includes('мл');
  const isThisFiber = labelLower.includes('fiber') || labelLower.includes('клітковина') || labelLower.includes('клітку') || labelLower.includes('пищевые волокна') || labelLower.includes('fib');
  const isThisProtein = labelLower.includes('protein') || labelLower.includes('білок') || labelLower.includes('білки') || labelLower.includes('белок') || labelLower.includes('белки') || labelLower.includes('prot');
  const isThisFat = isFat || labelLower.includes('fat') || labelLower.includes('жир');
  const isThisSugar = labelLower.includes('sugar') || labelLower.includes('цукор') || labelLower.includes('сахар') || (isSaltSugar && labelLower.includes('sugar'));
  const isThisSalt = labelLower.includes('salt') || labelLower.includes('сіль') || labelLower.includes('соль') || labelLower.includes('sodium') || labelLower.includes('натрій') || labelLower.includes('натрий');
  const isThisCaffeine = labelLower.includes('caffeine') || labelLower.includes('кофеїн') || labelLower.includes('кофеин');
  const isThisCarbs = isCarbs || labelLower.includes('carbs') || labelLower.includes('вуглеводи') || labelLower.includes('углеводы') || labelLower.includes('carb');
  const isMacro = isThisProtein || isThisFat || isThisCarbs;

  const isAnime = document.querySelector('.skin-anime') !== null;
  const isCalories = labelLower.includes('calories') || labelLower.includes('калорії') || labelLower.includes('exp');
  
  // Fiber, Water, and Protein are exempt from any breach/going red or shaking
  const isExempt = isThisWater || isThisFiber || isThisProtein;

  // Immediate breach nutrients (salt, sugar, fat, caffeine) go red/shake immediately when target is exceeded
  const isImmediateBreachNutrient = isThisFat || isThisSugar || isThisSalt || isThisCaffeine;

  // Other nutrients go red and shake only when exceeding 125% of the target value (exceed 25% of target)
  const isBreached = isExempt ? false : (
    isImmediateBreachNutrient 
      ? (current > goal) 
      : (current > goal * 1.25)
  );

  // For salt, sugar, fat, caffeine - goal met is when under limit. For others, it is when target is met.
  const isGoalMet = isSaltSugar 
    ? (current > 0 && current <= goal) 
    : (current >= goal);

  let barClassName = colorClass || "";
  let effectiveGradient = gradientClass;

  if (isAnime) {
    if (isCalories) {
      effectiveGradient = "from-[#ff5e98] via-[#ec4899] to-[#a855f7]";
      label = "EXP (Calories) ★";
    } else if (isThisWater) {
      effectiveGradient = "from-[#38bdf8] to-[#6366f1]";
      label = "MP (Water) 💧";
    } else if (isThisProtein) {
      effectiveGradient = "from-[#ff75a0] to-[#f43f5e]";
      label = "ATK (Protein) 🍖";
    } else if (isThisCarbs) {
      effectiveGradient = "from-[#38bdf8] to-[#06b6d4]";
      label = "SPD (Carbs) ⚡";
    } else if (isThisFat) {
      effectiveGradient = "from-[#fbbf24] to-[#f59e0b]";
      label = "DEF (Fats) 🛡️";
    } else if (isThisFiber) {
      effectiveGradient = "from-[#34d399] to-[#10b981]";
      label = "DEX (Fiber) 🌿";
    }
  }

  if (isBreached && !isAnime) {
    // Generate a gradient from the nutrient's normal color (from gradientClass or colorClass) to red
    let baseFromClass = "from-emerald-600"; // fallback
    if (gradientClass) {
      const fromMatch = gradientClass.match(/from-([a-z0-9-]+)/);
      if (fromMatch) {
        baseFromClass = fromMatch[0];
      }
    } else if (colorClass) {
      if (colorClass.startsWith('bg-')) {
        baseFromClass = colorClass.replace('bg-', 'from-');
      }
    }
    effectiveGradient = `${baseFromClass} to-red-500`;
    barClassName = "";
  } else if (isSaltSugar) {
    const ratio = current / goal;
    if (ratio > 0.85 && ratio <= 1) {
      barClassName = "bg-amber-500";
      effectiveGradient = "from-amber-600 to-amber-400";
    }
  }
  
  const vibrancy = percentage >= 70 ? 1 + (percentage - 70) / 30 : 1;
  const barStyle: React.CSSProperties = { filter: `saturate(${vibrancy})` };
  
  return (
    <div className={cn("space-y-3", onClick && "cursor-pointer group")} onClick={onClick}>
      <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-widest">
        <div className="flex items-center gap-1.5">
          <span className={cn("text-zinc-500 dark:text-zinc-400 font-black whitespace-pre-line leading-tight transition-colors", onClick && "group-hover:text-emerald-500", isBreached && "text-red-500 dark:text-red-400")}>{label}</span>
          {isGoalMet && !isBreached && (isSaltSugar ? current > 0 : true) && (
            <AnimatePresence>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex gap-0.5 items-center"
              >
                <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
              </motion.div>
            </AnimatePresence>
          )}
          {isBreached && (
            <AnimatePresence>
              <motion.div
                initial={{ scale: 0, rotate: -15 }}
                animate={{ scale: [1, 1.15, 1], rotate: [0, 10, -10, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="flex gap-0.5 items-center"
              >
                <Flame className="w-3 h-3 text-red-500 fill-red-500" />
              </motion.div>
            </AnimatePresence>
          )}
        </div>
        <span className={cn("text-[#71717b] dark:text-zinc-500 normal-case font-bold flex flex-wrap items-center justify-end gap-1 sm:gap-1.5 text-[10px] sm:text-xs", isBreached ? "text-red-500 font-black" : (isGoalMet && !noSuccessColor ? "text-emerald-500" : noSuccessColor ? "text-purple-500" : ""))}>
          <span className="whitespace-nowrap">
            {typeof current === 'number' ? Math.round(current) : current}{unit} {projected && projected > current && (
              <span className="text-emerald-500 dark:text-emerald-400 font-extrabold ml-0.5 sm:ml-1 bg-emerald-500/10 px-1.5 py-0.5 rounded-[6px] text-[9px] sm:text-[10px] animate-pulse">
                +{Math.round(projected - current)}{unit} ({goal > 0 ? `+${Math.round(((projected - current) / goal) * 100)}%` : '+0%'})
              </span>
            )} / <span className="opacity-70">{goal}{unit}</span>
          </span>
          {goal > 0 && (() => {
            const badgeColor = isThisProtein 
              ? '#a855f7' 
              : isThisCarbs 
                ? '#0ea5e9' 
                : isThisFat 
                  ? '#f97316' 
                  : isThisWater 
                    ? '#3b82f6' 
                    : isThisFiber 
                      ? '#10B981' 
                      : isThisSugar 
                        ? '#0ea5e9' 
                        : isThisSalt 
                          ? '#64748b' 
                          : isThisCaffeine 
                            ? '#92400e' 
                            : (labelLower.includes('calories') || labelLower.includes('калорії') || labelLower.includes('калории') || labelLower.includes('kcal')) 
                              ? '#10B981' 
                              : '#71717b';
            
            const currentPct = Math.round((current / goal) * 100);
            const projectedPct = projected && projected > current ? Math.round((projected / goal) * 100) : currentPct;

            return (
              <motion.span 
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.15 }}
                className="text-[10px] font-black px-1.5 py-0.5 rounded transition-all duration-300 inline-block" 
                style={{ 
                  color: badgeColor,
                  backgroundColor: `${badgeColor}18`
                }}
              >
                {projected && projected > current ? `${currentPct}% → ${projectedPct}%` : `${currentPct}%`}
              </motion.span>
            );
          })()}
        </span>
      </div>
      <div className="h-3 bg-zinc-100 dark:bg-zinc-800/40 rounded-full overflow-hidden relative">
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "200%" }}
          transition={{ duration: 1.2, ease: "easeInOut", delay: 0.1 }}
          className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 dark:via-white/10 to-transparent pointer-events-none z-30"
        />
        {(projectedPercentage > percentage || isBreached) && (
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: isBreached ? "100%" : `${projectedPercentage}%` }}
            transition={{
              width: { type: "spring", stiffness: 65, damping: 16, delay: 0.05 }
            }}
            className={cn(
              "absolute inset-y-0 left-0 opacity-20 rounded-full z-0 h-full", 
              isBreached ? "bg-red-500" : (effectiveGradient ? `bg-gradient-to-r ${effectiveGradient}` : barClassName)
            )}
            style={barStyle}
          />
        )}
        <motion.div 
          initial={{ width: 0 }}
          animate={{ 
            width: `${percentage}%`,
            x: isBreached ? [0, -1.2, 1.2, -1.2, 1.2, 0] : 0,
            scaleY: isBreached ? [1, 1.15, 0.95, 1.15, 1] : 1,
            boxShadow: isBreached ? [
              "0 0 0px rgba(239, 68, 68, 0)",
              "0 0 15px rgba(239, 68, 68, 0.6)",
              "0 0 0px rgba(239, 68, 68, 0)"
            ] : (isGoalMet && !noSuccessColor ? [
              "0 0 0px rgba(16, 185, 129, 0)",
              "0 0 12px rgba(16, 185, 129, 0.4)",
              "0 0 0px rgba(16, 185, 129, 0)"
            ] : "none")
          }}
          transition={{
            width: { type: "spring", stiffness: 65, damping: 16, delay: 0.05 },
            x: isBreached ? { repeat: Infinity, duration: 0.5, ease: "linear" } : {},
            scaleY: isBreached ? { repeat: Infinity, duration: 1.5, ease: "easeInOut" } : {},
            boxShadow: { repeat: Infinity, duration: 2 }
          }}
          style={barStyle}
          className={cn(
            "h-full rounded-full transition-colors duration-500 relative z-10", 
            effectiveGradient ? `bg-gradient-to-r ${effectiveGradient}` : barClassName,
            onClick && "group-hover:brightness-110",
            (isGoalMet && !noSuccessColor && !isBreached) && "animate-pulse-subtle"
          )}
        >
          {isAnime && percentage === 100 && isCalories && (
            <motion.div
              animate={{ opacity: [0, 1, 0], scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="absolute inset-0 bg-[#a855f7]/20 rounded-full shadow-[0_0_20px_#a855f7]"
            />
          )}
          {percentage > 0 && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="absolute right-0 top-0 bottom-0 w-2.5 bg-white/40 rounded-full shadow-[0_0_10px_rgba(255,255,255,0.9)] z-20" 
            />
          )}
        </motion.div>
      </div>

      <div className="hidden group-hover:block transition-all duration-300 opacity-0 group-hover:opacity-100 h-0 group-hover:h-auto group-hover:mt-2 text-left">
        <div className="p-3 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl text-[11px] font-medium leading-relaxed text-[var(--text)] space-y-2 card-shadow transition-all duration-300">
          <div className="flex justify-between items-center text-xs border-b border-[var(--card-border)] pb-1.5">
            <span className="font-bold text-zinc-800 dark:text-zinc-100">
              {lang === 'ua' ? 'Джерело інформації' : 'Nutrient Insights'}
            </span>
            <span className="font-mono text-[10px] bg-zinc-100 dark:bg-zinc-800/60 px-1.5 py-0.5 rounded text-zinc-600 dark:text-zinc-300">
              {label}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs pt-0.5">
            <div className="bg-zinc-50 dark:bg-zinc-900/30 p-2 rounded-xl border border-zinc-100/60 dark:border-zinc-800/20">
              <span className="text-[10px] text-zinc-400 dark:text-zinc-500 block uppercase tracking-wider font-semibold">
                {lang === 'ua' ? 'Денна Ціль' : 'Daily Goal'}
              </span>
              <span className="font-mono font-bold text-zinc-800 dark:text-zinc-100">
                {Math.round(goal)}{unit}
              </span>
            </div>
            
            <div className="bg-zinc-50 dark:bg-zinc-900/30 p-2 rounded-xl border border-zinc-100/60 dark:border-zinc-800/20">
              <span className="text-[10px] text-zinc-400 dark:text-zinc-500 block uppercase tracking-wider font-semibold">
                {isSaltSugar 
                  ? (lang === 'ua' ? 'До ліміту' : 'Remaining') 
                  : (lang === 'ua' ? 'Залишилось' : 'Remaining')}
              </span>
              <span className={cn(
                "font-mono font-bold",
                isSaltSugar 
                  ? (current > goal ? "text-red-500" : "text-emerald-500") 
                  : (current >= goal ? "text-emerald-500" : "text-amber-500")
              )}>
                {isSaltSugar ? (
                  current > goal 
                    ? `+${Math.round(current - goal)}${unit}` 
                    : `${Math.round(goal - current)}${unit}`
                ) : (
                  current >= goal 
                    ? (lang === 'ua' ? 'Виконано!' : 'Met!') 
                    : `${Math.round(goal - current)}${unit}`
                )}
              </span>
            </div>
          </div>

          <div className="text-[11px] text-zinc-600 dark:text-zinc-300 bg-zinc-50/50 dark:bg-zinc-900/20 p-2 rounded-xl border border-zinc-100/50 dark:border-zinc-800/10 mt-1">
            {isSaltSugar ? (
              current > goal ? (
                lang === 'ua' 
                  ? `⚠️ Ви перевищили рекомендований ліміт ${label.toLowerCase()} на ${Math.round(current - goal)}${unit}.`
                  : `⚠️ You've exceeded the recommended limit of ${label.toLowerCase()} by ${Math.round(current - goal)}${unit}.`
              ) : (
                lang === 'ua'
                  ? `✓ Ви в межах ліміту. Намагайтеся не перевищувати його.`
                  : `✓ You are within the limit. Keep aiming to stay below.`
              )
            ) : (
              current >= goal ? (
                lang === 'ua'
                  ? `🎉 Чудово! Ви повністю покрили свою норму ${label.toLowerCase()} на сьогодні.`
                  : `🎉 Excellent! You've successfully covered your daily intake requirement of ${label.toLowerCase()}.`
              ) : (
                lang === 'ua'
                  ? `💪 Потрібно ще ${Math.round(goal - current)}${unit} ${label.toLowerCase()} для досягнення цілі.`
                  : `💪 Need ${Math.round(goal - current)}${unit} more of ${label.toLowerCase()} to hit your daily goal.`
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DailyStreakProgress = ({ 
  streaks, 
  consecutiveStreak, 
  totals, 
  goals, 
  hasIncreased, 
  t, 
  lang, 
  illumination,
  onNutrientClick,
  viewMode = 'standard',
  skin
}: { 
  streaks: { 
    breakfast: boolean, 
    lunch: boolean, 
    dinner: boolean, 
    snack: boolean, 
    currentMeal: MealType | null, 
    count: number 
  }, 
  consecutiveStreak: number, 
  totals: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    water: number;
    sugar: number;
    sodium: number;
    caffeine: number;
  },
  goals: UserGoals,
  hasIncreased: boolean,
  skin: string,
  t: any,
  lang: string,
  illumination: boolean,
  onNutrientClick?: (key: string, label: string) => void,
  viewMode?: 'standard' | 'radial'
}) => {
  const [activeRing, setActiveRing] = useState<'calories' | 'protein' | 'carbs' | 'fat' | 'fiber' | 'water' | 'caffeine' | 'sugar' | 'sodium'>('calories');

  const [fireParticles, setFireParticles] = useState<Array<{
    id: number;
    x: number;
    y: number;
    tx: number;
    ty: number;
    size: number;
    color: string;
    delay: number;
    duration: number;
    rotateStart: number;
    rotateEnd: number;
    opacity: number;
  }>>([]);

  useEffect(() => {
    if (hasIncreased) {
      const colors = [
        '#ff3b30', // Cyber red
        '#ff9500', // Neon orange
        '#ffcc00', // Bright gold
        '#10B981', // Branded green accent
        '#ff5e36', // Cyber coral
      ];
      const newParticles = Array.from({ length: 24 }).map((_, i) => {
        const angle = (Math.random() * Math.PI) - (Math.PI / 2); // -90 deg to 90 deg (mostly upwards)
        const distance = 50 + Math.random() * 70; // random travel distance
        
        const tx = Math.sin(angle) * distance;
        const ty = -Math.cos(angle) * distance - 15; // always drift upward

        return {
          id: Date.now() + i,
          x: (Math.random() - 0.5) * 12,
          y: (Math.random() - 0.5) * 12,
          tx,
          ty,
          size: 4 + Math.random() * 9,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 0.25,
          duration: 0.7 + Math.random() * 0.7,
          rotateStart: Math.random() * 360,
          rotateEnd: Math.random() * 360 + (Math.random() > 0.5 ? 180 : -180),
          opacity: 0.8 + Math.random() * 0.2
        };
      });
      setFireParticles(newParticles);
    } else {
      const timer = setTimeout(() => {
        setFireParticles([]);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [hasIncreased]);

  const calsPercent = goals.calories > 0 ? (totals.calories / goals.calories) * 100 : 0;
  const proteinPercent = goals.protein > 0 ? (totals.protein / goals.protein) * 100 : 0;
  const carbsPercent = goals.carbs > 0 ? (totals.carbs / goals.carbs) * 100 : 0;
  const fatPercent = goals.fat > 0 ? (totals.fat / goals.fat) * 100 : 0;
  const fiberPercent = goals.fiber_g > 0 ? (totals.fiber / goals.fiber_g) * 100 : 0;

  const isCalsBreached = goals.calories > 0 && totals.calories > goals.calories * 1.25;
  const isProteinBreached = false;
  const isCarbsBreached = goals.carbs > 0 && totals.carbs > goals.carbs * 1.25;
  const isFatBreached = goals.fat > 0 && totals.fat > goals.fat;
  const isFiberBreached = false;

  const macrosList = useMemo(() => [
    { key: 'calories', label: t.calories, current: totals.calories, goal: goals.calories, percent: calsPercent, unit: t.kcal, color: isCalsBreached ? '#ef4444' : '#10B981', isBreached: isCalsBreached, bgClass: isCalsBreached ? 'bg-red-500/10 hover:bg-red-500/25' : 'bg-[#10B981]/10 hover:bg-[#10B981]/25', borderClass: isCalsBreached ? 'hover:border-red-500/40' : 'hover:border-[#10B981]/40', icon: <Flame className={cn("w-4 h-4", isCalsBreached ? "text-red-500" : "text-[#10B981]")} /> },
    { key: 'protein', label: t.protein, current: totals.protein, goal: goals.protein, percent: proteinPercent, unit: 'g', color: '#a855f7', isBreached: false, bgClass: 'bg-purple-500/10 hover:bg-purple-500/25', borderClass: 'hover:border-purple-500/40', icon: <Beef className="w-4 h-4 text-purple-400" /> },
    { key: 'carbs', label: t.carbs, current: totals.carbs, goal: goals.carbs, percent: carbsPercent, unit: 'g', color: isCarbsBreached ? '#ef4444' : '#0ea5e9', isBreached: isCarbsBreached, bgClass: isCarbsBreached ? 'bg-red-500/10 hover:bg-red-500/25' : 'bg-blue-500/10 hover:bg-blue-500/25', borderClass: isCarbsBreached ? 'hover:border-red-500/40' : 'hover:border-blue-500/40', icon: <Wheat className={cn("w-4 h-4", isCarbsBreached ? "text-red-500" : "text-blue-400")} /> },
    { key: 'fat', label: t.fat, current: totals.fat, goal: goals.fat, percent: fatPercent, unit: 'g', color: isFatBreached ? '#ef4444' : '#f97316', isBreached: isFatBreached, bgClass: isFatBreached ? 'bg-red-500/10 hover:bg-red-500/25' : 'bg-orange-500/10 hover:bg-orange-500/25', borderClass: isFatBreached ? 'hover:border-red-500/40' : 'hover:border-orange-500/40', icon: <Droplets className={cn("w-4 h-4", isFatBreached ? "text-red-500" : "text-orange-400")} /> },
    { key: 'fiber', label: t.fiber || 'Fiber', current: totals.fiber, goal: goals.fiber_g, percent: fiberPercent, unit: 'g', color: '#10B981', isBreached: false, bgClass: 'bg-emerald-500/10 hover:bg-emerald-500/25', borderClass: 'hover:border-emerald-500/40', icon: <Leaf className="w-4 h-4 text-emerald-400" /> },
  ], [totals, goals, t, calsPercent, proteinPercent, carbsPercent, fatPercent, fiberPercent, isCalsBreached, isCarbsBreached, isFatBreached]);

  const secondaryNutrientsList = useMemo(() => [
    { key: 'water', label: t.water, current: totals.water, goal: goals.water_ml, percent: goals.water_ml > 0 ? (totals.water / goals.water_ml) * 100 : 0, unit: t.ml, color: '#3b82f6', icon: <Waves className="w-4 h-4 text-blue-400" /> },
    { key: 'caffeine', label: t.caffeine || 'Caffeine', current: totals.caffeine, goal: goals.caffeine_mg, percent: goals.caffeine_mg > 0 ? (totals.caffeine / goals.caffeine_mg) * 100 : 0, unit: 'mg', color: '#92400e', icon: <Coffee className="w-4 h-4 text-amber-600" /> },
    { key: 'sugar', label: t.sugar, current: totals.sugar, goal: goals.sugar_g, percent: goals.sugar_g > 0 ? (totals.sugar / goals.sugar_g) * 100 : 0, unit: 'g', color: '#0ea5e9', icon: <Candy className="w-4 h-4 text-blue-400" /> },
    { key: 'sodium', label: totals.sodium / 1000, goal: goals.sodium_mg / 1000, percent: goals.sodium_mg > 0 ? (totals.sodium / goals.sodium_mg) * 100 : 0, unit: 'g', color: '#64748b', icon: <Zap className="w-4 h-4 text-slate-400" /> },
  ], [totals, goals, t]);

  const RadialProgressCard = ({ item, size = 56, strokeWidth = 5 }: { item: any, size?: number, strokeWidth?: number }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (Math.min(item.percent, 100) / 100) * circumference;
    const isSelected = activeRing === item.key;
    
    return (
      <div 
        onClick={() => {
          setActiveRing(item.key as any);
          if (onNutrientClick) onNutrientClick(item.key, item.label);
          sounds.playPop();
          triggerHaptic('SUCCESS');
        }}
        className={cn(
          "p-3 rounded-2xl border transition-all duration-300 cursor-pointer flex items-center gap-3 relative overflow-hidden select-none",
          isSelected 
            ? "bg-[var(--card-bg)] shadow-md scale-[1.02] ring-1 ring-[#10B981]/20" 
            : "bg-zinc-500/5 border-zinc-200/50 dark:border-zinc-800/30 hover:bg-zinc-500/10",
          item.borderClass
        )}
        style={isSelected ? { borderColor: item.color } : {}}
      >
        <div className="relative shrink-0" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="transform -rotate-90">
            <circle cx={size/2} cy={size/2} r={radius} stroke={item.color} strokeWidth={strokeWidth} fill="transparent" className="opacity-15" />
            <motion.circle
              cx={size/2}
              cy={size/2}
              r={radius}
              stroke={item.color}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center scale-90">
            {item.icon}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-wider text-zinc-400 dark:text-zinc-500 leading-none">{item.label}</p>
          <div className="flex items-baseline gap-1 mt-1 leading-none">
            <span className="text-sm font-extrabold text-[var(--text)] leading-none">{typeof item.current === 'number' ? Math.round(item.current) : item.current}</span>
            <span className="text-[10px] font-bold text-zinc-500 leading-none">/ {item.goal}{item.unit}</span>
          </div>
          <p className="text-[10px] font-black mt-1 leading-none" style={{ color: item.color }}>{Math.round(item.percent)}%</p>
        </div>
      </div>
    );
  };

  const getActiveNutrientData = () => {
    switch (activeRing) {
      case 'protein':
        return {
          label: t.protein,
          current: Math.round(totals.protein),
          goal: goals.protein,
          unit: 'g',
          color: '#a855f7',
          icon: <Beef className="w-5 h-5 text-[#a855f7]" />
        };
      case 'carbs':
        return {
          label: t.carbs,
          current: Math.round(totals.carbs),
          goal: goals.carbs,
          unit: 'g',
          color: '#0ea5e9',
          icon: <Wheat className="w-5 h-5 text-[#0ea5e9]" />
        };
      case 'fat':
        return {
          label: t.fat,
          current: Math.round(totals.fat),
          goal: goals.fat,
          unit: 'g',
          color: '#f97316',
          icon: <Droplets className="w-5 h-5 text-[#f97316]" />
        };
      case 'fiber':
        return {
          label: t.fiber || 'Fiber',
          current: Math.round(totals.fiber),
          goal: goals.fiber_g,
          unit: 'g',
          color: '#10B981',
          icon: <Leaf className="w-5 h-5 text-[#10B981]" />
        };
      case 'water':
        return {
          label: t.water,
          current: Math.round(totals.water),
          goal: goals.water_ml,
          unit: t.ml,
          color: '#3b82f6',
          icon: <Waves className="w-5 h-5 text-[#3b82f6]" />
        };
      case 'caffeine':
        return {
          label: t.caffeine || 'Caffeine',
          current: Math.round(totals.caffeine),
          goal: goals.caffeine_mg,
          unit: 'mg',
          color: '#92400e',
          icon: <Coffee className="w-5 h-5 text-[#92400e]" />
        };
      case 'sugar':
        return {
          label: t.sugar,
          current: Math.round(totals.sugar),
          goal: goals.sugar_g,
          unit: 'g',
          color: '#0ea5e9',
          icon: <Candy className="w-5 h-5 text-[#0ea5e9]" />
        };
      case 'sodium':
        return {
          label: t.sodium,
          current: +(totals.sodium / 1000).toFixed(2),
          goal: +(goals.sodium_mg / 1000).toFixed(2),
          unit: 'g',
          color: '#64748b',
          icon: <Zap className="w-5 h-5 text-[#64748b]" />
        };
      default:
        return {
          label: t.calories,
          current: Math.round(totals.calories),
          goal: goals.calories,
          unit: t.kcal,
          color: '#10B981',
          icon: <Flame className="w-5 h-5 text-[#10B981]" />
        };
    }
  };

  const activeNutrient = getActiveNutrientData();

  return (
    <div 
      id="daily-streak-section"
      className={cn("bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 space-y-6 relative overflow-hidden card-shadow transition-all mx-2 scroll-mt-24", illumination ? (activeRing === 'water' ? 'illumination-water' : 'illumination-food') : "")}
    >
      {illumination && (
        <>
          <div className="absolute -left-20 -top-20 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] pointer-events-none" />
        </>
      )}
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-4">
          <motion.div 
            animate={{
              scale: hasIncreased ? 1.25 : 1,
              rotate: hasIncreased ? [0, 12, -10, 6, 0] : 0,
            }}
            transition={{ 
              type: "spring",
              stiffness: 300,
              damping: 15,
            }}
            className="bg-amber-500/10 p-3 rounded-2xl relative overflow-visible"
          >
            <Flame className={cn("w-8 h-8", consecutiveStreak > 0 ? "text-amber-500 fill-amber-500 animate-pulse" : "text-zinc-300")} />
            
            {/* Fire Particle Effect */}
            <AnimatePresence>
              {hasIncreased && fireParticles.map((particle) => (
                <motion.div
                  key={particle.id}
                  initial={{
                    x: particle.x,
                    y: particle.y,
                    scale: 0.2,
                    opacity: particle.opacity,
                    rotate: particle.rotateStart,
                  }}
                  animate={{
                    x: particle.tx,
                    y: particle.ty,
                    scale: [0.5, 1.2, 0], // Swell then dissolve to nothingness
                    opacity: [particle.opacity, particle.opacity, 0],
                    rotate: particle.rotateEnd,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: particle.duration,
                    delay: particle.delay,
                    ease: "easeOut",
                  }}
                  className="absolute pointer-events-none rounded-full"
                  style={{
                    width: particle.size,
                    height: particle.size,
                    backgroundColor: particle.color,
                    boxShadow: `0 0 ${particle.size * 1.5}px ${particle.color}, 0 0 ${particle.size * 3}px ${particle.color}`,
                    left: "50%",
                    top: "50%",
                    marginLeft: -particle.size / 2,
                    marginTop: -particle.size / 2,
                  }}
                />
              ))}
            </AnimatePresence>

            {hasIncreased && (
              <motion.span 
                initial={{ scale: 0.8, opacity: 1 }}
                animate={{ scale: 2.2, opacity: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="absolute inset-0 bg-gradient-to-r from-amber-500 to-emerald-500 opacity-30 rounded-2xl pointer-events-none"
              />
            )}
          </motion.div>
          <div>
            <h3 className="text-lg font-bold leading-tight">
              {skin === 'stalker' ? (lang === 'ua' ? 'Днів виживання' : 'Days Survived') : t.dailyStreak}
            </h3>
            <p className="text-zinc-500 text-xs">
              {skin === 'stalker' ? (lang === 'ua' ? 'Онови біологічний маніфест, Одиначе.' : 'Update your biological manifest, Loner.') : t.logMeals}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="flex items-baseline justify-end gap-1">
              <motion.span
                animate={{
                  scale: hasIncreased ? [1, 1.45, 0.9, 1.25, 0.95, 1] : 1,
                  y: hasIncreased ? [0, -12, 3, -6, 1, 0] : 0,
                  color: hasIncreased ? ["#f59e0b", "#10B981", "#fbbf24", "#10B981"] : "#f59e0b",
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 10,
                  mass: 0.5,
                }}
                className="text-3xl font-black text-amber-500 inline-block streak-counter-number"
              >
                {consecutiveStreak}
              </motion.span>
              <span className="text-[#71717b] text-[10px] font-bold uppercase tracking-widest">{t.days || 'DAYS'}</span>
            </div>
            <p className="text-[10px] text-[#71717b] font-bold uppercase tracking-widest">{t.streakSuffix || 'STREAK'}</p>
          </div>
        </div>
      </div>
      
      <AnimatePresence mode="wait">
        {viewMode === 'radial' ? (
          <motion.div
            key="radial"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              {/* Left Side: Concentric Rings Graph */}
              <div className="md:col-span-5 flex justify-center">
                <motion.div 
                  id="ring-progress"
                  className="relative w-56 h-56 sm:w-64 sm:h-64 select-none flex items-center justify-center mx-auto"
                  animate={(isCalsBreached || isProteinBreached || isCarbsBreached || isFatBreached) ? {
                    x: [0, -0.5, 0.5, -0.5, 0.5, 0],
                    scale: [1, 1.02, 0.98, 1.02, 1]
                  } : {}}
                  transition={(isCalsBreached || isProteinBreached || isCarbsBreached || isFatBreached) ? {
                    x: { repeat: Infinity, duration: 0.5, ease: "linear" },
                    scale: { repeat: Infinity, duration: 2, ease: "easeInOut" }
                  } : {}}
                >
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 240 240">
                    {/* Ring 1: Calories */}
                    <circle cx="120" cy="120" r="108" stroke={isCalsBreached ? "#ef4444" : "#10B981"} strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                       cx="120"
                       cy="120"
                       r="108"
                       stroke={isCalsBreached ? "#ef4444" : "#10B981"}
                       strokeWidth="7"
                       fill="transparent"
                       strokeLinecap="round"
                       strokeDasharray={2 * Math.PI * 108}
                       initial={{ strokeDashoffset: 2 * Math.PI * 108 }}
                       animate={{ 
                         strokeDashoffset: 2 * Math.PI * 108 - (Math.min(calsPercent, 100) / 100) * (2 * Math.PI * 108),
                         strokeWidth: isCalsBreached ? [7, 8.5, 7] : (activeRing === 'calories' ? 8.5 : 7)
                       }}
                       transition={{ 
                         strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                         strokeWidth: isCalsBreached ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : { duration: 0.2 }
                       }}
                       className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'calories' && "stroke-[8.5px]")}
                       onClick={() => { setActiveRing('calories'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />

                    {/* Ring 2: Protein */}
                    <circle cx="120" cy="120" r="94" stroke={isProteinBreached ? "#ef4444" : "#a855f7"} strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                       cx="120"
                       cy="120"
                       r="94"
                       stroke={isProteinBreached ? "#ef4444" : "#a855f7"}
                       strokeWidth="7"
                       fill="transparent"
                       strokeLinecap="round"
                       strokeDasharray={2 * Math.PI * 94}
                       initial={{ strokeDashoffset: 2 * Math.PI * 94 }}
                       animate={{ 
                         strokeDashoffset: 2 * Math.PI * 94 - (Math.min(proteinPercent, 100) / 100) * (2 * Math.PI * 94),
                         strokeWidth: isProteinBreached ? [7, 8.5, 7] : (activeRing === 'protein' ? 8.5 : 7)
                       }}
                       transition={{ 
                         strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                         strokeWidth: isProteinBreached ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : { duration: 0.2 }
                       }}
                       className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'protein' && "stroke-[8.5px]")}
                       onClick={() => { setActiveRing('protein'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />

                    {/* Ring 3: Carbs */}
                    <circle cx="120" cy="120" r="80" stroke={isCarbsBreached ? "#ef4444" : "#0ea5e9"} strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                       cx="120"
                       cy="120"
                       r="80"
                       stroke={isCarbsBreached ? "#ef4444" : "#0ea5e9"}
                       strokeWidth="7"
                       fill="transparent"
                       strokeLinecap="round"
                       strokeDasharray={2 * Math.PI * 80}
                       initial={{ strokeDashoffset: 2 * Math.PI * 80 }}
                       animate={{ 
                         strokeDashoffset: 2 * Math.PI * 80 - (Math.min(carbsPercent, 100) / 100) * (2 * Math.PI * 80),
                         strokeWidth: isCarbsBreached ? [7, 8.5, 7] : (activeRing === 'carbs' ? 8.5 : 7)
                       }}
                       transition={{ 
                         strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                         strokeWidth: isCarbsBreached ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : { duration: 0.2 }
                       }}
                       className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'carbs' && "stroke-[8.5px]")}
                       onClick={() => { setActiveRing('carbs'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />

                    {/* Ring 4: Fat */}
                    <circle cx="120" cy="120" r="66" stroke={isFatBreached ? "#ef4444" : "#f97316"} strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                       cx="120"
                       cy="120"
                       r="66"
                       stroke={isFatBreached ? "#ef4444" : "#f97316"}
                       strokeWidth="7"
                       fill="transparent"
                       strokeLinecap="round"
                       strokeDasharray={2 * Math.PI * 66}
                       initial={{ strokeDashoffset: 2 * Math.PI * 66 }}
                       animate={{ 
                         strokeDashoffset: 2 * Math.PI * 66 - (Math.min(fatPercent, 100) / 100) * (2 * Math.PI * 66),
                         strokeWidth: isFatBreached ? [7, 8.5, 7] : (activeRing === 'fat' ? 8.5 : 7)
                       }}
                       transition={{ 
                         strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                         strokeWidth: isFatBreached ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : { duration: 0.2 }
                       }}
                       className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'fat' && "stroke-[8.5px]")}
                       onClick={() => { setActiveRing('fat'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />

                    {/* Ring 5: Fiber */}
                    <circle cx="120" cy="120" r="52" stroke="#10B981" strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                       cx="120"
                       cy="120"
                       r="52"
                       stroke="#10B981"
                       strokeWidth="7"
                       fill="transparent"
                       strokeLinecap="round"
                       strokeDasharray={2 * Math.PI * 52}
                       initial={{ strokeDashoffset: 2 * Math.PI * 52 }}
                       animate={{ 
                         strokeDashoffset: 2 * Math.PI * 52 - (Math.min(fiberPercent, 100) / 100) * (2 * Math.PI * 52),
                         strokeWidth: activeRing === 'fiber' ? 8.5 : 7
                       }}
                       transition={{ 
                         strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                         strokeWidth: { duration: 0.2 }
                       }}
                       className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'fiber' && "stroke-[8.5px]")}
                       onClick={() => { setActiveRing('fiber'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />
                  </svg>

                  {/* Inner dynamic content overlay (unrotated text) */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center pointer-events-none">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeRing}
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col items-center justify-center"
                      >
                        <div className="p-1 rounded-full bg-zinc-500/10 mb-0.5 flex items-center justify-center">
                          {activeNutrient.icon}
                        </div>
                        <div className="flex items-baseline justify-center gap-0.5 leading-none mt-0.5">
                          <span className="text-xl sm:text-2xl font-black text-[var(--text)] tracking-tight">
                            {activeNutrient.current}
                          </span>
                          <span className="text-[10px] sm:text-xs font-bold text-zinc-500">
                            {activeNutrient.unit}
                          </span>
                        </div>
                        <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mt-1 leading-tight">
                          {activeNutrient.label}
                        </span>
                        <span className="text-[10px] sm:text-[11px] font-extrabold mt-0.5 leading-none" style={{ color: activeNutrient.color }}>
                          {Math.round((activeNutrient.current / (activeNutrient.goal || 1)) * 100)}%
                        </span>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </motion.div>
              </div>

              {/* Right Side: Interactive Micro Macro Dashboard */}
              <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {macrosList.map((item, idx) => {
                  const isSelected = activeRing === item.key;
                  const itemCirc = 2 * Math.PI * 14;
                  return (
                    <div 
                      key={item.key}
                      onClick={() => {
                        setActiveRing(item.key as any);
                        sounds.playPop();
                        triggerHaptic('SUCCESS');
                      }}
                      className={cn(
                        "p-3 rounded-2xl border transition-all duration-300 cursor-pointer flex items-center gap-3 relative overflow-hidden select-none",
                        isSelected 
                          ? "bg-[var(--card-bg)] shadow-md scale-[1.02] ring-1 ring-[#10B981]/20" 
                          : "bg-zinc-500/5 border-zinc-200/50 dark:border-zinc-800/30 hover:bg-zinc-500/10",
                        item.borderClass,
                        idx === 4 && "col-span-2 sm:col-span-1"
                      )}
                      style={isSelected ? { borderColor: item.color } : {}}
                    >
                      {skin === 'anime' ? (
                        <div className="flex-1 w-full flex flex-col justify-center">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5">
                              <span className="scale-75">{item.icon}</span>
                              <p className="text-[10px] font-black uppercase tracking-wider text-purple-300 leading-none drop-shadow-[0_0_4px_rgba(168,85,247,0.6)]">{item.label}</p>
                            </div>
                            <div className="flex items-baseline gap-1 leading-none">
                              <span className="text-sm font-extrabold text-pink-400 leading-none drop-shadow-[0_0_4px_rgba(236,72,153,0.6)]">{Math.round(item.current)}</span>
                              <span className="text-[9px] font-bold text-zinc-500 leading-none">/ {item.goal}</span>
                            </div>
                          </div>
                          <div className="h-2.5 w-full bg-pink-950/30 dark:bg-purple-950/40 border border-pink-400/40 rounded-full overflow-hidden relative shadow-[inset_0_0_6px_rgba(0,0,0,0.5)]">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.min(item.percent, 100)}%` }}
                              transition={{ duration: 1, ease: "easeOut" }}
                              className="h-full bg-gradient-to-r from-[#ff5e98] via-[#ec4899] to-[#a855f7] shadow-[0_0_12px_rgba(236,72,153,0.8)] relative rounded-full"
                            >
                              <div className="absolute inset-0 bg-white/30 animate-pulse mix-blend-overlay rounded-full" />
                            </motion.div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="relative shrink-0 w-10 h-10">
                            <svg width="40" height="40" className="transform -rotate-90">
                              <circle cx="20" cy="20" r="14" stroke={item.color} strokeWidth="3" fill="transparent" className="opacity-15" />
                              <motion.circle
                                cx="20"
                                cy="20"
                                r="14"
                                stroke={item.color}
                                strokeWidth="3"
                                fill="transparent"
                                strokeLinecap="round"
                                strokeDasharray={itemCirc}
                                initial={{ strokeDashoffset: itemCirc }}
                                animate={{ strokeDashoffset: itemCirc - (Math.min(item.percent, 100) / 100) * itemCirc }}
                                transition={{ duration: 1, ease: "easeOut" }}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center scale-75">
                              {item.icon}
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[9px] font-black uppercase tracking-wider text-zinc-500 leading-none">{item.label}</p>
                            <div className="flex items-baseline gap-1 mt-1 leading-none">
                              <span className="text-sm font-extrabold text-[var(--text)] leading-none">{Math.round(item.current)}</span>
                              <span className="text-[9px] font-bold text-zinc-500 leading-none">/ {item.goal}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-zinc-200/30 dark:border-zinc-800/30 my-2" />

            {/* Other parameters (Water, Caffeine, Sugar, Salt) as separate radial rings */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {secondaryNutrientsList.map((item) => (
                <RadialProgressCard key={item.key} item={item} />
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="standard"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6 relative z-10"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-[#71717b]">
                <div className="flex items-center gap-1">
                  <span>{t.calories}</span>
                  <AnimatePresence>
                    {goals.calories > 0 && Math.round(totals.calories) >= Math.floor(goals.calories * 0.95) && (
                      <motion.div
                        initial={{ scale: 0, rotate: -45, opacity: 0 }}
                        animate={{ scale: 1, rotate: 0, opacity: 1 }}
                        transition={{ 
                          type: "spring", 
                          stiffness: 500, 
                          damping: 15,
                          mass: 0.8
                        }}
                      >
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <span className="text-emerald-500 font-bold">{Math.round(totals.calories)} <span className="text-[#71717b] font-normal">/ {goals.calories}</span></span>
              </div>
              <div className="h-3 bg-zinc-100 dark:bg-zinc-800/40 rounded-full overflow-hidden relative">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ 
                    width: `${calsPercent}%`,
                    boxShadow: goals.calories > 0 && Math.round(totals.calories) >= Math.floor(goals.calories * 0.95) ? [
                      "0 0 0px rgba(16, 185, 129, 0)",
                      "0 0 15px rgba(16, 185, 129, 0.5)",
                      "0 0 0px rgba(16, 185, 129, 0)"
                    ] : "none"
                  }}
                  transition={{
                    width: { type: "spring", stiffness: 100, damping: 20 },
                    boxShadow: { repeat: Infinity, duration: 2 }
                  }}
                  className={cn(
                    "h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full relative transition-colors duration-500",
                    goals.calories > 0 && Math.round(totals.calories) >= Math.floor(goals.calories * 0.95) && "animate-pulse-subtle"
                  )}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-[#71717b]">
                <div className="flex items-center gap-1">
                  <span>{t.water}</span>
                  <AnimatePresence>
                    {Math.round(totals.water) >= goals.water_ml && (
                      <motion.div
                        initial={{ scale: 0, rotate: -45, opacity: 0 }}
                        animate={{ scale: 1, rotate: 0, opacity: 1 }}
                        transition={{ 
                          type: "spring", 
                          stiffness: 500, 
                          damping: 15,
                          mass: 0.8
                        }}
                      >
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <span className="text-[#71717b]">{Math.round(totals.water)} / {goals.water_ml}{t.ml || 'ml'}</span>
              </div>
              <div className="h-3 bg-zinc-100 dark:bg-zinc-800/40 rounded-full overflow-hidden relative">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(totals.water / goals.water_ml) * 100}%` }}
                  transition={{
                    width: { type: "spring", stiffness: 100, damping: 20 }
                  }}
                  className={cn(
                    "h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full relative transition-colors duration-500",
                    Math.round(totals.water) >= goals.water_ml && "animate-pulse-subtle"
                  )}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="flex justify-between px-1 relative z-10">
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <div className={cn(
              "w-2 h-2 rounded-full transition-all duration-300", 
              streaks.breakfast ? "bg-emerald-500" : "bg-[var(--input-bg)] border border-[var(--card-border)]",
              streaks.currentMeal === 'breakfast' && !streaks.breakfast && "ring-2 ring-emerald-500 ring-offset-2 animate-pulse"
            )} />
            <AnimatePresence>
              {streaks.breakfast && (
                <motion.div
                  initial={{ scale: 0, opacity: 0, rotate: -15 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 600, 
                    damping: 12,
                    mass: 0.5
                  }}
                  className="absolute -top-1 -right-1"
                >
                  <div className="bg-emerald-500 rounded-full p-0.5 shadow-sm border border-white/20">
                    <CheckCircle2 className="w-2 h-2 text-white" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <span className={cn(
            "text-[10px] font-bold uppercase tracking-tighter transition-colors duration-300", 
            streaks.breakfast ? "text-emerald-500" : (streaks.currentMeal === 'breakfast' ? "text-emerald-500/70" : "text-zinc-500")
          )}>{t.breakfast}</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <div className={cn(
              "w-2 h-2 rounded-full transition-all duration-300", 
              streaks.lunch ? "bg-emerald-500" : "bg-[var(--input-bg)] border border-[var(--card-border)]",
              streaks.currentMeal === 'lunch' && !streaks.lunch && "ring-2 ring-emerald-500 ring-offset-2 animate-pulse"
            )} />
            <AnimatePresence>
              {streaks.lunch && (
                <motion.div
                  initial={{ scale: 0, opacity: 0, rotate: -15 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 600, 
                    damping: 12,
                    mass: 0.5
                  }}
                  className="absolute -top-1 -right-1"
                >
                  <div className="bg-emerald-500 rounded-full p-0.5 shadow-sm border border-white/20">
                    <CheckCircle2 className="w-2 h-2 text-white" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <span className={cn(
            "text-[10px] font-bold uppercase tracking-tighter transition-colors duration-300", 
            streaks.lunch ? "text-emerald-500" : (streaks.currentMeal === 'lunch' ? "text-emerald-500/70" : "text-zinc-500")
          )}>{t.lunch}</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <div className={cn(
              "w-2 h-2 rounded-full transition-all duration-300", 
              streaks.dinner ? "bg-emerald-500" : "bg-[var(--input-bg)] border border-[var(--card-border)]",
              streaks.currentMeal === 'dinner' && !streaks.dinner && "ring-2 ring-emerald-500 ring-offset-2 animate-pulse"
            )} />
            <AnimatePresence>
              {streaks.dinner && (
                <motion.div
                  initial={{ scale: 0, opacity: 0, rotate: -15 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 600, 
                    damping: 12,
                    mass: 0.5
                  }}
                  className="absolute -top-1 -right-1"
                >
                  <div className="bg-emerald-500 rounded-full p-0.5 shadow-sm border border-white/20">
                    <CheckCircle2 className="w-2 h-2 text-white" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <span className={cn(
            "text-[10px] font-bold uppercase tracking-tighter transition-colors duration-300", 
            streaks.dinner ? "text-emerald-500" : (streaks.currentMeal === 'dinner' ? "text-emerald-500/70" : "text-zinc-500")
          )}>{t.dinner}</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <div className={cn(
              "w-2 h-2 rounded-full transition-all duration-300", 
              streaks.snack ? "bg-emerald-500" : "bg-[var(--input-bg)] border border-[var(--card-border)]",
              streaks.currentMeal === 'snack' && !streaks.snack && "ring-2 ring-emerald-500 ring-offset-2 animate-pulse"
            )} />
            <AnimatePresence>
              {streaks.snack && (
                <motion.div
                  initial={{ scale: 0, opacity: 0, rotate: -15 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 600, 
                    damping: 12,
                    mass: 0.5
                  }}
                  className="absolute -top-1 -right-1"
                >
                  <div className="bg-emerald-500 rounded-full p-0.5 shadow-sm border border-white/20">
                    <CheckCircle2 className="w-2 h-2 text-white" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <span className={cn(
            "text-[10px] font-bold uppercase tracking-tighter transition-colors duration-300", 
            streaks.snack ? "text-emerald-500" : (streaks.currentMeal === 'snack' ? "text-emerald-500/70" : "text-zinc-500")
          )}>{t.snack}</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <div className={cn(
              "w-2 h-2 rounded-full transition-all duration-300", 
              totals.water >= goals.water_ml ? "bg-blue-500" : "bg-[var(--input-bg)] border border-[var(--card-border)]",
              totals.water < goals.water_ml && totals.water > 0 && "ring-2 ring-blue-500 ring-offset-2 animate-pulse"
            )} />
            <AnimatePresence>
              {totals.water >= goals.water_ml && (
                <motion.div
                  initial={{ scale: 0, opacity: 0, rotate: -15 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 600, 
                    damping: 12,
                    mass: 0.5
                  }}
                  className="absolute -top-1 -right-1"
                >
                  <div className="bg-blue-500 rounded-full p-0.5 shadow-sm border border-white/20">
                    <CheckCircle2 className="w-2 h-2 text-white" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <span className={cn(
            "text-[10px] font-bold uppercase tracking-tighter transition-colors duration-300", 
            totals.water >= goals.water_ml ? "text-blue-500" : (totals.water > 0 ? "text-blue-500/70" : "text-zinc-500")
          )}>{t.water}</span>
        </div>
      </div>
    </div>
  );
};

const DailyProgressSummary = ({ 
  totals, 
  goals, 
  title, 
  projectedTotals, 
  selectedDate, 
  lang, 
  theme, 
  t, 
  illumination, 
  onNutrientClick,
  viewMode,
  streaks,
  consecutiveStreak,
  hasIncreased,
  skin
}: { 
  totals: any, 
  goals: UserGoals, 
  title?: string, 
  projectedTotals?: any, 
  selectedDate: Date, 
  lang: string, 
  theme: 'dark' | 'light', 
  t: any, 
  illumination: boolean, 
  onNutrientClick: (key: any, label: string) => void,
  skin?: Skin,
  viewMode: 'horizontal' | 'radial',
  streaks?: { breakfast: boolean, lunch: boolean, dinner: boolean, snack: boolean, currentMeal: MealType | null, count: number },
  consecutiveStreak?: number,
  hasIncreased?: boolean
}) => {
  const isToday = new Date().toDateString() === selectedDate.toDateString();

  const [activeRing, setActiveRing] = useState<'calories' | 'protein' | 'carbs' | 'fat' | 'fiber' | 'water' | 'caffeine' | 'sugar' | 'sodium'>('calories');

  const [fireParticles, setFireParticles] = useState<Array<{
    id: number;
    x: number;
    y: number;
    tx: number;
    ty: number;
    size: number;
    color: string;
    delay: number;
    duration: number;
    rotateStart: number;
    rotateEnd: number;
    opacity: number;
  }>>([]);

  useEffect(() => {
    if (hasIncreased) {
      const colors = [
        '#ff3b30', // Cyber red
        '#ff9500', // Neon orange
        '#ffcc00', // Bright gold
        '#10B981', // Branded green accent
        '#ff5e36', // Cyber coral
      ];
      const newParticles = Array.from({ length: 24 }).map((_, i) => {
        const angle = (Math.random() * Math.PI) - (Math.PI / 2); // -90 deg to 90 deg (mostly upwards)
        const distance = 50 + Math.random() * 70; // random travel distance
        
        const tx = Math.sin(angle) * distance;
        const ty = -Math.cos(angle) * distance - 15; // always drift upward

        return {
          id: Date.now() + i,
          x: (Math.random() - 0.5) * 12,
          y: (Math.random() - 0.5) * 12,
          tx,
          ty,
          size: 4 + Math.random() * 9,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 0.25,
          duration: 0.7 + Math.random() * 0.7,
          rotateStart: Math.random() * 360,
          rotateEnd: Math.random() * 360 + (Math.random() > 0.5 ? 180 : -180),
          opacity: 0.8 + Math.random() * 0.2
        };
      });
      setFireParticles(newParticles);
    } else {
      const timer = setTimeout(() => {
        setFireParticles([]);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [hasIncreased]);

  const getNutrientStars = (val: number, target: number, type: 'limit' | 'macro' | 'target') => {
    if (val <= 0) return 0;
    if (type === 'limit') {
      // Salt/Sugar: Stay under 125%
      return val <= target * 1.25 ? 1 : 0;
    }
    if (type === 'macro') {
      // Carbs/Fats: Hit target within 125%
      return (val >= target && val <= target * 1.25) ? 1 : 0;
    }
    // Others (calories/targets): 5% grace margin (>= 95% of goal) counts as met
    return val >= target * 0.95 ? 1 : 0;
  };

  const displayTotals = projectedTotals || totals;

  const activeRingInfo = useMemo(() => {
    switch (activeRing) {
      case 'calories':
        return {
          label: t.calories,
          current: Math.round(displayTotals.calories),
          goal: goals.calories,
          unit: t.kcal,
          color: '#10B981',
          icon: <Flame className="w-5 h-5 text-[#10B981]" />
        };
      case 'protein':
        return {
          label: t.protein,
          current: Math.round(displayTotals.protein),
          goal: goals.protein,
          unit: 'g',
          color: '#a855f7',
          icon: <Beef className="w-5 h-5 text-[#a855f7]" />
        };
      case 'carbs':
        return {
          label: t.carbs,
          current: Math.round(displayTotals.carbs),
          goal: goals.carbs,
          unit: 'g',
          color: '#0ea5e9',
          icon: <Wheat className="w-5 h-5 text-[#0ea5e9]" />
        };
      case 'fat':
        return {
          label: t.fat,
          current: Math.round(displayTotals.fat),
          goal: goals.fat,
          unit: 'g',
          color: '#f97316',
          icon: <Droplets className="w-5 h-5 text-[#f97316]" />
        };
      case 'fiber':
        return {
          label: t.fiber || 'Fiber',
          current: Math.round(displayTotals.fiber),
          goal: goals.fiber_g,
          unit: 'g',
          color: '#10B981',
          icon: <Leaf className="w-5 h-5 text-[#10B981]" />
        };
      case 'water':
        return {
          label: t.water,
          current: Math.round(displayTotals.water),
          goal: goals.water_ml,
          unit: t.ml,
          color: '#3b82f6',
          icon: <Waves className="w-5 h-5 text-[#3b82f6]" />
        };
      case 'caffeine':
        return {
          label: t.caffeine || 'Caffeine',
          current: Math.round(displayTotals.caffeine),
          goal: goals.caffeine_mg,
          unit: 'mg',
          color: '#92400e',
          icon: <Coffee className="w-5 h-5 text-[#92400e]" />
        };
      case 'sugar':
        return {
          label: t.sugar,
          current: Math.round(displayTotals.sugar),
          goal: goals.sugar_g,
          unit: 'g',
          color: '#0ea5e9',
          icon: <Candy className="w-5 h-5 text-[#0ea5e9]" />
        };
      case 'sodium':
        return {
          label: t.sodium,
          current: +(displayTotals.sodium / 1000).toFixed(2),
          goal: +(goals.sodium_mg / 1000).toFixed(2),
          unit: 'g',
          color: '#64748b',
          icon: <Zap className="w-5 h-5 text-[#64748b]" />
        };
      default:
        return {
          label: t.calories,
          current: Math.round(displayTotals.calories),
          goal: goals.calories,
          unit: t.kcal,
          color: '#10B981',
          icon: <Flame className="w-5 h-5 text-[#10B981]" />
        };
    }
  }, [activeRing, displayTotals, goals, t]);

  const calsPercent = goals.calories > 0 ? (totals.calories / goals.calories) * 100 : 0;
  const proteinPercent = goals.protein > 0 ? (totals.protein / goals.protein) * 100 : 0;
  const carbsPercent = goals.carbs > 0 ? (totals.carbs / goals.carbs) * 100 : 0;
  const fatPercent = goals.fat > 0 ? (totals.fat / goals.fat) * 100 : 0;
  const fiberPercent = goals.fiber_g > 0 ? (totals.fiber / goals.fiber_g) * 100 : 0;

  const calsProjPercent = projectedTotals && goals.calories > 0 ? (projectedTotals.calories / goals.calories) * 100 : calsPercent;
  const proteinProjPercent = projectedTotals && goals.protein > 0 ? (projectedTotals.protein / goals.protein) * 100 : proteinPercent;
  const carbsProjPercent = projectedTotals && goals.carbs > 0 ? (projectedTotals.carbs / goals.carbs) * 100 : carbsPercent;
  const fatProjPercent = projectedTotals && goals.fat > 0 ? (projectedTotals.fat / goals.fat) * 100 : fatPercent;
  const fiberProjPercent = projectedTotals && goals.fiber_g > 0 ? (projectedTotals.fiber / goals.fiber_g) * 100 : fiberPercent;

  const isDisplayCalsBreached = goals.calories > 0 && displayTotals.calories > goals.calories * 1.25;
  const isDisplayProteinBreached = false;
  const isDisplayCarbsBreached = goals.carbs > 0 && displayTotals.carbs > goals.carbs * 1.25;
  const isDisplayFatBreached = goals.fat > 0 && displayTotals.fat > goals.fat;
  const isDisplayFiberBreached = false;

  const macrosList = useMemo(() => [
    { 
      key: 'calories', 
      label: t.calories, 
      current: totals.calories, 
      projected: projectedTotals ? projectedTotals.calories : undefined,
      goal: goals.calories, 
      percent: calsPercent, 
      projectedPercent: calsProjPercent,
      unit: t.kcal, 
      color: isDisplayCalsBreached ? '#ef4444' : '#10B981', 
      isBreached: isDisplayCalsBreached, 
      bgClass: isDisplayCalsBreached ? 'bg-red-500/10 hover:bg-red-500/25' : 'bg-[#10B981]/10 hover:bg-[#10B981]/25', 
      borderClass: isDisplayCalsBreached ? 'hover:border-red-500/40' : 'hover:border-[#10B981]/40', 
      icon: <Flame className={cn("w-4 h-4", isDisplayCalsBreached ? "text-red-500" : "text-[#10B981]")} /> 
    },
    { 
      key: 'protein', 
      label: skin === 'anime' ? '『ATK』 Protein' : t.protein, 
      current: totals.protein, 
      projected: projectedTotals ? projectedTotals.protein : undefined,
      goal: goals.protein, 
      percent: proteinPercent, 
      projectedPercent: proteinProjPercent,
      unit: 'g', 
      color: '#a855f7', 
      isBreached: false, 
      bgClass: 'bg-purple-500/10 hover:bg-purple-500/25', 
      borderClass: 'hover:border-purple-500/40', 
      icon: <Beef className="w-4 h-4 text-purple-400" /> 
    },
    { 
      key: 'carbs', 
      label: skin === 'anime' ? '『SPD』 Carbs' : t.carbs, 
      current: totals.carbs, 
      projected: projectedTotals ? projectedTotals.carbs : undefined,
      goal: goals.carbs, 
      percent: carbsPercent, 
      projectedPercent: carbsProjPercent,
      unit: 'g', 
      color: isDisplayCarbsBreached ? '#ef4444' : '#0ea5e9', 
      isBreached: isDisplayCarbsBreached, 
      bgClass: isDisplayCarbsBreached ? 'bg-red-500/10 hover:bg-red-500/25' : 'bg-blue-500/10 hover:bg-blue-500/25', 
      borderClass: isDisplayCarbsBreached ? 'hover:border-red-500/40' : 'hover:border-blue-500/40', 
      icon: <Wheat className={cn("w-4 h-4", isDisplayCarbsBreached ? "text-red-500" : "text-blue-400")} /> 
    },
    { 
      key: 'fat', 
      label: skin === 'anime' ? '『DEF』 Fats' : t.fat, 
      current: totals.fat, 
      projected: projectedTotals ? projectedTotals.fat : undefined,
      goal: goals.fat, 
      percent: fatPercent, 
      projectedPercent: fatProjPercent,
      unit: 'g', 
      color: isDisplayFatBreached ? '#ef4444' : '#f97316', 
      isBreached: isDisplayFatBreached, 
      bgClass: isDisplayFatBreached ? 'bg-red-500/10 hover:bg-red-500/25' : 'bg-orange-500/10 hover:bg-orange-500/25', 
      borderClass: isDisplayFatBreached ? 'hover:border-red-500/40' : 'hover:border-orange-500/40', 
      icon: <Droplets className={cn("w-4 h-4", isDisplayFatBreached ? "text-red-500" : "text-orange-400")} /> 
    },
    { 
      key: 'fiber', 
      label: skin === 'anime' ? '『DEX』 Fiber' : (t.fiber || 'Fiber'), 
      current: totals.fiber, 
      projected: projectedTotals ? projectedTotals.fiber : undefined,
      goal: goals.fiber_g, 
      percent: fiberPercent, 
      projectedPercent: fiberProjPercent,
      unit: 'g', 
      color: '#10B981', 
      isBreached: false, 
      bgClass: 'bg-emerald-500/10 hover:bg-emerald-500/25', 
      borderClass: 'hover:border-emerald-500/40', 
      icon: <Leaf className="w-4 h-4 text-emerald-400" /> 
    },
  ], [totals, projectedTotals, goals, t, calsPercent, proteinPercent, carbsPercent, fatPercent, fiberPercent, calsProjPercent, proteinProjPercent, carbsProjPercent, fatProjPercent, fiberProjPercent, isDisplayCalsBreached, isDisplayCarbsBreached, isDisplayFatBreached, skin]);

  const secondaryNutrientsList = useMemo(() => [
    { 
      key: 'water', 
      label: skin === 'anime' ? '『MP』 Hydration' : t.water, 
      current: totals.water, 
      projected: projectedTotals ? projectedTotals.water : undefined,
      goal: goals.water_ml, 
      percent: goals.water_ml > 0 ? (totals.water / goals.water_ml) * 100 : 0, 
      projectedPercent: projectedTotals && goals.water_ml > 0 ? (projectedTotals.water / goals.water_ml) * 100 : (goals.water_ml > 0 ? (totals.water / goals.water_ml) * 100 : 0),
      unit: t.ml, 
      color: '#3b82f6', 
      icon: <Waves className="w-4 h-4 text-blue-400" /> 
    },
    { 
      key: 'caffeine', 
      label: t.caffeine || 'Caffeine', 
      current: totals.caffeine, 
      projected: projectedTotals ? projectedTotals.caffeine : undefined,
      goal: goals.caffeine_mg, 
      percent: goals.caffeine_mg > 0 ? (totals.caffeine / goals.caffeine_mg) * 100 : 0, 
      projectedPercent: projectedTotals && goals.caffeine_mg > 0 ? (projectedTotals.caffeine / goals.caffeine_mg) * 100 : (goals.caffeine_mg > 0 ? (totals.caffeine / goals.caffeine_mg) * 100 : 0),
      unit: 'mg', 
      color: '#92400e', 
      icon: <Coffee className="w-4 h-4 text-amber-600" /> 
    },
    { 
      key: 'sugar', 
      label: t.sugar, 
      current: totals.sugar, 
      projected: projectedTotals ? projectedTotals.sugar : undefined,
      goal: goals.sugar_g, 
      percent: goals.sugar_g > 0 ? (totals.sugar / goals.sugar_g) * 100 : 0, 
      projectedPercent: projectedTotals && goals.sugar_g > 0 ? (projectedTotals.sugar / goals.sugar_g) * 100 : (goals.sugar_g > 0 ? (totals.sugar / goals.sugar_g) * 100 : 0),
      unit: 'g', 
      color: '#0ea5e9', 
      icon: <Candy className="w-4 h-4 text-blue-400" /> 
    },
    { 
      key: 'sodium', 
      label: t.sodium, 
      current: totals.sodium / 1000, 
      projected: projectedTotals ? projectedTotals.sodium / 1000 : undefined,
      goal: goals.sodium_mg / 1000, 
      percent: goals.sodium_mg > 0 ? (totals.sodium / goals.sodium_mg) * 100 : 0, 
      projectedPercent: projectedTotals && goals.sodium_mg > 0 ? (projectedTotals.sodium / goals.sodium_mg) * 100 : (goals.sodium_mg > 0 ? (totals.sodium / goals.sodium_mg) * 100 : 0),
      unit: 'g', 
      color: '#64748b', 
      icon: <Zap className="w-4 h-4 text-slate-400" /> 
    },
  ], [totals, projectedTotals, goals, t]);

  const RadialProgressCard = ({ item, size = 56, strokeWidth = 5 }: { item: any, size?: number, strokeWidth?: number }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (Math.min(item.percent, 100) / 100) * circumference;
    const isSelected = activeRing === item.key;
    
    const hasProjected = item.projected !== undefined && item.projected > item.current;
    const addedVal = hasProjected ? item.projected - item.current : 0;
    const addedPct = hasProjected && item.goal > 0 ? (addedVal / item.goal) * 100 : 0;
    const projectedPercent = hasProjected ? item.projectedPercent : item.percent;

    return (
      <div 
        onClick={() => {
          setActiveRing(item.key as any);
          if (onNutrientClick) onNutrientClick(item.key, item.label);
          sounds.playPop();
          triggerHaptic('SUCCESS');
        }}
        className={cn(
          "p-3 rounded-2xl border transition-all duration-300 cursor-pointer flex items-center gap-3 relative overflow-hidden select-none",
          isSelected 
            ? "bg-[var(--card-bg)] shadow-md scale-[1.02] ring-1 ring-[#10B981]/20" 
            : "bg-zinc-500/5 border-zinc-200/50 dark:border-zinc-800/30 hover:bg-zinc-500/10",
          item.borderClass
        )}
        style={isSelected ? { borderColor: item.color } : {}}
      >
        <div className="relative shrink-0" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="transform -rotate-90">
            <circle cx={size/2} cy={size/2} r={radius} stroke={item.color} strokeWidth={strokeWidth} fill="transparent" className="opacity-15" />
            
            {/* Projected Ring */}
            {hasProjected && (
              <motion.circle
                cx={size/2}
                cy={size/2}
                r={radius}
                stroke={item.color}
                strokeWidth={strokeWidth}
                fill="transparent"
                strokeLinecap="round"
                strokeDasharray={circumference}
                initial={{ strokeDashoffset: circumference }}
                animate={{ strokeDashoffset: circumference - (Math.min(projectedPercent, 100) / 100) * circumference }}
                className="opacity-45 animate-pulse"
                transition={{ duration: 1.2, ease: "easeOut" }}
              />
            )}

            {/* Current Ring */}
            <motion.circle
              cx={size/2}
              cy={size/2}
              r={radius}
              stroke={item.color}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center scale-90">
            {item.icon}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-wider text-zinc-400 dark:text-zinc-500 leading-none">{item.label}</p>
          <div className="flex items-baseline gap-1 mt-1 leading-none">
            <span className="text-sm font-extrabold text-[var(--text)] leading-none">
              {typeof item.current === 'number' ? (item.key === 'sodium' ? Number(item.current.toFixed(2)) : Math.round(item.current)) : item.current}
            </span>
            <span className="text-[10px] font-bold text-zinc-500 leading-none">/ {item.goal}{item.unit}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1 leading-none">
            <p className="text-[10px] font-black leading-none" style={{ color: item.color }}>
              {hasProjected ? `${Math.round(item.percent)}% → ${Math.round(projectedPercent)}%` : `${Math.round(item.percent)}%`}
            </p>
            {hasProjected && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full text-emerald-500 bg-emerald-500/10 shrink-0 leading-none animate-pulse">
                +{item.key === 'sodium' ? Number(addedVal.toFixed(2)) : Math.round(addedVal)}{item.unit} (+{Math.round(addedPct)}%)
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const totalStars = 
    getNutrientStars(displayTotals.calories, goals.calories, 'target') +
    getNutrientStars(displayTotals.water, goals.water_ml, 'target') +
    getNutrientStars(displayTotals.protein, goals.protein, 'target') +
    getNutrientStars(displayTotals.fiber, goals.fiber_g, 'target') +
    getNutrientStars(displayTotals.carbs, goals.carbs, 'macro') +
    getNutrientStars(displayTotals.fat, goals.fat, 'macro') +
    getNutrientStars(displayTotals.sugar, goals.sugar_g, 'limit') +
    getNutrientStars(displayTotals.sodium, goals.sodium_mg, 'limit') +
    getNutrientStars(displayTotals.caffeine, goals.caffeine_mg || 400, 'limit');

  const maxStars = 9;

  const isCalorieGoalMet = goals.calories > 0 && displayTotals.calories >= goals.calories * 0.95;

  const isSixOrSevenStars = totalStars === 6 || totalStars === 7;
  const isFiveToSevenStars = totalStars >= 5 && totalStars <= 7;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (isCalorieGoalMet) {
      const dateStr = selectedDate ? new Date(selectedDate).toDateString() : new Date().toDateString();
      const celebKey = `celebrated_cal_${dateStr}`;
      const hasCelebrated = localStorage.getItem(celebKey) === 'true';

      if (!hasCelebrated) {
        // Play major celebration/success sound
        sounds.playSuccess();
        localStorage.setItem(celebKey, 'true');
        
        // Confetti celebration blast
        const duration = 2.5 * 1000;
        const end = Date.now() + duration;
        const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

        (function frame() {
          // Primary energetic side blasts (default gravity)
          confetti({
            particleCount: 3,
            angle: 60,
            spread: 50,
            origin: { x: 0, y: 0.8 },
            colors: ['#ef4444', '#10b981', '#fbbf24', '#3b82f6', '#dc2626', '#22c55e']
          });
          confetti({
            particleCount: 3,
            angle: 120,
            spread: 50,
            origin: { x: 1, y: 0.8 },
            colors: ['#ef4444', '#10b981', '#fbbf24', '#3b82f6', '#dc2626', '#22c55e']
          });

          // Slower-falling secondary layer centered around the .trophy-icon
          if (Math.random() < 0.25) {
            let trophyOrigin = { x: 0.5, y: 0.3 }; // Fallback to upper-center
            try {
              const trophyElements = document.querySelectorAll('.trophy-icon');
              let visibleTrophy = null;
              for (let i = 0; i < trophyElements.length; i++) {
                const el = trophyElements[i];
                const rect = el.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                  visibleTrophy = el;
                  break;
                }
              }
              if (visibleTrophy) {
                const rect = visibleTrophy.getBoundingClientRect();
                trophyOrigin = {
                  x: (rect.left + rect.width / 2) / window.innerWidth,
                  y: (rect.top + rect.height / 2) / window.innerHeight
                };
              }
            } catch (e) {
              console.warn('Could not determine trophy-icon position:', e);
            }

            // Fire slower-falling particles with low gravity and vibrant multicolor palette
            confetti({
              particleCount: 2,
              angle: randomInRange(60, 120),
              spread: 45,
              startVelocity: randomInRange(10, 18),
              gravity: 0.45, // Much slower falling speed (default is 1)
              scalar: randomInRange(0.7, 1.1),
              drift: randomInRange(-0.5, 0.5),
              origin: trophyOrigin,
              colors: ['#ef4444', '#10b981', '#fbbf24', '#3b82f6', '#f59e0b', '#22c55e']
            });
          }

          if (Date.now() < end) {
            requestAnimationFrame(frame);
          }
        }());
      }
    } else {
      // Reset celebration if they edit or remove a meal to drop below target
      const dateStr = selectedDate ? new Date(selectedDate).toDateString() : new Date().toDateString();
      const celebKey = `celebrated_cal_${dateStr}`;
      if (localStorage.getItem(celebKey) === 'true') {
        localStorage.removeItem(celebKey);
      }
    }
  }, [isCalorieGoalMet, selectedDate]);

  return (
    <div className={cn(
      "bg-[var(--card-bg)] border rounded-3xl p-4 sm:p-6 space-y-4 relative card-shadow transition-all duration-500 daily-grind-container overflow-hidden", 
      isCalorieGoalMet 
        ? "active border-amber-400/40 ring-1 ring-amber-400/20 shadow-[0_0_30px_rgba(251,191,36,0.15)]" 
        : "border-[var(--card-border)]",
      isFiveToSevenStars ? "px-3 py-5 sm:p-6" : "",
      illumination ? "illumination-both" : "",
      hasIncreased ? "streak-increased" : ""
    )}>
      {illumination && (
        <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
          <div className="absolute -left-20 -top-20 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px]" />
        </div>
      )}

      {/* Top Header Row: Flame/Title & Badges on Left, Streak Counter & Checkmark on Right */}
      <div className="flex items-start justify-between gap-3 relative z-10">
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <motion.div 
              animate={{
                scale: hasIncreased ? 1.25 : 1,
                rotate: hasIncreased ? [0, 12, -10, 6, 0] : 0,
              }}
              transition={{ 
                type: "spring",
                stiffness: 300,
                damping: 15,
              }}
              className="bg-amber-500/10 p-3 rounded-2xl relative overflow-visible shrink-0"
            >
              <Flame className={cn("w-7 h-7 sm:w-8 sm:h-8", (consecutiveStreak || 0) > 0 ? "text-amber-500 fill-amber-500 animate-pulse" : "text-zinc-300")} />
              
              {/* Fire Particle Effect */}
              <AnimatePresence>
                {hasIncreased && fireParticles.map((particle) => (
                  <motion.div
                    key={particle.id}
                    initial={{
                      x: particle.x,
                      y: particle.y,
                      scale: 0.2,
                      opacity: particle.opacity,
                      rotate: particle.rotateStart,
                    }}
                    animate={{
                      x: particle.tx,
                      y: particle.ty,
                      scale: [0.5, 1.2, 0],
                      opacity: [particle.opacity, particle.opacity, 0],
                      rotate: particle.rotateEnd,
                    }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: particle.duration,
                      delay: particle.delay,
                      ease: "easeOut",
                    }}
                    className="absolute pointer-events-none rounded-full"
                    style={{
                      width: particle.size,
                      height: particle.size,
                      backgroundColor: particle.color,
                      boxShadow: `0 0 ${particle.size * 1.5}px ${particle.color}, 0 0 ${particle.size * 3}px ${particle.color}`,
                      left: "50%",
                      top: "50%",
                      marginLeft: -particle.size / 2,
                      marginTop: -particle.size / 2,
                    }}
                  />
                ))}
              </AnimatePresence>

              {hasIncreased && (
                <motion.span 
                  initial={{ scale: 0.8, opacity: 1 }}
                  animate={{ scale: 2.2, opacity: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="absolute inset-0 bg-gradient-to-r from-amber-500 to-emerald-500 opacity-30 rounded-2xl pointer-events-none"
                />
              )}
            </motion.div>

            <div>
              <h3 className="text-base sm:text-lg font-bold leading-tight">
                {skin === 'stalker' ? (lang === 'ua' ? 'Днів виживання' : 'Days Survived') : skin === 'anime' ? (lang === 'ua' ? '『КВЕСТ ЛОГ ТА КОМБО』 ★★★' : "『TODAY'S QUEST & COMBO』 ★★★") : (title || t.dailyStreak)}
              </h3>
              <p className="text-zinc-500 text-xs mt-0.5">
                {skin === 'stalker' ? (lang === 'ua' ? 'Онови біологічний маніфест, Одиначе.' : 'Update your biological manifest, Loner.') : skin === 'anime' ? (lang === 'ua' ? 'Час для нового левел-апу, Герою! ⚔️' : 'Level up your power status today, Hero! ⚔️') : t.logMeals}
              </p>
            </div>
          </div>

          {/* Stars & Trophy Badge (when goal met or stars earned) */}
          {(totalStars > 0 || isCalorieGoalMet) && (
            <div className="flex items-center gap-1.5 pt-0.5">
              <motion.div 
                className={cn(
                  "inline-flex gap-1 items-center px-2 py-1 rounded-xl transition-all duration-500 stars-shimmer-container",
                  isCalorieGoalMet 
                    ? "bg-amber-400/15 border border-amber-400/30 shadow-[0_0_15px_rgba(251,191,36,0.25)] active-shimmer" 
                    : "bg-zinc-500/10 border border-[var(--card-border)]"
                )}
                animate={isCalorieGoalMet ? {
                  boxShadow: ["0 0 10px rgba(251,191,36,0.2)", "0 0 20px rgba(251,191,36,0.5)", "0 0 10px rgba(251,191,36,0.2)"]
                } : {}}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                {isCalorieGoalMet && (
                  <motion.span
                    initial={{ scale: 0, rotate: -45 }}
                    animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                    transition={{
                      scale: { repeat: Infinity, duration: 1.8, repeatType: "reverse" },
                      rotate: { repeat: Infinity, duration: 2.2, repeatType: "reverse" },
                      default: { type: "spring", stiffness: 300, damping: 15 }
                    }}
                    className="text-xs sm:text-sm select-none mr-0.5 trophy-icon"
                    title={lang === 'ua' ? "Ціль по калоріях досягнута!" : "Calorie Goal Met!"}
                  >
                    🏆
                  </motion.span>
                )}

                <div className="flex gap-0.5 items-center">
                  {Array.from({ length: totalStars }).map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0, rotate: -180 }}
                      animate={isCalorieGoalMet ? {
                        opacity: 1,
                        scale: [1, 1.25, 1],
                        rotate: [0, 8, -8, 0],
                      } : {
                        opacity: 1,
                        scale: 1,
                        rotate: 0,
                      }}
                      exit={{ opacity: 0, scale: 0 }}
                      transition={isCalorieGoalMet ? {
                        scale: {
                          repeat: Infinity,
                          repeatType: "reverse",
                          duration: 1.2,
                          delay: i * 0.15,
                        },
                        rotate: {
                          repeat: Infinity,
                          repeatType: "reverse",
                          duration: 1.5,
                          delay: i * 0.1,
                        },
                        default: {
                          type: "spring",
                          stiffness: 260,
                          damping: 20,
                          delay: i * 0.05
                        }
                      } : {
                        type: "spring",
                        stiffness: 260,
                        damping: 20,
                        delay: i * 0.05
                      }}
                    >
                      <Star className={cn("w-3.5 h-3.5 fill-amber-400 text-amber-400", isCalorieGoalMet ? "drop-shadow-[0_0_3px_rgba(251,191,36,0.7)] animate-pulse" : "")} />
                    </motion.div>
                  ))}
                </div>

                {totalStars === maxStars && (
                  <motion.span
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-[9px] sm:text-[10px] font-bold text-emerald-500 uppercase tracking-wider ml-1 bg-emerald-500/10 px-1.5 py-0.5 rounded-full"
                  >
                    Perfect Day!
                  </motion.span>
                )}
              </motion.div>
            </div>
          )}
        </div>

        {/* Right Side: Streak Counter */}
        {streaks && (
          <div className="flex flex-col items-end text-right shrink-0 select-none">
            <div className="flex items-baseline justify-end gap-1">
              <motion.span
                animate={{
                  scale: hasIncreased ? [1, 1.5, 0.9, 1.3, 0.95, 1] : 1,
                  y: hasIncreased ? [0, -14, 4, -7, 2, 0] : 0,
                  color: hasIncreased ? ["#f59e0b", "#10B981", "#fbbf24", "#10B981"] : "#f59e0b",
                }}
                transition={{
                  type: "spring",
                  stiffness: 420,
                  damping: 10,
                  mass: 0.5,
                }}
                className="text-2xl sm:text-3xl font-black text-amber-500 inline-block leading-none streak-counter-number"
              >
                {consecutiveStreak || 0}
              </motion.span>
              <span className="text-[#71717b] text-[10px] font-bold uppercase tracking-widest">{t.days || 'DAYS'}</span>
            </div>
            <p className="text-[10px] text-[#71717b] font-bold uppercase tracking-widest mt-0.5">{t.streakSuffix || 'STREAK'}</p>
          </div>
        )}
      </div>

      {/* Targets & Calorie Progress Subheader Row */}
      <div className="flex items-center justify-between gap-3 -mt-[15px] pt-2 border-t border-zinc-200/20 dark:border-zinc-800/30 relative z-10">
        <p className="text-zinc-500 text-xs font-medium truncate flex-1 min-w-0">{t.progressTargets}</p>
        <div 
          className="flex flex-col items-end cursor-pointer group shrink-0 select-none" 
          onClick={() => onNutrientClick('calories', t.calories)}
        >
          <div className="flex items-center gap-1.5">
            {isCalorieGoalMet && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 15 }}
                className="bg-emerald-500/15 border border-emerald-500/30 p-1 rounded-full text-emerald-500 flex items-center justify-center shadow-xs"
                title={lang === 'ua' ? "Ціль по калоріях досягнута!" : "Goal reached!"}
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </motion.div>
            )}
            <p className="text-2xl sm:text-3xl font-black text-emerald-500 leading-none group-hover:scale-105 transition-transform">
              {Math.round(displayTotals.calories)}
            </p>
          </div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#71717b] whitespace-nowrap mt-1">
            {t.kcal} / {goals.calories}
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {viewMode === 'horizontal' ? (
          <motion.div
            key="horizontal"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            <ProgressBar 
              label={t.calories} 
              current={Math.round(totals.calories)} 
              projected={projectedTotals ? Math.round(projectedTotals.calories) : undefined}
              goal={goals.calories} 
              gradientClass="from-emerald-600 to-emerald-400"
              unit={t.kcal}
              onClick={() => onNutrientClick('calories', t.calories)}
              lang={lang}
            />
            <ProgressBar 
              label={t.water} 
              current={Math.round(totals.water)} 
              projected={projectedTotals ? Math.round(projectedTotals.water) : undefined}
              goal={goals.water_ml} 
              gradientClass="from-blue-600 to-blue-400" 
              unit={t.ml}
              onClick={() => onNutrientClick('water', t.water)}
              lang={lang}
            />
            <ProgressBar 
              label={t.caffeine || 'Caffeine'} 
              current={totals.caffeine} 
              projected={projectedTotals ? projectedTotals.caffeine : undefined}
              goal={goals.caffeine_mg} 
              gradientClass={(totals.caffeine / goals.caffeine_mg) > 1.2 ? "from-red-600 to-red-400" : (totals.caffeine / goals.caffeine_mg) > 1 ? "from-red-500 to-red-400" : (totals.caffeine / goals.caffeine_mg) > 0.8 ? "from-amber-600 to-amber-400" : "from-amber-800 to-amber-600"}
              unit="mg"
              isSaltSugar
              onClick={() => onNutrientClick('caffeine_mg', t.caffeine || 'Caffeine')}
              lang={lang}
            />
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4 sm:space-y-6">
                <ProgressBar 
                  label={t.protein} 
                  current={totals.protein}
                  projected={projectedTotals?.protein}
                  goal={goals.protein} 
                  gradientClass="from-purple-600 to-purple-400" 
                  isNutrient
                  noSuccessColor
                  onClick={() => onNutrientClick('protein_g', t.protein)}
                  lang={lang}
                />
                <ProgressBar 
                  label={t.carbs} 
                  current={totals.carbs} 
                  projected={projectedTotals?.carbs}
                  goal={goals.carbs} 
                  gradientClass="from-teal-600 to-teal-400" 
                  isNutrient
                  isCarbs
                  onClick={() => onNutrientClick('carbs_g', t.carbs)}
                  lang={lang}
                />
                <ProgressBar 
                  label={t.fat} 
                  current={totals.fat} 
                  projected={projectedTotals?.fat}
                  goal={goals.fat} 
                  gradientClass="from-orange-600 to-orange-400" 
                  isNutrient
                  isFat
                  onClick={() => onNutrientClick('fat_g', t.fat)}
                  lang={lang}
                />
              </div>
              <div className="space-y-4 sm:space-y-6">
                <ProgressBar 
                  label={t.fiber || 'Fiber'} 
                  current={totals.fiber} 
                  projected={projectedTotals?.fiber}
                  goal={goals.fiber_g} 
                  gradientClass="from-emerald-600 to-emerald-400" 
                  unit="g"
                  isNutrient
                  onClick={() => onNutrientClick('fiber_g', t.fiber || 'Fiber')}
                  lang={lang}
                />
                <ProgressBar 
                  label={t.sugar} 
                  current={totals.sugar} 
                  projected={projectedTotals?.sugar}
                  goal={goals.sugar_g} 
                  gradientClass="from-zinc-200 to-zinc-100" 
                  unit="g"
                  isSaltSugar
                  onClick={() => onNutrientClick('sugar_g', t.sugar)}
                  lang={lang}
                />
                <ProgressBar 
                  label={t.sodium} 
                  current={totals.sodium / 1000} 
                  projected={projectedTotals ? projectedTotals.sodium / 1000 : undefined}
                  goal={goals.sodium_mg / 1000} 
                  gradientClass="from-zinc-400 to-zinc-300" 
                  unit="g"
                  isSaltSugar
                  onClick={() => onNutrientClick('sodium_mg', totals.sodium)}
                  lang={lang}
                />
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="radial"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {/* DYNAMIC RADIAL PROGRESS RINGS GRAPHICS */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              {/* Left Side: Interactive Concentric Rings SVG */}
              <div className="md:col-span-5 flex flex-col items-center justify-center relative py-2">
                <motion.div 
                  id="ring-progress"
                  className="relative w-56 h-56 sm:w-64 sm:h-64 select-none flex items-center justify-center mx-auto"
                  animate={(isDisplayCalsBreached || isDisplayProteinBreached || isDisplayCarbsBreached || isDisplayFatBreached) ? {
                    x: [0, -0.5, 0.5, -0.5, 0.5, 0],
                    scale: [1, 1.02, 0.98, 1.02, 1]
                  } : {}}
                  transition={(isDisplayCalsBreached || isDisplayProteinBreached || isDisplayCarbsBreached || isDisplayFatBreached) ? {
                    x: { repeat: Infinity, duration: 0.5, ease: "linear" },
                    scale: { repeat: Infinity, duration: 2, ease: "easeInOut" }
                  } : {}}
                >
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 240 240">
                    {/* Ring 1: Calories */}
                    <circle cx="120" cy="120" r="108" stroke={isDisplayCalsBreached ? "#ef4444" : "#10B981"} strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                      cx="120"
                      cy="120"
                      r="108"
                      stroke={isDisplayCalsBreached ? "#ef4444" : "#10B981"}
                      strokeWidth="7"
                      fill="transparent"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 108}
                      initial={{ strokeDashoffset: 2 * Math.PI * 108 }}
                      animate={{ 
                        strokeDashoffset: 2 * Math.PI * 108 - (Math.min(calsPercent, 100) / 100) * (2 * Math.PI * 108),
                        strokeWidth: isDisplayCalsBreached ? [7, 8.5, 7] : (activeRing === 'calories' ? 8.5 : 7)
                      }}
                      transition={{ 
                        strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                        strokeWidth: isDisplayCalsBreached ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : { duration: 0.2 }
                      }}
                      className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'calories' && "stroke-[8.5px]")}
                      onClick={() => { setActiveRing('calories'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />

                    {/* Ring 2: Protein */}
                    <circle cx="120" cy="120" r="94" stroke={isDisplayProteinBreached ? "#ef4444" : "#a855f7"} strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                      cx="120"
                      cy="120"
                      r="94"
                      stroke={isDisplayProteinBreached ? "#ef4444" : "#a855f7"}
                      strokeWidth="7"
                      fill="transparent"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 94}
                      initial={{ strokeDashoffset: 2 * Math.PI * 94 }}
                      animate={{ 
                        strokeDashoffset: 2 * Math.PI * 94 - (Math.min(proteinPercent, 100) / 100) * (2 * Math.PI * 94),
                        strokeWidth: isDisplayProteinBreached ? [7, 8.5, 7] : (activeRing === 'protein' ? 8.5 : 7)
                      }}
                      transition={{ 
                        strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                        strokeWidth: isDisplayProteinBreached ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : { duration: 0.2 }
                      }}
                      className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'protein' && "stroke-[8.5px]")}
                      onClick={() => { setActiveRing('protein'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />

                    {/* Ring 3: Carbs */}
                    <circle cx="120" cy="120" r="80" stroke={isDisplayCarbsBreached ? "#ef4444" : "#0ea5e9"} strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                      cx="120"
                      cy="120"
                      r="80"
                      stroke={isDisplayCarbsBreached ? "#ef4444" : "#0ea5e9"}
                      strokeWidth="7"
                      fill="transparent"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 80}
                      initial={{ strokeDashoffset: 2 * Math.PI * 80 }}
                      animate={{ 
                        strokeDashoffset: 2 * Math.PI * 80 - (Math.min(carbsPercent, 100) / 100) * (2 * Math.PI * 80),
                        strokeWidth: isDisplayCarbsBreached ? [7, 8.5, 7] : (activeRing === 'carbs' ? 8.5 : 7)
                      }}
                      transition={{ 
                        strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                        strokeWidth: isDisplayCarbsBreached ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : { duration: 0.2 }
                      }}
                      className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'carbs' && "stroke-[8.5px]")}
                      onClick={() => { setActiveRing('carbs'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />

                    {/* Ring 4: Fat */}
                    <circle cx="120" cy="120" r="66" stroke={isDisplayFatBreached ? "#ef4444" : "#f97316"} strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                      cx="120"
                      cy="120"
                      r="66"
                      stroke={isDisplayFatBreached ? "#ef4444" : "#f97316"}
                      strokeWidth="7"
                      fill="transparent"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 66}
                      initial={{ strokeDashoffset: 2 * Math.PI * 66 }}
                      animate={{ 
                        strokeDashoffset: 2 * Math.PI * 66 - (Math.min(fatPercent, 100) / 100) * (2 * Math.PI * 66),
                        strokeWidth: isDisplayFatBreached ? [7, 8.5, 7] : (activeRing === 'fat' ? 8.5 : 7)
                      }}
                      transition={{ 
                        strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                        strokeWidth: isDisplayFatBreached ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : { duration: 0.2 }
                      }}
                      className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'fat' && "stroke-[8.5px]")}
                      onClick={() => { setActiveRing('fat'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />

                    {/* Ring 5: Fiber */}
                    <circle cx="120" cy="120" r="52" stroke="#10B981" strokeWidth="7" fill="transparent" className="opacity-10 dark:opacity-15" />
                    <motion.circle
                      cx="120"
                      cy="120"
                      r="52"
                      stroke="#10B981"
                      strokeWidth="7"
                      fill="transparent"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 52}
                      initial={{ strokeDashoffset: 2 * Math.PI * 52 }}
                      animate={{ 
                        strokeDashoffset: 2 * Math.PI * 52 - (Math.min(fiberPercent, 100) / 100) * (2 * Math.PI * 52),
                        strokeWidth: activeRing === 'fiber' ? 8.5 : 7
                      }}
                      transition={{ 
                        strokeDashoffset: { duration: 0.45, ease: [0.25, 1, 0.5, 1] },
                        strokeWidth: { duration: 0.2 }
                      }}
                      className={cn("cursor-pointer transition-all duration-200 hover:stroke-[8.5px]", activeRing === 'fiber' && "stroke-[8.5px]")}
                      onClick={() => { setActiveRing('fiber'); sounds.playPop(); triggerHaptic('SUCCESS'); }}
                    />
                  </svg>

                  {/* Inner dynamic content overlay (unrotated text) */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center pointer-events-none">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeRing}
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col items-center justify-center"
                      >
                        <div className="p-1 rounded-full bg-zinc-500/10 mb-0.5 flex items-center justify-center">
                          {activeRingInfo.icon}
                        </div>
                        <div className="flex items-baseline justify-center gap-0.5 leading-none mt-0.5">
                          <span className="text-xl sm:text-2xl font-black text-[var(--text)] tracking-tight">
                            {activeRingInfo.current}
                          </span>
                          <span className="text-[10px] sm:text-xs font-bold text-zinc-500">
                            {activeRingInfo.unit}
                          </span>
                        </div>
                        <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mt-1 leading-tight">
                          {activeRingInfo.label}
                        </span>
                        <span className="text-[10px] sm:text-[11px] font-extrabold mt-0.5 leading-none" style={{ color: activeRingInfo.color }}>
                          {Math.round((activeRingInfo.current / (activeRingInfo.goal || 1)) * 100)}%
                        </span>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </motion.div>
              </div>

              {/* Right Side: Interactive Micro Macro Dashboard */}
              <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {macrosList.map((item, idx) => {
                  const isSelected = activeRing === item.key;
                  const itemCirc = 2 * Math.PI * 14;
                  return (
                    <div
                      key={item.key}
                      onClick={() => {
                        setActiveRing(item.key as any);
                        sounds.playPop();
                        triggerHaptic('SUCCESS');
                      }}
                      onMouseEnter={() => setActiveRing(item.key as any)}
                      className={cn(
                        "p-3 rounded-2xl border transition-all duration-300 cursor-pointer flex items-center gap-2.5 relative overflow-hidden select-none",
                        isSelected 
                          ? "bg-[var(--card-bg)] shadow-md scale-[1.02] ring-1 ring-[#10B981]/20" 
                          : "bg-zinc-500/5 border-zinc-200/50 dark:border-zinc-800/30 hover:bg-zinc-500/10",
                        item.borderClass,
                        idx === 4 && "col-span-2 sm:col-span-1"
                      )}
                      style={isSelected ? { borderColor: item.color } : {}}
                    >
                      {skin === 'anime' ? (
                        <div className="flex-1 w-full flex flex-col justify-center">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5">
                              <span className="scale-75">{item.icon}</span>
                              <p className="text-[10px] font-black uppercase tracking-wider text-[#a855f7] leading-none drop-shadow-[0_0_2px_rgba(168,85,247,0.5)]">{item.label}</p>
                            </div>
                            <div className="flex items-baseline gap-1 leading-none">
                              <span className="text-sm font-extrabold text-[#ec4899] leading-none drop-shadow-[0_0_2px_rgba(236,72,153,0.5)]">{Math.round(item.current)}</span>
                              <span className="text-[10px] font-bold text-zinc-500 leading-none">/ {item.goal}{item.unit}</span>
                            </div>
                          </div>
                          <div className="h-2.5 w-full bg-[#0A0B10] border border-[#a855f7]/30 rounded-sm overflow-hidden relative shadow-[inset_0_0_4px_rgba(0,0,0,1)]">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.min(item.percent, 100)}%` }}
                              transition={{ duration: 1, ease: "easeOut" }}
                              className="h-full bg-gradient-to-r from-[#a855f7] to-[#ec4899] shadow-[0_0_10px_rgba(236,72,153,0.8)] relative"
                            >
                              <div className="absolute inset-0 bg-white/20 animate-pulse mix-blend-overlay" />
                            </motion.div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Mini circular progress indicator inside card */}
                          <div className="relative w-9 h-9 shrink-0 flex items-center justify-center">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                              <circle cx="18" cy="18" r="14" stroke={item.color} strokeWidth="3" fill="transparent" className="opacity-15" />
                              <motion.circle
                                cx="18"
                                cy="18"
                                r="14"
                                stroke={item.color}
                                strokeWidth="3"
                                fill="transparent"
                                strokeLinecap="round"
                                strokeDasharray={itemCirc}
                                initial={{ strokeDashoffset: itemCirc }}
                                animate={{ strokeDashoffset: itemCirc - (Math.min(item.percent, 100) / 100) * itemCirc }}
                                transition={{ duration: 1, ease: "easeOut" }}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              {item.icon}
                            </div>
                          </div>
  
                          {/* Nutrient texts */}
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-black uppercase tracking-wider text-zinc-400 dark:text-zinc-500 leading-none">{item.label}</p>
                            <div className="flex items-baseline gap-1 mt-1 leading-none">
                              <span className="text-sm font-extrabold text-[var(--text)] leading-none">{Math.round(item.current)}</span>
                              <span className="text-[10px] font-bold text-zinc-500 leading-none">/ {item.goal}{item.unit}</span>
                            </div>
                            <p className="text-[10px] font-black mt-1 leading-none" style={{ color: item.color }}>{Math.round(item.percent)}%</p>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-zinc-200/30 dark:border-zinc-800/30 my-2" />

            {/* Other parameters (Water, Caffeine, Sugar, Salt) as separate radial rings */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {secondaryNutrientsList.map((item) => (
                <RadialProgressCard key={item.key} item={item} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {streaks && (
        <div className="hidden sm:flex justify-between px-1 relative z-10 pt-4 border-t border-[var(--card-border)] mt-4">
          <div className="flex flex-col items-center gap-1">
            <div className="relative">
              <div className={cn(
                "w-2 h-2 rounded-full transition-all duration-300", 
                streaks.breakfast ? "bg-emerald-500" : "bg-[var(--input-bg)] border border-[var(--card-border)]",
                streaks.currentMeal === 'breakfast' && !streaks.breakfast && "ring-2 ring-emerald-500 ring-offset-2 animate-pulse"
              )} />
              <AnimatePresence>
                {streaks.breakfast && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0, rotate: -15 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 600, damping: 12, mass: 0.5 }}
                    className="absolute -top-1 -right-1"
                  >
                    <div className="bg-emerald-500 rounded-full p-0.5 shadow-sm border border-white/20">
                      <CheckCircle2 className="w-2 h-2 text-white" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <span className={cn(
              "text-[10px] font-bold uppercase tracking-tighter transition-colors duration-300", 
              streaks.breakfast ? "text-emerald-500" : (streaks.currentMeal === 'breakfast' ? "text-emerald-500/70" : "text-zinc-500")
            )}>{t.breakfast}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="relative">
              <div className={cn(
                "w-2 h-2 rounded-full transition-all duration-300", 
                streaks.lunch ? "bg-emerald-500" : "bg-[var(--input-bg)] border border-[var(--card-border)]",
                streaks.currentMeal === 'lunch' && !streaks.lunch && "ring-2 ring-emerald-500 ring-offset-2 animate-pulse"
              )} />
              <AnimatePresence>
                {streaks.lunch && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0, rotate: -15 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 600, damping: 12, mass: 0.5 }}
                    className="absolute -top-1 -right-1"
                  >
                    <div className="bg-emerald-500 rounded-full p-0.5 shadow-sm border border-white/20">
                      <CheckCircle2 className="w-2 h-2 text-white" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <span className={cn(
              "text-[10px] font-bold uppercase tracking-tighter transition-colors duration-300", 
              streaks.lunch ? "text-emerald-500" : (streaks.currentMeal === 'lunch' ? "text-emerald-500/70" : "text-zinc-500")
            )}>{t.lunch}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="relative">
              <div className={cn(
                "w-2 h-2 rounded-full transition-all duration-300", 
                streaks.dinner ? "bg-emerald-500" : "bg-[var(--input-bg)] border border-[var(--card-border)]",
                streaks.currentMeal === 'dinner' && !streaks.dinner && "ring-2 ring-emerald-500 ring-offset-2 animate-pulse"
              )} />
              <AnimatePresence>
                {streaks.dinner && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0, rotate: -15 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 600, damping: 12, mass: 0.5 }}
                    className="absolute -top-1 -right-1"
                  >
                    <div className="bg-emerald-500 rounded-full p-0.5 shadow-sm border border-white/20">
                      <CheckCircle2 className="w-2 h-2 text-white" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <span className={cn(
              "text-[10px] font-bold uppercase tracking-tighter transition-colors duration-300", 
              streaks.dinner ? "text-emerald-500" : (streaks.currentMeal === 'dinner' ? "text-emerald-500/70" : "text-zinc-500")
            )}>{t.dinner}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="relative">
              <div className={cn(
                "w-2 h-2 rounded-full transition-all duration-300", 
                streaks.snack ? "bg-emerald-500" : "bg-[var(--input-bg)] border border-[var(--card-border)]",
                streaks.currentMeal === 'snack' && !streaks.snack && "ring-2 ring-emerald-500 ring-offset-2 animate-pulse"
              )} />
              <AnimatePresence>
                {streaks.snack && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0, rotate: -15 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 600, damping: 12, mass: 0.5 }}
                    className="absolute -top-1 -right-1"
                  >
                    <div className="bg-emerald-500 rounded-full p-0.5 shadow-sm border border-white/20">
                      <CheckCircle2 className="w-2 h-2 text-white" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <span className={cn(
              "text-[10px] font-bold uppercase tracking-tighter transition-colors duration-300", 
              streaks.snack ? "text-emerald-500" : (streaks.currentMeal === 'snack' ? "text-emerald-500/70" : "text-zinc-500")
            )}>{t.snack}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const ProgressOverviewChart = ({ 
  totals, 
  goals, 
  t, 
  theme 
}: { 
  totals: any, 
  goals: UserGoals, 
  t: any, 
  theme: 'dark' | 'light' 
}) => {
  const data = [
    { name: t.calories, current: totals.calories, goal: goals.calories, unit: t.kcal },
    { name: t.protein, current: totals.protein, goal: goals.protein, unit: 'g' },
    { name: t.carbs, current: totals.carbs, goal: goals.carbs, unit: 'g' },
    { name: t.fat, current: totals.fat, goal: goals.fat, unit: 'g' },
    { name: t.sugar, current: totals.sugar, goal: goals.sugar_g, unit: 'g' },
    { name: t.fiber || 'Fiber', current: totals.fiber, goal: goals.fiber_g, unit: 'g' },
    { name: t.sodium, current: totals.sodium, goal: goals.sodium_mg, unit: 'mg' },
  ];

  const chartData = data.map(item => ({
    name: item.name,
    progress: Math.round((item.current / item.goal) * 100),
    currentValue: Math.round(item.current),
    goalValue: item.goal,
    unit: item.unit
  }));

  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[32px] p-8 card-shadow space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h3 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{t.progressOverview}</h3>
          <p className="text-sm text-zinc-500 font-medium uppercase tracking-wider">{t.trackingGoals}</p>
        </div>
        <div className="bg-blue-500/10 p-3 rounded-2xl">
          <TrendingUp className="w-6 h-6 text-blue-500" />
        </div>
      </div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="h-[300px] w-full"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#e4e4e7'} />
            <XAxis 
              dataKey="name" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: theme === 'dark' ? '#a1a1aa' : '#71717a', fontSize: 10, fontWeight: 700 }}
            />
            <YAxis 
              hide 
            />
            <Tooltip 
              cursor={{ fill: theme === 'dark' ? '#18181b' : '#f4f4f5' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] p-4 rounded-2xl card-shadow">
                      <p className="text-sm font-black text-zinc-900 dark:text-zinc-100 uppercase tracking-widest mb-2">{data.name}</p>
                      <div className="space-y-1">
                        <p className="text-2xl font-black text-emerald-500">{data.progress}%</p>
                        <p className="text-xs text-zinc-500 font-bold">
                          {data.currentValue}{data.unit} / {data.goalValue}{data.unit}
                        </p>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar 
              dataKey="progress" 
              radius={[8, 8, 8, 8]}
              isAnimationActive={true}
              animationDuration={1500}
              animationEasing="ease-out"
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={
                    index === 0 ? '#10b981' : // calories
                    index === 1 ? '#a855f7' : // protein (purple)
                    index === 2 ? '#f59e0b' : // carbs
                    index === 3 ? '#f43f5e' : // fat
                    index === 4 ? '#ffffff' : // sugar (white)
                    '#e2e8f0' // sodium (silver white)
                  } 
                  stroke={index === 4 ? '#e4e4e7' : 'none'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </motion.div>
    </div>
  );
};

export default function App() {
  // ==========================================
  // 1. TOP-LEVEL STATE & REF HOOKS (HOISTED)
  // ==========================================
  const [autoThemeEnabled, setAutoThemeEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('auto_theme_enabled');
    return saved === 'true';
  });
  const autoThemeEnabledRef = useRef(autoThemeEnabled);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as 'dark' | 'light') || 'dark';
  });
  const themeRef = useRef<'dark' | 'light'>(
    (typeof window !== 'undefined' && localStorage.getItem('theme') === 'light') ? 'light' : 'dark'
  );
  const [vibe, setVibe] = useState<Vibe>(() => {
    const saved = localStorage.getItem('app_vibe');
    if (saved === 'bro') return 'casual';
    return (saved as Vibe) || 'casual';
  });
  const [declineStyle, setDeclineStyle] = useState<DeclineStyle>(() => {
    const saved = localStorage.getItem('decline_style');
    return (saved as DeclineStyle) || 'regular';
  });
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>(() => {
    const saved = localStorage.getItem('time_format');
    return (saved as '12h' | '24h') || '12h';
  });
  const [dateFormat, setDateFormat] = useState<'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'D MMMM YYYY'>(() => {
    const saved = localStorage.getItem('date_format');
    return (saved as 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'D MMMM YYYY') || 'DD/MM/YYYY';
  });
  const [safeChoiceProfile, setSafeChoiceProfile] = useState<SafeChoiceProfile>(() => {
    const saved = localStorage.getItem('safe_choice_profile');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        // ignore
      }
    }
    return { active: true, medicalAllergies: [], lifestylePreferences: [] };
  });
  const [onboardingLanguage, setOnboardingLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('app_lang');
    if (saved === 'en') return 'en-US';
    return (saved as Language) || 'en-US';
  });
  const [onboardingTheme, setOnboardingTheme] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as 'dark' | 'light') || 'dark';
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeEmail, setUpgradeEmail] = useState('');
  const [upgradePassword, setUpgradePassword] = useState('');
  const [showUpgradePassword, setShowUpgradePassword] = useState(false);
  const [isEDecoderOpen, setIsEDecoderOpen] = useState(false);
  const [eDecoderInitialQuery, setEDecoderInitialQuery] = useState('');
  const [streakIncreased, setStreakIncreased] = useState(false);
  const [showLiquidSplash, setShowLiquidSplash] = useState(false);
  const [splashBeverageType, setSplashBeverageType] = useState<'water' | 'coffee' | 'tea'>('water');
  const [wrapUpRefreshTick, setWrapUpRefreshTick] = useState(0);
  const [isEveningWrapUpTestEnabled, setIsEveningWrapUpTestEnabled] = useState(false);
  const [isDismissedWrapUp, setIsDismissedWrapUp] = useState<boolean>(() => {
    return getEveningWrapUpStatus(false).isDismissed;
  });
  const prevStreakRef = useRef(0);
  const hasInitialStreakCapturedRef = useRef(false);
  const hasRunLaunchSyncRef = useRef(false);
  const isSyncingRef = useRef(false);
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userDocEmail, setUserDocEmail] = useState<string | null>(() => localStorage.getItem('user_email'));
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);
  const [isLinkingAuth, setIsLinkingAuth] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [isStorageBlocked, setIsStorageBlocked] = useState(false);
  const [isMinSplashDone, setIsMinSplashDone] = useState(false);
  const [loginError, setLoginError] = useState<any>(null);
  const [isQuotaExceededState, setIsQuotaExceededState] = useState(isQuotaExceeded);
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [offlineQueueLength, setOfflineQueueLength] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(() => {
    const saved = localStorage.getItem('last_cloud_sync_time');
    return saved ? new Date(saved) : null;
  });
  const [showSyncIndicator, setShowSyncIndicator] = useState<boolean>(false);
  const [selectedMealType, setSelectedMealType] = useState<MealType>(() => {
    const now = new Date();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const timeInMinutes = hour * 60 + minute;

    // Breakfast: 5:00 AM - 10:30 AM (300 - 630 mins)
    if (timeInMinutes >= 300 && timeInMinutes < 630) return 'breakfast';
    
    // Lunch: 12:00 PM - 3:30 PM (720 - 930 mins)
    if (timeInMinutes >= 720 && timeInMinutes < 930) return 'lunch';
    
    // Dinner: 6:30 PM - 10:30 PM (1110 - 1350 mins)
    if (timeInMinutes >= 1110 && timeInMinutes < 1350) return 'dinner';
    
    // Everything else is a snack
    return 'snack';
  });
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [onboardingName, setOnboardingName] = useState<string>(() => {
    const saved = localStorage.getItem('userName');
    return (saved && saved !== 'null') ? saved : '';
  });
  const [onboardingVibe, setOnboardingVibe] = useState<'standard' | 'casual'>(() => {
    return (localStorage.getItem('app_vibe') as 'standard' | 'casual') || 'casual';
  });
  const [onboardingGender, setOnboardingGender] = useState<'male' | 'female'>(() => {
    return (localStorage.getItem('onboardingGender') as 'male' | 'female') || 'male';
  });
  const [onboardingAge, setOnboardingAge] = useState<string>(() => {
    return localStorage.getItem('onboardingAge') || '25';
  });
  const [onboardingWeight, setOnboardingWeight] = useState<string>(() => {
    return localStorage.getItem('onboardingWeight') || '70';
  });
  const [onboardingHeight, setOnboardingHeight] = useState<string>(() => {
    return localStorage.getItem('onboardingHeight') || '170';
  });
  const [onboardingActivity, setOnboardingActivity] = useState<number>(() => {
    const val = localStorage.getItem('onboardingActivity');
    return val ? parseFloat(val) : 1.2;
  });
  const [onboardingGoal, setOnboardingGoal] = useState<'lose' | 'maintain' | 'gain'>(() => {
    return (localStorage.getItem('onboardingGoal') as 'lose' | 'maintain' | 'gain') || 'maintain';
  });
  const [weightToLose, setWeightToLose] = useState<string>(() => {
    return localStorage.getItem('weightToLose') || '5';
  });
  const [onboardingProtein, setOnboardingProtein] = useState<string>('');
  const [onboardingCarbs, setOnboardingCarbs] = useState<string>('');
  const [onboardingFat, setOnboardingFat] = useState<string>('');
  const [onboardingWater, setOnboardingWater] = useState<string>('');
  const [onboardingSugar, setOnboardingSugar] = useState<string>('');
  const [onboardingSodium, setOnboardingSodium] = useState<string>('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [justFinishedOnboarding, setJustFinishedOnboarding] = useState(false);
  const [isTargetsCustomized, setIsTargetsCustomized] = useState(false);
  const [isMealTimeFocusOpen, setIsMealTimeFocusOpen] = useState(false);
  const [fabPosition, setFabPosition] = useState<{ x: number, y: number }>(() => {
    try {
      const saved = localStorage.getItem('fab_position');
      return saved ? JSON.parse(saved) : { x: 0, y: 0 };
    } catch (e) {
      return { x: 0, y: 0 };
    }
  });
  const [view, setView] = useState<View>(() => {
    if (typeof localStorage !== 'undefined') {
      if (localStorage.getItem('explicit_logout') === 'true') {
        return 'login';
      }
    }
    return 'home';
  });
  const [isManualLoggedClicked, setIsManualLoggedClicked] = useState(false);
  const [isDetailsLoggedClicked, setIsDetailsLoggedClicked] = useState(false);
  const [chartRepresentation, setChartRepresentation] = useState<'bar' | 'line'>(() => {
    return (localStorage.getItem('progress_chart_representation') as 'bar' | 'line') || 'bar';
  });
  const [achievementSubTab, setAchievementSubTab] = useState<'stars' | 'streak' | 'trend'>('stars');
  const [calorieSubTab, setCalorieSubTab] = useState<'calories' | 'distribution' | 'windows'>('calories');
  const [macroSubTab, setMacroSubTab] = useState<'summary' | 'trend'>('summary');
  const [weightSubTab, setWeightSubTab] = useState<'trend' | 'history'>('trend');
  const [showWaterModal, setShowWaterModal] = useState(false);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [biomarkers, setBiomarkers] = useState<BiomarkerLog[]>(() => {
    try {
      const saved = localStorage.getItem('biomarker_logs');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return DEFAULT_BIOMARKER_LOGS;
  });
  const [navigationHistory, setNavigationHistory] = useState<View[]>([]);
  const [isBackNavigating, setIsBackNavigating] = useState(false);
  const lastViewRef = useRef<View>('home');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshCount, setRefreshCount] = useState(() => Math.floor(Math.random() * 100));
  const [pullDistance, setPullDistance] = useState(0);
  const [favorites, setFavorites] = useState<HistoryEntry[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBugModalOpen, setIsBugModalOpen] = useState(false);
  const [bugDescription, setBugDescription] = useState('');
  const [bugScreenshot, setBugScreenshot] = useState<string | null>(null);
  const [isVerifyingBugImage, setIsVerifyingBugImage] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState<HistoryEntry | null>(null);
  const [selectedNutrient, setSelectedNutrient] = useState<{ key: keyof TotalNutrition | 'calories' | 'water', label: string } | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [skeletonVariation, setSkeletonVariation] = useState(0);
  const [isAnalyzingText, setIsAnalyzingText] = useState(false);
  const [chatThinkingStep, setChatThinkingStep] = useState(0);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [showThreadList, setShowThreadList] = useState(false);
  const [isScrollingChat, setIsScrollingChat] = useState<boolean>(false);
  const chatScrollTimeoutRef = useRef<any>(null);
  const [showMatchOverlay, setShowMatchOverlay] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [showThemesSubmenu, setShowThemesSubmenu] = useState(false);
  const [chatBackground, setChatBackground] = useState<'default' | 'simple-grid' | 'dots' | 'grid' | 'citrus' | 'leaves' | 'waves'>(() => {
    return (localStorage.getItem('chat_background') as any) || 'default';
  });
  const [chatTextureColor, setChatTextureColor] = useState<string>(() => {
    return localStorage.getItem('chat_texture_color') || '#10b981';
  });
  const [colorCycleEnabled, setColorCycleEnabled] = useState<boolean>(() => {
    return localStorage.getItem('color_cycle_enabled') === 'true';
  });
  const chatEndRef = useRef<HTMLDivElement>(null);
  const prevChatMessagesLengthRef = useRef<number>(0);
  const chatFileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const chatMenuRef = useRef<HTMLDivElement>(null);
  const [analysisTimer, setAnalysisTimer] = useState(0);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [result, setResult] = useState<MealAnalysis | null>(null);
  const [safeChoiceDetailModalData, setSafeChoiceDetailModalData] = useState<MealAnalysis | null>(null);
  const [originalResult, setOriginalResult] = useState<MealAnalysis | null>(null);
  const [loadingPortionsIdx, setLoadingPortionsIdx] = useState<number | null>(null);
  const [parsingPortionIdx, setParsingPortionIdx] = useState<number | null>(null);
  const [customPortionText, setCustomPortionText] = useState<{ [foodIdx: number]: string }>({});
  const [parseExplanation, setParseExplanation] = useState<{ [foodIdx: number]: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [isLogged, setIsLogged] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>(() => {
    try {
      const saved = localDb.getItemSync('meal_history');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.map(item => ({
            ...item,
            timestamp: ensureTimestampMillis(item.timestamp)
          }));
        }
      }
      return [];
    } catch { return []; }
  });
  const [lastHistoryDoc, setLastHistoryDoc] = useState<any>(null);
  const [daysLimit, setDaysLimit] = useState<number>(365);
  const [hasMoreHistory, setHasMoreHistory] = useState<boolean>(true);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState<boolean>(false);
  const [waterLogs, setWaterLogs] = useState<WaterLog[]>(() => {
    try {
      const saved = localDb.getItemSync('water_logs');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.map(item => ({
            ...item,
            timestamp: ensureTimestampMillis(item.timestamp)
          }));
        }
      }
      return [];
    } catch { return []; }
  });
  const [scannedProducts, setScannedProducts] = useState<ScannedProduct[]>(() => {
    try {
      const saved = localDb.getItemSync('scanned_products');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.map(item => ({
            ...item,
            timestamp: ensureTimestampMillis(item.timestamp)
          }));
        }
      }
      return [];
    } catch { return []; }
  });
  const [weightLogs, setWeightLogs] = useState<WeightLog[]>(() => {
    try {
      const saved = localDb.getItemSync('weight_logs');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((item: any) => ({
            ...item,
            timestamp: ensureTimestampMillis(item.timestamp)
          }));
        }
      }
    } catch { }

    const initialWeightStr = localStorage.getItem('onboardingWeight');
    const initW = initialWeightStr ? parseFloat(initialWeightStr) : 70;
    if (initW > 0) {
      const now = Date.now();
      const seed: WeightLog[] = [
        { id: 'seed-w-1', timestamp: now - 14 * 86400000, weight_kg: Number((initW + 1.2).toFixed(1)) },
        { id: 'seed-w-2', timestamp: now - 7 * 86400000, weight_kg: Number((initW + 0.6).toFixed(1)) },
        { id: 'seed-w-3', timestamp: now - 3 * 86400000, weight_kg: Number((initW + 0.2).toFixed(1)) },
        { id: 'seed-w-4', timestamp: now, weight_kg: Number(initW.toFixed(1)) }
      ];
      try {
        localDb.setItem('weight_logs', JSON.stringify(safeClone(seed)));
      } catch (e) {}
      return seed;
    }
    return [];
  });
  const [weightLogInput, setWeightLogInput] = useState<string>(() => {
    return localStorage.getItem('onboardingWeight') || '70';
  });
  const [weightLogDate, setWeightLogDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [weightLogNote, setWeightLogNote] = useState<string>('');
  const [historyTab, setHistoryTab] = useState<'logs' | 'scanned'>('logs');
  const [archivedCount, setArchivedCount] = useState<number>(0);
  const [isArchiving, setIsArchiving] = useState<boolean>(false);
  const [isHistoryHeaderFolded, setIsHistoryHeaderFolded] = useState<boolean>(false);
  const [expandedMealLogIds, setExpandedMealLogIds] = useState<string[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState<boolean>(false);
  const [selectedMealIds, setSelectedMealIds] = useState<string[]>([]);
  const [selectedScannedProduct, setSelectedScannedProduct] = useState<ScannedProduct | null>(null);
  const [scannedPortionAmount, setScannedPortionAmount] = useState<number>(100);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<HistoryEntry | null>(null);
  const [detailsChartType, setDetailsChartType] = useState<'macro' | 'micro'>('macro');
  const [mealScaleMultiplier, setMealScaleMultiplier] = useState<number>(1);
  const [animatedMultiplier, setAnimatedMultiplier] = useState<number>(1);
  const [customWeightInput, setCustomWeightInput] = useState<string>('');
  const [nutrientSortOrder, setNutrientSortOrder] = useState<'desc' | 'asc'>('desc');
  const [favoritesSearchQuery, setFavoritesSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editingMeal, setEditingMeal] = useState<HistoryEntry | null>(null);
  const [isMealNameManuallyEdited, setIsMealNameManuallyEdited] = useState(false);
  const [rawGoals, setGoals] = useState<UserGoals>(() => {
    try {
      const saved = localDb.getItemSync('user_goals') || (typeof localStorage !== 'undefined' ? localStorage.getItem('user_goals') : null);
      return saved ? JSON.parse(saved) : DEFAULT_GOALS;
    } catch { return DEFAULT_GOALS; }
  });
  const [lang, setLang] = useState<Language>(() => {
    const saved = localStorage.getItem('app_lang');
    if (saved === 'en') return 'en-US';
    return (saved as Language) || 'en-US';
  });
  const [sourceChatMessageIndex, setSourceChatMessageIndex] = useState<number | null>(null);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [userMetrics, setUserMetrics] = useState<string | null>(() => localStorage.getItem('user_metrics_summary'));
  const [lastChatUndoAction, setLastChatUndoAction] = useState<{
    type: 'message' | 'log';
    messageIndex: number;
    mealId?: string;
    waterId?: string;
    timestamp: number;
  } | null>(null);
  const [skin, setSkin] = useState<Skin>(() => {
    const saved = localStorage.getItem('app_skin');
    if (saved === 'stalker' || saved === 'cyberpunk' || saved === 'anime' || saved === 'default') {
      return saved as Skin;
    }
    return 'default';
  });
  const [cyberpunkTone, setCyberpunkTone] = useState<'yellow-cyan' | 'cyan-magenta'>(() => {
    const saved = localStorage.getItem('app_cyberpunk_tone');
    return (saved === 'cyan-magenta') ? 'cyan-magenta' : 'yellow-cyan';
  });
  const [skinTransitionActive, setSkinTransitionActive] = useState(false);
  const [skinTransitionTarget, setSkinTransitionTarget] = useState<Skin>('default');
  const [logoTrigger, setLogoTrigger] = useState(false);
// [theme declared at top of App]
// [autoThemeEnabled declared at top of App]
  const [autoThemeSunrise, setAutoThemeSunrise] = useState<string>(() => {
    return localStorage.getItem('auto_theme_sunrise') || '06:00';
  });
  const [autoThemeSunset, setAutoThemeSunset] = useState<string>(() => {
    return localStorage.getItem('auto_theme_sunset') || '20:00';
  });
  const [autoThemeLocationActive, setAutoThemeLocationActive] = useState<boolean>(() => {
    return localStorage.getItem('auto_theme_location_active') === 'true';
  });
  const [illumination, setIllumination] = useState<boolean>(() => {
    const saved = localStorage.getItem('app_illumination');
    return saved === null ? true : saved === 'true';
  });
  const [illuminationColor, setIlluminationColor] = useState<string>(() => {
    return localStorage.getItem('app_illumination_color') || 'emerald';
  });
  const [previewIlluminationColor, setPreviewIlluminationColor] = useState<string>(illuminationColor);
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('pro_audio_enabled') !== 'false';
    }
    return true;
  });
  const [notifPermState, setNotifPermState] = useState<string>(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission;
    }
    return 'not-supported';
  });
  const [enableMealReview, setEnableMealReview] = useState<boolean>(() => {
    const saved = localStorage.getItem('enable_meal_review');
    return saved === null ? true : saved === 'true';
  });
  const [vibrateOnLogging, setVibrateOnLogging] = useState<boolean>(() => {
    const saved = localStorage.getItem('vibrate_on_logging');
    return saved === null ? true : saved === 'true';
  });
  const [enableAiSuggestions, setEnableAiSuggestions] = useState<boolean>(() => {
    const saved = localStorage.getItem('enable_ai_suggestions');
    return saved === null ? true : saved === 'true';
  });
  const [ignoreMealTimeLogging, setIgnoreMealTimeLogging] = useState<boolean>(() => {
    const saved = localStorage.getItem('ignore_meal_time_logging');
    return saved === 'true';
  });
  const [dismissedAiSuggestionsDate, setDismissedAiSuggestionsDate] = useState<string | null>(() => localStorage.getItem('dismissed_ai_suggestions_date'));
  const [isAiSuggestionsCollapsed, setIsAiSuggestionsCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('ai_suggestions_collapsed');
    return saved === null ? false : saved === 'true';
  });
  const [activeMealSuggestionIndices, setActiveMealSuggestionIndices] = useState<Record<number, number>>({});
  const [enableHealthDeficiencyAlert, setEnableHealthDeficiencyAlert] = useState<boolean>(() => {
    const saved = localStorage.getItem('enable_health_deficiency_alert');
    return saved === null ? true : saved === 'true';
  });
  const [isDeficiencyAlertsCollapsed, setIsDeficiencyAlertsCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('deficiency_alerts_collapsed');
    return saved === null ? false : saved === 'true';
  });
  const [dismissedDeficiencyAlertsDate, setDismissedDeficiencyAlertsDate] = useState<string | null>(() => localStorage.getItem('dismissed_deficiency_alerts_date'));
  const [showFastingWidget, setShowFastingWidget] = useState<boolean>(() => {
    const saved = localStorage.getItem('show_fasting_widget');
    return saved === null ? false : saved === 'true';
  });
  const [fastingState, setFastingState] = useState<FastingState>(() => {
    try {
      const saved = localStorage.getItem('main_nutrition_fasting_state');
      return saved ? JSON.parse(saved) : undefined;
    } catch (e) {
      return undefined;
    }
  });
  const [showMealReviewTooltip, setShowMealReviewTooltip] = useState(false);
  const [showDeficiencyAlertTooltip, setShowDeficiencyAlertTooltip] = useState(false);
  const [showAutoThemeTooltip, setShowAutoThemeTooltip] = useState(false);
  const [showIgnoreMealTimeTooltip, setShowIgnoreMealTimeTooltip] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showNoFoodModal, setShowNoFoodModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewLogDate, setReviewLogDate] = useState<string>('');
  const [pendingReviewData, setPendingReviewData] = useState<MealAnalysis | null>(null);
  const originalPendingReviewFoodsRef = useRef<any[] | null>(null);
  const [isReCalculating, setIsReCalculating] = useState(false);
  const [recalcStep, setRecalcStep] = useState(0);
  const [preloadedAnalysis, setPreloadedAnalysis] = useState<MealAnalysis | null>(null);
  const [preloadedImage, setPreloadedImage] = useState<string | null>(null);
  const [isPreloadingAnalysis, setIsPreloadingAnalysis] = useState(false);
  const preloadedFoodListRef = useRef<string>('');
  const preloadingTimeoutRef = useRef<any>(null);
  const [showQuasoModal, setShowQuasoModal] = useState(false);
  const [pendingQuasoData, setPendingQuasoData] = useState<MealAnalysis | null>(null);
  const [showAdminPurgeConfirm, setShowAdminPurgeConfirm] = useState(false);
  const [isAdminPurging, setIsAdminPurging] = useState(false);
  const [excludedFoods, setExcludedFoods] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('ai_excluded_foods');
      return safeJsonParse(saved, []);
    } catch (e) {
      return [];
    }
  });
  const [preferredIngredients, setPreferredIngredients] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('ai_preferred_ingredients');
      return saved || '';
    } catch (e) {
      return '';
    }
  });
  const [showAiSuggestionsSettings, setShowAiSuggestionsSettings] = useState(false);
  const [filterSafeChoiceAllergens, setFilterSafeChoiceAllergens] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('filter_safe_choice_allergens');
      return saved === 'true';
    } catch (e) {
      return false;
    }
  });
  const [showAllergenFilterTooltip, setShowAllergenFilterTooltip] = useState<boolean>(false);
  const [isBadgesExpanded, setIsBadgesExpanded] = useState<boolean>(false);
  const [isRankExpanded, setIsRankExpanded] = useState<boolean>(false);
  const [aiMealSuggestions, setAiMealSuggestions] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem('ai_meal_suggestions_cache');
      const parsed = safeJsonParse(saved, []);
      if (!Array.isArray(parsed) || parsed.length === 0) return [];
      // Safety check: if the first item doesn't have 'options', it's the old flat structure
      if (parsed[0].meal_name && !parsed[0].options) {
        const groupedMap: Record<string, any[]> = {};
        parsed.forEach((item: any) => {
          const mType = (item.meal_type || 'snack').toLowerCase();
          if (!groupedMap[mType]) groupedMap[mType] = [];
          groupedMap[mType].push(item);
        });
        return Object.entries(groupedMap).map(([meal_type, options]) => ({ meal_type, options }));
      }
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [groceryItems, setGroceryItems] = useState<GroceryItem[]>(() => {
    try {
      const saved = localStorage.getItem('grocery_items');
      return safeJsonParse(saved, []);
    } catch { return []; }
  });
  const [isAddingToGrocery, setIsAddingToGrocery] = useState<string | null>(null);
  const [activeRecipeModal, setActiveRecipeModal] = useState<{
    isOpen: boolean;
    mealName: string;
    ingredients?: any[];
    macros?: any;
  }>({ isOpen: false, mealName: '' });
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [suggestionsGeneratedForDate, setSuggestionsGeneratedForDate] = useState<string | null>(() => localStorage.getItem('ai_suggestions_generated_key'));
  const [lastSuggestionFetchAttempt, setLastSuggestionFetchAttempt] = useState<number>(0);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [addingSuggestionId, setAddingSuggestionId] = useState<string | null>(null);
  const [adjustingSuggestionId, setAdjustingSuggestionId] = useState<string | null>(null);
  const [adjustmentInput, setAdjustmentInput] = useState<string>('');
  const [loggingQuickAddId, setLoggingQuickAddId] = useState<string | null>(null);
  const [isQuickAddExpanded, setIsQuickAddExpanded] = useState<boolean>(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [showStatusTooltip, setShowStatusTooltip] = useState(false);
  const [isGoogleFitModalOpen, setIsGoogleFitModalOpen] = useState(false);
  const [isGoogleFitConnected, setIsGoogleFitConnected] = useState<boolean>(() => {
    return localStorage.getItem('google_fit_connected') === 'true';
  });
  const [isAppleHealthConnected, setIsAppleHealthConnected] = useState<boolean>(() => {
    return localStorage.getItem('apple_health_connected') === 'true';
  });
  const [appleHealthTodayCalories, setAppleHealthTodayCalories] = useState<number | null>(null);
  const [itemToRemove, setItemToRemove] = useState<number | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraPurpose, setCameraPurpose] = useState<'scan' | 'attach'>('scan');
  const [captureCallback, setCaptureCallback] = useState<((img: string) => void) | null>(null);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [cameraMode, setCameraMode] = useState<'photo' | 'barcode'>('photo');
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [isScanning, setIsScanning] = useState(false);
  const [scannedActiveProduct, setScannedActiveProduct] = useState<any | null>(null);
  const html5QrCodeRef = useRef<any | null>(null);
  const lastFailedScanRef = useRef<{ code: string, time: number }>({ code: '', time: 0 });
  const [countdown, setCountdown] = useState(3);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right'>('right');
  const [expandedFoodIdx, setExpandedFoodIdx] = useState<number | null>(null);
  const [expandedPortionIdx, setExpandedPortionIdx] = useState<number | null>(null);
  const [isNutritionFactsExpanded, setIsNutritionFactsExpanded] = useState(false);
  const [isAdjustPortionExpanded, setIsAdjustPortionExpanded] = useState(false);
  const [isAdjustPortionModalOpen, setIsAdjustPortionModalOpen] = useState(false);
  const [manualMealName, setManualMealName] = useState('');
  const [manualMealNotes, setManualMealNotes] = useState('');
  const [manualCalories, setManualCalories] = useState('');
  const [manualProtein, setManualProtein] = useState('');
  const [manualCarbs, setManualCarbs] = useState('');
  const [manualFat, setManualFat] = useState('');
  const [manualSugar, setManualSugar] = useState('');
  const [manualFiber, setManualFiber] = useState('');
  const [manualSodium, setManualSodium] = useState('');
  const [manualAmount, setManualAmount] = useState('100');
  const [manualUnit, setManualUnit] = useState<'gr' | 'ml' | 'pcs'>('gr');
  const [manualCategory, setManualCategory] = useState<string>('other');
  const [manualImage, setManualImage] = useState<string | null>(null);
  const [lastAutoFillQuery, setLastAutoFillQuery] = useState('');
  const [isAutoFillingNutrients, setIsAutoFillingNutrients] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoProgressMsg, setVideoProgressMsg] = useState<string>('');
  const [manualInsight, setManualInsight] = useState('');
  const [manualPortionAnalysis, setManualPortionAnalysis] = useState('');
  const [cameraPermissionStatus, setCameraPermissionStatus] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [micPermissionStatus, setMicPermissionStatus] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [searchTerm, setSearchTerm] = useState('');
  const [isHistorySearchFocused, setIsHistorySearchFocused] = useState(false);
  const [isFavoritesSearchFocused, setIsFavoritesSearchFocused] = useState(false);
  const [sortKey, setSortKey] = useState<'date' | 'calories' | 'name' | 'tag' | 'rating'>('date');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(false);
  const [tagSortMode, setTagSortMode] = useState<'mealType' | 'category'>('mealType');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [historyStartDate, setHistoryStartDate] = useState<string>('');
  const [historyEndDate, setHistoryEndDate] = useState<string>('');
  const [isDateRangeSpoilerOpen, setIsDateRangeSpoilerOpen] = useState<boolean>(false);
  const [historyVisibleDaysCount, setHistoryVisibleDaysCount] = useState<number>(3);
  const [foodExplanation, setFoodExplanation] = useState<{name: string, explanation: string} | null>(null);
  const [explanationCache, setExplanationCache] = useState<Record<string, string>>({});
  const [isGettingExplanation, setIsGettingExplanation] = useState(false);
  const preloadingKeysRef = useRef<Set<string>>(new Set());
  const [progressTab, setProgressTab] = useState<'daily' | 'weekly' | 'monthly' | 'calendar' | 'ecosystem'>('daily');
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const [lastSelectedPeriod, setLastSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const periodDropdownRef = useRef<HTMLDivElement>(null);
  const [trendMicronutrient, setTrendMicronutrient] = useState<string>('potassium_mg');
  const [googleFitData, setGoogleFitData] = useState<any[]>([]);
  const [googleFitLoading, setGoogleFitLoading] = useState<boolean>(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [galleryMealTypeFilter, setGalleryMealTypeFilter] = useState<MealType | 'all'>('all');
  const [galleryStartDate, setGalleryStartDate] = useState<string>('');
  const [galleryEndDate, setGalleryEndDate] = useState<string>('');
  const [isGalleryFiltersOpen, setIsGalleryFiltersOpen] = useState(false);
  const [isGalleryTagFilterOpen, setIsGalleryTagFilterOpen] = useState(false);
  const [waterSliderValue, setWaterSliderValue] = useState(250);
  const [liquidType, setLiquidType] = useState<'water' | 'coffee' | 'tea' | 'custom' | string>('water');
  const [userResidence, setUserResidenceState] = useState<'US' | 'UK' | 'UA' | 'GLOBAL'>(() => {
    const saved = localStorage.getItem('user_residence') as any;
    if (saved && ['US', 'UK', 'UA', 'GLOBAL'].includes(saved)) return saved;
    return lang === 'ua' ? 'UA' : lang === 'en-GB' ? 'UK' : 'US';
  });
  const [isAge18Plus, setIsAge18PlusState] = useState<boolean>(() => {
    return localStorage.getItem('is_age_18_plus') === 'true';
  });
  const [selectedDrinkIds, setSelectedDrinkIdsState] = useState<string[]>(() => {
    const saved = localStorage.getItem('selected_drink_ids');
    if (saved) {
      const parsed = safeJsonParse(saved, []);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed.slice(0, 10);
    }
    return getDefaultSelectedDrinkIds(lang === 'ua' ? 'UA' : lang === 'en-GB' ? 'UK' : 'US');
  });
  const [customUserDrinks, setCustomUserDrinksState] = useState<CustomDrinkOption[]>(() => {
    const saved = localStorage.getItem('custom_user_drinks');
    return safeJsonParse(saved, []);
  });
  const [selectedBrandDrinkId, setSelectedBrandDrinkId] = useState<string | null>(null);
  const [showAgeVerificationModal, setShowAgeVerificationModal] = useState<boolean>(false);
  const [pendingEnergyDrink, setPendingEnergyDrink] = useState<DrinkBrandItem | null>(null);
  const [isAddCustomDrinkModalOpen, setIsAddCustomDrinkModalOpen] = useState<boolean>(false);
  const [beverageSearchQuery, setBeverageSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [newDrinkName, setNewDrinkName] = useState('');
  const [newDrinkBrand, setNewDrinkBrand] = useState('');
  const [newDrinkVolume, setNewDrinkVolume] = useState(330);
  const [newDrinkCalories, setNewDrinkCalories] = useState(42);
  const [newDrinkSugar, setNewDrinkSugar] = useState(10);
  const [newDrinkCaffeine, setNewDrinkCaffeine] = useState(0);
  const [newDrinkIsEnergy, setNewDrinkIsEnergy] = useState(false);
  const [chatInput, setChatInput] = useState(() => localStorage.getItem('chat_input_draft') || '');
  const [chatImage, setChatImage] = useState<string | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showChooseOptions, setShowChooseOptions] = useState(false);
  const [isChatPicking, setIsChatPicking] = useState(false);
  const [chatPickingSource, setChatPickingSource] = useState<'favorites' | 'gallery' | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribingVoiceNote, setIsTranscribingVoiceNote] = useState(false);
  const [voiceNoteDuration, setVoiceNoteDuration] = useState(0);
  const voiceNoteTimerRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [lastReminderDate, setLastReminderDate] = useState<string | null>(() => localStorage.getItem('last_reminder_date'));
  const [reminderEnabled, setReminderEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('reminder_enabled');
    return saved === null ? true : saved === 'true';
  });
  const [reminderHour, setReminderHour] = useState<number>(() => {
    const saved = localStorage.getItem('reminder_hour');
    return saved === null ? 12 : parseInt(saved, 10);
  });
  const [reminderToastId, setReminderToastId] = useState<string | number | null>(null);
  const [lastProgressReminderDate, setLastProgressReminderDate] = useState<string>(() => localStorage.getItem('last_progress_reminder_date') || '');
  const [progressReminderToastId, setProgressReminderToastId] = useState<string | number | null>(null);
  const [isGoalsExpanded, setIsGoalsExpanded] = useState(false);
  const [isPersonalDataExpanded, setIsPersonalDataExpanded] = useState(false);
  const [dailyStreakViewMode, setDailyStreakViewMode] = useState<'standard' | 'radial'>(() => {
    return (localStorage.getItem('daily_streak_view_mode') as 'standard' | 'radial') || 'standard';
  });
  const [dailyProgressViewMode, setDailyProgressViewMode] = useState<'horizontal' | 'radial'>(() => {
    return (localStorage.getItem('daily_progress_view_mode') as 'horizontal' | 'radial') || 'horizontal';
  });
  const [isPersonalizationExpanded, setIsPersonalizationExpanded] = useState(false);
  const [isDrinksBrandsExpanded, setIsDrinksBrandsExpanded] = useState(false);
  const [isDietaryPlanExpanded, setIsDietaryPlanExpanded] = useState(false);
  const [foodPreferences, setFoodPreferences] = useState<string>(() => {
    return localStorage.getItem('food_preferences') || '';
  });
  const [foodBudget, setFoodBudget] = useState<string>(() => {
    return localStorage.getItem('food_budget') || '';
  });
  const [isPermissionsExpanded, setIsPermissionsExpanded] = useState(false);
  const [isLegalExpanded, setIsLegalExpanded] = useState(false);
  const [isAdditionalExpanded, setIsAdditionalExpanded] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<'free' | 'pro' | 'premium' | 'premium_pro'>(() => {
    const saved = localStorage.getItem('subscription_tier');
    if (saved === 'premium_pro') return 'premium';
    if (saved === 'pro' || saved === 'premium' || saved === 'free') return saved;
    const email = localStorage.getItem('user_email') || getSession()?.email || '';
    if (isKimAdmin(email)) return 'premium';
    return 'free';
  });
  const [isSubscriptionExpanded, setIsSubscriptionExpanded] = useState(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [isNutriAgentConfigOpen, setIsNutriAgentConfigOpen] = useState(false);
  const [isNutriAgentActive, setIsNutriAgentActive] = useState(false);
  const [nutriAgentConfig, setNutriAgentConfig] = useState<NutriAgentLaunchConfig | null>(null);
  const [isNutriAgentApprovalOpen, setIsNutriAgentApprovalOpen] = useState(false);

  useEffect(() => {
    sounds.setEnabled(soundEnabled);
    sfx.setMuted(!soundEnabled);
  }, [soundEnabled]);

  useEffect(() => {
    const handleOpenNutriAgent = (e?: any) => {
      const detail = e?.detail;
      const isPremiumUser = subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';

      if (!isPremiumUser) {
        toast.info(
          lang === 'ua'
            ? 'Автономне замовлення їжі через Nutri-Agent доступне ексклюзивно у тарифі Premium!'
            : 'Autonomous food ordering via Nutri-Agent is exclusively available on the Premium tier!',
          { icon: '👑' }
        );
        setIsBillingModalOpen(true);
        return;
      }

      if (localStorage.getItem('nutri_agent_enabled') === 'false') {
        toast.info(
          lang === 'ua'
            ? 'Nutri-Agent вимкнено в налаштуваннях. Увімкніть його в меню налаштувань.'
            : 'Nutri-Agent is disabled in settings. Enable it in settings menu.',
          { icon: '🤖' }
        );
        return;
      }

      const isUa = lang === 'ua';
      const targetStore = detail?.store || (isUa ? 'silpo' : 'instacart');
      const currency = detail?.currency || (isUa ? 'UAH' : 'USD');
      const currencySymbol = currency === 'UAH' ? '₴' : '$';

      const items = detail?.items && Array.isArray(detail.items) ? detail.items : [];
      const budget = detail?.budget || (currency === 'UAH' ? 500 : 40);

      const generatedConfig: NutriAgentLaunchConfig = {
        budgetLimitUSD: budget,
        currency,
        currencySymbol,
        targetStores: [targetStore],
        messengerPlatform: ['app_push'],
        notificationChannels: ['app_push'],
        userNotes: detail?.userNotes || (isUa ? 'Замовлення через ШІ Чат Nutri-Agent' : 'Order via Nutri-Agent AI Chat'),
        groceryListMode: items.length > 0 ? 'only_grocery' : 'include_grocery',
        groceryItems: items.length > 0 ? items.map((i: any) => ({
          name: i.name,
          amount: String(i.amount || '1'),
          unit: i.unit || (isUa ? 'шт' : 'item'),
          checked: false
        })) : undefined,
        fulfillmentMethod: detail?.fulfillmentMethod || 'delivery'
      };

      setNutriAgentConfig(generatedConfig);

      if (detail?.directLaunch && items.length > 0) {
        setIsNutriAgentActive(true);
        toast.success(
          isUa
            ? 'Nutri-Agent розпочав сканування каталогу Сільпо!'
            : 'Nutri-Agent started scanning store catalog!',
          { icon: '🛒' }
        );
      } else {
        setIsNutriAgentConfigOpen(true);
      }
    };
    const handleOpenSubscription = () => {
      setIsBillingModalOpen(true);
    };
    window.addEventListener('open-nutriagent', handleOpenNutriAgent);
    window.addEventListener('open-subscription-modal', handleOpenSubscription);
    return () => {
      window.removeEventListener('open-nutriagent', handleOpenNutriAgent);
      window.removeEventListener('open-subscription-modal', handleOpenSubscription);
    };
  }, [subscriptionTier, lang]);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [checkoutTier, setCheckoutTier] = useState<'pro' | 'premium' | null>(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [isTrialActive, setIsTrialActive] = useState(() => false);
  const [mealContextMenu, setMealContextMenu] = useState<{ id: string, x: number, y: number } | null>(null);
  const mealLongPressTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isLongPressingMeal, setIsLongPressingMeal] = useState(false);
  const prevViewRef = useRef<any>(view);
  const [userName, setUserName] = useState<string>(() => {
    const saved = localStorage.getItem('userName');
    return (saved && saved !== 'null') ? saved : '';
  });
  const [customAvatar, setCustomAvatar] = useState<string>(() => localStorage.getItem('custom_avatar') || '');
  const customAvatarRef = useRef(customAvatar);
  const [avatarImageError, setAvatarImageError] = useState<boolean>(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [gamificationTitleName, setGamificationTitleName] = useState<string>(() => localStorage.getItem('gamification_title_name') || '');
  const [gamificationNeonColor, setGamificationNeonColor] = useState<string>(() => localStorage.getItem('gamification_neon_color') || '');
  const [mutualLikesCount, setMutualLikesCount] = useState<number>(() => Number(localStorage.getItem('mutual_likes_count') || '0'));
  const [userTokensCount, setUserTokensCount] = useState<number>(() => {
    try {
      const cached = localStorage.getItem('main_nutrition_gamification_profile');
      if (cached) {
        const parsed = JSON.parse(cached);
        return typeof parsed.token_balance === 'number' ? parsed.token_balance : (parsed.total_tokens_earned || 100);
      }
    } catch {}
    return 100;
  });
  const [gamificationTotalLogs, setGamificationTotalLogs] = useState<number>(() => Number(localStorage.getItem('gamification_total_logs') || '0'));
  const [userBioBoxes, setUserBioBoxes] = useState<BioBoxInventoryItem[]>(() => {
    try {
      const cached = localStorage.getItem('main_nutrition_bio_boxes') || localStorage.getItem('main_nutrition_bio_crates');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [selectedBioBoxForUnbox, setSelectedBioBoxForUnbox] = useState<BioBoxInventoryItem | null>(null);
  const [isBioBoxModalOpen, setIsBioBoxModalOpen] = useState<boolean>(false);
  const [includeRecipe, setIncludeRecipe] = useState<boolean>(false);
// [themeRef declared at top of App]
  const goalsRef = useRef(rawGoals);
  const userNameRef = useRef(userName);
  const langRef = useRef(lang);
  const autoThemeSunriseRef = useRef(autoThemeSunrise);
  const autoThemeSunsetRef = useRef(autoThemeSunset);
  const autoThemeLocationActiveRef = useRef(autoThemeLocationActive);
  const userMetricsRef = useRef(userMetrics);
  const recognitionRef = useRef<any>(null);
  const scannerRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const remoteTotalsRef = useRef<{ calories: number; water: number; day: string }>({ calories: -1, water: -1, day: '' });
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);

  // ==========================================
  // 2. CORE HELPER FUNCTIONS & FIREBASE UTILITIES
  // ==========================================
  const isPremiumOrPro = subscriptionTier === 'premium' || subscriptionTier === 'pro' || subscriptionTier === 'premium_pro';
  // isAdmin moved to Section 3 useMemo
  const completeOnboarding = async () => {
    if (user) {
      // Calculate goals
      const weight = parseFloat(onboardingWeight) || 70;
      const height = parseFloat(onboardingHeight) || 170;
      const age = parseFloat(onboardingAge) || 25;
      const s = onboardingGender === 'male' ? 5 : -161;
      
      const bmr = (10 * weight) + (6.25 * height) - (5 * age) + s;
      const tdee = bmr * onboardingActivity;
      
      let targetCalories = tdee;
      if (onboardingGoal === 'lose') targetCalories -= 500;
      if (onboardingGoal === 'gain') targetCalories += 500;
      
      targetCalories = Math.round(targetCalories);
      
      // Macro ratios based on goal
      let pRatio = 0.3;
      let cRatio = 0.4;
      let fRatio = 0.3;

      if (onboardingGoal === 'lose') {
        pRatio = 0.4;
        cRatio = 0.3;
        fRatio = 0.3;
      } else if (onboardingGoal === 'gain') {
        pRatio = 0.25;
        cRatio = 0.5;
        fRatio = 0.25;
      }

      // Default macros if not provided
      const p = onboardingProtein ? parseFloat(onboardingProtein) : Math.round((targetCalories * pRatio) / 4);
      const c = onboardingCarbs ? parseFloat(onboardingCarbs) : Math.round((targetCalories * cRatio) / 4);
      const f = onboardingFat ? parseFloat(onboardingFat) : Math.round((targetCalories * fRatio) / 9);
      
      const newGoals: UserGoals = {
        calories: targetCalories,
        protein: p,
        carbs: c,
        fat: f,
        water_ml: onboardingWater ? parseFloat(onboardingWater) : Math.round(weight * 33), // 33ml per kg
        sugar_g: onboardingSugar ? parseFloat(onboardingSugar) : Math.round(targetCalories * 0.1 / 4), // 10% of calories
        fiber_g: Math.round(targetCalories / 1000 * 14), // 14g per 1000kcal
        sodium_mg: onboardingSodium ? parseFloat(onboardingSodium) : 2300,
        caffeine_mg: 400,
      };
      
      setGoals(newGoals);
      localStorage.setItem('user_goals', JSON.stringify(safeClone(newGoals)));
      localStorage.setItem('onboarding_completed', 'true');
      
      // Persist individual metrics locally
      localStorage.setItem('onboardingGender', onboardingGender);
      localStorage.setItem('onboardingAge', onboardingAge);
      localStorage.setItem('onboardingWeight', onboardingWeight);
      localStorage.setItem('onboardingHeight', onboardingHeight);
      localStorage.setItem('onboardingActivity', String(onboardingActivity));
      localStorage.setItem('onboardingGoal', onboardingGoal);
      localStorage.setItem('weightToLose', weightToLose);
      
      // Apply language and theme
      setLang(onboardingLanguage);
      localStorage.setItem('app_lang', onboardingLanguage);
      setTheme(onboardingTheme);
      localStorage.setItem('theme', onboardingTheme);
      
      const metricsSummary = `User physical profile: ${onboardingGender} ${onboardingAge}yo, ${onboardingWeight}kg, ${onboardingHeight}cm, Activity: ${onboardingActivity}x, Goal: ${onboardingGoal}`;
      setUserMetrics(metricsSummary);
      localStorage.setItem('user_metrics_summary', metricsSummary);

      setJustFinishedOnboarding(true);
      const userPath = `users/${user.uid}`;
      try {
        await setDocWithQueue(doc(db, 'users', user.uid), { 
          userName: onboardingName,
          vibe: onboardingVibe,
          onboardingCompleted: true,
          goals: newGoals,
          language: onboardingLanguage,
          theme: onboardingTheme,
          metrics: {
            gender: onboardingGender,
            age: parseFloat(onboardingAge),
            weight: parseFloat(onboardingWeight),
            height: parseFloat(onboardingHeight),
            activity: onboardingActivity,
            goal: onboardingGoal
          },
          weightToLose: weightToLose,
          metricsSummary // Save this too
        }, { merge: true });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, userPath);
      }
      
      setShowOnboarding(false);
      setView('home');
    }
  };
  const skipOnboarding = async () => {
    const currentUser = user || auth.currentUser;
    console.log('skipOnboarding called, user:', currentUser);
    if (currentUser) {
      // Use current goals if they are already set (e.g. returning user), otherwise use defaults
      const goalsToSave = (rawGoals && rawGoals.calories > 0 && rawGoals.calories !== DEFAULT_GOALS.calories) 
        ? rawGoals 
        : DEFAULT_GOALS;
      
      setGoals(goalsToSave);
      localStorage.setItem('user_goals', JSON.stringify(safeClone(goalsToSave)));
      localStorage.setItem('onboarding_completed', 'true');
      
      // Defaults for language and theme if not already set
      if (!localStorage.getItem('app_lang')) {
        setLang('en-US');
        localStorage.setItem('app_lang', 'en-US');
      }
      if (!localStorage.getItem('theme')) {
        setTheme('dark');
        localStorage.setItem('theme', 'dark');
      }
      
      if (onboardingName) {
        setUserName(onboardingName);
        localStorage.setItem('userName', onboardingName);
      }
      
      setJustFinishedOnboarding(true);
      const userPath = `users/${currentUser.uid}`;
      try {
        await setDocWithQueue(doc(db, 'users', currentUser.uid), { 
          onboardingCompleted: true,
          goals: goalsToSave,
          ...((onboardingName || userName) ? { userName: (onboardingName || userName).trim() } : {}),
          language: lang.startsWith('en') ? lang : (lang || 'en-US'),
          theme: theme || 'dark'
        }, { merge: true });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, userPath);
      }
      
      setShowOnboarding(false);
      setView('home');
    } else {
      console.error('User is null in skipOnboarding');
    }
  };
  const submitBugReport = async () => {
    if (!bugDescription.trim()) {
      toast.error('Tell us what happened first, dawg!');
      return;
    }

    setIsVerifyingBugImage(true);
    try {
      if (bugScreenshot) {
        const verification = await verifyBugReportImage(bugScreenshot);
        if (!verification.isBug) {
          toast.error(verification.reason || "Bruh... that doesn't look like a bug report! 🤨", {
            duration: 5000
          });
          setIsVerifyingBugImage(false);
          return;
        }
      }

      const report = {
        userId: user?.uid || 'anonymous',
        userEmail: user?.email || 'anonymous',
        description: bugDescription,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        image: bugScreenshot || null
      };

      // Save to Firestore for permanent record
      const docRef = doc(collection(db, 'bug_reports')); await setDocWithQueue(docRef, report);

      // Send to backend for email notification
      fetchWrapper('/api/bug-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(safeClone(report))
      }).catch(err => console.error("Failed to call backend bug report api:", err));

      toast.success('Bug report submitted! Thanks, bro! 👊');
      setBugDescription('');
      setBugScreenshot(null);
      setIsBugModalOpen(false);
    } catch (error) {
      console.error("Failed to submit bug report:", error);
      toast.error("Failed to submit report. Try again later, bro.");
    } finally {
      setIsVerifyingBugImage(false);
    }
  };
  const scrollToSectionTop = (target?: string | HTMLElement | null, smooth: boolean = true) => {
    try {
      if (typeof window === 'undefined') return;

      if (!target) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: smooth ? 'smooth' : 'auto'
        });
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;

        // Ensure alignment after view switch re-render
        requestAnimationFrame(() => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: smooth ? 'smooth' : 'auto'
          });
          if (document.documentElement) document.documentElement.scrollTop = 0;
          if (document.body) document.body.scrollTop = 0;
        });
        return;
      }

      const performScroll = (element: HTMLElement) => {
        const header = document.querySelector('header');
        const headerHeight = header ? header.getBoundingClientRect().height : 70;
        const elementRect = element.getBoundingClientRect();
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const absoluteElementTop = elementRect.top + currentScrollY;
        const targetScroll = Math.max(0, absoluteElementTop - headerHeight - 12);

        window.scrollTo({
          top: targetScroll,
          left: 0,
          behavior: smooth ? 'smooth' : 'auto'
        });
      };

      if (typeof target !== 'string') {
        if (target && typeof (target as any).getBoundingClientRect === 'function') {
          performScroll(target as HTMLElement);
        }
        return;
      }

      const targetId = target;
      const startTime = Date.now();
      const maxWaitMs = 1200;

      const attemptScroll = () => {
        const el = document.getElementById(targetId);
        if (el) {
          performScroll(el);
          // Re-align after accordion / dynamic height transitions settle
          setTimeout(() => {
            const freshEl = document.getElementById(targetId);
            if (freshEl) performScroll(freshEl);
          }, 250);
          setTimeout(() => {
            const freshEl = document.getElementById(targetId);
            if (freshEl) performScroll(freshEl);
          }, 450);
          return;
        }

        if (Date.now() - startTime < maxWaitMs) {
          requestAnimationFrame(attemptScroll);
        } else {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: smooth ? 'smooth' : 'auto'
          });
        }
      };

      requestAnimationFrame(attemptScroll);
    } catch (e) {
      try {
        if (typeof target === 'string') {
          const el = document.getElementById(target);
          if (el) el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
        } else if (target && typeof (target as any).scrollIntoView === 'function') {
          (target as any).scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
        }
      } catch (err) {
        console.error('scrollToSectionTop fallback error:', err);
      }
    }
  };
  const scrollToBottom = (instant = false) => {
    if (view === 'chat') {
      const performScroll = () => {
        if (chatEndRef.current) {
          try {
            chatEndRef.current.scrollIntoView({
              behavior: instant ? 'auto' : 'smooth',
              block: 'end'
            });
          } catch (e) {}
        }
        window.scrollTo({
          top: Math.max(
            document.documentElement.scrollHeight,
            document.body.scrollHeight || 0
          ),
          behavior: instant ? 'auto' : 'smooth'
        });
      };

      performScroll();
      setTimeout(performScroll, 30);
      setTimeout(performScroll, 100);
      setTimeout(performScroll, 250);
      setTimeout(performScroll, 500);
    }
  };
  const multiplierMotionValue = useMotionValue(1);
  const springMultiplier = useSpring(multiplierMotionValue, { stiffness: 100, damping: 15 });
  const formatSyncTime = (date: Date) => {
    return date.toLocaleTimeString(lang === 'ua' ? 'uk-UA' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: timeFormat === '12h'
    });
  };
  const getSafeChoiceMessage = (status: 'red' | 'amber' | 'green', customMessage?: string) => {
    if (customMessage && customMessage.trim().length > 0) return customMessage;
    const activeT = t || (TRANSLATIONS[lang] || TRANSLATIONS['en-US']).bro || (TRANSLATIONS['en-US'].standard);
    if (status === 'red') return activeT.safeChoiceStopDesc || "Warning: A medical allergen or lifestyle restriction has been detected in this meal!";
    if (status === 'amber') return activeT.safeChoiceReviewDesc || "Possible cross-contamination or unverified ingredients. Please check the label carefully.";
    return activeT.safeChoicePassedDesc || "This meal is completely safe and aligns perfectly with your active profile.";
  };
  const transliterate = (text: string): string => {
    if (!text) return '';
    const rules: Record<string, string> = {
      'а': 'a', 'б': 'b', 'в': 'v', 'г': 'h', 'ґ': 'g', 'д': 'd', 'е': 'e', 'є': 'ye', 'ж': 'zh', 'з': 'z',
      'и': 'y', 'і': 'i', 'ї': 'yi', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n', 'о': 'o', 'п': 'p',
      'р': 'r', 'с': 's', 'т': 't', 'у': 'u', 'ф': 'f', 'х': 'kh', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'shch',
      'ь': '', 'ю': 'yu', 'я': 'ya',
      'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'H', 'Ґ': 'G', 'Д': 'D', 'Е': 'E', 'Є': 'Ye', 'Ж': 'Zh', 'З': 'Z',
      'И': 'Y', 'І': 'I', 'Ї': 'Yi', 'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M', 'Н': 'N', 'О': 'O', 'П': 'P',
      'Р': 'R', 'С': 'S', 'Т': 'T', 'У': 'U', 'Ф': 'F', 'Х': 'Kh', 'Ц': 'Ts', 'Ч': 'Ch', 'Ш': 'Sh', 'Щ': 'Shch',
      'Ь': '', 'Ю': 'Yu', 'Я': 'Ya'
    };
    return text.split('').map(char => rules[char] !== undefined ? rules[char] : char).join('');
  };
  const getTimestampForSelectedDate = (customDate?: string | Date) => {
    const mealDate = customDate ? new Date(customDate) : new Date(selectedDate);
    const currentTime = new Date();
    mealDate.setHours(currentTime.getHours(), currentTime.getMinutes(), currentTime.getSeconds(), currentTime.getMilliseconds());
    return mealDate.getTime();
  };
  const syncDayMealsAndWaterToFirebase = async (dateStr: string, currentHistory: HistoryEntry[], currentWater: WaterLog[], uid: string) => {
    if (isQuotaExceeded) return;
    if (!uid || !dateStr) return;

    try {
      const dayMeals = currentHistory.filter(h => formatDate(new Date(h.timestamp), 'YYYY-MM-DD') === dateStr);
      const dayWater = currentWater.filter(w => formatDate(new Date(w.timestamp), 'YYYY-MM-DD') === dateStr);

      const syncData = {
        date: dateStr,
        history: dayMeals,
        water: dayWater
      };

      const syncDataString = JSON.stringify(safeClone(syncData));
      const cacheKey = `${uid}_${dateStr}`;

      if (daySyncCache.get(cacheKey) === syncDataString) {
        console.log(`[Cache] Skipping Firestore write for ${dateStr}, no changes.`);
        return;
      }
      daySyncCache.set(cacheKey, syncDataString);

      const dayDocRef = doc(db, 'users', uid, 'days', dateStr);
      
      // If both are empty, we can delete the day document to save space/cost, otherwise set it
      if (dayMeals.length === 0 && dayWater.length === 0) {
        await deleteDocWithQueue(dayDocRef);
      } else {
        await setDocWithQueue(dayDocRef, syncData);
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `users/${uid}/days/${dateStr}`);
    }
  };
  const syncHistoryToFirebase = async (entry: HistoryEntry, uid: string, customWaterLogs?: WaterLog[]) => {
    if (isQuotaExceeded) return;
    
    if (!uid || !entry || !entry.id) {
      console.error('Invalid entry or uid in syncHistoryToFirebase', { entry, uid });
      return;
    }
    
    let entryToSync = { ...entry };
    
    // Check if the entry is too large for Firestore (1MB limit)
    const entryString = JSON.stringify(safeClone(entryToSync));
    if (entryString.length > 1000000) {
      console.warn('Entry too large, stripping image to save data');
      entryToSync.image = ''; // Strip image to save the rest of the data
    }

    const dateStr = formatDate(new Date(entryToSync.timestamp), 'YYYY-MM-DD');

    try {
      const otherMeals = history.filter(h => h.id !== entryToSync.id);
      const updatedMeals = [...otherMeals, entryToSync];

      await syncDayMealsAndWaterToFirebase(dateStr, updatedMeals, customWaterLogs || waterLogs, uid);
      
      // Sync to HealthKit / Google Fit in background
      queueHealthSync('MEAL', {
        calories: entryToSync.total_nutrition.calories_kcal,
        protein: entryToSync.total_nutrition.protein_g,
        carbs: entryToSync.total_nutrition.carbs_g,
        fat: entryToSync.total_nutrition.fat_g,
        sugar: entryToSync.total_nutrition.sugar_g || 0,
        fiber: entryToSync.total_nutrition.fiber_g || 0
      }, entryToSync.id);
      
      // Log gamification
      const gamificationLogId = `meal_${entryToSync.id}`;
      await setDocWithQueue(doc(db, 'users', uid, 'gamification_logs', gamificationLogId), {
        log_date: dateStr,
        macros_hit: true,
        water_ml: 0,
        caffeine_mg: entryToSync.foods.reduce((sum, f) => sum + (Number(f.caffeine_mg) || 0), 0),
        scanned_barcodes: [],
        additives_flagged: [],
        engine_processed: false
      });
      
      // Clean up local storage to prevent infinite sync loops on reload
      localDb.removeItem(`pending_sync_${entryToSync.id}`);
      localStorage.removeItem(`pending_sync_${entryToSync.id}`);

      evaluateGamificationState(true);

      console.log('Entry synced to Firebase (or queued)');
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `users/${uid}/days/${dateStr}`);
    }
  };
  const syncWaterToFirebase = async (log: WaterLog, uid: string) => {
    if (isQuotaExceeded) return;
    if (!uid || !log || !log.id) return;

    const dateStr = formatDate(new Date(log.timestamp), 'YYYY-MM-DD');

    try {
      const otherWater = waterLogs.filter(w => w.id !== log.id);
      const updatedWater = [...otherWater, log];

      await syncDayMealsAndWaterToFirebase(dateStr, history, updatedWater, uid);
      
      // Sync to HealthKit / Google Fit in background
      queueHealthSync('WATER', {
        amount: log.amount_ml
      }, log.id);

      const nutrients = calculateWaterLogNutrients(log);
      const gamificationLogId = `water_${log.id}`;
      await setDocWithQueue(doc(db, 'users', uid, 'gamification_logs', gamificationLogId), {
        log_date: dateStr,
        macros_hit: false,
        water_ml: log.amount_ml,
        caffeine_mg: nutrients.caff || 0,
        scanned_barcodes: [],
        additives_flagged: [],
        engine_processed: false
      });
      
      // Clean up local storage to prevent infinite sync loops on reload
      localDb.removeItem(`pending_water_sync_${log.id}`);
      localStorage.removeItem(`pending_water_sync_${log.id}`);

      evaluateGamificationState(true);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `users/${uid}/days/${dateStr}`);
    }
  };
  const triggerMatch = () => {
    setShowMatchOverlay(true);
    sounds.playConfetti();
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#10b981', '#fb7185', '#ffffff']
    });
    
    setTimeout(() => {
      setShowMatchOverlay(false);
    }, 4000);
  };
  const syncSettings = async (settings: any) => {
    if (isQuotaExceeded) return;
    if (user) {
      try {
        await setDocWithQueue(doc(db, 'users', user.uid), {
          ...settings,
          lastUpdatedAt: Date.now()
        }, { merge: true });
      } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, `users/${user.uid}`);
      }
    }
  };
  const checkPermissions = async () => {
    if (typeof navigator === 'undefined' || !navigator.permissions) return;
    
    try {
      const camStatus = await navigator.permissions.query({ name: 'camera' as any });
      setCameraPermissionStatus(camStatus.state as any);
      camStatus.onchange = () => setCameraPermissionStatus(camStatus.state as any);

      const micStatus = await navigator.permissions.query({ name: 'microphone' as any });
      setMicPermissionStatus(micStatus.state as any);
      micStatus.onchange = () => setMicPermissionStatus(micStatus.state as any);
    } catch (err) {
      console.warn("Permissions API not fully supported:", err);
    }
  };
  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setCameraPermissionStatus('granted');
      toast.success("Camera access granted!");
    } catch (err) {
      setCameraPermissionStatus('denied');
      toast.error("Camera access denied.");
    }
  };
  const requestMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setMicPermissionStatus('granted');
      toast.success("Microphone access granted!");
    } catch (err) {
      setMicPermissionStatus('denied');
      toast.error("Microphone access denied.");
    }
  };
  const undoLastAction = (mealId?: string, waterId?: string) => {
    if (!mealId && !waterId) return;
    
    if (mealId) {
      let deletedItem: HistoryEntry | undefined;
      setHistory(prev => {
        deletedItem = prev.find(e => e.id === mealId);
        const updated = prev.filter(e => e.id !== mealId);
        if (user && deletedItem) {
          const dateStr = formatDate(new Date(deletedItem.timestamp), 'YYYY-MM-DD');
          syncDayMealsAndWaterToFirebase(dateStr, updated, waterLogs, user.uid).catch(err => {
            console.error("Failed to sync day meals after delete during undoLastAction:", err);
          });
        }
        return saveMealHistory(updated);
      });
    }
    
    if (waterId) {
      let deletedItem: WaterLog | undefined;
      setWaterLogs(prev => {
        deletedItem = prev.find(e => e.id === waterId);
        const updated = prev.filter(e => e.id !== waterId);
        localStorage.setItem('water_logs', JSON.stringify(safeClone(updated)));
        if (user && deletedItem) {
          const dateStr = formatDate(new Date(deletedItem.timestamp), 'YYYY-MM-DD');
          syncDayMealsAndWaterToFirebase(dateStr, history, updated, user.uid).catch(err => {
            console.error("Failed to sync day water after delete during undoLastAction:", err);
          });
        }
        return updated;
      });
    }
    
    toast.info(t.actionUndone || 'Action undone!', {
      icon: <RotateCcw className="w-5 h-5 text-zinc-500" />,
      className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold",
    });
  };
  const showUndoToast = (message: string, mealId?: string, waterId?: string) => {
    toast.success(
      <div className="flex items-center justify-between gap-4 w-full">
        <span>{message}</span>
        {(mealId || waterId) && (
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              undoLastAction(mealId, waterId);
              toast.dismiss();
            }}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center gap-1 shrink-0"
          >
            <RotateCcw className="w-3 h-3" />
            {t.undo || 'Undo'}
          </button>
        )}
      </div>,
      {
        icon: <AnimatedSuccessIcon />,
        className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold min-w-[300px]",
        duration: 5000,
      }
    );
  };
  const undoChatAction = () => {
    if (!lastChatUndoAction) return;

    const { type, messageIndex, mealId, waterId } = lastChatUndoAction;

    if (type === 'log') {
      undoLastAction(mealId, waterId);
      setChatMessages(prev => {
        const newMsgs = [...prev];
        if (newMsgs[messageIndex]) {
          newMsgs[messageIndex] = { ...newMsgs[messageIndex], logged: false };
        }
        return newMsgs;
      });
    } else if (type === 'message') {
      setChatMessages(prev => {
        const newMsgs = [...prev];
        // Remove both user and AI messages if possible
        if (messageIndex < newMsgs.length) {
          // If messageIndex is the AI message, and it's the last one
          if (messageIndex === newMsgs.length - 1 && newMsgs.length >= 2) {
            newMsgs.splice(newMsgs.length - 2, 2);
          } else {
            newMsgs.splice(messageIndex, 1);
          }
        }
        localStorage.setItem('chat_history', JSON.stringify(safeClone(newMsgs)));
        return newMsgs;
      });
    }

    setLastChatUndoAction(null);
  };
  const changeLang = (newLang: Language) => {
    setLang(newLang);
    setOnboardingLanguage(newLang);
    localStorage.setItem('app_lang', newLang);
    syncSettings({ language: newLang });
    sounds.playSave();
    toast.success((TRANSLATIONS[newLang][vibe === 'casual' ? 'bro' : vibe] as any).settingsSaved || 'Settings saved!', {
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
      className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold",
    });
  };
  const inferMealType = (foods: any[]): MealType => {
    const hour = new Date().getHours();
    const names = (foods || []).map(f => (f && f.name ? String(f.name) : '').toLowerCase()).join(' ');

    // Breakfast: 5am to 11am or breakfast foods
    if (hour >= 5 && hour < 11) return 'breakfast';
    if (/egg|pancake|waffle|cereal|oatmeal|toast|yogurt|muesli|croissant|breakfast/i.test(names)) return 'breakfast';

    // Lunch: 11am to 4pm or lunch foods
    if (hour >= 11 && hour < 16) return 'lunch';
    if (/sandwich|salad|wrap|burger|soup|taco|burrito|bowls/i.test(names)) return 'lunch';

    // Dinner: 4pm to 10pm or dinner foods
    if (hour >= 16 && hour < 23) return 'dinner';
    if (/steak|salmon|risotto|pasta|curry|roast|dinner/i.test(names)) return 'dinner';

    // Default to snack if it's very late/early or small portion
    return 'snack';
  };
  const generateMealName = (foods: any[]): string => {
    if (!foods || !Array.isArray(foods) || foods.length === 0) return '';
    
    // Sort foods by calories descending to get the most prominent items first
    const sortedFoods = [...foods].sort((a, b) => (Number(b?.calories_kcal || b?.calories) || 0) - (Number(a?.calories_kcal || a?.calories) || 0));
    
    const validFoods = sortedFoods.filter(f => f && typeof f.name === 'string' && f.name.trim() !== '');
    if (validFoods.length === 0) return '';
    if (validFoods.length === 1) return validFoods[0].name;

    const names = validFoods.map(f => f.name.trim());
    
    // Suggesting a concise name based on ingredients (top 2-3 prominent items)
    if (names.length === 2) {
      return lang.startsWith('ua') ? `${names[0]} та ${names[1]}` : `${names[0]} and ${names[1]}`;
    }
    
    const isBowl = names.slice(0, 3).some(n => /rice|quinoa|salad|bowl|grains/i.test(n));
    const suffix = isBowl ? (lang.startsWith('ua') ? ' Боул' : ' Bowl') : 
                   (lang.startsWith('ua') ? ' Тарілка' : ' Plate');

    if (names.length === 3) {
      return `${names[0]}, ${names[1]} & ${names[2]}${suffix}`;
    }

    // For 4+ items, take top 3 and add "with more" logic
    const base = `${names[0]}, ${names[1]} & ${names[2]}${suffix}`;
    const moreSuffix = lang.startsWith('ua') ? ' та інше' : ' with more';
    return `${base}${moreSuffix}`;
  };
  const cancelAnalysis = () => {
    setIsAnalyzing(false);
    setIsAnalyzingText(false);
    setError(null);
  };
  const getMacroData = (nutrition: any) => [
    { name: t.protein, value: nutrition.protein_g || 0, color: '#a855f7' },
    { name: t.carbs, value: nutrition.carbs_g || 0, color: '#ef4444' },
    { name: t.fat, value: nutrition.fat_g || 0, color: '#f59e0b' },
  ];
  const getPrimaryMacroBadge = (nutrition: any) => {
    if (!nutrition) return null;
    const protein = nutrition.protein_g || 0;
    const carbs = nutrition.carbs_g || 0;
    const fat = nutrition.fat_g || 0;

    const totalGrams = protein + carbs + fat;
    if (totalGrams === 0) return null;

    const proteinKcal = protein * 4;
    const carbsKcal = carbs * 4;
    const fatKcal = fat * 9;
    const totalKcal = proteinKcal + carbsKcal + fatKcal;

    if (protein >= 15 || (totalKcal > 0 && proteinKcal / totalKcal >= 0.3)) {
      return {
        label: lang === 'ua' ? 'Багато білка 💪' : 'High Protein 💪',
        class: 'bg-blue-500/10 text-blue-500 border-blue-500/20'
      };
    }
    if (carbs < 10 && fat >= 12 && totalKcal > 0 && fatKcal / totalKcal >= 0.5) {
      return {
        label: lang === 'ua' ? 'Кето / Низьковугл. 🥑' : 'Keto / Low Carb 🥑',
        class: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
      };
    }
    if (carbs >= 35 || (totalKcal > 0 && carbsKcal / totalKcal >= 0.55)) {
      return {
        label: lang === 'ua' ? 'Високовугл. / Енергія ⚡' : 'High Carb / Energy ⚡',
        class: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20'
      };
    }
    if (fat >= 15 || (totalKcal > 0 && fatKcal / totalKcal >= 0.45)) {
      return {
        label: lang === 'ua' ? 'Корисні жири 🥑' : 'Healthy Fats 🥑',
        class: 'bg-amber-500/10 text-amber-500 border-amber-500/20'
      };
    }

    const maxVal = Math.max(protein, carbs, fat);
    if (maxVal === protein && protein > 0) {
      return {
        label: lang === 'ua' ? 'Джерело білка 🍗' : 'Protein Source 🍗',
        class: 'bg-blue-500/10 text-blue-500 border-blue-500/20'
      };
    } else if (maxVal === carbs && carbs > 0) {
      return {
        label: lang === 'ua' ? 'Вуглеводи 🍏' : 'Carb Source 🍏',
        class: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20'
      };
    } else if (maxVal === fat && fat > 0) {
      return {
        label: lang === 'ua' ? 'Жири 🥑' : 'Fat Source 🥑',
        class: 'bg-amber-500/10 text-amber-500 border-amber-500/20'
      };
    }

    return null;
  };
  const getFoodEmoji = (foodName: string, category?: string): string => {
    const name = (foodName || '').toLowerCase().trim();

    // 1. Grains, Cereals, Legumes & Seeds
    if (/греч|buckwheat/i.test(name)) return '🌾';
    if (/нут|chickpea|garbanzo|хумус|hummus/i.test(name)) return '🧆';
    if (/квасол|фасол|боб|bean/i.test(name)) return '🫘';
    if (/сочевиц|чечевиц|горох| pea|pea |peas|lentil/i.test(name)) return '🫛';
    if (/соя|тофу|tofu|edamame/i.test(name)) return '🫛';
    if (/вівсян|овсян|каш|porridge|oatmeal|muesli|мюсл|гранол|granola|пластівц|корнфлекс/i.test(name)) return '🥣';
    if (/рис|risotto|різотто|rice/i.test(name)) return '🍚';
    if (/кіноа|кускус|булгур|пшон|перлов|круп|quinoa|couscous|bulgur|barley|millet/i.test(name)) return '🌾';
    if (/паст|макарон|локшин|спагет|вермішел|pasta|noodle|spaghetti|macaroni|ramen|рамен|лапш/i.test(name)) return '🍝';
    if (/кукурудз|полент|corn|polenta/i.test(name)) return '🌽';
    if (/хліб|тост|грінк|сухар|батон|булоч|пампушк|bread|toast|bun|baguette|багет/i.test(name)) return '🍞';
    if (/круасан|croissant/i.test(name)) return '🥐';
    if (/бублик|bagel/i.test(name)) return '🥯';
    if (/млинець|оладок|pancake|crepe|waffle|вафл/i.test(name)) return '🥞';

    // 2. Dairy, Cream, Cheese & Eggs
    if (/сметан|сливк|sour cream|heavy cream|whipped cream/i.test(name)) return '🥛';
    if (/сирник|кисломолочний|творог|бринз|фета|моцарел|пармезан|рикота|дорблу|сулугуні|сыр|сир|cheese|curd|cottage/i.test(name)) return '🧀';
    if (/йогурт|кефір|ряжанк|маслянк|yogurt|kefir/i.test(name)) return '🥣';
    if (/молок|молоч|milk/i.test(name)) return '🥛';
    if (/яйц|яєч|омлет|яйцо|пашот|egg|omelet|scrambled/i.test(name)) return '🍳';
    if (/вершкове.*масло|масло|ghee|butter/i.test(name)) return '🧈';

    // 3. Vegetables, Pickles & Greens
    if (/огір|огурец|cucumber|pickle/i.test(name)) return '🥒';
    if (/капуст|квашен|cabbage|sauerkraut|coleslaw|брюссел|кольраб/i.test(name)) return '🥬';
    if (/помідор|томат|чері|tomato/i.test(name)) return '🍅';
    if (/картоп|картоф|пюре|дран|potato|fries|картофель/i.test(name)) return '🥔';
    if (/моркв|морков|carrot/i.test(name)) return '🥕';
    if (/броккол|брокол|цвітн.*капуст|broccoli|cauliflower/i.test(name)) return '🥦';
    if (/салат|шпинат|рукол|зелень|петрушк|кріп|кроп|базилік|айсберг|lettuce|greens|spinach|arugula|salad|parsley|dill/i.test(name)) return '🥗';
    if (/гриб|печериц|глив|шампіньйон|mushroom/i.test(name)) return '🍄';
    if (/цибул|лук|onion|leek|shallot/i.test(name)) return '🧅';
    if (/часник|чеснок|garlic/i.test(name)) return '🧄';
    if (/перець|перц|паприк|pepper|capsicum|chili|чілі|чилі/i.test(name)) return '🌶️';
    if (/баклажан|кабачок|цукіні|патисон|eggplant|zucchini|squash/i.test(name)) return '🍆';
    if (/авокадо|гвакамол|avocado|guacamole/i.test(name)) return '🥑';
    if (/буряк|свекл|beet|beetroot/i.test(name)) return '🍠';
    if (/гарбуз|тыков|pumpkin/i.test(name)) return '🎃';
    if (/редис|редьк|radish/i.test(name)) return '🫚';
    if (/оливк|маслин|olive/i.test(name)) return '🫒';

    // 4. Meats & Poultry
    if (/кур|цыпл|індик|індіан|качк|перепел|chicken|turkey|poultry|duck/i.test(name)) return '🍗';
    if (/бекон|сало|шинк|ветчин|прошут|хамон|bacon|ham|lard|prosciutto/i.test(name)) return '🥓';
    if (/ковбас|сосис|сардел|салямі|паштет|sausage|salami|hot dog|frankfurter/i.test(name)) return '🌭';
    if (/ялович|говяд|свинин|телят|баранин|м'яс|мяс|стейк|котлет|фрикадельк|биток|фарш|гуляш|печінк|печен|beef|steak|pork|meat|meatball|cutlet|mince|liver|venison|lamb/i.test(name)) return '🥩';

    // 5. Seafood & Fish
    if (/креветк|shrimp|prawn/i.test(name)) return '🍤';
    if (/краб|кальмар|восьминіг|мідії|морепродук|омар|лобстер|crab|squid|octopus|mussels|seafood|lobster/i.test(name)) return '🦀';
    if (/риб|лосос|форель|тунець|тріск|оселед|сардин|скумбрі|судак|хек|митай|семг|семга|fish|salmon|tuna|trout|cod|herring|sardine/i.test(name)) return '🐟';

    // 6. Soups, Stews & Fast Food
    if (/борщ|суп|солян|розсольн|бульйон|рагу|чаудер|soup|stew|broth|chowder|ramen/i.test(name)) return '🥣';
    if (/піц|pizza/i.test(name)) return '🍕';
    if (/бургер|чизбургер|burger|hamburger/i.test(name)) return '🍔';
    if (/сендвіч|сэндвич|бутерброд|канапе|wrap|sandwich/i.test(name)) return '🥪';
    if (/тако|буріто|шаурма|шаверм|гірос|taco|burrito|shawarma|gyros/i.test(name)) return '🌯';
    if (/суші|суши|рол|sushi|roll/i.test(name)) return '🍣';
    if (/вареник|пельмен|хінкал|манти|дамплінг|dumpling|pierogi/i.test(name)) return '🥟';

    // 7. Fruits & Berries
    if (/яблук|яблок|apple/i.test(name)) return '🍎';
    if (/банан|banana/i.test(name)) return '🍌';
    if (/апельсин|мандарин|грейпфрут|цитрус|orange|tangerine|mandarin|grapefruit|citrus/i.test(name)) return '🍊';
    if (/полуниц|клубник|малин|чорниц|черник|ожин|ягод|ягід|смородин|лохин|strawberry|raspberry|blueberry|blackberry|berry|berries/i.test(name)) return '🍓';
    if (/виноград|ізюм|родзинк|grape|raisin/i.test(name)) return '🍇';
    if (/дин|дыня|кавун|арбуз|watermelon|melon/i.test(name)) return '🍉';
    if (/лимон|лайм|lemon|lime/i.test(name)) return '🍋';
    if (/ананас|pineapple/i.test(name)) return '🍍';
    if (/персик|абрикос|слив|вишн|черешн|peach|apricot|plum|cherry/i.test(name)) return '🍑';
    if (/груш|pear/i.test(name)) return '🍐';
    if (/ківі|kiwi/i.test(name)) return '🥝';

    // 8. Nuts, Seeds & Oils
    if (/горіх|орех|мигдаль|миндаль|арахіс|кеш'ю|фундук|фісташк|насін|семечк|кунжут|nut|almond|peanut|walnut|hazelnut|pistachio|seed|sesame|cashew/i.test(name)) return '🥜';
    if (/олій|olive oil|oil/i.test(name)) return '🫒';

    // 9. Desserts & Sweets
    if (/торт|тістеч|пиріг|пирожен|пирожное|випічк|бисквит|cake|pie|tart|pastry|muffin|мафін/i.test(name)) return '🍰';
    if (/печив|печенье|бісквіт|cookie|biscuit/i.test(name)) return '🍪';
    if (/шоколад|какао|брауні|chocolate|cocoa|brownie/i.test(name)) return '🍫';
    if (/морозив|сорбет|джелато|ice cream|gelato|sorbet/i.test(name)) return '🍦';
    if (/пончик|донат|donut|doughnut/i.test(name)) return '🍩';
    if (/цукерк|конфет|сладост|солодощ|candy|sweet/i.test(name)) return '🍬';
    if (/мед|honey/i.test(name)) return '🍯';
    if (/попкорн|popcorn/i.test(name)) return '🍿';

    // 10. Drinks & Beverages
    if (/вод|вода|water/i.test(name)) return '💧';
    if (/кав|кофе|еспресо|лате|капучино|американо|флет|coffee|espresso|latte|cappuccino/i.test(name)) return '☕';
    if (/чай|tea|matcha|матча/i.test(name)) return '🍵';
    if (/сік|сок|газ|шейк|смузі|кола|лимонад|компот|морс|juice|soda|smoothie|shake|lemonade|coca|pepsi/i.test(name)) return '🥤';
    if (/пиво|beer|ale|эль/i.test(name)) return '🍺';
    if (/вино|wine/i.test(name)) return '🍷';

    // Fallback to category if provided
    if (category) {
      const cat = category.toLowerCase();
      if (cat === 'meat') return '🥩';
      if (cat === 'vegetable') return '🥦';
      if (cat === 'fruit') return '🍎';
      if (cat === 'grain') return '🍞';
      if (cat === 'dairy') return '🧀';
      if (cat === 'drink') return '🥤';
      if (cat === 'snack') return '🥨';
      if (cat === 'dessert') return '🍰';
    }

    return '🍲';
  };
  const FOOD_CATEGORIES = [
    { id: 'meat', label: 'MEAT', icon: '🥩' },
    { id: 'vegetable', label: 'GREENS', icon: '🥦' },
    { id: 'fruit', label: 'FRUIT', icon: '🍎' },
    { id: 'grain', label: 'GRAIN', icon: '🍞' },
    { id: 'dairy', label: 'DAIRY', icon: '🧀' },
    { id: 'drink', label: 'DRINK', icon: '🥤' },
    { id: 'snack', label: 'SNACK', icon: '🥨' },
    { id: 'dessert', label: 'DESSERT', icon: '🍰' },
    { id: 'other', label: 'OTHER', icon: '🍴' },
  ] as const;
  const getFoodIcon = (food: { name: string, category?: string }) => {
    const emoji = getFoodEmoji(food.name, food.category);
    return <span className="text-xl">{emoji}</span>;
  };

  // ==========================================
  // 3. COMPUTED METRICS & DERIVED MEMOS / CALLBACKS
  // ==========================================
  const t: any = useMemo(() => {
    const rawT = {
      ...((TRANSLATIONS[lang] || TRANSLATIONS['en-US']).bro),
      ...((TRANSLATIONS[lang] || TRANSLATIONS['en-US'])[vibe === 'casual' ? 'bro' : vibe] || (TRANSLATIONS['en-US'].standard))
    };

    if (onboardingGender === 'female' && vibe === 'casual') {
      const isUa = lang === 'ua';
      const genderedT = { ...rawT };
      Object.keys(genderedT).forEach(key => {
        if (typeof genderedT[key] === 'string') {
          if (isUa) {
            genderedT[key] = genderedT[key]
              .replace(/бро/g, 'красуне')
              .replace(/Бро/g, 'Красуне')
              .replace(/друже/g, 'красуне')
              .replace(/братан/g, 'мала')
              .replace(/чувак/g, 'дівчино')
              .replace(/заправився/g, 'заправилась')
              .replace(/впевнений/g, 'впевнена')
              .replace(/з'їв/g, "з'їла")
              .replace(/записав/g, 'записала');
          } else {
            genderedT[key] = genderedT[key]
              .replace(/\bbro\b/g, 'girl')
              .replace(/\bBro\b/g, 'Girl')
              .replace(/\bhomie\b/g, 'bestie')
              .replace(/\bdawg\b/g, 'queen')
              .replace(/\bmate\b/g, 'hun')
              .replace(/\bman\b/g, 'babe');
          }
        }
      });
      return genderedT;
    }
    return rawT;
  }, [lang, vibe, onboardingGender]);
  const wrapUpStatus = useMemo<EveningWrapUpState>(() => {
    return getEveningWrapUpStatus(isEveningWrapUpTestEnabled);
  }, [isEveningWrapUpTestEnabled, wrapUpRefreshTick]);
  const effectiveUserEmail = useMemo(() => {
    return (
      user?.email ||
      auth.currentUser?.email ||
      user?.providerData?.find((p: any) => p?.email)?.email ||
      auth.currentUser?.providerData?.find((p: any) => p?.email)?.email ||
      userDocEmail ||
      getSession()?.email ||
      localStorage.getItem('user_email') ||
      ''
    );
  }, [user, userDocEmail]);

  const isAdmin = useMemo(() => isKimAdmin(effectiveUserEmail), [effectiveUserEmail]);
  const goals = useMemo(() => {
    const isWeekly = rawGoals.targetMode === 'weekly';
    return {
      ...rawGoals,
      calories: isWeekly ? Math.round(rawGoals.calories / 7) : rawGoals.calories,
      water_ml: isWeekly ? Math.round(rawGoals.water_ml / 7) : rawGoals.water_ml,
      protein: isWeekly ? Math.round(rawGoals.protein / 7) : rawGoals.protein,
      carbs: isWeekly ? Math.round(rawGoals.carbs / 7) : rawGoals.carbs,
      fat: isWeekly ? Math.round(rawGoals.fat / 7) : rawGoals.fat,
      sugar_g: isWeekly ? Math.round(rawGoals.sugar_g / 7) : rawGoals.sugar_g,
      fiber_g: isWeekly ? Math.round(rawGoals.fiber_g / 7) : rawGoals.fiber_g,
      sodium_mg: isWeekly ? Math.round(rawGoals.sodium_mg / 7) : rawGoals.sodium_mg,
      caffeine_mg: isWeekly && rawGoals.caffeine_mg ? Math.round(rawGoals.caffeine_mg / 7) : rawGoals.caffeine_mg,
    };
  }, [rawGoals]);
  const liveConfidence = useMemo(() => {
    if (analysisProgress === 0) return 0;
    if (analysisProgress < 15) {
      // Step 1: Scanning / Initializing
      return 10 + (analysisProgress / 15) * 15 + Math.sin(analysisProgress) * 2;
    } else if (analysisProgress < 45) {
      // Step 2: Detecting / Matching
      const pct = (analysisProgress - 15) / 30;
      return 25 + pct * 45 + Math.cos(analysisProgress * 1.5) * 4;
    } else if (analysisProgress < 75) {
      // Step 3: Calibrating / Database check
      const pct = (analysisProgress - 45) / 30;
      return 70 + pct * 20 + Math.sin(analysisProgress * 2) * 2;
    } else if (analysisProgress < 95) {
      // Step 4: Finalizing
      const pct = (analysisProgress - 75) / 20;
      return 90 + pct * 8 + Math.cos(analysisProgress * 3) * 0.5;
    }
    return 98.4;
  }, [analysisProgress]);
  const liveConfidenceStatus = useMemo(() => {
    if (lang === 'ua') {
      if (analysisProgress < 15) return "Отримання сигналу...";
      if (analysisProgress < 35) return "Пошук кандидатів страв...";
      if (analysisProgress < 60) return "Фільтрація хибних збігів...";
      if (analysisProgress < 85) return "Оптимізація меж сегментів...";
      return "Збіг зафіксовано!";
    } else {
      if (analysisProgress < 15) return "Visual Signal Lock...";
      if (analysisProgress < 35) return "Generating Match Candidates...";
      if (analysisProgress < 60) return "Filtering False Positives...";
      if (analysisProgress < 85) return "Optimizing Segment Boundaries...";
      return "High Certainty Locked-In!";
    }
  }, [analysisProgress, lang]);
  const activeDeficiencies = useMemo<DeficiencyAlert[]>(() => {
    if (history.length === 0) return [];

    const getLocalDateString = (timestamp: number) => {
      const d = new Date(timestamp);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const dailyIntake: { [dateStr: string]: { [nutrient: string]: number } } = {};
    
    // Change lookback span from 7 days to 30 days
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recentHistory = history.filter(meal => meal.timestamp >= thirtyDaysAgo);

    recentHistory.forEach(meal => {
      const dateStr = getLocalDateString(meal.timestamp);
      if (!dailyIntake[dateStr]) {
        dailyIntake[dateStr] = {
          potassium_mg: 0,
          calcium_mg: 0,
          iron_mg: 0,
          vitamin_c_mg: 0,
          vitamin_a_mcg: 0,
          vitamin_d_mcg: 0,
          vitamin_b12_mcg: 0,
          magnesium_mg: 0,
          zinc_mg: 0,
          protein_g: 0,
          carbs_g: 0,
          fat_g: 0
        };
      }
      const nutrition: Partial<TotalNutrition> = meal.total_nutrition || {};
      dailyIntake[dateStr].potassium_mg += Number(nutrition.potassium_mg) || 0;
      dailyIntake[dateStr].calcium_mg += Number(nutrition.calcium_mg) || 0;
      dailyIntake[dateStr].iron_mg += Number(nutrition.iron_mg) || 0;
      dailyIntake[dateStr].vitamin_c_mg += Number(nutrition.vitamin_c_mg) || 0;
      dailyIntake[dateStr].vitamin_a_mcg += Number(nutrition.vitamin_a_mcg) || 0;
      dailyIntake[dateStr].vitamin_d_mcg += Number(nutrition.vitamin_d_mcg) || 0;
      dailyIntake[dateStr].vitamin_b12_mcg += Number(nutrition.vitamin_b12_mcg) || 0;
      dailyIntake[dateStr].magnesium_mg += Number(nutrition.magnesium_mg) || 0;
      dailyIntake[dateStr].zinc_mg += Number(nutrition.zinc_mg) || 0;
      dailyIntake[dateStr].protein_g += Number(nutrition.protein_g) || 0;
      dailyIntake[dateStr].carbs_g += Number(nutrition.carbs_g) || 0;
      dailyIntake[dateStr].fat_g += Number(nutrition.fat_g) || 0;
    });

    const sortedDates = Object.keys(dailyIntake).sort();

    const isConsecutiveStr = (d1Str: string, d2Str: string) => {
      const d1 = new Date(d1Str + 'T00:00:00');
      const d2 = new Date(d2Str + 'T00:00:00');
      const diffTime = d2.getTime() - d1.getTime();
      return Math.round(diffTime / (1000 * 60 * 60 * 24)) === 1;
    };

    const alerts: DeficiencyAlert[] = [];
    const todayStr = getLocalDateString(Date.now());
    const yesterdayStr = getLocalDateString(Date.now() - 24 * 60 * 60 * 1000);

    const checkTargets = {
      ...MICRONUTRIENT_TARGETS,
      protein_g: { name: 'Protein', target: goals?.protein || 100, unit: 'g' },
      carbs_g: { name: 'Carbs', target: goals?.carbs || 250, unit: 'g' },
      fat_g: { name: 'Fats', target: goals?.fat || 70, unit: 'g' }
    };

    Object.entries(checkTargets).forEach(([key, spec]) => {
      let currentRun: string[] = [];
      let longestRun: string[] = [];

      sortedDates.forEach((dateStr) => {
        const intake = dailyIntake[dateStr][key];
        const percent = (intake / spec.target) * 100;

        if (percent < 80) {
          if (currentRun.length === 0) {
            currentRun.push(dateStr);
          } else {
            const lastDate = currentRun[currentRun.length - 1];
            if (isConsecutiveStr(lastDate, dateStr)) {
              currentRun.push(dateStr);
            } else {
              if (currentRun.length > longestRun.length) {
                longestRun = [...currentRun];
              }
              currentRun = [dateStr];
            }
          }
        } else {
          if (currentRun.length > longestRun.length) {
            longestRun = [...currentRun];
          }
          currentRun = [];
        }
      });

      if (currentRun.length > longestRun.length) {
        longestRun = [...currentRun];
      }

      // Check if the run of 3+ consecutive days is active (ends today or yesterday)
      const isRunActive = longestRun.length >= 3 && (longestRun[longestRun.length - 1] === todayStr || longestRun[longestRun.length - 1] === yesterdayStr);

      if (isRunActive) {
        const isMacro = key.endsWith('_g');
        const nutrientName = lang.startsWith('ua') 
          ? (key === 'protein_g' ? 'Білок' :
             key === 'carbs_g' ? 'Вуглеводи' :
             key === 'fat_g' ? 'Жири' :
             key === 'potassium_mg' ? 'Калій' :
             key === 'calcium_mg' ? 'Кальцій' :
             key === 'iron_mg' ? 'Залізо' :
             key === 'vitamin_c_mg' ? 'Вітамін C' :
             key === 'vitamin_a_mcg' ? 'Вітамін A' :
             key === 'vitamin_d_mcg' ? 'Вітамін D' :
             key === 'vitamin_b12_mcg' ? 'Вітамін B12' :
             key === 'magnesium_mg' ? 'Магній' : 'Цинк')
          : spec.name;

        const recommendedFoods = isMacro
          ? (lang.startsWith('ua') ? MACRO_FOODS[key].ua : MACRO_FOODS[key].en)
          : (lang.startsWith('ua') ? MICRONUTRIENT_FOODS[key].ua : MICRONUTRIENT_FOODS[key].en);

        const intakePercentages = longestRun.map(dStr => {
          const intake = dailyIntake[dStr][key];
          return Math.round((intake / spec.target) * 100);
        });

        alerts.push({
          nutrientKey: key,
          nutrientName,
          daysCount: longestRun.length,
          consecutiveDates: longestRun,
          intakePercentages,
          recommendedFoods,
          severity: 'warning',
          isMacro
        });
      }
    });

    return alerts;
  }, [history, lang, goals]);
// [t memo placed at top of Section 3]
  const chatThinkingSteps = useMemo(() => [
    t.noodling || 'Noodling...',
    t.sendingToAI || 'Sending to AI for wisdom...',
    t.analyzingText || 'Analyzing',
    t.daydreaming || 'Daydreaming...',
    t.analyzingNutrition || 'Analyzing nutrition values...',
    t.tripleChecking || 'Triple checking everything, bro...',
    t.calculatingFinalStats || 'Calculating final stats...',
    t.almostThere || 'Almost there, homie...',
    t.wrappingUp || 'Wrapping up now, homie...'
  ], [t]);
  const filteredAiSuggestions = useMemo(() => {
    if (!aiMealSuggestions || !Array.isArray(aiMealSuggestions)) return [];
    
    // Only filter for TODAY
    const todayStr = new Date().toDateString();
    const todayMeals = history.filter(m => new Date(m.timestamp).toDateString() === todayStr);

    const hasLoggedBreakfast = todayMeals.some(m => (m.meal_type || '').toLowerCase() === 'breakfast');
    const hasLoggedLunch = todayMeals.some(m => (m.meal_type || '').toLowerCase() === 'lunch');
    const hasLoggedDinner = todayMeals.some(m => (m.meal_type || '').toLowerCase() === 'dinner');

    return aiMealSuggestions
      .map(group => {
        const groupType = (group.meal_type || '').toLowerCase();
        const options = Array.isArray(group.options) ? group.options : [];
        
        // Hide breakfast suggestions once breakfast is logged today
        if (groupType === 'breakfast' && hasLoggedBreakfast) return null;
        // Hide lunch suggestions once lunch is logged today
        if (groupType === 'lunch' && hasLoggedLunch) return null;
        // Hide dinner suggestions once dinner is logged today
        if (groupType === 'dinner' && hasLoggedDinner) return null;

        // Snack recommendations remain all the time
        if (groupType === 'snack') {
          const unloggedSnackOptions = options.filter((opt: any) => {
            if (!opt || !opt.meal_name) return false;
            const isExactLogged = todayMeals.some(m => m.meal_name?.trim().toLowerCase() === opt.meal_name?.trim().toLowerCase());
            return !isExactLogged;
          });
          const finalSnackOptions = unloggedSnackOptions.length > 0 ? unloggedSnackOptions : options;
          return {
            ...group,
            meal_type: groupType,
            options: finalSnackOptions
          };
        }

        // For any other meal group, filter out already logged options
        const unloggedOptions = options.filter((opt: any) => {
          if (!opt || !opt.meal_name) return false;
          const isLogged = todayMeals.some(m => m.meal_name?.trim().toLowerCase() === opt.meal_name?.trim().toLowerCase());
          return !isLogged;
        });

        return { 
          ...group, 
          meal_type: groupType, 
          options: unloggedOptions 
        };
      })
      .filter((group): group is { meal_type: "breakfast" | "lunch" | "dinner" | "snack"; options: any[] } => 
        group !== null && Array.isArray(group.options) && group.options.length > 0
      );
  }, [aiMealSuggestions, history]);
  const quickAddItems = useMemo(() => {
    const counts: { [key: string]: { count: number; lastTimestamp: number; meal: HistoryEntry } } = {};
    const pastTwoDaysThreshold = Date.now() - 2 * 24 * 60 * 60 * 1000;
    
    history.forEach(entry => {
      if (!entry.meal_name) return;
      if (entry.timestamp < pastTwoDaysThreshold) return;
      const normalized = entry.meal_name.trim().toLowerCase();
      if (!counts[normalized]) {
        counts[normalized] = {
          count: 0,
          lastTimestamp: entry.timestamp,
          meal: entry
        };
      }
      counts[normalized].count += 1;
      if (entry.timestamp > counts[normalized].lastTimestamp) {
        counts[normalized].lastTimestamp = entry.timestamp;
        counts[normalized].meal = entry;
      }
    });

    return Object.values(counts)
      .sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        return b.lastTimestamp - a.lastTimestamp;
      })
      .slice(0, 5)
      .map(item => item.meal);
  }, [history]);
  const loggingSuggestions = useMemo(() => {
    const suggestionsMap = new Map<string, { text: string; type: 'food' | 'meal'; frequency: number; lastLogged: number }>();

    // Process history logs
    history.forEach((entry) => {
      const timestamp = entry.timestamp || Date.now();
      
      // Process meal name
      if (entry.meal_name && entry.meal_name.trim()) {
        const mealKey = entry.meal_name.trim().toLowerCase();
        const existing = suggestionsMap.get(mealKey);
        if (existing) {
          existing.frequency += 1;
          existing.lastLogged = Math.max(existing.lastLogged, timestamp);
        } else {
          suggestionsMap.set(mealKey, {
            text: entry.meal_name.trim(),
            type: 'meal',
            frequency: 1,
            lastLogged: timestamp,
          });
        }
      }

      // Process individual foods
      if (Array.isArray(entry.foods)) {
        entry.foods.forEach((food) => {
          if (food.name && food.name.trim()) {
            const foodKey = food.name.trim().toLowerCase();
            const existing = suggestionsMap.get(foodKey);
            if (existing) {
              existing.frequency += 1;
              existing.lastLogged = Math.max(existing.lastLogged, timestamp);
            } else {
              suggestionsMap.set(foodKey, {
                text: food.name.trim(),
                type: 'food',
                frequency: 1,
                lastLogged: timestamp,
              });
            }
          }
        });
      }
    });

    // Process favorites too
    favorites.forEach((fav) => {
      const timestamp = Date.now();
      
      if (fav.meal_name && fav.meal_name.trim()) {
        const mealKey = fav.meal_name.trim().toLowerCase();
        const existing = suggestionsMap.get(mealKey);
        if (existing) {
          existing.frequency += 2;
          existing.lastLogged = Math.max(existing.lastLogged, timestamp);
        } else {
          suggestionsMap.set(mealKey, {
            text: fav.meal_name.trim(),
            type: 'meal',
            frequency: 2,
            lastLogged: timestamp,
          });
        }
      }

      if (Array.isArray(fav.foods)) {
        fav.foods.forEach((food) => {
          if (food.name && food.name.trim()) {
            const foodKey = food.name.trim().toLowerCase();
            const existing = suggestionsMap.get(foodKey);
            if (existing) {
              existing.frequency += 2;
              existing.lastLogged = Math.max(existing.lastLogged, timestamp);
            } else {
              suggestionsMap.set(foodKey, {
                text: food.name.trim(),
                type: 'food',
                frequency: 2,
                lastLogged: timestamp,
              });
            }
          }
        });
      }
    });

    const now = Date.now();
    const suggestions = Array.from(suggestionsMap.values()).map(item => {
      const daysAgo = (now - item.lastLogged) / (1000 * 60 * 60 * 24);
      const recencyMultiplier = Math.max(0.1, 1 / (1 + daysAgo * 0.1));
      const score = item.frequency * recencyMultiplier;
      return { ...item, score };
    });

    return suggestions.sort((a, b) => b.score - a.score);
  }, [history, favorites]);
  const activeHistorySuggestions = useMemo(() => {
    if (!searchTerm) {
      return loggingSuggestions.slice(0, 6);
    }
    const lower = searchTerm.toLowerCase();
    return loggingSuggestions
      .filter((s) => s.text.toLowerCase().includes(lower))
      .slice(0, 6);
  }, [loggingSuggestions, searchTerm]);
  const activeFavoritesSuggestions = useMemo(() => {
    if (!favoritesSearchQuery) {
      return loggingSuggestions.slice(0, 6);
    }
    const lower = favoritesSearchQuery.toLowerCase();
    return loggingSuggestions
      .filter((s) => s.text.toLowerCase().includes(lower))
      .slice(0, 6);
  }, [loggingSuggestions, favoritesSearchQuery]);
  const availableBrandDrinks = useMemo(() => {
    const resDrinks = getDrinksForResidence(userResidence, isAge18Plus);
    const customAsBrand: DrinkBrandItem[] = customUserDrinks.map(c => ({
      id: c.id,
      name: c.name,
      brand: c.brand,
      category: 'other',
      regions: ['US', 'UK', 'UA', 'GLOBAL'],
      defaultVolumeMl: c.defaultVolumeMl,
      caloriesPer100ml: c.caloriesPer100ml,
      sugarPer100ml: c.sugarPer100ml,
      caffeinePer100ml: c.caffeinePer100ml,
      isEnergyDrink: c.isEnergyDrink,
      primaryColor: c.primaryColor || '#10b981',
      badgeBgColor: '#10b981',
      logoType: 'custom'
    }));
    return [...resDrinks, ...customAsBrand];
  }, [userResidence, isAge18Plus, customUserDrinks]);
  const activePersonalizedDrinks = useMemo(() => {
    return availableBrandDrinks.filter(d => selectedDrinkIds.includes(d.id));
  }, [availableBrandDrinks, selectedDrinkIds]);
  const filteredBrandDrinksForModal = useMemo(() => {
    return availableBrandDrinks.filter(drink => {
      if (selectedCategoryFilter !== 'all' && drink.category !== selectedCategoryFilter) {
        return false;
      }
      if (beverageSearchQuery.trim()) {
        const query = beverageSearchQuery.toLowerCase();
        return (
          drink.name.toLowerCase().includes(query) ||
          drink.brand.toLowerCase().includes(query) ||
          drink.category.toLowerCase().includes(query) ||
          (drink.descriptionEn || '').toLowerCase().includes(query) ||
          (drink.descriptionUa || '').toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [availableBrandDrinks, beverageSearchQuery, selectedCategoryFilter]);
  const displayedBrandDrinks = useMemo(() => {
    if (beverageSearchQuery.trim() || selectedCategoryFilter !== 'all') {
      return filteredBrandDrinksForModal;
    }
    return activePersonalizedDrinks.length > 0 ? activePersonalizedDrinks : availableBrandDrinks;
  }, [beverageSearchQuery, selectedCategoryFilter, filteredBrandDrinksForModal, activePersonalizedDrinks, availableBrandDrinks]);
  const selectedBrandDrink = useMemo(() => {
    const defaultDrink = activePersonalizedDrinks[0] || availableBrandDrinks[0] || null;
    if (!selectedBrandDrinkId) return defaultDrink;
    return availableBrandDrinks.find(d => d.id === selectedBrandDrinkId) || defaultDrink;
  }, [selectedBrandDrinkId, availableBrandDrinks, activePersonalizedDrinks]);
  const effectiveAvatarUrl = useMemo(() => {
    // 1. Explicit custom user upload
    if (!avatarImageError && customAvatar && customAvatar.trim() !== '') return customAvatar;
    
    // 2. Direct Google Auth user photo
    const directPhoto = user?.photoURL || auth.currentUser?.photoURL;
    if (!avatarImageError && directPhoto && directPhoto.trim() !== '') return directPhoto;

    // 3. Google provider profile photo from providerData
    const providerPhoto = user?.providerData?.find((p: any) => p?.photoURL)?.photoURL || 
                          auth.currentUser?.providerData?.find((p: any) => p?.photoURL)?.photoURL;
    if (!avatarImageError && providerPhoto && providerPhoto.trim() !== '') return providerPhoto;

    // 4. Local cached avatar string
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('custom_avatar') : null;
    if (!avatarImageError && stored && stored !== 'null' && stored !== 'undefined' && stored.trim() !== '') return stored;

    // 5. Fallbacks if avatarImageError was triggered on a temporary network issue
    if (customAvatar && customAvatar.startsWith('data:image')) return customAvatar;
    if (directPhoto) return directPhoto;
    if (providerPhoto) return providerPhoto;
    if (stored && stored !== 'null' && stored !== 'undefined' && stored.trim() !== '') return stored;

    return null;
  }, [customAvatar, avatarImageError, user?.photoURL, user?.providerData, auth.currentUser?.photoURL]);
  const effectiveGamificationLogsCount = useMemo(() => {
    return Math.max(history ? history.length : 0, gamificationTotalLogs);
  }, [history?.length, gamificationTotalLogs]);
  const effectiveGamificationTitle = useMemo(() => {
    if (gamificationTitleName) return gamificationTitleName;
    const isUA = (lang || '').startsWith('ua');
    const logs = effectiveGamificationLogsCount;
    if (logs >= 25) return isUA ? 'Апекс Архітектор' : 'Apex Architect';
    if (logs >= 10) return isUA ? 'Сигма Мезоморф' : 'Sigma Shredder';
    if (logs >= 3) return isUA ? 'Майстер Макросу' : 'Macro Manager';
    return isUA ? 'Початківець' : 'Initiate';
  }, [gamificationTitleName, effectiveGamificationLogsCount, lang]);
  const effectiveGamificationColor = useMemo(() => {
    if (gamificationNeonColor) return gamificationNeonColor;
    const logs = effectiveGamificationLogsCount;
    if (logs >= 25) return '#6366F1';
    if (logs >= 10) return '#8B5CF6';
    if (logs >= 3) return '#3B82F6';
    return '#10B981';
  }, [gamificationNeonColor, effectiveGamificationLogsCount]);
  const filteredHistory = useMemo(() => {
    let result = history;
    if (showFavoritesOnly) {
      result = result.filter(item => 
        (item.rating && item.rating > 0) || 
        favorites.some(f => 
          (isValidMealId(f.id) && f.id === item.id) ||
          (f.meal_name && item.meal_name && f.meal_name.trim().toLowerCase() === item.meal_name.trim().toLowerCase())
        )
      );
    }
    if (historyStartDate) {
      const startTime = new Date(historyStartDate + 'T00:00:00').getTime();
      result = result.filter(item => new Date(item.timestamp).getTime() >= startTime);
    }
    if (historyEndDate) {
      const endTime = new Date(historyEndDate + 'T23:59:59.999').getTime();
      result = result.filter(item => new Date(item.timestamp).getTime() <= endTime);
    }
    if (searchTerm) {
      const lowerSearch = (searchTerm || '').toLowerCase();
      result = result.filter(item => 
        (item.meal_name || '').toLowerCase().includes(lowerSearch) ||
        item.foods?.some(food => (food.name || '').toLowerCase().includes(lowerSearch))
      );
    }
    return result;
  }, [history, searchTerm, historyStartDate, historyEndDate, showFavoritesOnly, favorites]);
  const filteredWaterLogs = useMemo(() => {
    let result = waterLogs;
    if (historyStartDate) {
      const startTime = new Date(historyStartDate + 'T00:00:00').getTime();
      result = result.filter(log => new Date(log.timestamp).getTime() >= startTime);
    }
    if (historyEndDate) {
      const endTime = new Date(historyEndDate + 'T23:59:59.999').getTime();
      result = result.filter(log => new Date(log.timestamp).getTime() <= endTime);
    }
    if (!searchTerm) return result;
    const lowerSearch = (searchTerm || '').toLowerCase();
    const waterTerms = ['water', 'вода', 'drink', 'напій', 'ml', 'мл'];
    if (waterTerms.some(term => term.includes(lowerSearch) || lowerSearch.includes(term))) {
      return result;
    }
    return result.filter(log => log.amount_ml.toString().includes(lowerSearch));
  }, [waterLogs, searchTerm, historyStartDate, historyEndDate]);
  const filteredScannedProducts = useMemo(() => {
    if (!searchTerm) return scannedProducts;
    const lower = searchTerm.toLowerCase();
    return scannedProducts.filter(product => {
      return (product.name || '').toLowerCase().includes(lower) || 
             (product.barcode || '').includes(lower);
    });
  }, [scannedProducts, searchTerm]);
  const groupedHistory = useMemo(() => {
    const allEntries: CombinedEntry[] = [
      ...filteredHistory.map(h => ({ ...h, type: 'meal' as const })),
      ...filteredWaterLogs.map(w => ({ ...w, type: 'water' as const }))
    ];

    // Apply sorting to allEntries
    allEntries.sort((a, b) => {
      let comparison = 0;
      if (sortKey === 'date') {
        comparison = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      } else if (sortKey === 'calories') {
        const calA = a.type === 'meal' ? (a.total_nutrition?.calories_kcal || 0) : 0;
        const calB = b.type === 'meal' ? (b.total_nutrition?.calories_kcal || 0) : 0;
        comparison = calA - calB;
      } else if (sortKey === 'name') {
        const nameA = a.type === 'meal' ? a.meal_name : 'Water';
        const nameB = b.type === 'meal' ? b.meal_name : 'Water';
        comparison = nameA.localeCompare(nameB);
      } else if (sortKey === 'rating') {
        const ratingA = a.type === 'meal' ? (a.rating || (a.isFavorite ? 5 : 0)) : 0;
        const ratingB = b.type === 'meal' ? (b.rating || (b.isFavorite ? 5 : 0)) : 0;
        comparison = ratingA - ratingB;
      } else if (sortKey === 'tag') {
        let tagA = '';
        let tagB = '';
        
        if (tagSortMode === 'mealType') {
          tagA = a.type === 'meal' ? a.meal_type : 'Water';
          tagB = b.type === 'meal' ? b.meal_type : 'Water';
        } else {
          // Sort by first category tag detected for food items
          if (a.type === 'meal') {
            tagA = a.foods.find(f => f.category)?.category || a.meal_type;
          } else {
            tagA = 'drink';
          }
          
          if (b.type === 'meal') {
            tagB = b.foods.find(f => f.category)?.category || b.meal_type;
          } else {
            tagB = 'drink';
          }
        }
        comparison = tagA.localeCompare(tagB);
      }
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    if (sortKey !== 'date') {
      const overallTotals = allEntries.reduce((acc, item) => {
        if (item.type === 'meal') {
          acc.calories += item.total_nutrition?.calories_kcal || 0;
          acc.protein += item.total_nutrition?.protein_g || 0;
          acc.carbs += item.total_nutrition?.carbs_g || 0;
          acc.fat += item.total_nutrition?.fat_g || 0;
        } else {
          acc.water += item.amount_ml;
        }
        return acc;
      }, { calories: 0, protein: 0, carbs: 0, fat: 0, water: 0 });

      const header = lang.startsWith('en') ? 'Sorted Results' : 'Результати сортування';
      return [[header, { items: allEntries, totals: overallTotals }]];
    }

    const groups: { [key: string]: { items: CombinedEntry[], totals: any } } = {};
    
    allEntries.forEach(item => {
      const date = formatDate(new Date(item.timestamp), dateFormat, lang);
      if (!groups[date]) {
        groups[date] = { 
          items: [], 
          totals: { calories: 0, protein: 0, carbs: 0, fat: 0, water: 0 } 
        };
      }
      groups[date].items.push(item);
      if (item.type === 'meal') {
        groups[date].totals.calories += item.total_nutrition?.calories_kcal || 0;
        groups[date].totals.protein += item.total_nutrition?.protein_g || 0;
        groups[date].totals.carbs += item.total_nutrition?.carbs_g || 0;
        groups[date].totals.fat += item.total_nutrition?.fat_g || 0;
      } else {
        groups[date].totals.water += item.amount_ml;
      }
    });
    
    return Object.entries(groups).sort((a, b) => {
      const timeA = a[1].items[0]?.timestamp ? new Date(a[1].items[0].timestamp).getTime() : 0;
      const timeB = b[1].items[0]?.timestamp ? new Date(b[1].items[0].timestamp).getTime() : 0;
      return sortDirection === 'desc' ? timeB - timeA : timeA - timeB;
    });
  }, [filteredHistory, waterLogs, sortKey, tagSortMode, sortDirection, dateFormat, lang]);
  const onscreenMealItems = useMemo(() => {
    return groupedHistory.flatMap(([_, group]) => group.items).filter(item => item.type === 'meal');
  }, [groupedHistory]);
  const areAllMealItemsExpanded = useMemo(() => {
    if (onscreenMealItems.length === 0) return false;
    return onscreenMealItems.every(item => expandedMealLogIds.includes(item.id));
  }, [onscreenMealItems, expandedMealLogIds]);
  const groupedGallery = useMemo(() => {
    const groups: { [key: string]: { meals: HistoryEntry[]; totalCalories: number; totalWater: number } } = {};
    let meals = [...history];
    
    if (galleryMealTypeFilter !== 'all') {
      meals = meals.filter(m => m.meal_type === galleryMealTypeFilter);
    }

    if (galleryStartDate) {
      const start = new Date(galleryStartDate).getTime();
      meals = meals.filter(m => m.timestamp >= start);
    }

    if (galleryEndDate) {
      const end = new Date(galleryEndDate);
      end.setHours(23, 59, 59, 999);
      const endTime = end.getTime();
      meals = meals.filter(m => m.timestamp <= endTime);
    }

    meals.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    meals.forEach(item => {
      const date = formatDate(new Date(item.timestamp), dateFormat, lang);
      if (!groups[date]) {
        groups[date] = { meals: [], totalCalories: 0, totalWater: 0 };
      }
      groups[date].meals.push(item);
    });

    // Calculate daily status for each date in groups
    Object.keys(groups).forEach(dateStr => {
      const dailyMeals = history.filter(h => formatDate(new Date(h.timestamp), dateFormat, lang) === dateStr);
      const dailyWater = waterLogs.filter(w => formatDate(new Date(w.timestamp), dateFormat, lang) === dateStr);
      
      groups[dateStr].totalCalories = dailyMeals.reduce((sum, m) => sum + (m.total_nutrition?.calories_kcal || 0), 0);
      groups[dateStr].totalWater = dailyWater.reduce((sum, w) => sum + (w.amount_ml || 0), 0);
    });

    return Object.entries(groups).sort((a, b) => {
      return new Date(b[1].meals[0].timestamp).getTime() - new Date(a[1].meals[0].timestamp).getTime();
    });
  }, [history, waterLogs, dateFormat, galleryMealTypeFilter, galleryStartDate, galleryEndDate]);
  const todayTotals = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    const todayMeals = history.filter(item => {
      const itemDate = new Date(item.timestamp);
      return itemDate >= todayStart && itemDate <= todayEnd;
    });

    const todayWater = waterLogs.filter(log => {
      const logDate = new Date(log.timestamp);
      return logDate >= todayStart && logDate <= todayEnd;
    });

    const liquidNutrients = todayWater.reduce((acc, w) => {
      // If this water log was created from a meal, its calories/caffeine are already in todayMeals
      if (w.source_meal_id) return acc;
      const nutrients = calculateWaterLogNutrients(w);
      return { 
        cal: acc.cal + nutrients.cal, 
        caff: acc.caff + nutrients.caff,
        sugar: acc.sugar + nutrients.sugar
      };
    }, { cal: 0, caff: 0, sugar: 0 });

    return {
      calories: Math.round(todayMeals.reduce((sum, item) => sum + (item.total_nutrition?.calories_kcal || 0), 0) + liquidNutrients.cal),
      protein: Number(todayMeals.reduce((sum, item) => sum + (item.total_nutrition?.protein_g || 0), 0).toFixed(1)),
      carbs: Number(todayMeals.reduce((sum, item) => sum + (item.total_nutrition?.carbs_g || 0), 0).toFixed(1)),
      fat: Number(todayMeals.reduce((sum, item) => sum + (item.total_nutrition?.fat_g || 0), 0).toFixed(1)),
      fiber: Number(todayMeals.reduce((sum, item) => sum + (item.total_nutrition?.fiber_g || 0), 0).toFixed(1)),
      sugar: Number((todayMeals.reduce((sum, item) => sum + (item.total_nutrition?.sugar_g || 0), 0) + liquidNutrients.sugar).toFixed(1)),
      sodium: Math.round(todayMeals.reduce((sum, item) => sum + (item.total_nutrition?.sodium_mg || 0), 0)),
      caffeine: Math.round(todayMeals.reduce((sum, item) => sum + (item.total_nutrition?.caffeine_mg || 0), 0) + liquidNutrients.caff),
      water: Math.round(todayWater.reduce((sum, log) => sum + log.amount_ml, 0) + todayMeals.reduce((sum, item) => {
        // Only add liquid from meal if it didn't create a source_water_log already (to avoid double counting if UI adds it to both)
        // However, saveToHistory DOES create a waterLog, so we should be careful.
        // If saveToHistory created a waterLog, it is already in todayWater.
        // If it didn't (e.g. legacy entry or bug), we check the meal's water_ml.
        const hasLinkedWaterLog = todayWater.some(w => w.source_meal_id === item.id);
        if (hasLinkedWaterLog) return sum;
        return sum + (item.total_nutrition?.water_ml || 0);
      }, 0))
    };
  }, [history, waterLogs]);
  const selectedDayTotals = useMemo(() => {
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);
    
    const mealTotals = history
      .filter(item => {
        const itemDate = new Date(item.timestamp);
        return itemDate >= dayStart && itemDate <= dayEnd;
      })
      .reduce((acc, item) => {
        const nutrition = item.total_nutrition || { 
          calories_kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, 
          sugar_g: 0, fiber_g: 0, sodium_mg: 0, caffeine_mg: 0,
          potassium_mg: 0, cholesterol_mg: 0, vitamin_c_mg: 0, calcium_mg: 0, iron_mg: 0
        };

        return {
          calories: acc.calories + (nutrition.calories_kcal || 0),
          protein: acc.protein + (nutrition.protein_g || 0),
          carbs: acc.carbs + (nutrition.carbs_g || 0),
          fat: acc.fat + (nutrition.fat_g || 0),
          sugar: acc.sugar + (nutrition.sugar_g || 0),
          fiber: acc.fiber + (nutrition.fiber_g || 0),
          sodium: acc.sodium + (nutrition.sodium_mg || 0),
          caffeine: acc.caffeine + (nutrition.caffeine_mg || 0),
          water: acc.water + (nutrition.water_ml || 0)
        };
      }, { calories: 0, protein: 0, carbs: 0, fat: 0, sugar: 0, fiber: 0, sodium: 0, caffeine: 0, water: 0 });

    const waterEntries = waterLogs
      .filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= dayStart && logDate <= dayEnd;
      })
      .reduce((acc, log) => {
        // If this water log was created from a meal, we ALREADY counted the liquid in mealTotals (liquidFromMeal)
        // to avoid double counting, we only add calories/caffeine here if any, but NOT water if it's from a meal.
        if (log.source_meal_id) {
          return acc;
        }

        const nutrients = calculateWaterLogNutrients(log);

        return {
          water: acc.water + log.amount_ml,
          calories: acc.calories + nutrients.cal,
          caffeine: acc.caffeine + nutrients.caff,
          sugar: acc.sugar + nutrients.sugar
        };
      }, { water: 0, calories: 0, caffeine: 0, sugar: 0 });

    return { 
      ...mealTotals, 
      water: mealTotals.water + waterEntries.water,
      calories: mealTotals.calories + waterEntries.calories,
      caffeine: mealTotals.caffeine + waterEntries.caffeine,
      sugar: Number((mealTotals.sugar + waterEntries.sugar).toFixed(1))
    };
  }, [history, waterLogs, selectedDate]);
  const selectedDayWrapUpTotals = useMemo<TotalNutrition>(() => ({
    calories_kcal: selectedDayTotals.calories || 0,
    protein_g: selectedDayTotals.protein || 0,
    carbs_g: selectedDayTotals.carbs || 0,
    fat_g: selectedDayTotals.fat || 0,
    sugar_g: selectedDayTotals.sugar || 0,
    fiber_g: selectedDayTotals.fiber || 0,
    sodium_mg: selectedDayTotals.sodium || 0,
    water_ml: selectedDayTotals.water || 0,
    caffeine_mg: selectedDayTotals.caffeine || 0
  }), [selectedDayTotals]);
  const smartScalingSuggestions = useMemo(() => {
    if (!isAdjustPortionModalOpen) return [];
    const baseToUse = originalResult || selectedHistoryItem || result;
    if (!baseToUse) return [];

    const baseCals = baseToUse.total_nutrition?.calories_kcal || 0;
    if (baseCals <= 0) return [];

    const suggestions: Array<{
      id: string;
      titleEn: string;
      titleUa: string;
      subEn: string;
      subUa: string;
      multiplier: number;
      projectedCals: number;
      badgeEn: string;
      badgeUa: string;
      type: 'history_meal' | 'history_type' | 'goal_balance' | 'standard';
    }> = [];

    const mealNameLower = (baseToUse.meal_name || '').toLowerCase().trim();

    // 1. History match by meal name or food names
    const historyMatches = history.filter(h => {
      if (!h) return false;
      const hName = (h.meal_name || '').toLowerCase();
      if (mealNameLower && hName && (hName.includes(mealNameLower) || mealNameLower.includes(hName))) return true;
      if (baseToUse.foods && Array.isArray(baseToUse.foods) && h.foods && Array.isArray(h.foods)) {
        return baseToUse.foods.some(bf => 
          bf.name && h.foods.some(hf => hf.name && hf.name.toLowerCase().includes(bf.name.toLowerCase()))
        );
      }
      return false;
    });

    if (historyMatches.length > 0) {
      const totalMatchCals = historyMatches.reduce((sum, h) => sum + (h.total_nutrition?.calories_kcal || 0), 0);
      const avgMatchCals = Math.round(totalMatchCals / historyMatches.length);
      if (avgMatchCals > 0) {
        const mult = Number((avgMatchCals / baseCals).toFixed(2));
        if (mult >= 0.25 && mult <= 4.0) {
          suggestions.push({
            id: 'history_meal',
            titleEn: `Your Usual ${baseToUse.meal_name || 'Meal'}`,
            titleUa: `Ваша звична порція`,
            subEn: `Based on ${historyMatches.length} past log${historyMatches.length > 1 ? 's' : ''}`,
            subUa: `На основі ${historyMatches.length} записів`,
            multiplier: mult,
            projectedCals: avgMatchCals,
            badgeEn: 'Frequent',
            badgeUa: 'Часта порція',
            type: 'history_meal'
          });
        }
      }
    }

    // 2. History match by Meal Type
    if (baseToUse.meal_type) {
      const typeMatches = history.filter(h => h && h.meal_type && String(h.meal_type).toLowerCase() === String(baseToUse.meal_type).toLowerCase());
      if (typeMatches.length >= 2) {
        const totalTypeCals = typeMatches.reduce((sum, h) => sum + (h.total_nutrition?.calories_kcal || 0), 0);
        const avgTypeCals = Math.round(totalTypeCals / typeMatches.length);
        if (avgTypeCals > 0 && (!suggestions[0] || Math.abs(avgTypeCals - suggestions[0].projectedCals) > 35)) {
          const mult = Number((avgTypeCals / baseCals).toFixed(2));
          if (mult >= 0.25 && mult <= 4.0) {
            const mLower = String(baseToUse.meal_type).toLowerCase();
            const uaLabel = mLower === 'breakfast' ? 'Сніданок' : mLower === 'lunch' ? 'Обід' : mLower === 'dinner' ? 'Вечеря' : 'Перекус';
            suggestions.push({
              id: 'history_type',
              titleEn: `Average ${baseToUse.meal_type}`,
              titleUa: `Середній ${uaLabel}`,
              subEn: `Your avg intake (${typeMatches.length} logs)`,
              subUa: `Ваш середній прийом (${typeMatches.length} записів)`,
              multiplier: mult,
              projectedCals: avgTypeCals,
              badgeEn: 'Avg Habit',
              badgeUa: 'Звичка',
              type: 'history_type'
            });
          }
        }
      }
    }

    // 3. Goal Balance Fit
    const calorieGoal = goals.calories || 2000;
    const currentEaten = selectedDayTotals.calories || 0;
    const isHist = !!(selectedHistoryItem && selectedHistoryItem.id && selectedHistoryItem.id !== 'temp');
    const otherEaten = isHist ? Math.max(0, currentEaten - baseCals) : currentEaten;
    const remainingCals = Math.max(120, calorieGoal - otherEaten);

    if (remainingCals > 150 && remainingCals < calorieGoal * 1.5) {
      const goalMult = Number((remainingCals / baseCals).toFixed(2));
      if (goalMult >= 0.25 && goalMult <= 3.5 && !suggestions.some(s => Math.abs(s.projectedCals - remainingCals) < 30)) {
        suggestions.push({
          id: 'goal_balance',
          titleEn: 'Remaining Goal Fit',
          titleUa: 'Під залишок норми',
          subEn: `Fits remaining ${Math.round(remainingCals)} kcal`,
          subUa: `Заповнює залишок ${Math.round(remainingCals)} ккал`,
          multiplier: goalMult,
          projectedCals: Math.round(remainingCals),
          badgeEn: 'Target Fit',
          badgeUa: 'Точно в ціль',
          type: 'goal_balance'
        });
      }
    }

    // 4. Smart Hearty Serving fallback if suggestions < 2
    if (suggestions.length < 2) {
      const standardMult = 1.25;
      const stdCals = Math.round(baseCals * standardMult);
      if (!suggestions.some(s => Math.abs(s.multiplier - standardMult) < 0.08)) {
        suggestions.push({
          id: 'smart_hearty',
          titleEn: 'Hearty Serving (+25%)',
          titleUa: 'Ситна порція (+25%)',
          subEn: 'Smart scaling for higher satiety',
          subUa: 'Розумне збільшення порції',
          multiplier: standardMult,
          projectedCals: stdCals,
          badgeEn: 'Smart Suggestion',
          badgeUa: 'Розумна порада',
          type: 'standard'
        });
      }
    }

    return suggestions;
  }, [isAdjustPortionModalOpen, originalResult, selectedHistoryItem, result, history, goals.calories, selectedDayTotals.calories, lang]);
  const selectedDayCombined = useMemo(() => {
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);

    const meals = history.filter(item => {
      const itemDate = new Date(item.timestamp);
      return itemDate >= dayStart && itemDate <= dayEnd;
    }).map(item => ({ ...item, type: 'meal' as const }));

    const water = waterLogs.filter(log => {
      const logDate = new Date(log.timestamp);
      return logDate >= dayStart && logDate <= dayEnd;
    }).map(log => ({ ...log, type: 'water' as const }));

    return [...meals, ...water].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [history, waterLogs, selectedDate]);
  const selectedDayLogs = useMemo(() => {
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);
    
    return waterLogs.filter(log => {
      const logDate = new Date(log.timestamp);
      return logDate >= dayStart && logDate <= dayEnd;
    });
  }, [waterLogs, selectedDate]);
  const consecutiveDaysStreak = useMemo(() => {
    if (!history || history.length === 0) return 0;

    // Sort history by date descending
    const sortedHistory = [...history].sort((a, b) => 
      ensureTimestampMillis(b.timestamp) - ensureTimestampMillis(a.timestamp)
    );

    let streak = 0;
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    // Check if user completed goals on a given date
    const checkDayCompletion = (date: Date) => {
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      const startTime = dayStart.getTime();
      const endTime = dayEnd.getTime();

      const dayMeals = sortedHistory.filter(item => {
        const itemTime = ensureTimestampMillis(item.timestamp);
        return itemTime >= startTime && itemTime <= endTime;
      });

      const dayWaterLogs = (waterLogs || []).filter(log => {
        const logTime = ensureTimestampMillis(log.timestamp);
        return logTime >= startTime && logTime <= endTime;
      });

      const liquidCal = dayWaterLogs.reduce((sum, w) => {
        if (w.source_meal_id) return sum;
        const { cal } = calculateWaterLogNutrients(w);
        return sum + cal;
      }, 0);

      const dayCalories = dayMeals.reduce((sum, m) => sum + (m.total_nutrition?.calories_kcal || 0), 0) + liquidCal;
      return goals.calories > 0 && Math.round(dayCalories) >= Math.floor(goals.calories * 0.95);
    };

    // Start checking from today
    let dateToCheck = new Date(currentDate);
    const completedToday = checkDayCompletion(dateToCheck);
    
    if (!completedToday) {
      // If not completed today, check if yesterday was completed to continue streak
      dateToCheck.setDate(dateToCheck.getDate() - 1);
      if (!checkDayCompletion(dateToCheck)) return 0;
    }

    // Now count backwards
    while (checkDayCompletion(dateToCheck)) {
      streak++;
      dateToCheck.setDate(dateToCheck.getDate() - 1);
    }

    return streak;
  }, [history, goals, waterLogs]);
  const highestEarnedBadge = useMemo(() => {
    const earnedStreak = MASTERY_BADGES.filter(b => consecutiveDaysStreak >= b.days);
    if (earnedStreak.length > 0) {
      return earnedStreak[earnedStreak.length - 1];
    }
    const logs = history ? history.length : 0;
    if (logs >= 25) return { days: 0, icon: Sparkles, name: 'Apex Architect', color: 'text-indigo-500 dark:text-indigo-400', bg: 'animate-premium-gradient-bg', border: 'border-indigo-500/20 dark:border-purple-500/20' };
    if (logs >= 10) return { days: 0, icon: Medal, name: 'Sigma Shredder', color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' };
    if (logs >= 3) return { days: 0, icon: Award, name: 'Macro Manager', color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' };
    return { days: 0, icon: Trophy, name: 'Initiate', color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' };
  }, [consecutiveDaysStreak, history?.length]);
  const selectedDayStreaks = useMemo(() => {
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);
    const startTime = dayStart.getTime();
    const endTime = dayEnd.getTime();
    
    const dayMeals = (history || []).filter(item => {
      const itemTime = ensureTimestampMillis(item.timestamp);
      return itemTime >= startTime && itemTime <= endTime;
    });

    const isToday = new Date().toDateString() === selectedDate.toDateString();
    let currentMeal: MealType | null = null;
    if (isToday) {
      const hour = new Date().getHours();
      if (hour >= 5 && hour < 11) currentMeal = 'breakfast';
      else if (hour >= 11 && hour < 16) currentMeal = 'lunch';
      else if (hour >= 16 && hour < 22) currentMeal = 'dinner';
      else currentMeal = 'snack';
    }
    
    return {
      breakfast: dayMeals.some(m => m.meal_type === 'breakfast'),
      lunch: dayMeals.some(m => m.meal_type === 'lunch'),
      dinner: dayMeals.some(m => m.meal_type === 'dinner'),
      snack: dayMeals.some(m => m.meal_type === 'snack'),
      currentMeal,
      count: [
        dayMeals.some(m => m.meal_type === 'breakfast'),
        dayMeals.some(m => m.meal_type === 'lunch'),
        dayMeals.some(m => m.meal_type === 'dinner')
      ].filter(Boolean).length
    };
  }, [history, selectedDate]);
  const allGreetings = useMemo(() => {
    const isUa = lang === 'ua';
    const isFemale = onboardingGender === 'female';
    const nameToUse = (userName || '').trim() || (isUa ? (isFemale ? 'красуне' : 'бро') : (isFemale ? 'girl' : 'bro'));
    const capitalizedName = nameToUse.charAt(0).toUpperCase() + nameToUse.slice(1);
    
    const hour = new Date().getHours();
    let timeGreeting = '';
    
    if (isUa) {
      if (hour < 5) timeGreeting = 'Пора спати,';
      else if (hour < 12) timeGreeting = 'Доброго ранку,';
      else if (hour < 17) timeGreeting = 'Доброго дня,';
      else if (hour < 22) timeGreeting = 'Доброго вечора,';
      else timeGreeting = 'Доброї ночі,';
    } else {
      if (hour < 5) timeGreeting = 'Sleep tight,';
      else if (hour < 12) timeGreeting = 'Good morning,';
      else if (hour < 17) timeGreeting = 'Good afternoon,';
      else if (hour < 22) timeGreeting = 'Good evening,';
      else timeGreeting = 'Good night,';
    }

    const animeGreetings = isUa ? [
      { title: `З поверненням, ${capitalizedName}! ✨\nГотовий до нових квестів? 🍱`, subtitle: `Заряди свій рівень сили сьогодні!` },
      { title: `Плюс Ультра, ${nameToUse}! 🔥\nВийди за межі можливого!`, subtitle: `Корисні калорії — твоя суперсила ⚡` },
      { title: `Сугої, ${capitalizedName}! 🌟\nТвоя аура сьогодні непереможна!`, subtitle: `Час для відновлення мани та HP 🍜` },
      { title: `Ітадакімас, ${capitalizedName}! 🍱\nКорисний бенкет чекає на героя!`, subtitle: `Досягай нових рангів щодня` },
      { title: `${timeGreeting}\n${nameToUse}! 🌙✨`, subtitle: (hour >= 22 || hour < 4) ? `Час відпочинку для відновлення HP` : `Звіримо твій журнал квестів` }
    ] : [
      { title: `Okaeri, ${capitalizedName}! ✨\nReady for today's quest? 🍱`, subtitle: `Level up your power status today!` },
      { title: `Plus Ultra, ${nameToUse}! 🔥\nGo beyond your limits!`, subtitle: `Nutrient power is your ultimate skill ⚡` },
      { title: `Sugoi, ${capitalizedName}! 🌟\nYour power aura is radiant today!`, subtitle: `Time to restore HP and Mana 🍜` },
      { title: `Itadakimasu, ${capitalizedName}! 🍱\nA legendary feast awaits you!`, subtitle: `Keep climbing the Hunter ranks` },
      { title: `${timeGreeting}\n${nameToUse}! 🌙✨`, subtitle: (hour >= 22 || hour < 4) ? `Rest well to replenish your HP` : `Let's check your quest progress` }
    ];

    const uaCasualGreetings = [
      { title: isFemale ? `Залишайся сяючою, ${nameToUse}🧘` : `Залишайся сильним, ${nameToUse}🧘`, subtitle: `Твій прогрес вартий зусиль` },
      { title: isFemale ? `Ти просто неперевершена, ${nameToUse} 🦁` : `Ти просто машина, ${nameToUse} 🦁`, subtitle: `Заправляй тіло для перемог!` },
      { title: `${capitalizedName}, виглядаєш на всі сто⚡`, subtitle: `Твій вибір – твоя впевненість` },
      { title: isFemale ? `Привіт, ${nameToUse}!\nГотова до змін? 🏆` : `Здоров, ${nameToUse}!\nГотовий до змін? 🏆`, subtitle: `Успіх ховається у дрібницях` },
      { title: isFemale ? `Тільки позитив, ${nameToUse} ✨` : `Тільки позитив, ${nameToUse} ✨`, subtitle: isFemale ? `Будь зібраною та цілеспрямованою` : `Будь зібраним та цілеспрямованим` },
      { title: isFemale ? `Ах, чорт! Ми в справі, ${nameToUse} 🎮` : `Ах, чорт! Ми в справі, ${nameToUse} 🎮`, subtitle: `Час для чергової перемоги` },
      { title: `Ей, ${nameToUse}! На підзарядку? 🔋`, subtitle: `Твоє тіло скаже дякую` },
      { title: `Рада тебе бачити, ${capitalizedName}! 🚀`, subtitle: `Підірвемо цілі разом!` },
      { title: isFemale ? `Круто, що ти тут, ${nameToUse} 🤜` : `Круто, що ти тут, ${nameToUse} 🤜`, subtitle: `Кожна калорія на користь` },
      { title: `Ще один крок до мети, ${capitalizedName}! 🎯`, subtitle: `Кожна страва – шлях до мети` },
      { title: `${timeGreeting}\n${nameToUse}! 🌙`, subtitle: (hour >= 22 || hour < 5) ? `Час відпочинку` : `Звіримо твій прогрес` }
    ];

    const uaStandardGreetings = [
      { title: isFemale ? `Залишайся зосередженою,\n${nameToUse} 🧘` : `Залишайся зосередженим,\n${nameToUse} 🧘`, subtitle: `Ваш прогрес вартий зусиль` },
      { title: isFemale ? `Чудова робота,\nтак тримати, ${nameToUse} 🦁` : `Чудова робота,\nтак тримати, ${nameToUse} 🦁`, subtitle: `Точність та стабільність ведуть до результату` },
      { title: `${capitalizedName},\nчудовий вибір сьогодні ⚡`, subtitle: `Здорові звички формують результат` },
      { title: isFemale ? `Вітаємо, ${nameToUse}!\nГотові до нових цілей? 🏆` : `Вітаємо, ${nameToUse}!\nГотові до нових цілей? 🏆`, subtitle: `Кожен прийом їжі має значення` },
      { title: `Бажаємо енергійного дня,\n${nameToUse} ✨`, subtitle: `Фокус на збалансованому харчуванні` },
      { title: `Раді бачити вас знову,\n${capitalizedName}! 🍽️`, subtitle: `Час для корисного прийому їжі` },
      { title: `Привіт, ${nameToUse}!\nЧас для підзарядки? 🔋`, subtitle: `Поживні речовини для вашого організму` },
      { title: `Гарного дня,\n${nameToUse}! 🎯`, subtitle: `Досягаємо ваших цілей разом` },
      { title: `Стабільність — запорука успіху,\n${nameToUse} ✨`, subtitle: `Дотримання плану приносить результат` },
      { title: `Ще один крок до мети,\n${capitalizedName}! 🏗️`, subtitle: `Кожна страва – основа успіху` },
      { title: `${timeGreeting}\n${nameToUse}! 🌙`, subtitle: (hour >= 22 || hour < 5) ? `Час для відпочинку` : `Переглянемо ваш прогрес` }
    ];

    const mainGreetings = skin === 'anime' ? animeGreetings : isUa ? (vibe === 'casual' ? uaCasualGreetings : uaStandardGreetings) : vibe === 'casual' ? [
      { title: `Sup ${nameToUse}!\nReady to fuel up? ⚡️`, subtitle: `Treat your body right` },
      { title: `Yo ${capitalizedName}! Looking snack today🍎`, subtitle: `Stay glowing, log it all` },
      { title: isFemale ? `No doubt ${nameToUse}, you're doing great 🤜` : `No doubt ${nameToUse}, you're doing great 🤜`, subtitle: `Consistency is definitely key` },
      { title: `Finna crush those goals, ${nameToUse}? 🚀`, subtitle: `Every good meal is a win` },
      { title: `Ey yo, ${nameToUse}! ${hour < 12 ? 'Wakey wakey!' : 'Still grinding?'} ☕`, subtitle: `Moving the needle today` },
      { title: isFemale ? `Stay locked in, ${nameToUse} 🧘` : `Stay locked in, ${nameToUse} 🧘`, subtitle: `Clarity starts on the plate` },
      { title: isFemale ? `You're a beast, ${nameToUse}🦁` : `You're a beast, ${nameToUse}🦁`, subtitle: `Queens track their macros daily` },
      { title: isFemale ? `Ah, sh@t!\nHere we go again, ${nameToUse} 🎮` : `Ah, sh@t!\nHere we go again, ${nameToUse} 🎮`, subtitle: `Time for a victory!` }
    ] : [
      { title: `Stay strong\nand focused, ${nameToUse} 🧘`, subtitle: `Progress is worth the effort` },
      { title: `Excellent work,\nkeep it up, ${nameToUse} 🦁`, subtitle: `Precision leads to greatness` },
      { title: `${capitalizedName},\nlooking sharp today ⚡`, subtitle: `Healthy choices build confidence` },
      { title: isFemale ? `Hello, ${nameToUse}!\nReady for better? 🏆` : `Hello, ${nameToUse}!\nReady for better? 🏆`, subtitle: `Small wins, big changes` },
      { title: `Positive energy\nyour way, ${nameToUse} ✨`, subtitle: `Stay focused and stable` },
      { title: `Great to see you back,\n${capitalizedName}! 🍽️`, subtitle: `Time for a success meal` },
      { title: `Hey, ${nameToUse}!\nBack for a power-up? 🔋`, subtitle: `Recharge with nutrients` },
      { title: `Good to have you back,\n${nameToUse}! 🎯`, subtitle: `Let's reach your goals` },
      { title: `Consistency is key,\n${nameToUse} ✨`, subtitle: `Healthy choices show respect` },
      { title: `One closer to goals,\n${capitalizedName}! 🏗️`, subtitle: `Meal is a building block` },
      { title: `${timeGreeting}\n${nameToUse}! 🌙`, subtitle: (hour >= 22 || hour < 5) ? `Time for some rest` : `Let's review progress` }
    ];

    const all = [...mainGreetings];
    
    // Add special ones at context-relevant positions
    const streakDays = [3, 7, 14, 21, 28];
    if (streakDays.includes(consecutiveDaysStreak)) {
      const streakGreeting = { 
        title: isUa 
          ? (userName ? `Айо, ${nameToUse}! Твій стрік – це вогонь 🔥` : "Айо, красуне! Твій стрік – це вогонь 🔥") 
          : (userName ? `Ayo, ${nameToUse}! Your streak is fire 🔥` : "Ayo, homie! Your streak is fire 🔥"), 
        subtitle: isUa ? "Ти просто найкраща!" : "Absolutely crushing it!"
      };
      all.unshift(streakGreeting);
    }
    
    if (selectedDayTotals.calories > goals.calories * 0.7) {
      const progressGreeting = isUa ? (vibe === 'casual' ? {
        title: selectedDayTotals.calories > goals.calories ? "Оу! План перевиконано 🔥" : "Ти впевнено йдеш до мети! 📈",
        subtitle: selectedDayTotals.calories > goals.calories ? (isFemale ? `Ти сьогодні справжня красуня, ${nameToUse}` : `Ти сьогодні справжня машина, ${nameToUse}`) : (isFemale ? `Ще ${Math.round(goals.calories - selectedDayTotals.calories)} ккал, і ти в шоколаді` : `${Math.round(goals.calories - selectedDayTotals.calories)} ккал залишилось`)
      } : {
        title: selectedDayTotals.calories > goals.calories ? "Денну норму виконано 🎯" : "Стабільний рух до мети 📈",
        subtitle: selectedDayTotals.calories > goals.calories ? `Чудовий баланс раціону сьогодні, ${nameToUse}` : `Залишилось ${Math.round(goals.calories - selectedDayTotals.calories)} ккал до денної цілі`
      }) : vibe === 'casual' ? {
        title: selectedDayTotals.calories > goals.calories ? "Gains incoming! 📈" : "Lowkey killing it ✨",
        subtitle: selectedDayTotals.calories > goals.calories ? `Bulking season is officially here, ${nameToUse}` : `${Math.round(goals.calories - selectedDayTotals.calories)} kcal to go, easy money`
      } : {
        title: selectedDayTotals.calories > goals.calories ? "Efficiency at 100%! 🚀" : "Consistent & Steady 🎯",
        subtitle: selectedDayTotals.calories > goals.calories ? `You fueled well today, ${nameToUse}` : `${Math.round((selectedDayTotals.calories / goals.calories) * 100)}% through daily goal`
      };
      all.unshift(progressGreeting);
    }
    
    if (skin === 'stalker') {
      return isUa ? [
        { title: `З поверненням до багаття, ${userName ? userName : 'Сталкере'}.`, subtitle: `СИНХРОНІЗАЦІЯ ПДА.` }
      ] : [
        { title: `Welcome back to the Campfire, ${userName ? userName : 'Stalker'}.`, subtitle: `Syncing PDA.` }
      ];
    }
    
    return all;
  }, [consecutiveDaysStreak, userName, refreshCount, lang, selectedDayTotals, goals, vibe, onboardingGender, skin]);
  const userStatus = useMemo(() => {
    const lastLogDate = history.length > 0 ? Math.max(...history.map(h => h.timestamp)) : 0;
    const daysSinceLastLog = lastLogDate > 0 ? (Date.now() - lastLogDate) / (1000 * 60 * 60 * 24) : Infinity;
    
    if (skin === 'anime') {
      return {
        id: daysSinceLastLog >= 1 ? 'idle' : 'active',
        text: daysSinceLastLog >= 1 ? (lang.startsWith('en') ? '『RESTING HERO 🍙』' : '『ГЕРОЙ НА ВІДПОЧИНКУ 🍙』') : (lang.startsWith('en') ? '『QUEST ACTIVE ✨』' : '『КВЕСТ АКТИВНИЙ ✨』'),
        colorClass: daysSinceLastLog >= 1 ? 'text-amber-300 drop-shadow-[0_0_6px_rgba(251,191,36,0.6)]' : 'text-pink-300 drop-shadow-[0_0_6px_rgba(244,114,182,0.7)]',
        bgClass: daysSinceLastLog >= 1 ? 'bg-amber-500/15 shadow-[0_0_12px_rgba(251,191,36,0.25)]' : 'bg-pink-500/15 shadow-[0_0_12px_rgba(236,72,153,0.3)]',
        borderClass: daysSinceLastLog >= 1 ? 'border-amber-400/40' : 'border-pink-400/40',
        icon: daysSinceLastLog >= 1 ? <Sparkles className="w-3.5 h-3.5 text-amber-300" /> : <Flame className="w-3.5 h-3.5 text-pink-400 animate-bounce" />
      };
    }

    if (daysSinceLastLog >= 1) {
      return { 
        id: 'idle',
        text: lang.startsWith('en') ? 'IDLE' : 'ЛІНИВИЙ', 
        colorClass: 'text-orange-500', 
        bgClass: 'bg-orange-500/10', 
        borderClass: 'border-orange-500/20',
        icon: <CircleDot className="w-3 h-3 text-orange-500" />
      };
    } else if (consecutiveDaysStreak >= 3) {
      return { 
        id: 'crushin_it',
        text: lang.startsWith('en') ? "CRUSHIN' IT" : "В УДАРІ", 
        colorClass: 'text-emerald-500', 
        bgClass: 'bg-emerald-500/10', 
        borderClass: 'border-emerald-500/20',
        icon: <Flame className="w-3 h-3 text-emerald-500" />
      };
    } else {
      return { 
        id: 'active',
        text: lang.startsWith('en') ? 'ACTIVE' : 'АКТИВНИЙ', 
        colorClass: 'text-emerald-500', 
        bgClass: 'bg-emerald-500/10', 
        borderClass: 'border-emerald-500/20',
        icon: <TrendingUp className="w-3 h-3 text-emerald-500" />
      };
    }
  }, [history, consecutiveDaysStreak, lang, skin]);
  const handleFirestoreErrorLocal = useCallback((error: unknown, operationType: OperationType, path: string | null) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isQuotaExhausted = isFirestoreQuotaExhaustedError(error);
    if (isQuotaExhausted) {
      isQuotaExceeded = true;
      setIsQuotaExceededState(true);
      console.warn(`Firestore quota exceeded on [${operationType}] ${path || 'unknown'}:`, errorMessage);
      const isUa = (localStorage.getItem('app_lang') || '').startsWith('ua');
      toast.error(
        isUa
          ? '⚠️ Денний ліміт бази даних вичерпано. Додаток перейшов у локальний режим (Local Only Mode).'
          : '⚠️ Firestore daily quota exceeded. Application switched to Local Only Mode.',
        {
          id: 'quota-exceeded-toast',
          duration: Infinity,
          className: 'rounded-2xl border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950 text-red-900 dark:text-red-100 font-bold',
        }
      );
      return; 
    }
    
    // Friendly user visible warning for rules or connection problems
    toast.error(`Database Error [${operationType.toUpperCase()} on ${path || 'path'}]: ${errorMessage.substring(0, 150)}`);
    handleFirestoreError(error, operationType, path);
  }, []);
  const computeOnboardingTargets = useCallback(() => {
    const weight = parseFloat(onboardingWeight) || 70;
    const height = parseFloat(onboardingHeight) || 170;
    const age = parseFloat(onboardingAge) || 25;
    const s = onboardingGender === 'male' ? 5 : -161;
    
    const bmr = (10 * weight) + (6.25 * height) - (5 * age) + s;
    const tdee = bmr * onboardingActivity;
    
    let targetCalories = tdee;
    if (onboardingGoal === 'lose') targetCalories -= 500;
    if (onboardingGoal === 'gain') targetCalories += 500;
    
    targetCalories = Math.round(targetCalories);
    
    let pRatio = 0.3;
    let cRatio = 0.4;
    let fRatio = 0.3;

    if (onboardingGoal === 'lose') {
      pRatio = 0.4;
      cRatio = 0.3;
      fRatio = 0.3;
    } else if (onboardingGoal === 'gain') {
      pRatio = 0.25;
      cRatio = 0.5;
      fRatio = 0.25;
    }

    const p = Math.round((targetCalories * pRatio) / 4);
    const c = Math.round((targetCalories * cRatio) / 4);
    const f = Math.round((targetCalories * fRatio) / 9);

    return {
      calories: targetCalories,
      protein: p,
      carbs: c,
      fat: f,
      water: Math.round(weight * 33),
      sugar: Math.round((targetCalories * 0.1) / 4),
      sodium: 2300,
    };
  }, [onboardingWeight, onboardingHeight, onboardingAge, onboardingGender, onboardingActivity, onboardingGoal]);
  const loadData = useCallback(async (forceCloud = false) => {
    let allHistory: HistoryEntry[] = [];
    
    // 1. Load from localDb
    let savedHistory = localDb.getItemSync('meal_history');

    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        if (Array.isArray(parsed)) {
          allHistory = [...parsed];
        }
      } catch (e) {
        console.warn("Failed to parse local history", e);
      }
    }

    const savedGoals = localDb.getItemSync('user_goals');
    if (savedGoals) {
      try {
        const parsed = JSON.parse(savedGoals);
        if (parsed && parsed.calories > 0) {
          setGoals(parsed);
        }
      } catch (e) {
        console.warn("Failed to parse local goals", e);
      }
    }

    let finalWaterLogs: WaterLog[] = [];
    const localWaterLogsStr = localDb.getItemSync('water_logs');
    if (localWaterLogsStr) {
      try {
        const localWaterLogs = JSON.parse(localWaterLogsStr);
        if (Array.isArray(localWaterLogs)) {
          finalWaterLogs = localWaterLogs;
        }
      } catch (e) {
        console.warn("Failed to parse local water logs", e);
      }
    }

    const candidateUser = user || (typeof localStorage !== 'undefined' ? {
      email: localStorage.getItem('user_email') || '',
      uid: localStorage.getItem('user_uid') || ''
    } : null);

    // Automatic historical archive restoration check (seeding 293 meals, 127 water logs, goals, metrics)
    if (isEligibleForRestoration(candidateUser)) {
      try {
        const restoreRes = await restoreUserDataIfEligible(candidateUser, allHistory.length, allHistory, finalWaterLogs);
        if (restoreRes.restored && restoreRes.data) {
          const applied = applyRestoredUserData(restoreRes.data, allHistory, finalWaterLogs);
          allHistory = applied.mergedHistory;
          finalWaterLogs = applied.mergedWaterLogs;
          if (applied.goals) setGoals(applied.goals);
          if (applied.userName) setUserName(applied.userName);
          if (applied.favorites) setFavorites(applied.favorites);
          if (applied.scannedProducts) setScannedProducts(applied.scannedProducts);
        }
      } catch (restoreErr) {
        console.warn("[DATA-RESTORE] Automatic restoration error in loadData:", restoreErr);
      }
    }

    if (user) {
      const now = Date.now();
      const lastFetch = Number(localDb.getItemSync(`last_fetch_${user.uid}`) || 0);
      const cooldown = 60 * 1000; // 1 minute cooldown for cloud fetch

      if ((forceCloud || (now - lastFetch > cooldown) || allHistory.length === 0) && !isQuotaExceeded) {
        if (typeof window !== 'undefined' && navigator.onLine && !isQuotaExceeded) {
          window.dispatchEvent(new CustomEvent('syncing-started', { detail: { isLaunch: true } }));
        }
        try {
          // 1. Fetch days collection (daily meals and water logs)
          try {
            const daysQ = query(collection(db, 'users', user.uid, 'days'), orderBy('date', 'desc'), limit(30));
            const daysSnapshot = await getDocs(daysQ);
            daysSnapshot.docs.forEach(docSnap => {
              const d = docSnap.data();
              if (Array.isArray(d.history)) {
                d.history.forEach((m: any) => {
                  if (m && m.id) {
                    allHistory.push({
                      ...m,
                      timestamp: ensureTimestampMillis(m.timestamp)
                    });
                  }
                });
              }
              if (Array.isArray(d.water)) {
                d.water.forEach((w: any) => {
                  if (w && w.id) {
                    finalWaterLogs.push({
                      ...w,
                      timestamp: ensureTimestampMillis(w.timestamp)
                    });
                  }
                });
              }
            });
          } catch (daysErr) {
            console.warn("Days collection fetch note:", daysErr);
          }

          // 2. Fetch legacy history and water collections to ensure no unmigrated past meals are missed
          try {
            const historyQ = query(collection(db, 'users', user.uid, 'history'), orderBy('timestamp', 'desc'), limit(100));
            const historySnapshot = await getDocs(historyQ);
            historySnapshot.docs.forEach(docSnap => {
              const data = docSnap.data() as HistoryEntry;
              if (data && docSnap.id) {
                allHistory.push({
                  ...data,
                  id: docSnap.id,
                  timestamp: ensureTimestampMillis(data.timestamp)
                });
              }
            });
          } catch (histErr) {
            console.warn("Legacy history collection fetch note:", histErr);
          }

          try {
            const waterQ = query(collection(db, 'users', user.uid, 'water'), orderBy('timestamp', 'desc'), limit(100));
            const waterSnapshot = await getDocs(waterQ);
            waterSnapshot.docs.forEach(docSnap => {
              const data = docSnap.data() as WaterLog;
              if (data && docSnap.id) {
                finalWaterLogs.push({
                  ...data,
                  id: docSnap.id,
                  timestamp: ensureTimestampMillis(data.timestamp)
                });
              }
            });
          } catch (waterErr) {
            console.warn("Legacy water collection fetch note:", waterErr);
          }

          // Fetch scanned products from subcollection
          let firebaseScannedProducts: ScannedProduct[] = [];
          try {
            const scannedCollection = collection(db, 'users', user.uid, 'scanned_products');
            const scannedQuery = query(scannedCollection, orderBy('timestamp', 'desc'), limit(30));
            const scannedSnapshot = await getDocs(scannedQuery);
            firebaseScannedProducts = scannedSnapshot.docs.map(doc => {
              const d = doc.data() as ScannedProduct;
              return {
                ...d,
                timestamp: ensureTimestampMillis(d.timestamp)
              };
            });
          } catch (scannedError) {
            console.error("Failed to fetch scanned products from Firebase:", scannedError);
            if (isFirestoreQuotaExhaustedError(scannedError)) {
              isQuotaExceeded = true;
              setIsQuotaExceededState(true);
              toast.error('Daily database limit reached. Falling back to local storage.', {
                id: 'quota-error',
                className: 'rounded-2xl border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950 text-red-900 dark:text-red-100 font-bold',
              });
            }
          }
          
          try {
            const localScannedProductsStr = localDb.getItemSync('scanned_products');
            let finalScannedProducts: ScannedProduct[] = [];
            if (localScannedProductsStr) {
              const parsed = JSON.parse(localScannedProductsStr);
              if (Array.isArray(parsed)) {
                finalScannedProducts = parsed.map(p => ({
                   ...p,
                   timestamp: ensureTimestampMillis(p.timestamp)
                }));
              }
            }
            
            const uniqueScannedMap = new Map<string, ScannedProduct>();
            [...firebaseScannedProducts, ...finalScannedProducts].forEach(p => {
              if (p && p.barcode) {
                const normalizedP = { ...p, timestamp: ensureTimestampMillis(p.timestamp) };
                uniqueScannedMap.set(normalizedP.barcode, normalizedP);
              }
            });
            const combinedScanned = Array.from(uniqueScannedMap.values()).sort((a,b) => b.timestamp - a.timestamp);
            setScannedProducts(combinedScanned);
            localDb.setItem('scanned_products', JSON.stringify(safeClone(combinedScanned)));
          } catch (scannedLocalError) {
            console.error("Failed to load local scanned products fallback:", scannedLocalError);
          }
          
          localDb.setItem(`last_fetch_${user.uid}`, String(Date.now()));
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('sync-success'));
          }
        } catch (e) {
          handleFirestoreErrorLocal(e, OperationType.LIST, `users/${user.uid}/history`);
        } finally {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('syncing-ended'));
          }
        }
      }
      
      // 4. Load pending syncs from localDb
      const pendingSyncs: HistoryEntry[] = [];
      const pendingSyncEntries = localDb.scanKeysWithPrefixSync('pending_sync_');
      for (const item of pendingSyncEntries) {
        try {
          const entry = JSON.parse(item.value || '{}');
          if (entry.id) {
            pendingSyncs.push(entry);
          }
        } catch(e) {}
      }
      allHistory = [...allHistory, ...pendingSyncs];

      // Also handle water pending syncs
      const pendingWaterSyncs: WaterLog[] = [];
      const pendingWaterEntries = localDb.scanKeysWithPrefixSync('pending_water_sync_');
      for (const item of pendingWaterEntries) {
        try {
          const entry = JSON.parse(item.value || '{}');
          if (entry.id) {
            pendingWaterSyncs.push(entry);
          }
        } catch(e) {}
      }
      if (pendingWaterSyncs.length > 0) {
        const uniqueWaterMap = new Map<string, WaterLog>();
        [...finalWaterLogs, ...pendingWaterSyncs].forEach(log => uniqueWaterMap.set(log.id, log));
        finalWaterLogs = Array.from(uniqueWaterMap.values());
        setWaterLogs(finalWaterLogs);
      }
    }

    // Deduplicate by ID and sort by timestamp descending (preserving photos across sources)
    const uniqueHistoryMap = new Map<string, HistoryEntry>();
    allHistory.forEach(entry => {
      if (entry && entry.id) {
        const existing = uniqueHistoryMap.get(entry.id);
        const resolvedImage = (entry.image && entry.image.trim() !== '')
          ? entry.image
          : (existing?.image || '');
        uniqueHistoryMap.set(entry.id, {
          ...(existing || {}),
          ...entry,
          image: resolvedImage,
          timestamp: ensureTimestampMillis(entry.timestamp)
        });
      }
    });
    
    const finalHistory = Array.from(uniqueHistoryMap.values()).sort((a, b) => b.timestamp - a.timestamp);
    
    // Migration: Add caffeine_mg and water_ml to meals in the last 24h if missing
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    let historyChanged = false;
    let newWaterLogsAdded = false;
    const currentWaterLogs = [...finalWaterLogs];

    const migratedHistory = finalHistory.map(entry => {
      const isRecent = now - entry.timestamp <= twentyFourHours;
      if (isRecent && entry.total_nutrition && (entry.total_nutrition.caffeine_mg === undefined || (entry.total_nutrition.water_ml === undefined && entry.foods.some(f => f.category === 'drink' || /coffee|tea|water|stick/i.test(f.name))))) {
        historyChanged = true;
        
        let caffeine = entry.total_nutrition.caffeine_mg ?? 0;
        let water = entry.total_nutrition.water_ml ?? 0;
        
        const name = (entry.meal_name || '').toLowerCase();
        const foodsLower = entry.foods.map(f => (f.name || '').toLowerCase()).join(' ');
        const fullLower = `${name} ${foodsLower}`;

        const hasCoffee = /coffee|espresso|latte|cappuccino|americano|кава|еспресо|лате/i.test(fullLower);
        const hasTea = /tea|matcha|chai|чай|матча/i.test(fullLower);
        const hasEnergy = /energy|monster|red bull|burn|hell|pit bull|non stop|reign|rockstar|енерг/i.test(fullLower);
        const hasCola = /pepsi|cola|coke|dr pepper|mountain dew|пепсі|кола|кока/i.test(fullLower);
        
        if (caffeine === 0) {
          if (hasCoffee) caffeine = 80;
          else if (hasEnergy) caffeine = 120;
          else if (hasTea) caffeine = 35;
          else if (hasCola) caffeine = 34;
        }

        if (water === 0) {
          // Look at foods to sum up water
          water = entry.foods.reduce((sum, f) => {
            if (f.water_ml) return sum + f.water_ml;
            if (f.category === 'drink' || /water|coffee|tea|beverage|stick/i.test(f.name)) {
              if (f.unit === 'ml' || f.unit === 'g') return sum + (Number(f.amount) || 0);
              if (/stick|bag/i.test(f.name)) return sum + (Number(f.amount) || 1) * 250;
              if (f.unit === 'pcs') return sum + 250;
            }
            return sum;
          }, 0);
        }
        
        // If we found water and this meal doesn't have a corresponding water log today, add one
        if (water > 0) {
          const hasLog = currentWaterLogs.some(log => Math.abs(log.timestamp - entry.timestamp) < 5000);
          if (!hasLog) {
            currentWaterLogs.push({
              id: Math.random().toString(36).substring(2, 11),
              amount_ml: Math.round(water),
              timestamp: entry.timestamp - 1,
              source_meal_id: entry.id
            });
            newWaterLogsAdded = true;
          }
        }

        let updatedEntry: any = {
          ...entry,
          total_nutrition: { ...entry.total_nutrition, caffeine_mg: caffeine, water_ml: water },
          foods: entry.foods.map(f => ({ 
            ...f, 
            caffeine_mg: f.caffeine_mg ?? (f.category === 'drink' ? caffeine : 0),
            water_ml: f.water_ml ?? (f.category === 'drink' ? (Number(f.amount) || 0) : 0)
          }))
        };

        // Micronutrient Backfill & Auto-enrichment:
        // Ensure meals with missing/zero micronutrients (like Pâté, Rye Bread, Broth, meats, etc.) are enriched
        const entryTn = updatedEntry.total_nutrition || ({} as any);
        const microValues = [
          entryTn.potassium_mg, entryTn.cholesterol_mg, entryTn.vitamin_c_mg, entryTn.calcium_mg,
          entryTn.iron_mg, entryTn.vitamin_a_mcg, entryTn.vitamin_d_mcg, entryTn.vitamin_b12_mcg,
          entryTn.magnesium_mg, entryTn.zinc_mg
        ];
        const hasZeroMicros = microValues.filter(v => typeof v === 'number' && v > 0).length === 0;
        const entryNameLower = (updatedEntry.meal_name || '').toLowerCase();
        const isTargetPateCanapes = (
          entryNameLower.includes('rye') || entryNameLower.includes('canap') ||
          entryNameLower.includes('канап') || entryNameLower.includes('паштет') ||
          entryNameLower.includes('pâté') || entryNameLower.includes('pate')
        );

        if (hasZeroMicros || (isTargetPateCanapes && (!entryTn.vitamin_b12_mcg || entryTn.vitamin_b12_mcg < 1.0))) {
          historyChanged = true;
          updatedEntry = enrichMealMicronutrients(safeClone(updatedEntry)) as HistoryEntry;
        }

        return updatedEntry;
      }

      // If caffeine didn't need backfill, still check if micronutrients need enrichment
      let finalEntry = entry;
      const entryTn = finalEntry.total_nutrition || ({} as any);
      const microValues = [
        entryTn.potassium_mg, entryTn.cholesterol_mg, entryTn.vitamin_c_mg, entryTn.calcium_mg,
        entryTn.iron_mg, entryTn.vitamin_a_mcg, entryTn.vitamin_d_mcg, entryTn.vitamin_b12_mcg,
        entryTn.magnesium_mg, entryTn.zinc_mg
      ];
      const hasZeroMicros = microValues.filter(v => typeof v === 'number' && v > 0).length === 0;
      const entryNameLower = (finalEntry.meal_name || '').toLowerCase();
      const isTargetPateCanapes = (
        entryNameLower.includes('rye') || entryNameLower.includes('canap') ||
        entryNameLower.includes('канап') || entryNameLower.includes('паштет') ||
        entryNameLower.includes('pâté') || entryNameLower.includes('pate')
      );

      if (hasZeroMicros || (isTargetPateCanapes && (!entryTn.vitamin_b12_mcg || entryTn.vitamin_b12_mcg < 1.0))) {
        historyChanged = true;
        finalEntry = enrichMealMicronutrients(safeClone(finalEntry)) as HistoryEntry;
      }

      return finalEntry;
    });
    // Consolidate and persist all water logs
    const uniqueWaterMap = new Map<string, WaterLog>();
    (newWaterLogsAdded ? currentWaterLogs : finalWaterLogs).forEach(w => {
      if (w && w.id) {
        uniqueWaterMap.set(w.id, {
          ...w,
          timestamp: ensureTimestampMillis(w.timestamp)
        });
      }
    });
    const consolidatedWaterLogs = Array.from(uniqueWaterMap.values()).sort((a, b) => b.timestamp - a.timestamp);
    setWaterLogs(consolidatedWaterLogs);
    localDb.setItem('water_logs', JSON.stringify(safeClone(consolidatedWaterLogs)));

    const historyToUse = historyChanged ? migratedHistory : finalHistory;
    const saved = saveMealHistory(historyToUse);
    setHistory(saved);

    if (user && !isQuotaExceeded) {
      if (historyChanged) {
        // Only sync today's meals if meals in the last 24h were modified; never flood historical meals
        const recentCutoff = Date.now() - 24 * 60 * 60 * 1000;
        const hasRecentChange = migratedHistory.some(item => item && item.timestamp >= recentCutoff);
        if (hasRecentChange) {
          const todayStr = formatDate(new Date(), 'YYYY-MM-DD');
          syncDayMealsAndWaterToFirebase(todayStr, historyToUse, currentWaterLogs, user.uid);
        }
      } else if (forceCloud) {
        // Collect all distinct dates from local history and water to push any unsynced offline days
        const uniqueDates = new Set<string>();
        historyToUse.forEach(item => {
          if (item && item.timestamp) {
            uniqueDates.add(formatDate(new Date(item.timestamp), 'YYYY-MM-DD'));
          }
        });
        currentWaterLogs.forEach(w => {
          if (w && w.timestamp) {
            uniqueDates.add(formatDate(new Date(w.timestamp), 'YYYY-MM-DD'));
          }
        });
        const recentCutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const todayStr = formatDate(new Date(), 'YYYY-MM-DD');
        uniqueDates.forEach(dateStr => {
          const dayMeals = historyToUse.filter(h => formatDate(new Date(h.timestamp), 'YYYY-MM-DD') === dateStr);
          const hasRecent = dayMeals.some(m => m.timestamp >= recentCutoff);
          if (hasRecent || dateStr === todayStr) {
            syncDayMealsAndWaterToFirebase(dateStr, historyToUse, currentWaterLogs, user.uid);
          }
        });
      }
    }

    // Background migration: sync any unsynced entries to the new subcollection
    if (user && finalHistory.length > 0 && !isQuotaExceeded) {
      // Collect keys first from localDb
      const pendingKeys = localDb.scanKeysWithPrefixSync('pending_sync_').map(item => item.key);
      
      // Limit background sync to 10 items at a time to avoid hitting quota too fast
      const syncLimit = 10;
      const keysToSync = pendingKeys.slice(0, syncLimit);
      
      keysToSync.forEach(key => {
        try {
          const entry = JSON.parse(localDb.getItemSync(key) || '{}');
          if (entry.id) {
            syncHistoryToFirebase(entry, user.uid);
          }
        } catch (e) {
          console.warn('Failed to parse pending sync entry', e);
        }
      });

      // Also sync pending water logs
      const pendingWaterKeys = localDb.scanKeysWithPrefixSync('pending_water_sync_').map(item => item.key);
      pendingWaterKeys.slice(0, 10).forEach(key => {
        try {
          const entry = JSON.parse(localDb.getItemSync(key) || '{}');
          if (entry.id) {
            syncWaterToFirebase(entry, user.uid);
          }
        } catch (e) {
          console.warn('Failed to parse pending water sync entry', e);
        }
      });
    }

    // One-time automatic migration of legacy Firestore documents to the unified day-based schema
    if (user && !isQuotaExceeded) {
      const migrationFlag = `migrated_to_days_v1_${user.uid}`;
      if (!localStorage.getItem(migrationFlag)) {
        (async () => {
          try {
            console.log("Starting legacy Firestore schema migration...");
            
            const historyCollection = collection(db, 'users', user.uid, 'history');
            const historySnap = await getDocs(historyCollection);
            const legacyMeals = historySnap.docs.map(docSnap => ({
              ...docSnap.data() as HistoryEntry,
              timestamp: ensureTimestampMillis((docSnap.data() as any).timestamp)
            }));

            const waterCollection = collection(db, 'users', user.uid, 'water');
            const waterSnap = await getDocs(waterCollection);
            const legacyWater = waterSnap.docs.map(docSnap => ({
              ...docSnap.data() as WaterLog,
              timestamp: ensureTimestampMillis((docSnap.data() as any).timestamp)
            }));

            if (legacyMeals.length > 0 || legacyWater.length > 0) {
              console.log(`Migrating ${legacyMeals.length} meals and ${legacyWater.length} water logs to day-based schema...`);
              
              const daysMap = new Map<string, { meals: HistoryEntry[], water: WaterLog[] }>();
              
              legacyMeals.forEach(meal => {
                const dateStr = formatDate(new Date(meal.timestamp), 'YYYY-MM-DD');
                if (!daysMap.has(dateStr)) daysMap.set(dateStr, { meals: [], water: [] });
                daysMap.get(dateStr)!.meals.push(meal);
              });

              legacyWater.forEach(water => {
                const dateStr = formatDate(new Date(water.timestamp), 'YYYY-MM-DD');
                if (!daysMap.has(dateStr)) daysMap.set(dateStr, { meals: [], water: [] });
                daysMap.get(dateStr)!.water.push(water);
              });

              for (const [dateStr, dayData] of Array.from(daysMap.entries())) {
                const dayDocRef = doc(db, 'users', user.uid, 'days', dateStr);
                dayData.meals.sort((a, b) => b.timestamp - a.timestamp);
                dayData.water.sort((a, b) => b.timestamp - a.timestamp);

                await setDocWithQueue(dayDocRef, {
                  date: dateStr,
                  history: dayData.meals,
                  water: dayData.water
                });
              }

              const docsToDelete = [...historySnap.docs.map(d => d.ref), ...waterSnap.docs.map(d => d.ref)];
              await batchDeleteDocsWithQueue(docsToDelete);
              
              console.log("Migration to day-based schema completed successfully!");
            } else {
              console.log("No legacy data found. Schema is clean.");
            }

            localStorage.setItem(migrationFlag, 'true');
          } catch (error) {
            console.error("Error migrating legacy documents to days schema:", error);
          }
        })();
      }
    }

    setIsRefreshing(false);
    setRefreshCount(prev => prev + 1);
  }, [user]);

  useEffect(() => {
    const handleOnlineReconnect = () => {
      if (user && navigator.onLine && !isQuotaExceededState) {
        processOfflineQueue();
      }
    };
    window.addEventListener('online', handleOnlineReconnect);
    return () => {
      window.removeEventListener('online', handleOnlineReconnect);
    };
  }, [user, isQuotaExceededState]);
  const getRecalcSteps = useCallback(() => {
    const steps = [
      t.secFocusing || "Sec, figuring it out... focusing...",
      t.countingMacros || "Counting the macros..."
    ];
    if (pendingReviewData?.isSuggestion || !pendingReviewData?.image) {
      steps.push(t.generatingVisual || "Crafting the visual...");
    }
    steps.push(
      t.bringingTogether || "Bringing it all together...",
      t.almostReady || "Almost ready..."
    );
    return steps;
  }, [t, pendingReviewData]);
  const fetchGoogleFitData = useCallback(async () => {
    if (!user) return;
    try {
      setGoogleFitLoading(true);
      const res = await fetchWrapper(`/api/health-sync/data?lang=${lang}`);
      if (res.ok) {
        const resData = await safeJsonResponse(res);
        if (resData.connected && resData.data) {
          setGoogleFitData(resData.data);
        } else {
          setGoogleFitData([]);
        }
      }
    } catch (err) {
      console.warn("Failed to fetch Google Fit data:", err);
    } finally {
      setGoogleFitLoading(false);
    }
  }, [user, lang]);
  const handleCollapseAllSpoilers = useCallback(() => {
    setIsGoalsExpanded(false);
    setIsPersonalDataExpanded(false);
    setIsPersonalizationExpanded(false);
    setIsDrinksBrandsExpanded(false);
    setIsDietaryPlanExpanded(false);
    setIsPermissionsExpanded(false);
    setIsLegalExpanded(false);
    setIsAdditionalExpanded(false);
    setIsSubscriptionExpanded(false);
  }, []);
  const handleRateMeal = useCallback((mealId: string, rating: number) => {
    if (!mealId) return;

    setHistory(prevHistory => {
      const updatedHistory = prevHistory.map(item => {
        if (item.id === mealId) {
          const isFav = rating > 0 ? true : item.isFavorite;
          const updatedItem = { ...item, rating, isFavorite: isFav };
          if (user) {
            syncHistoryToFirebase(updatedItem, user.uid);
          }
          return updatedItem;
        }
        return item;
      });
      saveMealHistory(updatedHistory);
      return updatedHistory;
    });

    setSelectedHistoryItem(prev => {
      if (prev && prev.id === mealId) {
        const isFav = rating > 0 ? true : prev.isFavorite;
        return { ...prev, rating, isFavorite: isFav };
      }
      return prev;
    });

    setResult(prev => {
      if (prev && prev.id === mealId) {
        const isFav = rating > 0 ? true : prev.isFavorite;
        return { ...prev, rating, isFavorite: isFav };
      }
      return prev;
    });

    setFavorites(prevFavs => {
      const targetMeal = history.find(m => m.id === mealId) || (selectedHistoryItem?.id === mealId ? selectedHistoryItem : null);
      if (!targetMeal) return prevFavs;

      const isFav = rating > 0 ? true : targetMeal.isFavorite;
      const updatedMeal = { ...targetMeal, rating, isFavorite: isFav };
      const exists = prevFavs.some(f => isValidMealId(f.id) && f.id === mealId);

      let nextFavs: HistoryEntry[];
      if (rating > 0) {
        if (exists) {
          nextFavs = prevFavs.map(f => (isValidMealId(f.id) && f.id === mealId) ? updatedMeal : f);
        } else {
          nextFavs = [updatedMeal, ...prevFavs];
        }
      } else {
        nextFavs = prevFavs.filter(f => f.id !== mealId);
      }
      const jsonStr = JSON.stringify(safeClone(nextFavs));
      localStorage.setItem('favorites', jsonStr);
      localDb.setItem('favorites', jsonStr);
      if (user) {
        setDocWithQueue(doc(db, 'users', user.uid), { favorites: safeClone(nextFavs) }, { merge: true })
          .catch(err => console.warn('Failed to sync favorites to Firestore:', err));
      }
      return nextFavs;
    });

    sounds.playPop();
    triggerHaptic('SUCCESS');

    if (rating > 0) {
      toast.success(
        lang.startsWith('ua')
          ? `Оцінено на ${rating} ★! Збережено в улюблені`
          : `Rated ${rating} ★! Saved to favorites`
      );
    } else {
      toast.success(lang.startsWith('ua') ? 'Оцінку скинуто' : 'Rating cleared');
    }
  }, [history, selectedHistoryItem, user, lang]);
  const handleChatPaste = useCallback((e: React.ClipboardEvent | ClipboardEvent) => {
    const clipboardData = (e as any).clipboardData || (window as any).clipboardData;
    if (!clipboardData) return;

    const items = clipboardData.items;
    const files = clipboardData.files;

    // Check items first (direct clipboard image data from screenshots or copied images)
    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            const reader = new FileReader();
            reader.onloadend = () => {
              if (reader.result) {
                setChatImage(reader.result as string);
                sounds.playPop();
                triggerHaptic('SUCCESS');
                toast.success(
                  lang === 'ua'
                    ? 'Зображення страви вставлено з буфера обміну (Ctrl+V) 📋'
                    : 'Meal image pasted from clipboard (Ctrl+V) 📋',
                  { icon: '🍽️' }
                );
              }
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      }
    }

    // Check files array fallback (e.g. copied image file from file explorer)
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type && file.type.startsWith('image/')) {
          e.preventDefault();
          const reader = new FileReader();
          reader.onloadend = () => {
            if (reader.result) {
              setChatImage(reader.result as string);
              sounds.playPop();
              triggerHaptic('SUCCESS');
              toast.success(
                lang === 'ua'
                  ? 'Зображення страви вставлено з буфера обміну (Ctrl+V) 📋'
                  : 'Meal image pasted from clipboard (Ctrl+V) 📋',
                { icon: '🍽️' }
              );
            }
          };
          reader.readAsDataURL(file);
          return;
        }
      }
    }
  }, [lang]);
  const handleAddGroceryItem = useCallback(async (name: string, amount?: string, unit?: string, source_meal_id?: string) => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) 
      ? crypto.randomUUID() 
      : Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    
    const newItem: GroceryItem = {
      id,
      name,
      amount,
      unit,
      checked: false,
      source_meal_id,
      timestamp: Date.now()
    };

    const updatedItems = [newItem, ...groceryItems];
    setGroceryItems(updatedItems);
    localStorage.setItem('grocery_items', JSON.stringify(safeClone(updatedItems)));

    if (user) {
      try {
        await setDocWithQueue(doc(db, 'users', user.uid, 'grocery_list', id), safeClone(newItem));
      } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, `users/${user.uid}/grocery_list/${id}`);
      }
    }
  }, [groceryItems, user]);
  const handleToggleGroceryItem = useCallback(async (id: string) => {
    const item = groceryItems.find(i => i.id === id);
    if (!item) return;

    const updatedItem = { ...item, checked: !item.checked };
    const updatedItems = groceryItems.map(i => i.id === id ? updatedItem : i);
    setGroceryItems(updatedItems);
    localStorage.setItem('grocery_items', JSON.stringify(safeClone(updatedItems)));

    if (user) {
      try {
        await setDocWithQueue(doc(db, 'users', user.uid, 'grocery_list', id), safeClone(updatedItem));
      } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, `users/${user.uid}/grocery_list/${id}`);
      }
    }
  }, [groceryItems, user]);
  const handleDeleteGroceryItem = useCallback(async (id: string, skipDelay = false) => {
    if (!skipDelay) {
      setDeletingIds(prev => new Set(prev).add(id));
      try { sounds.playPop(); } catch (err) {}
      setTimeout(() => {
        handleDeleteGroceryItem(id, true);
      }, 320);
      return;
    }
    const updatedItems = groceryItems.filter(i => i.id !== id);
    setGroceryItems(updatedItems);
    localStorage.setItem('grocery_items', JSON.stringify(safeClone(updatedItems)));

    if (user) {
      try {
        await deleteDocWithQueue(doc(db, 'users', user.uid, 'grocery_list', id));
      } catch (e) {
        handleFirestoreError(e, OperationType.DELETE, `users/${user.uid}/grocery_list/${id}`);
      }
    }
    setDeletingIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, [groceryItems, user]);
  const handleClearCheckedGroceryItems = useCallback(async () => {
    const checkedItems = groceryItems.filter(i => i.checked);
    const updatedItems = groceryItems.filter(i => !i.checked);
    setGroceryItems(updatedItems);
    localStorage.setItem('grocery_items', JSON.stringify(safeClone(updatedItems)));

    if (user && checkedItems.length > 0) {
      const docRefs = checkedItems.map(item => doc(db, 'users', user.uid, 'grocery_list', item.id));
      batchDeleteDocsWithQueue(docRefs).catch(e => {
        console.warn('Batch delete checked grocery items error:', e);
      });
    }
  }, [groceryItems, user]);
  const handleAddMealIngredientsToGrocery = useCallback(async (meal: any) => {
    if (!meal.ingredients || meal.ingredients.length === 0) {
      // If no ingredients listed, add the meal name itself as an item
      handleAddGroceryItem(meal.meal_name, undefined, undefined, meal.id || 'suggestion');
      toast.success(lang === 'ua' ? 'Додано до списку продуктів' : 'Added to Concierge');
      return;
    }

    const mealId = meal.id || Math.random().toString(36).substring(7);
    setIsAddingToGrocery(mealId);

    try {
      for (const ing of meal.ingredients) {
        await handleAddGroceryItem(ing.name, String(ing.amount), ing.unit, mealId);
      }
      toast.success(lang === 'ua' ? 'Інгредієнти додано до списку' : 'Ingredients added to list');
    } catch (e) {
      toast.error(lang === 'ua' ? 'Помилка при додаванні' : 'Failed to add items');
    } finally {
      setIsAddingToGrocery(null);
    }
  }, [handleAddGroceryItem, lang]);
  const handleAddMultipleGroceryItems = useCallback(async (newItems: Array<{ name: string; amount?: string; unit?: string; source_meal_id?: string }>) => {
    const createdItems: GroceryItem[] = newItems.map(item => {
      const id = (typeof crypto !== 'undefined' && crypto.randomUUID) 
        ? crypto.randomUUID() 
        : Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      return {
        id,
        name: item.name,
        amount: item.amount,
        unit: item.unit,
        checked: false,
        source_meal_id: item.source_meal_id,
        timestamp: Date.now()
      };
    });

    const updatedItems = [...createdItems, ...groceryItems];
    setGroceryItems(updatedItems);
    localStorage.setItem('grocery_items', JSON.stringify(safeClone(updatedItems)));

    try {
      sounds.playSuccess();
    } catch (_) {}
    triggerHaptic('SUCCESS');

    toast.success(lang === 'ua' ? 'Інгредієнти додано до списку' : 'Ingredients added to list');

    if (user) {
      try {
        await Promise.all(createdItems.map(item => 
          setDocWithQueue(doc(db, 'users', user.uid, 'grocery_list', item.id), safeClone(item))
        ));
      } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, `users/${user.uid}/grocery_list`);
      }
    }
  }, [groceryItems, user, lang]);
  const recalculateTotalNutrition = useCallback((foods: any[]) => {
    const totals = foods.reduce((acc, food) => ({
      calories_kcal: acc.calories_kcal + (Number(food.calories_kcal) || 0),
      protein_g: acc.protein_g + (Number(food.protein_g) || 0),
      carbs_g: acc.carbs_g + (Number(food.carbs_g) || 0),
      fat_g: acc.fat_g + (Number(food.fat_g) || 0),
      sugar_g: acc.sugar_g + (Number(food.sugar_g) || 0),
      fiber_g: acc.fiber_g + (Number(food.fiber_g) || 0),
      sodium_mg: acc.sodium_mg + (Number(food.sodium_mg) || 0),
      potassium_mg: acc.potassium_mg + (Number(food.potassium_mg) || 0),
      cholesterol_mg: acc.cholesterol_mg + (Number(food.cholesterol_mg) || 0),
      vitamin_c_mg: acc.vitamin_c_mg + (Number(food.vitamin_c_mg) || 0),
      calcium_mg: acc.calcium_mg + (Number(food.calcium_mg) || 0),
      iron_mg: acc.iron_mg + (Number(food.iron_mg) || 0),
      vitamin_a_mcg: acc.vitamin_a_mcg + (Number(food.vitamin_a_mcg) || 0),
      vitamin_d_mcg: acc.vitamin_d_mcg + (Number(food.vitamin_d_mcg) || 0),
      vitamin_b12_mcg: acc.vitamin_b12_mcg + (Number(food.vitamin_b12_mcg) || 0),
      magnesium_mg: acc.magnesium_mg + (Number(food.magnesium_mg) || 0),
      zinc_mg: acc.zinc_mg + (Number(food.zinc_mg) || 0),
      caffeine_mg: acc.caffeine_mg + (Number(food.caffeine_mg) || 0),
      water_ml: acc.water_ml + (Number(food.water_ml) || 0),
    }), {
      calories_kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0,
      sugar_g: 0, fiber_g: 0, sodium_mg: 0,
      potassium_mg: 0, cholesterol_mg: 0, vitamin_c_mg: 0, calcium_mg: 0, iron_mg: 0,
      vitamin_a_mcg: 0, vitamin_d_mcg: 0, vitamin_b12_mcg: 0, magnesium_mg: 0, zinc_mg: 0,
      caffeine_mg: 0, water_ml: 0
    });

    return {
      calories_kcal: Math.round(totals.calories_kcal),
      protein_g: Number((totals.protein_g || 0).toFixed(1)),
      carbs_g: Number((totals.carbs_g || 0).toFixed(1)),
      fat_g: Number((totals.fat_g || 0).toFixed(1)),
      sugar_g: Number((totals.sugar_g || 0).toFixed(1)),
      fiber_g: Number((totals.fiber_g || 0).toFixed(1)),
      sodium_mg: Math.round(totals.sodium_mg || 0),
      caffeine_mg: Math.round(totals.caffeine_mg || 0),
      water_ml: Math.round(totals.water_ml || 0),
      potassium_mg: Math.round(totals.potassium_mg || 0),
      cholesterol_mg: Math.round(totals.cholesterol_mg || 0),
      vitamin_c_mg: Number((totals.vitamin_c_mg || 0).toFixed(1)),
      calcium_mg: Math.round(totals.calcium_mg || 0),
      iron_mg: Number((totals.iron_mg || 0).toFixed(1)),
      vitamin_a_mcg: Math.round(totals.vitamin_a_mcg || 0),
      vitamin_d_mcg: Number((totals.vitamin_d_mcg || 0).toFixed(1)),
      vitamin_b12_mcg: Number((totals.vitamin_b12_mcg || 0).toFixed(2)),
      magnesium_mg: Math.round(totals.magnesium_mg || 0),
      zinc_mg: Number((totals.zinc_mg || 0).toFixed(1)),
    };
  }, []);
  const handleFetchSuggestions = useCallback(async (customPrompt?: string, forceRefresh: boolean = false) => {
    const todayStr = new Date().toDateString();
    const todayMeals = selectedDayCombined.filter(i => i.type === 'meal') as HistoryEntry[];
    const mealsLength = todayMeals.length;
    const fetchKey = customPrompt 
      ? `${todayStr}-${mealsLength}-${lang}-${vibe}-adjusted-${customPrompt}-${forceRefresh ? Date.now() : ''}`
      : `${todayStr}-${mealsLength}-${lang}-${vibe}-${forceRefresh ? Date.now() : ''}`;

    setIsGeneratingSuggestions(true);
    setLastSuggestionFetchAttempt(Date.now());
    setSuggestionError(null);
    try {
      // Use higher timeout for suggestions as they take longer
      const activeExclusions = [...excludedFoods];
      if (filterSafeChoiceAllergens && safeChoiceProfile?.active && safeChoiceProfile?.medicalAllergies) {
        safeChoiceProfile.medicalAllergies.forEach((allergy: string) => {
          if (!activeExclusions.includes(allergy)) {
            activeExclusions.push(allergy);
          }
        });
      }

      const suggestions = await generateMealSuggestions(
        goals, 
        history, 
        todayMeals, 
        lang, 
        vibe, 
        userMetrics || undefined, 
        activeExclusions, 
        customPrompt, 
        preferredIngredients,
        forceRefresh,
        subscriptionTier,
        userName
      );
      if (suggestions && suggestions.length > 0) {
        setAiMealSuggestions(suggestions);
        localStorage.setItem('ai_meal_suggestions_cache', JSON.stringify(safeClone(suggestions)));
        setSuggestionsGeneratedForDate(fetchKey);
        localStorage.setItem('ai_suggestions_generated_key', fetchKey);
        setAdjustingSuggestionId(null);
        setAdjustmentInput('');
      } else {
        // It returned empty, which means it failed to generate valid options.
        setSuggestionError("error");
      }
    } catch (error: any) {
      console.error("Failed to generate suggestions", error);
      if (error?.message?.includes("QUOTA") || error?.message?.includes("429")) {
        setSuggestionError("quota");
      } else {
        setSuggestionError("error");
      }
    } finally {
      setIsGeneratingSuggestions(false);
    }
  }, [selectedDayCombined, lang, vibe, goals, history, userMetrics, excludedFoods, preferredIngredients, filterSafeChoiceAllergens, safeChoiceProfile]);

  // ==========================================
  // 4. EVENT HANDLERS & ACTION CONTROLLERS
  // ==========================================
  const handleMealTouchStart = (e: React.TouchEvent | React.MouseEvent, id: string) => {
    setIsLongPressingMeal(false);
    
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }

    mealLongPressTimeout.current = setTimeout(() => {
      setIsLongPressingMeal(true);
      setMealContextMenu({ id, x: clientX, y: clientY });
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
  };
  const handleMealTouchEnd = () => {
    if (mealLongPressTimeout.current) {
      clearTimeout(mealLongPressTimeout.current);
    }
  };
  const handleMealTouchMove = () => {
    if (mealLongPressTimeout.current) {
      clearTimeout(mealLongPressTimeout.current);
    }
  };
  const handleBack = () => {
    setIsChatPicking(false);
    setChatPickingSource(null);
    setIllumination(localStorage.getItem('app_illumination') === 'true');
    setPreviewIlluminationColor(illuminationColor);
    if (navigationHistory.length > 0) {
      const prevView = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory(prev => prev.slice(0, -1));
      setIsBackNavigating(true);
      setView(prevView);
    } else {
      setView('home');
    }
  };
  const handleLogin = async () => {
    try {
      localStorage.removeItem('explicit_logout');
      setIsGoogleSigningIn(true);
      setLoginError(null);
      
      // Explicitly check for storage access if possible
      try {
        localStorage.setItem('auth_test', '1');
        localStorage.removeItem('auth_test');
      } catch (e) {
        toast.error("Browser storage is blocked. Please enable cookies/storage or don't use Incognito mode.", { duration: 8000 });
        setIsGoogleSigningIn(false);
        return;
      }

      // Helper to process authenticated Firebase User
      const onUserAuthenticated = (u: FirebaseUser) => {
        const email = u.email || u.providerData?.find((p: any) => p?.email)?.email || '';
        if (email) {
          localStorage.setItem('user_email', email);
          setUserDocEmail(email);
        }
        const existingName = (localStorage.getItem('userName') || userNameRef.current || '').trim();
        if (u.displayName && (!existingName || existingName === 'null')) {
          localStorage.setItem('userName', u.displayName);
          setUserName(u.displayName);
          setOnboardingName(u.displayName);
        }
        const detectedPhoto = u.photoURL || u.providerData?.find((p: any) => p?.photoURL)?.photoURL || '';
        if (detectedPhoto) {
          localStorage.setItem('custom_avatar', detectedPhoto);
          setCustomAvatar(detectedPhoto);
          setAvatarImageError(false);
        }
        localStorage.removeItem('explicit_logout');
        setUser(u);
        setIsGoogleSigningIn(false);
        setIsAuthLoading(false);
        setView('home');
        forceRecreateSessionWithFirebase(true).catch(e => console.warn("[AUTH] Immediate session sync notice:", e));
        toast.success(lang === 'ua' ? `Вітаємо, ${u.displayName || email}!` : `Welcome, ${u.displayName || email}!`);
        flushAuthLogQueue().catch(e => console.warn("[AUTH] Login queue flush error:", e));
      };

      // If Firebase already has an active currentUser, resolve immediately
      if (auth.currentUser && !auth.currentUser.isAnonymous) {
        onUserAuthenticated(auth.currentUser);
        return;
      }

      // Standard Firebase Google Provider
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      provider.setCustomParameters({ prompt: 'select_account' });
      
      try {
        const result = await signInWithPopup(auth, provider);
        if (result && result.user) {
          onUserAuthenticated(result.user);
          return;
        }
      } catch (popupError: any) {
        if (popupError?.code === 'auth/popup-closed-by-user' || popupError?.code === 'auth/cancelled-popup-request') {
          console.log("[AUTH] User closed popup manually.");
          setIsGoogleSigningIn(false);
          return;
        }

        console.warn("[AUTH] Popup login failed, attempting Google redirect sign-in:", popupError);
        try {
          toast.info(lang === 'ua' ? 'Перенаправлення на сторінку Google...' : 'Connecting with Google...');
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectErr: any) {
          console.error("[AUTH] Redirect sign-in failed:", redirectErr);
          throw redirectErr;
        }
      }
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user' || error?.type === 'popup_closed' || error?.code === 'auth/cancelled-popup-request') {
        console.log("[AUTH] Google login popup was closed by user.");
        return;
      }
      
      console.error("[AUTH] Login failed", error);
      setLoginError(error);
      addAuthLog('Google_SignIn', error?.code || 'unknown', error?.message || String(error));
      
      if (error?.code === 'auth/unauthorized-domain') {
        toast.error(`Login failed: Unauthorized domain. Please add ${window.location.hostname} to the Firebase Console > Authentication > Settings > Authorized domains.`, { duration: 10000 });
      } else if (error.message?.includes('missing initial state') || error.code === 'auth/internal-error') {
        toast.error("Auth state lost. This often happens if 3rd-party cookies are blocked or in Incognito mode. Try enabling 'Allow all cookies' or use a standard window.", { duration: 12000 });
      } else if (error?.code === 'auth/operation-not-allowed') {
        toast.error("Google Sign-In is not enabled. Please enable it in Firebase Console > Authentication > Sign-in method.", { duration: 10000 });
      } else if (error?.code === 'auth/network-request-failed') {
        toast.error(lang === 'ua' ? 'Помилка мережі при спробі авторизації через Google.' : 'Network error during Google Sign-In.');
      } else {
        toast.error(`Login failed: ${error?.message || 'Please try again.'}`);
      }
    } finally {
      setIsGoogleSigningIn(false);
    }
  };
  const handleLoginRedirect = async () => {
    try {
      localStorage.removeItem('explicit_logout');
      setIsGoogleSigningIn(true);
      setLoginError(null);
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithRedirect(auth, provider);
    } catch (error: any) {
      console.error("[AUTH] Redirect login failed", error);
      setLoginError(error);
      setIsGoogleSigningIn(false);
      toast.error(`Redirect login failed: ${error?.message || 'Please try again.'}`);
    }
  };
  const handleGuestLogin = async () => {
    try {
      localStorage.removeItem('explicit_logout');
      setIsAuthLoading(true);
      setLoginError(null);
      const cred = await signInAnonymously(auth);
      if (cred && cred.user) {
        const guestEmail = `guest_${cred.user.uid.substring(0, 8)}@mainnutrition.app`;
        localStorage.setItem('user_email', guestEmail);
        setUserDocEmail(guestEmail);
        setUser(cred.user);
        setIsAuthLoading(false);
        setView('home');
        toast.success(lang === 'ua' ? 'Вітаємо! Увійшли в гостьовому режимі.' : 'Welcome! Logged in as Guest.');
      }
    } catch (error: any) {
      console.error("[AUTH] Guest login failed", error);
      setIsAuthLoading(false);
      toast.error(`Guest login failed: ${error?.message || 'Please try again.'}`);
    }
  };
  const handleLinkGuestAccount = async (email: string, pass: string) => {
    if (!auth.currentUser) {
      toast.error(lang === 'ua' ? 'Помилка: Користувач не авторизований.' : 'Error: No active user session.');
      return;
    }
    setIsLinkingAuth(true);
    try {
      const credential = EmailAuthProvider.credential(email.trim().toLowerCase(), pass);
      await linkWithCredential(auth.currentUser, credential);
      
      const normEmail = email.trim().toLowerCase();
      localStorage.setItem('user_email', normEmail);
      setUserDocEmail(normEmail);
      
      // Update firestore user metadata to indicate they are no longer temporary
      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        await setDoc(userRef, { 
          email: normEmail,
          isAnonymous: false,
          updatedAt: new Date().toISOString()
        }, { merge: true });
      } catch (dbErr) {
        console.warn("[AUTH] Failed to update Firestore user document after linking:", dbErr);
      }
      
      toast.success(lang === 'ua' ? 'Акаунт успішно прив\'язано! Ви тепер постійний користувач.' : 'Guest account linked successfully! You are now a permanent user.');
      setShowUpgradeModal(false);
      flushAuthLogQueue().catch(e => console.warn("[AUTH] Account linking flush error:", e));
    } catch (err: any) {
      console.error("[AUTH] Account linking failed:", err);
      addAuthLog('Link_Email', err?.code || 'auth_error', err?.message || String(err));
      if (err.code === 'auth/email-already-in-use') {
        toast.error(lang === 'ua' 
          ? 'Цей email вже використовується іншим акаунтом.' 
          : 'This email is already in use by another account.');
      } else {
        toast.error(err.message || 'Account linking failed');
      }
    } finally {
      setIsLinkingAuth(false);
    }
  };
  const handleLinkGuestGoogle = async () => {
    if (!auth.currentUser) {
      toast.error(lang === 'ua' ? 'Помилка: Користувач не авторизований.' : 'Error: No active user session.');
      return;
    }
    setIsLinkingAuth(true);
    try {
      const provider = new GoogleAuthProvider();
      const linkPromise = linkWithPopup(auth.currentUser, provider);
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("AUTH_TIMEOUT")), 30000)
      );
      await Promise.race([linkPromise, timeoutPromise]);
      
      const email = auth.currentUser.email || '';
      localStorage.setItem('user_email', email);
      setUserDocEmail(email);
      
      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        await setDoc(userRef, { 
          email: email,
          isAnonymous: false,
          updatedAt: new Date().toISOString()
        }, { merge: true });
      } catch (dbErr) {
        console.warn("[AUTH] Failed to update Firestore user document after Google linking:", dbErr);
      }
      
      toast.success(lang === 'ua' ? 'Акаунт успішно прив\'язано до Google!' : 'Guest account linked to Google successfully!');
      setShowUpgradeModal(false);
      flushAuthLogQueue().catch(e => console.warn("[AUTH] Google linking flush error:", e));
    } catch (err: any) {
      if (err?.message === "AUTH_TIMEOUT") {
        toast.error(lang === "ua" ? "Час очікування прив'язки Google вичерпано." : "Google linking timed out.");
        addAuthLog("Link_Google_Timeout", "AUTH_TIMEOUT", "Google linking timed out.");
      } else if (err?.code === 'auth/popup-closed-by-user') {
        console.log("[AUTH] Google link popup was closed by user.");
      } else {
        console.error("[AUTH] Google linking failed:", err);
        addAuthLog('Link_Google', err?.code || 'auth_error', err?.message || String(err));
        toast.error(err.message || 'Google linking failed');
      }
    } finally {
      setIsLinkingAuth(false);
    }
  };
  const handleLogout = async () => {
    localStorage.setItem('explicit_logout', 'true');
    setIsMenuOpen(false);
    setUser(null);
    setView('login');
    setIsAuthLoading(false);
    setShowOnboarding(false);
    try {
      // Invalidate the session database-side completely
      const session = getSession();
      if (session && session.refreshToken) {
        try {
          await fetch("/api/auth/session/logout", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: safeStringify({ refreshToken: session.refreshToken })
          });
        } catch (e) {
          console.error("Failed to notify server of session invalidation:", e);
        }
      }

      // Clear front-end session tokens
      clearSession();

      if (auth) {
        await signOut(auth).catch((e) => console.warn("Firebase signOut error:", e));
      }

      // Clear local storage on logout to prevent data leakage between users
      localStorage.removeItem('user_goals');
      localStorage.removeItem('meal_history');
      localStorage.removeItem('water_logs');
      localStorage.removeItem('favorites');
      localStorage.removeItem('app_lang');
      localStorage.removeItem('theme');
      localStorage.removeItem('onboarding_completed');
      localStorage.removeItem('google_fit_connected');
      localStorage.removeItem('google_fit_access_token');
      localStorage.removeItem('google_fit_token_timestamp');
      localStorage.removeItem('google_fit_pending_link');
      localStorage.removeItem('user_email');
      localStorage.removeItem('userName');
      localStorage.removeItem('custom_avatar');
      localStorage.removeItem('grocery_items');
      localStorage.removeItem('scanned_products');
      
      // Reset local state
      setUserDocEmail('');
      setUserName('');
      setCustomAvatar('');
      setHistory([]);
      setWaterLogs([]);
      setFavorites([]);
      setGroceryItems([]);
      setScannedProducts([]);
      setGoals({
        calories: 2000,
        protein: 150,
        carbs: 200,
        fat: 67,
        water_ml: 2500,
        sugar_g: 50,
        fiber_g: 28,
        sodium_mg: 2300,
      });
      
      toast.success(lang === 'ua' ? 'Ви вийшли з акаунту' : 'Signed out successfully');
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      if (user) {
        const uid = user.uid;
        const subcollections = [
          'history',
          'water',
          'days',
          'grocery_list',
          'scanned_products',
          'notifications',
          'gamification_logs',
          'loot_inventory',
          'gamification',
          'meal_templates',
          'offline_sync_queue'
        ];

        for (const sub of subcollections) {
          try {
            const subRef = collection(db, 'users', uid, sub);
            const snapshot = await getDocs(subRef);
            const deletePromises = snapshot.docs.map(d => deleteDocWithQueue(d.ref));
            await Promise.all(deletePromises);
          } catch (err) {
            console.warn(`Error clearing subcollection ${sub}:`, err);
          }
        }

        try {
          await deleteDocWithQueue(doc(db, 'users', uid));
        } catch (err) {
          console.warn("Error deleting user document:", err);
        }

        try {
          await deleteDocWithQueue(doc(db, 'audit_tracking', uid));
        } catch (err) {
          console.warn("Error deleting audit document:", err);
        }

        if (auth && auth.currentUser) {
          try {
            await deleteUser(auth.currentUser);
          } catch (authErr: any) {
            console.warn("Firebase deleteUser error:", authErr);
            if (authErr?.code === 'auth/requires-recent-login') {
              toast.error(
                lang === 'ua'
                  ? 'З міркувань безпеки, для видалення акаунту потрібно заново увійти. Будь ласка, вийдіть і увійдіть знову.'
                  : 'For security reasons, deleting your account requires a fresh login. Please sign out and sign back in.',
                { duration: 6000 }
              );
              setIsDeletingAccount(false);
              setShowDeleteAccountConfirm(false);
              return;
            }
          }
        }
      }

      try {
        await localDb.clear();
      } catch (e) {
        console.warn("Error clearing localDb:", e);
      }

      const session = getSession();
      if (session && session.refreshToken) {
        try {
          await fetch("/api/auth/session/logout", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: safeStringify({ refreshToken: session.refreshToken })
          });
        } catch (e) {
          console.error("Failed to notify server of session deletion:", e);
        }
      }
      clearSession();

      localStorage.removeItem('user_goals');
      localStorage.removeItem('meal_history');
      localStorage.removeItem('water_logs');
      localStorage.removeItem('favorites');
      localStorage.removeItem('app_lang');
      localStorage.removeItem('theme');
      localStorage.removeItem('onboarding_completed');
      localStorage.removeItem('google_fit_connected');
      localStorage.removeItem('google_fit_access_token');
      localStorage.removeItem('google_fit_token_timestamp');
      localStorage.removeItem('google_fit_pending_link');
      localStorage.removeItem('user_email');
      localStorage.removeItem('userName');
      localStorage.removeItem('custom_avatar');
      localStorage.removeItem('grocery_items');
      localStorage.removeItem('scanned_products');
      localStorage.removeItem('explicit_logout');

      setUser(null);
      setUserDocEmail('');
      setUserName('');
      setCustomAvatar('');
      setHistory([]);
      setWaterLogs([]);
      setFavorites([]);
      setGroceryItems([]);
      setScannedProducts([]);
      setShowDeleteAccountConfirm(false);
      setIsDeletingAccount(false);
      setView('login');

      toast.success(
        lang === 'ua' ? 'Ваш акаунт та всі дані успішно видалено.' : 'Your account and all associated data have been permanently deleted.',
        {
          icon: <Trash2 className="w-5 h-5 text-red-500" />,
          className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold",
        }
      );
    } catch (error) {
      console.error("Account deletion failed:", error);
      toast.error(
        lang === 'ua' ? 'Не вдалося видалити акаунт. Спробуйте ще раз.' : 'Failed to delete account. Please try again.',
        { className: "rounded-2xl border-red-500/20 bg-white dark:bg-zinc-900 text-red-500 font-bold" }
      );
      setIsDeletingAccount(false);
    }
  };
  const handleStartNewChat = () => {
    setActiveThreadId(null);
    setChatMessages([]);
    localDb.removeItem('chat_active_thread_id');
    localDb.removeItem('chat_history');
    localStorage.removeItem('chat_history');
    setChatInput('');
    setChatImage(null);
    setShowThreadList(false);
  };
  const handleClearCurrentThread = () => {
    if (activeThreadId) {
      setChatThreads(prev => {
        const filtered = prev.filter(t => t.id !== activeThreadId);
        localDb.setItem('chat_threads', JSON.stringify(filtered));
        return filtered;
      });
    }
    setChatMessages([]);
    setActiveThreadId(null);
    localDb.removeItem('chat_active_thread_id');
    localDb.removeItem('chat_history');
    localStorage.removeItem('chat_history');
    setChatInput('');
    setChatImage(null);
    setShowClearConfirm(false);
    setShowThreadList(false);
  };
  const loadMoreHistory = async () => {
    if (!user || loadingMoreHistory || !hasMoreHistory) return;
    setLoadingMoreHistory(true);
    try {
      setDaysLimit(prev => prev + 15);
      toast.success(lang.startsWith('en') ? "Loaded more history entries!" : "Завантажено більше записів!");
    } catch (e) {
      console.error("Error loading more history:", e);
      toast.error(lang.startsWith('en') ? "Failed to load more history" : "Не вдалося завантажити більше записів");
    } finally {
      setLoadingMoreHistory(false);
    }
  };
  const handleSaveWeightLog = (weightKg: number, dateStr?: string, note?: string) => {
    if (isNaN(weightKg) || weightKg <= 20 || weightKg >= 400) {
      toast.error(lang === 'ua' ? 'Введіть коректну вагу (20 - 400 кг)' : 'Please enter a valid weight (20 - 400 kg)');
      return;
    }

    const targetDate = dateStr ? new Date(dateStr + 'T12:00:00') : new Date();
    const timestamp = targetDate.getTime();
    const dKey = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;

    const newLog: WeightLog = {
      id: `weight_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      timestamp,
      weight_kg: Number(weightKg.toFixed(1)),
      note: note?.trim() || undefined
    };

    let updatedWeightLogs: WeightLog[] = [];
    setWeightLogs(prev => {
      const filtered = prev.filter(item => {
        const itemDate = new Date(item.timestamp);
        const itemKey = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}-${String(itemDate.getDate()).padStart(2, '0')}`;
        return itemKey !== dKey;
      });
      const updated = [...filtered, newLog].sort((a, b) => a.timestamp - b.timestamp);
      updatedWeightLogs = updated;
      localDb.setItem('weight_logs', JSON.stringify(safeClone(updated)));
      return updated;
    });

    localStorage.setItem('onboardingWeight', String(Number(weightKg.toFixed(1))));
    setOnboardingWeight(String(Number(weightKg.toFixed(1))));
    clearProgressChartsCache();
    triggerHaptic('SUCCESS');
    setShowWeightModal(false);
    toast.success(lang === 'ua' ? `Вагу ${Number(weightKg.toFixed(1))} кг збережено!` : `Weight ${Number(weightKg.toFixed(1))} kg saved!`);

    if (user && !isQuotaExceeded) {
      try {
        setDocWithQueue(doc(db, 'users', user.uid), {
          metrics: { weight: Number(weightKg.toFixed(1)) },
          lastWeightLog: newLog,
          weightLogs: safeClone(updatedWeightLogs)
        }, { merge: true });
      } catch (e) {
        console.warn("Weight log Firestore sync queued:", e);
      }
    }
  };
  const handleDeleteWeightLog = (id: string) => {
    let updatedWeightLogs: WeightLog[] = [];
    setWeightLogs(prev => {
      const updated = prev.filter(l => l.id !== id);
      updatedWeightLogs = updated;
      localDb.setItem('weight_logs', JSON.stringify(safeClone(updated)));
      return updated;
    });
    if (user && !isQuotaExceeded) {
      try {
        setDocWithQueue(doc(db, 'users', user.uid), {
          weightLogs: safeClone(updatedWeightLogs)
        }, { merge: true });
      } catch (e) {
        console.warn("Weight log deletion Firestore sync queued:", e);
      }
    }
    clearProgressChartsCache();
    triggerHaptic('SUCCESS');
    toast.success(lang === 'ua' ? 'Запис ваги видалено' : 'Weight entry removed');
  };
  const handleScaleMealAmount = (multiplier: number, targetAmountStr?: string) => {
    const base = view === 'details' ? (selectedHistoryItem || result) : (result || selectedHistoryItem);
    if (!base) return;

    // Scale all nutritional values
    const scaleValue = (val: number | undefined) => {
      if (val === undefined) return undefined;
      return Number((val * multiplier).toFixed(1));
    };

    const scaledNutrition = base.total_nutrition ? {
      calories_kcal: scaleValue(base.total_nutrition.calories_kcal) || 0,
      protein_g: scaleValue(base.total_nutrition.protein_g) || 0,
      carbs_g: scaleValue(base.total_nutrition.carbs_g) || 0,
      fat_g: scaleValue(base.total_nutrition.fat_g) || 0,
      sugar_g: scaleValue(base.total_nutrition.sugar_g),
      fiber_g: scaleValue(base.total_nutrition.fiber_g),
      sodium_mg: scaleValue(base.total_nutrition.sodium_mg),
      potassium_mg: scaleValue(base.total_nutrition.potassium_mg),
      cholesterol_mg: scaleValue(base.total_nutrition.cholesterol_mg),
      vitamin_c_mg: scaleValue(base.total_nutrition.vitamin_c_mg),
      calcium_mg: scaleValue(base.total_nutrition.calcium_mg),
      iron_mg: scaleValue(base.total_nutrition.iron_mg),
      vitamin_a_mcg: scaleValue(base.total_nutrition.vitamin_a_mcg),
      vitamin_d_mcg: scaleValue(base.total_nutrition.vitamin_d_mcg),
      vitamin_b12_mcg: scaleValue(base.total_nutrition.vitamin_b12_mcg),
      magnesium_mg: scaleValue(base.total_nutrition.magnesium_mg),
      zinc_mg: scaleValue(base.total_nutrition.zinc_mg),
      caffeine_mg: scaleValue(base.total_nutrition.caffeine_mg),
      water_ml: scaleValue(base.total_nutrition.water_ml),
    } : undefined;

    // Scale individual foods
    const scaledFoods = base.foods ? base.foods.map(food => {
      const scaledAmount = typeof food.amount === 'number' ? scaleValue(food.amount) : food.amount;
      
      // Update string representation of portion if we have an original amount and unit
      let scaledPortion = food.portion;
      if (typeof scaledAmount === 'number' && typeof food.amount === 'number') {
        const unit = food.unit || 'g';
        scaledPortion = `${scaledAmount}${unit}`;
      }

      return {
        ...food,
        amount: scaledAmount,
        portion: scaledPortion,
        calories_kcal: scaleValue(food.calories_kcal) || 0,
        protein_g: scaleValue(food.protein_g) || 0,
        carbs_g: scaleValue(food.carbs_g) || 0,
        fat_g: scaleValue(food.fat_g) || 0,
        sugar_g: scaleValue(food.sugar_g),
        fiber_g: scaleValue(food.fiber_g),
        sodium_mg: scaleValue(food.sodium_mg),
        potassium_mg: scaleValue(food.potassium_mg),
        cholesterol_mg: scaleValue(food.cholesterol_mg),
        vitamin_c_mg: scaleValue(food.vitamin_c_mg),
        calcium_mg: scaleValue(food.calcium_mg),
        iron_mg: scaleValue(food.iron_mg),
        vitamin_a_mcg: scaleValue(food.vitamin_a_mcg),
        vitamin_d_mcg: scaleValue(food.vitamin_d_mcg),
        vitamin_b12_mcg: scaleValue(food.vitamin_b12_mcg),
        magnesium_mg: scaleValue(food.magnesium_mg),
        zinc_mg: scaleValue(food.zinc_mg),
        caffeine_mg: scaleValue(food.caffeine_mg),
        water_ml: scaleValue(food.water_ml),
      };
    }) : [];

    // Construct scaled meal object
    const finalTotalAmountStr = targetAmountStr || (() => {
      const matchNum = (base.total_amount || '').match(/[\d.]+/);
      const matchUnit = (base.total_amount || '').replace(/[\d.]+/g, '').trim();
      if (matchNum) {
        const val = parseFloat(matchNum[0]) * multiplier;
        return `${Number(val.toFixed(1))}${matchUnit ? ' ' + matchUnit : ''}`;
      }
      return `${Number(multiplier.toFixed(2))}x portion`;
    })();

    const scaledMeal = postProcessNutrition({
      ...base,
      total_amount: finalTotalAmountStr,
      total_nutrition: scaledNutrition,
      foods: scaledFoods,
      insight: "" // Force dynamic recalculation to reflect the adjusted portion and new counts
    } as any, lang, vibe);

    // Save
    if (view === 'details' && selectedHistoryItem && selectedHistoryItem.id !== 'temp') {
      const updated = { ...selectedHistoryItem, ...scaledMeal } as HistoryEntry;
      setSelectedHistoryItem(updated);
      const newHistory = history.map(item => item.id === updated.id ? updated : item);
      const saved = saveMealHistory(newHistory);
      setHistory(saved);
      if (user) syncHistoryToFirebase(updated, user.uid);
    } else {
      setResult(scaledMeal);
    }

    setOriginalResult(safeClone(scaledMeal));
    toast.success(lang === 'ua' ? 'Розмір порції збережено та дані оновлено!' : 'Portion adjusted and all nutritional data updated!');
    setMealScaleMultiplier(1.0);
    setCustomWeightInput('');
    setIsMealNameManuallyEdited(true);
  };
  const handleSaveFastingState = async (newState: FastingState) => {
    setFastingState(newState);
    localStorage.setItem('main_nutrition_fasting_state', JSON.stringify(safeClone(newState)));
    if (user) {
      try {
        await setDocWithQueue(doc(db, 'users', user.uid), {
          fastingState: newState,
          lastUpdatedAt: Date.now()
        }, { merge: true });
      } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, `users/${user.uid}`);
      }
    }
  };
  const handleSuggestionScroll = (gIdx: number, e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const width = container.offsetWidth;
    if (width > 0) {
      const newIdx = Math.round(scrollLeft / width);
      if ((activeMealSuggestionIndices[gIdx] || 0) !== newIdx) {
        setActiveMealSuggestionIndices(prev => ({
          ...prev,
          [gIdx]: newIdx
        }));
      }
    }
  };
  const handleQuickAddSuggestion = async (option: any, mealType: string, id: string) => {
    setAddingSuggestionId(id);
    setError(null);
    try {
      // Fast path: Instead of waiting for a slow and expensive remote model processing to re-analyze
      // text we ALREADY suggested, we formulate the MealAnalysis object instantly using the pre-calculated macros of the option!
      // This provides 0ms latency for logging/reviewing while keeping calculations perfectly synchronized.
      const optionMacros = option.macros || { protein: 0, carbs: 0, fat: 0 };
      const rawIngredients = option.ingredients || getIngredientsForMeal(
        option.meal_name,
        option.expected_calories,
        optionMacros.protein,
        optionMacros.carbs,
        optionMacros.fat,
        lang
      );

      const estimateItemCaffeine = (itemName: string, explicitCaff?: number) => {
        const name = (itemName || '').toLowerCase();
        if (/cookie|oreo|biscuit|cake|pastry|bread|waffle|pancake|cracker|bar|candy|chocolate|snack|печив|орео|бісквіт|торт|тістечк|мафін|кекс|пончик|круасан|хліб|тост|булочк|вафл|панкейк|млинц|крекер|цукерк|солодощ|шоколад|батончик|снек/i.test(name)) return 0;
        if (typeof explicitCaff === 'number' && !isNaN(explicitCaff) && explicitCaff > 0) return explicitCaff;
        if (/coffee|espresso|latte|cappuccino|americano|кава|еспресо|лате|капучино/i.test(name)) return 80;
        if (/energy|monster|red bull|burn|hell|pit bull|non stop|reign|rockstar|енерг/i.test(name)) return 120;
        if (/tea|matcha|chai|чай|матча/i.test(name)) return 35;
        if (/pepsi|cola|coke|dr pepper|mountain dew|пепсі|кола|кока/i.test(name)) return 34;
        return 0;
      };

      const foods = rawIngredients.map((ing: any) => ({
        name: ing.name,
        portion: ing.portion || `${ing.amount}${ing.unit}`,
        amount: ing.amount !== undefined ? ing.amount : 1,
        unit: ing.unit || "g",
        calories_kcal: ing.calories_kcal,
        protein_g: ing.protein_g,
        carbs_g: ing.carbs_g,
        fat_g: ing.fat_g,
        caffeine_mg: estimateItemCaffeine(ing.name, ing.caffeine_mg),
        water_ml: ing.water_ml || 0,
        sugar_g: ing.sugar_g || 0,
        fiber_g: ing.fiber_g || 0,
        sodium_mg: ing.sodium_mg || 0
      }));

      const mainFoodItem = {
        name: option.meal_name,
        portion: "1 serving",
        amount: 1,
        unit: "serving",
        calories_kcal: option.expected_calories,
        protein_g: optionMacros.protein,
        carbs_g: optionMacros.carbs,
        fat_g: optionMacros.fat,
        caffeine_mg: estimateItemCaffeine(option.meal_name),
        water_ml: 0,
        sugar_g: 0,
        fiber_g: 0,
        sodium_mg: 0
      };

      const targetTimestamp = getTimestampForSelectedDate();
      const hasValidDetailedInsight = typeof option.insight === 'string' && option.insight.trim().length >= 60 && !isGenericInsight(option.insight);
      const initialInsight = hasValidDetailedInsight ? option.insight.trim() : "";
      const suggestionReasoning = option.description ? `${option.description}${option.reason ? ` • ${option.reason}` : ''}` : (option.reason || '');

      const data: MealAnalysis = {
        meal_name: option.meal_name,
        meal_type: mealType as MealType,
        total_amount: "1 serving",
        foods: foods.length > 0 ? foods : [mainFoodItem],
        total_nutrition: {
          calories_kcal: option.expected_calories,
          protein_g: optionMacros.protein,
          carbs_g: optionMacros.carbs,
          fat_g: optionMacros.fat,
          sugar_g: 0,
          fiber_g: 0,
          sodium_mg: 0,
          caffeine_mg: foods.reduce((sum, f) => sum + (f.caffeine_mg || 0), 0) || estimateItemCaffeine(option.meal_name),
          water_ml: 0
        },
        confidence: {
          recognition: 1.0,
          portion: 1.0,
          nutrition: 1.0
        },
        insight: initialInsight,
        reasoning: suggestionReasoning,
        isSuggestion: true
      };

      if (data) {
        data.meal_name = option.meal_name;
        data.meal_type = mealType as any;
        data.isSuggestion = true;
        
        if (!data.total_nutrition) {
          data.total_nutrition = {
            calories_kcal: 0,
            protein_g: 0,
            carbs_g: 0,
            fat_g: 0,
            sugar_g: 0,
            fiber_g: 0,
            sodium_mg: 0
          };
        }
        data.total_nutrition.calories_kcal = option.expected_calories;
        data.total_nutrition.protein_g = optionMacros.protein;
        data.total_nutrition.carbs_g = optionMacros.carbs;
        data.total_nutrition.fat_g = optionMacros.fat;

        if (data.foods && Array.isArray(data.foods) && data.foods.length > 0) {
          const totalCals = data.foods.reduce((sum, f) => sum + Number(f.calories_kcal || 0), 0);
          
          if (totalCals === 0) {
            data.foods[0] = {
              ...data.foods[0],
              calories_kcal: option.expected_calories,
              protein_g: optionMacros.protein,
              carbs_g: optionMacros.carbs,
              fat_g: optionMacros.fat,
              caffeine_mg: 0,
              sugar_g: 0,
              sodium_mg: 0
            };
          } else {
            const calRatio = option.expected_calories / totalCals;
            const protRatio = optionMacros.protein / (data.foods.reduce((sum, f) => sum + Number(f.protein_g || 0), 0) || 1);
            const carbRatio = optionMacros.carbs / (data.foods.reduce((sum, f) => sum + Number(f.carbs_g || 0), 0) || 1);
            const fatRatio = optionMacros.fat / (data.foods.reduce((sum, f) => sum + Number(f.fat_g || 0), 0) || 1);
            
            data.foods = data.foods.map(f => {
              let caff = Number(f.caffeine_mg || 0);
              const isDrink = f.category === 'drink' || /coffee|tea|water|energy|drink/i.test(f.name);
              if (!isDrink || caff > 1000) caff = 0;
              
              return {
                ...f,
                calories_kcal: Math.round(Number(f.calories_kcal || 0) * calRatio),
                protein_g: Number((Number(f.protein_g || 0) * protRatio).toFixed(1)),
                carbs_g: Number((Number(f.carbs_g || 0) * carbRatio).toFixed(1)),
                fat_g: Number((Number(f.fat_g || 0) * fatRatio).toFixed(1)),
                caffeine_mg: caff
              };
            });
          }
        }
      }

      const processedData = postProcessNutrition(
        data,
        lang,
        vibe,
        targetTimestamp,
        ignoreMealTimeLogging,
        onboardingGender
      );

      // Store a pristine copy of foods for change tracking in the Review Modal
      originalPendingReviewFoodsRef.current = safeClone(processedData.foods);

        playScanCompleteSound();
      if (enableMealReview) {
        // Show Review Modal immediately!
        setPendingReviewData({
          ...processedData,
          image: '',
          imageLoading: false
        } as any);
        setShowReviewModal(true);
        setAddingSuggestionId(null);
      } else {
        // Fast path: Log instantly
        const tempId = getTimestampForSelectedDate().toString();
        const newEntry: HistoryEntry = {
          ...processedData,
          id: tempId,
          timestamp: getTimestampForSelectedDate(),
          image: ''
        };
        
        const updatedHistory = [newEntry, ...history];
        saveMealHistory(updatedHistory);
        setHistory(updatedHistory);
        
        if (user) {
          syncHistoryToFirebase(newEntry, user.uid).catch(e => console.error("Background sync error:", e));
        }

        // Optimistically remove logged option from AI suggestions carousel
        setAiMealSuggestions(prev => {
          const updated = prev.map(group => {
            if (!group.options) return group;
            return {
              ...group,
              options: group.options.filter((opt: any) => opt.meal_name !== option.meal_name)
            };
          }).filter(group => group.options && group.options.length > 0);
          localStorage.setItem('ai_meal_suggestions_cache', JSON.stringify(safeClone(updated)));
          return updated;
        });
        
        showUndoToast(t.successLogged ? t.successLogged.replace('{meal}', data.meal_name) : `Meal logged: ${data.meal_name}`, tempId);
        setAddingSuggestionId(null);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to analyze meal text');
    } finally {
      setAddingSuggestionId(null);
    }
  };
  const handleQuickAddMeal = async (meal: HistoryEntry) => {
    try {
      setLoggingQuickAddId(meal.id);
      sounds.playFoodSuccess();
      
      const hour = new Date().getHours();
      let calculatedMealType: MealType = 'snack';
      if (hour >= 5 && hour < 11) calculatedMealType = 'breakfast';
      else if (hour >= 11 && hour < 16) calculatedMealType = 'lunch';
      else if (hour >= 16 && hour < 22) calculatedMealType = 'dinner';

      const newEntry: HistoryEntry = {
        id: getTimestampForSelectedDate().toString(),
        timestamp: getTimestampForSelectedDate(),
        meal_name: meal.meal_name,
        meal_type: calculatedMealType,
        total_nutrition: { ...meal.total_nutrition },
        foods: meal.foods.map(f => ({ ...f })),
        total_amount: meal.total_amount || "1 serving",
        confidence: meal.confidence || { recognition: 1.0, portion: 1.0, nutrition: 1.0 },
        image: meal.image || '',
        tags: meal.tags ? [...meal.tags] : [],
        insight: meal.insight || ''
      };

      const updatedHistory = [newEntry, ...history];
      saveMealHistory(updatedHistory);
      setHistory(updatedHistory);

      if (user) {
        syncHistoryToFirebase(newEntry, user.uid).catch(e => console.error("Background sync error:", e));
      }

      triggerHaptic('MEAL', vibrateOnLogging);
      
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.85 }
      });
      triggerHaptic('CONFETTI', vibrateOnLogging);

      showUndoToast(t.mealLoggedSuccess || 'Meal logged successfully!', newEntry.id);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to log meal');
    } finally {
      setLoggingQuickAddId(null);
    }
  };
  const updatePendingFood = (index: number, fieldOrUpdates: keyof FoodItem | Partial<FoodItem>, value?: any) => {
    setPendingReviewData(prev => {
      if (!prev) return prev;
      const newFoods = [...prev.foods];
      if (typeof fieldOrUpdates === 'object' && fieldOrUpdates !== null) {
        newFoods[index] = { ...newFoods[index], ...fieldOrUpdates };
      } else {
        newFoods[index] = { ...newFoods[index], [fieldOrUpdates as keyof FoodItem]: value };
      }
      return { ...prev, foods: newFoods };
    });
  };
  const removePendingFood = (index: number) => {
    setPendingReviewData(prev => {
      if (!prev) return prev;
      const newFoods = prev.foods.filter((_, i) => i !== index);
      return { ...prev, foods: newFoods };
    });
  };
  const exportAsCSV = () => {
    try {
      const headers = [
        'Type',
        'Date',
        'Time',
        'Meal or Beverage',
        'Calories (kcal)',
        'Protein (g)',
        'Carbs (g)',
        'Fat (g)',
        'Sugar (g)',
        'Fiber (g)',
        'Sodium (mg)',
        'Water/Amount (ml)',
        'Ingredients/Foods'
      ];

      const csvRows = [headers.join(',')];

      const allActivities: CombinedEntry[] = [
        ...history.map(m => ({ ...m, type: 'meal' as const })),
        ...waterLogs.map(w => ({ ...w, type: 'water' as const }))
      ].sort((a, b) => b.timestamp - a.timestamp);

      allActivities.forEach(item => {
        const dateObj = new Date(item.timestamp);
        const yyyy = dateObj.getFullYear();
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dateObj.getDate()).padStart(2, '0');
        const formattedDate = `${yyyy}-${mm}-${dd}`;
        const formattedTime = String(dateObj.getHours()).padStart(2, '0') + ':' + String(dateObj.getMinutes()).padStart(2, '0');

        if (item.type === 'meal') {
          const m = item as HistoryEntry;
          const name = m.meal_name || m.meal_type || 'Meal';
          const cals = m.total_nutrition.calories_kcal || 0;
          const prot = m.total_nutrition.protein_g || 0;
          const carbs = m.total_nutrition.carbs_g || 0;
          const fat = m.total_nutrition.fat_g || 0;
          const sugar = m.total_nutrition.sugar_g || 0;
          const fiber = m.total_nutrition.fiber_g || 0;
          const sodium = m.total_nutrition.sodium_mg || 0;
          const water = m.total_nutrition.water_ml || 0;
          
          const foodsStr = (m.foods || []).map(f => {
            const qty = f.portion || '';
            return `${f.name}${qty ? ` (${qty})` : ''}: ${f.calories_kcal} kcal, P:${f.protein_g}g, C:${f.carbs_g}g, F:${f.fat_g}g`;
          }).join(' | ');

          const escapedName = `"${name.replace(/"/g, '""')}"`;
          const escapedFoodsStr = `"${foodsStr.replace(/"/g, '""')}"`;

          const row = [
            'Meal',
            formattedDate,
            formattedTime,
            escapedName,
            cals,
            prot,
            carbs,
            fat,
            sugar,
            fiber,
            sodium,
            water,
            escapedFoodsStr
          ];
          csvRows.push(row.join(','));
        } else {
          const w = item as WaterLog;
          const bev = w.beverage_type ? (w.beverage_type.toUpperCase()) : 'Water';
          const qty = w.amount_ml;
          
          const row = [
            'Water/Drink',
            formattedDate,
            formattedTime,
            bev,
            0, 0, 0, 0, 0, 0, 0,
            qty,
            '""'
          ];
          csvRows.push(row.join(','));
        }
      });

      const csvContent = "\uFEFF" + csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nutriscan_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(lang === 'ua' ? 'Дані експортовано в CSV успішно' : 'Data exported to CSV successfully');
    } catch (err) {
      console.error(err);
      toast.error(lang === 'ua' ? 'Помилка експорту CSV' : 'Error exporting CSV');
    }
  };
  const exportAsJSON = () => {
    try {
      const exportData = {
        meals: history,
        waterLogs: waterLogs,
        exportedAt: new Date().toISOString(),
        appVersion: "0.9.7"
      };

      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nutriscan_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(lang === 'ua' ? 'Дані експортовано в JSON успішно' : 'Data exported to JSON successfully');
    } catch (err) {
      console.error(err);
      toast.error(lang === 'ua' ? 'Помилка експорту JSON' : 'Error exporting JSON');
    }
  };
  const exportAsPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      let y = 15;

      const checkPageOverflow = (neededHeight: number) => {
        if (y + neededHeight > 275) {
          doc.addPage();
          y = 15;
          doc.setFillColor(244, 244, 245);
          doc.rect(10, 10, 190, 8, 'F');
          doc.setFont('Helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(113, 113, 122);
          doc.text(transliterate(lang === 'ua' ? 'ЗВІТ NUTRISCAN - ПРОДОВЖЕННЯ' : 'NUTRISCAN HEALTH REPORT - CONTINUED'), 12, 16);
          y = 25;
        }
      };

      doc.setFillColor(16, 185, 129);
      doc.rect(10, y, 190, 20, 'F');
      
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(transliterate(lang === 'ua' ? 'NUTRISCAN - ЗВІТ ЗДОРОВ\'Я ТА ХАРЧУВАННЯ' : 'NUTRISCAN - NUTRITION & HEALTH REPORT'), 15, y + 12);
      
      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(new Date().toLocaleDateString(), 170, y + 12);
      
      y += 26;

      doc.setFillColor(244, 244, 245);
      doc.rect(10, y, 190, 26, 'F');
      
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(24, 24, 27);
      doc.text(transliterate(lang === 'ua' ? 'ПРОФІЛЬ : ПАРАМЕТРИ ЗДОРОВ\'Я' : 'USER HEALTH PARAMETERS:'), 15, y + 6);
      
      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(63, 63, 70);
      const nameString = `User: ${userName || 'Active member'}`;
      doc.text(transliterate(nameString), 15, y + 14);
      doc.text(`Export Date: ${new Date().toISOString().split('T')[0]}`, 15, y + 21);

      const totalCaloriesAll = history.reduce((acc, m) => acc + (m.total_nutrition.calories_kcal || 0), 0);
      const totalWaterAll = waterLogs.reduce((acc, w) => acc + w.amount_ml, 0);
      
      doc.text(transliterate(lang === 'ua' ? `Калорійність: ${totalCaloriesAll} ккал` : `Total Cals: ${totalCaloriesAll} kcal`), 110, y + 14);
      doc.text(transliterate(lang === 'ua' ? `Споживання води: ${totalWaterAll} мл` : `Water Tracked: ${totalWaterAll} ml`), 110, y + 21);

      y += 32;

      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(16, 185, 129);
      doc.text(transliterate(lang === 'ua' ? 'ЖУРНАЛ АКТИВНОСТІ' : 'ACTIVITY LOG HISTORY'), 10, y);
      
      doc.setDrawColor(228, 228, 231);
      doc.line(10, y + 2, 200, y + 2);
      
      y += 8;

      const allActivities: CombinedEntry[] = [
        ...history.map(m => ({ ...m, type: 'meal' as const })),
        ...waterLogs.map(w => ({ ...w, type: 'water' as const }))
      ].sort((a, b) => b.timestamp - a.timestamp);

      if (allActivities.length === 0) {
        doc.setFont('Helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(113, 113, 122);
        doc.text(transliterate(lang === 'ua' ? 'Немає записаних даних.' : 'No activities recorded.'), 10, y);
      } else {
        allActivities.forEach(item => {
          checkPageOverflow(30);

          const dateObj = new Date(item.timestamp);
          const dateStr = dateObj.toLocaleDateString();
          const timeStr = String(dateObj.getHours()).padStart(2, '0') + ':' + String(dateObj.getMinutes()).padStart(2, '0');

          if (item.type === 'meal') {
            const m = item as HistoryEntry;
            const name = m.meal_name || m.meal_type || 'Meal';
            const cals = m.total_nutrition.calories_kcal || 0;
            const prot = m.total_nutrition.protein_g || 0;
            const carbs = m.total_nutrition.carbs_g || 0;
            const fat = m.total_nutrition.fat_g || 0;

            doc.setFillColor(255, 255, 255);
            doc.setDrawColor(228, 228, 231);
            doc.rect(10, y, 190, 24);
            
            doc.setFillColor(16, 185, 129);
            doc.rect(10, y, 3, 24, 'F');

            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(24, 24, 27);
            doc.text(`${dateStr} ${timeStr} - ${transliterate(name.toUpperCase())}`, 16, y + 6);

            doc.setFont('Helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(82, 82, 91);
            doc.text(`Calories: ${cals} kcal | Protein: ${prot}g | Carbs: ${carbs}g | Fat: ${fat}g`, 16, y + 12);

            const listStr = (m.foods || []).map(f => `${f.name} (${f.portion || '1 serving'})`).join(', ');
            const trimmedList = listStr.length > 105 ? listStr.slice(0, 102) + '...' : listStr;
            doc.text(`Foods: ${transliterate(trimmedList)}`, 16, y + 18);

            y += 28;
          } else {
            const w = item as WaterLog;
            const beverage = w.beverage_type ? w.beverage_type.toUpperCase() : 'WATER';
            const qty = w.amount_ml;

            doc.setFillColor(255, 255, 255);
            doc.setDrawColor(228, 228, 231);
            doc.rect(10, y, 190, 16);
            
            doc.setFillColor(14, 165, 233);
            doc.rect(10, y, 3, 16, 'F');

            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(24, 24, 27);
            doc.text(`${dateStr} ${timeStr} - DRINK: ${transliterate(beverage)}`, 16, y + 6);

            doc.setFont('Helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(82, 82, 91);
            doc.text(`Amount: ${qty} ml`, 16, y + 11);

            y += 20;
          }
        });
      }

      doc.save(`nutriscan_export_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(lang === 'ua' ? 'Звіт PDF успішно завантажено' : 'PDF Report downloaded successfully');

    } catch (err) {
      console.error(err);
      toast.error(lang === 'ua' ? 'Помилка експорту PDF' : 'Error exporting PDF');
    }
  };
  const addPendingFood = () => {
    setPendingReviewData(prev => {
      if (!prev) return prev;
      const newItem: FoodItem = {
        name: '',
        portion: '',
        amount: '',
        unit: 'g',
        calories_kcal: 0,
        protein_g: 0,
        carbs_g: 0,
        fat_g: 0,
        sugar_g: 0,
        fiber_g: 0,
        sodium_mg: 0,
        caffeine_mg: 0
      };
      return { ...prev, foods: [...prev.foods, newItem] };
    });
  };
  const handleConfirmReview = async () => {
    if (!pendingReviewData) return;
    
    setIsReCalculating(true);
    const waitDelay = async () => {
      await new Promise(resolve => setTimeout(resolve, 80));
    };

    try {
      const targetTimestamp = getTimestampForSelectedDate(reviewLogDate);

      const foodList = pendingReviewData.foods
        .filter(f => f.name.trim() !== '')
        .map(f => `${f.amount}${f.unit} ${f.name}`)
        .join(', ');
      
      if (foodList.trim() === '') {
        await waitDelay();
        setShowReviewModal(false);
        setIsReCalculating(false);
        return;
      }

      // Check if the foods list actually changed compared to the original pristine copy
      const foodsChanged = (() => {
        if (!originalPendingReviewFoodsRef.current) return true;
        if (pendingReviewData.foods.length !== originalPendingReviewFoodsRef.current.length) return true;
        for (let i = 0; i < pendingReviewData.foods.length; i++) {
          const f1 = pendingReviewData.foods[i];
          const f2 = originalPendingReviewFoodsRef.current[i];
          if (f1.name !== f2.name || f1.amount !== f2.amount || f1.unit !== f2.unit) {
            return true;
          }
        }
        return false;
      })();

      // If nothing changed, we can completely bypass any remote AI or local recalculations.
      if (!foodsChanged) {
        console.log('[REVIEW] Foods did not change. Preserving original analysis.');
        const finalData = pendingReviewData;
        setResult(finalData);
        setOriginalResult(safeClone(finalData));
        setSelectedHistoryItem({
          ...finalData,
          id: 'temp',
          timestamp: targetTimestamp,
          image: pendingReviewData.image || image || '',
          isSuggestion: pendingReviewData.isSuggestion,
        });
        await waitDelay();
        setShowReviewModal(false);
        setView('details');
        setIsReCalculating(false);
        return;
      }

      // Check if we have preloaded analysis data for this exact list of foods
      if (preloadedAnalysis && preloadedFoodListRef.current === foodList) {
        console.log('Using preloaded analysis data for instant details load!');
        const finalData = preloadedAnalysis;
        
        let finalImage = pendingReviewData.image || image || '';
        if (!finalImage && preloadedImage) {
          finalImage = preloadedImage;
          if (!pendingReviewData.isSuggestion) {
            setImage(preloadedImage);
          }
        }

        setResult(finalData);
        setOriginalResult(safeClone(finalData));
        setSelectedHistoryItem({
          ...finalData,
          id: 'temp',
          timestamp: targetTimestamp,
          image: finalImage,
          isSuggestion: pendingReviewData.isSuggestion,
        });
        await waitDelay();
        setShowReviewModal(false);
        setView('details');
        setIsReCalculating(false);
        return;
      }

      if (pendingReviewData.isSuggestion) {
        // Ultra-fast path for suggestions
        const updatedFoods = pendingReviewData.foods.map((food, idx) => {
          const originalFood = originalPendingReviewFoodsRef.current?.[idx];
          if (!originalFood) return { ...food }; // Newly added item

          const origAmt = parseFloat(String(originalFood.amount));
          const currAmt = parseFloat(String(food.amount));

          if (!isNaN(origAmt) && !isNaN(currAmt) && origAmt > 0 && currAmt !== origAmt) {
            const scale = currAmt / origAmt;
            return {
              ...food,
              calories_kcal: Math.round(originalFood.calories_kcal * scale),
              protein_g: Number((originalFood.protein_g * scale).toFixed(1)),
              carbs_g: Number((originalFood.carbs_g * scale).toFixed(1)),
              fat_g: Number((originalFood.fat_g * scale).toFixed(1)),
              sugar_g: Number(((originalFood.sugar_g || 0) * scale).toFixed(1)),
              fiber_g: Number(((originalFood.fiber_g || 0) * scale).toFixed(1)),
              sodium_mg: Math.round((originalFood.sodium_mg || 0) * scale),
              caffeine_mg: Math.round((originalFood.caffeine_mg || 0) * scale),
              water_ml: Math.round((originalFood.water_ml || 0) * scale)
            };
          }
          return { ...food };
        });

        const totals = recalculateTotalNutrition(updatedFoods);

        const suggestionFinalData = postProcessNutrition({
          ...pendingReviewData,
          foods: updatedFoods,
          total_nutrition: totals,
          insight: "" // Force dynamic recalculation to reflect the adjusted portion and new counts
        } as any, lang, vibe, targetTimestamp, ignoreMealTimeLogging, onboardingGender);

        setResult(suggestionFinalData);
        setOriginalResult(safeClone(suggestionFinalData));
        setSelectedHistoryItem({
          ...suggestionFinalData,
          id: 'temp',
          timestamp: targetTimestamp,
          image: pendingReviewData.image || image || '',
          isSuggestion: pendingReviewData.isSuggestion,
        });
        await waitDelay();
        setShowReviewModal(false);
        setView('details');
        setIsReCalculating(false);
        return;
      }

      // Non-suggestion meals
      const updatedAnalysis = await analyzeMealText(`Calculate nutrition for: ${foodList}`, goals, lang, false, vibe, pendingReviewData.meal_type, ignoreMealTimeLogging, safeChoiceProfile, onboardingGender, subscriptionTier, userName);
      
      if (updatedAnalysis.foods && Array.isArray(updatedAnalysis.foods)) {
        updatedAnalysis.foods = updatedAnalysis.foods.map(f => {
          let caff = Number(f.caffeine_mg || 0);
          const isDrink = f.category === 'drink' || /coffee|tea|water|energy|drink/i.test(f.name);
          if (!isDrink || caff > 1000) caff = 0;
          return { ...f, caffeine_mg: caff };
        });
        updatedAnalysis.total_nutrition = recalculateTotalNutrition(updatedAnalysis.foods) as any;
      }

      const getMoreSevereStatus = (status1?: string, status2?: string): 'green' | 'amber' | 'red' | undefined => {
        if (status1 === 'red' || status2 === 'red') return 'red';
        if (status1 === 'amber' || status2 === 'amber') return 'amber';
        if (status1 === 'green' || status2 === 'green') return 'green';
        return undefined;
      };

      const finalStatus = getMoreSevereStatus(pendingReviewData.safe_choice_status, updatedAnalysis.safe_choice_status);
      let finalMessage = updatedAnalysis.safe_choice_message;
      let finalRiskComponents = updatedAnalysis.detected_risk_components;

      if (finalStatus === pendingReviewData.safe_choice_status && pendingReviewData.safe_choice_status !== undefined) {
        finalMessage = pendingReviewData.safe_choice_message;
        finalRiskComponents = pendingReviewData.detected_risk_components;
      }

      const finalData = postProcessNutrition({
        ...updatedAnalysis,
        meal_name: updatedAnalysis.meal_name || pendingReviewData.meal_name,
        meal_type: pendingReviewData.meal_type,
        safe_choice_status: finalStatus,
        safe_choice_message: finalMessage,
        detected_risk_components: finalRiskComponents
      } as any, lang, vibe);
      
      setResult(finalData);
      setOriginalResult(safeClone(finalData));
      setSelectedHistoryItem({
        ...finalData,
        id: 'temp',
        timestamp: targetTimestamp,
        image: pendingReviewData.image || image || '',
        isSuggestion: pendingReviewData.isSuggestion,
      });
      await waitDelay();
      setShowReviewModal(false);
      setView('details');
    } catch (err) {
      console.error("Failed to re-calculate:", err);
      const processedFallback = postProcessNutrition({
        ...pendingReviewData,
        insight: ""
      } as any, lang, vibe);

      setResult(processedFallback);
      setOriginalResult(safeClone(processedFallback));
      setSelectedHistoryItem({
        ...processedFallback,
        id: 'temp',
        timestamp: getTimestampForSelectedDate(reviewLogDate),
        image: pendingReviewData.image || image || '',
        isSuggestion: pendingReviewData.isSuggestion,
      });
      await waitDelay();
      setShowReviewModal(false);
      setView('details');
    } finally {
      setIsReCalculating(false);
    }
  };
  const setUserResidence = (res: 'US' | 'UK' | 'UA' | 'GLOBAL') => {
    setUserResidenceState(res);
    localStorage.setItem('user_residence', res);
    const defaults = getDefaultSelectedDrinkIds(res);
    setSelectedDrinkIdsState(defaults);
    localStorage.setItem('selected_drink_ids', JSON.stringify(defaults));
    syncSettings({ userResidence: res, selectedDrinkIds: defaults });
    sounds.playPop();
  };
  const setIsAge18Plus = (val: boolean) => {
    setIsAge18PlusState(val);
    localStorage.setItem('is_age_18_plus', String(val));
    syncSettings({ isAge18Plus: val });
    sounds.playSave();
    toast.success(
      val 
        ? (lang === 'ua' ? 'Енергетичні напої дозволено (18+)' : 'Energy drinks enabled (18+)')
        : (lang === 'ua' ? 'Енергетичні напої приховано' : 'Energy drinks restricted')
    );
  };
  const setSelectedDrinkIds = (ids: string[]) => {
    const truncated = ids.slice(0, 10);
    setSelectedDrinkIdsState(truncated);
    localStorage.setItem('selected_drink_ids', JSON.stringify(truncated));
    syncSettings({ selectedDrinkIds: truncated });
  };
  const addCustomDrink = (drink: CustomDrinkOption) => {
    const updated = [...customUserDrinks, drink];
    setCustomUserDrinksState(updated);
    localStorage.setItem('custom_user_drinks', JSON.stringify(updated));
    syncSettings({ customUserDrinks: updated });
    if (selectedDrinkIds.length < 10) {
      setSelectedDrinkIds([...selectedDrinkIds, drink.id]);
    }
  };
  const removeCustomDrink = (id: string) => {
    const updated = customUserDrinks.filter(d => d.id !== id);
    setCustomUserDrinksState(updated);
    localStorage.setItem('custom_user_drinks', JSON.stringify(updated));
    syncSettings({ customUserDrinks: updated });
    setSelectedDrinkIds(selectedDrinkIds.filter(dId => dId !== id));
  };
  const handleUpgradeSubscription = async (tier: 'free' | 'pro' | 'premium', interval: 'month' | 'year' = 'month') => {
    triggerHaptic('SUCCESS');
    if (isAdmin) {
      setSubscriptionTier(tier);
      localStorage.setItem('subscription_tier', tier);
      syncSettings({ subscriptionTier: tier, subscriptionTrialActive: false });
      toast.success(
        lang === 'ua' 
          ? `[Admin] Тариф переключено на ${tier.toUpperCase()}` 
          : `[Admin] Switched tier to ${tier.toUpperCase()}`
      );
      return;
    }

    if (tier === 'free') {
      setSubscriptionTier('free');
      localStorage.setItem('subscription_tier', 'free');
      syncSettings({ subscriptionTier: 'free', subscriptionTrialActive: false });
      toast.success(
        lang === 'ua' 
          ? "Повернуто на безкоштовний тариф" 
          : "Switched to Free Tier"
      );
      return;
    }

    setCheckoutTier(tier);
    setIsCheckoutModalOpen(true);
    setIsProcessingCheckout(true);
    
    try {
      const checkoutRes = await createStripeCheckoutSession(tier, interval);
      if (checkoutRes && checkoutRes.url) {
        // Redirect client directly to Stripe hosted checkout page
        window.location.href = checkoutRes.url;
        return;
      }
    } catch (err: any) {
      console.warn("[STRIPE] Hosted checkout init note:", err.message || err);
    } finally {
      setIsProcessingCheckout(false);
      setIsCheckoutModalOpen(false);
    }

    toast.info(
      lang === 'ua' 
        ? "Платні підписки незабаром з'являться! Наразі вам доступні всі можливості безкоштовного тарифу." 
        : "Paid subscriptions are coming soon! In the meantime, you have full access to all Free tier features."
    );
  };
  const handleOpenBillingPortal = async () => {
    try {
      toast.loading(lang === 'ua' ? 'Відкриття кабінету Stripe...' : 'Opening Stripe portal...', { id: 'portal-toast' });
      const portalRes = await createStripePortalSession();
      toast.dismiss('portal-toast');
      if (portalRes && portalRes.url) {
        window.location.href = portalRes.url;
      }
    } catch (err: any) {
      toast.dismiss('portal-toast');
      toast.info(
        lang === 'ua' 
          ? "Платіжний кабінет стане доступним після відкриття підписок." 
          : "Billing portal will become available once paid subscriptions launch."
      );
    }
  };
  const handleActivateTrialToken = () => {
    triggerHaptic('SUCCESS');
    if (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') {
      toast.info(
        lang === 'ua'
          ? "У вас уже діє тариф PREMIUM! 🥗"
          : "You are already on the active PREMIUM membership plan! 🥗"
      );
      return;
    }
    if (isAdmin) {
      setIsTrialActive(false);
      localStorage.setItem('subscription_trial_active', 'false');
      setSubscriptionTier('premium');
      localStorage.setItem('subscription_tier', 'premium');
      syncSettings({ subscriptionTier: 'premium', subscriptionTrialActive: false });
      
      if (typeof confetti === 'function') {
        confetti({
          particleCount: 100,
          spread: 60,
          origin: { y: 0.6 }
        });
      }
      toast.success(
        lang === 'ua'
          ? "Ваш акаунт переведено на тариф PREMIUM! 🥗"
          : "Your account is active on the PREMIUM membership tier! 🥗"
      );
      return;
    }

    toast.info(
      lang === 'ua'
        ? "Пробний період та платні тарифи незабаром стануть доступними. Насолоджуйтесь безкоштовним тарифом!"
        : "Free trials & paid plans are coming soon. Enjoy the Free tier!"
    );
  };
  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(lang === 'ua' ? 'Розмір файлу має бути до 5 МБ' : 'File size must be under 5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        setCustomAvatar(result);
        setAvatarImageError(false);
        localStorage.setItem('custom_avatar', result);
        syncSettings({ customAvatar: result });
        toast.success(lang === 'ua' ? 'Аватар оновлено!' : 'Avatar updated!');
      }
    };
    reader.readAsDataURL(file);
  };
  const saveScannedProduct = async (product: ScannedProduct) => {
    setScannedProducts(prev => {
      const filtered = prev.filter(p => p.barcode !== product.barcode);
      const updated = [product, ...filtered];
      localStorage.setItem('scanned_products', JSON.stringify(safeClone(updated)));
      return updated;
    });

    if (user && !isQuotaExceeded) {
      try {
        await setDocWithQueue(doc(db, 'users', user.uid, 'scanned_products', product.barcode), safeClone(product));
        
        const gamificationLogId = `scan_${product.barcode}_${Date.now()}`;
        await setDocWithQueue(doc(db, 'users', user.uid, 'gamification_logs', gamificationLogId), {
          log_date: new Date().toISOString().split('T')[0],
          macros_hit: false,
          water_ml: 0,
          caffeine_mg: 0,
          scanned_barcodes: [product.barcode],
          additives_flagged: (product as any).ingredients_analysis_tags || [],
          engine_processed: false
        });
        
        evaluateGamificationState();
        console.log('Scanned product synced to Firebase');
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `users/${user.uid}/scanned_products/${product.barcode}`);
      }
    }
  };
  const toggleExpandAllMealItems = () => {
    sounds.playPop();
    if (areAllMealItemsExpanded) {
      setExpandedMealLogIds([]);
    } else {
      setExpandedMealLogIds(onscreenMealItems.map(item => item.id));
    }
  };
  const handleVoiceNoteTranscription = async (audioBlob: Blob) => {
    setIsTranscribingVoiceNote(true);
    const toastId = toast.loading(lang === 'ua' ? 'Розпізнавання голосу за допомогою AI...' : 'Transcribing voice note using AI...');

    try {
      // Convert Blob to Base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        try {
          const base64Data = (reader.result as string).split(',')[1];
          const response = await fetchWrapper('/api/ai/transcribe', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: safeStringify({
              audio: base64Data,
              mimeType: audioBlob.type || 'audio/webm'
            })
          });

          if (!response.ok) {
            const errData = await safeJsonResponse(response);
            throw new Error(errData.error || 'Transcription request failed');
          }

          const data = await safeJsonResponse(response);
          const transcribedText = data.text?.trim() || '';

          toast.dismiss(toastId);
          if (transcribedText) {
            setChatInput(transcribedText);
            sounds.playPop();
            toast.success(lang === 'ua' ? 'Голос успішно розпізнано!' : 'Voice transcribed successfully!');
          } else {
            toast.error(lang === 'ua' ? 'Не вдалося почути мовлення. Спробуйте ще раз.' : 'Could not hear any speech. Please try again.');
          }
        } catch (err: any) {
          console.error('Transcription error:', err);
          toast.dismiss(toastId);
          toast.error(lang === 'ua' ? `Помилка розпізнавання: ${err.message}` : `Transcription error: ${err.message}`);
        } finally {
          setIsTranscribingVoiceNote(false);
        }
      };
    } catch (err: any) {
      console.error('Failed to read audio blob:', err);
      toast.dismiss(toastId);
      toast.error(lang === 'ua' ? 'Не вдалося обробити аудіофайл.' : 'Failed to process audio file.');
      setIsTranscribingVoiceNote(false);
    }
  };
  const startVoiceNoteRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      
      let options = {};
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        options = { mimeType: 'audio/webm' };
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        options = { mimeType: 'audio/mp4' };
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        options = { mimeType: 'audio/ogg' };
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        options = { mimeType: 'audio/wav' };
      }

      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await handleVoiceNoteTranscription(audioBlob);
      };

      recorder.start();
      setIsListening(true);
      setVoiceNoteDuration(15);
      sounds.playPop();

      voiceNoteTimerRef.current = setInterval(() => {
        setVoiceNoteDuration(prev => {
          if (prev <= 1) {
            if (voiceNoteTimerRef.current) clearInterval(voiceNoteTimerRef.current);
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
              mediaRecorderRef.current.stop();
            }
            setIsListening(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err) {
      console.error('Failed to start recording:', err);
      toast.error(lang === 'ua' ? 'Не вдалося отримати доступ до мікрофона.' : 'Could not access the microphone.');
    }
  };
  const stopVoiceNoteRecording = () => {
    if (voiceNoteTimerRef.current) {
      clearInterval(voiceNoteTimerRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
  };
  const toggleListening = () => {
    if (isListening) {
      stopVoiceNoteRecording();
    } else {
      startVoiceNoteRecording();
    }
  };
  const handleMutualLikeUnlocked = async (msgTimestamp: number) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const logId = `mutual_like_${msgTimestamp}`;
    
    const newCount = mutualLikesCount + 1;
    setMutualLikesCount(newCount);
    localStorage.setItem('mutual_likes_count', String(newCount));
    
    try {
      // 1. Write gamification log
      await setDocWithQueue(doc(db, 'users', uid, 'gamification_logs', logId), {
        log_date: new Date().toISOString().split('T')[0],
        macros_hit: false,
        water_ml: 0,
        caffeine_mg: 0,
        scanned_barcodes: [],
        additives_flagged: [],
        is_mutual_like: true,
        engine_processed: false
      });
      
      // 2. Increment in user document
      await setDocWithQueue(doc(db, 'users', uid), {
        gamification: {
          mutual_likes_count: newCount
        }
      }, { merge: true });
      
      // 3. Trigger evaluation
      evaluateGamificationState();
    } catch (err) {
      console.error('Failed to register mutual like:', err);
    }
  };
  const handleMutualLikeRemoved = async (msgTimestamp: number) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const logId = `mutual_like_${msgTimestamp}`;
    
    const newCount = Math.max(0, mutualLikesCount - 1);
    setMutualLikesCount(newCount);
    localStorage.setItem('mutual_likes_count', String(newCount));
    
    try {
      // 1. Delete from gamification_logs
      await deleteDocWithQueue(doc(db, 'users', uid, 'gamification_logs', logId));
      
      // 2. Decrement in user document
      await setDocWithQueue(doc(db, 'users', uid), {
        gamification: {
          mutual_likes_count: newCount
        }
      }, { merge: true });
      
      // 3. Trigger evaluation
      evaluateGamificationState();
    } catch (err) {
      console.error('Failed to remove mutual like:', err);
    }
  };
  const toggleMessageLike = (index: number) => {
    const newMessages = [...chatMessages];
    const targetMsg = newMessages[index];
    
    // Only users can like AI messages
    if (targetMsg.role === 'ai') {
      targetMsg.isLiked = !targetMsg.isLiked;
      sounds.playPop();
      
      // Check for mutual match
      // A match happens if the user likes an AI message AND the AI liked a user message nearby
      if (targetMsg.isLiked) {
        const hasAiLikeNearby = newMessages.some((m, i) => m.role === 'user' && m.aiLiked && Math.abs(i - index) <= 2);
        if (hasAiLikeNearby) {
          triggerMatch();
          handleMutualLikeUnlocked(targetMsg.timestamp);
        }
      } else {
        const hasAiLikeNearby = newMessages.some((m, i) => m.role === 'user' && m.aiLiked && Math.abs(i - index) <= 2);
        if (hasAiLikeNearby) {
          handleMutualLikeRemoved(targetMsg.timestamp);
        }
      }
      
      setChatMessages(newMessages);
      localStorage.setItem('chat_history', JSON.stringify(safeClone(newMessages)));
    }
  };
  const handleChatSubmit = async (e?: React.FormEvent, overrideInput?: string, overrideImage?: string, isResuming = false) => {
    if (e) e.preventDefault();
    if (!isResuming) sounds.playPop();
    const input = (overrideInput || chatInput).trim();
    let imageToUpload = overrideImage || chatImage;
    if (!input && !imageToUpload) return;

    // Resize image if present
    if (imageToUpload && !isResuming) { // Don't re-resize if resuming as it's already resized
      try {
        imageToUpload = await resizeImage(imageToUpload, 1024, 0.85);
      } catch (err) {
        console.warn('Failed to resize chat image:', err);
      }
    }

    const userMsg: ChatMessage = { 
      role: 'user', 
      content: input,
      image: imageToUpload || undefined,
      timestamp: isResuming && overrideInput ? (chatMessages.find(m => m.content === overrideInput)?.timestamp || Date.now()) : Date.now(),
      aiLiked: false,
      isPending: true
    };
    
    if (!isResuming) {
      setChatMessages(prev => [...prev, userMsg]);
      setChatInput('');
      setChatImage(null);
    }
    setIsAnalyzingText(true);
    setError(null);

    try {
      // Use prior conversation history (excluding the current pending message)
      const currentHistory = isResuming 
        ? chatMessages.filter(m => !m.isPending && m.timestamp !== userMsg.timestamp) 
        : chatMessages;
      // Keep only the last 8 messages of history to control input token costs
      const limitedHistory = currentHistory.slice(-8);
      const chatHistory = limitedHistory.map(m => {
        const parts: any[] = [];
        if (m.content && m.content.trim()) {
          parts.push({ text: m.content });
        } else if (m.image) {
          parts.push({ text: t.sentAnImage || "[Sent an image]" });
        } else {
          parts.push({ text: "..." });
        }

        return {
          role: m.role === 'user' ? 'user' as const : 'model' as const,
          parts
        };
      });

      let response: any;
      try {
        response = await processAIChat(input, chatHistory, goals, history, lang, imageToUpload || undefined, vibe, safeChoiceProfile, subscriptionTier, userName, onboardingGender);
      } catch (firstErr: any) {
        const firstErrMsg = firstErr?.message || String(firstErr);
        if (firstErrMsg.includes("UNAUTHORIZED") || firstErrMsg.includes("Unauthorized") || firstErrMsg.includes("access token") || firstErrMsg.includes("TOKEN_EXPIRED")) {
          console.warn("handleChatSubmit: Session issue detected, performing transparent re-authentication and retrying AI chat...");
          const activeUser = user || auth.currentUser;
          if (activeUser) {
            await forceRecreateSessionWithFirebase(true).catch(() => "");
          }
          await asyncGetAccessToken(true).catch(() => "");
          response = await processAIChat(input, chatHistory, goals, history, lang, imageToUpload || undefined, vibe, safeChoiceProfile, subscriptionTier, userName, onboardingGender);
        } else if (
          firstErrMsg.includes("SERVER_OFFLINE") || 
          firstErrMsg.includes("SERVER_ENDPOINT_NOT_FOUND") || 
          firstErrMsg.includes("Server API unavailable") ||
          firstErrMsg.includes("SERVER_OVERLOADED") ||
          firstErrMsg.includes("UNAVAILABLE") ||
          firstErrMsg.includes("Service unavailable") ||
          firstErrMsg.includes("Service Unavailable") ||
          firstErrMsg.includes("503") ||
          firstErrMsg.includes("502") ||
          firstErrMsg.includes("overloaded")
        ) {
          console.warn("handleChatSubmit: Server offline, overloaded, or cold start detected, transparently retrying after 1.8s delay...");
          await new Promise(r => setTimeout(r, 1800));
          response = await processAIChat(input, chatHistory, goals, history, lang, imageToUpload || undefined, vibe, safeChoiceProfile, subscriptionTier, userName, onboardingGender);
        } else {
          throw firstErr;
        }
      }
      
      // Update user message to not be pending anymore
      setChatMessages(prev => {
        const newMessages = [...prev];
        const msgIndex = newMessages.findIndex(m => 
          m.role === 'user' && 
          m.timestamp === userMsg.timestamp && 
          m.content === userMsg.content &&
          m.isPending
        );
        if (msgIndex !== -1) {
          newMessages[msgIndex] = { ...newMessages[msgIndex], isPending: false, aiLiked: response.aiLiked || false };
        }
        return newMessages;
      });

      if (response.mealAnalysis && response.mealAnalysis.foods && Array.isArray(response.mealAnalysis.foods) && response.mealAnalysis.foods.length > 0) {
        try {
          playScanCompleteSound();
          // Ensure total_nutrition is calculated with exact mathematical precision from the detected foods
          const calculatedTotals = recalculateTotalNutrition(response.mealAnalysis.foods);
          
          // Automatically suggest a better meal name if generic
          const nameIsGeneric = !response.mealAnalysis.meal_name || 
                               /^(meal|food|dish|analysed meal|dinner|lunch|breakfast|snack)$/i.test(response.mealAnalysis.meal_name);
          
          if (nameIsGeneric && response.mealAnalysis.foods.length > 1) {
            response.mealAnalysis.meal_name = generateMealName(response.mealAnalysis.foods);
          }

          // Infer meal type if AI didn't provide one or provided 'snack' but time suggests a main meal
          if (!response.mealAnalysis.meal_type || response.mealAnalysis.meal_type === 'snack') {
            const inferred = inferMealType(response.mealAnalysis.foods);
            // Only override if the AI explicitly returned nothing or if we're doing a context check
            if (!response.mealAnalysis.meal_type) {
              response.mealAnalysis.meal_type = inferred;
            }
          }

          response.mealAnalysis.total_nutrition = {
            ...response.mealAnalysis.total_nutrition,
            ...calculatedTotals,
            calories_kcal: calculatedTotals.calories_kcal || response.mealAnalysis.total_nutrition?.calories_kcal || 0,
            protein_g: calculatedTotals.protein_g !== undefined ? calculatedTotals.protein_g : (response.mealAnalysis.total_nutrition?.protein_g || 0),
            carbs_g: calculatedTotals.carbs_g !== undefined ? calculatedTotals.carbs_g : (response.mealAnalysis.total_nutrition?.carbs_g || 0),
            fat_g: calculatedTotals.fat_g !== undefined ? calculatedTotals.fat_g : (response.mealAnalysis.total_nutrition?.fat_g || 0),
            sugar_g: calculatedTotals.sugar_g !== undefined ? calculatedTotals.sugar_g : (response.mealAnalysis.total_nutrition?.sugar_g || 0),
            fiber_g: calculatedTotals.fiber_g !== undefined ? calculatedTotals.fiber_g : (response.mealAnalysis.total_nutrition?.fiber_g || 0),
            sodium_mg: calculatedTotals.sodium_mg !== undefined ? calculatedTotals.sodium_mg : (response.mealAnalysis.total_nutrition?.sodium_mg || 0),
            caffeine_mg: calculatedTotals.caffeine_mg !== undefined ? calculatedTotals.caffeine_mg : (response.mealAnalysis.total_nutrition?.caffeine_mg || 0),
            water_ml: calculatedTotals.water_ml !== undefined ? calculatedTotals.water_ml : (response.mealAnalysis.total_nutrition?.water_ml || 0),
          };
        } catch (calcErr) {
          console.warn("Non-fatal nutrition recalculation warning:", calcErr);
        }
      }
      
      const aiMsg: ChatMessage = { 
        role: 'ai', 
        content: response?.text || (lang === 'ua' ? "Ось моя відповідь!" : "Here is my response!"),
        mealAnalysis: response.mealAnalysis,
        timestamp: Date.now(),
        animating: imageToUpload ? false : true,
        nutriAgentOrder: response.nutriAgentOrder || null
      };
      
      let nextIdx = -1;
      setChatMessages(prev => {
        const newMsgs = [...prev, aiMsg];
        nextIdx = newMsgs.length - 1;
        setLastChatUndoAction({
          type: 'message',
          messageIndex: newMsgs.length - 1,
          timestamp: Date.now()
        });
        return newMsgs;
      });

      if (response.mealAnalysis && response.mealAnalysis.foods && imageToUpload) {
        if (enableMealReview) {
          originalPendingReviewFoodsRef.current = safeClone(response.mealAnalysis.foods);
          setPendingReviewData({
            ...response.mealAnalysis,
            image: imageToUpload || ''
          } as any);
          
          const currentMsgsLength = chatMessages.length;
          const targetIdx = currentMsgsLength + (isResuming ? 0 : 1);
          setSourceChatMessageIndex(targetIdx);
          // Show the result back in the chat together with the AI response first.
          // The user can click "Review" or "Log" directly inside the chat inline block!
          // setShowReviewModal(true);
        }
      }
    } catch (err: any) {
      const errorMsg = err?.message || String(err);
      console.error('Chat analysis error:', err);
      
      if (errorMsg === "BILLING_DENIED" || errorMsg.includes("dunning") || errorMsg.includes("Lightning dunning")) {
        setError(lang === 'ua' 
          ? "Помилка оплати API: платіжний статус проекту Google Cloud заблоковано або призупинено (dunning). Будь ласка, перевірте налаштування білінгу в консолі GCP або оновіть ключ GEMINI_API_KEY у налаштуваннях." 
          : "API Billing Suspended: The Google Cloud project's billing status has been denied or suspended (Lightning dunning). Please check your GCP Console billing settings or update your GEMINI_API_KEY in the Settings.");
      } else if (errorMsg === "QUOTA_EXCEEDED") {
        setError(lang === 'ua' ? "Перевищено ліміт запитів до API. Будь ласка, спробуйте пізніше або перевірте налаштування квоти." : "API quota exceeded. Please try again later or check your billing details.");
      } else if (
        errorMsg === "SERVER_OVERLOADED" || 
        errorMsg.includes("SERVER_OVERLOADED") || 
        errorMsg.includes("overloaded") || 
        errorMsg.includes("503") || 
        errorMsg.includes("UNAVAILABLE") || 
        errorMsg.includes("Service unavailable") || 
        errorMsg.includes("Service Unavailable")
      ) {
        setError(lang === 'ua' ? "Сервер перевантажений через високий попит або тимчасово недоступний. Будь ласка, зачекайте хвилинку і спробуйте ще раз." : "The AI model is currently experiencing high demand or is temporarily unavailable. Please wait a moment and try again.");
      } else if (errorMsg.includes("SAFETY_BLOCKED")) {
        setError(lang === 'ua' ? "Служба безпеки штучного інтелекту заблокувала відповідь. Спробуйте змінити опис." : "The AI security service blocked the response. Please try rephrasing your description.");
      } else if (errorMsg.includes("SERVER_ENDPOINT_NOT_FOUND") || errorMsg.includes("SERVER_OFFLINE") || errorMsg.includes("Server API unavailable") || errorMsg.includes("Unexpected token") || errorMsg.includes("Failed to execute 'json'")) {
        setError(lang === 'ua' ? "Сервер тимчасово недоступний. Спробуй ще раз за мить." : "Server temporarily unavailable. Please try again in a moment.");
      } else if (errorMsg.includes("UNAUTHORIZED") || errorMsg.includes("Unauthorized") || errorMsg.includes("access token") || errorMsg.includes("TOKEN_EXPIRED")) {
        setError(lang === 'ua' ? "Тимчасова помилка авторизації сесії. Спробуйте надіслати ще раз." : "Session sync issue. Please try sending your message again.");
      } else if (errorMsg.includes("Forbidden") || errorMsg.includes("FORBIDDEN") || errorMsg.includes("403") || errorMsg.includes("PERMISSION_DENIED") || errorMsg.includes("API_KEY_MISSING") || errorMsg.includes("INVALID_API_KEY") || errorMsg.includes("GEMINI_API_KEY")) {
        setError(lang === 'ua' ? "Помилка доступу Gemini API (403 Forbidden). Перевірте GEMINI_API_KEY у налаштуваннях середовища або консолі Google Cloud." : "Gemini API Access Error (403 Forbidden). Please verify GEMINI_API_KEY in environment settings or GCP console.");
      } else {
        setError(lang === 'ua' ? `Помилка: ${errorMsg.length > 60 ? errorMsg.substring(0, 60) + '...' : errorMsg}` : `Error: ${errorMsg.length > 60 ? errorMsg.substring(0, 60) + '...' : errorMsg}`);
      }
      
      const isOverloadedOrUnavailable = 
        errorMsg === "SERVER_OVERLOADED" || 
        errorMsg.includes("SERVER_OVERLOADED") || 
        errorMsg.includes("overloaded") || 
        errorMsg.includes("503") || 
        errorMsg.includes("UNAVAILABLE") || 
        errorMsg.includes("Service unavailable") || 
        errorMsg.includes("Service Unavailable");

      const userFriendlyError = errorMsg.includes("SAFETY_BLOCKED") 
        ? (lang === 'ua' ? "Вибачте, зміст вашого повідомлення було заблоковано фільтрами безпеки." : "Sorry, your message content was blocked by safety filters.")
        : (errorMsg === "BILLING_DENIED" || errorMsg.includes("dunning") || errorMsg.includes("Lightning dunning"))
          ? (lang === 'ua' ? "Помилка оплати API: платіжний статус заблоковано або призупинено (dunning). Будь ласка, перевірте консоль GCP або оновіть свій GEMINI_API_KEY." : "API Billing Suspended: The Google Cloud project's billing status has been suspended. Please check your GCP Console or update your GEMINI_API_KEY.")
          : (errorMsg.includes("Forbidden") || errorMsg.includes("FORBIDDEN") || errorMsg.includes("403") || errorMsg.includes("PERMISSION_DENIED"))
            ? (lang === 'ua' ? "Помилка доступу API (403 Forbidden). Перевірте наявність та дозволи GEMINI_API_KEY у середовищі сервера." : "API Permission Error (403 Forbidden). Please verify GEMINI_API_KEY configuration and GCP permissions.")
            : isOverloadedOrUnavailable
              ? (lang === 'ua' ? "Сервер штучного інтелекту тимчасово перевантажений або оновлюється. Будь ласка, зачекайте кілька секунд і надішліть повідомлення знову." : "The AI service is temporarily unavailable or experiencing high demand. Please wait a moment and try sending your message again.")
              : (errorMsg.includes("SERVER_ENDPOINT_NOT_FOUND") || errorMsg.includes("SERVER_OFFLINE") || errorMsg.includes("Server API unavailable") || errorMsg.includes("Unexpected token") || errorMsg.includes("Failed to execute 'json'"))
                ? (lang === 'ua' ? "Сервер тимчасово недоступний. Спробуй ще раз за мить!" : "Server temporarily unavailable. Please try again in a moment!")
                : (errorMsg.includes("UNAUTHORIZED") || errorMsg.includes("Unauthorized") || errorMsg.includes("access token") || errorMsg.includes("TOKEN_EXPIRED"))
                  ? ((auth.currentUser || user) 
                      ? (lang === 'ua' ? "Сесію оновлено. Будь ласка, натисніть щоб надіслати знову." : "Session sync refreshed. Please tap to try sending again.")
                      : (lang === 'ua' ? "Для спілкування з AI-асистентом, будь ласка, увійдіть у свій акаунт:\n\n[🔑 Увійти в акаунт](#login)" : "To chat with the AI assistant, please sign in to your account:\n\n[🔑 Log In to Account](#login)"))
                  : (errorMsg.startsWith("API_ERROR") || errorMsg.startsWith("INVALID_API_KEY") || errorMsg.includes("GEMINI_API_KEY") || errorMsg.includes("API_KEY_MISSING"))
                    ? (lang === 'ua' ? "Помилка конфігурації API Gemini на сервері. Зверніться до підтримки." : "Gemini API configuration error on server. Please contact support.")
                    : (errorMsg.includes("Failed to fetch") || errorMsg.includes("NetworkError"))
                      ? (lang === 'ua' ? "Помилка мережі. Будь ласка, перевірте ваше інтернет-з'єднання." : "Network error. Please check your internet connection and try again.")
                      : (lang === 'ua' ? `Вибачте, сталася помилка: ${errorMsg.length > 80 ? errorMsg.substring(0, 80) + '...' : errorMsg}` : `Sorry, an error occurred: ${errorMsg.length > 80 ? errorMsg.substring(0, 80) + '...' : errorMsg}`);

      const errorAiMsg: ChatMessage = {
        role: 'ai',
        content: isOverloadedOrUnavailable
          ? (lang === 'ua' ? "Сервер штучного інтелекту тимчасово перевантажений або оновлюється. Будь ласка, зачекайте кілька секунд і надішліть повідомлення знову." : "The AI service is temporarily unavailable or experiencing high demand. Please wait a moment and try sending your message again.")
          : userFriendlyError,
        timestamp: Date.now(),
        animating: true
      };
      setChatMessages(prev => {
        // Clear pending status on catching error too
        const cleanedMsgs = prev.map(m => 
          (m.role === 'user' && m.timestamp === userMsg.timestamp && m.isPending)
          ? { ...m, isPending: false, aiLiked: false }
          : m
        );
        return [...cleanedMsgs, errorAiMsg];
      });
    } finally {
      setIsAnalyzingText(false);
    }
  };
  const saveGoals = async (
    newGoals: UserGoals, 
    newName?: string, 
    keepViewOpen?: boolean, 
    overrides?: {
      gender?: string;
      age?: string;
      weight?: string;
      height?: string;
      activity?: number;
      goal?: string;
      weightToLose?: string;
      foodPreferences?: string;
      foodBudget?: string;
    }
  ) => {
    setGoals(newGoals);
    localStorage.setItem('user_goals', JSON.stringify(safeClone(newGoals)));
    const nameToSave = newName !== undefined ? newName : userName;
    if (newName !== undefined) {
      setUserName(newName);
      setOnboardingName(newName);
      localStorage.setItem('userName', newName);
    }
    
    const prefToSave = overrides?.foodPreferences !== undefined ? overrides.foodPreferences : foodPreferences;
    const budgetToSave = overrides?.foodBudget !== undefined ? overrides.foodBudget : foodBudget;
    localStorage.setItem('food_preferences', prefToSave);
    localStorage.setItem('food_budget', budgetToSave);
    
    const genderToSave = overrides?.gender !== undefined ? overrides.gender : onboardingGender;
    const ageToSave = overrides?.age !== undefined ? overrides.age : onboardingAge;
    const weightToSave = overrides?.weight !== undefined ? overrides.weight : onboardingWeight;
    const heightToSave = overrides?.height !== undefined ? overrides.height : onboardingHeight;
    const activityToSave = overrides?.activity !== undefined ? overrides.activity : onboardingActivity;
    const goalToSave = overrides?.goal !== undefined ? overrides.goal : onboardingGoal;
    const wtLoseToSave = overrides?.weightToLose !== undefined ? overrides.weightToLose : weightToLose;

    localStorage.setItem('onboardingGender', genderToSave);
    localStorage.setItem('onboardingAge', ageToSave);
    localStorage.setItem('onboardingWeight', weightToSave);
    localStorage.setItem('onboardingHeight', heightToSave);
    localStorage.setItem('onboardingActivity', String(activityToSave));
    localStorage.setItem('onboardingGoal', goalToSave);
    localStorage.setItem('weightToLose', wtLoseToSave);

    // Save illumination settings
    localStorage.setItem('app_illumination', String(illumination));
    setIlluminationColor(previewIlluminationColor);
    localStorage.setItem('app_illumination_color', previewIlluminationColor);
    
    const metricsSummary = `User physical profile: ${genderToSave} ${ageToSave}yo, ${weightToSave}kg, ${heightToSave}cm, Activity: ${activityToSave}x, Goal: ${goalToSave}`;
    setUserMetrics(metricsSummary);
    localStorage.setItem('user_metrics_summary', metricsSummary);
    
    if (user) {
      const userPath = `users/${user.uid}`;
      try {
        await setDocWithQueue(doc(db, 'users', user.uid), { 
          goals: newGoals,
          userName: nameToSave,
          foodPreferences: prefToSave,
          foodBudget: budgetToSave,
          metrics: {
            gender: genderToSave,
            age: parseFloat(ageToSave) || 25,
            weight: parseFloat(weightToSave) || 70,
            height: parseFloat(heightToSave) || 170,
            activity: activityToSave,
            goal: goalToSave
          },
          weightToLose: wtLoseToSave,
          illumination: illumination,
          illuminationColor: previewIlluminationColor,
          metricsSummary
        }, { merge: true });
      } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, userPath);
      }
    }
    if (!keepViewOpen) {
      setView('home');
      sounds.playConfetti();
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.8 },
      });
    }
    toast.success(t.settingsSaved, {
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
      className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold",
    });
  };
  const handleGoalsFormChange = async (formElement: HTMLFormElement) => {
    const formData = new FormData(formElement);
    const newName = (formData.get('userName') as string) || onboardingName || userName || '';
    const foodPrefs = formData.get('foodPreferences') !== null ? (formData.get('foodPreferences') as string) : foodPreferences;
    const foodBgt = formData.get('foodBudget') !== null ? (formData.get('foodBudget') as string) : foodBudget;
    
    const parseField = (val: FormDataEntryValue | null, fallback: number) => {
      if (val === null) return fallback;
      const str = String(val).trim();
      if (str === '') return 0;
      const num = Number(str);
      return isNaN(num) ? fallback : Math.max(0, num);
    };

    await saveGoals({
      targetMode: (formData.get('targetMode') as 'daily' | 'weekly') || rawGoals.targetMode || 'daily',
      calories: parseField(formData.get('calories'), rawGoals.calories),
      protein: parseField(formData.get('protein'), rawGoals.protein),
      carbs: parseField(formData.get('carbs'), rawGoals.carbs),
      fat: parseField(formData.get('fat'), rawGoals.fat),
      water_ml: parseField(formData.get('water_ml'), rawGoals.water_ml),
      sugar_g: parseField(formData.get('sugar_g'), rawGoals.sugar_g),
      fiber_g: parseField(formData.get('fiber_g'), rawGoals.fiber_g),
      sodium_mg: parseField(formData.get('sodium_mg'), rawGoals.sodium_mg),
      caffeine_mg: parseField(formData.get('caffeine_mg'), rawGoals.caffeine_mg),
    }, newName, true, {
      foodPreferences: foodPrefs,
      foodBudget: foodBgt
    });
  };
  const toggleTimeFormat = (target?: '12h' | '24h') => {
    const newFormat = target || (timeFormat === '12h' ? '24h' : '12h');
    if (target && newFormat === timeFormat) return;
    setTimeFormat(newFormat);
    localStorage.setItem('time_format', newFormat);
    syncSettings({ timeFormat: newFormat });
    sounds.playSave();
    toast.success(t.settingsSaved, {
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
      className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold",
    });
  };
  const toggleVibe = (target?: 'standard' | 'casual') => {
    const newVibe = target || (vibe === 'casual' ? 'standard' : 'casual');
    if (target && newVibe === vibe) return;
    setVibe(newVibe);
    localStorage.setItem('app_vibe', newVibe);
    syncSettings({ vibe: newVibe });
    sounds.playSave();
    toast.success(t.settingsSaved, {
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
      className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold",
    });
  };
  const handleBarcodeScan = async (decodedText: string) => {
    if (isScanning || scannedActiveProduct) return;

    // Check if the scanned string is a URL / Quick link / QR code menu link
    if (decodedText.startsWith('http://') || decodedText.startsWith('https://')) {
      toast.success(lang.startsWith('ua') ? 'QR-код розпізнано! Відкриваємо посилання...' : 'QR Link Scanned! Opening link...', { id: 'barcode-status' });
      triggerHaptic("SUCCESS");
      window.open(decodedText, '_blank', 'noopener,noreferrer');
      return;
    }

    // Prevent re-scanning ANY barcode within 5 seconds of a failed scan.
    // This prevents infinite AI fallback loops if the scanner hallucinates noisy barcodes on a meal photo.
    const now = Date.now();
    if ((now - lastFailedScanRef.current.time) < 5000) {
      return;
    }
    if (lastFailedScanRef.current.code === decodedText && (now - lastFailedScanRef.current.time) < 8000) {
      return;
    }

    setIsScanning(true);
    toast.info(t.scanningBarcode, { id: 'barcode-status' });
    
    try {
      const response = await fetchWrapper(`/api/barcode/${encodeURIComponent(decodedText)}`);
      
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Invalid response format from server (non-JSON)");
      }
      
      const data = await safeJsonResponse(response);
      
      if (data.status === 1 && data.product) {
        const product = data.product;
        toast.success(t.productFound, { id: 'barcode-status' });
        
        // Robust name extraction
        const productName = product.product_name || 
                          product.product_name_uk || 
                          product.product_name_en || 
                          product.generic_name || 
                          product.generic_name_uk || 
                          product.generic_name_en || 
                          'Unknown Product';

        // Check if we have any nutritional data at all
        const hasNutriments = product.nutriments && 
                            (product.nutriments['energy-kcal_100g'] || 
                             product.nutriments['energy_100g'] || 
                             product.nutriments.proteins_100g || 
                             product.nutriments.fat_100g);

        // If data is too sparse, we might want to tell the user or try AI
        if (!hasNutriments) {
          console.warn("Barcode found but no nutritional data available.");
        }

        // Try to extract quantity for scaling
        let ratio = 1;
        let amount = 0;
        let unit = 'g';
        
        // OpenFoodFacts provides product_quantity and product_quantity_unit
        if (product.product_quantity && !isNaN(parseFloat(product.product_quantity))) {
          amount = parseFloat(product.product_quantity);
          unit = (product.product_quantity_unit || 'g').toLowerCase();
        } else {
          // Fallback to parsing strings
          const quantityStr = product.quantity || product.serving_size || '';
          const match = quantityStr.match(/([\d.,]+)\s*(kg|g|gr|l|ml)/i);
          if (match) {
            amount = parseFloat(match[1].replace(',', '.'));
            unit = (match[2] || '').toLowerCase();
          }
        }

        if (amount > 0) {
          let normalizedAmount = amount;
          if (unit === 'kg' || unit === 'l') {
            normalizedAmount = amount * 1000;
          }
          // OpenFoodFacts nutriments are per 100g/ml
          ratio = normalizedAmount / 100;
        }

        const finalUnit = (unit === 'ml' || unit === 'l' || (product.quantity && product.quantity.toLowerCase?.().includes('ml'))) ? 'ml' : 'gr';

        // Helper to get nutriment with fallbacks
        const getNutriment = (key: string, fallbackKeys: string[] = []) => {
          if (product.nutriments?.[key] !== undefined) return parseFloat(product.nutriments[key]);
          for (const fallback of fallbackKeys) {
            if (product.nutriments?.[fallback] !== undefined) return parseFloat(product.nutriments[fallback]);
          }
          return 0;
        };

        // Energy can be in kcal or kJ
        let calories = getNutriment('energy-kcal_100g', ['energy-kcal', 'energy_100g_kcal']);
        if (calories === 0 && getNutriment('energy_100g') > 0) {
          // Convert kJ to kcal if only kJ is available
          calories = getNutriment('energy_100g') / 4.184;
        }

        // Caffeine parsing with intelligent heuristics for unlisted fields
        let caffeine_100g = getNutriment('caffeine_100g', ['caffeine', 'caffeine_serving', 'caffeine_value']);
        if (caffeine_100g > 0) {
          const cUnit = product.nutriments?.caffeine_unit || (caffeine_100g < 5 ? 'g' : 'mg');
          if (cUnit === 'g') {
            caffeine_100g = caffeine_100g * 1000;
          }
        }

        const nameLower = productName.toLowerCase();
        let isCaffeinated = false;
        let autoCaffeine100g = 0;

        if (nameLower.includes('energy drink') || nameLower.includes('red bull') || nameLower.includes('monster energy') || nameLower.includes('hell energy') || nameLower.includes('non stop') || nameLower.includes('revo') || nameLower.includes('берн') || nameLower.includes('bern')) {
          isCaffeinated = true;
          autoCaffeine100g = 32; // 32mg per 100ml average
        } else if (nameLower.includes('coffee stick') || nameLower.includes('3в1') || nameLower.includes('3in1') || nameLower.includes('3 в 1') || nameLower.includes('maccoffee') || nameLower.includes('петрівська слобода') || nameLower.includes('jacobs 3') || nameLower.includes('nescafe 3') || nameLower.includes('кава 3') || nameLower.includes('кофе 3')) {
          isCaffeinated = true;
          autoCaffeine100g = 350; // ~50mg per 15g stick
        } else if (nameLower.includes('instant coffee') || nameLower.includes('espresso') || nameLower.includes('nescafe') || nameLower.includes('jacobs') || nameLower.includes('кава розчинна') || nameLower.includes('растворимый кофе') || nameLower.includes('davidoff') || nameLower.includes('lavazza') || nameLower.includes('кава мелена') || nameLower.includes('кофе молотый')) {
          isCaffeinated = true;
          autoCaffeine100g = 2000; // ~2% caffeine for coffee powder/beans
        } else if (nameLower.includes('cola') || nameLower.includes('coke') || nameLower.includes('pepsi') || nameLower.includes('кока-кола') || nameLower.includes('кока кола') || nameLower.includes('пепсі') || nameLower.includes('пепси')) {
          isCaffeinated = true;
          autoCaffeine100g = 10; // ~10mg per 100ml
        } else if (nameLower.includes('black tea') || nameLower.includes('green tea') || nameLower.includes('чорний чай') || nameLower.includes('зелений чай') || nameLower.includes('черный чай') || nameLower.includes('зеленый чай')) {
          isCaffeinated = true;
          autoCaffeine100g = 20; // ~20mg per 100ml
        } else if (nameLower.includes('tea') || nameLower.includes('чай') || nameLower.includes('matcha') || nameLower.includes('матча') || nameLower.includes('mate') || nameLower.includes('мате')) {
          isCaffeinated = true;
          autoCaffeine100g = 15;
        } else if (nameLower.includes('coffee') || nameLower.includes('coffe') || nameLower.includes('кава') || nameLower.includes('кофе') || nameLower.includes('cappuccino') || nameLower.includes('latte') || nameLower.includes('macchiato') || nameLower.includes('капучино') || nameLower.includes('лате') || nameLower.includes('латте') || nameLower.includes('макіато')) {
          isCaffeinated = true;
          autoCaffeine100g = 40; // brewed coffee/latte is around 40mg per 100ml
        }

        if (caffeine_100g === 0 && isCaffeinated) {
          caffeine_100g = autoCaffeine100g;
        }

        const cleanedMealName = cleanBarcodeMealName(productName);

        // Convert Open Food Facts data to our MealAnalysis format
        const analysis: MealAnalysis = {
          meal_name: cleanedMealName,
          meal_type: selectedMealType,
          total_amount: product.quantity || `${amount}${unit}`,
          foods: [{
            name: cleanedMealName,
            category: (finalUnit === 'ml' ? 'drink' : 'other') as any,
            portion: product.serving_size || '1 serving',
            amount: Math.round(ratio * 100),
            unit: finalUnit as any,
            calories_kcal: calories * ratio,
            protein_g: getNutriment('proteins_100g', ['protein_100g', 'proteins', 'protein']) * ratio,
            carbs_g: getNutriment('carbohydrates_100g', ['carbohydrates', 'carbs_100g']) * ratio,
            fat_g: getNutriment('fat_100g', ['fat']) * ratio,
            sugar_g: getNutriment('sugars_100g', ['sugars', 'sugar_100g']) * ratio,
            fiber_g: getNutriment('fiber_100g', ['fiber']) * ratio,
            sodium_mg: getNutriment('salt_100g', ['salt']) * 1000 * ratio,
            caffeine_mg: caffeine_100g * ratio,
          }],
          total_nutrition: {
            calories_kcal: calories * ratio,
            protein_g: getNutriment('proteins_100g', ['protein_100g', 'proteins', 'protein']) * ratio,
            carbs_g: getNutriment('carbohydrates_100g', ['carbohydrates', 'carbs_100g']) * ratio,
            fat_g: getNutriment('fat_100g', ['fat']) * ratio,
            sugar_g: getNutriment('sugars_100g', ['sugars', 'sugar_100g']) * ratio,
            fiber_g: getNutriment('fiber_100g', ['fiber']) * ratio,
            sodium_mg: getNutriment('salt_100g', ['salt']) * 1000 * ratio,
            caffeine_mg: caffeine_100g * ratio,
          },
          insight: "" // Let postProcessNutrition build the same beautiful dynamic insight as other products
        };
        
        if (safeChoiceProfile?.active) {
          const ingredients = product.ingredients_text || product.ingredients_tags?.join(', ') || '';
          const safeCheck = await checkSafeChoiceForProduct(ingredients, cleanedMealName, safeChoiceProfile, vibe, lang);
          analysis.safe_choice_status = safeCheck.status;
          analysis.safe_choice_message = safeCheck.message;
          analysis.detected_risk_components = safeCheck.detected_risk_components;
        }

        // Extract real scan image using fallbacks from OFF database
        const realImageUrl = product.image_url || 
                             product.image_front_url || 
                             product.image_front_small_url || 
                             product.image_small_url || 
                             product.image_front_display_url || 
                             product.image_display_url || 
                             '';

        analysis.image = realImageUrl || '';

        const processedAnalysis = postProcessNutrition(analysis, lang, vibe);

        // Populate quick add product and keep barcode camera active for on-the-fly additions
        setScannedActiveProduct({
          analysis: processedAnalysis,
          productName: cleanedMealName,
          calories100g: calories,
          protein100g: getNutriment('proteins_100g', ['protein_100g', 'proteins', 'protein']),
          carbs100g: getNutriment('carbohydrates_100g', ['carbohydrates', 'carbs_100g']),
          fat100g: getNutriment('fat_100g', ['fat']),
          sugar100g: getNutriment('sugars_100g', ['sugars', 'sugar_150g', 'sugars_100g']),
          fiber100g: getNutriment('fiber_100g', ['fiber']),
          sodium100g: getNutriment('salt_100g', ['salt']) * 1000,
          caffeine100g: caffeine_100g,
          originalAmount: amount > 0 ? amount : 100,
          currentAmount: amount > 0 ? amount : 100,
          unit: finalUnit,
          imageUrl: realImageUrl,
          hasNutriments: !!hasNutriments
        });

        const scannedProd: ScannedProduct = {
          barcode: decodedText,
          name: cleanedMealName,
          calories100g: calories,
          protein100g: getNutriment('proteins_100g', ['protein_100g', 'proteins', 'protein']),
          carbs100g: getNutriment('carbohydrates_100g', ['carbohydrates', 'carbs_100g']),
          fat100g: getNutriment('fat_100g', ['fat']),
          sugar100g: getNutriment('sugars_100g', ['sugars', 'sugar_150g', 'sugars_100g']),
          fiber100g: getNutriment('fiber_100g', ['fiber']),
          sodium100g: getNutriment('salt_100g', ['salt']) * 1000,
          caffeine100g: caffeine_100g,
          unit: finalUnit,
          imageUrl: realImageUrl,
          timestamp: Date.now()
        };
        saveScannedProduct(scannedProd);
        // Background image generation has been permanently disabled to prevent unexpected API costs.
        
        lastFailedScanRef.current = { code: '', time: 0 };
      } else {
        lastFailedScanRef.current = { code: decodedText, time: Date.now() };
        toast.error(t.productNotFound, { id: 'barcode-status' });
      }
    } catch (err) {
      console.error("Error fetching product:", err);
      toast.error(t.errorFailed, { id: 'barcode-status' });
    } finally {
      // Update the time after the scan attempt finishes
      lastFailedScanRef.current.time = Date.now();
      setIsScanning(false);
    }
  };
  const startCamera = async (purpose: 'scan' | 'attach' = 'scan', callback?: (img: string) => void) => {
    console.log("startCamera called, purpose:", purpose);
    setCameraPurpose(purpose);
    setCaptureCallback(callback || null);
    if (purpose === 'scan' && !callback) {
      setSelectedHistoryItem(null);
      setResult(null);
      setOriginalResult(null);
      setEditingMeal(null);
      setImage(null);
      setError(null);
      setIsLogged(false);
      setView('analyze');
    }
    if (cameraMode === 'barcode') {
      setIsCameraActive(true);
      return;
    }
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera API not supported");
      }
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: facingMode }
        });
      } catch (firstErr) {
        console.warn("Camera request with facingMode failed, retrying with basic video constraint:", firstErr);
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      console.log("Stream acquired:", stream);
      
      // Check for torch support
      const track = stream.getVideoTracks()[0];
      if (track) {
        const capabilities = track.getCapabilities() as any;
        setHasTorch(!!capabilities?.torch);
      }
      
      streamRef.current = stream;
      setActiveStream(stream);
      setIsCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      setIsTorchOn(false); // Reset torch when starting camera
    } catch (err: any) {
      console.error("Error accessing camera:", err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError' || String(err).includes('Permission denied')) {
        setError("Camera access denied. Please allow camera permissions in your browser settings. If you are in an iframe, try opening the app in a new tab.");
      } else {
        setError("Could not access camera. Please check permissions.");
      }
    }
  };
  const stopCamera = async () => {
    if (html5QrCodeRef.current) {
      try {
        const scanner = html5QrCodeRef.current;
        const isScanning = scanner.isScanning || (typeof scanner.getState === 'function' && scanner.getState() === 2);
        if (isScanning) {
          await scanner.stop();
        }
      } catch (err) {
        console.error("Error stopping scanner:", err);
      } finally {
        html5QrCodeRef.current = null;
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setActiveStream(null);
    setIsCameraActive(false);
    setIsTorchOn(false);
    setHasTorch(false);
    setCameraMode('photo'); // Reset to photo mode for next time
    setFacingMode('environment'); // Reset to back camera for next time
  };
  const toggleFacingMode = async () => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setIsTorchOn(false); // Reset torch when switching camera
    
    // If photo mode is active, we need to restart the stream
    if (isCameraActive && cameraMode === 'photo') {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: newMode }
        });
        
        // Check for torch support on new stream
        const track = stream.getVideoTracks()[0];
        if (track) {
          const capabilities = track.getCapabilities() as any;
          setHasTorch(!!capabilities.torch);
        } else {
          setHasTorch(false);
        }

        streamRef.current = stream;
        setActiveStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Error switching camera:", err);
        toast.error("Could not switch camera.");
        return;
      }
    }
    
    // For barcode mode, the useEffect will handle the restart because facingMode is in dependencies
    setFacingMode(newMode);
  };
  const toggleTorch = async () => {
    if (cameraMode === 'photo') {
      if (streamRef.current) {
        const track = streamRef.current.getVideoTracks()[0];
        if (track) {
          try {
            const newTorchState = !isTorchOn;
            await track.applyConstraints({
              advanced: [{ torch: newTorchState }]
            } as any);
            setIsTorchOn(newTorchState);
          } catch (err) {
            console.error("Error toggling torch:", err);
            toast.error("Could not toggle torch.");
          }
        }
      }
    } else if (cameraMode === 'barcode' && html5QrCodeRef.current) {
      try {
        const newTorchState = !isTorchOn;
        await html5QrCodeRef.current.applyVideoConstraints({
          advanced: [{ torch: newTorchState }]
        } as any);
        setIsTorchOn(newTorchState);
      } catch (err) {
        console.error("Error toggling torch in barcode mode:", err);
        toast.error("Could not toggle torch.");
      }
    }
  };
  const capturePhoto = () => {
    sounds.playScan();
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Safety check for video dimensions
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.error("Video dimensions are 0");
        return;
      }

      // Resize to a reasonable maximum dimension to save memory and make AI processing as fast as possible
      const maxDim = 512;
      let width = video.videoWidth;
      let height = video.videoHeight;

      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = (maxDim / width) * height;
          width = maxDim;
        } else {
          width = (maxDim / height) * width;
          height = maxDim;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (context) {
        try {
          context.drawImage(video, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.6); // Use 0.6 quality to save space
          
          if (captureCallback) {
            captureCallback(dataUrl);
            setCaptureCallback(null);
          } else {
            setSelectedHistoryItem(null);
            setResult(null);
            setOriginalResult(null);
            setEditingMeal(null);
            setImage(null);
            setImageToCrop(dataUrl);
            setError(null);
            setIsLogged(false);
          }
          stopCamera();
        } catch (e) {
          console.error("Error capturing photo:", e);
          toast.error("Failed to capture photo. Image might be too large.");
        }
      }
    }
  };
  const handleGenerateVideo = async () => {
    // @ts-ignore
    if (typeof window.aistudio === 'undefined' || !window.aistudio.hasSelectedApiKey) {
      toast.error('API key selection is not available in this environment.');
      return;
    }
    
    // @ts-ignore
    const hasKey = await window.aistudio.hasSelectedApiKey();
    if (!hasKey) {
      // @ts-ignore
      await window.aistudio.openSelectKey();
    }

    // @ts-ignore
    const apiKey = typeof window !== 'undefined' && window.process?.env?.API_KEY ? window.process.env.API_KEY : '';
    if (!apiKey) {
      toast.error('API key is missing. Please select an API key.');
      // @ts-ignore
      await window.aistudio.openSelectKey();
      return;
    }

    setIsGeneratingVideo(true);
    setVideoProgressMsg('Preparing weekly stats...');
    
    try {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      
      const weekEntries = history.filter(e => new Date(e.timestamp) >= start);
      const avgCalories = weekEntries.length > 0 ? Math.round(weekEntries.reduce((sum, e) => sum + (e.total_nutrition?.calories_kcal || 0), 0) / 7) : 0;
      const totalProtein = Math.round(weekEntries.reduce((sum, e) => sum + (e.total_nutrition?.protein_g || 0), 0));
      
      const statsSummary = `The user has maintained a ${consecutiveDaysStreak} day streak! Over the last 7 days, they averaged ${avgCalories} calories per day and consumed a total of ${totalProtein}g of protein. They are crushing their goals!`;

       
      
      const url = await generateWeeklyProgressVideo(statsSummary, apiKey, (msg) => {
        setVideoProgressMsg(msg);
      });
      
      if (url) {
        setVideoUrl(url);
        toast.success(lang.startsWith('en') ? 'Weekly recap video generated successfully!' : 'Відео-підсумок тижня успішно згенеровано!');
      } else {
        toast.error(lang.startsWith('en') ? 'Failed to generate video.' : 'Не вдалося згенерувати відео.');
      }
    } catch (error: any) {
      const errorMsg = error?.message || String(error);
      if (errorMsg === "QUOTA_EXCEEDED") {
        toast.error(lang === 'ua' ? "Перевищено ліміт запитів до API." : "API quota exceeded.");
      } else {
        console.error("Failed to generate video:", error);
        if (errorMsg.includes('PERMISSION_DENIED') || errorMsg.includes('Requested entity was not found')) {
          toast.error(lang.startsWith('en') ? 'Permission denied. Please select a different API key from a paid project.' : 'Доступ заборонено. Будь ласка, виберіть інший API-ключ із платного проекту.');
          // @ts-ignore
          await window.aistudio.openSelectKey();
        } else {
          toast.error(lang.startsWith('en') ? 'Failed to generate video.' : 'Не вдалося згенерувати відео.');
        }
      }
    } finally {
      setIsGeneratingVideo(false);
      setVideoProgressMsg('');
    }
  };
  const handleGenerateImage = async (mealNameParam?: string | React.MouseEvent, onSuccessParam?: (url: string) => void) => {
    if (isGeneratingImage) return;
    const mealName = typeof mealNameParam === 'string' ? mealNameParam : manualMealName;
    const onSuccess = typeof onSuccessParam === 'function' ? onSuccessParam : (url: string) => setManualImage(url);

    if (!mealName.trim()) {
      toast.error(lang.startsWith('en') ? 'Please enter a meal name first to generate an image.' : 'Будь ласка, введіть назву страви спочатку, щоб згенерувати зображення.');
      return;
    }
    
    setIsGeneratingImage(true);
    try {
      let imageUrl = await generateMealImage(mealName);
      if (imageUrl) {
        // Resize generated image to ensure it fits in Firestore (1MB limit)
        try {
          imageUrl = await resizeImage(imageUrl, 512);
        } catch (resizeError) {
          console.warn("Failed to resize generated image:", resizeError);
        }
        onSuccess(imageUrl);
        toast.success(lang.startsWith('en') ? 'Image generated successfully!' : 'Зображення успішно згенеровано!');
      } else {
        toast.error(lang.startsWith('en') ? 'Failed to generate image.' : 'Не вдалося згенерувати зображення.');
      }
    } catch (error: any) {
      const errorMsg = error?.message || String(error);
      if (errorMsg === "QUOTA_EXCEEDED") {
        toast.error(lang === 'ua' ? "Перевищено ліміт запитів до API." : "API quota exceeded.");
      } else {
        console.error("Failed to generate image:", error);
        if (errorMsg.includes('PERMISSION_DENIED') || errorMsg.includes('Requested entity was not found')) {
          toast.error(lang.startsWith('en') ? 'Permission denied. Please select a different API key from a paid project.' : 'Доступ заборонено. Будь ласка, виберіть інший API-ключ із платного проекту.');
          // @ts-ignore
          await window.aistudio.openSelectKey();
        } else {
          toast.error(lang.startsWith('en') ? 'Failed to generate image.' : 'Не вдалося згенерувати зображення.');
        }
      }
    } finally {
      setIsGeneratingImage(false);
    }
  };
  const handleAutoFillNutrients = async () => {
    if (!manualMealName.trim()) return;
    
    let nameToUse = manualMealName;
    const match = manualMealName.match(/^(\d+)\s*(g|gr|ml|oz|lb)?\s*(.*)$/i);
    
    if (match) {
      const amount = match[1];
      const unit = match[2] ? (match[2].toLowerCase() === 'g' ? 'gr' : (match[2] || '').toLowerCase()) : 'gr';
      const rest = match[3];
      
      if (amount) setManualAmount(amount);
      if (unit === 'gr' || unit === 'ml' || unit === 'pcs') setManualUnit(unit as 'gr' | 'ml' | 'pcs');
      
      if (rest) {
        nameToUse = rest;
        setManualMealName(rest);
      }
    }

    const query = `${manualAmount} ${manualUnit} ${nameToUse}`;
    if (query === lastAutoFillQuery) return;
    
    setIsAutoFillingNutrients(true);
    try {
      let analysis: any;
      try {
        analysis = await analyzeMealText(query, goals, lang, false, vibe, selectedMealType, ignoreMealTimeLogging, safeChoiceProfile, onboardingGender, subscriptionTier, userName);
      } catch (firstErr: any) {
        const firstErrMsg = firstErr?.message || String(firstErr);
        if (firstErrMsg.includes("UNAUTHORIZED") || firstErrMsg.includes("Unauthorized") || firstErrMsg.includes("access token") || firstErrMsg.includes("TOKEN_EXPIRED")) {
          console.warn("handleAnalyzeMeal: Session issue detected, re-authenticating and retrying...");
          clearSession();
          const activeUser = user || auth.currentUser;
          if (activeUser) {
            await forceRecreateSessionWithFirebase(true).catch(() => "");
          }
          await asyncGetAccessToken(true).catch(() => "");
          analysis = await analyzeMealText(query, goals, lang, false, vibe, selectedMealType, ignoreMealTimeLogging, safeChoiceProfile, onboardingGender, subscriptionTier, userName);
        } else {
          throw firstErr;
        }
      }
      
      if (analysis && analysis.total_nutrition && !analysis.error) {
        setManualCalories(analysis.total_nutrition.calories_kcal.toString());
        setManualProtein(analysis.total_nutrition.protein_g.toString());
        setManualCarbs(analysis.total_nutrition.carbs_g.toString());
        setManualFat(analysis.total_nutrition.fat_g.toString());
        setManualSugar(analysis.total_nutrition.sugar_g.toString());
        setManualFiber(analysis.total_nutrition.fiber_g.toString());
        setManualSodium(analysis.total_nutrition.sodium_mg.toString());
        setManualInsight(analysis.insight || '');
        setManualPortionAnalysis(analysis.portion_analysis || '');
        
        // Suggest a better name if it's generic and we have multiple items
        const currentNameIsGeneric = /^(meal|food|dish|analysed meal|dinner|lunch|breakfast|snack)$/i.test(manualMealName.trim());
        if ((currentNameIsGeneric || analysis.meal_name !== manualMealName) && analysis.foods && analysis.foods.length > 1) {
          const suggestedName = analysis.meal_name || generateMealName(analysis.foods);
          if (suggestedName && suggestedName !== manualMealName) {
            setManualMealName(suggestedName);
          }
        }

        if (analysis.foods && analysis.foods.length > 0 && analysis.foods[0].category) {
          setManualCategory(analysis.foods[0].category);
        }
        setLastAutoFillQuery(query);
      }
    } catch (error: any) {
      const errorMsg = error?.message || String(error);
      if (errorMsg === "QUOTA_EXCEEDED") {
        toast.error(lang === 'ua' ? "Перевищено ліміт запитів до API." : "API quota exceeded.");
      } else {
        console.error("Failed to auto-fill nutrients:", error);
      }
    } finally {
      setIsAutoFillingNutrients(false);
    }
  };
  const reconcileWaterLogForMeal = (
    mealId: string,
    foods: FoodItem[],
    timestamp: number,
    mealName: string,
    currentWaterLogs: WaterLog[]
  ): { updatedWaterLogs: WaterLog[]; waterId?: string } => {
    const drinkWater = foods.reduce((sum, food) => {
      const foodName = (food.name || '').toLowerCase();
      const isDrink = food.category === 'drink' || 
                     /\b(water|coffee|tea|latte|cappuccino|americano|espresso|macchiato|mocha|flat white|matcha|drink|beverage|juice|soda|cola|coke|pepsi|sprite|fanta|lemonade|milk|smoothie|shake|soup|broth|bouillon|borscht|wine|beer|cider|brew)\b/i.test(foodName) ||
                     /вода|водичка|кава|еспресо|лате|латте|капучино|капучіно|американо|макіато|мока|чай|чаю|матча|матчя|напій|напою|сік|соку|молоко|молока|коктейль|смузі|йогурт|пиво|вино|пепсі|кола|кока|спрайт|fanta|лимонад|суп|бульйон|борщ/i.test(foodName);
      
      if (isDrink) {
        if (typeof food.water_ml === 'number' && food.water_ml > 0) {
          return sum + food.water_ml;
        }
        
        const amount = Number(food.amount) || 0;
        const unit = (food.unit || '').toLowerCase();
        
        if (unit === 'ml' || unit === 'мл' || unit === 'g' || unit === 'gr' || unit === 'г' || unit === 'гр' || !unit) {
          return sum + (amount > 0 ? amount : 250);
        }
        
        if (unit === 'can') return sum + (amount || 1) * 330;
        if (unit === 'bottle') return sum + (amount || 1) * 500;
        if (unit === 'cup' || unit === 'glass' || unit === 'serving' || unit === 'portion') return sum + (amount || 1) * 250;
        if (unit === 'pcs' || unit === 'piece' || unit === 'шт') {
           if (/\b(espresso)\b/i.test(foodName) || /еспресо/i.test(foodName)) return sum + (amount || 1) * 30;
           return sum + (amount || 1) * 250;
        }
      }
      
      if ((/stick|bag/i.test(food.name) || /стік|пакет|пакетик/i.test(food.name)) && (food.unit === 'pcs' || food.unit === 'шт')) {
        return sum + (Number(food.amount) || 0) * 250;
      }
      
      return sum;
    }, 0);

    const foodsLower = foods.map(f => (f.name || '').toLowerCase()).join(' ');
    const mealNameLower = (mealName || '').toLowerCase();
    const isCoffee = /\b(coffee|espresso|latte|cappuccino|americano|macchiato|mocha|flat white)\b/i.test(mealNameLower) || 
                     /\b(coffee|espresso|latte|cappuccino|americano|macchiato|mocha|flat white)\b/i.test(foodsLower) ||
                     /кава|еспресо|лате|латте|капучино|капучіно|американо|макіато|макіято|мока|мокачино|флет вайт|флет-вайт/i.test(mealNameLower) ||
                     /кава|еспресо|лате|латте|капучино|капучіно|американо|макіато|макіято|мока|мокачино|флет вайт|флет-вайт/i.test(foodsLower);
    const isTea = /\b(tea|matcha|chai)\b/i.test(mealNameLower) || 
                  /\b(tea|matcha|chai)\b/i.test(foodsLower) ||
                  /чай|чаю|матча|матчя|трав'яний/i.test(mealNameLower) ||
                  /чай|чаю|матча|матчя|трав'яний/i.test(foodsLower);
    const beverageType: 'water' | 'coffee' | 'tea' = isCoffee ? 'coffee' : (isTea ? 'tea' : 'water');

    const totalDrinkCalories = foods.filter(f => f.category === 'drink' || /\b(water|coffee|tea|latte|cappuccino|americano|espresso|matcha|drink|beverage|juice|soda|cola|smoothie|shake|milk)\b/i.test((f.name || '').toLowerCase())).reduce((sum, f) => sum + (f.calories_kcal || 0), 0);
    const totalDrinkCaffeine = foods.filter(f => f.category === 'drink' || /\b(water|coffee|tea|latte|cappuccino|americano|espresso|matcha|drink|beverage|juice|soda|cola|smoothie|shake|milk)\b/i.test((f.name || '').toLowerCase())).reduce((sum, f) => sum + (f.caffeine_mg || 0), 0);
    const totalDrinkSugar = foods.filter(f => f.category === 'drink' || /\b(water|coffee|tea|latte|cappuccino|americano|espresso|matcha|drink|beverage|juice|soda|cola|smoothie|shake|milk)\b/i.test((f.name || '').toLowerCase())).reduce((sum, f) => sum + (f.sugar_g || 0), 0);

    const existingLogIndex = currentWaterLogs.findIndex(log => log.source_meal_id === mealId);

    if (drinkWater > 0) {
      const roundedWater = Math.round(drinkWater);
      if (existingLogIndex > -1) {
        const updatedLogs = [...currentWaterLogs];
        const oldLog = updatedLogs[existingLogIndex];
        const updatedLog: WaterLog = {
          ...oldLog,
          amount_ml: roundedWater,
          beverage_type: beverageType,
          calories_kcal: totalDrinkCalories,
          caffeine_mg: totalDrinkCaffeine,
          sugar_g: totalDrinkSugar,
          timestamp: timestamp - 1
        };
        updatedLogs[existingLogIndex] = updatedLog;
        return { updatedWaterLogs: updatedLogs, waterId: updatedLog.id };
      } else {
        const waterId = (typeof crypto !== 'undefined' && crypto.randomUUID) 
          ? crypto.randomUUID() 
          : Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
        
        const newWaterLog: WaterLog = {
          id: waterId,
          amount_ml: roundedWater,
          timestamp: timestamp - 1,
          source_meal_id: mealId,
          beverage_type: beverageType,
          calories_kcal: totalDrinkCalories,
          caffeine_mg: totalDrinkCaffeine,
          sugar_g: totalDrinkSugar
        };
        
        return { updatedWaterLogs: [newWaterLog, ...currentWaterLogs], waterId };
      }
    } else {
      if (existingLogIndex > -1) {
        const updatedLogs = currentWaterLogs.filter((_, idx) => idx !== existingLogIndex);
        return { updatedWaterLogs: updatedLogs };
      }
    }

    return { updatedWaterLogs: currentWaterLogs };
  };

  const saveToHistory = (
    analysis: MealAnalysis, 
    img: string, 
    customToastMessage?: string, 
    stayOnCurrentView?: boolean,
    customTargetDate?: Date | string
  ): { id: string, waterId?: string } | undefined => {
    console.log('Attempting to save meal to history...');
    try {
      if (!analysis) {
        console.error('No analysis result to save');
        return;
      }

      const existingId = (analysis as any)?.id;
      const id = isValidMealId(existingId)
        ? existingId
        : ((typeof crypto !== 'undefined' && crypto.randomUUID) 
            ? crypto.randomUUID() 
            : Math.random().toString(36).substring(2, 15) + Date.now().toString(36));

      const mealDate = customTargetDate ? new Date(customTargetDate) : new Date(selectedDate);
      const currentTime = new Date();
      mealDate.setHours(currentTime.getHours(), currentTime.getMinutes(), currentTime.getSeconds(), currentTime.getMilliseconds());
      const timestamp = mealDate.getTime();

      const processedAnalysis = (!analysis.insight || isGenericInsight(analysis.insight))
        ? postProcessNutrition(analysis, lang, vibe, timestamp, false, onboardingGender)
        : analysis;

      const isFav = favorites.some(f => 
        (isValidMealId(f.id) && f.id === id) ||
        (f.meal_name && (f.meal_name || '').trim().toLowerCase() === (processedAnalysis.meal_name || '').trim().toLowerCase() && (f.meal_name || '').trim() !== '')
      );
      const newEntry: HistoryEntry = {
        ...processedAnalysis,
        id,
        timestamp,
        image: img || processedAnalysis.image || analysis.image || '',
        tags: [processedAnalysis.meal_type?.toLowerCase() || 'meal'],
        isFavorite: isFav
      };
      
      console.log('New entry created with ID:', id, 'and timestamp:', timestamp);
      
      const updatedHistory = [newEntry, ...history.filter(h => h.id !== id)].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      saveMealHistory(updatedHistory);
      setHistory(updatedHistory);
      setSelectedHistoryItem(newEntry);
      setResult(newEntry);
      
      // Extract drinks and create separate WaterLog entries
      const { updatedWaterLogs, waterId } = reconcileWaterLogForMeal(
        id,
        analysis.foods,
        newEntry.timestamp,
        analysis.meal_name,
        waterLogs
      );
      
      setWaterLogs(updatedWaterLogs);
      localStorage.setItem('water_logs', JSON.stringify(safeClone(updatedWaterLogs)));

      clearProgressChartsCache();
      setSelectedDate(mealDate);
      sounds.playFoodSuccess();

      if (user) {
        syncHistoryToFirebase(newEntry, user.uid, updatedWaterLogs);
      }

      // Optimistically remove logged meal from AI suggestions carousel if it was suggested
      setAiMealSuggestions(prev => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        const updated = prev.map(group => {
          if (!group.options) return group;
          return {
            ...group,
            options: group.options.filter((opt: any) => opt.meal_name?.trim().toLowerCase() !== analysis.meal_name?.trim().toLowerCase())
          };
        }).filter(group => group.options && group.options.length > 0);
        localStorage.setItem('ai_meal_suggestions_cache', JSON.stringify(safeClone(updated)));
        return updated;
      });

      // Mark source chat message as logged if applicable
      if (sourceChatMessageIndex !== null) {
        const newMessages = [...chatMessages];
        if (newMessages[sourceChatMessageIndex]) {
          newMessages[sourceChatMessageIndex] = { ...newMessages[sourceChatMessageIndex], logged: true };
          setChatMessages(newMessages);
          localStorage.setItem('chat_history', JSON.stringify(safeClone(newMessages)));
          
          setLastChatUndoAction({
            type: 'log',
            messageIndex: sourceChatMessageIndex,
            mealId: id,
            waterId: waterId,
            timestamp: Date.now()
          });
        }
        setSourceChatMessageIndex(null);
      }
      
      try {
        console.log('History saved to state successfully');
        triggerHaptic('MEAL', vibrateOnLogging);
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.8 },
          colors: [...DEFAULT_CONFETTI_COLORS, '#10b981', '#10b981', '#10b981']
        });
        triggerHaptic('CONFETTI', vibrateOnLogging);
      } catch (storageError) {
        console.warn('Error during saveToHistory completion', storageError);
      }

      if (!stayOnCurrentView) {
        setIsLogged(true);
        setCountdown(3);
        const timer = setInterval(() => {
          setCountdown(prev => Math.max(0, prev - 1));
        }, 1000);
        
        setTimeout(() => {
          clearInterval(timer);
          setView('home');
          setImage(null);
          setResult(null);
          setIsLogged(false);
        }, 3000);
      } else {
        setImage(null);
        setResult(null);
        toast.success(customToastMessage || (lang === 'ua' ? 'Успішно записано!' : 'Successfully logged!'));
      }
      
      console.log('isLogged set to true, navigation delayed for animation');
      
      // Success feedback
      try {
        if (typeof confetti === 'function') {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: [...DEFAULT_CONFETTI_COLORS, '#10b981', '#10b981', '#10b981']
          });
          triggerHaptic('CONFETTI', vibrateOnLogging);
          console.log('Confetti triggered');
        }
      } catch (confettiError) {
        console.error('Confetti failed:', confettiError);
      }

      if (toast && typeof toast.success === 'function') {
        const mealId = newEntry.id;
        const msg = customToastMessage || t.mealLoggedSuccess || 'Meal logged!';
        showUndoToast(msg, mealId, waterId);
        console.log('Toast triggered');
      }

      return { id, waterId };
    } catch (error) {
      console.error('CRITICAL: Failed to log meal:', error);
      toast.error(lang === 'ua' ? 'Не вдалося зберегти прийом їжі. Спробуйте ще раз.' : 'Failed to log meal. Please try again.');
    }
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sounds.playPop();
    if (!manualMealName.trim()) {
      toast.error(lang === 'ua' ? 'Будь ласка, введіть назву страви' : 'Please enter a meal name');
      return;
    }

    const analysis: MealAnalysis = {
      meal_name: manualMealName,
      meal_type: selectedMealType,
      foods: [
        {
          name: manualMealName,
          category: manualCategory as any,
          portion: `${manualAmount}${manualUnit}`,
          amount: parseFloat(manualAmount) || 0,
          unit: manualUnit,
          calories_kcal: Math.round(parseFloat(manualCalories)) || 0,
          protein_g: parseFloat(manualProtein) || 0,
          carbs_g: parseFloat(manualCarbs) || 0,
          fat_g: parseFloat(manualFat) || 0,
          sugar_g: parseFloat(manualSugar) || 0,
          fiber_g: parseFloat(manualFiber) || 0,
          sodium_mg: parseFloat(manualSodium) || 0,
          potassium_mg: 0,
          cholesterol_mg: 0,
          vitamin_c_mg: 0,
          calcium_mg: 0,
          iron_mg: 0
        }
      ],
      total_nutrition: {
        calories_kcal: Math.round(parseFloat(manualCalories)) || 0,
        protein_g: parseFloat(manualProtein) || 0,
        carbs_g: parseFloat(manualCarbs) || 0,
        fat_g: parseFloat(manualFat) || 0,
        sugar_g: parseFloat(manualSugar) || 0,
        fiber_g: parseFloat(manualFiber) || 0,
        sodium_mg: parseFloat(manualSodium) || 0,
        potassium_mg: 0,
        cholesterol_mg: 0,
        vitamin_c_mg: 0,
        calcium_mg: 0,
        iron_mg: 0
      },
      notes: manualMealNotes,
      insight: manualInsight || '',
      portion_analysis: manualPortionAnalysis
    };

    const processedAnalysis = postProcessNutrition(analysis, lang, vibe);
    saveToHistory(processedAnalysis, manualImage || '');
    setManualMealName('');
    setManualMealNotes('');
    setManualInsight('');
    setManualPortionAnalysis('');
    setManualCalories('');
    setManualProtein('');
    setManualCarbs('');
    setManualFat('');
    setManualSugar('');
    setManualFiber('');
    setManualSodium('');
    setManualInsight('');
    setManualImage(null);
    setLastAutoFillQuery('');
    setView('history');
    toast.success(t.mealLoggedSuccess, {
      icon: <AnimatedSuccessIcon />,
      className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold",
    });
    triggerHaptic('MEAL', vibrateOnLogging);
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: [...DEFAULT_CONFETTI_COLORS, '#10b981', '#10b981', '#10b981']
    });
    triggerHaptic('CONFETTI', vibrateOnLogging);
  };
  const handleShowExplanation = async (foodName: string, calories: number) => {
    const cacheKey = `${foodName}-${calories}`;
    if (explanationCache[cacheKey]) {
      setFoodExplanation({ name: foodName, explanation: explanationCache[cacheKey] });
      return;
    }

    setIsGettingExplanation(true);
    setFoodExplanation({ name: foodName, explanation: '' }); // Show modal with loader
    try {
      const explanation = await getFoodExplanation(foodName, calories, lang, vibe);
      setExplanationCache(prev => ({ ...prev, [cacheKey]: explanation }));
      setFoodExplanation({ name: foodName, explanation });
    } catch (error: any) {
      const errorMsg = error?.message || String(error);
      if (errorMsg === "QUOTA_EXCEEDED") {
        toast.error(lang === 'ua' ? "Перевищено ліміт запитів до API." : "API quota exceeded.");
      } else {
        toast.error(lang === 'ua' ? 'Не вдалося отримати пояснення' : 'Failed to get explanation');
      }
      setFoodExplanation(null);
    } finally {
      setIsGettingExplanation(false);
    }
  };
  const handleQuasoChoice = (isEntire: boolean) => {
    if (!pendingQuasoData) return;
    
    let finalData = { ...pendingQuasoData };
    
    if (isEntire) {
      // Double the macros
      finalData.foods = finalData.foods.map(food => ({
        ...food,
        amount: (Number(food.amount) || 0) * 2,
        calories_kcal: (food.calories_kcal || 0) * 2,
        protein_g: (food.protein_g || 0) * 2,
        carbs_g: (food.carbs_g || 0) * 2,
        fat_g: (food.fat_g || 0) * 2,
        sugar_g: (food.sugar_g || 0) * 2,
        fiber_g: (food.fiber_g || 0) * 2,
        sodium_mg: (food.sodium_mg || 0) * 2,
        potassium_mg: (food.potassium_mg || 0) * 2,
        cholesterol_mg: (food.cholesterol_mg || 0) * 2,
        vitamin_c_mg: (food.vitamin_c_mg || 0) * 2,
        calcium_mg: (food.calcium_mg || 0) * 2,
        iron_mg: (food.iron_mg || 0) * 2,
      }));
      
      finalData.total_nutrition = {
        calories_kcal: (finalData.total_nutrition?.calories_kcal || 0) * 2,
        protein_g: (finalData.total_nutrition?.protein_g || 0) * 2,
        carbs_g: (finalData.total_nutrition?.carbs_g || 0) * 2,
        fat_g: (finalData.total_nutrition?.fat_g || 0) * 2,
        sugar_g: (finalData.total_nutrition?.sugar_g || 0) * 2,
        fiber_g: (finalData.total_nutrition?.fiber_g || 0) * 2,
        sodium_mg: (finalData.total_nutrition?.sodium_mg || 0) * 2,
        potassium_mg: (finalData.total_nutrition?.potassium_mg || 0) * 2,
        cholesterol_mg: (finalData.total_nutrition?.cholesterol_mg || 0) * 2,
        vitamin_c_mg: (finalData.total_nutrition?.vitamin_c_mg || 0) * 2,
        calcium_mg: (finalData.total_nutrition?.calcium_mg || 0) * 2,
        iron_mg: (finalData.total_nutrition?.iron_mg || 0) * 2,
      };
    }
    
    setResult(finalData);
    setOriginalResult(safeClone(finalData));
    setSelectedHistoryItem({
      ...finalData,
      id: 'temp',
      timestamp: getTimestampForSelectedDate(),
      image: image || undefined,
    });
    
    setShowQuasoModal(false);
    setPendingQuasoData(null);
    setView('details');
  };
  // [reconcileWaterLogForMeal moved before saveToHistory]
  // [saveToHistory moved before handleManualSubmit]
  const sanitizeFavorites = useCallback((rawList: any[]): HistoryEntry[] => {
    if (!Array.isArray(rawList)) return [];
    let modified = false;
    const sanitized = rawList.map(item => {
      if (!item || typeof item !== 'object') return null;
      if (!isValidMealId(item.id)) {
        modified = true;
        return {
          ...item,
          id: (typeof crypto !== 'undefined' && crypto.randomUUID) 
            ? crypto.randomUUID() 
            : Math.random().toString(36).substring(2, 15) + Date.now().toString(36),
          isFavorite: true
        };
      }
      return { ...item, isFavorite: true };
    }).filter((item): item is HistoryEntry => item !== null);

    if (modified) {
      try {
        const jsonStr = JSON.stringify(safeClone(sanitized));
        localStorage.setItem('favorites', jsonStr);
        localDb.setItem('favorites', jsonStr);
        if (user) {
          setDocWithQueue(doc(db, 'users', user.uid), { favorites: safeClone(sanitized) }, { merge: true })
            .catch(err => console.warn('Failed to sync sanitized favorites to Firestore:', err));
        }
      } catch (_) {}
    }
    return sanitized;
  }, [user]);

  const saveToFavorites = (meal: HistoryEntry) => {
    const validMealId = isValidMealId(meal.id) 
      ? meal.id 
      : ((typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15) + Date.now().toString(36));

    const alreadyExists = favorites.some(f => isValidMealId(f.id) && f.id === validMealId);

    let updatedFavorites: HistoryEntry[];
    if (alreadyExists) {
      updatedFavorites = favorites.map(f => {
        if (isValidMealId(f.id) && f.id === validMealId) {
          return { ...f, ...meal, id: validMealId, isFavorite: true };
        }
        return f;
      });
      toast.info(lang === 'ua' ? 'Оновлено в улюблених!' : 'Updated in favorites!');
    } else {
      const favMeal: HistoryEntry = {
        ...meal,
        id: validMealId,
        timestamp: meal.timestamp || Date.now(),
        isFavorite: true
      };
      updatedFavorites = [favMeal, ...favorites.filter(f => f.id !== validMealId)];
      toast.success(lang === 'ua' ? 'Страву збережено в улюблені!' : 'Meal saved to favorites!');
    }

    setFavorites(updatedFavorites);
    const jsonStr = JSON.stringify(safeClone(updatedFavorites));
    localStorage.setItem('favorites', jsonStr);
    localDb.setItem('favorites', jsonStr);

    if (user) {
      setDocWithQueue(doc(db, 'users', user.uid), { favorites: safeClone(updatedFavorites) }, { merge: true })
        .catch(err => console.warn('Failed to sync favorites to Firestore:', err));
    }

    // Keep active viewing meal and history in sync with favorite status
    if (result && ((result as any).id === validMealId || !(result as any).id)) {
      setResult((prev: any) => prev ? { ...prev, id: validMealId, isFavorite: true } : prev);
    }
    if (selectedHistoryItem && (selectedHistoryItem.id === validMealId || !selectedHistoryItem.id)) {
      setSelectedHistoryItem(prev => prev ? { ...prev, id: validMealId, isFavorite: true } : prev);
    }
    setHistory(prevHist => {
      const hasMatch = prevHist.some(h => (h.id === validMealId || (h.meal_name && h.meal_name === meal.meal_name)) && !h.isFavorite);
      if (!hasMatch) return prevHist;
      const nextHist = prevHist.map(h => (h.id === validMealId || (h.meal_name && h.meal_name === meal.meal_name)) ? { ...h, isFavorite: true } : h);
      saveMealHistory(nextHist);
      return nextHist;
    });

    sounds.playSave();
    triggerHaptic('SUCCESS', vibrateOnLogging);
  };
  const copyToLog = (meal: HistoryEntry) => {
    setResult(meal);
    setOriginalResult(safeClone(meal));
    setImage(meal.image || '');
    setView('analyze');
    setIsLogged(false);
  };
  const duplicateMealToToday = (item: CombinedEntry | HistoryEntry | WaterLog | any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    // Determine if it is a meal log
    const isMeal = 'foods' in item || item.type === 'meal' || !('amount_ml' in item);
    
    if (isMeal) {
      const mealToCopy = item as HistoryEntry;
      const newMeal: HistoryEntry = {
        ...safeClone(mealToCopy),
        id: crypto.randomUUID(),
        timestamp: getTimestampForSelectedDate()
      };
      
      const updatedHistory = [newMeal, ...history];
      const saved = saveMealHistory(updatedHistory);
      setHistory(saved);
      
      const { updatedWaterLogs } = reconcileWaterLogForMeal(
        newMeal.id,
        newMeal.foods,
        newMeal.timestamp,
        newMeal.meal_name,
        waterLogs
      );
      setWaterLogs(updatedWaterLogs);
      localStorage.setItem('water_logs', JSON.stringify(safeClone(updatedWaterLogs)));

      if (user) {
        syncHistoryToFirebase(newMeal, user.uid, updatedWaterLogs);
      }
      sounds.playSave();
      toast.success(lang === 'ua' ? 'Страву успішно продубльовано!' : 'Meal duplicated successfully!');
    } else {
      const waterToCopy = item as WaterLog;
      const newWater: WaterLog = {
        ...safeClone(waterToCopy),
        id: crypto.randomUUID(),
        timestamp: getTimestampForSelectedDate()
      };
      const updatedWater = [newWater, ...waterLogs];
      localStorage.setItem('water_logs', JSON.stringify(safeClone(updatedWater)));
      setWaterLogs(updatedWater);
      if (user) {
        syncWaterToFirebase(newWater, user.uid);
      }
      sounds.playSave();
      toast.success(lang === 'ua' ? 'Воду успішно продубльовано!' : 'Water duplicated successfully!');
    }
  };
  const updateMealInHistory = (updatedMeal: HistoryEntry) => {
    const processedMeal = postProcessNutrition({
      ...updatedMeal,
      insight: "" // Force dynamic recalculation of nutritional insight for the corrected details
    } as any, lang, vibe) as HistoryEntry;

    // Reconcile water logs for updated/added drinks in the meal
    const { updatedWaterLogs } = reconcileWaterLogForMeal(
      processedMeal.id,
      processedMeal.foods,
      processedMeal.timestamp,
      processedMeal.meal_name,
      waterLogs
    );

    setWaterLogs(updatedWaterLogs);
    localStorage.setItem('water_logs', JSON.stringify(safeClone(updatedWaterLogs)));

    const updatedHistory = history.map(item => item.id === processedMeal.id ? processedMeal : item);
    saveMealHistory(updatedHistory);
    setHistory(updatedHistory);
    if (user) {
        syncHistoryToFirebase(processedMeal, user.uid, updatedWaterLogs);
    }
    setSelectedHistoryItem(processedMeal);
    setView('details');
    sounds.playSave();
    
    toast.success(t.mealUpdatedSuccess || 'Meal updated successfully!');
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.8 },
      colors: [...DEFAULT_CONFETTI_COLORS, '#10b981', '#10b981', '#10b981']
    });
    triggerHaptic('CONFETTI', vibrateOnLogging);
  };
  const addWaterWithBrand = (amount: number, drink: DrinkBrandItem) => {
    const waterId = Math.random().toString(36).substr(2, 9);
    const logDate = new Date(selectedDate);
    const now = new Date();
    logDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

    const calculatedCals = Math.round((amount / 100) * drink.caloriesPer100ml);
    const calculatedCaff = Math.round((amount / 100) * drink.caffeinePer100ml);
    const calculatedSugar = Math.round((amount / 100) * drink.sugarPer100ml);

    const newLog: WaterLog = {
      id: waterId,
      timestamp: logDate.getTime(),
      amount_ml: amount,
      beverage_type: 'custom',
      brand_id: drink.id,
      brand_name: drink.name,
      calories_kcal: calculatedCals,
      caffeine_mg: calculatedCaff,
      sugar_g: calculatedSugar,
      tags: [drink.category, drink.brand.toLowerCase(), ...(drink.isEnergyDrink ? ['energy_drink', '18+'] : [])]
    };

    const updatedLogs = [...waterLogs, newLog];
    setWaterLogs(updatedLogs);
    localStorage.setItem('water_logs', JSON.stringify(safeClone(updatedLogs)));

    setSplashBeverageType('water');
    setShowLiquidSplash(true);
    setTimeout(() => setShowLiquidSplash(false), 2000);

    if (user) {
      syncWaterToFirebase(newLog, user.uid);
    }

    triggerHaptic('WATER', vibrateOnLogging);
    sounds.playWaterLogged();

    let message = lang === 'ua'
      ? `${drink.name} (${amount}мл) додано! +${calculatedCals}ккал`
      : `${drink.name} (${amount}ml) logged! +${calculatedCals}kcal`;

    if (drink.isEnergyDrink) {
      message = lang === 'ua'
        ? `⚡ ${drink.name} (${amount}мл) записано! +${calculatedCaff}мг кофеїну`
        : `⚡ ${drink.name} (${amount}ml) logged! +${calculatedCaff}mg caffeine`;
    }

    toast.success(
      <div className="flex items-center justify-between gap-4 w-full">
        <div className="flex items-center gap-2">
          <BrandLogoCircle logoType={drink.logoType} name={drink.name} sizePx={28} />
          <span className="font-bold text-xs">{message}</span>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            undoLastAction(undefined, waterId);
            toast.dismiss();
          }}
          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center gap-1 shrink-0"
        >
          <RotateCcw className="w-3 h-3" />
          {t.undo || 'Undo'}
        </button>
      </div>,
      {
        className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold min-w-[320px]"
      }
    );

    confetti({
      particleCount: 25,
      spread: 50,
      origin: { y: 0.8 }
    });
  };
  const addWater = (amount: number, beverageType: 'water' | 'coffee' | 'tea' | string = 'water') => {
    const waterId = Math.random().toString(36).substr(2, 9);
    const logDate = new Date(selectedDate);
    const now = new Date();
    logDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

    const nutrients = calculateWaterLogNutrients({ amount_ml: amount, beverage_type: beverageType });

    const newLog: WaterLog = {
      id: waterId,
      timestamp: logDate.getTime(),
      amount_ml: amount,
      beverage_type: beverageType,
      calories_kcal: nutrients.cal,
      caffeine_mg: nutrients.caff,
      sugar_g: nutrients.sugar,
      tags: [beverageType]
    };
    const updatedLogs = [...waterLogs, newLog];
    setWaterLogs(updatedLogs);
    localStorage.setItem('water_logs', JSON.stringify(safeClone(updatedLogs)));
    
    setSplashBeverageType(beverageType === 'coffee' ? 'coffee' : beverageType === 'tea' ? 'tea' : 'water');
    setShowLiquidSplash(true);
    setTimeout(() => setShowLiquidSplash(false), 2000);
    
    if (user) {
      syncWaterToFirebase(newLog, user.uid);
    }

    triggerHaptic('WATER', vibrateOnLogging);

    sounds.playWaterLogged();
    
    let message = t.waterLogged || 'Water logged! Stay hydrated, bro! 💧';
    if (beverageType === 'coffee') message = lang === 'ua' ? 'Каву записано! Заряд бадьорості! ☕️' : 'Coffee logged! Energy boost! ☕️';
    if (beverageType === 'tea') message = lang === 'ua' ? 'Чай записано! Час розслабитись! 🍵' : 'Tea logged! Tea time! 🍵';

    toast.success(
      <div className="flex items-center justify-between gap-4 w-full">
        <span>{message}</span>
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            undoLastAction(undefined, waterId);
            toast.dismiss();
          }}
          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center gap-1 shrink-0"
        >
          <RotateCcw className="w-3 h-3" />
          {t.undo || 'Undo'}
        </button>
      </div>,
      {
        icon: beverageType === 'coffee' ? <Coffee className="w-5 h-5 text-amber-500" /> : beverageType === 'tea' ? <Leaf className="w-5 h-5 text-emerald-500" /> : <Droplets className="w-5 h-5 text-blue-500" />,
        className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold min-w-[300px]",
        duration: 3500,
      }
    );
    
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.8 },
      colors: beverageType === 'coffee' ? [...DEFAULT_CONFETTI_COLORS, '#f59e0b', '#f59e0b', '#f59e0b'] : beverageType === 'tea' ? [...DEFAULT_CONFETTI_COLORS, '#10b981', '#10b981', '#10b981'] : [...DEFAULT_CONFETTI_COLORS, '#3b82f6', '#3b82f6', '#3b82f6']
    });
  };
  const deleteHistoryItem = (id: string, e?: React.MouseEvent | Event | any, skipDelay = false) => {
    if (e && e.stopPropagation) e.stopPropagation();
    
    if (!skipDelay) {
      setDeletingIds(prev => new Set(prev).add(id));
      try { sounds.playPop(); } catch (err) {}
      setTimeout(() => {
        deleteHistoryItem(id, undefined, true);
      }, 320);
      return;
    }

    if (selectedHistoryItem && selectedHistoryItem.id === id) {
      setSelectedHistoryItem(null);
    }
    
    const mealToDelete = history.find(item => item.id === id);
    const waterToDelete = waterLogs.find(item => item.id === id);
    
    if (mealToDelete) {
      const updatedHistory = history.filter(item => item.id !== id);
      const saved = saveMealHistory(updatedHistory);
      setHistory(saved);
      
      const updatedWater = waterLogs.filter(w => w.source_meal_id !== id);
      const hasLinkedWater = updatedWater.length !== waterLogs.length;
      if (hasLinkedWater) {
        setWaterLogs(updatedWater);
        localStorage.setItem('water_logs', JSON.stringify(safeClone(updatedWater)));
      }

      if (user) {
        const dateStr = formatDate(new Date(mealToDelete.timestamp), 'YYYY-MM-DD');
        syncDayMealsAndWaterToFirebase(dateStr, saved, updatedWater, user.uid);
        deleteDocWithQueue(doc(db, 'users', user.uid, 'history', id)).catch(() => {});
      }
      
      toast.success(lang === 'ua' ? 'Страву видалено' : 'Meal removed', {
        action: {
          label: 'Undo',
          onClick: () => {
            const restoredHistory = [mealToDelete, ...updatedHistory].sort((a, b) => 
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            );
            const saved = saveMealHistory(restoredHistory);
            setHistory(saved);

            let restoredWater = [...waterLogs];
            if (hasLinkedWater) {
              const deletedWater = waterLogs.filter(w => w.source_meal_id === id);
              deletedWater.forEach(dw => {
                if (!restoredWater.some(rw => rw.id === dw.id)) {
                  restoredWater.push(dw);
                }
              });
              restoredWater.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
              setWaterLogs(restoredWater);
              localStorage.setItem('water_logs', JSON.stringify(safeClone(restoredWater)));
            }

            if (user) {
              const dateStr = formatDate(new Date(mealToDelete.timestamp), 'YYYY-MM-DD');
              syncDayMealsAndWaterToFirebase(dateStr, saved, restoredWater, user.uid);
            }
            setDeletingIds(prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }
        }
      });
    } else if (waterToDelete) {
      const updatedWater = waterLogs.filter(item => item.id !== id);
      setWaterLogs(updatedWater);
      localStorage.setItem('water_logs', JSON.stringify(safeClone(updatedWater)));
      
      if (user) {
        const dateStr = formatDate(new Date(waterToDelete.timestamp), 'YYYY-MM-DD');
        syncDayMealsAndWaterToFirebase(dateStr, history, updatedWater, user.uid);
      }
      
      toast.success(lang === 'ua' ? 'Запис води видалено' : 'Water log removed', {
        action: {
          label: 'Undo',
          onClick: () => {
            const restoredWater = [waterToDelete, ...updatedWater].sort((a, b) => 
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            );
            setWaterLogs(restoredWater);
            localStorage.setItem('water_logs', JSON.stringify(safeClone(restoredWater)));
            if (user) {
              const dateStr = formatDate(new Date(waterToDelete.timestamp), 'YYYY-MM-DD');
              syncDayMealsAndWaterToFirebase(dateStr, history, restoredWater, user.uid);
            }
            setDeletingIds(prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }
        }
      });
    }
  };
  const deleteMultipleHistoryItems = (ids: string[], skipDelay = false) => {
    if (ids.length === 0) return;
    
    if (!skipDelay) {
      setDeletingIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.add(id));
        return next;
      });
      try { sounds.playPop(); } catch (err) {}
      setTimeout(() => {
        deleteMultipleHistoryItems(ids, true);
      }, 320);
      return;
    }
    
    const mealsToDelete = history.filter(item => ids.includes(item.id));
    const mealIdsToDelete = mealsToDelete.map(m => m.id);
    const waterToDelete = waterLogs.filter(item => ids.includes(item.id) || (item.source_meal_id && mealIdsToDelete.includes(item.source_meal_id)));
    
    // Perform local history updates
    const updatedHistory = history.filter(item => !ids.includes(item.id));
    const saved = saveMealHistory(updatedHistory);
    setHistory(saved);
    
    // Perform local water updates
    const updatedWater = waterLogs.filter(item => !ids.includes(item.id) && !(item.source_meal_id && mealIdsToDelete.includes(item.source_meal_id)));
    setWaterLogs(updatedWater);
    localStorage.setItem('water_logs', JSON.stringify(safeClone(updatedWater)));
    
    // Sync to Firestore if logged in
    if (user) {
      const uniqueDates = new Set<string>();
      mealsToDelete.forEach(m => uniqueDates.add(formatDate(new Date(m.timestamp), 'YYYY-MM-DD')));
      waterToDelete.forEach(w => uniqueDates.add(formatDate(new Date(w.timestamp), 'YYYY-MM-DD')));
      
      uniqueDates.forEach(dateStr => {
        syncDayMealsAndWaterToFirebase(dateStr, saved, updatedWater, user.uid);
      });

      const deleteRefs: any[] = [];
      ids.forEach(id => {
        deleteRefs.push(doc(db, 'users', user.uid, 'history', id));
        deleteRefs.push(doc(db, 'users', user.uid, 'water', id));
      });
      batchDeleteDocsWithQueue(deleteRefs).catch(() => {});
    }
    
    // Play sounds & animations
    sounds.playPop();
    
    toast.success(lang.startsWith('ua') ? `Видалено ${ids.length} елементів` : `Removed ${ids.length} entries`, {
      action: {
        label: lang.startsWith('ua') ? 'Скасувати' : 'Undo',
        onClick: () => {
          // Restore meals
          let restoredHistory = [...history];
          mealsToDelete.forEach(meal => {
            if (!restoredHistory.some(h => h.id === meal.id)) {
              restoredHistory.push(meal);
            }
          });
          restoredHistory.sort((a, b) => 
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
          const savedRestored = saveMealHistory(restoredHistory);
          setHistory(savedRestored);
          
          // Restore water
          let restoredWater = [...waterLogs];
          waterToDelete.forEach(water => {
            if (!restoredWater.some(w => w.id === water.id)) {
              restoredWater.push(water);
            }
          });
          restoredWater.sort((a, b) => 
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
          setWaterLogs(restoredWater);
          localStorage.setItem('water_logs', JSON.stringify(safeClone(restoredWater)));

          if (user) {
            const uniqueDates = new Set<string>();
            mealsToDelete.forEach(m => uniqueDates.add(formatDate(new Date(m.timestamp), 'YYYY-MM-DD')));
            waterToDelete.forEach(w => uniqueDates.add(formatDate(new Date(w.timestamp), 'YYYY-MM-DD')));
            
            uniqueDates.forEach(dateStr => {
              syncDayMealsAndWaterToFirebase(dateStr, savedRestored, restoredWater, user.uid);
            });
          }
        }
      }
    });
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    sounds.playScan();
    const file = e.target.files?.[0];
    if (file) {
      // Clear previous meal context to prevent old history item/result hijack
      setSelectedHistoryItem(null);
      setResult(null);
      setOriginalResult(null);
      setEditingMeal(null);
      setImage(null);
      setError(null);
      setIsLogged(false);

      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const resizedImage = await resizeImage(reader.result as string, 512);
          setImageToCrop(resizedImage);
        } catch (err) {
          console.error("Error resizing uploaded image:", err);
          toast.error(lang === 'ua' ? 'Не вдалося обробити зображення.' : 'Failed to process image.');
        }
      };
      reader.readAsDataURL(file);
    }
  };
  // [handleCropComplete moved after handleAnalyze]
  const handleAnalyze = async (useAlternateModel: boolean = false, overrideImage?: string, mealTypeOverride?: MealType) => {
    if (isAnalyzing && !useAlternateModel) {
      console.warn("Meal analysis already in progress, skipping duplicate trigger.");
      return;
    }

    const targetImage = overrideImage || image;
    const activeMealType = mealTypeOverride || selectedMealType;
    if (!targetImage) {
      // If user tapped Analyze with no photo selected, prompt camera or file picker immediately
      sounds.playPop();
      if (fileInputRef.current) {
        fileInputRef.current.click();
      } else {
        startCamera();
      }
      return;
    }

    if (!auth.currentUser && auth && typeof auth.authStateReady === 'function') {
      try {
        await Promise.race([
          auth.authStateReady(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('AUTH_TIMEOUT')), 2500))
        ]);
      } catch (e) {}
    }

    sounds.playPop();
    triggerHaptic('MEDIUM');
    setIsAnalyzing(true);
    setError(null);
    setIsLogged(false);
    setSelectedHistoryItem(null);
    setResult(null);
    setOriginalResult(null);

    // Scroll to scanner animation smoothly
    setTimeout(() => {
      if (scannerRef.current) {
        scannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    try {
      let data: any;
      try {
        data = await analyzeFoodImage(targetImage, goals, lang, useAlternateModel, vibe, activeMealType, ignoreMealTimeLogging, safeChoiceProfile, onboardingGender, subscriptionTier, userName);
      } catch (firstErr: any) {
        const firstErrMsg = firstErr?.message || String(firstErr);
        if (firstErrMsg.includes("UNAUTHORIZED") || firstErrMsg.includes("Unauthorized") || firstErrMsg.includes("access token") || firstErrMsg.includes("TOKEN_EXPIRED")) {
          console.warn("handleAnalyze: Session issue detected, automatically re-authenticating and retrying meal scan...");
          clearSession();
          if (auth.currentUser) {
            await forceRecreateSessionWithFirebase(true).catch(() => "");
          }
          await asyncGetAccessToken(true).catch(() => "");
          data = await analyzeFoodImage(targetImage, goals, lang, useAlternateModel, vibe, activeMealType, ignoreMealTimeLogging, safeChoiceProfile, onboardingGender, subscriptionTier, userName);
        } else {
          throw firstErr;
        }
      }

      if (!data || typeof data !== 'object') {
        throw new Error(lang === 'ua' ? "Не вдалося розпізнати страву на фото. Будь ласка, спробуйте ще раз." : "Failed to analyze meal from photo. Please try again.");
      }

      if (data.error) {
        setError(t.errorNoFood);
        setShowNoFoodModal(true);
      } else if (data.easter_egg_half_quaso && data.foods?.some(f => f.name?.toLowerCase().includes('croissant'))) {
        const enrichedData = { ...data, image: targetImage || data.image || '', meal_type: activeMealType, includeRecipe };
        setPendingQuasoData(enrichedData);
        setShowQuasoModal(true);
      } else {
        // Override AI's meal type with user's selection
        const enrichedData = { ...data, image: targetImage || data.image || '', meal_type: activeMealType, includeRecipe };
        
        // Automatically suggest a better meal name if generic
        const nameIsGeneric = !enrichedData.meal_name || 
                             /^(meal|food|dish|analysed meal|dinner|lunch|breakfast|snack)$/i.test(enrichedData.meal_name);
        
        if (nameIsGeneric && enrichedData.foods && enrichedData.foods.length > 1) {
          enrichedData.meal_name = generateMealName(enrichedData.foods);
        }

        playScanCompleteSound();
        setResult(enrichedData);
        setOriginalResult(safeClone(enrichedData));
        setView('analyze');

        if (enableMealReview) {
          originalPendingReviewFoodsRef.current = safeClone(enrichedData.foods);
          setPendingReviewData(enrichedData);
          setShowReviewModal(true);
        }
      }
    } catch (err: any) {
      const errorMsg = err?.message || String(err);
      if (errorMsg.includes("Forbidden") || errorMsg.includes("FORBIDDEN") || errorMsg.includes("403") || errorMsg.includes("PERMISSION_DENIED") || errorMsg === "BILLING_DENIED" || errorMsg.includes("dunning") || errorMsg.includes("Lightning dunning")) {
        setError(lang === 'ua' 
          ? "Помилка доступу Gemini API (403 Forbidden): Перевірте наявність GEMINI_API_KEY у змінних середовища Cloud Run та увімкнений дозвіл Generative Language API в консолі GCP." 
          : "Gemini API Permission Error (403 Forbidden): Please verify GEMINI_API_KEY in GCP Cloud Run environment variables and ensure Generative Language API is enabled.");
      } else if (errorMsg === "QUOTA_EXCEEDED") {
        setError(lang === 'ua' ? "Перевищено ліміт запитів до API. Будь ласка, спробуйте пізніше або перевірте налаштування квоти." : "API quota exceeded. Please try again later or check your billing details.");
      } else if (errorMsg === "SERVER_OVERLOADED") {
        setError(lang === 'ua' ? "Сервер перевантажений через високий попит. Будь ласка, зачекайте хвилинку і спробуйте ще раз." : "The AI model is currently experiencing high demand. Please wait a moment and try again.");
      } else if (errorMsg.includes("UNAUTHORIZED") || errorMsg.includes("Unauthorized") || errorMsg.includes("access token") || errorMsg.includes("TOKEN_EXPIRED")) {
        clearSession();
        const activeUser = user || auth.currentUser;
        if (activeUser) {
          forceRecreateSessionWithFirebase(true).catch(() => "");
          setError(lang === 'ua' ? "Сесію оновлено. Будь ласка, натисніть Сканувати ще раз." : "Session refreshed. Please tap Scan again.");
        } else {
          setError(lang === 'ua' ? "Для аналізу фото за допомогою AI увійдіть через Google або збережіть страву вручну." : "To analyze meals with AI, please sign in with Google or log manually.");
        }
      } else if (errorMsg === "TIMEOUT_ERROR" || errorMsg.includes("TIMEOUT") || errorMsg.includes("timed out")) {
        setError(lang === 'ua' ? "Час очікування аналізу вичерпано. Будь ласка, спробуйте ще раз або перевірте інтернет-з'єднання." : "Analysis timed out. Please try again or check your internet connection.");
      } else if (errorMsg.includes("API_KEY_MISSING")) {
        setError(lang === 'ua' ? "API ключ не знайдено. Будь ласка, перевірте налаштування середовища." : "Gemini API key is missing. Please check your environment variables.");
      } else if (err?.isConfigError) {
        const diagnosticMessage = `${errorMsg}${err.foundKeys?.length > 0 ? ` (Available secrets: ${err.foundKeys.join(', ')})` : ' (No relevant secrets found)'}`;
        setError(diagnosticMessage);
      } else {
        // Show actual error if present, otherwise fallback
        if (errorMsg && errorMsg.length > 3) {
          setError(errorMsg);
        } else {
          setError(t.errorFailed);
        }
        console.error(err);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCropComplete = (croppedImage: string, mealTypeOverride?: MealType) => {
    if (mealTypeOverride) {
      setSelectedMealType(mealTypeOverride);
    }
    const activeMealType = mealTypeOverride || selectedMealType;
    setImage(croppedImage);
    setImageToCrop(null);
    
    if (view === 'manual') {
      setManualImage(croppedImage);
      return;
    }

    if (editingMeal && view === 'edit') {
      setEditingMeal({ ...editingMeal, image: croppedImage });
      return;
    }

    // Only update selectedHistoryItem if we are explicitly viewing it in details mode
    if (selectedHistoryItem && view === 'details') {
      const updatedItem = { ...selectedHistoryItem, image: croppedImage };
      setSelectedHistoryItem(updatedItem);
      
      if (updatedItem.id !== 'temp') {
        const newHistory = history.map(item => item.id === updatedItem.id ? updatedItem : item);
        const saved = saveMealHistory(newHistory);
        setHistory(saved);
        if (user) syncHistoryToFirebase(updatedItem, user.uid);
      }
      setView('details');
      return;
    }
    
    // For new scans, ensure old history item/results are cleared and trigger analysis automatically
    setSelectedHistoryItem(null);
    setResult(null);
    setOriginalResult(null);
    setView('analyze');
    handleAnalyze(false, croppedImage, activeMealType);
  };
  const handleReanalyze = async () => {
    if (image) {
      // Re-analyze image
      await handleAnalyze(true);
    } else if (result && !image) {
      // Re-analyze text if no image but we have a result (meaning it was a text analysis)
      // We need the original text input. We might not have it saved directly if it was from chat.
      // But we can use the meal name or foods as a fallback text prompt if chatInput is empty.
      const textToAnalyze = chatInput || result.meal_name;
      setIsAnalyzingText(true);
      setError(null);
      try {
        let analysis: any;
        try {
          analysis = await analyzeMealText(textToAnalyze, goals, lang, true, vibe, selectedMealType, ignoreMealTimeLogging, safeChoiceProfile, onboardingGender, subscriptionTier, userName);
        } catch (firstErr: any) {
          const firstErrMsg = firstErr?.message || String(firstErr);
          if (firstErrMsg.includes("UNAUTHORIZED") || firstErrMsg.includes("Unauthorized") || firstErrMsg.includes("access token") || firstErrMsg.includes("TOKEN_EXPIRED")) {
            console.warn("handleReanalyze: Session expired, auto-refreshing session and retrying...");
            clearSession();
            if (auth.currentUser) {
              await forceRecreateSessionWithFirebase(true).catch(() => "");
            }
            await asyncGetAccessToken(true).catch(() => "");
            analysis = await analyzeMealText(textToAnalyze, goals, lang, true, vibe, selectedMealType, ignoreMealTimeLogging, safeChoiceProfile, onboardingGender, subscriptionTier, userName);
          } else {
            throw firstErr;
          }
        }
        if (analysis.error) {
          setError(t.notFoodError);
          setShowNoFoodModal(true);
        } else {
          // Automatically suggest a better meal name if generic
          const nameIsGeneric = !analysis.meal_name || 
                               /^(meal|food|dish|analysed meal|dinner|lunch|breakfast|snack)$/i.test(analysis.meal_name);
          
          if (nameIsGeneric && analysis.foods && analysis.foods.length > 1) {
            analysis.meal_name = generateMealName(analysis.foods);
          }

          if (!analysis.meal_type || analysis.meal_type === 'snack') {
            const inferred = inferMealType(analysis.foods);
            if (!analysis.meal_type) {
              analysis.meal_type = inferred;
            }
          }

          playScanCompleteSound();
          setResult(analysis);
          setOriginalResult(safeClone(analysis));
        }
      } catch (err: any) {
        const errorMsg = err?.message || String(err);
        if (errorMsg === "QUOTA_EXCEEDED") {
          setError(lang === 'ua' ? "Перевищено ліміт запитів до API. Будь ласка, спробуйте пізніше." : "API quota exceeded. Please try again later.");
        } else if (errorMsg === "SERVER_OVERLOADED") {
          setError(lang === 'ua' ? "Сервер перевантажений через високий попит. Будь ласка, зачекайте хвилинку і спробуйте ще раз." : "The AI model is currently experiencing high demand. Please wait a moment and try again.");
        } else if (errorMsg === "TIMEOUT_ERROR") {
          setError(lang === 'ua' ? "Час очікування вичерпано." : "Request timed out.");
        } else {
          console.error('Re-analyze error:', err);
          setError(errorMsg.length > 5 ? errorMsg : t.errorFailed);
        }
      } finally {
        setIsAnalyzingText(false);
      }
    }
  };
  const handleNutrientClick = (key: any, label: string) => {
    setSelectedNutrient({ key, label });
    setView('nutrient_breakdown');
  };
  const removeFromFavorites = (indexOrId: number | string) => {
    let updatedFavorites: HistoryEntry[];
    let removedMealId: string | null = null;
    let removedMealName: string | null = null;

    if (typeof indexOrId === 'string') {
      removedMealId = indexOrId;
      const targetFav = favorites.find(f => f.id === indexOrId);
      removedMealName = targetFav?.meal_name || null;
      updatedFavorites = favorites.filter(f => f.id !== indexOrId);
    } else {
      const targetFav = favorites[indexOrId];
      removedMealId = targetFav?.id || null;
      removedMealName = targetFav?.meal_name || null;
      updatedFavorites = favorites.filter((_, i) => i !== indexOrId);
    }

    setFavorites(updatedFavorites);
    const jsonStr = JSON.stringify(safeClone(updatedFavorites));
    localStorage.setItem('favorites', jsonStr);
    localDb.setItem('favorites', jsonStr);

    if (removedMealId || removedMealName) {
      if (result && ((removedMealId && (result as any).id === removedMealId) || (removedMealName && result.meal_name === removedMealName))) {
        setResult((prev: any) => prev ? { ...prev, isFavorite: false } : prev);
      }
      if (selectedHistoryItem && ((removedMealId && selectedHistoryItem.id === removedMealId) || (removedMealName && selectedHistoryItem.meal_name === removedMealName))) {
        setSelectedHistoryItem(prev => prev ? { ...prev, isFavorite: false } : prev);
      }
      setHistory(prevHist => {
        const hasMatch = prevHist.some(h => ((removedMealId && h.id === removedMealId) || (removedMealName && h.meal_name === removedMealName)) && h.isFavorite);
        if (!hasMatch) return prevHist;
        const nextHist = prevHist.map(h => ((removedMealId && h.id === removedMealId) || (removedMealName && h.meal_name === removedMealName)) ? { ...h, isFavorite: false } : h);
        saveMealHistory(nextHist);
        return nextHist;
      });
    }

    if (user) {
      setDocWithQueue(doc(db, 'users', user.uid), { favorites: safeClone(updatedFavorites) }, { merge: true })
        .catch(err => console.warn('Failed to sync favorites to Firestore:', err));
    }

    triggerHaptic('WATER', vibrateOnLogging);
    toast.success(lang === 'ua' ? 'Страву видалено з улюблених!' : 'Meal removed from favorites!');
  };
  const saveToLogFromFavorites = (meal: HistoryEntry) => {
    sounds.playPop();
    triggerHaptic('MEAL', vibrateOnLogging);
    const newEntry: HistoryEntry = {
      ...safeClone(meal),
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      isFavorite: true
    };
    saveToHistory(newEntry, newEntry.image || '', lang === 'ua' ? 'Прийом їжі збережено!' : 'Meal logged!');
  };
  const handleSafeChoiceSave = (newProfile: SafeChoiceProfile) => {
    setSafeChoiceProfile(newProfile);
    localStorage.setItem('safe_choice_profile', JSON.stringify(safeClone(newProfile)));
    syncSettings({ safeChoiceProfile: newProfile });
    toast.success(lang === 'ua' ? 'Налаштування Safe Choice збережено!' : 'Safe Choice Saved!');
  };
  const handleSkinChange = (newSkin: Skin) => {
    let isUnlockedViaGamification = newSkin === 'default';
    try {
      const cached = localStorage.getItem('gamification_profile_v1');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed.unlocked_themes) && parsed.unlocked_themes.includes(newSkin)) {
          isUnlockedViaGamification = true;
        }
      }
    } catch (_) {}

    if (subscriptionTier === 'free' && newSkin !== 'default' && !isUnlockedViaGamification) {
      triggerHaptic('WARNING');
      toast.info(
        lang === 'ua' 
          ? "Атмосферні скіни доступні у тарифі PRO або відкриваються у Сховищі / Біо-Боксах!" 
          : "Atmospheric skins are available with the PRO plan or unlocked via Bio-Boxes & Redemption Vault!"
      );
      setView('goals');
      setIsSubscriptionExpanded(true);
      setTimeout(() => {
        const s = document.getElementById('subscription-card-settings');
        if (s) s.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
      return;
    }
    if (newSkin === skin) return;
    // Trigger transition animation
    triggerHaptic('SUCCESS');
    setSkinTransitionTarget(newSkin);
    setSkinTransitionActive(true);
    setTimeout(() => {
      setSkin(newSkin);
      localStorage.setItem('app_skin', newSkin);
      syncSettings({ skin: newSkin });
      setView('home');
    }, 600);
    setTimeout(() => {
      setSkinTransitionActive(false);
      toast.success(lang === 'ua' ? 'Скін оновлено!' : 'Skin updated!');
    }, 1200);
  };
  const handleCyberpunkToneChange = (tone: 'yellow-cyan' | 'cyan-magenta') => {
    if (tone === cyberpunkTone) return;
    triggerHaptic('SUCCESS');
    setCyberpunkTone(tone);
    localStorage.setItem('app_cyberpunk_tone', tone);
    syncSettings({ cyberpunkTone: tone });
    sounds.playPop();
    toast.success(lang === 'ua' ? 'Тон оновлено!' : 'Color tone updated!');
  };
  const handleMealPickForChat = (meal: HistoryEntry | any) => {
    if (meal.image) {
      setChatImage(meal.image);
    }
    setChatInput((prev) => {
      const prefix = prev ? prev + "\n" : "";
      return prefix + (lang === 'ua' ? `Я з'їв ${meal.meal_name}` : `I ate ${meal.meal_name}`);
    });
    setIsChatPicking(false);
    setChatPickingSource(null);
    setView('chat');
    sounds.playPop();
  };
  const updateFoodItem = (foodIdx: number, updates: Partial<FoodItem>) => {
    const currentBase = view === 'details' ? selectedHistoryItem : (result || selectedHistoryItem);
    if (!currentBase || !currentBase.foods || !currentBase.foods[foodIdx]) return;
    const baseOriginal = view === 'details' ? currentBase : (originalResult || currentBase);

    const currentFood = currentBase.foods[foodIdx];
    const originalFood = baseOriginal.foods?.[foodIdx] || currentFood;

    let newUnit = updates.unit !== undefined ? updates.unit : (currentFood.unit || 'g');
    let updatedFood: FoodItem = { ...currentFood, ...updates, unit: newUnit };

    if (updates.amount !== undefined && typeof updates.amount === 'number') {
      const origAmt = Number(originalFood.amount) || Number(currentFood.amount) || 100;
      const ratio = origAmt > 0 ? updates.amount / origAmt : 1;

      const origCals = originalFood.calories_kcal || currentFood.calories_kcal || 0;
      const origProt = originalFood.protein_g || currentFood.protein_g || 0;
      const origCarbs = originalFood.carbs_g || currentFood.carbs_g || 0;
      const origFat = originalFood.fat_g || currentFood.fat_g || 0;

      updatedFood = {
        ...updatedFood,
        amount: updates.amount,
        portion: `${updates.amount}${newUnit}`,
        calories_kcal: Number((origCals * ratio).toFixed(1)),
        protein_g: Number((origProt * ratio).toFixed(1)),
        carbs_g: Number((origCarbs * ratio).toFixed(1)),
        fat_g: Number((origFat * ratio).toFixed(1)),
        sugar_g: originalFood.sugar_g !== undefined ? Number(((originalFood.sugar_g || 0) * ratio).toFixed(1)) : currentFood.sugar_g,
        fiber_g: originalFood.fiber_g !== undefined ? Number(((originalFood.fiber_g || 0) * ratio).toFixed(1)) : currentFood.fiber_g,
        sodium_mg: originalFood.sodium_mg !== undefined ? Number(((originalFood.sodium_mg || 0) * ratio).toFixed(1)) : currentFood.sodium_mg,
        potassium_mg: originalFood.potassium_mg !== undefined ? Number(((originalFood.potassium_mg || 0) * ratio).toFixed(1)) : currentFood.potassium_mg,
        caffeine_mg: originalFood.caffeine_mg !== undefined ? Number(((originalFood.caffeine_mg || 0) * ratio).toFixed(1)) : currentFood.caffeine_mg,
      };
    } else if (updates.unit !== undefined) {
      updatedFood.portion = `${updatedFood.amount || 100}${newUnit}`;
    }

    const updatedFoods = currentBase.foods.map((f, i) => i === foodIdx ? updatedFood : f);
    const updatedTotals = recalculateTotalNutrition ? recalculateTotalNutrition(updatedFoods) : {
      calories_kcal: Number(updatedFoods.reduce((sum, f) => sum + (f.calories_kcal || 0), 0).toFixed(1)),
      protein_g: Number(updatedFoods.reduce((sum, f) => sum + (f.protein_g || 0), 0).toFixed(1)),
      carbs_g: Number(updatedFoods.reduce((sum, f) => sum + (f.carbs_g || 0), 0).toFixed(1)),
      fat_g: Number(updatedFoods.reduce((sum, f) => sum + (f.fat_g || 0), 0).toFixed(1)),
      sugar_g: updatedFoods.reduce((sum, f) => sum + (f.sugar_g || 0), 0),
      fiber_g: updatedFoods.reduce((sum, f) => sum + (f.fiber_g || 0), 0),
      sodium_mg: updatedFoods.reduce((sum, f) => sum + (f.sodium_mg || 0), 0),
    };

    const updatedResult = {
      ...currentBase,
      foods: updatedFoods,
      total_nutrition: updatedTotals
    };

    if (view === 'details' && selectedHistoryItem) {
      const updatedHistoryItem = {
        ...selectedHistoryItem,
        ...updatedResult,
        id: selectedHistoryItem.id,
        timestamp: selectedHistoryItem.timestamp,
        image: selectedHistoryItem.image,
      } as HistoryEntry;

      setSelectedHistoryItem(updatedHistoryItem);

      if (selectedHistoryItem.id !== 'temp') {
        const newHistory = history.map(item => item.id === updatedHistoryItem.id ? updatedHistoryItem : item);
        const saved = saveMealHistory(newHistory);
        setHistory(saved);
        if (user) syncHistoryToFirebase(updatedHistoryItem, user.uid);
      }
    } else {
      setResult(updatedResult);
      if (!originalResult) {
        setOriginalResult(safeClone(updatedResult));
      }
      if (selectedHistoryItem) {
        const updatedHistoryItem = {
          ...selectedHistoryItem,
          ...updatedResult,
          id: selectedHistoryItem.id,
          timestamp: selectedHistoryItem.timestamp,
          image: selectedHistoryItem.image,
        } as HistoryEntry;

        setSelectedHistoryItem(updatedHistoryItem);

        if (selectedHistoryItem.id !== 'temp') {
          const newHistory = history.map(item => item.id === updatedHistoryItem.id ? updatedHistoryItem : item);
          const saved = saveMealHistory(newHistory);
          setHistory(saved);
          if (user) syncHistoryToFirebase(updatedHistoryItem, user.uid);
        }
      }
    }
  };
  const updateFoodAmount = (foodIdx: number, newAmount: number) => {
    updateFoodItem(foodIdx, { amount: newAmount });
  };
  const handleFetchPortions = async (foodIdx: number, foodName: string) => {
    setLoadingPortionsIdx(foodIdx);
    try {
      const suggestions = await getAiSuggestedPortions(foodName, lang);
      if (suggestions && suggestions.length > 0 && result) {
        const updatedFoods = result.foods.map((f, idx) => {
          if (idx === foodIdx) {
            return {
              ...f,
              common_portions: suggestions
            };
          }
          return f;
        });
        setResult({
          ...result,
          foods: updatedFoods
        });
        toast.success(lang === 'ua' ? 'Порції оновлено штучним інтелектом!' : 'Portions updated via AI!');
      }
    } catch (err) {
      toast.error(lang === 'ua' ? 'Не вдалося оновити порції.' : 'Failed to update portions.');
    } finally {
      setLoadingPortionsIdx(null);
    }
  };
  const handleParsePortionText = async (foodIdx: number, foodName: string) => {
    const text = (customPortionText[foodIdx] || '').trim();
    if (!text) return;

    setParsingPortionIdx(foodIdx);
    try {
      const parsed = await parseNaturalLanguagePortion(foodName, text, lang);
      if (parsed && result) {
        updateFoodAmount(foodIdx, parsed.weight_g);
        setParseExplanation(prev => ({
          ...prev,
          [foodIdx]: parsed.explanation
        }));
        toast.success(lang === 'ua' ? 'Порцію успішно розраховано!' : 'Portion parsed successfully!');
      }
    } catch (err) {
      toast.error(lang === 'ua' ? 'Не вдалося обробити опис.' : 'Failed to parse portion description.');
    } finally {
      setParsingPortionIdx(null);
    }
  };

  const refreshBioBoxes = useCallback(async () => {
    try {
      const activeUid = user?.uid || auth.currentUser?.uid;
      const boxes = await getUserBioBoxes(activeUid);
      setUserBioBoxes(boxes || []);
      const profile = await getGamificationProfile(activeUid);
      if (profile) {
        setUserTokensCount(profile.token_balance ?? profile.total_tokens_earned ?? 100);
      }
    } catch (e) {
      console.warn('[App] Error refreshing bio-boxes and tokens:', e);
    }
  }, [user?.uid]);

  // ==========================================
  // 5. SIDE EFFECTS & LIFECYCLE LISTENERS
  // ==========================================
  useEffect(() => {
    refreshBioBoxes();
    const handleBoxesUpdated = () => {
      refreshBioBoxes();
    };
    window.addEventListener('bio-boxes-updated', handleBoxesUpdated);
    window.addEventListener('gamification-profile-updated', handleBoxesUpdated);
    window.addEventListener('storage', handleBoxesUpdated);
    return () => {
      window.removeEventListener('bio-boxes-updated', handleBoxesUpdated);
      window.removeEventListener('gamification-profile-updated', handleBoxesUpdated);
      window.removeEventListener('storage', handleBoxesUpdated);
    };
  }, [refreshBioBoxes]);

  useEffect(() => {
    if (isEveningWrapUpTestEnabled) {
      setIsDismissedWrapUp(false);
      return;
    }
    setIsDismissedWrapUp(wrapUpStatus.isDismissed);
  }, [isEveningWrapUpTestEnabled, wrapUpStatus]);
  useEffect(() => {
    const splashTimer = setTimeout(() => {
      setIsMinSplashDone(true);
    }, 2000);
    return () => clearTimeout(splashTimer);
  }, []);
  useEffect(() => {
    const authSafetyTimer = setTimeout(() => {
      setIsAuthLoading(prev => {
        if (prev) {
          console.warn("[AUTH] Auth safety timer (8s) elapsed. Forcing isAuthLoading to false.");
          return false;
        }
        return false;
      });
    }, 8000);
    return () => clearTimeout(authSafetyTimer);
  }, []);
  useEffect(() => {
    if (isGoogleSigningIn) {
      const timer = setTimeout(() => {
        setIsGoogleSigningIn(false);
      }, 60000);
      return () => clearTimeout(timer);
    }
  }, [isGoogleSigningIn]);
  useEffect(() => {
    try {
      localStorage.setItem('storage_check', '1');
      localStorage.removeItem('storage_check');
    } catch (e) {
      setIsStorageBlocked(true);
    }
  }, []);
  useEffect(() => {
    if (!user) return;
  }, [user]);
  useEffect(() => {
    let safetyTimeoutId: any;
    if (isSyncing) {
      setShowSyncIndicator(true);
      // Failsafe timer: prevent any infinite spinning indicator
      safetyTimeoutId = setTimeout(() => {
        setIsSyncing(false);
        setTimeout(() => {
          if (navigator.onLine && !isQuotaExceeded) {
            setShowSyncIndicator(false);
          }
        }, 2000);
      }, 7000);
    }
    return () => {
      if (safetyTimeoutId) clearTimeout(safetyTimeoutId);
    };
  }, [isSyncing]);
  useEffect(() => {
    setOfflineQueueLength(getOfflineQueueLength());
    
    const getIsUa = () => (localStorage.getItem('app_lang') || '').startsWith('ua');

    let isAppJustRefreshed = true;
    const initialLaunchTimer = setTimeout(() => {
      isAppJustRefreshed = false;
    }, 6000);

    const handleOnline = () => {
      const wasOffline = !isOnline;
      setIsOnline(true);
      toast.dismiss('offline-mode-toast');
      if (wasOffline && !isAppJustRefreshed) {
        toast.success(
          getIsUa() 
            ? "Зв'язок відновлено! Синхронізація даних..." 
            : 'Connection restored! Synchronizing data...',
          { id: 'online-restored-toast', duration: 3000 }
        );
      }
      processOfflineQueue();
      flushAuthLogQueue().catch(e => console.warn("[AUTH] Online queue flush warning:", e));
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.error(
        getIsUa()
          ? "⚠️ Втрачено зв'язок. Додаток працює в автономному режимі (Offline Mode)."
          : '⚠️ Connection lost. Application switched to Offline Mode.',
        { id: 'offline-mode-toast', duration: Infinity }
      );
    };
    
    let syncFinishTimer: any = null;
    let syncHideTimer: any = null;
    let syncStartTime = 0;

    const handleQueueUpdated = (e: any) => setOfflineQueueLength(e.detail);
    const handleSyncingStarted = (e?: any) => {
      const isManual = e?.detail?.manual;
      const isLaunch = e?.detail?.isLaunch;

      // Visible sync animation runs on initial app launch, refresh, or when manually requested
      if (!hasRunLaunchSyncRef.current || isManual || isLaunch) {
        if (syncFinishTimer) clearTimeout(syncFinishTimer);
        if (syncHideTimer) clearTimeout(syncHideTimer);
        syncStartTime = Date.now();
        isSyncingRef.current = true;
        setIsSyncing(true);
        setShowSyncIndicator(true);
      }
    };
    const handleSyncSuccess = () => {
      const now = new Date();
      setLastSyncTime(now);
      localStorage.setItem('last_cloud_sync_time', now.toISOString());
      
      // If the sync indicator is active (launch or manual sync), run the 2s Synced sequence then hide
      if (!hasRunLaunchSyncRef.current || isSyncingRef.current || isSyncing) {
        const elapsed = Date.now() - syncStartTime;
        const minSyncingTime = 2000; // Display "Syncing..." for 2 seconds
        const remainingTime = Math.max(0, minSyncingTime - elapsed);

        if (syncFinishTimer) clearTimeout(syncFinishTimer);
        syncFinishTimer = setTimeout(() => {
          isSyncingRef.current = false;
          setIsSyncing(false);
          // Display "Synced <time>" for 2 seconds, then disappear
          if (syncHideTimer) clearTimeout(syncHideTimer);
          syncHideTimer = setTimeout(() => {
            hasRunLaunchSyncRef.current = true;
            if (navigator.onLine && !isQuotaExceeded) {
              setShowSyncIndicator(false);
            }
          }, 2000);
        }, remainingTime);
      }
    };
    const handleSyncingEnded = () => {
      handleSyncSuccess();
    };
    const handleQuotaExceeded = () => {
      isQuotaExceeded = true;
      setIsQuotaExceededState(true);
      setShowSyncIndicator(true);
      toast.error(
        getIsUa()
          ? '⚠️ Денний ліміт бази даних вичерпано. Додаток перейшов у локальний режим (Local Only Mode).'
          : '⚠️ Firestore daily quota exceeded. Application switched to Local Only Mode.',
        { id: 'quota-exceeded-toast', duration: 6000 }
      );
    };
    const handleQuotaCleared = (options?: { silent?: boolean } | Event) => {
      const isSilent = (options && 'silent' in options) ? !!options.silent : false;
      const wasExceeded = isQuotaExceeded || isQuotaExceededState || (typeof window !== 'undefined' && Boolean((window as any).__firestoreQuotaExceeded));
      isQuotaExceeded = false;
      setIsQuotaExceededState(false);
      setShowSyncIndicator(false);
      toast.dismiss('quota-exceeded-toast');
      toast.dismiss('quota-error');
      if (typeof window !== 'undefined') {
        (window as any).__firestoreQuotaExceeded = false;
      }
      // Never show restore notification on initial launch or refresh; only show if quota was previously exceeded during active session
      if (wasExceeded && !isSilent && !isAppJustRefreshed && hasRunLaunchSyncRef.current) {
        toast.success(
          getIsUa()
            ? '✅ Зв’язок із хмарною базою даних відновлено!'
            : '✅ Cloud database connection restored!',
          { id: 'quota-cleared-toast', duration: 3000 }
        );
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('offline-queue-updated', handleQueueUpdated);
    window.addEventListener('syncing-started', handleSyncingStarted);
    window.addEventListener('syncing-ended', handleSyncingEnded);
    window.addEventListener('sync-success', handleSyncSuccess);
    window.addEventListener('firestore-quota-exceeded', handleQuotaExceeded);
    window.addEventListener('firestore-quota-cleared', handleQuotaCleared);

    // Initial launch sync animation: triggers once on launch
    if (!hasRunLaunchSyncRef.current) {
      if (typeof window !== 'undefined' && navigator.onLine && !isQuotaExceeded) {
        handleSyncingStarted({ detail: { isLaunch: true } });
        setTimeout(() => {
          handleSyncSuccess();
        }, 800);
      }
    }

    // Initial check
    if (!navigator.onLine) {
      handleOffline();
      setShowSyncIndicator(true);
    } else if (getOfflineQueueLength() > 0) {
      processOfflineQueue();
    }

    let quotaRecoveryTimer: any = null;
    // Verify Cloud Firestore connection on launch ONLY if app is in quota exceeded mode
    if (isQuotaExceeded || (typeof window !== 'undefined' && (window as any).__firestoreQuotaExceeded)) {
      checkFirestoreConnection().then(isRestored => {
        if (isRestored) {
          handleQuotaCleared({ silent: true });
        } else {
          handleQuotaExceeded();
        }
      }).catch(() => {});
    }

    // Proactive periodic verification: if in local mode, check every 30s to detect daily quota reset (10:00 am)
    const quotaRecoveryInterval = setInterval(() => {
      if (isQuotaExceeded || isQuotaExceededState || (typeof window !== 'undefined' && (window as any).__firestoreQuotaExceeded)) {
        checkFirestoreConnection().then(isRestored => {
          if (isRestored) {
            handleQuotaCleared();
          }
        }).catch(() => {});
      }
    }, 30000);

    return () => {
      if (initialLaunchTimer) clearTimeout(initialLaunchTimer);
      if (syncFinishTimer) clearTimeout(syncFinishTimer);
      if (syncHideTimer) clearTimeout(syncHideTimer);
      if (quotaRecoveryTimer) clearTimeout(quotaRecoveryTimer);
      clearInterval(quotaRecoveryInterval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('offline-queue-updated', handleQueueUpdated);
      window.removeEventListener('syncing-started', handleSyncingStarted);
      window.removeEventListener('syncing-ended', handleSyncingEnded);
      window.removeEventListener('sync-success', handleSyncSuccess);
      window.removeEventListener('firestore-quota-exceeded', handleQuotaExceeded);
      window.removeEventListener('firestore-quota-cleared', handleQuotaCleared);
    };
  }, []);
  useEffect(() => {
    if (showOnboarding && !isTargetsCustomized) {
      const calculated = computeOnboardingTargets();
      setOnboardingProtein(String(calculated.protein));
      setOnboardingCarbs(String(calculated.carbs));
      setOnboardingFat(String(calculated.fat));
      setOnboardingWater(String(calculated.water));
      setOnboardingSugar(String(calculated.sugar));
      setOnboardingSodium(String(calculated.sodium));
    }
  }, [
    showOnboarding,
    onboardingWeight,
    onboardingHeight,
    onboardingAge,
    onboardingGender,
    onboardingActivity,
    onboardingGoal,
    isTargetsCustomized,
    computeOnboardingTargets
  ]);
  useEffect(() => {
    if (!isBackNavigating) {
      if (lastViewRef.current !== view) {
        if (lastViewRef.current !== 'login' && lastViewRef.current !== 'onboarding') {
          setNavigationHistory(prev => [...prev, lastViewRef.current]);
        }
      }
    }
    lastViewRef.current = view;
    setIsBackNavigating(false);
  }, [view, isBackNavigating]);
  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn("Unauthorized or expired session event detected.");
      if (!auth.currentUser) {
        console.log("No active Firebase user; attempting transparent guest session recovery...");
        fetch("/api/auth/session/guest", {
          method: "POST",
          headers: { "Content-Type": "application/json" }
        })
          .then(res => res.ok ? safeJsonResponse(res) : null)
          .then(data => {
            if (data && data.accessToken) {
              saveSession({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                accessTokenExpiresAt: data.accessTokenExpiresAt,
                userId: data.userId,
                email: data.email || ""
              });
              console.log("Guest session recovered seamlessly.");
            }
          })
          .catch(err => console.warn("Guest session recovery notice:", err));
      } else {
        console.log("Active Firebase user exists; preserving state and refreshing server session in background.");
        auth.currentUser.getIdToken(true).then(idToken => {
          return fetch("/api/auth/session/create", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: safeStringify({ idToken, email: auth.currentUser?.email, userId: auth.currentUser?.uid })
          });
        }).then(res => {
          if (res && res.ok) return safeJsonResponse(res);
        }).then(data => {
          if (data && data.accessToken) {
            saveSession({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              accessTokenExpiresAt: data.accessTokenExpiresAt,
              userId: data.userId,
              email: data.email
            });
            console.log("Background session sync recovered.");
          }
        }).catch(err => console.warn("Background session sync recovery failed:", err));
      }
    };

    const handleOpenLogin = () => {
      setView('login');
    };

    window.addEventListener("unauthorized-session-detected", handleUnauthorized);
    window.addEventListener("open-login-modal", handleOpenLogin);
    return () => {
      window.removeEventListener("unauthorized-session-detected", handleUnauthorized);
      window.removeEventListener("open-login-modal", handleOpenLogin);
    };
  }, []);
  useEffect(() => {
    getRedirectResult(auth).then(async (result) => {
      if (result && result.user) {
        if (localStorage.getItem('google_fit_pending_link') === 'true') {
          localStorage.removeItem('google_fit_pending_link');
          const credential = GoogleAuthProvider.credentialFromResult(result);
          const token = credential?.accessToken;
          
          if (token) {
            try {
              const userRef = doc(db, 'users', result.user.uid);
              await setDocWithQueue(userRef, {
                googleFitConnected: true,
                googleFitAccessToken: token,
                googleFitTokenTimestamp: Date.now()
              });
              
              localStorage.setItem('google_fit_connected', 'true');
              localStorage.setItem('google_fit_access_token', token);
              localStorage.setItem('google_fit_token_timestamp', String(Date.now()));
              
              toast.success(lang === 'ua' ? 'Успішно підключено до Google Fit!' : 'Successfully linked to Google Fit!');
              window.dispatchEvent(new CustomEvent('google-fit-connected'));
              
              if (view === 'login' || view === 'onboarding') setView('home');
              else setView('goals');
              
            } catch (err) {
              console.error("[AUTH] Failed to update Google Fit from redirect:", err);
              addAuthLog('GoogleFitRedirect_Update', 'db_error', err);
              toast.error(lang === 'ua' ? 'Помилка підключення до Google Fit' : 'Failed to connect Google Fit');
            }
          } else {
            addAuthLog('GoogleFitRedirect_NoToken', 'missing_scopes', 'Scopes might not have been granted');
            toast.error(lang === 'ua' 
              ? 'Доступ до даних Google Fit не отримано.' 
              : 'Google Fit access token missing.');
          }
        } else {
          setShowOnboarding(false);
          setView('home');
        }
      }
    }).catch(error => {
      console.warn("[AUTH] getRedirectResult notice:", error?.message || error);
      if (localStorage.getItem('google_fit_pending_link') === 'true') {
        addAuthLog('GoogleFitRedirect_Catch', error.code || 'unknown', error.message || error);
        localStorage.removeItem('google_fit_pending_link');
        toast.error(lang === 'ua' ? 'Помилка авторизації Google Fit' : 'Google Fit authorization failed');
      }
      // Only set loginError if it is a genuine Firebase user-facing auth error and user is not authenticated
      if (error?.code && error.code.startsWith('auth/') && error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request' && !auth.currentUser) {
        setLoginError(error);
      }
    });

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        const detectedEmail = firebaseUser ? (firebaseUser.email || firebaseUser.providerData?.find((p: any) => p?.email)?.email || '') : '';
        
        if (firebaseUser) {
          setLoginError(null);
          localStorage.removeItem('explicit_logout');
          const emailToStore = detectedEmail || '';
          const prevEmail = localStorage.getItem('user_email');
          const prevUid = localStorage.getItem('user_uid');
          
          const isRealPrevEmail = Boolean(prevEmail && prevEmail.includes('@') && !prevEmail.startsWith('guest'));
          const isRealCurrentEmail = Boolean(emailToStore && emailToStore.includes('@') && !emailToStore.startsWith('guest'));
          const isDifferentRegisteredUser = isRealPrevEmail && isRealCurrentEmail && prevEmail.trim().toLowerCase() !== emailToStore.trim().toLowerCase();
          const isRealPrevUid = Boolean(prevUid && !prevUid.startsWith('guest') && prevUid !== firebaseUser.uid);

          if (isDifferentRegisteredUser && isRealPrevUid) {
             console.log(`[AUTH] User changed from UID:${prevUid}/Email:${prevEmail} to UID:${firebaseUser.uid}/Email:${emailToStore}. Clearing stale local cache.`);
             localStorage.removeItem('userName');
             localStorage.removeItem('custom_avatar');
             localStorage.removeItem('user_goals');
             localStorage.removeItem('meal_history');
             localStorage.removeItem('water_logs');
             localStorage.removeItem('favorites');
             localStorage.removeItem('app_lang');
             localStorage.removeItem('theme');
             localStorage.removeItem('onboarding_completed');
             localStorage.removeItem('google_fit_connected');
             localStorage.removeItem('google_fit_access_token');
             localStorage.removeItem('google_fit_token_timestamp');
             localStorage.removeItem('google_fit_pending_link');
             localStorage.removeItem('grocery_items');
             localStorage.removeItem('scanned_products');
             localStorage.removeItem('weight_logs');
             localStorage.removeItem('main_nutrition_boxes');
             localStorage.removeItem('main_nutrition_crates');
             localStorage.removeItem('main_nutrition_loot_inventory');
             localStorage.removeItem('main_nutrition_fasting_state');
             localStorage.removeItem('safe_choice_profile');

             // Also purge IndexedDB / LocalDb cache to prevent data bleed
             localDb.removeItem('meal_history');
             localDb.removeItem('water_logs');
             localDb.removeItem('favorites');
             localDb.removeItem('scanned_products');
             localDb.removeItem('weight_logs');
             localDb.removeItem('user_goals');
             localDb.removeItem('userName');
             localDb.removeItem('chat_history');
             
             // Clear in-memory state
             setUserName('');
             setCustomAvatar('');
             setHistory([]);
             setWaterLogs([]);
             setFavorites([]);
             setGroceryItems([]);
             setScannedProducts([]);
             setWeightLogs([]);
             setUserBioBoxes([]);
             setGoals(DEFAULT_GOALS);
          }
          localStorage.setItem('user_uid', firebaseUser.uid);
          setUserDocEmail(emailToStore);
          if (emailToStore) {
            localStorage.setItem('user_email', emailToStore);
          }
          const existingSavedName = (localStorage.getItem('userName') || userNameRef.current || '').trim();
          if (firebaseUser.displayName && (!existingSavedName || existingSavedName === 'null')) {
            localStorage.setItem('userName', firebaseUser.displayName);
            setUserName(firebaseUser.displayName);
            setOnboardingName(firebaseUser.displayName);
          }
          const detectedPhoto = firebaseUser.photoURL || firebaseUser.providerData?.find((p: any) => p?.photoURL)?.photoURL || '';
          if (detectedPhoto) {
            localStorage.setItem('custom_avatar', detectedPhoto);
            setCustomAvatar(detectedPhoto);
            setAvatarImageError(false);
          }
          setUser(firebaseUser);
          
          // Immediately release loading states and transition away from login
          setIsAuthLoading(false);
          setIsGoogleSigningIn(false);
          setView(prev => (prev === 'login' ? 'home' : prev));

          // Synchronize server-side custom session non-blockingly in background
          (async () => {
            try {
              const existingSession = getSession();
              const isSessionExpired = !existingSession || Date.now() >= (existingSession.accessTokenExpiresAt - 60000);
              if (!existingSession || existingSession.userId !== firebaseUser.uid || isSessionExpired) {
                await forceRecreateSessionWithFirebase(true);
                console.log("Server custom session synchronized successfully.");
              }
            } catch (err: any) {
              console.warn("Authentication session synchronization failed:", err);
            }
          })();

          // Trigger queue flush after successful auth resolution
          flushAuthLogQueue().catch(e => console.warn("[AUTH] Queue flush warning:", e));
        } else {
          // Transitioning from authenticated to unauthenticated state:
          // ONLY clear session if the user explicitly clicked Log Out!
          const isExplicitLogout = typeof localStorage !== 'undefined' && localStorage.getItem('explicit_logout') === 'true';
          if (isExplicitLogout) {
            clearSession();
            setUserDocEmail('');
            setUserName('');
            setCustomAvatar('');
            setHistory([]);
            setWaterLogs([]);
            setFavorites([]);
            setGroceryItems([]);
            setScannedProducts([]);
            setWeightLogs([]);
            setUserBioBoxes([]);
            setAiMealSuggestions([]);
            setGoals(DEFAULT_GOALS);

            localStorage.removeItem('user_uid');
            localStorage.removeItem('user_email');
            localStorage.removeItem('userName');
            localStorage.removeItem('custom_avatar');
            localStorage.removeItem('user_goals');
            localStorage.removeItem('meal_history');
            localStorage.removeItem('water_logs');
            localStorage.removeItem('favorites');
            localStorage.removeItem('grocery_items');
            localStorage.removeItem('scanned_products');
            localStorage.removeItem('weight_logs');
            localStorage.removeItem('main_nutrition_boxes');
            localStorage.removeItem('main_nutrition_crates');
            localStorage.removeItem('main_nutrition_loot_inventory');
            localStorage.removeItem('main_nutrition_fasting_state');
            localStorage.removeItem('safe_choice_profile');
            localStorage.removeItem('google_fit_connected');
            localStorage.removeItem('google_fit_access_token');
            localStorage.removeItem('google_fit_token_timestamp');
            localStorage.removeItem('google_fit_pending_link');

            localDb.removeItem('meal_history');
            localDb.removeItem('water_logs');
            localDb.removeItem('favorites');
            localDb.removeItem('scanned_products');
            localDb.removeItem('weight_logs');
            localDb.removeItem('user_goals');
            localDb.removeItem('userName');
            localDb.removeItem('chat_history');

            setShowOnboarding(false);
            setUser(null);
            setView('login');
          } else {
            console.log("[AUTH] No active Firebase user yet; preserving local storage & cached session.");
            const cachedEmail = typeof localStorage !== 'undefined' ? localStorage.getItem('user_email') : null;
            if (cachedEmail) {
              setUserDocEmail(cachedEmail);
              setView(prev => (prev === 'login' ? 'home' : prev));
            }
          }
        }
      } catch (authCallbackErr) {
        console.error("[AUTH] Error in onAuthStateChanged handler:", authCallbackErr);
      } finally {
        setIsAuthLoading(false);
        setIsGoogleSigningIn(false);
        setIsLinkingAuth(false);
      }
    });
    return () => unsubscribe();
  }, [handleFirestoreErrorLocal, isQuotaExceededState]);
  useEffect(() => {
    if (user && user.uid && !isAuthLoading) {
      const activeAuthEmail = user.email || user.providerData?.find((p: any) => p?.email)?.email;
      const cachedEmail = localStorage.getItem('user_email');
      
      const isRealCachedEmail = Boolean(cachedEmail && cachedEmail.includes('@') && !cachedEmail.startsWith('guest'));
      const isRealActiveEmail = Boolean(activeAuthEmail && activeAuthEmail.includes('@') && !activeAuthEmail.startsWith('guest'));
      const isDifferentEmail = isRealCachedEmail && isRealActiveEmail && activeAuthEmail.trim().toLowerCase() !== cachedEmail.trim().toLowerCase();

      if (isDifferentEmail) {
        console.warn(`[SESSION VALIDATOR] Account switch detected: Auth email is ${activeAuthEmail}, but local storage was bound to ${cachedEmail}. Synchronizing local cache.`);
        
        // Wipe stale user-specific state to prevent cross-account leakage
        clearSession();
        localStorage.removeItem('user_email');
        localStorage.removeItem('userName');
        localStorage.removeItem('custom_avatar');
        localStorage.removeItem('user_goals');
        localStorage.removeItem('meal_history');
        localStorage.removeItem('water_logs');
        localStorage.removeItem('favorites');
        localStorage.removeItem('google_fit_connected');
        localStorage.removeItem('google_fit_access_token');
        localStorage.removeItem('google_fit_token_timestamp');
        localStorage.removeItem('google_fit_pending_link');
        
        // Correct the cache to the actual active user
        localStorage.setItem('user_email', activeAuthEmail);
        setUserDocEmail(activeAuthEmail);
        setUserName(user.displayName || '');
        setHistory([]);
        setWaterLogs([]);
        setFavorites([]);
      }
    }
  }, [user, isAuthLoading]);
  useEffect(() => {
    localStorage.setItem('chat_background', chatBackground);
  }, [chatBackground]);
  useEffect(() => {
    localStorage.setItem('chat_texture_color', chatTextureColor);
  }, [chatTextureColor]);
  useEffect(() => {
    localStorage.setItem('color_cycle_enabled', String(colorCycleEnabled));
  }, [colorCycleEnabled]);
  useEffect(() => {
    if (!colorCycleEnabled || view !== 'chat' || chatBackground === 'default') return;

    const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#64748b'];
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const totalCycle = colors.length * 7000; // 7 seconds per color
      const currentElapsed = elapsed % totalCycle;
      
      const index = Math.floor(currentElapsed / 7000);
      const nextIndex = (index + 1) % colors.length;
      const factor = (currentElapsed % 7000) / 7000; // 0 to 1

      const c1 = colors[index];
      const c2 = colors[nextIndex];

      const r1 = parseInt(c1.substring(1, 3), 16);
      const g1 = parseInt(c1.substring(3, 5), 16);
      const b1 = parseInt(c1.substring(5, 7), 16);

      const r2 = parseInt(c2.substring(1, 3), 16);
      const g2 = parseInt(c2.substring(3, 5), 16);
      const b2 = parseInt(c2.substring(5, 7), 16);

      const r = Math.round(r1 + factor * (r2 - r1));
      const g = Math.round(g1 + factor * (g2 - g1));
      const b = Math.round(b1 + factor * (b2 - b1));

      const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      setChatTextureColor(hex);
    }, 250);

    return () => clearInterval(interval);
  }, [colorCycleEnabled, view, chatBackground]);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (chatMenuRef.current && !chatMenuRef.current.contains(event.target as Node)) {
        setShowChatMenu(false);
      }
    }
    if (showChatMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showChatMenu]);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);
  useEffect(() => {
    let threads: ChatThread[] = [];
    const savedThreads = localDb.getItemSync('chat_threads');
    if (savedThreads) {
      try {
        const parsed = JSON.parse(savedThreads);
        if (Array.isArray(parsed)) {
          threads = parsed;
          setChatThreads(parsed);
        }
      } catch (e) {
        console.warn("Failed to parse chat threads", e);
      }
    }

    let activeId = localDb.getItemSync('chat_active_thread_id');
    const savedChat = localDb.getItemSync('chat_history');
    
    if (savedChat && threads.length === 0) {
      try {
        const parsed = JSON.parse(savedChat);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Create a migrated thread
          const firstUserMsg = parsed.find(m => m.role === 'user');
          const title = firstUserMsg ? (firstUserMsg.content.slice(0, 30) + (firstUserMsg.content.length > 30 ? '...' : '')) : 'Migrated Chat';
          const newThread: ChatThread = {
            id: 'thread_' + Date.now(),
            title,
            timestamp: Date.now(),
            messages: parsed
          };
          threads = [newThread];
          setChatThreads(threads);
          localDb.setItem('chat_threads', JSON.stringify(threads));
          
          activeId = newThread.id;
          setActiveThreadId(activeId);
          localDb.setItem('chat_active_thread_id', activeId);
          setChatMessages(parsed);
        }
      } catch (e) {
        console.error("Failed to migrate legacy chat history", e);
      }
    } else {
      if (activeId) {
        setActiveThreadId(activeId);
        const activeThread = threads.find(t => t.id === activeId);
        if (activeThread) {
          setChatMessages(activeThread.messages);
        }
      } else if (threads.length > 0) {
        // If there's no active ID but we have threads, load the most recent one
        const sorted = [...threads].sort((a, b) => b.timestamp - a.timestamp);
        setActiveThreadId(sorted[0].id);
        setChatMessages(sorted[0].messages);
        localDb.setItem('chat_active_thread_id', sorted[0].id);
      }
    }

    // Resume pending message if any
    if (savedChat) {
      try {
        const parsed = JSON.parse(savedChat);
        if (Array.isArray(parsed)) {
          const lastMsg = parsed[parsed.length - 1];
          if (lastMsg && lastMsg.role === 'user' && lastMsg.isPending) {
            console.log('Detected pending chat message on boot, resuming...');
            setTimeout(() => {
              handleChatSubmit(undefined, lastMsg.content, lastMsg.image, true);
            }, 1000);
          }
        }
      } catch (e) {}
    }
  }, []);
  useEffect(() => {
    if (chatMessages.length > 0) {
      saveChatHistory(safeClone(chatMessages));
      localStorage.setItem('chat_history', JSON.stringify(safeClone(chatMessages)));

      setChatThreads(prevThreads => {
        let currentId = activeThreadId;
        let updatedThreads = [...prevThreads];

        if (!currentId) {
          // If there is no active thread ID, create one
          currentId = 'thread_' + Date.now();
          setActiveThreadId(currentId);
          localDb.setItem('chat_active_thread_id', currentId);
        }

        const firstUserMsg = chatMessages.find(m => m.role === 'user');
        const title = firstUserMsg 
          ? (firstUserMsg.content.slice(0, 30) + (firstUserMsg.content.length > 30 ? '...' : '')) 
          : 'New Chat';

        const existingIndex = updatedThreads.findIndex(t => t.id === currentId);
        if (existingIndex > -1) {
          // Update existing thread
          updatedThreads[existingIndex] = {
            ...updatedThreads[existingIndex],
            messages: safeClone(chatMessages),
            timestamp: Date.now(),
            // Only update title if it was default
            title: updatedThreads[existingIndex].title === 'New Chat' || updatedThreads[existingIndex].title === 'Migrated Chat' ? title : updatedThreads[existingIndex].title
          };
        } else {
          // Insert new thread
          updatedThreads.push({
            id: currentId!,
            title,
            timestamp: Date.now(),
            messages: safeClone(chatMessages)
          });
        }

        localDb.setItem('chat_threads', JSON.stringify(updatedThreads));
        return updatedThreads;
      });
    } else {
      localDb.removeItem('chat_history');
      localStorage.removeItem('chat_history');
    }
  }, [chatMessages, activeThreadId]);
  useEffect(() => {
    if (view === 'chat') {
      scrollToBottom(true);
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });
    }
  }, [view]);
  useEffect(() => {
    if (view !== 'chat') {
      setIsScrollingChat(false);
      return;
    }

    let isScrollingChatActive = false;
    const handleScroll = () => {
      if (!isScrollingChatActive) {
        isScrollingChatActive = true;
        setIsScrollingChat(true);
      }
      if (chatScrollTimeoutRef.current) {
        clearTimeout(chatScrollTimeoutRef.current);
      }
      chatScrollTimeoutRef.current = setTimeout(() => {
        isScrollingChatActive = false;
        setIsScrollingChat(false);
      }, 400);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (chatScrollTimeoutRef.current) {
        clearTimeout(chatScrollTimeoutRef.current);
      }
    };
  }, [view]);
  useEffect(() => {
    if (view === 'chat' && chatMessages.length > 0) {
      if (chatMessages.length > prevChatMessagesLengthRef.current) {
        const lastMsg = chatMessages[chatMessages.length - 1];
        const lastIdx = chatMessages.length - 1;
        // When AI starts responding, scroll so the AI message is visible at the top
        if (lastMsg.role === 'ai') {
          setTimeout(() => {
            const element = document.getElementById(`chat-message-${lastIdx}`);
            if (element && typeof element.getBoundingClientRect === 'function') {
              const yOffset = -120; // Offset for sticky header
              const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
              window.scrollTo({
                top: Math.max(0, y),
                behavior: 'smooth'
              });
            } else {
              scrollToBottom();
            }
          }, 80);
        } else {
          scrollToBottom();
        }
      }
      prevChatMessagesLengthRef.current = chatMessages.length;
    }
  }, [view, chatMessages]);
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isAnalyzing || isAnalyzingText) {
      // Use wall-clock time so background-tab throttling of setInterval doesn't affect accuracy.
      // Browser can throttle setInterval to ~1fps (or more) in background tabs — a counter-based
      // watchdog would fire minutes too late. Date.now() is always accurate regardless of throttling.
      const startedAt = Date.now();
      const maxMs = (isAnalyzing ? 45 : 30) * 1000;
      setAnalysisTimer(0);
      interval = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const elapsedSeconds = Math.floor(elapsed / 1000);
        setAnalysisTimer(elapsedSeconds);
        if (elapsed >= maxMs) {
          // Watchdog: analysis exceeded max allowed time — reset state and show retry message.
          // Per AGENTS.md §1: it is strictly forbidden to silently substitute a failed scan
          // with a hardcoded fallback. Always present an honest error + retry option.
          setIsAnalyzing(false);
          setIsAnalyzingText(false);
          setError(lang === 'ua'
            ? 'Час очікування аналізу вичерпано. Будь ласка, спробуйте ще раз.'
            : 'Analysis timed out. Please try again.');
        }
      }, 1000);
    } else {
      setAnalysisTimer(0);
    }
    return () => clearInterval(interval);
  }, [isAnalyzing, isAnalyzingText, lang]);
  useEffect(() => {
    let interval: any;
    if (isAnalyzing || isAnalyzingText) {
      interval = setInterval(() => {
        setSkeletonVariation(prev => (prev + 1) % 3);
      }, 3000);
    } else {
      setSkeletonVariation(0);
    }
    return () => clearInterval(interval);
  }, [isAnalyzing, isAnalyzingText]);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAnalyzing) {
      setAnalysisStep(0);
      setAnalysisProgress(0);
      interval = setInterval(() => {
        setAnalysisProgress((prev) => {
          if (prev >= 95) return prev; // Cap at 95 until done
          const next = prev + Math.random() * 7 + 3;
          return next > 95 ? 95 : next;
        });
      }, 120);
    }
    return () => clearInterval(interval);
  }, [isAnalyzing]);
  useEffect(() => {
    const stepsCount = 11;
    const stepSize = 100 / stepsCount;
    const currentStep = Math.floor(analysisProgress / stepSize);
    setAnalysisStep(currentStep >= stepsCount ? stepsCount - 1 : currentStep);
  }, [analysisProgress]);
  useEffect(() => {
    if (view === 'history') {
      localDb.getArchivedMeals().then(meals => {
        setArchivedCount(meals.length);
      }).catch(err => {
        console.error("Failed to load archive count:", err);
      });
    }
  }, [view, history]);
  useEffect(() => {
    setIsHistoryHeaderFolded(false);
  }, [view]);
  useEffect(() => {
    setIsSelectionMode(false);
    setSelectedMealIds([]);
  }, [view, historyTab]);
  useEffect(() => {
    multiplierMotionValue.set(mealScaleMultiplier);
  }, [mealScaleMultiplier, multiplierMotionValue]);
  useEffect(() => {
    const unsub = springMultiplier.on("change", (latest: number) => {
      setAnimatedMultiplier(latest);
    });
    return () => unsub();
  }, [springMultiplier]);

  useEffect(() => {
    if (justFinishedOnboarding && view === 'home') {
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 100 };

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      const interval: any = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 250);

      const timeout = setTimeout(() => {
        setJustFinishedOnboarding(false);
      }, duration + 1000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [justFinishedOnboarding, view]);
  useEffect(() => {
    if (isAnalyzingText) {
      setChatThinkingStep(0);
      const interval = setInterval(() => {
        setChatThinkingStep(prev => prev + 1);
      }, 4000);
      return () => clearInterval(interval);
    } else {
      setChatThinkingStep(0);
    }
  }, [isAnalyzingText]);
  useEffect(() => {
    autoThemeEnabledRef.current = autoThemeEnabled;
  }, [autoThemeEnabled]);
  useEffect(() => {
    if (!autoThemeEnabled) return;

    const lastFetched = localStorage.getItem('auto_theme_last_fetched');
    const todayStr = new Date().toDateString();
    
    // If we fetched today already, we skip fetching to avoid repeated prompts/API requests
    if (lastFetched === todayStr) {
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          fetch(`https://api.sunrise-sunset.org/json?lat=${lat}&lng=${lng}&formatted=0`)
            .then(res => res.text())
            .then(text => {
              if (!text || text.trim() === '' || text.trim() === 'undefined' || text.trim() === 'null') return null;
              try {
                return JSON.parse(text);
              } catch (e) {
                return null;
              }
            })
            .then(apiData => {
              if (apiData && apiData.status === 'OK' && apiData.results) {
                const sunriseISO = apiData.results.sunrise;
                const sunsetISO = apiData.results.sunset;
                
                const sunriseDate = new Date(sunriseISO);
                const sunsetDate = new Date(sunsetISO);
                
                const sunriseHHMM = `${sunriseDate.getHours().toString().padStart(2, '0')}:${sunriseDate.getMinutes().toString().padStart(2, '0')}`;
                const sunsetHHMM = `${sunsetDate.getHours().toString().padStart(2, '0')}:${sunsetDate.getMinutes().toString().padStart(2, '0')}`;
                
                setAutoThemeSunrise(sunriseHHMM);
                setAutoThemeSunset(sunsetHHMM);
                setAutoThemeLocationActive(true);
                
                localStorage.setItem('auto_theme_sunrise', sunriseHHMM);
                localStorage.setItem('auto_theme_sunset', sunsetHHMM);
                localStorage.setItem('auto_theme_location_active', 'true');
                localStorage.setItem('auto_theme_last_fetched', todayStr);
                
                syncSettings({
                  autoThemeSunrise: sunriseHHMM,
                  autoThemeSunset: sunsetHHMM,
                  autoThemeLocationActive: true,
                  autoThemeLastFetched: todayStr
                });
              } else {
                localStorage.setItem('auto_theme_last_fetched', todayStr);
              }
            })
            .catch(err => {
              console.warn("Failed to fetch solar times:", err);
              localStorage.setItem('auto_theme_last_fetched', todayStr);
            });
        },
        (err) => {
          console.warn("Geolocation failed or denied:", err);
          setAutoThemeLocationActive(false);
          localStorage.setItem('auto_theme_location_active', 'false');
          localStorage.setItem('auto_theme_last_fetched', todayStr);
          syncSettings({ 
            autoThemeLocationActive: false,
            autoThemeLastFetched: todayStr
          });
        }
      );
    } else {
      setAutoThemeLocationActive(false);
      localStorage.setItem('auto_theme_location_active', 'false');
      localStorage.setItem('auto_theme_last_fetched', todayStr);
      syncSettings({ 
        autoThemeLocationActive: false,
        autoThemeLastFetched: todayStr
      });
    }
  }, [autoThemeEnabled]);
  useEffect(() => {
    if (!autoThemeEnabled) return;

    const checkTheme = () => {
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      const sunriseStr = autoThemeSunrise && autoThemeSunrise.includes(':') ? autoThemeSunrise : '06:00';
      const sunsetStr = autoThemeSunset && autoThemeSunset.includes(':') ? autoThemeSunset : '20:00';

      const [sunriseH, sunriseM] = sunriseStr.split(':').map(Number);
      const sunriseMinutes = (isNaN(sunriseH) ? 6 : sunriseH) * 60 + (isNaN(sunriseM) ? 0 : sunriseM);

      const [sunsetH, sunsetM] = sunsetStr.split(':').map(Number);
      const sunsetMinutes = (isNaN(sunsetH) ? 20 : sunsetH) * 60 + (isNaN(sunsetM) ? 0 : sunsetM);

      const isDay = currentMinutes >= sunriseMinutes && currentMinutes < sunsetMinutes;
      const targetTheme = isDay ? 'light' : 'dark';

      if (themeRef.current !== targetTheme) {
        setTheme(targetTheme);
        localStorage.setItem('theme', targetTheme);
        // DO NOT call syncSettings with the auto-calculated theme to avoid Firestore/cache updates race-condition loop
      }
    };

    // Run once on load/enable
    checkTheme();

    // Check every 30 seconds
    const interval = setInterval(checkTheme, 30000);
    return () => clearInterval(interval);
  }, [autoThemeEnabled, autoThemeSunrise, autoThemeSunset]);
  useEffect(() => {
    const selected = ILLUMINATION_COLORS.find(c => c.id === previewIlluminationColor);
    if (selected) {
      document.documentElement.style.setProperty('--illumination-color', selected.rgb);
      if (previewIlluminationColor === 'emerald' || previewIlluminationColor === 'original') {
        document.documentElement.style.setProperty('--illumination-food-color', '16, 185, 129'); // green
        document.documentElement.style.setProperty('--illumination-water-color', '59, 130, 246'); // blue
      } else {
        document.documentElement.style.setProperty('--illumination-food-color', selected.rgb);
        document.documentElement.style.setProperty('--illumination-water-color', selected.rgb);
      }
    }
  }, [previewIlluminationColor]);
  useEffect(() => {
    let interval: any;
    if (isReCalculating) {
      setRecalcStep(0);
      interval = setInterval(() => {
        setRecalcStep(prev => prev + 1);
      }, 700);
    } else {
      setRecalcStep(0);
    }
    return () => clearInterval(interval);
  }, [isReCalculating]);
  useEffect(() => {
    const handleConnected = () => setIsGoogleFitConnected(true);
    const handleDisconnected = () => setIsGoogleFitConnected(false);
    window.addEventListener('google-fit-connected', handleConnected);
    window.addEventListener('google-fit-disconnected', handleDisconnected);

    const handleAppleConnected = () => setIsAppleHealthConnected(true);
    const handleAppleDisconnected = () => setIsAppleHealthConnected(false);
    window.addEventListener('apple-health-connected', handleAppleConnected);
    window.addEventListener('apple-health-disconnected', handleAppleDisconnected);

    return () => {
      window.removeEventListener('google-fit-connected', handleConnected);
      window.removeEventListener('google-fit-disconnected', handleDisconnected);
      window.removeEventListener('apple-health-connected', handleAppleConnected);
      window.removeEventListener('apple-health-disconnected', handleAppleDisconnected);
    };
  }, []);
  useEffect(() => {
    setExpandedPortionIdx(null);
  }, [expandedFoodIdx]);
  useEffect(() => {
    setIsAdjustPortionExpanded(false);
    setIsAdjustPortionModalOpen(false);
  }, [selectedHistoryItem, result]);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (periodDropdownRef.current && !periodDropdownRef.current.contains(event.target as Node)) {
        setIsPeriodDropdownOpen(false);
      }
    }
    if (isPeriodDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isPeriodDropdownOpen]);
  useEffect(() => {
    if (!user) return;
    
    // Fetch if connected
    const isConnected = localStorage.getItem('google_fit_connected') === 'true';
    if (isConnected) {
      fetchGoogleFitData();
    } else {
      setGoogleFitData([]);
    }
    
    // Listen for connection events to auto-refresh
    const handleConnected = () => {
      fetchGoogleFitData();
    };
    const handleDisconnected = () => {
      setGoogleFitData([]);
    };
    
    window.addEventListener('google-fit-connected', handleConnected);
    window.addEventListener('google-fit-disconnected', handleDisconnected);
    
    return () => {
      window.removeEventListener('google-fit-connected', handleConnected);
      window.removeEventListener('google-fit-disconnected', handleDisconnected);
    };
  }, [user, fetchGoogleFitData]);
  useEffect(() => {
    localStorage.setItem('chat_input_draft', chatInput);
  }, [chatInput]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const search = window.location.search;
    if (!search) return;

    const urlParams = new URLSearchParams(search);
    const paymentStatus = urlParams.get('payment');
    const paymentTier = urlParams.get('tier');

    if (paymentStatus === 'success') {
      const targetTier = (paymentTier === 'premium' ? 'premium' : 'pro') as ('pro' | 'premium');
      setSubscriptionTier(targetTier);
      localStorage.setItem('subscription_tier', targetTier);
      localStorage.setItem('subscription_trial_active', 'false');
      syncSettings({ subscriptionTier: targetTier, subscriptionTrialActive: false });

      if (typeof confetti === 'function') {
        confetti({
          particleCount: 200,
          spread: 90,
          origin: { y: 0.5 }
        });
      }

      toast.success(
        lang === 'ua'
          ? `Оплата успішна! Тариф оновлено до ${targetTier.toUpperCase()} 🎉`
          : `Payment successful! Your tier is upgraded to ${targetTier.toUpperCase()} 🎉`,
        { duration: 6000 }
      );

      // Clean query parameters from URL
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    } else if (paymentStatus === 'cancel') {
      toast.info(
        lang === 'ua'
          ? 'Оформлення підписки скасовано.'
          : 'Subscription checkout canceled.'
      );
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [lang]);
  useEffect(() => {
    window.addEventListener('collapse-all-spoilers', handleCollapseAllSpoilers);
    return () => window.removeEventListener('collapse-all-spoilers', handleCollapseAllSpoilers);
  }, [handleCollapseAllSpoilers]);
  useEffect(() => {
    if ((prevViewRef.current as string) === 'settings' && (view as string) !== 'settings') {
      // User left the settings/account page -> trigger 3s collapse for settings spoilers
      spoilerManager.notifyPageLeave();
    }
    prevViewRef.current = view;
  }, [view]);
  useEffect(() => {
    const handleVisibilityOrBlur = () => {
      if ((view as string) === 'settings' && (document.visibilityState === 'hidden' || !document.hasFocus())) {
        spoilerManager.notifyPageLeave();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrBlur);
    window.addEventListener('blur', handleVisibilityOrBlur);
    window.addEventListener('pagehide', handleVisibilityOrBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrBlur);
      window.removeEventListener('blur', handleVisibilityOrBlur);
      window.removeEventListener('pagehide', handleVisibilityOrBlur);
    };
  }, [view]);

  // Tab-return resilience: when the user switches back to this tab after it was idle/backgrounded,
  // reset any stuck analysis state (the async Promise may have been throttled or resolved silently)
  // and proactively re-validate the session token so the next user action works immediately.
  useEffect(() => {
    const handleTabReturn = () => {
      if (document.visibilityState !== 'visible') return;

      // If analysis was in progress while the tab went to background, the async call
      // may have resolved (or timed-out) without triggering the finally{} block in some
      // browser throttling scenarios. Reset state so UI is not stuck in loading mode.
      if (isAnalyzing || isAnalyzingText) {
        setIsAnalyzing(false);
        setIsAnalyzingText(false);
        setError(lang === 'ua'
          ? 'Аналіз перервано (вкладка була у фоні). Будь ласка, спробуйте ще раз.'
          : 'Analysis interrupted (tab was in background). Please try again.');
      }

      // Silently refresh the access token in the background so the first action after
      // returning to the tab (logging a meal, adding water, etc.) never hits a 401.
      asyncGetAccessToken(true).catch(() => {});
    };

    document.addEventListener('visibilitychange', handleTabReturn);
    return () => document.removeEventListener('visibilitychange', handleTabReturn);
  }, [isAnalyzing, isAnalyzingText, lang]);

  useEffect(() => { customAvatarRef.current = customAvatar; }, [customAvatar]);
  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);
  useEffect(() => {
    goalsRef.current = rawGoals;
  }, [rawGoals]);
  useEffect(() => {
    userNameRef.current = userName;
  }, [userName]);
  useEffect(() => {
    langRef.current = lang;
  }, [lang]);
  useEffect(() => {
    autoThemeSunriseRef.current = autoThemeSunrise;
  }, [autoThemeSunrise]);
  useEffect(() => {
    autoThemeSunsetRef.current = autoThemeSunset;
  }, [autoThemeSunset]);
  useEffect(() => {
    autoThemeLocationActiveRef.current = autoThemeLocationActive;
  }, [autoThemeLocationActive]);
  useEffect(() => {
    userMetricsRef.current = userMetrics;
  }, [userMetrics]);
  useEffect(() => {
    if (user) {
      evaluateGamificationState();
    }
  }, [user]);
  useEffect(() => {
    const handleOnline = () => {
      // Handle old pending_sync array
      const pendingSync = localStorage.getItem('pending_sync');
      if (pendingSync && pendingSync !== 'undefined' && pendingSync !== 'null' && user) {
        try {
          const parsed = JSON.parse(pendingSync);
          if (Array.isArray(parsed)) {
            parsed.forEach(entry => syncHistoryToFirebase(entry, user.uid));
          } else {
            syncHistoryToFirebase(parsed, user.uid);
          }
        } catch (e) {
          console.warn("Failed to parse pending_sync", e);
        }
        localStorage.removeItem('pending_sync');
      }
      
      // Handle new pending_sync_ keys
      if (user && !isQuotaExceeded) {
        const pendingKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('pending_sync_')) {
            pendingKeys.push(key);
          }
        }
        
        pendingKeys.forEach(key => {
          try {
            const entry = JSON.parse(localStorage.getItem(key) || '{}');
            if (entry.id) {
              syncHistoryToFirebase(entry, user.uid);
            }
          } catch (e) {
            console.warn('Failed to parse pending sync entry', e);
          }
        });
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [user]);
  useEffect(() => {
    if (result && resultRef.current && view === 'analyze') {
      const timer = setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [result, view]);
  useEffect(() => {
    if ((isAnalyzing || isAnalyzingText) && scannerRef.current) {
      scannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isAnalyzing, isAnalyzingText]);
  useEffect(() => {
    if (view === 'details' && selectedHistoryItem && selectedHistoryItem.id !== 'temp') {
      if (!originalResult || originalResult.id !== selectedHistoryItem.id) {
        setOriginalResult(safeClone(selectedHistoryItem));
      }
      setMealScaleMultiplier(1.0);
      setCustomWeightInput('');
    } else if (view === 'analyze' && result) {
      if (!originalResult || originalResult.meal_name !== result.meal_name || (originalResult as any).id !== (result as any).id) {
        setOriginalResult(safeClone(result));
      }
    } else if (view !== 'details' && view !== 'analyze') {
      setOriginalResult(null);
      setMealScaleMultiplier(1.0);
      setCustomWeightInput('');
    }
  }, [view, selectedHistoryItem?.id, originalResult?.id, result]);
  useEffect(() => {
    const p = parseFloat(manualProtein) || 0;
    const c = parseFloat(manualCarbs) || 0;
    const f = parseFloat(manualFat) || 0;
    if (p > 0 || c > 0 || f > 0) {
      setManualCalories(Math.round((p * 4) + (c * 4) + (f * 9)).toString());
    }
  }, [manualProtein, manualCarbs, manualFat]);
  useEffect(() => {
    return () => {
      if (voiceNoteTimerRef.current) {
        clearInterval(voiceNoteTimerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);
  useEffect(() => {
    if (view !== 'chat') return;

    const handleGlobalPaste = (e: ClipboardEvent) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;
      const items = clipboardData.items;
      const files = clipboardData.files;
      let hasImage = false;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type && items[i].type.startsWith('image/')) {
            hasImage = true;
            break;
          }
        }
      }
      if (!hasImage && files) {
        for (let i = 0; i < files.length; i++) {
          if (files[i].type && files[i].type.startsWith('image/')) {
            hasImage = true;
            break;
          }
        }
      }

      if (hasImage) {
        handleChatPaste(e);
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => {
      window.removeEventListener('paste', handleGlobalPaste);
    };
  }, [view, handleChatPaste]);
  useEffect(() => {
    scrollToSectionTop(undefined, false);
    const scrollTimer = setTimeout(() => {
      scrollToSectionTop(undefined, false);
    }, 40);
    setExpandedFoodIdx(null);
    if (view === 'analyze') {
      const hour = new Date().getHours();
      let type: MealType = 'snack';
      if (hour >= 5 && hour < 11) type = 'breakfast';
      else if (hour >= 11 && hour < 16) type = 'lunch';
      else if (hour >= 16 && hour < 22) type = 'dinner';
      setSelectedMealType(type);
    }
    return () => clearTimeout(scrollTimer);
  }, [view]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  useEffect(() => {
    if (isHistoryLoading) return;

    const runReversal = async () => {
      // Ensure we only ever run this once
      if (localStorage.getItem('reverted_latest_water_task_checkpoint_1')) {
        return;
      }

      console.log("[REVERSAL] Checking for latest task with water tag...");

      // Find the latest water log
      const latestWaterLog = waterLogs.reduce<WaterLog | null>((latest, current) => {
        if (!latest || current.timestamp > latest.timestamp) {
          return current;
        }
        return latest;
      }, null);

      // Find the latest meal with a water tag
      const latestWaterTaggedMeal = history.reduce<HistoryEntry | null>((latest, current) => {
        const hasWaterTag = current.tags && current.tags.includes('water');
        if (hasWaterTag) {
          if (!latest || current.timestamp > latest.timestamp) {
            return current;
          }
        }
        return latest;
      }, null);

      let targetId: string | null = null;
      let targetType: 'meal' | 'water' | null = null;
      let targetTimestamp = 0;

      if (latestWaterLog) {
        targetId = latestWaterLog.id;
        targetType = 'water';
        targetTimestamp = latestWaterLog.timestamp;
      }

      if (latestWaterTaggedMeal && latestWaterTaggedMeal.timestamp > targetTimestamp) {
        targetId = latestWaterTaggedMeal.id;
        targetType = 'meal';
        targetTimestamp = latestWaterTaggedMeal.timestamp;
      }

      if (targetId) {
        console.log(`[REVERSAL] Found latest water task to reverse: ID=${targetId}, Type=${targetType}`);
        // Perform deletion using the native deleteHistoryItem function
        deleteHistoryItem(targetId);
        // Mark as completed
        localStorage.setItem('reverted_latest_water_task_checkpoint_1', 'true');
        toast.info(lang === 'ua' ? 'Останній запис води автоматично скасовано' : 'Latest water track has been automatically undone!', {
          icon: <RotateCcw className="w-5 h-5 text-emerald-500" />,
          duration: 8000,
        });
      } else {
        console.log("[REVERSAL] No water logs or water tagged meals found to reverse.");
        localStorage.setItem('reverted_latest_water_task_checkpoint_1', 'true');
      }
    };

    runReversal();
  }, [isHistoryLoading, history, waterLogs, lang]);
  useEffect(() => {
    if (!user || isQuotaExceeded || isQuotaExceededState) {
      setIsHistoryLoading(false);
      setIsAuthLoading(false);
      return;
    }
    
    setIsHistoryLoading(true);
    
    // Set up realtime listener for days (unified history and water)
    const daysQuery = query(
      collection(db, 'users', user.uid, 'days'),
      orderBy('date', 'desc'),
      limit(daysLimit)
    );
    const unsubscribeDays = onSnapshot(daysQuery, (snapshot) => {
      const liveMeals: HistoryEntry[] = [];
      const liveWater: WaterLog[] = [];

      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.history && Array.isArray(data.history)) {
          data.history.forEach((m: any) => {
            liveMeals.push({
              ...m,
              timestamp: ensureTimestampMillis(m.timestamp)
            });
          });
        }
        if (data.water && Array.isArray(data.water)) {
          data.water.forEach((w: any) => {
            liveWater.push({
              ...w,
              timestamp: ensureTimestampMillis(w.timestamp)
            });
          });
        }
      });

      // Update history state (reconciling live meals with local state to purge items deleted cross-platform)
      setHistory(prev => {
        const liveMealIds = new Set(liveMeals.map(m => m.id));
        const activeSnapshotDates = new Set(snapshot.docs.map(d => d.id));
        
        const removedDays = snapshot.docChanges()
          .filter(change => change.type === 'removed')
          .map(change => change.doc.id);
        const removedDaysSet = new Set(removedDays);

        const mergedMap = new Map<string, HistoryEntry>();
        
        prev.forEach(p => {
          const itemDate = formatDate(new Date(p.timestamp), 'YYYY-MM-DD');
          
          // 1. If day document was removed entirely from Firestore, drop the item
          if (removedDaysSet.has(itemDate)) {
            return;
          }

          // Preserve all existing meals unless the entire day was explicitly deleted
          if (!removedDaysSet.has(itemDate)) {
            mergedMap.set(p.id, p);
          }
        });

        // Add or update all live meals from Firestore (preserving photos across sessions/snapshots)
        liveMeals.forEach(m => {
          const existing = mergedMap.get(m.id);
          const resolvedImage = (m.image && m.image.trim() !== '')
            ? m.image
            : (existing?.image || '');
          mergedMap.set(m.id, {
            ...(existing || {}),
            ...m,
            image: resolvedImage
          });
        });

        const combined = Array.from(mergedMap.values()).sort((a, b) => b.timestamp - a.timestamp);
        saveMealHistory(combined);
        return combined;
      });

      // Update water logs state (reconciling live water with local state)
      setWaterLogs(prev => {
        const liveWaterIds = new Set(liveWater.map(w => w.id));
        const activeSnapshotDates = new Set(snapshot.docs.map(d => d.id));

        const removedDays = snapshot.docChanges()
          .filter(change => change.type === 'removed')
          .map(change => change.doc.id);
        const removedDaysSet = new Set(removedDays);

        const mergedMap = new Map<string, WaterLog>();

        prev.forEach(p => {
          const itemDate = formatDate(new Date(p.timestamp), 'YYYY-MM-DD');

          if (!removedDaysSet.has(itemDate)) {
            mergedMap.set(p.id, p);
          }
        });

        liveWater.forEach(w => mergedMap.set(w.id, w));

        const combined = Array.from(mergedMap.values()).sort((a, b) => b.timestamp - a.timestamp);
        localStorage.setItem('water_logs', JSON.stringify(safeClone(combined)));
        return combined;
      });

      setHasMoreHistory(snapshot.docs.length >= daysLimit);
      if (snapshot.docs.length > 0) {
        setLastHistoryDoc(snapshot.docs[snapshot.docs.length - 1]);
      } else {
        setLastHistoryDoc(null);
      }

      const syncNow = new Date();
      setLastSyncTime(syncNow);
      localStorage.setItem('last_cloud_sync_time', syncNow.toISOString());

      setIsHistoryLoading(false);
    }, (error) => {
      console.warn("Cross-device sync (days) failed", error);
      handleFirestoreErrorLocal(error, OperationType.LIST, `users/${user.uid}/days`);
      setIsHistoryLoading(false);
    });

    // Set up realtime listener for user doc settings
    const unsubscribeUser = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
      if (docSnap.exists()) {
         const data = docSnap.data();
         
         const authEmail = user.email || user.providerData?.find((p: any) => p?.email)?.email;
         const correctEmail = authEmail || data.email;
         if (correctEmail) {
           setUserDocEmail(correctEmail);
           localStorage.setItem('user_email', correctEmail);
           
           if (authEmail && data.email && data.email !== authEmail) {
             console.warn(`Correcting mismatched email in user document from ${data.email} to ${authEmail}`);
             setDocWithQueue(doc(db, 'users', user.uid), { email: authEmail }, { merge: true });
           }
         }
         
         // Update remote totals ref for cost optimization / loop prevention
         remoteTotalsRef.current = {
           calories: data.todayCalories !== undefined ? data.todayCalories : -1,
           water: data.todayWater !== undefined ? data.todayWater : -1,
           day: data.lastActiveDay || ''
         };

         // Local cache of user configuration document to prevent repeated reads
         (window as any).__userDocCache = {
           uid: user.uid,
           data: data,
           timestamp: Date.now()
         };

         // Track tester & user device metadata (essential for diagnosing push notification readiness on iOS)
         const detectDeviceOS = () => {
           const ua = window.navigator.userAgent.toLowerCase();
           if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'iOS';
           if (ua.includes('android')) return 'Android';
           return 'Desktop/Web';
         };
         const detectIsPWA = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
         const detectNotifPerm = 'Notification' in window ? Notification.permission : 'not-supported';
         
         const os = detectDeviceOS();
         const pwa = detectIsPWA;
         const perm = detectNotifPerm;
         
         // Rate-limit metadata writes locally per client to once every 12 hours to strictly prevent cross-device write ping-pong loops
         const lastLocalSync = Number(localStorage.getItem(`last_device_metadata_sync_${user.uid}`) || 0);
         const twelveHours = 12 * 60 * 60 * 1000;
         if (Date.now() - lastLocalSync > twelveHours) {
           localStorage.setItem(`last_device_metadata_sync_${user.uid}`, String(Date.now()));
           setDocWithQueue(doc(db, 'users', user.uid), {
             deviceOS: os,
             isPWA: pwa,
             notificationPermission: perm,
             lastActiveAt: Date.now()
           }, { merge: true }).catch(err => console.warn("Failed to update user device info:", err));
         }

         if (data.googleFitConnected !== undefined) {
           const currentConnected = localStorage.getItem('google_fit_connected') === 'true';
           if (currentConnected !== data.googleFitConnected) {
             localStorage.setItem('google_fit_connected', String(data.googleFitConnected));
             window.dispatchEvent(new CustomEvent(data.googleFitConnected ? 'google-fit-connected' : 'google-fit-disconnected'));
           }
         }

         if (data.appleHealthConnected !== undefined) {
           const currentAppleConnected = localStorage.getItem('apple_health_connected') === 'true';
           if (currentAppleConnected !== data.appleHealthConnected) {
             localStorage.setItem('apple_health_connected', String(data.appleHealthConnected));
             window.dispatchEvent(new CustomEvent(data.appleHealthConnected ? 'apple-health-connected' : 'apple-health-disconnected'));
           }
         }

         if (data.appleHealthTodayCalories !== undefined) {
           setAppleHealthTodayCalories(data.appleHealthTodayCalories);
         }

         // Unified onboarding completion check and view routing
         let isCompleted = data.onboardingCompleted === true || 
           localStorage.getItem('onboarding_completed') === 'true' || 
           !!data.metrics || 
           (!!data.goals && data.goals.calories > 0 && data.goals.calories !== DEFAULT_GOALS.calories) ||
           (!!data.userName && data.userName.trim().length > 0);

         if (isCompleted) {
           localStorage.setItem('onboarding_completed', 'true');
           setShowOnboarding(false);
           setView(prev => (prev === 'login' || prev === 'onboarding') ? 'home' : prev);
           if (data.onboardingCompleted !== true) {
             setDocWithQueue(doc(db, 'users', user.uid), { onboardingCompleted: true }, { merge: true }).catch(() => {});
           }
         } else {
           localStorage.removeItem('onboarding_completed');
           setShowOnboarding(true);
           setView('onboarding');

           // Reset local storage and state for new user if no saved profile exists
           if (!data.goals) {
             setGoals(DEFAULT_GOALS);
             localStorage.removeItem('user_goals');
           }
           if (!data.userName) {
             setUserName(user.displayName || '');
             setOnboardingName(user.displayName || '');
             localStorage.removeItem('userName');
           }
           if (!data.metrics) {
             setOnboardingGender('male');
             setOnboardingAge('25');
             setOnboardingWeight('70');
             setOnboardingHeight('170');
             setOnboardingActivity(1.375);
             setOnboardingGoal('maintain');
             setUserMetrics('');
             localStorage.removeItem('onboardingGender');
             localStorage.removeItem('onboardingAge');
             localStorage.removeItem('onboardingWeight');
             localStorage.removeItem('onboardingHeight');
             localStorage.removeItem('onboardingActivity');
             localStorage.removeItem('onboardingGoal');
             localStorage.removeItem('user_metrics_summary');
           }
         }

         if (data.goals) {
           const remoteGoalsStr = JSON.stringify(data.goals);
           const localGoalsStr = JSON.stringify(goalsRef.current);
           if (remoteGoalsStr !== localGoalsStr) {
             setGoals(data.goals);
             localStorage.setItem('user_goals', JSON.stringify(safeClone(data.goals)));
           }
         }
          if (data.userName) {
            const trimmedDataName = String(data.userName).trim();
            if (trimmedDataName && userNameRef.current !== trimmedDataName) {
              setUserName(trimmedDataName);
            }
            if (trimmedDataName) {
              setOnboardingName(trimmedDataName);
              localStorage.setItem('userName', trimmedDataName);
            }
          }
         const remoteAvatar = data.customAvatar || data.photoURL || data.photoUrl || data.avatar || data.profilePhoto;
          if (remoteAvatar && remoteAvatar !== customAvatarRef.current) {
            setCustomAvatar(remoteAvatar);
            localStorage.setItem('custom_avatar', remoteAvatar);
            setAvatarImageError(false);
          }
         if (data.language && langRef.current !== data.language) {
           setLang(data.language);
           setOnboardingLanguage(data.language);
           localStorage.setItem('app_lang', data.language);
         } else if (!data.language && !localStorage.getItem('app_lang')) {
           setLang('en-US');
           setOnboardingLanguage('en-US');
           localStorage.setItem('app_lang', 'en-US');
         }
         if (!docSnap.metadata?.hasPendingWrites) {
           let currentAutoThemeState = autoThemeEnabledRef.current;
           if (data.autoThemeEnabled !== undefined && autoThemeEnabledRef.current !== data.autoThemeEnabled) {
             currentAutoThemeState = data.autoThemeEnabled;
             setAutoThemeEnabled(data.autoThemeEnabled);
             localStorage.setItem('auto_theme_enabled', String(data.autoThemeEnabled));
           }
           if (data.theme && !data.autoThemeEnabled && !currentAutoThemeState) {
             if (themeRef.current !== data.theme) {
               setTheme(data.theme);
               localStorage.setItem('theme', data.theme);
             }
           }
         }
         if (data.autoThemeSunrise !== undefined && autoThemeSunriseRef.current !== data.autoThemeSunrise) {
           setAutoThemeSunrise(data.autoThemeSunrise);
           localStorage.setItem('auto_theme_sunrise', data.autoThemeSunrise);
         }
         if (data.autoThemeSunset !== undefined && autoThemeSunsetRef.current !== data.autoThemeSunset) {
           setAutoThemeSunset(data.autoThemeSunset);
           localStorage.setItem('auto_theme_sunset', data.autoThemeSunset);
         }
         if (data.autoThemeLocationActive !== undefined && autoThemeLocationActiveRef.current !== data.autoThemeLocationActive) {
           setAutoThemeLocationActive(data.autoThemeLocationActive);
           localStorage.setItem('auto_theme_location_active', String(data.autoThemeLocationActive));
         }
         if (data.metricsSummary && userMetricsRef.current !== data.metricsSummary) {
           setUserMetrics(data.metricsSummary);
           localStorage.setItem('user_metrics_summary', data.metricsSummary);
         }
         if (data.metrics) {
           if (data.metrics.gender) {
             setOnboardingGender(data.metrics.gender);
             localStorage.setItem('onboardingGender', data.metrics.gender);
           }
           if (data.metrics.age !== undefined) {
             setOnboardingAge(String(data.metrics.age));
             localStorage.setItem('onboardingAge', String(data.metrics.age));
           }
           if (data.metrics.weight !== undefined) {
             setOnboardingWeight(String(data.metrics.weight));
             localStorage.setItem('onboardingWeight', String(data.metrics.weight));
           }
           if (data.metrics.height !== undefined) {
             setOnboardingHeight(String(data.metrics.height));
             localStorage.setItem('onboardingHeight', String(data.metrics.height));
           }
           if (data.metrics.activity !== undefined) {
             setOnboardingActivity(Number(data.metrics.activity));
             localStorage.setItem('onboardingActivity', String(data.metrics.activity));
           }
           if (data.metrics.goal) {
             setOnboardingGoal(data.metrics.goal);
             localStorage.setItem('onboardingGoal', data.metrics.goal);
           }
         }
         if (data.weightToLose !== undefined) {
           setWeightToLose(String(data.weightToLose));
           localStorage.setItem('weightToLose', String(data.weightToLose));
         }
         if (data.timeFormat) {
           setTimeFormat(data.timeFormat);
           localStorage.setItem('time_format', data.timeFormat);
         }
         if (data.dateFormat) {
           setDateFormat(data.dateFormat);
           localStorage.setItem('date_format', data.dateFormat);
         }
         if (data.vibe) {
           setVibe(data.vibe);
           localStorage.setItem('app_vibe', data.vibe);
         }
         if (data.declineStyle) {
           setDeclineStyle(data.declineStyle);
           localStorage.setItem('decline_style', data.declineStyle);
         }
         if (data.illumination !== undefined) {
           setIllumination(data.illumination);
           localStorage.setItem('app_illumination', String(data.illumination));
         }
         if (data.illuminationColor !== undefined) {
           setIlluminationColor(data.illuminationColor);
           localStorage.setItem('app_illumination_color', data.illuminationColor);
         }
         if (data.soundEnabled !== undefined) {
           setSoundEnabled(data.soundEnabled);
           sounds.setEnabled(data.soundEnabled);
           sfx.setMuted(!data.soundEnabled);
           localStorage.setItem('pro_audio_enabled', String(data.soundEnabled));
         }
         if (data.enableMealReview !== undefined) {
           setEnableMealReview(data.enableMealReview);
           localStorage.setItem('enable_meal_review', String(data.enableMealReview));
         }
         if (data.foodPreferences !== undefined) {
           setFoodPreferences(data.foodPreferences);
           localStorage.setItem('food_preferences', data.foodPreferences);
         }
         if (data.foodBudget !== undefined) {
           setFoodBudget(data.foodBudget);
           localStorage.setItem('food_budget', data.foodBudget);
         }
         if (data.vibrateOnLogging !== undefined) {
           setVibrateOnLogging(data.vibrateOnLogging);
           localStorage.setItem('vibrate_on_logging', String(data.vibrateOnLogging));
         }
         if (Array.isArray(data.favorites)) {
            const sanitized = sanitizeFavorites(data.favorites);
            setFavorites(sanitized);
            try {
              const jsonStr = JSON.stringify(safeClone(sanitized));
              localStorage.setItem('favorites', jsonStr);
              localDb.setItem('favorites', jsonStr);
            } catch (e) {}
          }
         if (data.enableAiSuggestions !== undefined) {
           setEnableAiSuggestions(data.enableAiSuggestions);
           localStorage.setItem('enable_ai_suggestions', String(data.enableAiSuggestions));
         }
         if (data.enableHealthDeficiencyAlert !== undefined) {
           setEnableHealthDeficiencyAlert(data.enableHealthDeficiencyAlert);
           localStorage.setItem('enable_health_deficiency_alert', String(data.enableHealthDeficiencyAlert));
         }
         if (data.ignoreMealTimeLogging !== undefined) {
           setIgnoreMealTimeLogging(data.ignoreMealTimeLogging);
           localStorage.setItem('ignore_meal_time_logging', String(data.ignoreMealTimeLogging));
         }
          if (data.gamification || data.gamification_profile) {
            const gamState = data.gamification_profile || data.gamification || {};
            if (gamState.title_name) {
              setGamificationTitleName(gamState.title_name);
              localStorage.setItem('gamification_title_name', gamState.title_name);
            }
            if (gamState.neon_color_hex) {
              setGamificationNeonColor(gamState.neon_color_hex);
              localStorage.setItem('gamification_neon_color', gamState.neon_color_hex);
            }
            if (gamState.mutual_likes_count !== undefined) {
              setMutualLikesCount(gamState.mutual_likes_count);
              localStorage.setItem('mutual_likes_count', String(gamState.mutual_likes_count));
            }
            if (gamState.total_processed_logs !== undefined) {
              setGamificationTotalLogs(gamState.total_processed_logs);
              localStorage.setItem('gamification_total_logs', String(gamState.total_processed_logs));
            }
            if (typeof gamState.token_balance === 'number') {
              setUserTokensCount(gamState.token_balance);
            }
            try {
              const currentCached = localStorage.getItem('main_nutrition_gamification_profile');
              const prevObj = currentCached ? JSON.parse(currentCached) : {};
              const mergedGam = {
                ...DEFAULT_GAMIFICATION_PROFILE,
                ...prevObj,
                ...gamState
              };
              localStorage.setItem('main_nutrition_gamification_profile', JSON.stringify(mergedGam));
              window.dispatchEvent(new CustomEvent('gamification-profile-updated'));
            } catch (e) {}
          }
         if (data.skin) {
           setSkin(data.skin);
           localStorage.setItem('app_skin', data.skin);
         }
         if (data.reminderEnabled !== undefined) {
           setReminderEnabled(data.reminderEnabled);
           localStorage.setItem('reminder_enabled', String(data.reminderEnabled));
         }
         if (data.reminderHour !== undefined) {
           setReminderHour(data.reminderHour);
           localStorage.setItem('reminder_hour', String(data.reminderHour));
         }
         if (data.safeChoiceProfile) {
           setSafeChoiceProfile(data.safeChoiceProfile);
           localStorage.setItem('safe_choice_profile', JSON.stringify(safeClone(data.safeChoiceProfile)));
         }
         if (data.fastingState) {
           setFastingState(data.fastingState);
           localStorage.setItem('main_nutrition_fasting_state', JSON.stringify(safeClone(data.fastingState)));
         }
         if (data.showFastingWidget !== undefined) {
           setShowFastingWidget(data.showFastingWidget);
           localStorage.setItem('show_fasting_widget', String(data.showFastingWidget));
         }
         if (data.preferredIngredients !== undefined) {
           setPreferredIngredients(data.preferredIngredients);
           localStorage.setItem('ai_preferred_ingredients', data.preferredIngredients);
         }
         if (data.dailyStreakViewMode !== undefined) {
           setDailyStreakViewMode(data.dailyStreakViewMode);
           localStorage.setItem('daily_streak_view_mode', data.dailyStreakViewMode);
         }
         if (data.dailyProgressViewMode !== undefined) {
            setDailyProgressViewMode(data.dailyProgressViewMode);
            localStorage.setItem('daily_progress_view_mode', data.dailyProgressViewMode);
          }
          const isUserAdmin = isKimAdmin(data.email || user.email || effectiveUserEmail);
          if (data.subscriptionTier !== undefined) {
            const mappedTier = data.subscriptionTier === 'premium_pro' ? 'premium' : data.subscriptionTier;
            setSubscriptionTier(mappedTier);
            localStorage.setItem('subscription_tier', mappedTier);
          } else if (isUserAdmin) {
            setSubscriptionTier('premium');
            localStorage.setItem('subscription_tier', 'premium');
          } else {
            setSubscriptionTier('free');
            localStorage.setItem('subscription_tier', 'free');
          }
          if (data.subscriptionTrialActive !== undefined) {
            setIsTrialActive(data.subscriptionTrialActive === true);
            localStorage.setItem('subscription_trial_active', String(data.subscriptionTrialActive === true));
          } else {
            setIsTrialActive(false);
            localStorage.setItem('subscription_trial_active', 'false');
          }
         if (data.filterSafeChoiceAllergens !== undefined) {
           setFilterSafeChoiceAllergens(data.filterSafeChoiceAllergens);
           localStorage.setItem('filter_safe_choice_allergens', String(data.filterSafeChoiceAllergens));
         }
         if (data.userResidence !== undefined) {
           setUserResidenceState(data.userResidence);
           localStorage.setItem('user_residence', data.userResidence);
         }
         if (data.isAge18Plus !== undefined) {
           setIsAge18PlusState(data.isAge18Plus);
           localStorage.setItem('is_age_18_plus', String(data.isAge18Plus));
         }
         if (Array.isArray(data.selectedDrinkIds)) {
           setSelectedDrinkIdsState(data.selectedDrinkIds);
           localStorage.setItem('selected_drink_ids', JSON.stringify(data.selectedDrinkIds));
         }
         if (Array.isArray(data.customUserDrinks)) {
           setCustomUserDrinksState(data.customUserDrinks);
           localStorage.setItem('custom_user_drinks', JSON.stringify(data.customUserDrinks));
         }
         if (Array.isArray(data.excludedFoods)) {
           setExcludedFoods(data.excludedFoods);
           localStorage.setItem('ai_excluded_foods', JSON.stringify(data.excludedFoods));
         }
         if (data.cyberpunkTone !== undefined) {
           setCyberpunkTone(data.cyberpunkTone);
           localStorage.setItem('app_cyberpunk_tone', data.cyberpunkTone);
         }
         if (data.preferredIngredients !== undefined) {
           setPreferredIngredients(data.preferredIngredients);
           localStorage.setItem('ai_preferred_ingredients', data.preferredIngredients);
         }
         if (data.nutriAgentEnabled !== undefined) {
           localStorage.setItem('nutri_agent_enabled', String(data.nutriAgentEnabled));
         }

         if (Array.isArray(data.weightLogs)) {
           const mappedLogs = data.weightLogs.map((l: any) => ({
             ...l,
             timestamp: ensureTimestampMillis(l.timestamp)
           })).sort((a: any, b: any) => a.timestamp - b.timestamp);
           setWeightLogs(mappedLogs);
           localDb.setItem('weight_logs', JSON.stringify(safeClone(mappedLogs)));
         }

         // Cross-device daily wrap-up dismissal sync:
         // When another device dismisses the wrap-up, it writes eveningWrapUpDismissed_{date}: true
         // to the Firestore user doc. This onSnapshot picks it up and hides the card instantly here.
         {
           const currentWrapUpSession = getEveningWrapUpSessionDate(new Date());
           const yesterdayWrapUpSession = getPreviousSessionDate(currentWrapUpSession);
           
           const remoteKeyToday = `eveningWrapUpDismissed_${currentWrapUpSession}`;
           const remoteKeyYesterday = `eveningWrapUpDismissed_${yesterdayWrapUpSession}`;
           
           if (data[remoteKeyToday] === true) {
             try { localStorage.setItem(`evening_wrapup_dismissed_${currentWrapUpSession}`, 'true'); } catch (e) {}
             setIsDismissedWrapUp(true);
           }
           if (data[remoteKeyYesterday] === true) {
             try { localStorage.setItem(`evening_wrapup_dismissed_${yesterdayWrapUpSession}`, 'true'); } catch (e) {}
             setIsDismissedWrapUp(true);
             setWrapUpRefreshTick(t => t + 1);
           }
         }

         setIsAuthLoading(false);
      } else {
         // Create default user doc in database if it doesn't exist
         const isCompleted = localStorage.getItem('onboarding_completed') === 'true' ||
           (!!localStorage.getItem('userName') && localStorage.getItem('userName') !== 'null') ||
           (!!user.displayName && user.displayName.trim().length > 0);
         if (isCompleted) {
           localStorage.setItem('onboarding_completed', 'true');
           setShowOnboarding(false);
           setView(prev => (prev === 'login' || prev === 'onboarding') ? 'home' : prev);
         } else {
           localStorage.removeItem('onboarding_completed');
           localStorage.removeItem('user_goals');
           localStorage.removeItem('userName');
           localStorage.removeItem('onboardingGender');
           localStorage.removeItem('onboardingAge');
           localStorage.removeItem('onboardingWeight');
           localStorage.removeItem('onboardingHeight');
           localStorage.removeItem('onboardingActivity');
           localStorage.removeItem('onboardingGoal');
           localStorage.removeItem('user_metrics_summary');
           localStorage.removeItem('food_preferences');
           localStorage.removeItem('food_budget');

           setUserName(user.displayName || '');
           setOnboardingName(user.displayName || '');
           setGoals(DEFAULT_GOALS);
           setOnboardingGender('male');
           setOnboardingAge('25');
           setOnboardingWeight('70');
           setOnboardingHeight('170');
           setOnboardingActivity(1.375);
           setOnboardingGoal('maintain');
           setUserMetrics('');

           setShowOnboarding(true);
           setView('onboarding');
         }

         const emailToSave = user.email || user.providerData?.find((p: any) => p?.email)?.email;
         const isUserAdmin = isKimAdmin(emailToSave);
         setDocWithQueue(doc(db, 'users', user.uid), {
           ...(emailToSave ? { email: emailToSave } : {}),
           createdAt: new Date().toISOString(),
           onboardingCompleted: isCompleted,
           subscriptionTier: isUserAdmin ? 'premium' : 'free',
           subscriptionTrialActive: false
         }, { merge: true }).catch(err => {
           console.error("Auto-creating user doc failed:", err);
         });
         setIsAuthLoading(false);
      }
    }, (error) => {
      console.warn("Cross-device sync (user settings) failed", error);
      handleFirestoreErrorLocal(error, OperationType.GET, `users/${user.uid}`);
      setIsAuthLoading(false);
    });

    // Automatic Database Cleanup removed for production stability

    // Set up realtime listener for AI Concierge grocery list across devices
    const groceryQuery = query(collection(db, 'users', user.uid, 'grocery_list'), orderBy('timestamp', 'desc'));
    const unsubscribeGrocery = onSnapshot(groceryQuery, (snapshot) => {
      const groceryData = snapshot.docs.map(doc => {
        const d = doc.data() as GroceryItem;
        return {
          ...d,
          timestamp: ensureTimestampMillis(d.timestamp)
        };
      });
      
      const removedIds = snapshot.docChanges().filter(change => change.type === 'removed').map(change => change.doc.id);
      
      setGroceryItems(prev => {
        const mergedMap = new Map<string, GroceryItem>();
        prev.forEach(p => mergedMap.set(p.id, p));
        groceryData.forEach(g => mergedMap.set(g.id, g));
        removedIds.forEach(id => mergedMap.delete(id));
        
        const combined = Array.from(mergedMap.values()).sort((a, b) => b.timestamp - a.timestamp);
        localStorage.setItem('grocery_items', JSON.stringify(safeClone(combined)));
        return combined;
      });
    }, (error) => {
      handleFirestoreErrorLocal(error, OperationType.GET, `users/${user.uid}/grocery_list`);
    });

    // Set up realtime listener for custom / scanned barcode products across devices
    const scannedQuery = query(collection(db, 'users', user.uid, 'scanned_products'), orderBy('timestamp', 'desc'));
    const unsubscribeScanned = onSnapshot(scannedQuery, (snapshot) => {
      const liveScanned: ScannedProduct[] = snapshot.docs.map(doc => {
        const d = doc.data() as ScannedProduct;
        return {
          ...d,
          timestamp: ensureTimestampMillis(d.timestamp)
        };
      });

      const removedIds = new Set(snapshot.docChanges().filter(c => c.type === 'removed').map(c => c.doc.id));

      setScannedProducts(prev => {
        const mergedMap = new Map<string, ScannedProduct>();
        prev.forEach(p => {
          if (p && p.barcode && !removedIds.has(p.barcode)) {
            mergedMap.set(p.barcode, p);
          }
        });
        liveScanned.forEach(p => {
          if (p && p.barcode) {
            mergedMap.set(p.barcode, p);
          }
        });
        const combined = Array.from(mergedMap.values()).sort((a, b) => b.timestamp - a.timestamp);
        localDb.setItem('scanned_products', JSON.stringify(safeClone(combined)));
        return combined;
      });
    }, (error) => {
      handleFirestoreErrorLocal(error, OperationType.GET, `users/${user.uid}/scanned_products`);
    });

    // Set up realtime listener for BioBox / loot inventory across devices
    const lootQuery = query(collection(db, 'users', user.uid, 'loot_inventory'));
    const unsubscribeLoot = onSnapshot(lootQuery, (snapshot) => {
      const boxes: BioBoxInventoryItem[] = [];
      const consumables: UserConsumableItem[] = [];

      snapshot.docs.forEach(docSnap => {
        const item = docSnap.data();
        if (item.type === 'box' || item.type === 'crate' || item.boxType || item.crateType) {
          boxes.push({
            id: docSnap.id,
            boxType: item.boxType || item.crateType || 'daily_supply',
            crateType: item.boxType || item.crateType || 'daily_supply',
            obtainedAt: item.obtainedAt || Date.now(),
            source: item.source || 'Daily Drop',
            seed: item.seed || Math.random().toString(36).substring(2)
          });
        } else {
          consumables.push({
            id: docSnap.id,
            type: item.type || 'streak_freeze',
            itemId: item.itemId || docSnap.id,
            count: item.count || 1,
            name: item.name || { en: 'Consumable Item', ua: 'Предмет' },
            description: item.description || { en: 'Consumable', ua: 'Предмет' },
            icon: item.icon || '❄️',
            acquiredAt: item.acquiredAt || Date.now()
          });
        }
      });

      setUserBioBoxes(boxes);
      localStorage.setItem('main_nutrition_boxes', JSON.stringify(boxes));
      localStorage.setItem('main_nutrition_crates', JSON.stringify(boxes));
      localStorage.setItem('main_nutrition_loot_inventory', JSON.stringify(consumables));
      window.dispatchEvent(new CustomEvent('bio-boxes-updated'));
      window.dispatchEvent(new CustomEvent('biobox-inventory-updated'));
    }, (error) => {
      handleFirestoreErrorLocal(error, OperationType.GET, `users/${user.uid}/loot_inventory`);
    });

    return () => {
      unsubscribeDays();
      unsubscribeUser();
      unsubscribeGrocery();
      unsubscribeScanned();
      unsubscribeLoot();
    };
  }, [user, daysLimit, isQuotaExceededState]);
  useEffect(() => {
    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndY = e.changedTouches[0].clientY;
      if (window.scrollY === 0 && touchEndY - touchStartY > 100) {
        setIsRefreshing(true);
        setTimeout(() => {
          loadData(true);
        }, 1000);
      }
    };
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [loadData]);
  useEffect(() => {
    themeRef.current = theme;
    localStorage.setItem('theme', theme);

    const applyTheme = () => {
      const root = window.document.documentElement;
      const targetIsDark = theme === 'dark';
      if (targetIsDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    if (
      typeof document !== 'undefined' &&
      'startViewTransition' in document &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      document.startViewTransition(() => {
        applyTheme();
      });
    } else {
      applyTheme();
    }
  }, [theme]);
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('cyberpunk', 'skin-stalker', 'skin-anime', 'tone-yellow-cyan', 'tone-cyan-magenta');
    if (skin === 'cyberpunk') {
      root.classList.add('cyberpunk');
      root.classList.add(cyberpunkTone === 'cyan-magenta' ? 'tone-cyan-magenta' : 'tone-yellow-cyan');
    } else if (skin === 'stalker') {
      root.classList.add('skin-stalker');
    } else if (skin === 'anime') {
      root.classList.add('skin-anime');
    }
  }, [skin, cyberpunkTone]);
  useEffect(() => {
    if (user && !isQuotaExceeded && !autoThemeEnabled) {
      // Only sync if it's different from what we might have in Firestore and auto-theme is off
      setDocWithQueue(doc(db, 'users', user.uid), { theme }, { merge: true }).catch(e => {
        handleFirestoreError(e, OperationType.WRITE, `users/${user.uid}`);
      });
    }
  }, [theme, user?.uid, autoThemeEnabled]);
  useEffect(() => {
    checkPermissions();
  }, []);
  useEffect(() => {
    if (lastChatUndoAction) {
      const timer = setTimeout(() => {
        setLastChatUndoAction(null);
      }, 10000); // 10 seconds timeout
      return () => clearTimeout(timer);
    }
  }, [lastChatUndoAction]);
  useEffect(() => {
    if (navigator.permissions && (navigator.permissions as any).query) {
      navigator.permissions.query({ name: 'camera' as any }).then(result => {
        if (result.state === 'denied') {
          setError("Camera access is blocked in your browser. Please enable it in settings or open the app in a new tab.");
        }
        result.onchange = () => {
          if (result.state === 'denied') {
            setError("Camera access is blocked in your browser. Please enable it in settings or open the app in a new tab.");
          } else if (result.state === 'granted') {
            setError(null);
          }
        };
      }).catch(() => {});
    }
  }, []);
  useEffect(() => {
    if (isCameraActive) {
      if (cameraMode === 'barcode') {
        // Stop photo stream if active
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
          setActiveStream(null);
        }
        
        const startScanner = async () => {
          try {
            // Wait for container to be in DOM
            setTimeout(async () => {
              try {
                const container = document.getElementById("barcode-scanner-container");
                if (!container) return;
                
                const { Html5Qrcode } = await import('html5-qrcode');
                const html5QrCode = new Html5Qrcode("barcode-scanner-container");
                html5QrCodeRef.current = html5QrCode;
                
                await html5QrCode.start(
                  { facingMode: facingMode },
                  {
                    fps: 15, // Higher FPS for faster scanning
                  },
                  (decodedText) => {
                    // Validation for Barcodes (length >= 8) OR URLs/QR links
                    if (decodedText && (decodedText.length >= 8 || decodedText.startsWith('http://') || decodedText.startsWith('https://'))) {
                      handleBarcodeScan(decodedText);
                    }
                  },
                  () => {
                    // Ignore errors
                  }
                );

                // Check for torch support after start
                try {
                  const capabilities = (html5QrCode as any).getRunningTrackCapabilities?.();
                  if (capabilities) {
                    setHasTorch(!!capabilities.torch);
                  } else {
                    const cameraCaps = (html5QrCode as any).getRunningTrackCameraCapabilities?.();
                    if (cameraCaps?.torchFeature) {
                      setHasTorch(cameraCaps.torchFeature().isSupported());
                    }
                  }
                } catch (e) {
                  console.error("Error checking torch in barcode mode:", e);
                }
              } catch (err: any) {
                console.error("Error starting barcode scanner:", err);
                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError' || String(err).includes('Permission denied')) {
                  setError("Camera access denied. Please allow camera permissions in your browser settings. If you are in an iframe, try opening the app in a new tab.");
                } else {
                  setError("Could not access camera for scanning. Please check permissions.");
                }
              }
            }, 100);
          } catch (err) {
            console.error("Error starting barcode scanner:", err);
          }
        };
        
        startScanner();
      } else {
        // Stop barcode scanner if active
        if (html5QrCodeRef.current) {
          try {
            const scanner = html5QrCodeRef.current;
            const isScanning = scanner.isScanning || (typeof scanner.getState === 'function' && scanner.getState() === 2);
            if (isScanning) {
              scanner.stop().then(() => {
                html5QrCodeRef.current = null;
                startCamera();
              }).catch(err => {
                console.error("Error stopping scanner:", err);
                html5QrCodeRef.current = null;
                startCamera();
              });
            } else {
              html5QrCodeRef.current = null;
              startCamera();
            }
          } catch (err) {
            console.error("Error stopping scanner (sync):", err);
            html5QrCodeRef.current = null;
            startCamera();
          }
        } else {
          // Just start photo stream if it's not already active
          if (!streamRef.current) {
            startCamera();
          }
        }
      }
    } else {
      // Cleanup both
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        setActiveStream(null);
      }
      if (html5QrCodeRef.current) {
        try {
          const scanner = html5QrCodeRef.current;
          const isScanning = scanner.isScanning || (typeof scanner.getState === 'function' && scanner.getState() === 2);
          if (isScanning) {
            scanner.stop().then(() => {
              html5QrCodeRef.current = null;
            }).catch(err => {
              console.error("Error stopping scanner:", err);
              html5QrCodeRef.current = null;
            });
          } else {
            html5QrCodeRef.current = null;
          }
        } catch (err) {
          console.error("Error stopping scanner (sync):", err);
          html5QrCodeRef.current = null;
        }
      }
    }
    
    return () => {
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach(track => track.stop());
        } catch (e) {}
        streamRef.current = null;
      }
      if (html5QrCodeRef.current) {
        try {
          const scanner = html5QrCodeRef.current;
          const isScanning = scanner.isScanning || (typeof scanner.getState === 'function' && scanner.getState() === 2);
          if (isScanning) {
            scanner.stop().catch(err => console.error("Error stopping scanner on unmount:", err));
          }
        } catch (err) {
          console.error("Error stopping scanner on unmount (sync):", err);
        }
        html5QrCodeRef.current = null;
      }
    };
  }, [isCameraActive, cameraMode, facingMode]);
  useEffect(() => {
    if (isCameraActive && cameraMode === 'photo' && activeStream && videoRef.current) {
      const videoEl = videoRef.current;
      if (videoEl.srcObject !== activeStream) {
        videoEl.srcObject = activeStream;
      }
      videoEl.play().catch(err => {
        console.warn("Autoplay was prevented or video play failed:", err);
      });
    }
  }, [isCameraActive, cameraMode, activeStream]);
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);
  useEffect(() => {
    (window as any).__appSaveToHistory = saveToHistory;
    (window as any).__restoreUserData = async () => {
      const res = await forceRestoreUserData(user, history, waterLogs);
      if (res.restored && res.data) {
        const applied = applyRestoredUserData(res.data, history, waterLogs);
        setHistory(applied.mergedHistory);
        setWaterLogs(applied.mergedWaterLogs);
        if (applied.goals) setGoals(applied.goals);
        if (applied.userName) setUserName(applied.userName);
        if (applied.favorites) setFavorites(applied.favorites);
        if (applied.scannedProducts) setScannedProducts(applied.scannedProducts);
      }
      return res;
    };
  }, [saveToHistory, user, history, waterLogs]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem('favorites');
      if (raw && raw !== 'undefined' && raw !== 'null') {
        const savedFavorites = JSON.parse(raw);
        if (Array.isArray(savedFavorites)) {
          setFavorites(sanitizeFavorites(savedFavorites));
        }
      }
    } catch (e) {
      console.warn('Failed to parse favorites from storage:', e);
    }
  }, [sanitizeFavorites]);
  useEffect(() => {
    if (editingMeal) {
      const newNutrition = recalculateTotalNutrition(editingMeal.foods);
      if (JSON.stringify(safeClone(newNutrition)) !== JSON.stringify(safeClone(editingMeal.total_nutrition))) {
        setEditingMeal(prev => prev ? ({ ...prev, total_nutrition: newNutrition }) : null);
      }
    }
  }, [editingMeal?.foods, recalculateTotalNutrition]);
  useEffect(() => {
    if (user && !isQuotaExceeded) {
      const today = new Date().toISOString().split('T')[0];
      const localCal = todayTotals.calories || 0;
      const localWater = todayTotals.water || 0;

      // Only write if there is a real change in totals or active day
      if (
        localCal !== remoteTotalsRef.current.calories ||
        localWater !== remoteTotalsRef.current.water ||
        today !== remoteTotalsRef.current.day
      ) {
        const updateDailySummary = async () => {
          try {
            const userRef = doc(db, 'users', user.uid);
            // Update ref immediately to prevent duplicate concurrent writes
            remoteTotalsRef.current = { calories: localCal, water: localWater, day: today };
            await setDocWithQueue(userRef, {
              todayCalories: localCal,
              todayWater: localWater,
              lastActiveDay: today,
              lastUpdatedAt: Date.now()
            }, { merge: true });
          } catch (err) {
            console.error("Failed to sync daily totals:", err);
          }
        };
        updateDailySummary();
      }
    }
  }, [todayTotals.calories, todayTotals.water, user?.uid]);
  useEffect(() => {
    // Auto-fetch meal suggestions on mount only if enabled, viewing today, and user has logged at least 1 meal today
    const isToday = selectedDate.toDateString() === new Date().toDateString();
    const todayMeals = selectedDayCombined.filter(i => i.type === 'meal') as HistoryEntry[];
    const mealsLength = todayMeals.length;
    
    // Cost Optimization: Do not auto-trigger AI suggestions on zero-meal days. User can still manually request them via button.
    if (mealsLength === 0) return;

    const currentFetchKey = `${isToday ? new Date().toDateString() : ''}-${mealsLength}-${lang}-${vibe}`;
    const needsFetch = aiMealSuggestions.length === 0 || (suggestionsGeneratedForDate !== currentFetchKey && suggestionsGeneratedForDate?.split('-')[0] !== new Date().toDateString());
    const isThrottled = Boolean(lastSuggestionFetchAttempt && (Date.now() - lastSuggestionFetchAttempt < 10000));

    if (
      enableAiSuggestions &&
      isToday &&
      needsFetch &&
      !isGeneratingSuggestions &&
      !suggestionError &&
      !isThrottled
    ) {
      handleFetchSuggestions();
    }
  }, [enableAiSuggestions, selectedDate, aiMealSuggestions.length, filteredAiSuggestions.length, suggestionsGeneratedForDate, selectedDayCombined, lang, vibe, isGeneratingSuggestions, suggestionError, lastSuggestionFetchAttempt, handleFetchSuggestions]);
  useEffect(() => {
    if (isHistoryLoading) return;

    if (!hasInitialStreakCapturedRef.current) {
      prevStreakRef.current = consecutiveDaysStreak;
      hasInitialStreakCapturedRef.current = true;
      return;
    }

    if (consecutiveDaysStreak > prevStreakRef.current) {
      setStreakIncreased(true);
      try {
        if (typeof confetti === 'function') {
          confetti({
            particleCount: 70,
            spread: 60,
            origin: { y: 0.6 },
            colors: ['#10b981', '#f59e0b', '#3b82f6', '#ec4899']
          });
        }
      } catch (e) {
        console.error('Confetti trigger error:', e);
      }
      const timer = setTimeout(() => {
        setStreakIncreased(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
    prevStreakRef.current = consecutiveDaysStreak;
  }, [consecutiveDaysStreak, isHistoryLoading]);
  useEffect(() => {
    if (!reminderEnabled) return;

    const checkReminder = () => {
      const now = new Date();
      const currentHour = now.getHours();
      
      const todayStr = now.toDateString();
      if (currentHour >= reminderHour && lastReminderDate !== todayStr) {
        // Check if any meals logged today
        const dayStart = new Date();
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date();
        dayEnd.setHours(23, 59, 59, 999);
        
        const mealsToday = history.filter(item => {
          const itemDate = new Date(item.timestamp);
          return itemDate >= dayStart && itemDate <= dayEnd;
        });
        
        if (mealsToday.length === 0 && !reminderToastId) {
          sounds.playSuccess();
          const id = toast.info(t.reminderMessage, {
            duration: Infinity,
            action: {
              label: t.logNow,
              onClick: () => {
                setView('add_meal_options');
                toast.dismiss(id);
                setReminderToastId(null);
                localStorage.setItem('last_reminder_date', todayStr);
                setLastReminderDate(todayStr);
              }
            },
            cancel: {
              label: t.cancel || 'Later',
              onClick: () => {
                toast.dismiss(id);
                setReminderToastId(null);
                localStorage.setItem('last_reminder_date', todayStr);
                setLastReminderDate(todayStr);
              }
            },
            onAutoClose: () => setReminderToastId(null),
            onDismiss: () => {
              setReminderToastId(null);
              localStorage.setItem('last_reminder_date', todayStr);
              setLastReminderDate(todayStr);
            }
          });
          setReminderToastId(id);
        }
      }

      // 3:00 PM (15:00) Progress Summary Check
      if (currentHour >= 15 && lastProgressReminderDate !== todayStr) {
        const actualTodayCalories = todayTotals.calories;

        if (actualTodayCalories < goals.calories * 0.5 && !progressReminderToastId) {
          sounds.playSuccess();
          const id = toast.info(t.progressReminder, {
            duration: Infinity,
            description: `${actualTodayCalories}/${goals.calories} ${t.kcal}`,
            action: {
              label: t.logNow,
              onClick: () => {
                setView('add_meal_options');
                toast.dismiss(id);
                setProgressReminderToastId(null);
                localStorage.setItem('last_progress_reminder_date', todayStr);
                setLastProgressReminderDate(todayStr);
              }
            },
            cancel: {
              label: t.cancel || 'Got it',
              onClick: () => {
                toast.dismiss(id);
                setProgressReminderToastId(null);
                localStorage.setItem('last_progress_reminder_date', todayStr);
                setLastProgressReminderDate(todayStr);
              }
            },
            onAutoClose: () => setProgressReminderToastId(null),
            onDismiss: () => {
              setProgressReminderToastId(null);
              localStorage.setItem('last_progress_reminder_date', todayStr);
              setLastProgressReminderDate(todayStr);
            }
          });
          setProgressReminderToastId(id);
        }
      }
    };

    // Auto-dismiss if meal logged or goals reached
    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    const mealsTodayCount = history.filter(item => new Date(item.timestamp) >= dayStart).length;
    if (mealsTodayCount > 0 && reminderToastId) {
      toast.dismiss(reminderToastId);
      setReminderToastId(null);
      const todayStr = new Date().toDateString();
      localStorage.setItem('last_reminder_date', todayStr);
      setLastReminderDate(todayStr);
    }
    if (todayTotals.calories >= goals.calories * 0.5 && progressReminderToastId) {
      toast.dismiss(progressReminderToastId);
      setProgressReminderToastId(null);
      const todayStr = new Date().toDateString();
      localStorage.setItem('last_progress_reminder_date', todayStr);
      setLastProgressReminderDate(todayStr);
    }

    // Check on mount and then every 15 minutes
    const timeout = setTimeout(checkReminder, 2000); // Small delay on mount
    const interval = setInterval(checkReminder, 15 * 60 * 1000);
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [history, lang, lastReminderDate, lastProgressReminderDate, t.reminderMessage, t.progressReminder, t.logNow, reminderEnabled, reminderHour, reminderToastId, progressReminderToastId, goals, todayTotals.calories, t.cancel]);

  // ==========================================
  // 6. MODAL & VIEW SUB-RENDERERS
  // ==========================================
  const renderQuasoModal = () => {
    if (!showQuasoModal) return null;
    const firstName = (userName || user?.displayName || '').trim().split(' ')[0] || 'friend';

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[32px] p-8 max-w-md w-full card-shadow relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-2 bg-amber-500" />
          
          <div className="space-y-6 text-center">
            <div className="text-6xl mb-4">🥐</div>
            
            <div className="space-y-2">
              <h3 className="text-2xl font-black text-zinc-900 dark:text-zinc-100 uppercase tracking-tighter italic">
                Half of a Quaso Detected
              </h3>
              <p className="text-zinc-500 dark:text-zinc-400 font-medium">
                So, {firstName}, was it only a half or an entire one?
              </p>
            </div>

            <div className="flex flex-col gap-3 pt-4">
              <button
                onClick={() => handleQuasoChoice(false)}
                className="w-full bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-bold py-4 rounded-2xl uppercase tracking-widest text-sm hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
              >
                Only a half
              </button>
              <button
                onClick={() => handleQuasoChoice(true)}
                className="w-full bg-amber-500 text-white font-bold py-4 rounded-2xl uppercase tracking-widest text-sm hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20"
              >
                An entire one
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };
  const renderUpgradeModal = () => {
    if (!showUpgradeModal) return null;

    return (
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 15 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[32px] p-6 max-w-md w-full card-shadow relative overflow-hidden"
        >
          {/* Header Accent */}
          <div className="absolute top-0 left-0 w-full h-2 bg-emerald-500" />
          
          {/* Close button */}
          <button 
            type="button"
            onClick={() => {
              setShowUpgradeModal(false);
              setUpgradeEmail('');
              setUpgradePassword('');
            }}
            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="space-y-5">
            <div className="flex flex-col items-center text-center space-y-2 mt-2">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500 shadow-md">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-black text-[var(--text)] uppercase tracking-tighter">
                {lang === 'ua' ? 'Зберегти гостьовий прогрес' : 'Secure Your Guest Data'}
              </h3>
              <p className="text-[11px] text-zinc-400 font-medium leading-relaxed max-w-sm">
                {lang === 'ua' 
                  ? 'Прив\'яжіть свій гостьовий акаунт, щоб ваші страви, статистика та офлайн-черги були назавжди збережені в хмарі.' 
                  : 'Link your temporary guest session to a secure account to save your food logs, streak milestones, and progress permanently in the cloud.'}
              </p>
            </div>

            {/* Quick Google Option */}
            <button
              type="button"
              onClick={handleLinkGuestGoogle}
              disabled={isLinkingAuth}
              className={cn(
                "w-full py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 transition-all cursor-pointer border shadow-sm disabled:opacity-50",
                theme === 'light'
                  ? "bg-white border-zinc-200 text-zinc-900 hover:bg-zinc-50"
                  : "bg-zinc-900/80 border-zinc-800 text-white hover:bg-zinc-800"
              )}
            >
              <img src="https://www.google.com/favicon.ico" className="w-4.5 h-4.5" alt="Google" />
              <span>{isLinkingAuth ? (lang === 'ua' ? 'ПОВ’ЯЗУВАННЯ...' : 'LINKING...') : (lang === 'ua' ? 'Прив’язати до Google' : 'Link with Google')}</span>
            </button>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
              <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400">
                {lang === 'ua' ? 'АБО СТВОРІТЬ ПАРОЛЬ' : 'OR SECURE WITH EMAIL'}
              </span>
              <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
            </div>

            {/* Email Form */}
            <form onSubmit={(e) => { e.preventDefault(); handleLinkGuestAccount(upgradeEmail, upgradePassword); }} className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                <input
                  type="email"
                  required
                  value={upgradeEmail}
                  onChange={(e) => setUpgradeEmail(e.target.value)}
                  placeholder={lang === 'ua' ? 'Електронна пошта' : 'Email Address'}
                  className={cn(
                    "w-full pl-10 pr-4 py-3 rounded-2xl border text-xs font-bold transition-all outline-none",
                    theme === 'light'
                      ? "bg-white border-zinc-200 text-zinc-900 focus:border-emerald-500"
                      : "bg-zinc-900/80 border-zinc-800 text-white focus:border-emerald-500"
                  )}
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                <input
                  type={showUpgradePassword ? "text" : "password"}
                  required
                  value={upgradePassword}
                  onChange={(e) => setUpgradePassword(e.target.value)}
                  placeholder={lang === 'ua' ? 'Новий пароль' : 'Create New Password'}
                  className={cn(
                    "w-full pl-10 pr-10 py-3 rounded-2xl border text-xs font-bold transition-all outline-none",
                    theme === 'light'
                      ? "bg-white border-zinc-200 text-zinc-900 focus:border-emerald-500"
                      : "bg-zinc-900/80 border-zinc-800 text-white focus:border-emerald-500"
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowUpgradePassword(!showUpgradePassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors cursor-pointer"
                >
                  {showUpgradePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLinkingAuth || !upgradeEmail || !upgradePassword}
                className="w-full py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 transition-all cursor-pointer"
              >
                {isLinkingAuth ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  lang === 'ua' ? 'Прив’язати Акаунт' : 'Link & Upgrade'
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  };
  const renderNoFoodModal = () => {
    if (!showNoFoodModal) return null;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[32px] p-8 max-w-md w-full card-shadow relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-2 bg-red-500" />
          <button 
            onClick={() => setShowNoFoodModal(false)}
            className="absolute top-4 right-4 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors text-zinc-500 dark:text-zinc-400"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="space-y-6 text-center">
            <div className="bg-red-500/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
              <Info className="w-10 h-10 text-red-500" />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-2xl font-black text-zinc-900 dark:text-zinc-100 uppercase tracking-tighter italic">
                {t.errorNoFood}
              </h3>
            </div>

            <button
              onClick={() => setShowNoFoodModal(false)}
              className="w-full p-4 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-2xl font-bold uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all"
            >
              {t.fairEnough}
            </button>
          </div>
        </motion.div>
      </div>
    );
  };
  const renderCheckoutModal = () => {
    if (!isCheckoutModalOpen) return null;

    return (
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={cn(
            "border rounded-[32px] p-8 max-w-md w-full card-shadow relative overflow-hidden",
            skin === 'stalker' 
              ? "bg-black border-[#4ade80] text-[#4ade80] font-mono" 
              : "bg-zinc-900 text-white border-zinc-800"
          )}
        >
          {skin === 'stalker' ? (
            <div className="absolute top-0 left-0 w-full h-1.5 bg-[#4ade80] animate-pulse" />
          ) : (
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-amber-500 to-purple-500" />
          )}

          <div className="space-y-6">
            <div className="text-center">
              <h3 className={cn(
                "text-xl font-black uppercase tracking-wider",
                skin === 'stalker' && "font-mono"
              )}>
                {skin === 'stalker'
                  ? (lang === 'ua' ? "БЕЗПЕЧНИЙ КАНАЛ КПК" : "SECURE PDA LINK")
                  : (lang === 'ua' ? "ШЛЮЗ ОПЛАТИ MAINNUTRITION" : "SECURE CHECKOUT TERMINAL")}
              </h3>
              <p className="text-[10px] text-zinc-400 mt-1 uppercase tracking-widest">
                {lang === 'ua' ? "Шифрування SHA-256" : "SHA-256 ENCRYPTED TRANSACTION"}
              </p>
            </div>

            {isProcessingCheckout ? (
              <div className="space-y-5 py-6">
                <div className="flex items-center justify-center">
                  <Loader2 className={cn(
                    "w-12 h-12 animate-spin",
                    skin === 'stalker' ? "text-[#4ade80]" : "text-amber-500"
                  )} />
                </div>
                <div className={cn(
                  "p-4 rounded-2xl bg-black/40 border border-zinc-800 text-left space-y-2 text-[10px]",
                  skin === 'stalker' ? "border-[#4ade80]/30" : ""
                )}>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                    <span className="text-zinc-500">[{new Date().toLocaleTimeString()}]</span>
                    <span className="font-bold">{lang === 'ua' ? "Встановлення зв'язку..." : "Dialing merchant gateway..."}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                    <span className="text-zinc-500">[{new Date().toLocaleTimeString()}]</span>
                    <span className="font-bold">{lang === 'ua' ? "Перевірка токена шифрування..." : "Cryptographic signature validation..."}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                    <span className="text-zinc-500">[{new Date().toLocaleTimeString()}]</span>
                    <span className="font-bold text-amber-500">{lang === 'ua' ? "Ініціалізація біо-лінку..." : "Provisioning subscription container..."}</span>
                  </div>
                </div>
                <p className="text-center text-xs font-medium text-zinc-400">
                  {lang === 'ua' ? "Будь ласка, зачекайте. Обробка транзакції..." : "Processing secure subscription contract..."}
                </p>
              </div>
            ) : (
              <div className="space-y-6 text-center">
                <div className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center mx-auto",
                  skin === 'stalker' ? "bg-[#4ade80]/10 border border-[#4ade80]" : "bg-emerald-500/10 border border-emerald-500"
                )}>
                  <CheckCircle2 className={cn(
                    "w-8 h-8",
                    skin === 'stalker' ? "text-[#4ade80]" : "text-emerald-500"
                  )} />
                </div>

                <div className="space-y-2">
                  <h4 className="text-lg font-black uppercase tracking-wider text-white">
                    {lang === 'ua' ? "БІЛІНГ УСПІШНИЙ!" : "BILLING SUCCESSFUL!"}
                  </h4>
                  <p className="text-xs text-zinc-400 leading-relaxed max-w-[260px] mx-auto">
                    {lang === 'ua' 
                      ? `Ваш PDA аккаунт активовано до рівня ${checkoutTier?.toUpperCase()}. Насолоджуйтесь професійними ШІ функціями!` 
                      : `Your account has been upgraded to ${checkoutTier === 'premium' ? 'PREMIUM' : 'PRO'} successfully!`}
                  </p>
                </div>

                <div className="p-3.5 rounded-2xl bg-zinc-950/60 border border-zinc-800 text-left space-y-2 text-xs">
                  <div className="flex justify-between items-center text-[10px] text-zinc-500">
                    <span>{lang === 'ua' ? "Тариф:" : "Tier:"}</span>
                    <span className="font-black text-white">{checkoutTier?.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] text-zinc-500">
                    <span>{lang === 'ua' ? "Статус:" : "Status:"}</span>
                    <span className="font-bold text-emerald-500 uppercase">{lang === 'ua' ? "АКТИВНИЙ" : "ACTIVE"}</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] text-zinc-500">
                    <span>{lang === 'ua' ? "Метод:" : "Billing Method:"}</span>
                    <span>{lang === 'ua' ? "Симуляція оплати (Адмін)" : "Simulated Checkout"}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    sounds.playPop();
                    setIsCheckoutModalOpen(false);
                  }}
                  className={cn(
                    "w-full py-3 text-xs font-black uppercase tracking-widest rounded-xl transition-all cursor-pointer",
                    skin === 'stalker' 
                      ? "bg-[#4ade80] hover:bg-[#4ade80]/90 text-black" 
                      : "bg-emerald-500 hover:bg-emerald-600 text-black"
                  )}
                >
                  {lang === 'ua' ? "ПОЧАТИ КОРУВАННЯ" : "LET'S LOG"}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  };
  const renderReviewModal = () => {
    return (
      <AnimatePresence>
        {showReviewModal && pendingReviewData && (
          <motion.div
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(16px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            onClick={() => setShowReviewModal(false)}
            className={cn(
              "fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 transition-all",
              skin === 'stalker' 
                ? "bg-black/80 backdrop-blur-md" 
                : theme === 'light' 
                  ? "bg-zinc-950/40 backdrop-blur-xl" 
                  : "bg-black/70 backdrop-blur-xl"
            )}
          >
            <motion.div
              initial={{ y: "100%", opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: "100%", opacity: 0, scale: 0.97 }}
              transition={{
                type: "spring",
                stiffness: 380,
                damping: 32,
                mass: 0.8
              }}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "max-w-lg w-full relative overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[90vh]",
                skin === 'stalker'
                  ? "bg-black border-2 border-[#4ade80]/40 rounded-t-[28px] sm:rounded-none shadow-[0_0_30px_rgba(74,222,128,0.15)]"
                  : skin === 'cyberpunk'
                    ? "bg-[#0a0a1a] border-2 border-[#FCEE0A]/40 rounded-t-[28px] sm:rounded-none shadow-[0_0_30px_rgba(252,238,10,0.2)]"
                    : skin === 'anime'
                      ? "bg-[#1c1636] border-2 border-pink-500/40 rounded-t-[32px] sm:rounded-[36px] shadow-[0_0_30px_rgba(236,72,153,0.2)]"
                      : theme === 'light' 
                        ? "bg-white border border-zinc-200/60 rounded-t-[32px] sm:rounded-[40px] shadow-[0_48px_100px_-12px_rgba(0,0,0,0.18)]" 
                        : "bg-zinc-950 border border-zinc-800/80 rounded-t-[28px] sm:rounded-[32px] shadow-black/80 shadow-2xl"
              )}
            >
          <div className={cn(
            "absolute top-0 left-0 w-full h-1.5 z-30",
            skin === 'stalker' ? "bg-[#4ade80]" : skin === 'cyberpunk' ? "bg-[#FCEE0A]" : skin === 'anime' ? "bg-pink-500" : "bg-emerald-500"
          )} />
          
          {/* Mobile Bottom Sheet Pull Handle Indicator */}
          <div className="pt-2.5 pb-1 sm:pt-0 sm:pb-0 flex flex-col items-center shrink-0 cursor-grab active:cursor-grabbing">
            <div className="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700/80 rounded-full mx-auto my-1 sm:hidden shrink-0" />
          </div>

          <div className={cn(
            "px-4 py-3 sm:p-6 sm:p-7 flex items-center justify-between border-b shrink-0",
            theme === 'light' ? "border-zinc-100" : "border-zinc-900"
          )}>
            <div>
              <h3 className={cn(
                "text-xl sm:text-2xl font-black uppercase tracking-tighter italic leading-none",
                theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.08)]" : "text-zinc-100"
              )}>
                {t.reviewAnalysis}
              </h3>
              <p className={cn(
                "text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] mt-1",
                theme === 'light' ? "text-zinc-400" : "text-zinc-500"
              )}>
                {t.isThisCorrect}
              </p>
            </div>
            <button 
              onClick={() => setShowReviewModal(false)}
              className={cn(
                "p-2 sm:p-2.5 rounded-2xl transition-all group cursor-pointer",
                theme === 'light' ? "hover:bg-zinc-100 text-zinc-400 hover:text-zinc-900" : "hover:bg-zinc-900 text-zinc-500 hover:text-zinc-100"
              )}
            >
              <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-7 space-y-3.5 sm:space-y-5 custom-scrollbar">
            {/* Target Log Date Selector */}
            <div className="flex items-center justify-between p-3 sm:p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
              <div className="flex items-center gap-2 sm:gap-2.5">
                <Calendar className="w-4 h-4 text-emerald-500 shrink-0" />
                <div>
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block">
                    {lang === 'ua' ? "Дата запису" : "Logging Date"}
                  </span>
                  <span className="text-xs font-bold text-[var(--text)]">
                    {reviewLogDate ? formatDate(new Date(reviewLogDate), 'D MMMM YYYY') : formatDate(new Date(selectedDate), 'D MMMM YYYY')}
                  </span>
                </div>
              </div>
              <input
                type="date"
                value={reviewLogDate || formatDate(new Date(selectedDate), 'YYYY-MM-DD')}
                onChange={(e) => setReviewLogDate(e.target.value)}
                className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl px-2 sm:px-2.5 py-1 sm:py-1.5 text-xs font-bold text-[var(--text)] outline-none focus:border-emerald-500 transition-colors cursor-pointer"
              />
            </div>
            {((pendingReviewData.safe_choice_status) || (isPremiumOrPro && safeChoiceProfile?.active !== false)) && safeChoiceProfile?.active !== false && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setSafeChoiceDetailModalData(pendingReviewData)}
                className={cn(
                  "p-3.5 sm:p-4 rounded-2xl sm:rounded-3xl border border-dashed flex items-start gap-3 sm:gap-4 cursor-pointer group hover:scale-[1.01] hover:shadow-lg transition-all",
                  pendingReviewData.safe_choice_status === 'red' ? "bg-rose-500/10 border-rose-500/50 hover:bg-rose-500/15 hover:border-rose-500 shadow-rose-500/5" :
                  pendingReviewData.safe_choice_status === 'amber' ? "bg-amber-500/10 border-amber-500/50 hover:bg-amber-500/15 hover:border-amber-500 shadow-amber-500/5" :
                  "bg-emerald-500/10 border-emerald-500/50 hover:bg-emerald-500/15"
                )}
              >
                <div className={cn(
                  "p-2 rounded-2xl flex-shrink-0 mt-0.5 shadow-md transition-transform group-hover:scale-110",
                  pendingReviewData.safe_choice_status === 'red' ? "bg-rose-500 text-white" :
                  pendingReviewData.safe_choice_status === 'amber' ? "bg-amber-500 text-white" :
                  "bg-emerald-500 text-white"
                )}>
                  {pendingReviewData.safe_choice_status === 'red' ? <AlertCircle className="w-5 h-5" /> :
                   pendingReviewData.safe_choice_status === 'amber' ? <AlertCircle className="w-5 h-5" /> :
                   <CheckCircle2 className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h4 className={cn(
                      "font-bold uppercase tracking-widest text-xs sm:text-sm",
                      pendingReviewData.safe_choice_status === 'red' ? "text-rose-500" :
                      pendingReviewData.safe_choice_status === 'amber' ? "text-amber-500" :
                      "text-emerald-500"
                    )}>
                      {pendingReviewData.safe_choice_status === 'red' ? (t.safeChoiceStop || "Safe Choice: STOP") :
                       pendingReviewData.safe_choice_status === 'amber' ? (t.safeChoiceReview || "Safe Choice: REVIEW") :
                       (t.safeChoicePassed || "Safe Choice: PASSED")}
                    </h4>
                    <span className={cn(
                      "text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border flex items-center gap-1 transition-all shrink-0 whitespace-nowrap",
                      pendingReviewData.safe_choice_status === 'red' ? "bg-rose-500/20 text-rose-600 dark:text-rose-400 border-rose-500/30 group-hover:bg-rose-500 group-hover:text-white" :
                      pendingReviewData.safe_choice_status === 'amber' ? "bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30 group-hover:bg-amber-500 group-hover:text-white" :
                      "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 group-hover:bg-emerald-500 group-hover:text-white"
                    )}>
                      {lang === 'ua' ? 'Детальніше' : 'Tap for details'} <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                  <p className={cn(
                    "text-xs leading-relaxed",
                    theme === 'light' ? "text-zinc-600" : "text-zinc-400"
                  )}>
                    {getSafeChoiceMessage((pendingReviewData.safe_choice_status || 'green') as any, pendingReviewData.safe_choice_message)}
                  </p>
                  {pendingReviewData.detected_risk_components && (
                    <RiskComponentsList 
                      components={pendingReviewData.detected_risk_components} 
                      lang={lang} 
                      onOpenEDecoder={(q) => {
                        triggerHaptic('SUCCESS');
                        setEDecoderInitialQuery(q || '');
                        setIsEDecoderOpen(true);
                      }}
                    />
                  )}
                </div>
              </motion.div>
            )}

            {pendingReviewData.foods.map((food, idx) => {
              const originalFood = originalPendingReviewFoodsRef.current?.[idx] || food;
              const origAmt = parseFloat(String(originalFood.amount)) || 100;
              const currAmt = parseFloat(String(food.amount)) || 0;
              const ratio = origAmt > 0 ? currAmt / origAmt : 1;
              const currentCals = Math.round((originalFood.calories_kcal || 0) * ratio);
              const currentProtein = ((originalFood.protein_g || 0) * ratio).toFixed(1);

              return (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.04 }}
                className={cn(
                  "p-3.5 sm:p-5 border space-y-3 sm:space-y-4",
                  theme === 'light' 
                    ? "bg-white border-zinc-100/80 rounded-2xl sm:rounded-[32px] shadow-[0_10px_35px_rgba(0,0,0,0.05)]" 
                    : "bg-zinc-900/60 border-zinc-800/60 rounded-2xl sm:rounded-[24px] shadow-sm"
                )}
              >
                <div className="flex gap-2.5 sm:gap-4">
                  <div className="flex-1 space-y-1.5 sm:space-y-2">
                    <label className={cn(
                      "text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] ml-1",
                      theme === 'light' ? "text-zinc-400" : "text-zinc-500"
                    )}>
                      {t.foodName}
                    </label>
                    <div className="relative group">
                      <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-lg sm:text-xl filter drop-shadow-sm pointer-events-none">
                        {getFoodEmoji(food.name)}
                      </span>
                      <input
                        type="text"
                        value={food.name}
                        onChange={(e) => updatePendingFood(idx, 'name', e.target.value)}
                        className={cn(
                          "w-full border rounded-xl sm:rounded-2xl pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3.5 text-xs sm:text-sm font-bold focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all shadow-xs",
                          theme === 'light' 
                            ? "bg-zinc-50/80 border-zinc-200/80 text-zinc-900 group-hover:border-zinc-300" 
                            : "bg-zinc-900 border-zinc-800 text-zinc-100 group-hover:border-zinc-700"
                        )}
                        placeholder={lang === 'ua' ? "напр. Куряча грудка" : "e.g. Chicken Breast"}
                      />
                    </div>
                  </div>
                  <button 
                    onClick={() => removePendingFood(idx)}
                    className={cn(
                      "self-end mb-0.5 p-2.5 sm:p-3 transition-all border border-transparent shrink-0 cursor-pointer",
                      theme === 'light' 
                        ? "text-zinc-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl sm:rounded-[20px] hover:border-rose-100" 
                        : "text-zinc-500 hover:text-rose-500 hover:bg-rose-500/10 rounded-xl sm:rounded-2xl hover:border-rose-500/20"
                    )}
                  >
                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>

                {food.portion && (
                  <div className="flex justify-between items-center gap-2 px-1 text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500">
                    <span className="shrink-0">{t.portion}</span>
                    <span 
                      className="text-zinc-500 dark:text-zinc-400 normal-case font-medium italic text-xs text-right truncate max-w-[60%] ml-auto"
                      title={food.portion}
                    >
                      {food.portion}
                    </span>
                  </div>
                )}

                {/* Quick Weight/Unit and Plate Portion Controls (both collapsible, closed by default) */}
                <div className="space-y-2.5 pt-1">
                  <WeightUnitSpoiler
                    food={food}
                    idx={idx}
                    lang={lang}
                    theme={theme}
                    updatePendingFood={updatePendingFood}
                    onUpdateFood={(foodIndex, updates) => updatePendingFood(foodIndex, updates)}
                    currentCals={currentCals}
                    currentProtein={currentProtein}
                  />
                  <PortionPickerSpoiler
                    food={food}
                    originalFood={originalFood}
                    idx={idx}
                    lang={lang}
                    theme={theme}
                    updatePendingFood={updatePendingFood}
                    onUpdateFood={(foodIndex, updates) => updatePendingFood(foodIndex, updates)}
                    t={t}
                  />
                </div>
              </motion.div>
            );
            })}

            <button
              onClick={addPendingFood}
              className={cn(
                "w-full py-3.5 sm:py-5 rounded-2xl sm:rounded-[24px] transition-all flex items-center justify-center gap-2.5 sm:gap-3 font-black text-[10px] uppercase tracking-[0.2em] border-2 shadow-xs cursor-pointer",
                theme === 'light' 
                  ? "bg-white border-zinc-200/80 text-zinc-900 hover:bg-zinc-50 hover:border-zinc-300 shadow-md shadow-zinc-100" 
                  : "bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:text-emerald-500 hover:border-emerald-500/50 hover:bg-emerald-500/5"
              )}
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              {t.addItem || 'Add Food Item'}
            </button>

            {pendingReviewData.portion_analysis && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "p-4 sm:p-5 rounded-2xl sm:rounded-[24px] flex flex-col transition-all border",
                  theme === 'light' 
                    ? "bg-amber-50/50 border-amber-100/50" 
                    : "bg-amber-950/20 border-amber-900/40"
                )}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Scale className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className={cn("text-[10px] font-bold uppercase tracking-widest", theme === 'light' ? "text-amber-700" : "text-amber-500")}>
                    {t.portionAnalysis || "Portion Analysis"}
                  </span>
                </div>
                <p className={cn("text-xs leading-relaxed italic", theme === 'light' ? "text-zinc-700/90" : "text-zinc-300")}>
                  {cleanInsightQuotes(pendingReviewData.portion_analysis)}
                </p>
              </motion.div>
            )}
          </div>

          <div className={cn(
            "p-4 sm:p-7 border-t shrink-0 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-7",
            theme === 'light' ? "bg-white border-zinc-100" : "bg-zinc-950 border-zinc-900"
          )}>
            <motion.button
              onClick={handleConfirmReview}
              disabled={isReCalculating}
              whileHover={isReCalculating ? {} : { scale: 1.01 }}
              whileTap={isReCalculating ? {} : { scale: 0.98 }}
              className={cn(
                "relative w-full font-black py-3.5 sm:py-5 rounded-2xl sm:rounded-[24px] uppercase tracking-[0.2em] text-xs transition-all flex items-center justify-center border cursor-pointer",
                isReCalculating 
                  ? "bg-emerald-600/90 text-white cursor-not-allowed border-transparent opacity-90 shadow-none" 
                  : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-xl shadow-emerald-500/30 border-transparent active:scale-98"
              )}
            >
              <div className="relative z-20 flex items-center justify-center text-white">
                {isReCalculating ? (
                  <span>{lang.startsWith('ua') ? "Запис..." : "Logging..."}</span>
                ) : (
                  <div className="flex items-center gap-2.5 sm:gap-3">
                    <motion.div
                      initial={{ scale: 0, rotate: -45 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 15 }}
                    >
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </motion.div>
                    {vibe === 'casual' ? (lang === 'ua' ? 'ЗАПИСАТИ СТРАВУ' : 'LOG THIS MEAL') : t.confirmItems}
                  </div>
                )}
              </div>
            </motion.button>
          </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };
  const renderFoodExplanationModal = () => {
    if (!foodExplanation && !isGettingExplanation) return null;

    const isLight = theme === 'light';

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          className={cn(
            "border rounded-[32px] p-8 max-w-md w-full shadow-2xl relative overflow-hidden transition-colors duration-300",
            isLight 
              ? "bg-white border-zinc-100 shadow-black/10 text-zinc-800" 
              : "bg-zinc-950 border-zinc-800 shadow-black/40 text-zinc-100"
          )}
        >
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-emerald-400 to-emerald-600" />
          <button 
            onClick={() => {
              setFoodExplanation(null);
              setIsGettingExplanation(false);
            }}
            className={cn(
              "absolute top-4 right-4 p-2 rounded-full transition-colors group",
              isLight ? "hover:bg-zinc-50" : "hover:bg-zinc-800"
            )}
          >
            <X className={cn(
              "w-5 h-5 transition-colors",
              isLight 
                ? "text-zinc-400 group-hover:text-zinc-600" 
                : "text-zinc-500 group-hover:text-zinc-300"
            )} />
          </button>

          <div className="space-y-6">
            <div className={cn(
              "w-16 h-16 rounded-2xl flex items-center justify-center shadow-inner transition-colors duration-300",
              isLight ? "bg-emerald-50" : "bg-emerald-500/10"
            )}>
              {isGettingExplanation ? (
                <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
              ) : (
                <HelpCircle className={cn("w-8 h-8", isLight ? "text-emerald-600" : "text-emerald-400")} />
              )}
            </div>
            
            <div className="space-y-1">
              <h3 className={cn(
                "text-2xl font-black tracking-tight leading-tight transition-colors duration-300",
                isLight ? "text-zinc-900" : "text-white"
              )}>
                {isGettingExplanation ? t.analyzingMeal : foodExplanation?.name}
              </h3>
              <p className={cn(
                "text-[10px] font-black uppercase tracking-[0.2em] transition-colors duration-300",
                isLight ? "text-emerald-600/70" : "text-emerald-400/80"
              )}>
                {t.foodExplanation}
              </p>
            </div>

            {isGettingExplanation ? (
              <div className="space-y-3">
                <div className={cn("h-3 rounded-full w-full animate-pulse", isLight ? "bg-zinc-100" : "bg-zinc-800")} />
                <div className={cn("h-3 rounded-full w-3/4 animate-pulse", isLight ? "bg-zinc-100" : "bg-zinc-800")} />
                <div className={cn("h-3 rounded-full w-5/6 animate-pulse", isLight ? "bg-zinc-100" : "bg-zinc-800")} />
              </div>
            ) : (
              <div className={cn(
                "markdown-body leading-relaxed text-sm transition-colors duration-300",
                isLight ? "text-zinc-700" : "text-zinc-300"
              )}>
                <ReactMarkdown 
                  components={{
                    ...chatMarkdownComponents,
                    strong: ({ children }: any) => (
                      <strong className={cn(
                        "font-extrabold transition-colors duration-300",
                        isLight ? "text-zinc-900" : "text-white"
                      )}>
                        {children}
                      </strong>
                    ),
                  }}
                >
                  {foodExplanation?.explanation}
                </ReactMarkdown>
              </div>
            )}

            {!isGettingExplanation && (
              <button
                onClick={() => setFoodExplanation(null)}
                className={cn(
                  "w-full bg-emerald-500 text-white py-4 rounded-2xl font-bold hover:bg-emerald-600 transition-all text-sm shadow-lg",
                  isLight ? "shadow-emerald-100" : "shadow-emerald-950/40"
                )}
              >
                {vibe === 'casual' 
                  ? (lang === 'ua' ? 'Зрозумів' : 'Got it') 
                  : (lang === 'ua' ? 'Зрозуміло' : 'Understood')
                }
              </button>
            )}
          </div>
        </motion.div>
      </div>
    );
  };
  const renderLoginView = () => {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen flex flex-col items-center justify-center p-6 bg-[var(--bg)] relative overflow-hidden"
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20 transform-gpu">
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
              x: [0, 50, 0],
              y: [0, 30, 0]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute -top-20 -left-20 w-96 h-96 bg-[radial-gradient(circle,rgba(16,185,129,0.3)_0%,transparent_70%)] rounded-full"
          />
          <motion.div 
            animate={{ 
              scale: [1, 1.3, 1],
              rotate: [0, -45, 0],
              x: [0, -40, 0],
              y: [0, 60, 0]
            }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute -bottom-20 -right-20 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(52,211,153,0.25)_0%,transparent_70%)] rounded-full"
          />
        </div>

        {/* Floating Icons */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden hidden md:block">
          <motion.div 
            animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }} 
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-[10%] left-[15%] text-emerald-500/10"
          >
            <Apple size={120} />
          </motion.div>
          <motion.div 
            animate={{ y: [0, 20, 0], rotate: [0, -15, 0] }} 
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute top-[20%] right-[10%] text-emerald-500/10"
          >
            <Cookie size={100} />
          </motion.div>
          <motion.div 
            animate={{ y: [0, -30, 0], rotate: [0, 20, 0] }} 
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute bottom-[15%] left-[20%] text-emerald-500/10"
          >
            <Carrot size={140} />
          </motion.div>
        </div>

        <motion.div 
          layoutId="brand-card-container"
          className="w-full max-w-md"
        >
          <div className="glass rounded-[48px] p-8 md:p-12 space-y-10 relative z-10 overflow-hidden">
            {/* Top Shine */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
            
            <div className="space-y-6">
              <motion.div 
                layoutId="brand-logo-container"
                className="relative my-2"
              >
              <motion.div 
                layoutId="brand-logo-squircle"
                className="w-24 h-24 bg-emerald-500 rounded-[32px] flex items-center justify-center mx-auto shadow-2xl shadow-emerald-500/40 relative group cursor-default"
              >
                <div className="absolute inset-0 bg-white/20 rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity" />
                <motion.svg layoutId="brand-logo-svg" className="w-16 h-16 text-white drop-shadow-xs relative z-10" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M 21 17 V 29 C 21 34.5 32 34.5 32 29 V 17 M 26.5 17 V 30 M 26.5 33.5 V 47"
                    stroke="currentColor"
                    strokeWidth="3.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M 42.5 17 V 47 M 42.5 17 C 49.5 17 49.5 31 42.5 31"
                    stroke="currentColor"
                    strokeWidth="3.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </motion.svg>
              </motion.div>
              </motion.div>

              <div className="space-y-2 w-full max-w-full px-2 overflow-visible">
                <motion.div layoutId="brand-title-container" className="flex justify-center w-full overflow-visible">
                  <motion.h1 
                    layoutId="brand-title-text"
                    className="text-3xl xs:text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[var(--text)] inline-flex items-center justify-center gap-1.5 italic text-center relative pr-7 sm:pr-9 whitespace-nowrap select-none"
                  >
                    <span>MAIN</span>
                    <span className="text-emerald-500 not-italic relative">
                      Nutrition
                    </span>
                    <motion.div
                      initial={{ opacity: 0, scale: 0, rotate: -25 }}
                      animate={{ 
                        opacity: [0, 0, 1, 0.85, 1], 
                        scale: [0, 0, 1.25, 0.95, 1], 
                        rotate: [-25, -25, 12, -5, 0] 
                      }}
                      transition={{ 
                        duration: 1.3, 
                        ease: [0.16, 1, 0.3, 1], 
                        delay: 0.8,
                        times: [0, 0.1, 0.55, 0.8, 1]
                      }}
                      className="absolute -top-2 sm:-top-3 right-0 text-emerald-500 pointer-events-none"
                    >
                      <motion.div
                        animate={{ 
                          scale: [1, 1.15, 1],
                          opacity: [1, 0.8, 1],
                          rotate: [0, 8, -5, 0]
                        }}
                        transition={{
                          duration: 3.2,
                          ease: "easeInOut",
                          repeat: Infinity,
                          repeatDelay: 1.5,
                          delay: 2.1
                        }}
                      >
                        <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 fill-emerald-500 text-emerald-500" />
                      </motion.div>
                    </motion.div>
                  </motion.h1>
                </motion.div>
                <motion.p 
                  className="text-zinc-500 text-xs sm:text-sm font-medium leading-relaxed max-w-[280px] mx-auto text-center whitespace-pre-line"
                >
                  {t.loginSlogan}
                </motion.p>
              </div>
            </div>

            <motion.div className="space-y-4">
              {isStorageBlocked && (
                <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-200 text-[11px] space-y-1">
                  <div className="flex items-center gap-2 font-bold text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    <span>Storage Access Restricted</span>
                  </div>
                  <p>Browser storage is blocked (typical in strict Incognito mode or if 3rd-party cookies are blocked). Login may fail.</p>
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={isStorageBlocked || isGoogleSigningIn}
                className={cn(
                  "group relative w-full py-4 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-4 hover:translate-y-[-2px] active:translate-y-[0] transition-all shadow-xl overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed border cursor-pointer",
                  theme === 'light'
                    ? "bg-white border-zinc-200/90 text-zinc-900 hover:bg-zinc-50 shadow-zinc-200/60"
                    : "bg-white border-transparent text-zinc-900 hover:bg-zinc-100 shadow-white/10"
                )}
              >
                <div className="absolute inset-0 bg-emerald-500/0 group-hover:bg-emerald-500/5 transition-colors" />
                <div className="bg-white rounded-lg p-1.5 shadow-sm group-hover:rotate-[360deg] transition-transform duration-500">
                  <img src="https://www.google.com/favicon.ico" className="w-4 h-4" alt="Google" loading="lazy" decoding="async" />
                </div>
                <span className="relative z-10">
                  {isGoogleSigningIn ? (lang === 'ua' ? 'ВХІД ЧЕРЕЗ GOOGLE...' : 'CONNECTING GOOGLE...') : t.signInWithGoogle}
                </span>
                {isGoogleSigningIn && <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />}
              </button>

              {isGoogleSigningIn && (
                <div className="text-center animate-in fade-in duration-300">
                  <button
                    type="button"
                    onClick={() => setIsGoogleSigningIn(false)}
                    className="text-[11px] font-bold text-zinc-400 hover:text-zinc-200 underline cursor-pointer py-1"
                  >
                    {lang === 'ua' ? 'Скасувати очікування' : 'Cancel waiting'}
                  </button>
                </div>
              )}

              {loginError && (
                <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-100 text-xs text-left space-y-2 mt-4 max-w-sm">
                  <div className="flex items-center gap-2 font-bold text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    <span>Login Issue: {loginError.code || 'Error'}</span>
                  </div>
                  {loginError.code === 'auth/unauthorized-domain' ? (
                    <div className="space-y-2 leading-relaxed">
                      <p className="text-zinc-300">
                        This domain is not authorized in Firebase! Please add <strong>{window.location.hostname}</strong> to your authorized domains:
                      </p>
                      <ol className="list-decimal list-inside space-y-1 text-zinc-400">
                        <li>
                          Open{' '}
                          <a
                            href={`https://console.firebase.google.com/project/${firebaseConfig.projectId}/authentication/settings`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-400 font-bold underline inline-flex items-center gap-0.5"
                          >
                            Firebase Settings <ExternalLink className="w-3 h-3 justify-center inline" />
                          </a>
                        </li>
                        <li>
                          Go to <strong>Authorized domains</strong> tab
                        </li>
                        <li>
                          Click <strong>Add domain</strong> and paste:
                        </li>
                      </ol>
                      <div className="flex flex-col gap-2 pt-1 font-mono text-[10px] break-all">
                        <div className="flex items-center justify-between gap-2 p-2 rounded bg-zinc-950 border border-zinc-800">
                          <span className="select-all">{window.location.hostname}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(window.location.hostname);
                              toast.success(lang === 'ua' ? 'Домен скопійовано!' : 'Domain copied!');
                            }}
                            className="bg-zinc-800 hover:bg-zinc-700 text-white font-sans px-2 py-1 rounded text-[9px] transition-colors"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="leading-relaxed text-zinc-400 text-[11px]">
                        {loginError.message || 'An unexpected error occurred. Please check your credentials/connection and try again.'}
                      </p>
                      {loginError.message?.includes('missing initial state') && (
                        <p className="text-[10px] text-amber-400 italic">
                          Tip: Disable "Block third-party cookies" in browser settings or try in a regular browser window.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Language Switcher */}
              <div className="flex items-center justify-center gap-2 pt-2">
                {[
                  { id: 'en-US', label: 'EN', flag: '🇺🇸' },
                  { id: 'ua', label: 'UA', flag: '🇺🇦' }
                ].map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => {
                      setLang(l.id as Language);
                      setOnboardingLanguage(l.id as Language);
                      localStorage.setItem('app_lang', l.id);
                    }}
                    className={cn(
                      "px-3 py-1.5 rounded-xl border text-[10px] font-black tracking-widest transition-all flex items-center gap-1.5 cursor-pointer",
                      lang === l.id 
                        ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20 scale-105" 
                        : "bg-white dark:bg-zinc-800 border-zinc-100 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:border-zinc-300"
                    )}
                  >
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-center gap-2">
                <div className="h-px w-8 bg-zinc-200 dark:bg-zinc-800" />
                <p className="text-[10px] text-zinc-300 dark:text-zinc-600 font-black uppercase tracking-widest">
                  v.0.9.8(2)
                </p>
                <div className="h-px w-8 bg-zinc-200 dark:bg-zinc-800" />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    );
  };
  const renderOnboardingView = () => {
    const steps = [
      {
        title: t.onboardingWelcome,
        description: t.onboardingWelcomeDesc,
        icon: <Utensils className="w-12 h-12 text-emerald-500" />,
      },
      {
        title: t.onboardingGettingStarted,
        description: t.onboardingGettingStartedDesc,
        icon: <UserCircle className="w-12 h-12 text-emerald-500" />,
        content: (
          <div className="space-y-4 pt-2">
            <input 
              type="text"
              value={onboardingName}
              onChange={(e) => setOnboardingName(e.target.value)}
              placeholder={t.onboardingNamePlaceholder}
              className={cn(
                "w-full px-4 py-3 rounded-2xl border text-center font-bold text-lg outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all",
                theme === 'light' 
                  ? "bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 shadow-xl shadow-zinc-200/50" 
                  : "bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-600"
              )}
              autoFocus
            />
          </div>
        )
      },
      {
        title: t.onboardingCoreFeatures,
        description: t.onboardingCoreFeaturesDesc,
        icon: <Zap className="w-12 h-12 text-yellow-500" />,
        content: (
          <div className="space-y-3 text-left">
            {[
              { icon: <Camera className="w-5 h-5 text-emerald-500" />, color: 'emerald', title: t.onboardingScanMealsTitle, desc: t.onboardingScanMealsDesc },
              { icon: <MessageSquare className="w-5 h-5 text-blue-500" />, color: 'blue', title: t.onboardingAiChatTitle, desc: t.onboardingAiChatDesc },
              { icon: <TrendingUp className="w-5 h-5 text-purple-500" />, color: 'purple', title: t.onboardingAnalyticsTitle, desc: t.onboardingAnalyticsDesc },
            ].map((feature, i) => (
              <div key={i} className={cn(
                "flex items-start gap-3 p-3 rounded-2xl border transition-all",
                theme === 'light' 
                  ? "bg-white border-zinc-100 shadow-xl shadow-zinc-200/50" 
                  : "bg-zinc-900 border-zinc-800"
              )}>
                <div className={cn("p-2 rounded-xl", `bg-${feature.color}-500/10`)}>
                  {feature.icon}
                </div>
                <div className="space-y-0.5 text-left">
                  <h4 className={cn(
                    "font-bold text-xs uppercase tracking-widest",
                    theme === 'light' ? "text-zinc-800" : "text-zinc-100"
                  )}>{feature.title}</h4>
                  <p className={cn(
                    "text-[10px] leading-tight",
                    theme === 'light' ? "text-zinc-500" : "text-zinc-400"
                  )}>{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )
      },
      {
        title: t.onboardingBodyMetrics,
        description: t.onboardingBodyMetricsDesc,
        icon: <User className="w-12 h-12 text-blue-500" />,
        content: (
          <div className="space-y-3 text-left">
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => setOnboardingGender('male')}
                className={cn(
                  "p-3 rounded-2xl border-2 transition-all font-bold uppercase tracking-widest text-[10px]",
                  onboardingGender === 'male' 
                    ? "border-emerald-500 bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                    : theme === 'light'
                      ? "border-zinc-100 bg-white text-zinc-500 hover:border-zinc-200 shadow-xl shadow-zinc-200/30"
                      : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                )}
              >
                {t.onboardingMaleLabel}
              </button>
              <button 
                onClick={() => setOnboardingGender('female')}
                className={cn(
                  "p-3 rounded-2xl border-2 transition-all font-bold uppercase tracking-widest text-[10px]",
                  onboardingGender === 'female' 
                    ? "border-emerald-500 bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                    : theme === 'light'
                      ? "border-zinc-100 bg-white text-zinc-500 hover:border-zinc-200 shadow-xl shadow-zinc-200/30"
                      : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                )}
              >
                {t.onboardingFemaleLabel}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-0.5">
                <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.onboardingAgeLabel}</label>
                <input 
                  type="number" 
                  value={onboardingAge} 
                  onChange={(e) => setOnboardingAge(e.target.value)}
                  className={cn(
                    "w-full border rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-xs font-bold shadow-sm",
                    theme === 'light' ? "bg-white border-zinc-200 text-zinc-900 shadow-zinc-200/50" : "bg-zinc-900 border-zinc-800 text-zinc-100"
                  )}
                />
              </div>
              <div className="space-y-0.5">
                <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.onboardingWeightLabel}</label>
                <input 
                  type="number" 
                  value={onboardingWeight} 
                  onChange={(e) => setOnboardingWeight(e.target.value)}
                  className={cn(
                    "w-full border rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-xs font-bold shadow-sm",
                    theme === 'light' ? "bg-white border-zinc-200 text-zinc-900 shadow-zinc-200/50" : "bg-zinc-900 border-zinc-800 text-zinc-100"
                  )}
                />
              </div>
              <div className="space-y-0.5">
                <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.onboardingHeightLabel}</label>
                <input 
                  type="number" 
                  value={onboardingHeight} 
                  onChange={(e) => setOnboardingHeight(e.target.value)}
                  className={cn(
                    "w-full border rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-xs font-bold shadow-sm",
                    theme === 'light' ? "bg-white border-zinc-200 text-zinc-900 shadow-zinc-200/50" : "bg-zinc-900 border-zinc-800 text-zinc-100"
                  )}
                />
              </div>
            </div>
          </div>
        )
      },
      {
        title: t.onboardingActivityGoal,
        description: t.onboardingActivityGoalDesc,
        icon: <Activity className="w-12 h-12 text-orange-500" />,
        content: (
          <div className="space-y-3 text-left">
            <div className="space-y-0.5">
              <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.onboardingActivityLabel}</label>
              <div className="grid grid-cols-1 gap-1.5">
                {[
                  { value: 1.2, label: t.activitySedentary, description: t.activitySedentaryDesc },
                  { value: 1.375, label: t.activityLight, description: t.activityLightDesc },
                  { value: 1.55, label: t.activityModerate, description: t.activityModerateDesc },
                  { value: 1.725, label: t.activityActive, description: t.activityActiveDesc },
                  { value: 1.9, label: t.activityVeryActive, description: t.activityVeryActiveDesc },
                ].map((level) => (
                   <button
                    key={level.value}
                    onClick={() => setOnboardingActivity(level.value)}
                    className={cn(
                      "w-full p-2 rounded-xl border transition-all text-left flex flex-col gap-0",
                      onboardingActivity === level.value 
                        ? "border-emerald-500 bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                        : theme === 'light'
                          ? "border-zinc-100 bg-white text-zinc-500 hover:border-zinc-200 shadow-xl shadow-zinc-200/40"
                          : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                    )}
                  >
                    <span className={cn(
                      "text-[10px] font-bold uppercase tracking-widest", 
                      onboardingActivity === level.value ? "text-white" : theme === 'light' ? "text-zinc-700" : "text-zinc-300"
                    )}>{level.label}</span>
                    <span className={cn(
                      "text-[9px]", 
                      onboardingActivity === level.value ? "text-emerald-100" : "text-zinc-500"
                    )}>{level.description}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-0.5">
              <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.onboardingGoalLabel}</label>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { id: 'lose', label: t.onboardingGoalLose, desc: t.onboardingGoalLoseDesc },
                  { id: 'maintain', label: t.onboardingGoalMaintain, desc: t.onboardingGoalMaintainDesc },
                  { id: 'gain', label: t.onboardingGoalGain, desc: t.onboardingGoalGainDesc }
                ].map((g) => (
                  <button
                    key={g.id}
                    onClick={() => setOnboardingGoal(g.id as any)}
                    className={cn(
                      "p-2 rounded-xl border transition-all text-center flex flex-col gap-0",
                      onboardingGoal === g.id 
                        ? "border-emerald-500 bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                        : theme === 'light'
                          ? "border-zinc-100 bg-white text-zinc-500 hover:border-zinc-200 shadow-xl shadow-zinc-200/40"
                          : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                    )}
                  >
                    <span className={cn(
                      "text-[10px] font-bold uppercase tracking-widest", 
                      onboardingGoal === g.id ? "text-white" : theme === 'light' ? "text-zinc-700" : "text-zinc-300"
                    )}>{g.label}</span>
                    <span className={cn(
                      "text-[8px]", 
                      onboardingGoal === g.id ? "text-emerald-100" : "text-zinc-500"
                    )}>{g.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )
      },
      {
        title: "Nutritional Targets",
        description: t.onboardingTargetMetricsDesc,
        icon: <Target className="w-12 h-12 text-purple-500" />,
        content: (() => {
          const computedTargets = computeOnboardingTargets();
          return (
            <div className="space-y-4 text-left">
              {/* Auto-calculated Calorie Target Banner */}
              <div className={cn(
                "p-3 rounded-2xl border flex items-center justify-between shadow-sm transition-all",
                theme === 'light' ? "bg-emerald-50/80 border-emerald-100 text-emerald-900" : "bg-emerald-950/30 border-emerald-800/50 text-emerald-200"
              )}>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-500 animate-pulse shrink-0" />
                  <div>
                    <div className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                      {lang === 'ua' ? 'Розрахована ціль калорій:' : 'Calculated Calorie Target:'}
                    </div>
                    <div className="text-[9px] text-emerald-600/80 dark:text-emerald-400/80 font-medium">
                      {isTargetsCustomized 
                        ? (lang === 'ua' ? 'Власні налаштування цілей' : 'Customized goal values') 
                        : (lang === 'ua' ? 'Автоматично за вашими метриками' : 'Auto-calculated from your metrics')}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-black tracking-tight text-emerald-600 dark:text-emerald-400">
                    ~{computedTargets.calories} <span className="text-[10px] font-bold">kcal</span>
                  </span>
                  {isTargetsCustomized && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsTargetsCustomized(false);
                        const calculated = computeOnboardingTargets();
                        setOnboardingProtein(String(calculated.protein));
                        setOnboardingCarbs(String(calculated.carbs));
                        setOnboardingFat(String(calculated.fat));
                        setOnboardingWater(String(calculated.water));
                        setOnboardingSugar(String(calculated.sugar));
                        setOnboardingSodium(String(calculated.sodium));
                      }}
                      title={lang === 'ua' ? 'Скинути до розрахованих' : 'Reset to auto-calculated'}
                      className="p-1 rounded-lg hover:bg-emerald-200/50 dark:hover:bg-emerald-800/50 transition-colors text-emerald-600 dark:text-emerald-300"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.protein} (g)</label>
                  <input 
                    type="number" 
                    value={onboardingProtein} 
                    onChange={(e) => {
                      setIsTargetsCustomized(true);
                      setOnboardingProtein(e.target.value);
                    }}
                    placeholder={String(computedTargets.protein)}
                    className={cn(
                      "w-full border rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-xs font-bold transition-all shadow-sm",
                      theme === 'light' ? "bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 shadow-zinc-200/50" : "bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600"
                    )}
                  />
                </div>
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.carbs} (g)</label>
                  <input 
                    type="number" 
                    value={onboardingCarbs} 
                    onChange={(e) => {
                      setIsTargetsCustomized(true);
                      setOnboardingCarbs(e.target.value);
                    }}
                    placeholder={String(computedTargets.carbs)}
                    className={cn(
                      "w-full border rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-xs font-bold transition-all shadow-sm",
                      theme === 'light' ? "bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 shadow-zinc-200/50" : "bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600"
                    )}
                  />
                </div>
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.fat} (g)</label>
                  <input 
                    type="number" 
                    value={onboardingFat} 
                    onChange={(e) => {
                      setIsTargetsCustomized(true);
                      setOnboardingFat(e.target.value);
                    }}
                    placeholder={String(computedTargets.fat)}
                    className={cn(
                      "w-full border rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-xs font-bold transition-all shadow-sm",
                      theme === 'light' ? "bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 shadow-zinc-200/50" : "bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600"
                    )}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.water} (ml)</label>
                  <input 
                    type="number" 
                    value={onboardingWater} 
                    onChange={(e) => {
                      setIsTargetsCustomized(true);
                      setOnboardingWater(e.target.value);
                    }}
                    placeholder={String(computedTargets.water)}
                    className={cn(
                      "w-full border rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-xs font-bold transition-all shadow-sm",
                      theme === 'light' ? "bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 shadow-zinc-200/50" : "bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600"
                    )}
                  />
                </div>
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.sugar} (g)</label>
                  <input 
                    type="number" 
                    value={onboardingSugar} 
                    onChange={(e) => {
                      setIsTargetsCustomized(true);
                      setOnboardingSugar(e.target.value);
                    }}
                    placeholder={String(computedTargets.sugar)}
                    className={cn(
                      "w-full border rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-xs font-bold transition-all shadow-sm",
                      theme === 'light' ? "bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 shadow-zinc-200/50" : "bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600"
                    )}
                  />
                </div>
                <div className="space-y-0.5">
                  <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.sodium} (mg)</label>
                  <input 
                    type="number" 
                    value={onboardingSodium} 
                    onChange={(e) => {
                      setIsTargetsCustomized(true);
                      setOnboardingSodium(e.target.value);
                    }}
                    placeholder={String(computedTargets.sodium)}
                    className={cn(
                      "w-full border rounded-xl px-3 py-2 outline-none focus:border-emerald-500 transition-colors text-xs font-bold transition-all shadow-sm",
                      theme === 'light' ? "bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 shadow-zinc-200/50" : "bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600"
                    )}
                  />
                </div>
              </div>
            </div>
          );
        })()
      },
      {
        title: t.onboardingPersonalization,
        description: t.onboardingPersonalizationDesc,
        icon: <Settings className="w-12 h-12 text-zinc-500" />,
        content: (
          <div className="space-y-4 text-left">
            <div className="space-y-1">
              <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.language}</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'en-US', label: 'US', flag: '🇺🇸' },
                  { id: 'en-GB', label: 'UK', flag: '🇬🇧' },
                  { id: 'ua', label: 'UA', flag: '🇺🇦' }
                ].map((l) => (
                  <button 
                    key={l.id}
                    onClick={() => {
                      setOnboardingLanguage(l.id as Language);
                      setLang(l.id as Language);
                    }}
                    className={cn(
                      "p-3 rounded-2xl border-2 transition-all font-bold uppercase tracking-widest text-[10px] flex flex-col items-center gap-1",
                      onboardingLanguage === l.id 
                        ? "border-emerald-500 bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                        : theme === 'light'
                          ? "border-zinc-100 bg-white text-zinc-500 hover:border-zinc-200 shadow-xl shadow-zinc-200/50"
                          : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                    )}
                  >
                    <span className="text-lg">{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.theme}</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'light', label: t.light, icon: <CircleDot className="w-3 h-3" /> },
                  { id: 'dark', label: t.dark, icon: <Flame className="w-3 h-3" /> }
                ].map((themeOpt) => (
                  <button 
                    key={themeOpt.id}
                    onClick={() => {
                      setOnboardingTheme(themeOpt.id as 'light' | 'dark');
                      setTheme(themeOpt.id as 'light' | 'dark');
                    }}
                    className={cn(
                      "p-3 rounded-2xl border-2 transition-all font-bold uppercase tracking-widest text-[10px] flex items-center justify-center gap-2",
                      onboardingTheme === themeOpt.id 
                        ? "border-emerald-500 bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                        : theme === 'light'
                          ? "border-zinc-100 bg-white text-zinc-500 hover:border-zinc-200 shadow-xl shadow-zinc-200/50"
                          : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                    )}
                  >
                    {themeOpt.icon}
                    {themeOpt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 ml-1">{t.vibe}</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'standard', label: t.onboardingVibeRegular, icon: '👔' },
                  { id: 'casual', label: t.onboardingVibeCasual, icon: '🫂' }
                ].map((v) => (
                  <button 
                    key={v.id}
                    onClick={() => {
                      setOnboardingVibe(v.id as any);
                      setVibe(v.id as any);
                    }}
                    className={cn(
                      "p-3 rounded-2xl border-2 transition-all font-bold uppercase tracking-widest text-[10px] flex flex-col items-center gap-1",
                      onboardingVibe === v.id 
                        ? "border-emerald-500 bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                        : theme === 'light'
                          ? "border-zinc-100 bg-white text-zinc-500 hover:border-zinc-200 shadow-xl shadow-zinc-200/50"
                          : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                    )}
                  >
                    <span className="text-lg">{v.icon}</span>
                    <span>{v.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )
      },
      {
        title: t.onboardingReady,
        description: t.onboardingReadyDesc,
        icon: <CheckCircle2 className="w-12 h-12 text-emerald-500" />,
      }
    ];

    const currentStep = steps[onboardingStep];

    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[var(--bg)] relative">
        <motion.div 
          key={onboardingStep}
          className={cn(
            "w-full max-w-md space-y-4 text-center",
            illumination && "drop-shadow-[0_0_13px_rgba(16,185,129,0.35)]"
          )}
        >
          <div className={cn(
            "w-16 h-16 bg-[var(--card-bg)] rounded-full flex items-center justify-center mx-auto card-shadow border border-[var(--card-border)]",
            illumination && "illumination-food"
          )}>
            {React.cloneElement(currentStep.icon as any, { className: "w-8 h-8 " + ((currentStep.icon as any).props?.className || "").split(' ').filter((c: string) => !c.startsWith('w-') && !c.startsWith('h-')).join(' ') })}
          </div>
          
          <div className="space-y-2">
            <h2 className="text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">
              {currentStep.title}
            </h2>
            <p className="text-zinc-600 dark:text-zinc-400 font-medium text-sm leading-relaxed whitespace-pre-line">
              {currentStep.description}
            </p>
          </div>

          {currentStep.content && (
            <div className="py-2">
              {currentStep.content}
            </div>
          )}

          <div className="flex gap-2 justify-center">
            {steps.map((_, i) => (
              <div 
                key={i}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  i === onboardingStep 
                    ? "w-8 bg-emerald-500" 
                    : theme === 'light' ? "w-2 bg-zinc-200" : "w-2 bg-zinc-800"
                )}
              />
            ))}
          </div>

          <div className="flex gap-3">
            {onboardingStep > 0 && (
              <button
                onClick={() => setOnboardingStep(prev => prev - 1)}
                className={cn(
                  "flex-1 py-3 rounded-2xl font-bold text-base uppercase tracking-widest transition-all",
                  theme === 'light' ? "bg-zinc-100 text-zinc-500 hover:bg-zinc-200" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                )}
              >
                {t.back}
              </button>
            )}
            <button
              onClick={() => {
                if (onboardingStep < steps.length - 1) {
                  setOnboardingStep(prev => prev + 1);
                } else {
                  completeOnboarding();
                }
              }}
              className="flex-[2] bg-emerald-500 text-white py-3 rounded-2xl font-bold text-base uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20"
            >
              {onboardingStep === steps.length - 1 ? t.letsGo : t.next}
            </button>
          </div>
          <button
            onClick={skipOnboarding}
            className={cn(
              "font-bold uppercase tracking-widest transition-colors text-xs",
              theme === 'light' ? "text-zinc-400 hover:text-zinc-600" : "text-zinc-500 hover:text-zinc-300"
            )}
          >
            {t.skipOnboarding}
          </button>
        </motion.div>
      </div>
    );
  };
  const renderProgressView = () => {
    const getThemeColors = () => {
      const selectedColorObj = ILLUMINATION_COLORS.find(c => c.id === illuminationColor) || ILLUMINATION_COLORS[0];
      let primaryColor = selectedColorObj.color;
      
      if (illuminationColor === 'glacier') {
        primaryColor = '#38bdf8'; // highly visible ice blue for charts
      }

      // Default colors matching existing code
      let stepsColor = '#10b981';
      let sleepColor = '#6366f1';
      let calorieColor = '#10b981';
      let mealTimeColor = '#10b981';
      let proteinColor = '#a855f7';
      let waterColor = '#3b82f6';
      let caffeineColor = '#78350f';
      let starsColor = '#fbbf24';

      let fiberColor = '#10b981';
      let potassiumColor = '#8b5cf6';
      let cholesterolColor = '#d946ef';
      let vitaminCColor = '#f97316';
      let calciumColor = '#06b6d4';
      let ironColor = '#a1a1aa';

      let sugarColor = '#f43f5e';
      let saltColor = '#eab308';

      let macroProteinColor = '#8b5cf6';
      let macroCarbsColor = '#f59e0b';
      let macroFatColor = '#ef4444';

      if (illumination) {
        stepsColor = primaryColor;
        calorieColor = primaryColor;
        mealTimeColor = primaryColor;
        
        if (illuminationColor === 'purple') {
          sleepColor = '#818cf8';
          proteinColor = '#d946ef';
          waterColor = '#3b82f6';
          caffeineColor = '#4b5563';
          starsColor = '#fcd34d';
          fiberColor = '#a855f7';
          potassiumColor = '#c084fc';
          cholesterolColor = '#e9d5ff';
        } else if (illuminationColor === 'pink') {
          sleepColor = '#c084fc';
          proteinColor = '#f472b6';
          waterColor = '#60a5fa';
          caffeineColor = '#4b5563';
          starsColor = '#fcd34d';
          fiberColor = '#ec4899';
          potassiumColor = '#f472b6';
          cholesterolColor = '#fce7f3';
        } else if (illuminationColor === 'amber') {
          sleepColor = '#fb923c';
          proteinColor = '#f43f5e';
          waterColor = '#38bdf8';
          caffeineColor = '#78350f';
          starsColor = '#fbbf24';
          fiberColor = '#b45309';
          potassiumColor = '#d97706';
          cholesterolColor = '#fef3c7';
        } else if (illuminationColor === 'glacier') {
          sleepColor = '#818cf8';
          proteinColor = '#a78bfa';
          waterColor = '#06b6d4';
          caffeineColor = '#64748b';
          starsColor = '#cbd5e1';
          fiberColor = '#0d9488';
          potassiumColor = '#06b6d4';
          cholesterolColor = '#e0f2fe';
        } else {
          // emerald / original / green
          sleepColor = '#34d399';
          proteinColor = '#a855f7';
          waterColor = '#3b82f6';
          starsColor = '#fbbf24';
          fiberColor = '#34d399';
          potassiumColor = '#10b981';
          cholesterolColor = '#a7f3d0';
        }
      }

      return {
        primary: primaryColor,
        steps: stepsColor,
        sleep: sleepColor,
        calorie: calorieColor,
        mealTime: mealTimeColor,
        protein: proteinColor,
        water: waterColor,
        caffeine: caffeineColor,
        stars: starsColor,
        fiber: fiberColor,
        potassium: potassiumColor,
        cholesterol: cholesterolColor,
        vitaminC: vitaminCColor,
        calcium: calciumColor,
        iron: ironColor,
        sugar: sugarColor,
        salt: saltColor,
        macroProtein: macroProteinColor,
        macroCarbs: macroCarbsColor,
        macroFat: macroFatColor,
        weight: '#10b981',
        weightTrend: '#06b6d4',
        weightTarget: '#f59e0b'
      };
    };

    const tColors = getThemeColors();

    const handleShareProgress = async () => {
      let text = `Check out my nutritional progress on AI Nutritionist! 💪\nI've been hitting my calorie goals!`;
      if (consecutiveDaysStreak > 0) {
          text += `\nCurrent Streak: ${consecutiveDaysStreak} days! 🔥`;
      }
      
      if (navigator.share) {
          try {
              await navigator.share({
                  title: 'My Progress',
                  text: text,
                  url: window.location.origin
              });
          } catch (e: any) {
              if (e.name !== 'AbortError') {
                  console.error('Error sharing', e);
                  navigator.clipboard.writeText(text);
                  toast.success(lang === 'ua' ? 'Прогрес скопійовано в буфер обміну!' : 'Progress copied to clipboard!');
              }
          }
      } else {
          navigator.clipboard.writeText(text);
          toast.success(lang === 'ua' ? 'Прогрес скопійовано в буфер обміну!' : 'Progress copied to clipboard!');
      }
    };

    const currentWeightNum = parseFloat(onboardingWeight) || 70;
    const targetWeightNum = onboardingGoal === 'lose'
      ? Math.max(30, currentWeightNum - (parseFloat(weightToLose) || 5))
      : onboardingGoal === 'gain'
        ? currentWeightNum + (parseFloat(weightToLose) || 5)
        : currentWeightNum;

    // High-performance cached chart data computation
    const {
      chartData,
      streakHistoryData,
      streak30DayData,
      mealTimeData,
      mealTimingWindowsData,
      trendData,
      macroTrendData,
      weightTrendData
    } = computeProgressChartsData(
      history,
      waterLogs,
      goals,
      progressTab,
      lang,
      trendMicronutrient,
      timeFormat,
      weightLogs,
      targetWeightNum
    );

    const renderEcosystem = () => {
      const todayStr = new Date().toDateString();
      const todayMeals = history.filter(m => new Date(m.timestamp).toDateString() === todayStr);
      const todayFoods = todayMeals.flatMap(m => m.foods || []);
      const todayFit = googleFitData && googleFitData.length > 0 ? googleFitData[googleFitData.length - 1] : null;
      const stepsToday = todayFit?.steps || 8500;
      const sleepHours = todayFit?.sleep || 7.5;

      const handleSaveBiomarker = (newBio: Partial<BiomarkerLog>) => {
        const fullLog: BiomarkerLog = {
          id: `bio-${Date.now()}`,
          timestamp: Date.now(),
          ...newBio
        };
        setBiomarkers(prev => {
          const updated = [fullLog, ...prev];
          try {
            localStorage.setItem('biomarker_logs', JSON.stringify(updated));
          } catch (e) {}
          return updated;
        });
      };

      return (
        <EcosystemDashboard
          history={history}
          todayFoods={todayFoods}
          biomarkers={biomarkers}
          subscriptionTier={subscriptionTier}
          onOpenUpgradeModal={() => setIsBillingModalOpen(true)}
          lang={lang}
          theme={theme}
          skin={skin}
          stepsToday={stepsToday}
          sleepHours={sleepHours}
          onSaveBiomarker={handleSaveBiomarker}
        />
      );
    };

    const renderCalendar = () => {
      const year = calendarMonth.getFullYear();
      const month = calendarMonth.getMonth();
      const firstDay = new Date(year, month, 1).getDay(); // 0 is Sunday
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      const weeks = [];
      let currentWeek = [];
      
      // Empty cells for days before the 1st
      for (let i = 0; i < firstDay; i++) {
        currentWeek.push(null);
      }
      
      for (let day = 1; day <= daysInMonth; day++) {
        currentWeek.push(day);
        if (currentWeek.length === 7) {
          weeks.push(currentWeek);
          currentWeek = [];
        }
      }
      if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
          currentWeek.push(null);
        }
        weeks.push(currentWeek);
      }
      
      const weekdays = lang.startsWith('en') ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
      
      const getLocalDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const todayStr = getLocalDateStr(new Date());
      const selectedStr = getLocalDateStr(selectedDate);
      
      return (
        <div className="space-y-4 px-2">
          <div className={cn(
            "flex items-center justify-between p-4 rounded-[32px] border transition-all",
            theme === 'light' 
              ? "bg-white border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" 
              : "bg-zinc-900 border-zinc-800 shadow-black/20 card-shadow"
          )}>
            <button 
              onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} 
              className={cn(
                "p-2 rounded-xl transition-colors",
                theme === 'light' ? "hover:bg-zinc-50 text-zinc-400 hover:text-zinc-900" : "hover:bg-zinc-800 text-zinc-500"
              )}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className={cn(
              "font-black text-lg uppercase tracking-tight italic",
              theme === 'light' ? "text-zinc-900 drop-shadow-[0_1px_1px_rgba(0,0,0,0.05)]" : "text-zinc-100"
            )}>
              {calendarMonth.toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <button 
              onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} 
              className={cn(
                "p-2 rounded-xl transition-colors",
                theme === 'light' ? "hover:bg-zinc-50 text-zinc-400 hover:text-zinc-900" : "hover:bg-zinc-800 text-zinc-500"
              )}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          <div className={cn(
            "border rounded-[40px] p-4 sm:p-7 transition-all",
            theme === 'light' 
              ? "bg-white border-zinc-50 shadow-[0_20px_50px_rgba(0,0,0,0.06)]" 
              : "bg-zinc-900 border-zinc-800 card-shadow"
          )}>
            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 sm:mb-4">
              {weekdays.map(wd => (
                <div key={wd} className="text-center text-[10px] sm:text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-widest">{wd}</div>
              ))}
            </div>
            <div className="space-y-1 sm:space-y-2">
              {weeks.map((week, wIdx) => (
                <div key={wIdx} className="grid grid-cols-7 gap-1 sm:gap-2">
                  {week.map((day, dIdx) => {
                    if (!day) return <div key={`empty-${wIdx}-${dIdx}`} className="aspect-square"></div>;
                    
                    const dateObj = new Date(year, month, day);
                    const dateStr = getLocalDateStr(dateObj);
                    
                    const dayEntries = history.filter(e => getLocalDateStr(new Date(e.timestamp)) === dateStr);
                    const dayWater = waterLogs.filter(w => getLocalDateStr(new Date(w.timestamp)) === dateStr);
                    
                    const hasMeals = dayEntries.length > 0;
                    const hasWater = dayWater.length > 0;
                    const isToday = todayStr === dateStr;
                    const isSelected = selectedStr === dateStr;
                    
                    return (
                      <button 
                        key={dIdx}
                        onClick={() => {
                          if (dateObj.toDateString() !== selectedDate.toDateString()) {
                            setSwipeDirection(dateObj > selectedDate ? 'left' : 'right');
                          }
                          setSelectedDate(dateObj);
                          setView('home');
                        }}
                        className={cn(
                          "aspect-square rounded-[22px] flex flex-col items-center justify-center gap-1 transition-all hover:scale-105 active:scale-95 border",
                          isSelected 
                            ? "bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/30 font-black"
                            : isToday
                              ? theme === 'light'
                                ? "bg-emerald-50 border-emerald-100 text-emerald-600 font-black shadow-sm shadow-emerald-500/5"
                                : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30 font-bold"
                              : theme === 'light'
                                ? "bg-zinc-50/50 hover:bg-zinc-100 text-zinc-900 border-zinc-100/50 font-bold"
                                : "bg-zinc-800/70 hover:bg-zinc-800 text-zinc-100 border-transparent font-medium"
                        )}
                      >
                        <span className="text-sm sm:text-base">{day}</span>
                        <div className="flex gap-1 h-1.5">
                          {hasMeals && <div className={cn("w-1.5 h-1.5 rounded-full", isSelected ? "bg-white" : "bg-amber-500")}></div>}
                          {hasWater && <div className={cn("w-1.5 h-1.5 rounded-full", isSelected ? "bg-white/80" : "bg-blue-500")}></div>}
                        </div>
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    return (
      <motion.div 
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -15 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="space-y-8"
      >
        <div className="flex items-center gap-4 px-2">
          <button 
            onClick={handleBack}
            className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h2 className={cn(
            "flex-1 text-base sm:text-lg font-bold uppercase flex items-center gap-1 tracking-normal whitespace-nowrap truncate",
            theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
          )}>
            {skin === 'stalker' ? 'BIOMETRIC STABILITY' : (lang === 'ua' ? 'Прогрес' : 'Progress')}
            <TrendingUp className="w-5 h-5 text-emerald-500 shrink-0" />
          </h2>
          <button
            onClick={handleShareProgress}
            className="flex items-center gap-2 bg-emerald-100/50 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 px-4 py-2 rounded-full font-bold text-xs uppercase tracking-widest hover:bg-emerald-200/50 dark:hover:bg-emerald-500/30 transition-colors"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>

        {(() => {
          const getTabLabel = (tab: 'daily' | 'weekly' | 'monthly' | 'calendar' | 'ecosystem') => {
            if (skin === 'stalker') {
              if (tab === 'daily') return 'STALK CYCLE';
              if (tab === 'weekly') return 'DEEP SCAN';
              if (tab === 'monthly') return 'MACRO SCAN';
              if (tab === 'calendar') return 'LOGS';
              if (tab === 'ecosystem') return 'NETWORK';
              return tab;
            }
            
            if (tab === 'daily') return lang === 'ua' ? 'День' : 'Daily';
            if (tab === 'weekly') return lang === 'ua' ? 'Тиждень' : 'Weekly';
            if (tab === 'monthly') return lang === 'ua' ? 'Місяць' : 'Monthly';
            if (tab === 'calendar') return lang === 'ua' ? 'Календар' : 'Calendar';
            if (tab === 'ecosystem') return lang === 'ua' ? 'Екосистема' : 'Ecosystem';
            return tab;
          };

          return (
            <div className={cn(
              "rounded-[28px] p-1.5 mx-2 relative transition-all",
              theme === 'light' 
                ? "bg-white border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" 
                : "bg-[var(--card-bg)] border border-[var(--card-border)] shadow-xl dark:shadow-black/50"
            )}>
              <div className="grid grid-cols-3 gap-1.5 w-full">
                {/* Section 1: Period Selection (Daily, Weekly, Monthly) */}
                <div className="relative w-full" ref={periodDropdownRef}>
                  <button
                    onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
                    className={cn(
                      "w-full px-2 py-2.5 rounded-2xl text-[10px] sm:text-xs font-bold transition-all uppercase tracking-wider flex items-center justify-center gap-1 sm:gap-1.5 cursor-pointer truncate",
                      ['daily', 'weekly', 'monthly'].includes(progressTab)
                        ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                        : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800/80"
                    )}
                  >
                    <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{getTabLabel(['daily', 'weekly', 'monthly'].includes(progressTab) ? (progressTab as any) : lastSelectedPeriod)}</span>
                    <ChevronDown className={cn("w-3.5 h-3.5 shrink-0 transition-transform duration-200", isPeriodDropdownOpen && "rotate-180")} />
                  </button>

                  <AnimatePresence>
                    {isPeriodDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className={cn(
                          "absolute left-0 mt-2 w-48 rounded-2xl p-1.5 shadow-2xl z-50 border transition-all",
                          theme === 'light'
                            ? "bg-white/95 backdrop-blur-md border-zinc-100 shadow-zinc-900/10"
                            : "bg-zinc-900/95 backdrop-blur-md border-zinc-800 shadow-black/80"
                        )}
                      >
                        {(['daily', 'weekly', 'monthly'] as const).map((tab) => (
                          <button
                            key={tab}
                            onClick={() => {
                              setProgressTab(tab);
                              setLastSelectedPeriod(tab);
                              setIsPeriodDropdownOpen(false);
                              triggerHaptic('SUCCESS');
                            }}
                            className={cn(
                              "w-full text-left px-3.5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer flex items-center justify-between",
                              progressTab === tab
                                ? "bg-emerald-500 text-white font-black"
                                : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800/80 hover:text-zinc-900 dark:hover:text-zinc-100"
                            )}
                          >
                            <span>{getTabLabel(tab)}</span>
                            {progressTab === tab && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Section 2: Calendar */}
                <button
                  onClick={() => {
                    setProgressTab('calendar');
                    setIsPeriodDropdownOpen(false);
                    triggerHaptic('SUCCESS');
                  }}
                  className={cn(
                    "w-full px-2 py-2.5 rounded-2xl text-[10px] sm:text-xs font-bold transition-all uppercase tracking-wider flex items-center justify-center gap-1 sm:gap-1.5 cursor-pointer truncate",
                    progressTab === 'calendar'
                      ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                      : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800/80"
                  )}
                >
                  <Calendar className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{getTabLabel('calendar')}</span>
                </button>

                {/* Section 3: Ecosystem */}
                <button
                  onClick={() => {
                    setProgressTab('ecosystem');
                    setIsPeriodDropdownOpen(false);
                    triggerHaptic('SUCCESS');
                  }}
                  className={cn(
                    "w-full px-2 py-2.5 rounded-2xl text-[10px] sm:text-xs font-bold transition-all uppercase tracking-wider flex items-center justify-center gap-1 sm:gap-1.5 cursor-pointer truncate",
                    progressTab === 'ecosystem'
                      ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                      : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800/80"
                  )}
                >
                  <Activity className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{getTabLabel('ecosystem')}</span>
                </button>
              </div>
            </div>
          );
        })()}

        {progressTab === 'calendar' ? renderCalendar() : progressTab === 'ecosystem' ? renderEcosystem() : (
          <>

            {/* {lang.startsWith('ua') ? 'Щотижневе відео-підсумок' : 'Weekly Video Recap'} Section */}
            {progressTab === 'weekly' && (
          <div className="mx-2 bg-emerald-500/10 border border-emerald-500/20 rounded-3xl p-6 flex flex-col items-center justify-center text-center space-y-4 relative overflow-hidden">
            {/* Background effects */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
              <div className="absolute top-[-50%] left-[-10%] w-40 h-40 bg-emerald-400/20 rounded-full blur-3xl" />
              <div className="absolute bottom-[-50%] right-[-10%] w-40 h-40 bg-teal-400/20 rounded-full blur-3xl" />
            </div>
            
            <div className="relative z-10 w-full">
              <h3 className="text-lg font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-2">{lang.startsWith('ua') ? 'Щотижневе відео-підсумок' : 'Weekly Video Recap'}</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">Generate a cinematic AI video celebrating your progress this week!</p>
              
              {videoUrl ? (
                <div className="w-full rounded-2xl overflow-hidden border-2 border-emerald-500/30 shadow-lg shadow-emerald-500/20">
                  <video src={videoUrl} controls autoPlay loop className="w-full h-auto" />
                </div>
              ) : (
                <button
                  onClick={handleGenerateVideo}
                  disabled={isGeneratingVideo}
                  className="mx-auto bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-full font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 transition-all disabled:opacity-50 shadow-lg shadow-emerald-500/30"
                >
                  {isGeneratingVideo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                  {isGeneratingVideo ? 'Generating...' : 'Generate Video'}
                </button>
              )}
              
              {isGeneratingVideo && videoProgressMsg && (
                <p className="text-xs font-bold text-emerald-500 uppercase tracking-widest animate-pulse mt-4">{videoProgressMsg}</p>
              )}
            </div>
          </div>
        )}

        {skin === 'stalker' ? (
          <div className="space-y-6">
            <StalkerProgressCharts chartData={chartData} goals={goals} lang={lang} history={history} period={progressTab} weightTrendData={weightTrendData} />
            {!ignoreMealTimeLogging && (
              <MealTimeClustering history={history} lang={lang} theme={theme} skin={skin} illumination={illumination} />
            )}
            <LiquidBreakdownChart 
              waterLogs={waterLogs} 
              history={history}
              liveMeals={[]}
              period={progressTab} 
              lang={lang} 
              theme={theme} 
              skin={skin}
              illumination={illumination}
            />
          </div>
        ) : (
        <div className="space-y-10 px-2">
          {/* Achievements & Streak Consolidated Chart */}
          <ChartCard 
            illumination={illumination} 
            vibe={vibe} 
            type={achievementSubTab === 'stars' ? "achievement" : "streak"} 
            defaultOpen={true}
            title={
              achievementSubTab === 'stars' 
                ? (lang === 'ua' ? "Зоряні Досягнення" : "Stars Achievements")
                : achievementSubTab === 'streak'
                  ? (lang === 'ua' ? "Історія серії досягнень" : "Daily Streak History")
                  : (lang === 'ua' ? "Тренд серії за 30 днів" : "30-Day Streak Trend")
            } 
            icon={
              achievementSubTab === 'stars' ? (
                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              ) : achievementSubTab === 'streak' ? (
                <Flame className="w-4 h-4 text-orange-500 animate-pulse fill-orange-500" />
              ) : (
                <TrendingUp className="w-4 h-4 text-orange-500" />
              )
            }
            chartData={
              achievementSubTab === 'stars' ? chartData : achievementSubTab === 'streak' ? streakHistoryData : streak30DayData
            }
            period={achievementSubTab === 'trend' ? "30days" : (achievementSubTab === 'streak' ? "daily" : progressTab)}
            lang={lang}
            headerActions={
              <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800/80 p-0.5 rounded-xl border border-zinc-200/50 dark:border-zinc-700/50">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setAchievementSubTab('stars');
                    triggerHaptic('SUCCESS');
                  }}
                  className={cn(
                    "p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center",
                    achievementSubTab === 'stars'
                      ? "bg-amber-500 text-white shadow-md shadow-amber-500/20 font-bold"
                      : "text-zinc-400 hover:text-amber-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                  )}
                  title={lang === 'ua' ? "Зоряні Досягнення" : "Stars Achievements"}
                >
                  <Star className="w-3.5 h-3.5 fill-current" />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setAchievementSubTab('streak');
                    triggerHaptic('SUCCESS');
                  }}
                  className={cn(
                    "hidden sm:flex p-1.5 rounded-lg transition-all cursor-pointer items-center justify-center",
                    achievementSubTab === 'streak'
                      ? "bg-orange-500 text-white shadow-md shadow-orange-500/20 font-bold"
                      : "text-zinc-400 hover:text-orange-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                  )}
                  title={lang === 'ua' ? "Серія досягнень" : "Streak History"}
                >
                  <Flame className="w-3.5 h-3.5 fill-current" />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setAchievementSubTab('trend');
                    triggerHaptic('SUCCESS');
                  }}
                  className={cn(
                    "hidden sm:flex p-1.5 rounded-lg transition-all cursor-pointer items-center justify-center",
                    achievementSubTab === 'trend'
                      ? "bg-orange-500 text-white shadow-md shadow-orange-500/20 font-bold"
                      : "text-zinc-400 hover:text-orange-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                  )}
                  title={lang === 'ua' ? "30-денний тренд" : "30-Day Streak Trend"}
                >
                  <TrendingUp className="w-3.5 h-3.5" />
                </button>
              </div>
            }
          >
            {(representation) => (
              <ResponsiveContainer width="100%" height="100%">
                {achievementSubTab === 'stars' ? (
                  representation === 'line' ? (
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 12, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <YAxis 
                        hide
                        domain={[0, 8]} 
                        ticks={[0, 2, 4, 6, 8]}
                        tick={{ fontSize: 12, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                        }}
                        formatter={(value: number) => [(value || 0).toFixed(1), 'Stars']}
                      />
                      <Line type="monotone" dataKey="stars" name="Stars" stroke={tColors.stars} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 3, strokeWidth: 1, fill: tColors.stars }} isAnimationActive={true} animationDuration={900} animationEasing="ease-out" />
                    </LineChart>
                  ) : (
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 12, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <YAxis 
                        hide
                        domain={[0, 8]} 
                        ticks={[0, 2, 4, 6, 8]}
                        tick={{ fontSize: 12, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                        }}
                        formatter={(value: number) => [(value || 0).toFixed(1), 'Stars']}
                      />
                      <Bar dataKey="stars" name="Stars" fill={tColors.stars} radius={[4, 4, 0, 0]} isAnimationActive={true} animationDuration={900} animationEasing="ease-out" />
                    </BarChart>
                  )
                ) : achievementSubTab === 'streak' ? (
                  representation === 'line' ? (
                    <LineChart data={streakHistoryData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 12, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <YAxis 
                        hide
                        domain={[0, 'auto']}
                        tick={{ fontSize: 12, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          color: theme === 'dark' ? '#f4f4f5' : '#18181b'
                        }}
                        formatter={(value: any, name: any, props: any) => {
                          const row = props.payload;
                          return [
                            `${value} ${lang === 'ua' ? 'дн' : 'days'} (${row.completed})`,
                            lang === 'ua' ? 'Серія калорій' : 'Calorie Streak'
                          ];
                        }}
                        labelFormatter={(label, items) => {
                          if (items && items[0]) {
                            return `${lang === 'ua' ? 'Дата' : 'Date'}: ${items[0].payload.fullDate}`;
                          }
                          return label;
                        }}
                      />
                      <Line type="monotone" dataKey="streak" name="Streak" stroke="#f97316" strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 3, strokeWidth: 1, fill: '#f97316' }} isAnimationActive={true} animationDuration={900} animationEasing="ease-out" />
                    </LineChart>
                  ) : (
                    <BarChart data={streakHistoryData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 12, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <YAxis 
                        hide
                        domain={[0, 'auto']}
                        tick={{ fontSize: 12, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          color: theme === 'dark' ? '#f4f4f5' : '#18181b'
                        }}
                        formatter={(value: any, name: any, props: any) => {
                          const row = props.payload;
                          return [
                            `${value} ${lang === 'ua' ? 'дн' : 'days'} (${row.completed})`,
                            lang === 'ua' ? 'Серія калорій' : 'Calorie Streak'
                          ];
                        }}
                        labelFormatter={(label, items) => {
                          if (items && items[0]) {
                            return `${lang === 'ua' ? 'Дата' : 'Date'}: ${items[0].payload.fullDate}`;
                          }
                          return label;
                        }}
                      />
                      <Bar dataKey="streak" name="Streak" radius={[4, 4, 0, 0]} isAnimationActive={true} animationDuration={900} animationEasing="ease-out">
                        {streakHistoryData.map((entry: any, index: number) => {
                          const isCompleted = entry.completed === (lang === 'ua' ? 'Пройдено' : 'Completed');
                          return (
                            <Cell 
                              key={`streak-cell-${index}`} 
                              fill={isCompleted ? '#f97316' : (theme === 'dark' ? '#3f3f46' : '#e4e4e7')} 
                            />
                          );
                        })}
                      </Bar>
                    </BarChart>
                  )
                ) : (
                  representation === 'line' ? (
                    <LineChart data={streak30DayData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                        interval={4}
                      />
                      <YAxis 
                        hide
                        domain={[0, 'auto']}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          color: theme === 'dark' ? '#f4f4f5' : '#18181b'
                        }}
                        formatter={(value: any, name: any, props: any) => {
                          const row = props.payload;
                          return [
                            `${value} ${lang === 'ua' ? 'дн' : 'days'} (${row.completed})`,
                            lang === 'ua' ? 'Серія калорій' : 'Calorie Streak'
                          ];
                        }}
                        labelFormatter={(label, items) => {
                          if (items && items[0]) {
                            return `${lang === 'ua' ? 'Дата' : 'Date'}: ${items[0].payload.fullDate}`;
                          }
                          return label;
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="streak" 
                        name="Streak" 
                        stroke="#f97316" 
                        strokeWidth={3} 
                        activeDot={{ r: 6 }} 
                        dot={{ r: 2, strokeWidth: 1, fill: '#f97316' }} 
                        isAnimationActive={true}
                        animationDuration={900}
                        animationEasing="ease-out"
                      />
                    </LineChart>
                  ) : (
                    <BarChart data={streak30DayData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                        interval={4}
                      />
                      <YAxis hide domain={[0, 'auto']} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          color: theme === 'dark' ? '#f4f4f5' : '#18181b'
                        }}
                        formatter={(value: any, name: any, props: any) => {
                          const row = props.payload;
                          return [
                            `${value} ${lang === 'ua' ? 'дн' : 'days'} (${row.completed})`,
                            lang === 'ua' ? 'Серія калорій' : 'Calorie Streak'
                          ];
                        }}
                        labelFormatter={(label, items) => {
                          if (items && items[0]) {
                            return `${lang === 'ua' ? 'Дата' : 'Date'}: ${items[0].payload.fullDate}`;
                          }
                          return label;
                        }}
                      />
                      <Bar dataKey="streak" name="Streak" fill="#f97316" radius={[4, 4, 0, 0]} isAnimationActive={true} animationDuration={900} animationEasing="ease-out" />
                    </BarChart>
                  )
                )}
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Calorie & Meal Timing Consolidated Chart */}
          <ChartCard 
            illumination={illumination} 
            vibe={vibe} 
            type="food" 
            defaultOpen={true}
            title={
              calorieSubTab === 'calories' 
                ? t.calorieProgress 
                : calorieSubTab === 'distribution'
                  ? (lang === 'ua' ? "Розподіл калорій за часом" : "Meal-Time Distribution")
                  : (lang === 'ua' ? "Розподіл прийомів їжі (4-год вікна)" : "Meal Timing Breakdown (4h Windows)")
            } 
            icon={
              calorieSubTab === 'calories' ? (
                <Flame className="w-4 h-4 text-emerald-500 fill-emerald-500" />
              ) : calorieSubTab === 'distribution' ? (
                <Clock className="w-4 h-4 text-emerald-500" />
              ) : (
                <Activity className="w-4 h-4 text-emerald-500" />
              )
            }
            chartData={
              calorieSubTab === 'calories' ? chartData : calorieSubTab === 'distribution' ? mealTimeData : mealTimingWindowsData
            }
            period={progressTab}
            lang={lang}
            headerActions={
              <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800/80 p-0.5 rounded-xl border border-zinc-200/50 dark:border-zinc-700/50">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCalorieSubTab('calories');
                    triggerHaptic('SUCCESS');
                  }}
                  className={cn(
                    "p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center",
                    calorieSubTab === 'calories'
                      ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20 font-bold"
                      : "text-zinc-400 hover:text-emerald-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                  )}
                  title={t.calorieProgress}
                >
                  <Flame className="w-3.5 h-3.5 fill-current" />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCalorieSubTab('distribution');
                    triggerHaptic('SUCCESS');
                  }}
                  className={cn(
                    "p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center",
                    calorieSubTab === 'distribution'
                      ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20 font-bold"
                      : "text-zinc-400 hover:text-emerald-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                  )}
                  title={lang === 'ua' ? "Розподіл калорій за часом" : "Meal-Time Distribution"}
                >
                  <Clock className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCalorieSubTab('windows');
                    triggerHaptic('SUCCESS');
                  }}
                  className={cn(
                    "p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center",
                    calorieSubTab === 'windows'
                      ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20 font-bold"
                      : "text-zinc-400 hover:text-emerald-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                  )}
                  title={lang === 'ua' ? "Розподіл прийомів їжі (4-год вікна)" : "Meal Timing Breakdown (4h Windows)"}
                >
                  <Activity className="w-3.5 h-3.5" />
                </button>
              </div>
            }
          >
            {(representation) => (
              <ResponsiveContainer width="100%" height="100%">
                {calorieSubTab === 'calories' ? (
                  representation === 'line' ? (
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorCalories" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={tColors.calorie} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={tColors.calorie} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <YAxis hide />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                        }}
                        itemStyle={{ color: tColors.calorie, fontWeight: 'bold' }}
                      />
                      <Area type="monotone" dataKey="calories" name="Calories (kcal)" stroke={tColors.calorie} fillOpacity={1} fill="url(#colorCalories)" activeDot={{ r: 4, strokeWidth: 1 }} dot={false} />
                    </AreaChart>
                  ) : (
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <YAxis hide />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                        }}
                        itemStyle={{ color: tColors.calorie, fontWeight: 'bold' }}
                      />
                      <Bar dataKey="calories" name="Calories (kcal)" fill={tColors.calorie} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )
                ) : calorieSubTab === 'distribution' ? (
                  representation === 'line' ? (
                    <LineChart data={mealTimeData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                        interval={timeFormat === '12h' ? 3 : 2}
                      />
                      <YAxis hide />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          color: theme === 'dark' ? '#f4f4f5' : '#18181b'
                        }}
                        formatter={(value: any, name: any) => {
                          if (name === 'calories') {
                            return [`${value} kcal`, lang === 'ua' ? 'Калорії' : 'Calories'];
                          }
                          if (name === 'mealCount') {
                            return [value, lang === 'ua' ? 'Прийоми їжі' : 'Meals'];
                          }
                          return [value, name];
                        }}
                        labelFormatter={(label) => `${lang === 'ua' ? 'Час' : 'Hour'}: ${label}`}
                      />
                      <Line type="monotone" dataKey="calories" name="calories" stroke={tColors.mealTime} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                    </LineChart>
                  ) : (
                    <BarChart data={mealTimeData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                        interval={timeFormat === '12h' ? 3 : 2}
                      />
                      <YAxis hide />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          color: theme === 'dark' ? '#f4f4f5' : '#18181b'
                        }}
                        formatter={(value: any, name: any) => {
                          if (name === 'calories') {
                            return [`${value} kcal`, lang === 'ua' ? 'Калорії' : 'Calories'];
                          }
                          if (name === 'mealCount') {
                            return [value, lang === 'ua' ? 'Прийоми їжі' : 'Meals'];
                          }
                          return [value, name];
                        }}
                        labelFormatter={(label) => `${lang === 'ua' ? 'Час' : 'Hour'}: ${label}`}
                      />
                      <Bar dataKey="calories" name="calories" fill={tColors.mealTime} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )
                ) : (
                  representation === 'line' ? (
                    <LineChart data={mealTimingWindowsData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <YAxis hide allowDecimals={false} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          color: theme === 'dark' ? '#f4f4f5' : '#18181b'
                        }}
                        formatter={(value: any) => [value, lang === 'ua' ? 'Прийоми їжі' : 'Meals']}
                        labelFormatter={(label, items) => {
                          if (items && items[0]) {
                            return `${items[0].payload.label} (${label})`;
                          }
                          return label;
                        }}
                      />
                      <Line type="monotone" dataKey="count" name="count" stroke={tColors.mealTime} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                    </LineChart>
                  ) : (
                    <BarChart data={mealTimingWindowsData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <YAxis hide allowDecimals={false} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          color: theme === 'dark' ? '#f4f4f5' : '#18181b'
                        }}
                        formatter={(value: any) => [value, lang === 'ua' ? 'Прийоми їжі' : 'Meals']}
                        labelFormatter={(label, items) => {
                          if (items && items[0]) {
                            return `${items[0].payload.label} (${label})`;
                          }
                          return label;
                        }}
                      />
                      <Bar dataKey="count" name="count" fill={tColors.mealTime} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )
                )}
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Macros & 30-Day Trend Consolidated Chart */}
          {(() => {
            return (
              <ChartCard 
                illumination={illumination} 
                vibe={vibe} 
                type="food" 
                defaultOpen={true}
                title={
                  macroSubTab === 'summary' 
                    ? t.nutritionalSummary 
                    : (lang === 'ua' ? "Тренд макронутрієнтів (30 днів)" : "30-Day Macro Nutrient Trend")
                } 
                icon={<TrendingUp className="w-4 h-4 text-emerald-500" />}
                chartData={macroSubTab === 'summary' ? chartData : macroTrendData}
                period={macroSubTab === 'summary' ? progressTab : "monthly"}
                lang={lang}
                headerActions={
                  <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800/80 p-0.5 rounded-xl border border-zinc-200/50 dark:border-zinc-700/50">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMacroSubTab('summary');
                        triggerHaptic('SUCCESS');
                      }}
                      className={cn(
                        "p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center",
                        macroSubTab === 'summary'
                          ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20 font-bold"
                          : "text-zinc-400 hover:text-emerald-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                      )}
                      title={t.nutritionalSummary}
                    >
                      <TrendingUp className="w-3.5 h-3.5" />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMacroSubTab('trend');
                        triggerHaptic('SUCCESS');
                      }}
                      className={cn(
                        "p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center",
                        macroSubTab === 'trend'
                          ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20 font-bold"
                          : "text-zinc-400 hover:text-emerald-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                      )}
                      title={lang === 'ua' ? "Тренд макронутрієнтів (30 днів)" : "30-Day Macro Nutrient Trend"}
                    >
                      <Calendar className="w-3.5 h-3.5" />
                    </button>
                  </div>
                }
              >
                {(representation) => (
                  <ResponsiveContainer width="100%" height="100%">
                    {macroSubTab === 'summary' ? (
                      representation === 'line' ? (
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                          <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                          />
                          <YAxis hide />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                            }}
                          />
                          <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                          <Line type="monotone" dataKey="protein" name={t.protein + " (g)"} stroke={tColors.macroProtein} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                          <Line type="monotone" dataKey="carbs" name={t.carbs + " (g)"} stroke={tColors.macroCarbs} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                          <Line type="monotone" dataKey="fat" name={t.fat + " (g)"} stroke={tColors.macroFat} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                          <Line type="monotone" dataKey="fiber" name={t.fiber + " (g)"} stroke={tColors.fiber} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                        </LineChart>
                      ) : (
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                          <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                          />
                          <YAxis hide />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                            }}
                          />
                          <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                          <Bar dataKey="protein" name={t.protein + " (g)"} fill={tColors.macroProtein} stackId="a" radius={[0, 0, 4, 4]} />
                          <Bar dataKey="carbs" name={t.carbs + " (g)"} fill={tColors.macroCarbs} stackId="a" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="fat" name={t.fat + " (g)"} fill={tColors.macroFat} stackId="a" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="fiber" name={t.fiber + " (g)"} fill={tColors.fiber} stackId="a" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )
                    ) : (
                      representation === 'line' ? (
                        <LineChart data={macroTrendData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                          <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                            interval={4}
                          />
                          <YAxis hide />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                            }}
                          />
                          <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                          <Line type="monotone" dataKey="protein" name={t.protein + " (g)"} stroke={tColors.macroProtein} strokeWidth={3} dot={false} activeDot={{ r: 4 }} />
                          <Line type="monotone" dataKey="carbs" name={t.carbs + " (g)"} stroke={tColors.macroCarbs} strokeWidth={3} dot={false} activeDot={{ r: 4 }} />
                          <Line type="monotone" dataKey="fat" name={t.fat + " (g)"} stroke={tColors.macroFat} strokeWidth={3} dot={false} activeDot={{ r: 4 }} />
                          <Line type="monotone" dataKey="fiber" name={t.fiber + " (g)"} stroke={tColors.fiber} strokeWidth={3} dot={false} activeDot={{ r: 4 }} />
                        </LineChart>
                      ) : (
                        <BarChart data={macroTrendData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                          <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                            interval={4}
                          />
                          <YAxis hide />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                            }}
                          />
                          <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                          <Bar dataKey="protein" name={t.protein + " (g)"} fill={tColors.macroProtein} stackId="a" radius={[0, 0, 4, 4]} />
                          <Bar dataKey="carbs" name={t.carbs + " (g)"} fill={tColors.macroCarbs} stackId="a" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="fat" name={t.fat + " (g)"} fill={tColors.macroFat} stackId="a" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="fiber" name={t.fiber + " (g)"} fill={tColors.fiber} stackId="a" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )
                    )}
                  </ResponsiveContainer>
                )}
              </ChartCard>
            );
          })()}

          {/* Body Weight Trend & History Consolidated Chart */}
          <ChartCard 
            illumination={illumination} 
            vibe={vibe} 
            type="food" 
            defaultOpen={true}
            title={
              weightSubTab === 'trend'
                ? (lang === 'ua' ? "Динаміка маси тіла та тренд" : "Body Weight Trend & History")
                : (lang === 'ua' ? "Журнал зважувань" : "Weight Log History")
            } 
            icon={<Scale className="w-4 h-4 text-emerald-500" />}
            chartData={weightTrendData?.data || []}
            period={progressTab}
            lang={lang}
            headerActions={
              <div className="flex items-center gap-1.5">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const latest = weightLogs.length > 0 ? weightLogs[weightLogs.length - 1].weight_kg : parseFloat(onboardingWeight) || 70;
                    setWeightLogInput(String(latest));
                    setWeightLogDate(new Date().toISOString().split('T')[0]);
                    setWeightLogNote('');
                    setShowWeightModal(true);
                    triggerHaptic('SUCCESS');
                  }}
                  className="px-2.5 py-1 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white font-bold text-xs flex items-center gap-1 shadow-sm shadow-emerald-500/20 transition-all cursor-pointer"
                  title={lang === 'ua' ? "Записати зважування" : "Log Weigh-in"}
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{lang === 'ua' ? "Зважування" : "Log Weight"}</span>
                </button>

                <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800/80 p-0.5 rounded-xl border border-zinc-200/50 dark:border-zinc-700/50">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setWeightSubTab('trend');
                      triggerHaptic('SUCCESS');
                    }}
                    className={cn(
                      "p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center",
                      weightSubTab === 'trend'
                        ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20 font-bold"
                        : "text-zinc-400 hover:text-emerald-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                    )}
                    title={lang === 'ua' ? "Графік тренду" : "Trend Chart"}
                  >
                    <TrendingUp className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setWeightSubTab('history');
                      triggerHaptic('SUCCESS');
                    }}
                    className={cn(
                      "p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center",
                      weightSubTab === 'history'
                        ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20 font-bold"
                        : "text-zinc-400 hover:text-emerald-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                    )}
                    title={lang === 'ua' ? "Журнал записів" : "Log History"}
                  >
                    <HistoryIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            }
          >
            {(representation) => (
              <div className="w-full h-full flex flex-col">
                {/* Metric Summary Badges */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 pb-2 border-b border-zinc-200/50 dark:border-zinc-800/60">
                  <div className="bg-zinc-50 dark:bg-zinc-800/40 p-2 rounded-xl border border-zinc-100 dark:border-zinc-800 flex flex-col">
                    <span className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                      {lang === 'ua' ? "Поточна вага" : "Current Weight"}
                    </span>
                    <span className="text-sm sm:text-base font-black text-zinc-900 dark:text-zinc-50 mt-0.5">
                      {weightTrendData?.currentWeight || '--'} <span className="text-xs font-semibold text-zinc-400">kg</span>
                    </span>
                  </div>

                  <div className="bg-zinc-50 dark:bg-zinc-800/40 p-2 rounded-xl border border-zinc-100 dark:border-zinc-800 flex flex-col">
                    <span className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                      {lang === 'ua' ? "Динаміка" : "Total Delta"}
                    </span>
                    <span className={cn(
                      "text-sm sm:text-base font-black mt-0.5",
                      (weightTrendData?.changeKg || 0) < 0 ? "text-emerald-500" : (weightTrendData?.changeKg || 0) > 0 ? "text-amber-500" : "text-zinc-500"
                    )}>
                      {(weightTrendData?.changeKg || 0) > 0 ? `+${weightTrendData.changeKg}` : (weightTrendData?.changeKg || 0)} <span className="text-xs font-semibold">kg</span>
                    </span>
                  </div>

                  <div className="bg-zinc-50 dark:bg-zinc-800/40 p-2 rounded-xl border border-zinc-100 dark:border-zinc-800 flex flex-col">
                    <span className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                      {lang === 'ua' ? "Темп / тиждень" : "Weekly Pace"}
                    </span>
                    <span className="text-sm sm:text-base font-black text-cyan-600 dark:text-cyan-400 mt-0.5">
                      {(weightTrendData?.weeklyRateKg || 0) > 0 ? `+${weightTrendData.weeklyRateKg}` : (weightTrendData?.weeklyRateKg || 0)} <span className="text-xs font-semibold">kg/wk</span>
                    </span>
                  </div>

                  <div className="bg-zinc-50 dark:bg-zinc-800/40 p-2 rounded-xl border border-zinc-100 dark:border-zinc-800 flex flex-col">
                    <span className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                      {lang === 'ua' ? "Ціль" : "Target Goal"}
                    </span>
                    <span className="text-sm sm:text-base font-black text-amber-500 mt-0.5">
                      {weightTrendData?.goalWeight ? `${weightTrendData.goalWeight} ` : '-- '}
                      <span className="text-xs font-semibold text-zinc-400">kg</span>
                    </span>
                  </div>
                </div>

                {weightSubTab === 'trend' ? (
                  <div className="w-full flex-1 min-h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      {representation === 'line' ? (
                        <LineChart data={weightTrendData?.data || []}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                          <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                          />
                          <YAxis 
                            domain={['auto', 'auto']}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                            unit="kg"
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                            }}
                            formatter={(value: any, name: any) => [
                              `${value} kg`,
                              name === 'trend'
                                ? (lang === 'ua' ? 'Лінія тренду' : 'Trend Line (regression)')
                                : (lang === 'ua' ? 'Фактична вага' : 'Actual Weight')
                            ]}
                            labelFormatter={(label, items) => {
                              if (items && items[0] && items[0].payload) {
                                return `${items[0].payload.fullDate || label}${items[0].payload.note ? ` • ${items[0].payload.note}` : ''}`;
                              }
                              return label;
                            }}
                          />
                          <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                          {weightTrendData?.goalWeight && (
                            <ReferenceLine 
                              y={weightTrendData.goalWeight} 
                              stroke={tColors.weightTarget} 
                              strokeDasharray="4 4" 
                              label={{ 
                                position: 'insideTopRight', 
                                value: lang === 'ua' ? `Ціль: ${weightTrendData.goalWeight}кг` : `Goal: ${weightTrendData.goalWeight}kg`, 
                                fill: tColors.weightTarget, 
                                fontSize: 10,
                                fontWeight: 'bold'
                              }} 
                            />
                          )}
                          <Line 
                            type="monotone" 
                            dataKey="weight" 
                            connectNulls={true}
                            name={lang === 'ua' ? "Вага (кг)" : "Actual Weight (kg)"} 
                            stroke={tColors.weight} 
                            strokeWidth={3} 
                            activeDot={{ r: 6 }} 
                            dot={{ r: 3, strokeWidth: 1.5, fill: tColors.weight }} 
                          />
                          <Line 
                            type="linear" 
                            dataKey="trend" 
                            name={lang === 'ua' ? "Лінія тренду (кг)" : "Trend Line (kg)"} 
                            stroke={tColors.weightTrend} 
                            strokeWidth={2} 
                            strokeDasharray="5 5" 
                            dot={false} 
                            activeDot={false} 
                          />
                        </LineChart>
                      ) : (
                        <BarChart data={weightTrendData?.data || []}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                          <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                          />
                          <YAxis 
                            domain={['auto', 'auto']}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                            unit="kg"
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                            }}
                            formatter={(value: any) => [`${value} kg`, lang === 'ua' ? 'Вага' : 'Weight']}
                            labelFormatter={(label, items) => {
                              if (items && items[0] && items[0].payload) {
                                return `${items[0].payload.fullDate || label}${items[0].payload.note ? ` • ${items[0].payload.note}` : ''}`;
                              }
                              return label;
                            }}
                          />
                          <Bar dataKey="displayWeight" name={lang === 'ua' ? "Вага (кг)" : "Weight (kg)"} fill={tColors.weight} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="w-full flex-1 max-h-[280px] overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                    {weightLogs.length === 0 ? (
                      <div className="py-8 text-center text-zinc-400 dark:text-zinc-500 text-xs">
                        {lang === 'ua' ? "Записів зважувань поки немає. Натисніть «+ Зважування» щоб додати перший запис." : "No weigh-ins recorded yet. Click «+ Log Weight» to record your first weight."}
                      </div>
                    ) : (
                      [...weightLogs].sort((a, b) => b.timestamp - a.timestamp).map((wLog, idx, arr) => {
                        const prevLog = arr[idx + 1];
                        const diff = prevLog ? Number((wLog.weight_kg - prevLog.weight_kg).toFixed(1)) : null;
                        const dateObj = new Date(wLog.timestamp);
                        const dateStr = formatDate(dateObj, dateFormat, lang);

                        return (
                          <div 
                            key={wLog.id}
                            className="flex items-center justify-between p-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/60 dark:border-zinc-700/50 hover:border-emerald-500/30 transition-all"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0 font-bold text-xs">
                                <Scale className="w-4 h-4" />
                              </div>
                              <div>
                                <div className="text-xs font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                                  <span>{wLog.weight_kg} kg</span>
                                  {diff !== null && (
                                    <span className={cn(
                                      "text-[10px] font-semibold px-1.5 py-0.2 rounded-md",
                                      diff < 0 ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
                                      diff > 0 ? "bg-amber-500/10 text-amber-600 dark:text-amber-400" :
                                      "bg-zinc-200/50 dark:bg-zinc-700 text-zinc-500"
                                    )}>
                                      {diff > 0 ? `+${diff}` : diff} kg
                                    </span>
                                  )}
                                </div>
                                <div className="text-[10px] text-zinc-400 dark:text-zinc-500">
                                  {dateStr} {wLog.note && `• ${wLog.note}`}
                                </div>
                              </div>
                            </div>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteWeightLog(wLog.id);
                              }}
                              className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                              title={lang === 'ua' ? "Видалити запис" : "Delete record"}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}
          </ChartCard>

          {/* Protein Progress Chart */}
          <ChartCard illumination={illumination} vibe={vibe} type="food" defaultOpen={true}
            title={t.proteinProgress} 
            icon={<Dumbbell className="w-4 h-4 text-purple-500" />}
            chartData={chartData}
            period={progressTab}
            lang={lang}
          >
            {(representation) => (
              <ResponsiveContainer width="100%" height="100%">
                {representation === 'line' ? (
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                    />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <ReferenceLine 
                      y={goals.protein} 
                      stroke={tColors.protein} 
                      strokeDasharray="3 3" 
                      label={{ 
                        position: 'insideTopRight', 
                        value: `${goals.protein}g Goal`, 
                        fill: tColors.protein, 
                        fontSize: 10,
                        fontWeight: 'bold'
                      }} 
                    />
                    <Line type="monotone" dataKey="protein" name={t.protein + " (g)"} stroke={tColors.protein} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 3, strokeWidth: 1 }} />
                  </LineChart>
                ) : (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                    />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <ReferenceLine 
                      y={goals.protein} 
                      stroke={tColors.protein} 
                      strokeDasharray="3 3" 
                      label={{ 
                        position: 'insideTopRight', 
                        value: `${goals.protein}g Goal`, 
                        fill: tColors.protein, 
                        fontSize: 10,
                        fontWeight: 'bold'
                      }} 
                    />
                    <Bar dataKey="protein" name={t.protein + " (g)"} fill={tColors.protein} radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Liquid Breakdown Chart (Moved above Water Chart) */}
          <LiquidBreakdownChart 
            waterLogs={waterLogs} 
            history={history}
            liveMeals={[]}
            period={progressTab} 
            lang={lang} 
            theme={theme} 
            skin={skin}
            illumination={illumination}
          />

          {/* Water Chart */}
          <ChartCard illumination={illumination} vibe={vibe} type="water" defaultOpen={true}
            title={t.waterProgress} 
            icon={<Droplets className="w-4 h-4 text-blue-500" />}
            chartData={chartData}
            period={progressTab}
            lang={lang}
          >
            {(representation) => (
              <ResponsiveContainer width="100%" height="100%">
                {representation === 'line' ? (
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorWater" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={tColors.water} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={tColors.water} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                    />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Area type="monotone" dataKey="water" name="Water (ml)" stroke={tColors.water} fillOpacity={1} fill="url(#colorWater)" activeDot={{ r: 4, strokeWidth: 1 }} dot={false} />
                  </AreaChart>
                ) : (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                    />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Bar dataKey="water" name="Water (ml)" fill={tColors.water} radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Caffeine Chart */}
          <ChartCard illumination={illumination} vibe={vibe} type="caffeine" defaultOpen={true}
            title={t.caffeineProgress || (lang.startsWith('en') ? 'Caffeine Progress' : 'Прогрес кофеїну')} 
            icon={<Coffee className="w-4 h-4 text-amber-700" />}
            chartData={chartData}
            period={progressTab}
            lang={lang}
          >
            {(representation) => (
              <ResponsiveContainer width="100%" height="100%">
                {representation === 'line' ? (
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorCaffeine" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={tColors.caffeine} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={tColors.caffeine} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                    />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Area type="monotone" dataKey="caffeine" name="Caffeine (mg)" stroke={tColors.caffeine} fillOpacity={1} fill="url(#colorCaffeine)" />
                  </AreaChart>
                ) : (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                    />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Bar dataKey="caffeine" name="Caffeine (mg)" fill={tColors.caffeine} radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Micro Breakdown Chart */}
          <ChartCard illumination={illumination} vibe={vibe} type="food" defaultOpen={true}
            title={lang === 'ua' ? "Мікронутрієнти" : "Micro Breakdown"} 
            icon={<CircleDot className="w-4 h-4 text-emerald-500" />}
            chartData={chartData}
            period={progressTab}
            lang={lang}
          >
            {(representation) => (
              <ResponsiveContainer width="100%" height="100%">
                {representation === 'line' ? (
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                    />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                    <Line type="monotone" dataKey="potassium" name={(t.potassium || "Potassium") + " (mg)"} stroke={tColors.potassium} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                    <Line type="monotone" dataKey="cholesterol" name={(t.cholesterol || "Cholesterol") + " (mg)"} stroke={tColors.cholesterol} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                    <Line type="monotone" dataKey="vitamin_c" name={(t.vitamin_c || "Vitamin C") + " (mg)"} stroke={tColors.vitaminC} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                    <Line type="monotone" dataKey="calcium" name={(t.calcium || "Calcium") + " (mg)"} stroke={tColors.calcium} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                    <Line type="monotone" dataKey="iron" name={(t.iron || "Iron") + " (mg)"} stroke={tColors.iron} strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 2, strokeWidth: 1 }} />
                  </LineChart>
                ) : (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                    />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                    <Bar dataKey="potassium" name={(t.potassium || "Potassium") + " (mg)"} fill={tColors.potassium} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="cholesterol" name={(t.cholesterol || "Cholesterol") + " (mg)"} fill={tColors.cholesterol} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="vitamin_c" name={(t.vitamin_c || "Vitamin C") + " (mg)"} fill={tColors.vitaminC} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="calcium" name={(t.calcium || "Calcium") + " (mg)"} fill={tColors.calcium} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="iron" name={(t.iron || "Iron") + " (mg)"} fill={tColors.iron} radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* 30-Day Micronutrient Trend Line Chart */}
          {(() => {
            const trendInfo = trendData;
            const trendChartList = trendInfo.data;
            const micronutrientOptions = Object.entries(MICRONUTRIENT_TARGETS).map(([key, spec]) => {
              const label = lang.startsWith('ua')
                ? (key === 'potassium_mg' ? 'Калій' :
                   key === 'calcium_mg' ? 'Кальцій' :
                   key === 'iron_mg' ? 'Залізо' :
                   key === 'vitamin_c_mg' ? 'Вітамін C' :
                   key === 'vitamin_a_mcg' ? 'Вітамін A' :
                   key === 'vitamin_d_mcg' ? 'Вітамін D' :
                   key === 'vitamin_b12_mcg' ? 'Вітамін B12' :
                   key === 'magnesium_mg' ? 'Магній' : 'Цинк')
                : spec.name;
              return { key, label, target: spec.target, unit: spec.unit };
            });
            const currentOpt = micronutrientOptions.find(o => o.key === trendMicronutrient) || micronutrientOptions[0];

            return (
              <ChartCard illumination={illumination} vibe={vibe} type="food"
                title={lang === 'ua' ? "Тренд мікронутрієнтів (30 днів)" : "30-Day Micronutrient Trend"} 
                icon={<TrendingUp className="w-4 h-4 text-emerald-500" />}
                chartData={trendChartList}
                period="monthly"
                lang={lang}
              >
                <div className="flex flex-col h-full space-y-2 select-none">
                  {/* Horizontal scrollable pills */}
                  <div className="flex gap-1.5 overflow-x-auto no-scrollbar py-1 shrink-0">
                    {micronutrientOptions.map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setTrendMicronutrient(opt.key)}
                        className={cn(
                          "px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest whitespace-nowrap transition-all border cursor-pointer",
                          trendMicronutrient === opt.key
                            ? "bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20"
                            : "bg-zinc-100 dark:bg-zinc-800/80 text-zinc-500 hover:text-emerald-500 border-zinc-200 dark:border-zinc-700/50"
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Stats & Badge indicator */}
                  <div className="flex items-center justify-between text-xs px-1 shrink-0">
                    <div className="text-left">
                      <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-semibold block leading-none mb-1">
                        {lang === 'ua' ? 'Середнє за 30 днів' : '30-Day Avg'}
                      </span>
                      <span className="font-extrabold text-sm text-zinc-800 dark:text-zinc-200 leading-none">
                        {trendInfo.average} / {currentOpt.target} <span className="text-[10px] text-zinc-500 font-mono">{currentOpt.unit}</span>
                      </span>
                    </div>
                    
                    <div className="text-right">
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider inline-block",
                        trendInfo.isImproving 
                          ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" 
                          : "bg-zinc-500/10 text-zinc-500 border border-zinc-500/20"
                      )}>
                        {trendInfo.isImproving 
                          ? (lang === 'ua' ? 'Покращується 📈' : 'Improving 📈') 
                          : (lang === 'ua' ? 'Стабільно 📊' : 'Stable 📊')}
                      </span>
                    </div>
                  </div>

                  {/* The LineChart showing actual daily intake vs linear regression trend */}
                  <div className="flex-1 min-h-0 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendChartList} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                        <XAxis 
                          dataKey="name" 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                          interval={5}
                        />
                        <YAxis 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 9, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                            color: theme === 'dark' ? '#f4f4f5' : '#18181b',
                            fontSize: '11px'
                          }}
                        />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '4px' }} />
                        <ReferenceLine 
                          y={currentOpt.target} 
                          stroke="#f59e0b" 
                          strokeDasharray="4 4" 
                          label={{ 
                            value: lang === 'ua' ? `Ціль` : `Target`, 
                            position: 'top', 
                            fill: '#f59e0b', 
                            fontSize: 9,
                            fontWeight: 'bold'
                          }} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="intake" 
                          name={lang === 'ua' ? 'Споживання' : 'Intake'} 
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="trend" 
                          name={lang === 'ua' ? 'Тренд лінія' : 'Trend Line'} 
                          stroke="#10b981" 
                          strokeWidth={2} 
                          strokeDasharray="5 5"
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </ChartCard>
            );
          })()}

          {/* Salt and Sugar Chart */}
          <ChartCard illumination={illumination} vibe={vibe} type="food" defaultOpen={true}
            title={lang === 'ua' ? "Сіль та Цукор" : "Salt & Sugar"} 
            icon={<Candy className="w-4 h-4 text-rose-500" />}
            chartData={chartData}
            period={progressTab}
            lang={lang}
          >
            {(representation) => {
              const localChartData = chartData.map(item => ({
                ...item,
                sugar_mg: Number(((item.sugar || 0) * 1000).toFixed(0)),
                salt_mg: Number(((item.salt || 0) * 1000).toFixed(0))
              }));
              return (
                <ResponsiveContainer width="100%" height="100%">
                  {representation === 'line' ? (
                    <LineChart data={localChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis type="number" hide />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 14, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                        }}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }} />
                      <Line type="monotone" dataKey="sugar_mg" name={t.sugar + " (mg)"} stroke={tColors.sugar} strokeWidth={3} />
                      <Line type="monotone" dataKey="salt_mg" name={t.sodium + " (mg)"} stroke={tColors.salt} strokeWidth={3} />
                    </LineChart>
                  ) : (
                    <BarChart data={localChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme === 'dark' ? '#27272a' : '#f4f4f5'} />
                      <XAxis type="number" hide />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 14, fill: theme === 'dark' ? '#71717a' : '#a1a1aa' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                        }}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }} />
                      <Bar dataKey="sugar_mg" name={t.sugar + " (mg)"} fill={tColors.sugar} radius={[0, 4, 4, 0]} barSize={20} />
                      <Bar dataKey="salt_mg" name={t.sodium + " (mg)"} fill={tColors.salt} radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              );
            }}
          </ChartCard>

          {/* Meal Time Clustering Chart */}
          {!ignoreMealTimeLogging && (
            <MealTimeClustering 
              history={history} 
              lang={lang} 
              theme={theme} 
              skin={skin} 
              illumination={illumination} 
            />
          )}

          </div>
        )}
          </>
        )}
      </motion.div>
    );
  };
  const old_ProgressBar = ({ label, current, goal, colorClass, unit = 'g', isNutrient, isSaltSugar, isFat, isCarbs, noSuccessColor, onClick, projected, gradientClass }: { label: string, current: number, goal: number, colorClass?: string, unit?: string, isNutrient?: boolean, isSaltSugar?: boolean, isFat?: boolean, isCarbs?: boolean, noSuccessColor?: boolean, onClick?: () => void, projected?: number, gradientClass?: string }) => {
    const percentage = Math.min((current / goal) * 100, 100);
    const projectedPercentage = projected ? Math.min((projected / goal) * 100, 100) : 0;
    
    const isGoalMet = isSaltSugar 
      ? (current > 0 && current <= goal * 1.25) 
      : ((isFat || isCarbs) ? (current >= goal && current <= goal * 1.25) : current >= goal);
    const over25Percent = current > goal * 1.25;

    let barClassName = colorClass || "";
    let effectiveGradient = gradientClass;

    if (isNutrient || isFat || isCarbs) {
      if (over25Percent) {
        barClassName = "bg-red-500";
        effectiveGradient = "from-red-600 to-red-400";
      }
    } else if (isSaltSugar) {
      const ratio = current / goal;
      if (ratio > 1.2) {
        barClassName = "bg-red-600";
        effectiveGradient = "from-red-700 to-red-500";
      } else if (ratio > 1) {
        barClassName = "bg-red-500";
        effectiveGradient = "from-red-600 to-red-400";
      } else if (ratio > 0.85) {
        barClassName = "bg-amber-500";
        effectiveGradient = "from-amber-600 to-amber-400";
      }
    }
    
    const vibrancy = percentage >= 70 ? 1 + (percentage - 70) / 30 : 1;
    const barStyle: React.CSSProperties = { filter: `saturate(${vibrancy})` };
    
    return (
      <div className={cn("space-y-3", onClick && "cursor-pointer group")} onClick={onClick}>
        <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-widest">
          <div className="flex items-center gap-1.5">
            <span className={cn("text-zinc-500 dark:text-zinc-400 font-black whitespace-pre-line leading-tight", onClick && "group-hover:text-emerald-500 transition-colors")}>{label}</span>
            {isGoalMet && (isSaltSugar ? current > 0 : true) && (
              <AnimatePresence>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex gap-0.5 items-center"
                >
                  <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
                </motion.div>
              </AnimatePresence>
            )}
          </div>
          <span className={cn("text-[#71717b] dark:text-zinc-500 normal-case font-bold", isGoalMet && !noSuccessColor && "text-emerald-500", noSuccessColor && "text-purple-500")}>
            {typeof current === 'number' ? Math.round(current) : current}{unit} {projected && projected > current && <span className="text-[#a1a1aa] dark:text-zinc-600">(+{Math.round(projected - current)})</span>} / <span className="opacity-70">{goal}{unit}</span>
          </span>
        </div>
        <div className="h-3 bg-zinc-100 dark:bg-zinc-800/40 rounded-full overflow-hidden relative">
          {projectedPercentage > percentage && (
            <motion.div 
              initial={{ width: `${percentage}%` }}
              animate={{ width: `${projectedPercentage}%` }}
              transition={{
                width: { type: "spring", stiffness: 100, damping: 20 }
              }}
              className={cn(
                "absolute inset-y-0 left-0 opacity-20 rounded-full z-0 h-full", 
                effectiveGradient ? `bg-gradient-to-r ${effectiveGradient}` : barClassName
              )}
              style={barStyle}
            />
          )}
          <motion.div 
            initial={{ width: 0 }}
            animate={{ 
              width: `${percentage}%`,
              boxShadow: isGoalMet && !noSuccessColor ? [
                "0 0 0px rgba(16, 185, 129, 0)",
                "0 0 12px rgba(16, 185, 129, 0.4)",
                "0 0 0px rgba(16, 185, 129, 0)"
              ] : "none"
            }}
            transition={{
              width: { type: "spring", stiffness: 100, damping: 20 },
              boxShadow: { repeat: Infinity, duration: 2 }
            }}
            style={barStyle}
            className={cn(
              "h-full rounded-full transition-colors duration-500 relative z-10", 
              effectiveGradient ? `bg-gradient-to-r ${effectiveGradient}` : barClassName,
              onClick && "group-hover:brightness-110",
              isGoalMet && !noSuccessColor && "animate-pulse-subtle"
            )}
          />
        </div>

        <div className="hidden group-hover:block transition-all duration-300 opacity-0 group-hover:opacity-100 h-0 group-hover:h-auto group-hover:mt-2 text-left">
          <div className="p-3 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl text-[11px] font-medium leading-relaxed text-[var(--text)] space-y-2 card-shadow transition-all duration-300">
            <div className="flex justify-between items-center text-xs border-b border-[var(--card-border)] pb-1.5">
              <span className="font-bold text-zinc-800 dark:text-zinc-100">
                {lang === 'ua' ? 'Джерело інформації' : 'Nutrient Insights'}
              </span>
              <span className="font-mono text-[10px] bg-zinc-100 dark:bg-zinc-800/60 px-1.5 py-0.5 rounded text-zinc-600 dark:text-zinc-300">
                {label}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-xs pt-0.5">
              <div className="bg-zinc-50 dark:bg-zinc-900/30 p-2 rounded-xl border border-zinc-100/60 dark:border-zinc-800/20">
                <span className="text-[10px] text-zinc-400 dark:text-zinc-500 block uppercase tracking-wider font-semibold">
                  {lang === 'ua' ? 'Денна Ціль' : 'Daily Goal'}
                </span>
                <span className="font-mono font-bold text-zinc-800 dark:text-zinc-100">
                  {Math.round(goal)}{unit}
                </span>
              </div>
              
              <div className="bg-zinc-50 dark:bg-zinc-900/30 p-2 rounded-xl border border-zinc-100/60 dark:border-zinc-800/20">
                <span className="text-[10px] text-zinc-400 dark:text-zinc-500 block uppercase tracking-wider font-semibold">
                  {isSaltSugar 
                    ? (lang === 'ua' ? 'До ліміту' : 'Remaining') 
                    : (lang === 'ua' ? 'Залишилось' : 'Remaining')}
                </span>
                <span className={cn(
                  "font-mono font-bold",
                  isSaltSugar 
                    ? (current > goal ? "text-red-500" : "text-emerald-500") 
                    : (current >= goal ? "text-emerald-500" : "text-amber-500")
                )}>
                  {isSaltSugar ? (
                    current > goal 
                      ? `+${Math.round(current - goal)}${unit}` 
                      : `${Math.round(goal - current)}${unit}`
                  ) : (
                    current >= goal 
                      ? (lang === 'ua' ? 'Виконано!' : 'Met!') 
                      : `${Math.round(goal - current)}${unit}`
                  )}
                </span>
              </div>
            </div>

            <div className="text-[11px] text-zinc-600 dark:text-zinc-300 bg-zinc-50/50 dark:bg-zinc-900/20 p-2 rounded-xl border border-zinc-100/50 dark:border-zinc-800/10 mt-1">
              {isSaltSugar ? (
                current > goal ? (
                  lang === 'ua' 
                    ? `⚠️ Ви перевищили рекомендований ліміт ${label.toLowerCase()} на ${Math.round(current - goal)}${unit}.`
                    : `⚠️ You've exceeded the recommended limit of ${label.toLowerCase()} by ${Math.round(current - goal)}${unit}.`
                ) : (
                  lang === 'ua'
                    ? `✓ Ви в межах ліміту. Намагайтеся не перевищувати його.`
                    : `✓ You are within the limit. Keep aiming to stay below.`
                )
              ) : (
                current >= goal ? (
                  lang === 'ua'
                    ? `🎉 Чудово! Ви повністю покрили свою норму ${label.toLowerCase()} на сьогодні.`
                    : `🎉 Excellent! You've successfully covered your daily intake requirement of ${label.toLowerCase()}.`
                ) : (
                  lang === 'ua'
                    ? `💪 Потрібно ще ${Math.round(goal - current)}${unit} ${label.toLowerCase()} для досягнення цілі.`
                    : `💪 Need ${Math.round(goal - current)}${unit} more of ${label.toLowerCase()} to hit your daily goal.`
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
  const old_DailyProgressSummary = ({ totals, goals, title = t.dailyProgress, projectedTotals }: { totals: any, goals: UserGoals, title?: string, projectedTotals?: any }) => {
    const handleNutrientClick = (key: keyof TotalNutrition | 'calories' | 'water', label: string) => {
      setSelectedNutrient({ key, label });
      setView('nutrient_breakdown');
    };

    const isToday = new Date().toDateString() === selectedDate.toDateString();

    const getNutrientStars = (val: number, target: number, type: 'limit' | 'macro' | 'target') => {
      if (val <= 0) return 0;
      if (type === 'limit') {
        // Salt/Sugar: Stay under 125%
        return val <= target * 1.25 ? 1 : 0;
      }
      if (type === 'macro') {
        // Carbs/Fats: Hit target within 125%
        return (val >= target && val <= target * 1.25) ? 1 : 0;
      }
      // Others (calories/targets): 5% grace margin (>= 95% of goal) counts as met
      return val >= target * 0.95 ? 1 : 0;
    };

    const displayTotals = projectedTotals || totals;

    const totalStars = 
      getNutrientStars(displayTotals.calories, goals.calories, 'target') +
      getNutrientStars(displayTotals.water, goals.water_ml, 'target') +
      getNutrientStars(displayTotals.protein, goals.protein, 'target') +
      getNutrientStars(displayTotals.fiber, goals.fiber_g, 'target') +
      getNutrientStars(displayTotals.carbs, goals.carbs, 'macro') +
      getNutrientStars(displayTotals.fat, goals.fat, 'macro') +
      getNutrientStars(displayTotals.sugar, goals.sugar_g, 'limit') +
      getNutrientStars(displayTotals.sodium, goals.sodium_mg, 'limit') +
      getNutrientStars(displayTotals.caffeine, goals.caffeine_mg || 400, 'limit');

    const maxStars = 9;

    const isCalorieGoalMet = goals.calories > 0 && displayTotals.calories >= goals.calories * 0.95;

    const isSixOrSevenStars = totalStars === 6 || totalStars === 7;
    const isFiveToSevenStars = totalStars >= 5 && totalStars <= 7;

    useEffect(() => {
      if (typeof window === 'undefined') return;
      
      if (isCalorieGoalMet) {
        const dateStr = selectedDate ? new Date(selectedDate).toDateString() : new Date().toDateString();
        const celebKey = `celebrated_cal_${dateStr}`;
        const hasCelebrated = localStorage.getItem(celebKey) === 'true';

        if (!hasCelebrated) {
          // Play major celebration/success sound
          sounds.playSuccess();
          localStorage.setItem(celebKey, 'true');
          
          // Confetti celebration blast
          const duration = 2.5 * 1000;
          const end = Date.now() + duration;
          const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

          (function frame() {
            // Primary energetic side blasts (default gravity)
            confetti({
              particleCount: 3,
              angle: 60,
              spread: 50,
              origin: { x: 0, y: 0.8 },
              colors: ['#ef4444', '#10b981', '#fbbf24', '#3b82f6', '#dc2626', '#22c55e']
            });
            confetti({
              particleCount: 3,
              angle: 120,
              spread: 50,
              origin: { x: 1, y: 0.8 },
              colors: ['#ef4444', '#10b981', '#fbbf24', '#3b82f6', '#dc2626', '#22c55e']
            });

            // Slower-falling secondary layer centered around the .trophy-icon
            if (Math.random() < 0.25) {
              let trophyOrigin = { x: 0.5, y: 0.3 }; // Fallback to upper-center
              try {
                const trophyElements = document.querySelectorAll('.trophy-icon');
                let visibleTrophy = null;
                for (let i = 0; i < trophyElements.length; i++) {
                  const el = trophyElements[i];
                  const rect = el.getBoundingClientRect();
                  if (rect.width > 0 && rect.height > 0) {
                    visibleTrophy = el;
                    break;
                  }
                }
                if (visibleTrophy) {
                  const rect = visibleTrophy.getBoundingClientRect();
                  trophyOrigin = {
                    x: (rect.left + rect.width / 2) / window.innerWidth,
                    y: (rect.top + rect.height / 2) / window.innerHeight
                  };
                }
              } catch (e) {
                console.warn('Could not determine trophy-icon position:', e);
              }

              // Fire slower-falling particles with low gravity and vibrant multicolor palette
              confetti({
                particleCount: 2,
                angle: randomInRange(60, 120),
                spread: 45,
                startVelocity: randomInRange(10, 18),
                gravity: 0.45, // Much slower falling speed (default is 1)
                scalar: randomInRange(0.7, 1.1),
                drift: randomInRange(-0.5, 0.5),
                origin: trophyOrigin,
                colors: ['#ef4444', '#10b981', '#fbbf24', '#3b82f6', '#f59e0b', '#22c55e']
              });
            }

            if (Date.now() < end) {
              requestAnimationFrame(frame);
            }
          }());
        }
      } else {
        // Reset celebration if they edit or remove a meal to drop below target
        const dateStr = selectedDate ? new Date(selectedDate).toDateString() : new Date().toDateString();
        const celebKey = `celebrated_cal_${dateStr}`;
        if (localStorage.getItem(celebKey) === 'true') {
          localStorage.removeItem(celebKey);
        }
      }
    }, [isCalorieGoalMet, selectedDate]);

    return (
      <div className={cn(
        "bg-[var(--card-bg)] border rounded-3xl p-6 space-y-6 relative overflow-hidden card-shadow transition-all duration-500 daily-grind-container", 
        isCalorieGoalMet 
          ? "active border-amber-400/40 ring-1 ring-amber-400/20 shadow-[0_0_30px_rgba(251,191,36,0.15)]" 
          : "border-[var(--card-border)]",
        isFiveToSevenStars ? "px-4 py-6 sm:p-6" : "",
        illumination ? "illumination-both" : "",
        isCalorieGoalMet ? "streak-increased" : ""
      )}>
        {illumination && (
          <>
            <div className="absolute -left-20 -top-20 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] pointer-events-none" />
          </>
        )}
        <div className={cn(
          "flex relative z-10 justify-between gap-2",
          isFiveToSevenStars ? "items-start" : "items-center"
        )}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={cn(
                "text-lg font-bold shrink-0",
                theme === 'light' ? "text-zinc-900 drop-shadow-[0_1px_2px_rgba(0,0,0,0.05)]" : ""
              )}>{title}</h3>
              <div className={cn(
                "flex gap-1 items-center flex-wrap shrink-0",
                isFiveToSevenStars ? "hidden sm:flex" : "flex"
              )}>
                <motion.div 
                  className={cn(
                    "flex gap-0.5 items-center flex-wrap px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full transition-all duration-500 stars-shimmer-container",
                    isCalorieGoalMet 
                      ? "bg-amber-400/15 border border-amber-400/30 shadow-[0_0_15px_rgba(251,191,36,0.25)] active-shimmer" 
                      : ""
                  )}
                  animate={isCalorieGoalMet ? {
                    boxShadow: ["0 0 10px rgba(251,191,36,0.2)", "0 0 20px rgba(251,191,36,0.5)", "0 0 10px rgba(251,191,36,0.2)"]
                  } : {}}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <AnimatePresence>
                    {Array.from({ length: totalStars }).map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0, rotate: -180 }}
                        animate={isCalorieGoalMet ? {
                          opacity: 1,
                          scale: [1, 1.25, 1],
                          rotate: [0, 8, -8, 0],
                        } : {
                          opacity: 1,
                          scale: 1,
                          rotate: 0,
                        }}
                        exit={{ opacity: 0, scale: 0 }}
                        transition={isCalorieGoalMet ? {
                          scale: {
                            repeat: Infinity,
                            repeatType: "reverse",
                            duration: 1.2,
                            delay: i * 0.15,
                          },
                          rotate: {
                            repeat: Infinity,
                            repeatType: "reverse",
                            duration: 1.5,
                            delay: i * 0.1,
                          },
                          default: {
                            type: "spring",
                            stiffness: 260,
                            damping: 20,
                            delay: i * 0.05
                          }
                        } : {
                          type: "spring",
                          stiffness: 260,
                          damping: 20,
                          delay: i * 0.05
                        }}
                      >
                        <Star className={cn("w-3.5 h-3.5 sm:w-4 sm:h-4 fill-amber-400 text-amber-400", isCalorieGoalMet ? "drop-shadow-[0_0_3px_rgba(251,191,36,0.7)] animate-pulse" : "")} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  
                  {totalStars === maxStars && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest ml-1 bg-emerald-500/10 px-2 py-0.5 rounded-full"
                    >
                      Perfect Day!
                    </motion.span>
                  )}
                </motion.div>
                
                {isCalorieGoalMet && (
                  <motion.div
                    initial={{ scale: 0, rotate: -45 }}
                    animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                    transition={{
                      scale: { repeat: Infinity, duration: 1.8, repeatType: "reverse" },
                      rotate: { repeat: Infinity, duration: 2.2, repeatType: "reverse" },
                      default: { type: "spring", stiffness: 300, damping: 15 }
                    }}
                    className={cn(
                      "text-sm select-none trophy-icon",
                      isSixOrSevenStars ? "hidden md:block" : ""
                    )}
                    title={lang === 'ua' ? "Ціль по калоріях досягнута!" : "Calorie Goal Met!"}
                  >
                    🏆
                  </motion.div>
                )}
              </div>
            </div>
            <p className="text-zinc-500 text-xs mt-1">{t.progressTargets}</p>
          </div>
          <div className="text-right cursor-pointer group shrink-0" onClick={() => handleNutrientClick('calories', t.calories)}>
            {isFiveToSevenStars && (
              <div className="block sm:hidden mb-1.5 text-right">
                <motion.div 
                  className={cn(
                    "inline-flex gap-1 items-center px-1.5 py-0.5 rounded-xl transition-all duration-500 stars-shimmer-container",
                    isCalorieGoalMet 
                      ? "bg-amber-400/15 border border-amber-400/30 shadow-[0_0_15px_rgba(251,191,36,0.25)] active-shimmer" 
                      : "bg-zinc-500/10 border border-[var(--card-border)]"
                  )}
                  animate={isCalorieGoalMet ? {
                    boxShadow: ["0 0 10px rgba(251,191,36,0.2)", "0 0 20px rgba(251,191,36,0.5)", "0 0 10px rgba(251,191,36,0.2)"]
                  } : {}}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <motion.span
                    initial={{ scale: 0, rotate: -45 }}
                    animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                    transition={{
                      scale: { repeat: Infinity, duration: 1.8, repeatType: "reverse" },
                      rotate: { repeat: Infinity, duration: 2.2, repeatType: "reverse" },
                      default: { type: "spring", stiffness: 300, damping: 15 }
                    }}
                    className="text-xs select-none mr-0.5 trophy-icon"
                  >
                    🏆
                  </motion.span>
                  <div className="flex gap-0.5 items-center">
                    {Array.from({ length: totalStars }).map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0, rotate: -180 }}
                        animate={isCalorieGoalMet ? {
                          opacity: 1,
                          scale: [1, 1.25, 1],
                          rotate: [0, 8, -8, 0],
                        } : {
                          opacity: 1,
                          scale: 1,
                          rotate: 0,
                        }}
                        exit={{ opacity: 0, scale: 0 }}
                        transition={isCalorieGoalMet ? {
                          scale: {
                            repeat: Infinity,
                            repeatType: "reverse",
                            duration: 1.2,
                            delay: i * 0.15,
                          },
                          rotate: {
                            repeat: Infinity,
                            repeatType: "reverse",
                            duration: 1.5,
                            delay: i * 0.1,
                          },
                          default: {
                            type: "spring",
                            stiffness: 260,
                            damping: 20,
                            delay: i * 0.05
                          }
                        } : {
                          type: "spring",
                          stiffness: 260,
                          damping: 20,
                          delay: i * 0.05
                        }}
                      >
                        <Star className={cn("w-3 h-3 fill-amber-400 text-amber-400", isCalorieGoalMet ? "drop-shadow-[0_0_3px_rgba(251,191,36,0.7)] animate-pulse" : "")} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            )}
            {isSixOrSevenStars && isCalorieGoalMet && (
              <div className={cn("mb-1", isFiveToSevenStars ? "hidden sm:block md:hidden" : "block md:hidden")}>
                <motion.div
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: [1, 1.25, 1], rotate: [0, 10, -10, 0] }}
                  transition={{
                    scale: { repeat: Infinity, duration: 1.8, repeatType: "reverse" },
                    rotate: { repeat: Infinity, duration: 2.2, repeatType: "reverse" },
                    default: { type: "spring", stiffness: 300, damping: 15 }
                  }}
                  className="text-base select-none text-right trophy-icon"
                >
                  🏆
                </motion.div>
              </div>
            )}
            <div className="flex items-center justify-end gap-1.5">
              <AnimatePresence>
                {goals.calories > 0 && Math.round(displayTotals.calories) >= Math.floor(goals.calories * 0.95) && (
                  <motion.div
                    initial={{ scale: 0, rotate: -45, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 600, 
                      damping: 15,
                      mass: 0.8 
                    }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  </motion.div>
                )}
              </AnimatePresence>
              <p className="text-2xl font-bold text-emerald-500 group-hover:scale-110 transition-transform">{Math.round(displayTotals.calories)}</p>
            </div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#71717b]">{t.kcal} / {goals.calories}</p>
          </div>
        </div>
        <div className="space-y-6">
          <ProgressBar 
            label={t.calories} 
            current={Math.round(totals.calories)} 
            projected={projectedTotals ? Math.round(projectedTotals.calories) : undefined}
            goal={goals.calories} 
            gradientClass="from-emerald-600 to-emerald-400"
            unit={t.kcal}
            onClick={() => handleNutrientClick('calories', t.calories)}
            lang={lang}
          />
          <ProgressBar 
            label={t.water} 
            current={Math.round(totals.water)} 
            projected={projectedTotals ? Math.round(projectedTotals.water) : undefined}
            goal={goals.water_ml} 
            gradientClass="from-blue-600 to-blue-400" 
            unit={t.ml}
            onClick={() => handleNutrientClick('water', t.water)}
            lang={lang}
          />
          <ProgressBar 
            label={t.caffeine || 'Caffeine'} 
            current={totals.caffeine} 
            projected={projectedTotals ? projectedTotals.caffeine : undefined}
            goal={goals.caffeine_mg} 
            gradientClass={(totals.caffeine / goals.caffeine_mg) > 1.2 ? "from-red-600 to-red-400" : (totals.caffeine / goals.caffeine_mg) > 1 ? "from-red-500 to-red-400" : (totals.caffeine / goals.caffeine_mg) > 0.8 ? "from-amber-600 to-amber-400" : "from-amber-800 to-amber-600"}
            unit="mg"
            isSaltSugar
            onClick={() => handleNutrientClick('caffeine_mg', t.caffeine || 'Caffeine')}
            lang={lang}
          />
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-6">
              <ProgressBar 
                label={t.protein} 
                current={totals.protein}
                projected={projectedTotals?.protein}
                goal={goals.protein} 
                gradientClass="from-purple-600 to-purple-400" 
                isNutrient
                noSuccessColor
                onClick={() => handleNutrientClick('protein_g', t.protein)}
                lang={lang}
              />
              <ProgressBar 
                label={t.carbs} 
                current={totals.carbs} 
                projected={projectedTotals?.carbs}
                goal={goals.carbs} 
                gradientClass="from-teal-600 to-teal-400" 
                isNutrient
                isCarbs
                onClick={() => handleNutrientClick('carbs_g', t.carbs)}
                lang={lang}
              />
              <ProgressBar 
                label={t.fat} 
                current={totals.fat} 
                projected={projectedTotals?.fat}
                goal={goals.fat} 
                gradientClass="from-orange-600 to-orange-400" 
                isNutrient
                isFat
                onClick={() => handleNutrientClick('fat_g', t.fat)}
                lang={lang}
              />
            </div>
            <div className="space-y-6">
              <ProgressBar 
                label={t.fiber || 'Fiber'} 
                current={totals.fiber} 
                projected={projectedTotals?.fiber}
                goal={goals.fiber_g} 
                gradientClass="from-emerald-600 to-emerald-400" 
                unit="g"
                isNutrient
                onClick={() => handleNutrientClick('fiber_g', t.fiber || 'Fiber')}
                lang={lang}
              />
              <ProgressBar 
                label={t.sugar} 
                current={totals.sugar} 
                projected={projectedTotals?.sugar}
                goal={goals.sugar_g} 
                gradientClass="from-zinc-200 to-zinc-100" 
                unit="g"
                isSaltSugar
                onClick={() => handleNutrientClick('sugar_g', t.sugar)}
                lang={lang}
              />
              <ProgressBar 
                label={t.sodium} 
                current={totals.sodium / 1000} 
                projected={projectedTotals ? projectedTotals.sodium / 1000 : undefined}
                goal={goals.sodium_mg / 1000} 
                gradientClass="from-zinc-400 to-zinc-300" 
                unit="g"
                isSaltSugar
                onClick={() => handleNutrientClick('sodium_mg', t.sodium)}
                lang={lang}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };
  const old_ProgressOverviewChart = ({ totals, goals }: { totals: any, goals: UserGoals }) => {
    const data = [
      { name: t.calories, current: totals.calories, goal: goals.calories, unit: t.kcal },
      { name: t.protein, current: totals.protein, goal: goals.protein, unit: 'g' },
      { name: t.carbs, current: totals.carbs, goal: goals.carbs, unit: 'g' },
      { name: t.fat, current: totals.fat, goal: goals.fat, unit: 'g' },
      { name: t.sugar, current: totals.sugar, goal: goals.sugar_g, unit: 'g' },
      { name: t.fiber || 'Fiber', current: totals.fiber, goal: goals.fiber_g, unit: 'g' },
      { name: t.sodium, current: totals.sodium, goal: goals.sodium_mg, unit: 'mg' },
    ];

    const chartData = data.map(item => ({
      name: item.name,
      progress: Math.round((item.current / item.goal) * 100),
      currentValue: Math.round(item.current),
      goalValue: item.goal,
      unit: item.unit
    }));

    return (
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[32px] p-8 card-shadow space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{t.progressOverview}</h3>
            <p className="text-sm text-zinc-500 font-medium uppercase tracking-wider">{t.trackingGoals}</p>
          </div>
          <div className="bg-blue-500/10 p-3 rounded-2xl">
            <TrendingUp className="w-6 h-6 text-blue-500" />
          </div>
        </div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="h-[300px] w-full"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#27272a' : '#e4e4e7'} />
              <XAxis 
                dataKey="name" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: theme === 'dark' ? '#a1a1aa' : '#71717a', fontSize: 10, fontWeight: 700 }}
              />
              <YAxis 
                hide 
              />
              <Tooltip 
                cursor={{ fill: theme === 'dark' ? '#18181b' : '#f4f4f5' }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] p-4 rounded-2xl card-shadow">
                        <p className="text-sm font-black text-zinc-900 dark:text-zinc-100 uppercase tracking-widest mb-2">{data.name}</p>
                        <div className="space-y-1">
                          <p className="text-2xl font-black text-emerald-500">{data.progress}%</p>
                          <p className="text-xs text-zinc-500 font-bold">
                            {data.currentValue}{data.unit} / {data.goalValue}{data.unit}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar 
                dataKey="progress" 
                radius={[8, 8, 8, 8]}
                isAnimationActive={true}
                animationDuration={1500}
                animationEasing="ease-out"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={
                      index === 0 ? '#10b981' : // calories
                      index === 1 ? '#a855f7' : // protein (purple)
                      index === 2 ? '#f59e0b' : // carbs
                      index === 3 ? '#f43f5e' : // fat
                      index === 4 ? '#ffffff' : // sugar (white)
                      '#e2e8f0' // sodium (silver white)
                    } 
                    stroke={index === 4 ? '#e4e4e7' : 'none'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    );
  };
  const renderChatView = () => {
    const encodedTextureColor = encodeURIComponent(chatTextureColor);
    
    return (
    <motion.div
      className="flex flex-col h-full"
    >
      <div className="sticky top-[73px] z-40 bg-[var(--card-bg)] pt-4 pb-2 -mx-4 px-4 flex items-center gap-4 mb-2 flex-shrink-0 border-b border-[var(--card-border)] text-[var(--text)] dark:shadow-none">
        <button 
          onClick={handleBack}
          className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors border border-transparent hover:border-[var(--card-border)] text-[var(--text)]"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h2 className={cn(
          "text-xl font-bold uppercase tracking-normal m-0 flex",
          theme === 'light' ? "text-zinc-900 drop-shadow-[0_1px_1px_rgba(0,0,0,0.05)]" : "text-[var(--text)]"
        )}>
          {skin === 'anime' ? (lang === 'ua' ? "『ШІ НУТРІ-СЕНСЕЙ』 🌸✨" : "『AI NUTRI-SENSEI』 🌸✨") : t.chatWithAI}
        </h2>
        <div className="ml-auto relative" ref={chatMenuRef}>
          <button
            onClick={() => setShowChatMenu(!showChatMenu)}
            className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors border border-transparent hover:border-[var(--card-border)] text-[var(--text)]"
            title={lang === 'ua' ? "Опції чату" : "Chat Options"}
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          <AnimatePresence>
            {showChatMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15 }}
                className={cn(
                  "absolute right-0 top-full mt-2 w-[185px] rounded-2xl border shadow-xl z-50 p-2",
                  theme === 'light'
                    ? "bg-white border-zinc-200 text-zinc-800"
                    : "bg-zinc-950/95 border-zinc-800 text-zinc-100"
                )}
              >
                {chatMessages.length > 0 && (
                  <button
                    onClick={() => {
                      handleStartNewChat();
                      setShowChatMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left text-[11px] font-black uppercase tracking-wider hover:bg-zinc-500/10 transition-all text-current"
                  >
                    <Plus className="w-4 h-4 text-emerald-500" />
                    <span>{lang === 'ua' ? "НОВИЙ" : "NEW"}</span>
                  </button>
                )}

                <div className="border-t border-[var(--card-border)] my-1 pt-1">
                  <button
                    onClick={() => setShowThemesSubmenu(!showThemesSubmenu)}
                    className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-left text-[11px] font-black uppercase tracking-wider hover:bg-zinc-500/10 transition-all text-current"
                  >
                    <div className="flex items-center gap-3">
                      <Palette className="w-4 h-4 text-emerald-500" />
                      <span>{lang === 'ua' ? "ТЕМИ" : "THEMES"}</span>
                    </div>
                    {showThemesSubmenu ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  
                  <AnimatePresence>
                    {showThemesSubmenu && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-2 pb-1 px-1 grid grid-cols-3 gap-2">
                            {[
                              { id: 'default', label: lang === 'ua' ? "ЧИСТИЙ" : "CLEAN", bgClass: 'bg-white dark:bg-zinc-950', style: {} },
                              { id: 'simple-grid', label: lang === 'ua' ? "ПРОСТА СІТКА" : "SIMPLE GRID", bgClass: '', style: { backgroundImage: `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='12' cy='12' r='1' fill='${encodedTextureColor}' fill-opacity='0.4'/%3E%3C/svg%3E")`, backgroundSize: '24px 24px' } },
                              { id: 'dots', label: lang === 'ua' ? "НЕЙРОННІ ЗВ'ЯЗКИ" : "NEURON DOTS", bgClass: '', style: { backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='15' y1='15' x2='45' y2='25' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='0.8'/%3E%3Cline x1='45' y1='25' x2='65' y2='55' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='0.8'/%3E%3Cline x1='15' y1='15' x2='10' y2='45' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='0.6'/%3E%3Cline x1='25' y1='60' x2='10' y2='45' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='0.6'/%3E%3Cline x1='45' y1='25' x2='25' y2='60' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='0.8'/%3E%3Cline x1='45' y1='25' x2='70' y2='10' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='0.6'/%3E%3Cline x1='65' y1='55' x2='25' y2='60' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='0.8'/%3E%3Cline x1='15' y1='15' x2='0' y2='10' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.6'/%3E%3Cline x1='80' y1='10' x2='70' y2='10' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.6'/%3E%3Cline x1='65' y1='55' x2='80' y2='65' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.6'/%3E%3Cline x1='0' y1='65' x2='10' y2='45' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.6'/%3E%3Ccircle cx='15' cy='15' r='2.5' fill='${encodedTextureColor}' fill-opacity='0.7'/%3E%3Ccircle cx='45' cy='25' r='3.5' fill='${encodedTextureColor}' fill-opacity='0.85'/%3E%3Ccircle cx='45' cy='25' r='7' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.8'/%3E%3Ccircle cx='25' cy='60' r='2' fill='${encodedTextureColor}' fill-opacity='0.65'/%3E%3Ccircle cx='65' cy='55' r='4' fill='${encodedTextureColor}' fill-opacity='0.8'/%3E%3Ccircle cx='65' cy='55' r='8' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.15' stroke-width='0.8'/%3E%3Ccircle cx='10' cy='45' r='1.5' fill='${encodedTextureColor}' fill-opacity='0.55'/%3E%3Ccircle cx='70' cy='10' r='2' fill='${encodedTextureColor}' fill-opacity='0.6'/%3E%3C/svg%3E")`, backgroundSize: 'contain' } },
                            { id: 'grid', label: lang === 'ua' ? "ВИНОГРАДНИК" : "GRAPE VINEYARD", bgClass: '', style: { backgroundImage: `url("data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg transform='rotate%2845, 50, 50%29'%3E%3Cpath d='M15 45 Q30 45, 40 45' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='2'/%3E%3Cpath d='M25 45 Q20 30, 32 25 T40 35' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cpath d='M30 45 Q20 35, 35 25 Q45 35, 35 45 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3Ccircle cx='42' cy='45' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.75' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='1'/%3E%3Ccircle cx='48' cy='34' r='7.5' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.65' stroke-width='1.5'/%3E%3Ccircle cx='48' cy='56' r='7.5' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.65' stroke-width='1.5'/%3E%3Ccircle cx='56' cy='45' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.4' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='1'/%3E%3Ccircle cx='60' cy='34' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.6' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='1'/%3E%3Ccircle cx='60' cy='56' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.6' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='1'/%3E%3Ccircle cx='70' cy='45' r='7.5' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.6' stroke-width='1.5'/%3E%3Ccircle cx='72' cy='36' r='6.5' fill='${encodedTextureColor}' fill-opacity='0.5'/%3E%3Ccircle cx='72' cy='54' r='6.5' fill='${encodedTextureColor}' fill-opacity='0.5'/%3E%3Ccircle cx='82' cy='45' r='6.5' fill='${encodedTextureColor}' fill-opacity='0.8'/%3E%3Ccircle cx='92' cy='45' r='5.5' fill='${encodedTextureColor}' fill-opacity='0.9'/%3E%3C/g%3E%3C/svg%3E")`, backgroundSize: 'contain' } },
                            { id: 'citrus', label: lang === 'ua' ? "ЦИТРУС" : "CITRUS SPLASH", bgClass: '', style: { backgroundImage: `url("data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='35' cy='35' r='18' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.55' stroke-width='1.5'/%3E%3Ccircle cx='35' cy='35' r='15' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='1'/%3E%3Ccircle cx='35' cy='35' r='3' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1'/%3E%3Cline x1='35' y1='17' x2='35' y2='31' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='35' y1='39' x2='35' y2='53' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='17' y1='35' x2='31' y2='35' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='39' y1='35' x2='53' y2='35' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='22' y1='22' x2='32' y2='32' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='38' y1='38' x2='48' y2='48' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='22' y1='48' x2='32' y2='38' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='38' y1='32' x2='48' y2='22' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cpath d='M65 40 A15 15 0 0 1 80 55 L65 55 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='1.5'/%3E%3Cline x1='65' y1='55' x2='68' y2='41' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cline x1='65' y1='55' x2='73' y2='44' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cline x1='65' y1='55' x2='77' y2='49' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Ccircle cx='40' cy='75' r='10' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='1.2'/%3E%3Cline x1='30' y1='75' x2='50' y2='75' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1'/%3E%3Cline x1='40' cy='65' x2='40' y2='85' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1'/%3E%3Cline x1='33' y1='68' x2='47' y2='82' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='0.8'/%3E%3Cline x1='33' y1='82' x2='47' y2='68' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='0.8'/%3E%3C/svg%3E")`, backgroundSize: 'contain' } },
                            { id: 'leaves', label: lang === 'ua' ? "САД ТРАВ" : "HERB GARDEN", bgClass: '', style: { backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 65 C10 45, 20 15, 50 15 C80 15, 90 45, 70 65 C55 80, 45 80, 30 65 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='1.8'/%3E%3Cpath d='M30 65 L50 15' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3Cpath d='M38 52 L24 44' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cpath d='M44 40 L32 30' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cpath d='M44 40 L58 34' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cpath d='M50 28 L62 20' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cpath d='M85 105 C70 85, 80 55, 100 45 C110 65, 105 90, 85 105 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.6'/%3E%3Cpath d='M85 105 L100 45' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1.2'/%3E%3Cpath d='M15 95 C10 85, 20 80, 15 95 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3Cpath d='M15 95 C20 85, 30 90, 15 95 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3Cpath d='M15 95 C10 95, 10 105, 15 95 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3C/svg%3E")`, backgroundSize: 'contain' } },
                            { id: 'waves', label: lang === 'ua' ? "АВОКАДО" : "AVOCADO STYLE", bgClass: '', style: { backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 15 C20 15, 12 28, 16 48 C20 68, 40 68, 44 48 C48 28, 40 15, 30 15 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.55' stroke-width='1.8'/%3E%3Cpath d='M30 20 C22 20, 16 30, 20 46 C24 62, 36 62, 40 46 C44 30, 38 20, 30 20 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.28' stroke-width='1.2'/%3E%3Ccircle cx='30' cy='46' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.65'/%3E%3Cpath d='M28 42 A4 4 0 0 1 32 42' fill='none' stroke='white' stroke-opacity='0.45' stroke-width='1'/%3E%3Cpath d='M75 55 C67 55, 60 63, 63 77 C66 91, 80 91, 83 77 C86 63, 83 55, 75 55 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.5'/%3E%3Cpath d='M75 59 C69 59, 64 65, 66 75 C68 85, 78 85, 80 75 C82 65, 81 59, 75 59 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='1'/%3E%3Ccircle cx='73' cy='73' r='4' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1.2' stroke-dasharray='1,1'/%3E%3C/svg%3E")`, backgroundSize: 'contain' } }
                          ].map((bgOption) => (
                            <button
                              key={bgOption.id}
                              onClick={() => { setChatBackground(bgOption.id as any); }}
                              className={cn(
                                "flex flex-col items-center justify-center p-2 rounded-xl border transition-all aspect-square w-full relative overflow-hidden shadow-sm hover:shadow-md",
                                chatBackground === bgOption.id 
                                  ? "border-emerald-500 shadow-[0_2px_8px_rgba(16,185,129,0.2)] bg-emerald-50/10 dark:bg-emerald-950/10 scale-102" 
                                  : theme === 'light'
                                    ? "border-zinc-200 bg-zinc-50/50 hover:bg-zinc-100/80 text-zinc-700 hover:border-zinc-300"
                                    : "border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900/60 text-zinc-300 dark:hover:border-zinc-700"
                              )}
                              title={bgOption.label}
                            >
                              <div className={cn("absolute inset-0 z-0", bgOption.id === 'default' ? "opacity-100" : "opacity-90 dark:opacity-40", bgOption.bgClass)} style={bgOption.style} />
                              <span className={cn(
                                "text-[9px] font-black tracking-wider uppercase mt-auto px-1.5 py-0.5 rounded-md backdrop-blur-md z-10 shadow-sm border text-center leading-none",
                                theme === 'light'
                                  ? "bg-white/95 text-zinc-800 border-zinc-200/50"
                                  : "bg-zinc-950/85 text-zinc-200 border-zinc-800/50"
                              )}>
                                {bgOption.id === 'default' ? (lang === 'ua' ? "ЧИСТИЙ" : "CLEAN") : bgOption.label.split(' ')[0]}
                              </span>
                            </button>
                          ))}
                        </div>
                        {chatBackground !== 'default' && (
                          <div className="pt-2 mt-2 mb-2 border-t border-[var(--card-border)]">
                            <div className="flex flex-nowrap items-center justify-between px-1 mb-2.5">
                              {['#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#64748b'].map(color => (
                                <button
                                  key={color}
                                  onClick={() => {
                                    setChatTextureColor(color);
                                    setColorCycleEnabled(false);
                                  }}
                                  className={cn("w-6 h-6 rounded-full border border-zinc-200/30 dark:border-zinc-800/30 transition-transform flex-shrink-0", chatTextureColor === color ? "scale-110 shadow-md" : "scale-100 opacity-70 hover:opacity-100")}
                                  style={{ backgroundColor: color, borderColor: theme === 'light' ? 'white' : '#18181b' }}
                                  title={`Set color ${color}`}
                                />
                              ))}
                            </div>
                            
                            <div className="px-1 pt-2 border-t border-[var(--card-border)]/50">
                              <button
                                onClick={() => setColorCycleEnabled(!colorCycleEnabled)}
                                className={cn(
                                  "w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all border",
                                  colorCycleEnabled
                                    ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                                    : theme === 'light'
                                      ? "bg-zinc-50 border-zinc-200 text-zinc-500 hover:bg-zinc-100"
                                      : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800"
                                )}
                              >
                                <span>{lang === 'ua' ? "ПЛАВНА ЗМІНА (7с)" : "COLOR CYCLE (7S)"}</span>
                                <div className={cn(
                                  "w-8 h-4 rounded-full p-0.5 transition-colors duration-200",
                                  colorCycleEnabled ? "bg-emerald-500" : "bg-zinc-300 dark:bg-zinc-700"
                                )}>
                                  <div className={cn(
                                    "w-3 h-3 rounded-full bg-white transition-transform duration-200 shadow-sm",
                                    colorCycleEnabled ? "translate-x-4" : "translate-x-0"
                                  )} />
                                </div>
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {chatThreads.length > 0 && (
                  <div className="border-t border-[var(--card-border)] my-1 pt-1">
                    <button
                      onClick={() => {
                        setShowThreadList(!showThreadList);
                        setShowChatMenu(false);
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left text-[11px] font-black uppercase tracking-wider transition-all",
                        showThreadList
                          ? "bg-emerald-500/10 text-emerald-500 font-black"
                          : "hover:bg-zinc-500/10 text-current"
                      )}
                    >
                      <HistoryIcon className="w-4 h-4 text-emerald-500" />
                      <span>{lang === 'ua' ? "ІСТОРІЯ" : "HISTORY"}</span>
                    </button>
                  </div>
                )}

                {chatMessages.length > 0 && (
                  <div className="border-t border-[var(--card-border)] my-1 pt-1">
                    {showClearConfirm ? (
                      <div className="p-2 bg-red-500/10 rounded-xl space-y-2">
                        <p className="text-[10px] text-center font-black text-red-500 uppercase tracking-wider">
                          {lang === 'ua' ? "ВПЕВНЕНІ?" : "ARE YOU SURE?"}
                        </p>
                        <div className="flex gap-1">
                          <button
                            onClick={() => {
                              handleClearCurrentThread();
                              setShowChatMenu(false);
                              setShowClearConfirm(false);
                            }}
                            className="flex-1 py-1.5 text-[10px] font-black uppercase bg-red-500 text-white rounded-lg transition-all hover:bg-red-600 text-center"
                          >
                            {lang === 'ua' ? "ТАК" : "YES"}
                          </button>
                          <button
                            onClick={() => setShowClearConfirm(false)}
                            className="flex-1 py-1.5 text-[10px] font-black uppercase bg-zinc-500/20 text-zinc-400 rounded-lg transition-all hover:bg-zinc-500/30 text-center"
                          >
                            {lang === 'ua' ? "НІ" : "NO"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowClearConfirm(true)}
                        className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left text-[11px] font-black uppercase tracking-wider hover:bg-red-500/10 hover:text-red-500 transition-all text-current"
                      >
                        <Eraser className="w-4 h-4 text-red-500" />
                        <span>{lang === 'ua' ? "ОЧИСТИТИ" : "CLEAR"}</span>
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {showThreadList && chatThreads.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={cn(
            "p-4 rounded-2xl border mb-4 space-y-3 z-30 relative shadow-lg",
            theme === 'light' 
              ? "bg-white/95 border-zinc-200 text-zinc-800" 
              : "bg-zinc-950/95 border-zinc-800 text-zinc-100"
          )}
        >
          <div className="flex items-center justify-between border-b border-[var(--card-border)] pb-2">
            <h3 className="text-xs font-black uppercase tracking-widest text-emerald-500">
              {lang === 'ua' ? "ІСТОРІЯ" : "HISTORY"}
            </h3>
            <button
              onClick={() => setShowThreadList(false)}
              className="text-xs text-zinc-400 hover:text-rose-500 transition-colors"
            >
              {lang === 'ua' ? "Закрити" : "Close"}
            </button>
          </div>
          <div className="max-h-[220px] overflow-y-auto space-y-2 pr-1 custom-scrollbar">
            {chatThreads.map((thread) => {
              const isActive = thread.id === activeThreadId;
              return (
                <div
                  key={thread.id}
                  className={cn(
                    "flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer",
                    isActive 
                      ? "border-emerald-500/50 bg-emerald-500/10" 
                      : "border-transparent hover:border-zinc-500/20 hover:bg-zinc-500/5"
                  )}
                  onClick={() => {
                    setChatMessages(thread.messages);
                    setActiveThreadId(thread.id);
                    localDb.setItem('chat_active_thread_id', thread.id);
                    setShowThreadList(false);
                  }}
                >
                  <div className="min-w-0 flex-1 pr-3">
                    <p className="text-xs font-bold truncate">
                      {thread.title}
                    </p>
                    <p className="text-[10px] text-zinc-400">
                      {new Date(thread.timestamp).toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: timeFormat === '12h'
                      })}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setChatThreads(prev => {
                        const filtered = prev.filter(t => t.id !== thread.id);
                        localDb.setItem('chat_threads', JSON.stringify(filtered));
                        return filtered;
                      });
                      if (thread.id === activeThreadId) {
                        setChatMessages([]);
                        setActiveThreadId(null);
                        localDb.removeItem('chat_active_thread_id');
                        localDb.removeItem('chat_history');
                        localStorage.removeItem('chat_history');
                      }
                    }}
                    className="p-1 text-zinc-400 hover:text-rose-500 transition-colors"
                    title={lang === 'ua' ? "Видалити" : "Delete"}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      <div 
        className={cn(
          "fixed inset-y-0 left-1/2 -translate-x-1/2 w-full max-w-4xl z-0 transition-opacity duration-300 pointer-events-none overflow-hidden",
          chatBackground === 'default' ? "hidden" : "opacity-80 dark:opacity-50"
        )} 
        style={chatBackground !== 'default' ? {
          backgroundImage: 
            chatBackground === 'simple-grid' ? `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='12' cy='12' r='1' fill='${encodedTextureColor}' fill-opacity='0.4'/%3E%3C/svg%3E")` :
            chatBackground === 'dots' ? `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='15' y1='15' x2='45' y2='25' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='0.8'/%3E%3Cline x1='45' y1='25' x2='65' y2='55' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='0.8'/%3E%3Cline x1='15' y1='15' x2='10' y2='45' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='0.6'/%3E%3Cline x1='25' y1='60' x2='10' y2='45' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='0.6'/%3E%3Cline x1='45' y1='25' x2='25' y2='60' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='0.8'/%3E%3Cline x1='45' y1='25' x2='70' y2='10' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='0.6'/%3E%3Cline x1='65' y1='55' x2='25' y2='60' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='0.8'/%3E%3Cline x1='15' y1='15' x2='0' y2='10' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.6'/%3E%3Cline x1='80' y1='10' x2='70' y2='10' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.6'/%3E%3Cline x1='65' y1='55' x2='80' y2='65' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.6'/%3E%3Cline x1='0' y1='65' x2='10' y2='45' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.6'/%3E%3Ccircle cx='15' cy='15' r='2.5' fill='${encodedTextureColor}' fill-opacity='0.7'/%3E%3Ccircle cx='45' cy='25' r='3.5' fill='${encodedTextureColor}' fill-opacity='0.85'/%3E%3Ccircle cx='45' cy='25' r='7' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.2' stroke-width='0.8'/%3E%3Ccircle cx='25' cy='60' r='2' fill='${encodedTextureColor}' fill-opacity='0.65'/%3E%3Ccircle cx='65' cy='55' r='4' fill='${encodedTextureColor}' fill-opacity='0.8'/%3E%3Ccircle cx='65' cy='55' r='8' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.15' stroke-width='0.8'/%3E%3Ccircle cx='10' cy='45' r='1.5' fill='${encodedTextureColor}' fill-opacity='0.55'/%3E%3Ccircle cx='70' cy='10' r='2' fill='${encodedTextureColor}' fill-opacity='0.6'/%3E%3C/svg%3E")` :
            chatBackground === 'grid' ? `url("data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg transform='rotate%2845, 50, 50%29'%3E%3Cpath d='M15 45 Q30 45, 40 45' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='2'/%3E%3Cpath d='M25 45 Q20 30, 32 25 T40 35' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cpath d='M30 45 Q20 35, 35 25 Q45 35, 35 45 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3Ccircle cx='42' cy='45' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.75' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='1'/%3E%3Ccircle cx='48' cy='34' r='7.5' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.65' stroke-width='1.5'/%3E%3Ccircle cx='48' cy='56' r='7.5' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.65' stroke-width='1.5'/%3E%3Ccircle cx='56' cy='45' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.4' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='1'/%3E%3Ccircle cx='60' cy='34' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.6' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='1'/%3E%3Ccircle cx='60' cy='56' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.6' stroke='${encodedTextureColor}' stroke-opacity='0.3' stroke-width='1'/%3E%3Ccircle cx='70' cy='45' r='7.5' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.6' stroke-width='1.5'/%3E%3Ccircle cx='72' cy='36' r='6.5' fill='${encodedTextureColor}' fill-opacity='0.5'/%3E%3Ccircle cx='72' cy='54' r='6.5' fill='${encodedTextureColor}' fill-opacity='0.5'/%3E%3Ccircle cx='82' cy='45' r='6.5' fill='${encodedTextureColor}' fill-opacity='0.8'/%3E%3Ccircle cx='92' cy='45' r='5.5' fill='${encodedTextureColor}' fill-opacity='0.9'/%3E%3C/g%3E%3C/svg%3E")` :
            chatBackground === 'citrus' ? `url("data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='35' cy='35' r='18' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.55' stroke-width='1.5'/%3E%3Ccircle cx='35' cy='35' r='15' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='1'/%3E%3Ccircle cx='35' cy='35' r='3' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1'/%3E%3Cline x1='35' y1='17' x2='35' y2='31' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='35' y1='39' x2='35' y2='53' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='17' y1='35' x2='31' y2='35' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='39' y1='35' x2='53' y2='35' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='22' y1='22' x2='32' y2='32' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='38' y1='38' x2='48' y2='48' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='22' y1='48' x2='32' y2='38' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cline x1='38' y1='32' x2='48' y2='22' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.2'/%3E%3Cpath d='M65 40 A15 15 0 0 1 80 55 L65 55 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='1.5'/%3E%3Cline x1='65' y1='55' x2='68' y2='41' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cline x1='65' y1='55' x2='73' y2='44' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cline x1='65' y1='55' x2='77' y2='49' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Ccircle cx='40' cy='75' r='10' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='1.2'/%3E%3Cline x1='30' y1='75' x2='50' y2='75' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1'/%3E%3Cline x1='40' cy='65' x2='40' y2='85' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1'/%3E%3Cline x1='33' y1='68' x2='47' y2='82' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='0.8'/%3E%3Cline x1='33' y1='82' x2='47' y2='68' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='0.8'/%3E%3C/svg%3E")` :
            chatBackground === 'leaves' ? `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 65 C10 45, 20 15, 50 15 C80 15, 90 45, 70 65 C55 80, 45 80, 30 65 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.5' stroke-width='1.8'/%3E%3Cpath d='M30 65 L50 15' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3Cpath d='M38 52 L24 44' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cpath d='M44 40 L32 30' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cpath d='M44 40 L58 34' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cpath d='M50 28 L62 20' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1'/%3E%3Cpath d='M85 105 C70 85, 80 55, 100 45 C110 65, 105 90, 85 105 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.6'/%3E%3Cpath d='M85 105 L100 45' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1.2'/%3E%3Cpath d='M15 95 C10 85, 20 80, 15 95 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3Cpath d='M15 95 C20 85, 30 90, 15 95 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3Cpath d='M15 95 C10 95, 10 105, 15 95 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.4' stroke-width='1.2'/%3E%3C/svg%3E")` :
            chatBackground === 'waves' ? `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 15 C20 15, 12 28, 16 48 C20 68, 40 68, 44 48 C48 28, 40 15, 30 15 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.55' stroke-width='1.8'/%3E%3Cpath d='M30 20 C22 20, 16 30, 20 46 C24 62, 36 62, 40 46 C44 30, 38 20, 30 20 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.28' stroke-width='1.2'/%3E%3Ccircle cx='30' cy='46' r='7.5' fill='${encodedTextureColor}' fill-opacity='0.65'/%3E%3Cpath d='M28 42 A4 4 0 0 1 32 42' fill='none' stroke='white' stroke-opacity='0.45' stroke-width='1'/%3E%3Cpath d='M75 55 C67 55, 60 63, 63 77 C66 91, 80 91, 83 77 C86 63, 83 55, 75 55 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.45' stroke-width='1.5'/%3E%3Cpath d='M75 59 C69 59, 64 65, 66 75 C68 85, 78 85, 80 75 C82 65, 81 59, 75 59 Z' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.25' stroke-width='1'/%3E%3Ccircle cx='73' cy='73' r='4' fill='none' stroke='${encodedTextureColor}' stroke-opacity='0.35' stroke-width='1.2' stroke-dasharray='1,1'/%3E%3C/svg%3E")` : undefined,
          backgroundSize: chatBackground === 'simple-grid' ? '24px 24px' : undefined,
          backgroundRepeat: 'repeat'
        } : undefined} 
      />

      <div className="space-y-5 pb-[210px] pr-2 relative z-10">
        {chatMessages.map((msg, idx) => {
          const prevMsg = chatMessages[idx - 1];
          const showDateSeparator = !prevMsg || new Date(msg.timestamp).toDateString() !== new Date(prevMsg.timestamp).toDateString();
          
          return (
            <React.Fragment key={idx}>
              {showDateSeparator && (
                <div className="flex justify-center my-3">
                  <div className={cn(
                    "px-3 py-1 rounded-full text-[10px] uppercase tracking-widest text-zinc-500 font-bold border shadow-sm",
                    theme === 'light' ? "bg-white border-zinc-200 shadow-zinc-900/5" : "bg-zinc-800/50 border-zinc-700/50"
                  )}>
                    {new Date(msg.timestamp).toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  </div>
                </div>
              )}
              
              <motion.div
                id={`chat-message-${idx}`}
                initial={{ opacity: 0, x: msg.role === 'user' ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={cn(
                  "flex flex-col max-w-[85%]",
                  msg.role === 'user' ? "ml-auto items-end" : "mr-auto items-start"
                )}
              >
                <div 
                  className={cn(
                    "p-4 rounded-2xl shadow-md shadow-black/20 relative border",
                    msg.role === 'user' 
                      ? (skin === 'anime' ? "bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-400/50 text-white rounded-tr-none shadow-[0_4px_16px_rgba(236,72,153,0.25)] backdrop-blur-md" : "border-[var(--card-border)] text-[var(--text-main)] rounded-tr-none")
                      : (skin === 'anime' ? "bg-gradient-to-r from-purple-950/50 via-indigo-950/40 to-pink-950/50 border border-purple-400/40 text-pink-100 rounded-tl-none shadow-[0_4px_20px_rgba(168,85,247,0.25)] backdrop-blur-md" : (theme === 'light'
                        ? "bg-emerald-500/75 text-white border-emerald-400/20 rounded-tl-none"
                        : "bg-emerald-950/75 text-zinc-300 border-emerald-900/30 rounded-tl-none")),
                    illumination && (
                      getMessageIlluminationType(msg.content, msg.image) === 'water' ? "illumination-water" : "illumination-food"
                    )
                  )}
                  style={msg.role === 'user' ? (skin === 'anime' ? undefined : { backgroundColor: theme === 'light' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(24, 24, 27, 0.85)' }) : undefined}
                >
                {skin === 'anime' && msg.role === 'ai' && (
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 border border-white/60 shadow-[0_0_12px_rgba(236,72,153,0.8)] flex items-center justify-center z-10 text-white">
                    <Sparkles className="w-4 h-4 text-amber-200 fill-current animate-pulse" />
                  </div>
                )}
              {msg.image && (
                <div className="mb-3 rounded-xl overflow-hidden border border-white/10">
                  <img 
                    src={msg.image} 
                    alt="Meal" 
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto max-h-[200px] object-cover"
                    referrerPolicy="no-referrer"
                    onLoad={() => {
                      if (view === 'chat') scrollToBottom(true);
                    }}
                  />
                </div>
              )}
              {msg.role === 'ai' && msg.animating ? (
                <TypewriterText 
                  text={msg.content} 
                  onComplete={() => {
                    const newMessages = [...chatMessages];
                    newMessages[idx] = { ...newMessages[idx], animating: false };
                    setChatMessages(newMessages);
                    
                    // Scroll the page so that the start of this finished message is neatly visible below the sticky header
                    setTimeout(() => {
                      const element = document.getElementById(`chat-message-${idx}`);
                      if (element && typeof element.getBoundingClientRect === 'function') {
                        const yOffset = -140; // Safe offset margin for the sticky top navigation header
                        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
                        window.scrollTo({
                          top: Math.max(0, y),
                          behavior: 'smooth'
                        });
                      }
                    }, 100);
                  }}
                />
              ) : (
                <div className="markdown-body">
                  <ReactMarkdown components={chatMarkdownComponents}>{msg.content}</ReactMarkdown>
                </div>
              )}
              
              {/* Like Buttons (Overlapping Badge) */}
              <div className={cn(
                "absolute -bottom-3 z-10 flex items-center gap-1",
                msg.role === 'user' ? "left-4" : "right-4"
              )}>
                {msg.role === 'ai' ? (
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={async () => {
                        sounds.playPop();
                        if (navigator.share && navigator.canShare && navigator.canShare({ text: msg.content })) {
                          try {
                            await navigator.share({
                              title: 'MAIN Nutrition AI',
                              text: msg.content
                            });
                            toast.success(lang === 'ua' ? 'Повідомлення поширено!' : 'Response shared!');
                            return;
                          } catch (err) {
                            // User cancelled share or fallback to clipboard
                          }
                        }
                        try {
                          await navigator.clipboard.writeText(msg.content);
                          toast.success(lang === 'ua' ? 'Відповідь скопійовано!' : 'Response copied to clipboard!');
                        } catch (err) {
                          toast.error(lang === 'ua' ? 'Помилка копіювання' : 'Failed to copy text');
                        }
                      }}
                      className={cn(
                        "p-1.5 rounded-full border shadow-sm transition-all active:scale-125 cursor-pointer",
                        theme === 'light' 
                          ? "bg-white border-zinc-200 shadow-zinc-900/5 text-zinc-400 hover:text-emerald-500" 
                          : "bg-zinc-800 border-[var(--card-border)] text-zinc-400 hover:text-emerald-500"
                      )}
                      title={lang === 'ua' ? 'Поділитися відповіддю' : 'Share response'}
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                    <button 
                      onClick={() => toggleMessageLike(idx)}
                      className={cn(
                        "p-1.5 rounded-full border shadow-sm transition-all active:scale-125 cursor-pointer",
                        theme === 'light' 
                          ? "bg-white border-zinc-200 shadow-zinc-900/5 text-zinc-400 hover:text-emerald-500" 
                          : "bg-zinc-800 border-[var(--card-border)] text-zinc-400 hover:text-emerald-500",
                        msg.isLiked && "border-emerald-500/50 text-emerald-500 scale-110"
                      )}
                      title={lang === 'ua' ? 'Вподобати відповідь' : 'Like response'}
                    >
                      <Heart className={cn("w-3.5 h-3.5", msg.isLiked && "fill-emerald-500")} />
                    </button>
                  </div>
                ) : (
                  msg.aiLiked && (
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", bounce: 0.5 }}
                      className={cn(
                        "p-1 rounded-full border shadow-sm text-emerald-500",
                        theme === 'light' 
                          ? "bg-white border-zinc-200 shadow-zinc-900/5" 
                          : "bg-emerald-900 border-emerald-800/50"
                      )}
                    >
                      <Heart className="w-3.5 h-3.5 fill-emerald-500" />
                    </motion.div>
                  )
                )}
                
                {/* Mutual Like Badge */}
                {msg.role === 'ai' && msg.isLiked && idx > 0 && chatMessages[idx-1].role === 'user' && chatMessages[idx-1].aiLiked && (
                  <motion.div 
                    initial={{ scale: 0, y: 10 }}
                    animate={{ scale: 1, y: 0 }}
                    className="flex items-center gap-1 text-[8px] font-black uppercase tracking-tighter bg-emerald-500 text-white px-2 py-0.5 rounded-full shadow-lg shadow-emerald-500/20 animate-bounce cursor-default border border-[var(--card-bg)]"
                  >
                    <span>Mutual Like! 🔥</span>
                  </motion.div>
                )}

              </div>
              
              {(() => {
                if (!msg.mealAnalysis) return null;

                // Find associated user message to see if it mentions food or if there is an image
                let associatedUserMsgText = "";
                let hasUserImage = false;
                let associatedUserImage = "";
                for (let i = idx - 1; i >= 0; i--) {
                  if (chatMessages[i].role === 'user') {
                    associatedUserMsgText = chatMessages[i].content || "";
                    hasUserImage = !!chatMessages[i].image;
                    if (chatMessages[i].image) {
                      associatedUserImage = chatMessages[i].image!;
                    }
                    break;
                  }
                }

                const showAnalysis = hasUserImage || checkIfMessageMentionsFood(associatedUserMsgText) || checkIfUserMentionedEating(associatedUserMsgText);
                if (!showAnalysis) return null;

                const mentionedEating = checkIfUserMentionedEating(associatedUserMsgText);
                const isQuestion = /^(що|як|скільки|чому|чи|чи можна|навіщо|where|what|how|why|is it|can i|\?)/i.test(associatedUserMsgText.trim()) || associatedUserMsgText.includes('?');
                const isQuestionWithoutEating = !hasUserImage && !mentionedEating && isQuestion;

                // Only suppress the Review & Log section if it is a general informational question without eating intention
                if (isQuestionWithoutEating) return null;

                // Return a consolidated details dropdown spoiler containing the photo, safe choice alert, nutrient values, and log/decline buttons
                return (
                  <div className="mt-4 pt-4 border-t border-emerald-500/20 w-full">
                    <details className="group border border-emerald-500/20 rounded-2xl bg-black/10 dark:bg-black/30 overflow-hidden transition-all duration-300">
                      <summary className="flex items-center justify-between p-3 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-500">
                          <Utensils className="w-4 h-4 text-emerald-400" />
                          <span>
                            {lang !== 'ua' ? 'Food analysis & Log' : 'Аналіз їжі та запис'}
                          </span>
                          <span className="opacity-60 text-[10px] font-normal normal-case ml-1 tracking-normal">
                            ({msg.mealAnalysis.meal_name})
                          </span>
                        </div>
                        <ChevronDown className="w-4 h-4 text-emerald-500 transition-transform duration-300 group-open:rotate-180" />
                      </summary>
                      
                      <div className="p-3 pt-0 border-t border-emerald-500/10 space-y-3 bg-black/5">
                        {/* Analyzed Meal Photo Preview */}
                        {associatedUserImage && (
                          <div className="mt-2 rounded-xl overflow-hidden border border-white/10 shadow-sm max-h-[180px] relative">
                            <img 
                              src={associatedUserImage} 
                              alt={msg.mealAnalysis.meal_name || "Analyzed Meal"} 
                              className="w-full h-36 object-cover"
                            />
                            <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-md px-2 py-0.5 rounded-md text-[9px] font-semibold text-white/90">
                              {msg.mealAnalysis.meal_name}
                            </div>
                          </div>
                        )}

                        {/* Safe Choice Check / Alert */}
                        {((msg.mealAnalysis.safe_choice_status) || (isPremiumOrPro && safeChoiceProfile?.active !== false)) && safeChoiceProfile?.active !== false && (
                          <div 
                            onClick={() => setSafeChoiceDetailModalData(msg.mealAnalysis)}
                            className={cn(
                              "mt-3 p-3 rounded-2xl border flex items-start gap-3 cursor-pointer group hover:scale-[1.01] hover:shadow-lg transition-all",
                              msg.mealAnalysis.safe_choice_status === 'red' ? "bg-rose-500/20 border-rose-500 hover:bg-rose-500/30 text-rose-100" :
                              msg.mealAnalysis.safe_choice_status === 'amber' ? "bg-amber-500/20 border-amber-500 hover:bg-amber-500/30 text-amber-100" :
                              "bg-emerald-500/20 border-emerald-500 hover:bg-emerald-500/30 text-emerald-100"
                            )}
                          >
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <h4 className="font-bold text-xs uppercase tracking-widest">
                                  {msg.mealAnalysis.safe_choice_status === 'red' ? (t.safeChoiceAlert || 'Safe Choice Alert') : (t.safeChoiceCheck || 'Safe Choice Check')}
                                </h4>
                                <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/15 text-white border border-white/20 flex items-center gap-1 group-hover:bg-white group-hover:text-zinc-900 transition-all shrink-0 whitespace-nowrap">
                                  {lang === 'ua' ? 'Детальніше' : 'Tap for details'} <ChevronRight className="w-3 h-3" />
                                </span>
                              </div>
                              <p className="text-[10px] leading-snug opacity-90">
                                {getSafeChoiceMessage((msg.mealAnalysis.safe_choice_status || 'green') as any, msg.mealAnalysis.safe_choice_message)}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Nutrient breakdown */}
                        <div className="grid grid-cols-4 gap-1.5 mt-2">
                          <div className="bg-white/10 dark:bg-white/5 p-2 rounded-xl text-center">
                            <p className="text-[9px] text-zinc-400 uppercase font-black tracking-tighter">
                              {lang !== 'ua' ? 'Calories' : 'Калорії'}
                            </p>
                            <p className="text-xs font-bold text-white">{Math.round(msg.mealAnalysis.total_nutrition?.calories_kcal || 0)}</p>
                          </div>
                          <div className="bg-white/10 dark:bg-white/5 p-2 rounded-xl text-center">
                            <p className="text-[9px] text-zinc-400 uppercase font-black tracking-tighter">
                              {lang !== 'ua' ? 'Protein' : 'Білок'}
                            </p>
                            <p className="text-xs font-bold text-white">{Number((msg.mealAnalysis.total_nutrition?.protein_g || 0).toFixed(1))}g</p>
                          </div>
                          <div className="bg-white/10 dark:bg-white/5 p-2 rounded-xl text-center">
                            <p className="text-[9px] text-zinc-400 uppercase font-black tracking-tighter">
                              {lang !== 'ua' ? 'Carbs' : 'Вуглеводи'}
                            </p>
                            <p className="text-xs font-bold text-white">{Number((msg.mealAnalysis.total_nutrition?.carbs_g || 0).toFixed(1))}g</p>
                          </div>
                          <div className="bg-white/10 dark:bg-white/5 p-2 rounded-xl text-center">
                            <p className="text-[9px] text-zinc-400 uppercase font-black tracking-tighter">
                              {lang !== 'ua' ? 'Fat' : 'Жири'}
                            </p>
                            <p className="text-xs font-bold text-white">{Number((msg.mealAnalysis.total_nutrition?.fat_g || 0).toFixed(1))}g</p>
                          </div>
                        </div>

                        {/* Additional Micronutrients Badges */}
                        {((msg.mealAnalysis.total_nutrition?.fiber_g || 0) > 0 || (msg.mealAnalysis.total_nutrition?.sugar_g || 0) > 0 || (msg.mealAnalysis.total_nutrition?.sodium_mg || 0) > 0 || (msg.mealAnalysis.total_nutrition?.water_ml || 0) > 0 || (msg.mealAnalysis.total_nutrition?.caffeine_mg || 0) > 0) && (
                          <div className="flex flex-wrap gap-1.5 text-[10px] text-zinc-300 mt-1">
                            {(msg.mealAnalysis.total_nutrition?.fiber_g || 0) > 0 && (
                              <span className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10">
                                {lang === 'ua' ? 'Клітковина' : 'Fiber'}: <strong className="text-white">{msg.mealAnalysis.total_nutrition?.fiber_g}g</strong>
                              </span>
                            )}
                            {(msg.mealAnalysis.total_nutrition?.sugar_g || 0) > 0 && (
                              <span className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10">
                                {lang === 'ua' ? 'Цукор' : 'Sugar'}: <strong className="text-white">{msg.mealAnalysis.total_nutrition?.sugar_g}g</strong>
                              </span>
                            )}
                            {(msg.mealAnalysis.total_nutrition?.sodium_mg || 0) > 0 && (
                              <span className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10">
                                {lang === 'ua' ? 'Натрій' : 'Sodium'}: <strong className="text-white">{msg.mealAnalysis.total_nutrition?.sodium_mg}mg</strong>
                              </span>
                            )}
                            {(msg.mealAnalysis.total_nutrition?.water_ml || 0) > 0 && (
                              <span className="px-2 py-0.5 rounded-md bg-cyan-500/10 border border-cyan-500/20 text-cyan-300">
                                {lang === 'ua' ? 'Рідина' : 'Water'}: <strong>{msg.mealAnalysis.total_nutrition?.water_ml}ml</strong>
                              </span>
                            )}
                            {(msg.mealAnalysis.total_nutrition?.caffeine_mg || 0) > 0 && (
                              <span className="px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-300">
                                {lang === 'ua' ? 'Кофеїн' : 'Caffeine'}: <strong>{msg.mealAnalysis.total_nutrition?.caffeine_mg}mg</strong>
                              </span>
                            )}
                          </div>
                        )}

                        {/* Itemized Detected Ingredients / Foods */}
                        {msg.mealAnalysis.foods && msg.mealAnalysis.foods.length > 0 && (
                          <div className="space-y-1.5 mt-2">
                            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                              {lang !== 'ua' ? 'Detected Ingredients / Items' : 'Розпізнані компоненти та порції'}
                            </p>
                            <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                              {msg.mealAnalysis.foods.map((food, fIdx) => (
                                <div key={fIdx} className="flex items-center justify-between p-2 rounded-xl bg-white/5 border border-white/5 text-[11px]">
                                  <div className="flex-1 min-w-0 pr-2">
                                    <p className="font-medium text-white truncate">{food.name}</p>
                                    <p className="text-[9px] text-zinc-400">{food.amount} {food.unit || 'g'} {food.portion ? `(${food.portion})` : ''}</p>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <span className="font-bold text-emerald-400">{Math.round(food.calories_kcal || 0)} kcal</span>
                                    <p className="text-[9px] text-zinc-400">P:{food.protein_g || 0}g · C:{food.carbs_g || 0}g · F:{food.fat_g || 0}g</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Log / Decline buttons */}
                        <div className="mt-2">
                          {msg.logged ? (
                            <button
                              disabled
                              className="w-full py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest bg-emerald-600/50 text-white/50 cursor-default flex items-center justify-center gap-2 shadow-lg shadow-black/10"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {lang !== 'ua' ? 'Reviewed & logged' : 'Переглянуто та записано'}
                            </button>
                          ) : msg.declined ? (
                            <div className="w-full py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest bg-zinc-600/30 text-zinc-400/80 cursor-default flex items-center justify-center gap-2 border border-zinc-700/30">
                              <XCircle className="w-3.5 h-3.5 animate-pulse" />
                              {msg.declinedText || (lang !== 'ua' ? 'Declined' : 'Відхилено')}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    if (msg.logged) return;
                                    setSourceChatMessageIndex(idx);
                                    const finalAssociatedImg = associatedUserImage || (idx > 0 && chatMessages[idx - 1].role === 'user' && chatMessages[idx - 1].image ? chatMessages[idx - 1].image! : '');
                                    if (enableMealReview) {
                                      originalPendingReviewFoodsRef.current = safeClone(msg.mealAnalysis!.foods);
                                      setPendingReviewData({
                                        ...msg.mealAnalysis,
                                        image: finalAssociatedImg
                                      } as any);
                                      setShowReviewModal(true);
                                    } else {
                                      setResult(msg.mealAnalysis!);
                                      setOriginalResult(safeClone(msg.mealAnalysis!));
                                      setImage(finalAssociatedImg || null);
                                      setView('analyze');
                                    }
                                  }}
                                  className="flex-1 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 shadow-lg shadow-black/10 active:scale-95 bg-white text-emerald-600 hover:bg-emerald-50"
                                >
                                  <Edit className="w-3 h-3 text-emerald-500" />
                                  {lang !== 'ua' ? 'Review' : 'Переглянути'}
                                </button>
                                
                                <button
                                  onClick={() => {
                                    if (msg.logged) return;
                                    const finalAssociatedImg = associatedUserImage || (idx > 0 && chatMessages[idx - 1].role === 'user' && chatMessages[idx - 1].image ? chatMessages[idx - 1].image! : '');
                                    const updatedMsgs = [...chatMessages];
                                    updatedMsgs[idx] = { ...updatedMsgs[idx], logged: true };
                                    setChatMessages(updatedMsgs);
                                    localStorage.setItem('chat_history', JSON.stringify(safeClone(updatedMsgs)));

                                    setSourceChatMessageIndex(idx);
                                    saveToHistory(msg.mealAnalysis!, finalAssociatedImg, undefined, true);
                                  }}
                                  className="flex-1 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 shadow-lg shadow-black/10 active:scale-95 bg-emerald-500 text-white hover:bg-emerald-600"
                                >
                                  <Check className="w-3 h-3 text-white" />
                                  {lang !== 'ua' ? 'Log' : 'Записати'}
                                </button>
                              </div>
                              
                              <button
                                onClick={() => {
                                  const text = lang === 'ua' 
                                    ? (declineStyle === 'casual' ? 'Та ну' : 'Відхилено')
                                    : (declineStyle === 'casual' ? 'Nah' : 'Declined');
                                  
                                  const newMessages = [...chatMessages];
                                  newMessages[idx] = { 
                                    ...newMessages[idx], 
                                    declined: true, 
                                    declinedText: text 
                                  };
                                  setChatMessages(newMessages);
                                  localStorage.setItem('chat_history', JSON.stringify(safeClone(newMessages)));
                                  sounds.playSave();
                                }}
                                className={cn(
                                  "w-full py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all border active:scale-95 flex items-center justify-center",
                                  theme === 'light'
                                    ? "border-white/40 hover:bg-white/10 text-white"
                                    : "border-zinc-700 hover:bg-white/5 text-zinc-300"
                                )}
                              >
                                {lang === 'ua' 
                                  ? (declineStyle === 'casual' ? 'Та ну' : 'Відхилити')
                                  : (declineStyle === 'casual' ? 'Nah' : 'Decline')
                                }
                              </button>
                            </div>
                          )}

                          {/* Order Ingredients via Nutri-Agent */}
                          {msg.mealAnalysis.foods && msg.mealAnalysis.foods.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                const isPremium = subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';
                                if (!isPremium) {
                                  toast.info(
                                    lang === 'ua'
                                      ? 'Замовлення інгредієнтів через Nutri-Agent доступне ексклюзивно у тарифі Premium!'
                                      : 'Ordering meal ingredients with Nutri-Agent is exclusively available on the Premium plan!',
                                    { icon: '👑' }
                                  );
                                  setIsBillingModalOpen(true);
                                  return;
                                }
                                const orderItems = msg.mealAnalysis!.foods.map(f => ({
                                  name: f.name,
                                  amount: String(f.amount || '1'),
                                  unit: f.unit || (lang === 'ua' ? 'г' : 'g')
                                }));
                                window.dispatchEvent(new CustomEvent('open-nutriagent', {
                                  detail: {
                                    store: lang === 'ua' ? 'silpo' : 'instacart',
                                    items: orderItems,
                                    currency: lang === 'ua' ? 'UAH' : 'USD',
                                    userNotes: `${lang === 'ua' ? 'Інгредієнти для страви' : 'Ingredients for'}: ${msg.mealAnalysis!.meal_name}`,
                                    directLaunch: true
                                  }
                                }));
                              }}
                              className={cn(
                                "w-full mt-2 py-2.5 px-3 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-2 border transition-all cursor-pointer active:scale-95 shadow-sm",
                                subscriptionTier === 'premium' || subscriptionTier === 'premium_pro'
                                  ? "bg-emerald-500/20 hover:bg-emerald-500/30 border-emerald-500/40 text-emerald-300 hover:text-emerald-100"
                                  : "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/30 text-amber-300"
                              )}
                            >
                              <ShoppingCart className="w-3.5 h-3.5 text-emerald-400" />
                              <span>
                                {lang === 'ua' ? "Замовити інгредієнти (Nutri-Agent)" : "Order Ingredients (Nutri-Agent)"}
                              </span>
                              {!(subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') && (
                                <span className="text-[8px] bg-amber-500/30 px-1.5 py-0.5 rounded text-amber-200 uppercase font-black tracking-widest">PREMIUM</span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </details>
                  </div>
                );
              })()}

              {/* Interactive Nutri-Agent Food Order Card */}
              {msg.nutriAgentOrder && msg.nutriAgentOrder.items && msg.nutriAgentOrder.items.length > 0 && (() => {
                const order = msg.nutriAgentOrder;
                const isPremium = subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';
                const isUa = lang === 'ua';
                const currencySymbol = order.currency === 'UAH' ? '₴' : (order.currency === 'USD' ? '$' : (isUa ? '₴' : '$'));
                const storeDisplayNames: Record<string, string> = {
                  silpo: isUa ? 'Сільпо (Silpo MCP)' : 'Silpo (Silpo MCP)',
                  atb: 'АТБ Market',
                  fozzy: 'Fozzy',
                  instacart: 'Instacart',
                  amazon_fresh: 'Amazon Fresh',
                  walmart: 'Walmart Grocery'
                };
                const storeLabel = storeDisplayNames[(order.store || '').toLowerCase()] || (order.store ? order.store.toUpperCase() : (isUa ? 'Сільпо' : 'Silpo'));
                const totalSum = order.items.reduce((sum, it) => sum + (it.estimatedPrice || 0), 0);

                const handleLaunchOrder = (directLaunch: boolean) => {
                  if (!isPremium) {
                    toast.info(
                      isUa
                        ? 'Автономне замовлення їжі через Nutri-Agent доступне ексклюзивно у тарифі Premium!'
                        : 'Autonomous food ordering via Nutri-Agent is exclusively available on the Premium tier!',
                      { icon: '👑' }
                    );
                    setIsBillingModalOpen(true);
                    return;
                  }

                  if (localStorage.getItem('nutri_agent_enabled') === 'false') {
                    toast.info(
                      isUa
                        ? 'Nutri-Agent вимкнено в налаштуваннях. Увімкніть його в налаштуваннях.'
                        : 'Nutri-Agent is disabled in settings. Enable it in settings.',
                      { icon: '🤖' }
                    );
                    return;
                  }

                  const launchConfig: NutriAgentLaunchConfig = {
                    budgetLimitUSD: order.budget || Math.ceil(totalSum * 1.15),
                    currency: order.currency || (isUa ? 'UAH' : 'USD'),
                    currencySymbol,
                    targetStores: [order.store || (isUa ? 'silpo' : 'instacart')],
                    messengerPlatform: ['app_push'],
                    notificationChannels: ['app_push'],
                    userNotes: order.userNotes || (isUa ? 'Замовлення сформовано у ШІ чаті' : 'Order assembled in AI Chat'),
                    groceryListMode: 'only_grocery',
                    groceryItems: order.items.map(it => ({
                      name: it.name,
                      amount: it.amount || '1',
                      unit: it.unit || (isUa ? 'шт' : 'item'),
                      checked: false
                    })),
                    fulfillmentMethod: order.fulfillmentMethod || 'delivery'
                  };

                  setNutriAgentConfig(launchConfig);
                  if (directLaunch) {
                    setIsNutriAgentActive(true);
                    toast.success(
                      isUa 
                        ? `Nutri-Agent розпочав сканування кошика у ${storeLabel}!`
                        : `Nutri-Agent started scanning cart in ${storeLabel}!`,
                      { icon: '🛒' }
                    );
                  } else {
                    setIsNutriAgentConfigOpen(true);
                  }
                };

                return (
                  <div className="mt-4 pt-4 border-t border-emerald-500/20 w-full">
                    <div className={cn(
                      "rounded-2xl border p-4 shadow-lg transition-all",
                      theme === 'light'
                        ? "bg-white/95 border-emerald-500/30 shadow-emerald-500/5 text-zinc-900"
                        : "bg-zinc-900/90 border-emerald-500/30 shadow-black/40 text-zinc-100"
                    )}>
                      {/* Card Header */}
                      <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-[var(--card-border)]">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                            <Bot className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-black uppercase tracking-wider text-emerald-500">
                                Nutri-Agent
                              </span>
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                {storeLabel}
                              </span>
                            </div>
                            <p className="text-[10px] text-zinc-500 dark:text-zinc-400 font-medium">
                              {isUa ? 'Розумний кошик продуктів' : 'Smart Grocery Cart'}
                            </p>
                          </div>
                        </div>

                        <div className="shrink-0">
                          {isPremium ? (
                            <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500/20 to-emerald-500/20 border border-emerald-500/40 text-emerald-400">
                              <Sparkles className="w-3 h-3 text-amber-300" />
                              PREMIUM
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400">
                              <Lock className="w-3 h-3" />
                              PREMIUM ONLY
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Itemized List */}
                      <div className="space-y-1.5 mb-3 max-h-48 overflow-y-auto pr-1">
                        {order.items.map((item, itIdx) => (
                          <div 
                            key={itIdx}
                            className={cn(
                              "flex items-center justify-between p-2 rounded-xl border text-xs transition-colors",
                              theme === 'light'
                                ? "bg-zinc-50 border-zinc-200/80"
                                : "bg-zinc-800/50 border-zinc-700/50"
                            )}
                          >
                            <div className="flex items-center gap-2 min-w-0 pr-2">
                              <span className="text-base shrink-0">{item.icon || '🛒'}</span>
                              <div className="truncate">
                                <span className="font-semibold text-[11px] block truncate">{item.name}</span>
                                <span className="text-[9px] text-zinc-400">
                                  {item.amount ? `${item.amount} ` : ''}{item.unit || ''}
                                </span>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <span className="font-bold text-emerald-500 text-xs">
                                {currencySymbol}{item.estimatedPrice?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Financial & Delivery Summary */}
                      <div className="flex items-center justify-between text-xs py-2 px-3 rounded-xl bg-black/5 dark:bg-white/5 mb-3">
                        <div className="flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400 text-[11px]">
                          <Truck className="w-3.5 h-3.5 text-emerald-500" />
                          <span>
                            {order.fulfillmentMethod === 'pickup' 
                              ? (isUa ? 'Самовивіз' : 'Pickup')
                              : (isUa ? 'Кур’єрська доставка' : 'Courier Delivery')}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] text-zinc-400 mr-1.5 uppercase font-bold tracking-tight">
                            {isUa ? 'Разом:' : 'Est. Total:'}
                          </span>
                          <strong className="text-sm font-black text-[var(--text)]">
                            {currencySymbol}{totalSum.toFixed(2)}
                          </strong>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {isPremium ? (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleLaunchOrder(true)}
                            className="flex-1 py-3 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 transition-all cursor-pointer"
                          >
                            <ShoppingCart className="w-4 h-4" />
                            <span>{isUa ? 'Замовити через Nutri-Agent' : 'Order with Nutri-Agent'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLaunchOrder(false)}
                            className={cn(
                              "py-3 px-3.5 rounded-xl border text-xs font-bold uppercase tracking-wider flex items-center justify-center transition-all cursor-pointer active:scale-95",
                              theme === 'light'
                                ? "border-zinc-300 hover:bg-zinc-100 text-zinc-700"
                                : "border-zinc-700 hover:bg-zinc-800 text-zinc-300"
                            )}
                            title={isUa ? 'Налаштувати замовлення' : 'Configure Order'}
                          >
                            <Sliders className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <button
                            type="button"
                            onClick={() => {
                              setIsBillingModalOpen(true);
                            }}
                            className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 via-emerald-500 to-teal-500 hover:opacity-95 active:scale-95 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 transition-all cursor-pointer"
                          >
                            <Crown className="w-4 h-4 text-amber-200" />
                            <span>{isUa ? 'Розблокувати Premium для замовлення' : 'Unlock Premium to Order'}</span>
                          </button>
                          <p className="text-[10px] text-center text-zinc-500 dark:text-zinc-400">
                            {isUa 
                              ? 'Автономне замовлення продуктів у Сільпо через Nutri-Agent доступне лише у тарифі Premium.'
                              : 'Autonomous grocery ordering from Silpo via Nutri-Agent is available exclusively on Premium.'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
            <span className="text-[10px] opacity-40 mt-3 px-1">
              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h' })}
            </span>
          </motion.div>
          </React.Fragment>
          );
        })}
        
        {isAnalyzingText && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col items-start mr-auto max-w-[85%]"
          >
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] p-4 rounded-2xl rounded-tl-none flex flex-col gap-3">
              <div className="flex gap-1.5 items-center">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                    className="w-1.5 h-1.5 bg-emerald-500 rounded-full"
                  />
                ))}
              </div>
              {chatThinkingStep > 0 && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="text-xs text-emerald-500/80 font-medium italic"
                >
                  {chatThinkingSteps[Math.min(chatThinkingStep - 1, chatThinkingSteps.length - 1)]}
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
        <div ref={chatEndRef} />
      </div>

      <motion.div 
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
        className="fixed bottom-[106px] left-0 right-0 z-40 px-4 pointer-events-none"
      >
        <div className="max-w-2xl mx-auto pointer-events-auto">
          {chatMessages.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center space-y-4 mb-6 opacity-50">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-emerald-500" />
              </div>
              <p className="text-sm font-bold uppercase tracking-widest max-w-[400px] whitespace-pre-line">
                {t.chatWelcomePrompt || (lang !== 'ua' ? 'Ask me anything about nutrition\nor tell me what you ate!' : 'Запитуйте будь-що про харчування\nабо розкажіть, що ви з’їли!')}
              </p>
            </div>
          )}

          {/* Quick food suggestion pre-made buttons */}
          <AnimatePresence>
            {!isScrollingChat && (
              <motion.div
                initial={{ opacity: 0, height: 0, scaleY: 0 }}
                animate={{ opacity: 1, height: 'auto', scaleY: 1 }}
                exit={{ opacity: 0, height: 0, scaleY: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="flex items-center gap-2 mb-2 overflow-x-auto no-scrollbar scroll-smooth whitespace-nowrap origin-bottom py-1"
              >
                {(() => {
                  const suggestions = [
                    {
                      id: 'order-nutri-agent',
                      icon: '🛒',
                      label: lang === 'ua' ? "Замовити їжу (Nutri-Agent)" : "Order Food (Nutri-Agent)",
                      prompt: lang === 'ua'
                        ? `Замов мені корисні продукти на тиждень через Nutri-Agent у Сільпо з високим вмістом білка та свіжими овочами.`
                        : `Order healthy groceries for this week via Nutri-Agent from Silpo with high protein and fresh ingredients.`
                    },
                    {
                      id: 'weekly',
                      icon: '🥦',
                      label: lang === 'ua' ? "Тижневе меню" : "Weekly Plan",
                      prompt: lang === 'ua'
                        ? `Порадь мені тижневі варіанти страв на основі моїх уподобань "${foodPreferences || 'не вказано'}" та бюджету "${foodBudget || 'не вказано'}".`
                        : `Suggest me a weekly food suggestions plan based on my preferences "${foodPreferences || 'none specified'}" and budget "${foodBudget || 'none specified'}".`
                    },
                    {
                      id: 'monthly',
                      icon: '📅',
                      label: lang === 'ua' ? "Місячне меню" : "Monthly Plan",
                      prompt: lang === 'ua'
                        ? `Порадь мені місячні варіанти страв на основі моїх уподобань "${foodPreferences || 'не вказано'}" та бюджету "${foodBudget || 'не вказано'}".`
                        : `Suggest me a monthly food suggestions plan based on my preferences "${foodPreferences || 'none specified'}" and budget "${foodBudget || 'none specified'}".`
                    },
                    {
                      id: 'high-protein',
                      icon: '🥩',
                      label: lang === 'ua' ? "Більше білка" : "High Protein",
                      prompt: lang === 'ua'
                        ? `Порекомендуй 5 швидких та корисних страв або перекусів з високим вмістом білка, що відповідають моїм уподобанням "${foodPreferences || 'не вказано'}".`
                        : `Suggest 5 quick and healthy high-protein meals or snacks that align with my preferences: "${foodPreferences || 'none specified'}".`
                    },
                    {
                      id: 'grocery',
                      icon: '🛒',
                      label: lang === 'ua' ? "Список продуктів" : "Concierge",
                      prompt: lang === 'ua'
                        ? `Склади корисний тижневий список продуктів відповідно до моїх уподобань "${foodPreferences || 'здорова їжа'}".`
                        : `Create a healthy weekly grocery shopping list based on my food preferences: "${foodPreferences || 'healthy foods'}".`
                    },
                    fastingState?.isActive 
                      ? {
                          id: 'fasting',
                          icon: '⏱️',
                          label: lang === 'ua' ? "Поради для посту" : "Fasting Guide",
                          prompt: lang === 'ua'
                            ? `Дай мені пораду, як безпечно вийти з голодування/постування, та порекомендуй кілька поживних страв.`
                            : `Give me advice on how to break my fast safely and recommend some nutrient-dense meals for when I finish my fast.`
                        }
                      : {
                          id: 'calories',
                          icon: '🔥',
                          label: lang === 'ua' ? "Контроль калорій" : "Calorie Guide",
                          prompt: lang === 'ua'
                            ? `Порекомендуй низькокалорійні продукти великого об'єму, які допоможуть мені ситно харчуватися і залишатися в межах ліміту калорій.`
                            : `Suggest some low-calorie, high-volume foods that can help me stay full and within my calorie budget.`
                        }
                  ];

                  return (
                    <>
                      {suggestions.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setChatInput(item.prompt)}
                          className={cn(
                            "px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs border active:scale-95 cursor-pointer shrink-0 preset-bubble-hover",
                            theme === 'light'
                              ? "bg-white border-zinc-200 text-zinc-800 hover:bg-emerald-50/80 hover:text-emerald-700 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-200"
                              : "bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700/80 hover:text-emerald-400"
                          )}
                        >
                          <span>{item.icon}</span> <span>{item.label}</span>
                        </button>
                      ))}
                      {(foodPreferences || foodBudget) && (
                        <span className={cn(
                          "text-[10px] font-black uppercase tracking-wider animate-pulse ml-1 shrink-0",
                          theme === 'light' ? "text-emerald-700" : "text-[var(--brand-green)]"
                        )}>
                          {lang === 'ua' ? "✨ Налаштування враховано" : "✨ Preferences linked"}
                        </span>
                      )}
                    </>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>

          <div className={cn(
            "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-2 card-shadow shadow-xl dark:shadow-black/50",
            illumination && "illumination-food"
          )}>
        {chatImage && (
          <div className="px-4 py-2 flex items-center gap-3">
            <div className="relative w-12 h-12 rounded-lg overflow-hidden border border-emerald-500/30">
              <img src={chatImage} className="w-full h-full object-cover" loading="lazy" decoding="async" referrerPolicy="no-referrer" />
              <button 
                onClick={() => setChatImage(null)}
                className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl-lg"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <span className="text-[10px] font-bold uppercase text-emerald-500">
              {t.imageAttached || (lang === 'ua' ? 'Зображення додано' : 'Image attached')}
            </span>
          </div>
        )}
        <form onSubmit={handleChatSubmit} className="relative flex items-center gap-2">
          <input
            type="file"
            ref={chatFileInputRef}
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => setChatImage(reader.result as string);
                reader.readAsDataURL(file);
              }
            }}
          />
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
              className={cn(
                "p-2.5 transition-colors",
                showAttachmentMenu ? "text-emerald-500" : "text-zinc-400 hover:text-emerald-500"
              )}
            >
              <PlusCircle className="w-5 h-5" />
            </button>
            <AnimatePresence mode="wait">
              {showAttachmentMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => {
                      setShowAttachmentMenu(false);
                      setShowChooseOptions(false);
                    }} 
                  />
                  <motion.div
                    key={showChooseOptions ? "choose-options" : "main-menu"}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute bottom-full left-0 mb-2 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-xl overflow-hidden min-w-[180px] z-50 backdrop-blur-xl"
                  >
                    {!showChooseOptions ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            startCamera('attach', (img) => {
                              setChatImage(img);
                              setShowAttachmentMenu(false);
                            });
                          }}
                          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-emerald-500/10 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
                            <Camera className="w-4 h-4 text-emerald-500" />
                          </div>
                          <span className="text-xs font-bold uppercase tracking-wider">{t.takePhoto || 'Photo'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowChooseOptions(true);
                          }}
                          className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-emerald-500/10 transition-colors text-left border-t border-[var(--card-border)]/50"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                              <Plus className="w-4 h-4 text-blue-500" />
                            </div>
                            <span className="text-xs font-bold uppercase tracking-wider">{t.choose || 'Choose'}</span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-zinc-400" />
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="px-4 py-2 bg-[var(--input-bg)] border-b border-[var(--card-border)] flex items-center gap-2">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowChooseOptions(false);
                            }}
                            className="p-1 hover:bg-[var(--card-bg)] rounded-full transition-colors"
                          >
                            <ChevronLeft className="w-4 h-4 text-zinc-500" />
                          </button>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.choose || 'Choose'}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setIsChatPicking(true);
                            setChatPickingSource('favorites');
                            setView('favorites');
                            setShowAttachmentMenu(false);
                            setShowChooseOptions(false);
                          }}
                          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-emerald-500/10 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
                            <Heart className="w-4 h-4 text-emerald-500" />
                          </div>
                          <span className="text-xs font-bold uppercase tracking-wider">{t.favorites || 'FAVORITES'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsChatPicking(true);
                            setChatPickingSource('gallery');
                            setView('gallery');
                            setShowAttachmentMenu(false);
                            setShowChooseOptions(false);
                          }}
                          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-emerald-500/10 transition-colors text-left border-t border-[var(--card-border)]/50"
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
                            <Image className="w-4 h-4 text-emerald-500" />
                          </div>
                          <span className="text-xs font-bold uppercase tracking-wider">{t.mealGallery || 'Meal Gallery'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            chatFileInputRef.current?.click();
                            setShowAttachmentMenu(false);
                            setShowChooseOptions(false);
                          }}
                          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-emerald-500/10 transition-colors text-left border-t border-[var(--card-border)]/50"
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                            <Monitor className="w-4 h-4 text-blue-500" />
                          </div>
                          <span className="text-xs font-bold uppercase tracking-wider">{t.device || 'Device'}</span>
                        </button>
                      </>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
          {isListening ? (
            <div className="flex-1 flex items-center justify-between bg-red-500/5 border border-red-500/20 rounded-2xl px-4 py-3 h-[52px]">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
                <span className="text-xs font-bold uppercase tracking-wider text-red-500 animate-pulse">
                  {lang === 'ua' ? 'Запис голосової нотатки...' : 'Recording Voice Note...'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs font-bold text-red-500 bg-red-500/10 px-2 py-0.5 rounded-full">
                  {voiceNoteDuration}s / 15s
                </span>
                <button
                  type="button"
                  onClick={toggleListening}
                  className="text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-red-500 px-2 py-1 bg-zinc-500/10 hover:bg-red-500/10 rounded-xl transition-colors"
                >
                  {lang === 'ua' ? 'Готово' : 'Done'}
                </button>
              </div>
            </div>
          ) : isTranscribingVoiceNote ? (
            <div className="flex-1 flex items-center gap-3 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl px-4 py-3 h-[52px]">
              <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 animate-pulse">
                {lang === 'ua' ? 'ШІ розшифровує ваш запис...' : 'AI transcribing your recording...'}
              </span>
            </div>
          ) : (
            <textarea
              id="chat-input-textarea"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onPaste={handleChatPaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleChatSubmit();
                }
              }}
              placeholder={skin === 'anime' ? (lang === 'ua' ? "Запитай про страву (напр., 3 яйця і протеїн)..." : "Ask about meals (e.g., 3 eggs and a protein shake)...") : t.chatPlaceholder}
              rows={2}
              className={cn(
                "flex-1 bg-[var(--input-bg)] border border-transparent rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none placeholder:italic text-xs sm:text-sm leading-snug min-h-[52px]",
                !chatInput ? "overflow-hidden" : "overflow-y-auto no-scrollbar"
              )}
            />
          )}
          <div className="flex items-center gap-1 pr-1">
            <button
              type="button"
              onClick={toggleListening}
              disabled={isTranscribingVoiceNote}
              title={isListening ? (lang === 'ua' ? 'Зупинити запис' : 'Stop recording') : (lang === 'ua' ? 'Голосовий запис' : 'Voice recording')}
              className={cn(
                "p-2.5 rounded-xl transition-all cursor-pointer",
                isListening 
                  ? "bg-red-500 text-white animate-pulse btn-active-border-red" 
                  : "text-zinc-500 hover:text-emerald-500 hover:bg-emerald-500/10"
              )}
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>

            <button
              type="submit"
              disabled={isAnalyzingText || isListening || isTranscribingVoiceNote || (!chatInput.trim() && !chatImage)}
              className={cn(
                "p-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/20 cursor-pointer",
                isAnalyzingText && "btn-active-border"
              )}
            >
              {isAnalyzingText
                ? <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin block" />
                : <CornerDownLeft className="w-5 h-5" />
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  </motion.div>
</motion.div>
  );
};
  const renderCameraUI = () => {
    if (!isCameraActive) return null;
    return (
      <div className="fixed inset-0 z-[60] bg-black flex flex-col">
        <div className="relative flex-grow">
          {cameraMode === 'photo' ? (
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted
              className="w-full h-full object-cover"
            />
          ) : (
            <div id="barcode-scanner-container" className="w-full h-full bg-black"></div>
          )}
          
          <div className="absolute inset-0 border-2 border-emerald-500/30 pointer-events-none m-4 rounded-3xl">
            {cameraMode === 'barcode' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div 
                  animate={{
                    borderColor: ['rgba(16, 185, 129, 0.4)', 'rgba(16, 185, 129, 1)', 'rgba(16, 185, 129, 0.4)'],
                    boxShadow: [
                      '0 0 5px rgba(16, 185, 129, 0.1)',
                      '0 0 25px rgba(16, 185, 129, 0.5)',
                      '0 0 5px rgba(16, 185, 129, 0.1)'
                    ],
                    scale: [0.99, 1.01, 0.99]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className={cn("w-72 h-44 border-2 border-emerald-500 rounded-2xl relative overflow-hidden", illumination ? "illumination-food" : "")}
                >
                  {/* Subtle shimmer sheen */}
                  <motion.div
                    animate={{
                      x: ['-100%', '100%']
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500/10 to-transparent pointer-events-none"
                  />
                  <motion.div 
                    animate={{ top: ['0%', '100%'] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                    className={cn("absolute left-0 right-0 h-0.5 bg-emerald-500", illumination ? "shadow-[0_0_13px_#10b981] dark:shadow-[0_0_8px_#10b981]" : "")}
                  />
                </motion.div>
              </div>
            )}
          </div>

          {/* Mode Toggle */}
          <div className="absolute bottom-6 left-0 right-0 flex justify-center z-10">
            <div className="bg-zinc-900/80 backdrop-blur-xl rounded-full p-1 flex gap-1 border border-white/10">
              <button 
                onClick={() => setCameraMode('photo')}
                className={cn(
                  "px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2",
                  cameraMode === 'photo' ? "bg-emerald-500 text-white shadow-lg btn-active-border" : "text-zinc-500 dark:text-zinc-400 hover:text-white"
                )}
              >
                <Camera className="w-4 h-4" /> {t.photoMode}
              </button>
              <button 
                onClick={() => setCameraMode('barcode')}
                className={cn(
                  "px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2",
                  cameraMode === 'barcode' ? "bg-emerald-500 text-white shadow-lg btn-active-border" : "text-zinc-500 dark:text-zinc-400 hover:text-white"
                )}
              >
                <Barcode className="w-4 h-4" /> {t.barcodeMode}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-zinc-950 p-8 flex justify-center items-center gap-8">
          <button 
            onClick={stopCamera}
            className="bg-zinc-900 text-white w-14 h-14 rounded-full flex items-center justify-center hover:bg-zinc-800 transition-all border border-white/10"
            title={t.cancel}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          {cameraMode === 'photo' ? (
            <button 
              onClick={capturePhoto}
              className="w-20 h-20 rounded-2xl bg-white p-1 shadow-2xl shadow-emerald-500/40 group active:scale-95 transition-transform btn-active-border"
            >
              <div className="w-full h-full rounded-xl border-4 border-zinc-900 flex items-center justify-center bg-emerald-500 group-hover:bg-emerald-400 transition-colors">
                <Camera className="w-8 h-8 text-white" />
              </div>
            </button>
          ) : (
            <div className="w-20 h-20 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full border-4 border-emerald-500/30 border-t-emerald-500 animate-spin" />
            </div>
          )}
          
          <button 
            onClick={toggleFacingMode}
            className="bg-zinc-900 text-white w-14 h-14 rounded-full flex items-center justify-center hover:bg-zinc-800 transition-all border border-white/10"
            title={t.switchCamera}
          >
            <RotateCw className="w-6 h-6" />
          </button>
        </div>

        {/* Quick-Add Portion Adjuster Overlay in Barcode Scanner Mode */}
        <AnimatePresence>
          {scannedActiveProduct && (
            <div className="absolute inset-0 z-30 bg-black/75 backdrop-blur-md flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 30 }}
                transition={{ type: "spring", duration: 0.4 }}
                className={cn(
                  "w-full max-w-sm rounded-3xl border p-5 shadow-2xl relative overflow-hidden",
                  theme === 'light'
                    ? "bg-zinc-50/95 border-zinc-200 text-zinc-900"
                    : "bg-zinc-950/95 border-zinc-800 text-zinc-100"
                )}
              >
                {(() => {
                  const currentAmount = Number(scannedActiveProduct.currentAmount) || 0;
                  const ratio = currentAmount / 100;
                  const scaledCalories = Math.round(scannedActiveProduct.calories100g * ratio);
                  const scaledProtein = Math.round(scannedActiveProduct.protein100g * ratio * 10) / 10;
                  const scaledCarbs = Math.round(scannedActiveProduct.carbs100g * ratio * 10) / 10;
                  const scaledFat = Math.round(scannedActiveProduct.fat100g * ratio * 10) / 10;
                  const scaledSugar = Math.round(scannedActiveProduct.sugar100g * ratio * 10) / 10;
                  const scaledFiber = Math.round(scannedActiveProduct.fiber100g * ratio * 10) / 10;
                  const scaledCaffeine = Math.round((scannedActiveProduct.caffeine100g || 0) * ratio);

                  const handleQuickLog = () => {
                    const finalAmount = currentAmount || 100;
                    const finalRatio = finalAmount / 100;

                    const finalFoods = [{
                      ...scannedActiveProduct.analysis.foods[0],
                      amount: finalAmount,
                      calories_kcal: scannedActiveProduct.calories100g * finalRatio,
                      protein_g: scannedActiveProduct.protein100g * finalRatio,
                      carbs_g: scannedActiveProduct.carbs100g * finalRatio,
                      fat_g: scannedActiveProduct.fat100g * finalRatio,
                      sugar_g: scannedActiveProduct.sugar100g * finalRatio,
                      fiber_g: scannedActiveProduct.fiber100g * finalRatio,
                      sodium_mg: scannedActiveProduct.sodium100g * finalRatio,
                      caffeine_mg: (scannedActiveProduct.caffeine100g || 0) * finalRatio,
                      portion: `${finalAmount}${scannedActiveProduct.unit}`
                    }];

                    const finalNutrition = {
                      calories_kcal: scannedActiveProduct.calories100g * finalRatio,
                      protein_g: scannedActiveProduct.protein100g * finalRatio,
                      carbs_g: scannedActiveProduct.carbs100g * finalRatio,
                      fat_g: scannedActiveProduct.fat100g * finalRatio,
                      sugar_g: scannedActiveProduct.sugar100g * finalRatio,
                      fiber_g: scannedActiveProduct.fiber100g * finalRatio,
                      sodium_mg: scannedActiveProduct.sodium100g * finalRatio,
                      caffeine_mg: (scannedActiveProduct.caffeine100g || 0) * finalRatio,
                    };

                    const finalAnalysis = postProcessNutrition({
                      ...scannedActiveProduct.analysis,
                      image: scannedActiveProduct.imageUrl || '',
                      total_amount: `${finalAmount}${scannedActiveProduct.unit}`,
                      foods: finalFoods,
                      total_nutrition: finalNutrition,
                      insight: "" // Dynamically recalculate rich insight for the portion size
                    }, lang, vibe);

                    stopCamera();
                    saveToHistory(finalAnalysis, scannedActiveProduct.imageUrl, lang.startsWith('ua') ? "Продукт успішно записано!" : "Product successfully logged!");
                    sounds.playSuccess();
                    setScannedActiveProduct(null);
                    setView('home');
                  };

                  const handleOpenFullEditor = () => {
                    const finalAmount = currentAmount || 100;
                    const finalRatio = finalAmount / 100;

                    const finalFoods = [{
                      ...scannedActiveProduct.analysis.foods[0],
                      amount: finalAmount,
                      calories_kcal: scannedActiveProduct.calories100g * finalRatio,
                      protein_g: scannedActiveProduct.protein100g * finalRatio,
                      carbs_g: scannedActiveProduct.carbs100g * finalRatio,
                      fat_g: scannedActiveProduct.fat100g * finalRatio,
                      sugar_g: scannedActiveProduct.sugar100g * finalRatio,
                      fiber_g: scannedActiveProduct.fiber100g * finalRatio,
                      sodium_mg: scannedActiveProduct.sodium100g * finalRatio,
                      caffeine_mg: (scannedActiveProduct.caffeine100g || 0) * finalRatio,
                      portion: `${finalAmount}${scannedActiveProduct.unit}`
                    }];

                    const finalNutrition = {
                      calories_kcal: scannedActiveProduct.calories100g * finalRatio,
                      protein_g: scannedActiveProduct.protein100g * finalRatio,
                      carbs_g: scannedActiveProduct.carbs100g * finalRatio,
                      fat_g: scannedActiveProduct.fat100g * finalRatio,
                      sugar_g: scannedActiveProduct.sugar100g * finalRatio,
                      fiber_g: scannedActiveProduct.fiber100g * finalRatio,
                      sodium_mg: scannedActiveProduct.sodium100g * finalRatio,
                      caffeine_mg: (scannedActiveProduct.caffeine100g || 0) * finalRatio,
                    };

                    const finalAnalysis = postProcessNutrition({
                      ...scannedActiveProduct.analysis,
                      image: scannedActiveProduct.imageUrl || '',
                      total_amount: `${finalAmount}${scannedActiveProduct.unit}`,
                      foods: finalFoods,
                      total_nutrition: finalNutrition,
                      insight: "" // Dynamically recalculate rich insight for the portion size
                    }, lang, vibe);

                    setResult(finalAnalysis);
                    setOriginalResult(safeClone(finalAnalysis));
                    setImage(scannedActiveProduct.imageUrl);
                    setView('analyze');
                    stopCamera();
                    setScannedActiveProduct(null);
                  };

                  // Presets
                  const presetValues = scannedActiveProduct.unit === 'ml' 
                    ? [100, 200, 250, 330, 500] 
                    : [50, 100, 150, 250, 500];

                  return (
                    <div className="space-y-4">
                      {/* Logo & Headline */}
                      <div className="flex items-center justify-between border-b border-zinc-200/50 dark:border-zinc-800 pb-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">⚡</span>
                          <h3 className="text-xs font-black uppercase tracking-wider text-emerald-500">
                            {lang.startsWith('ua') ? "Швидке Додавання" : "Quick Add Portion"}
                          </h3>
                        </div>
                        <span className="text-[10px] font-mono bg-emerald-500/10 text-emerald-500 px-2 py-0.5 rounded-full font-bold">
                          {scannedActiveProduct.unit === 'ml' ? 'Liquid' : 'Solid'}
                        </span>
                      </div>

                      {/* Item Info Box with Image or Icon */}
                      <div className="flex flex-col gap-2 bg-zinc-100/50 dark:bg-zinc-900/40 p-3 rounded-2xl border border-zinc-200/10 dark:border-zinc-800/40">
                        <div className="flex items-center gap-3">
                          {scannedActiveProduct.imageUrl ? (
                            <img 
                              src={scannedActiveProduct.imageUrl} 
                              alt={scannedActiveProduct.productName} 
                              referrerPolicy="no-referrer"
                              className="w-10 h-10 rounded-xl object-cover border border-zinc-200 dark:border-zinc-800 shadow-sm"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-xl border border-emerald-500/10">
                              {getFoodEmoji(scannedActiveProduct.productName)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-xs truncate">
                              {scannedActiveProduct.productName}
                            </p>
                            <p className="text-[9px] text-zinc-500 dark:text-zinc-400 font-mono tracking-wide">
                              {lang.startsWith('ua') ? "Штрих-код продукт" : "Barcode Scanned"}
                            </p>
                          </div>
                        </div>
                        {((scannedActiveProduct.analysis?.safe_choice_status) || (isPremiumOrPro && safeChoiceProfile?.active !== false)) && safeChoiceProfile?.active !== false && (
                          <div 
                            onClick={() => {
                              if (scannedActiveProduct.analysis) {
                                setSafeChoiceDetailModalData({
                                  ...scannedActiveProduct.analysis,
                                  meal_name: scannedActiveProduct.productName,
                                  foods: [],
                                  total_nutrition: {
                                    calories_kcal: scannedActiveProduct.calories100g,
                                    protein_g: scannedActiveProduct.protein100g,
                                    carbs_g: scannedActiveProduct.carbs100g,
                                    fat_g: scannedActiveProduct.fat100g,
                                    sugar_g: scannedActiveProduct.sugar100g || 0,
                                    fiber_g: scannedActiveProduct.fiber100g || 0,
                                    sodium_mg: scannedActiveProduct.sodium100g || 0,
                                  },
                                  meal_type: 'snack'
                                });
                              }
                            }}
                            className={cn(
                              "mt-2 p-2.5 rounded-xl flex items-start gap-2 border text-[10px] leading-tight cursor-pointer group hover:scale-[1.01] hover:shadow-md transition-all",
                              scannedActiveProduct.analysis?.safe_choice_status === 'red' ? "bg-rose-500/10 border-rose-500/50 hover:bg-rose-500/15 text-rose-600 dark:text-rose-400" :
                              scannedActiveProduct.analysis?.safe_choice_status === 'amber' ? "bg-amber-500/10 border-amber-500/50 hover:bg-amber-500/15 text-amber-600 dark:text-amber-400" :
                              "bg-emerald-500/10 border-emerald-500/50 hover:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                            )}
                          >
                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-0.5">
                                <span className="font-bold uppercase tracking-wider block">
                                  {scannedActiveProduct.analysis?.safe_choice_status === 'red' ? (t.safeChoiceStop || 'Safe Choice: STOP') :
                                   scannedActiveProduct.analysis?.safe_choice_status === 'amber' ? (t.safeChoiceReview || 'Safe Choice: REVIEW') :
                                   (t.safeChoicePassed || 'Safe Choice: PASSED')}
                                </span>
                                <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-current/10 border border-current/20 flex items-center gap-1 group-hover:bg-current group-hover:text-white transition-all shrink-0 whitespace-nowrap">
                                  {lang === 'ua' ? 'Детальніше' : 'Tap for details'} <ChevronRight className="w-3 h-3" />
                                </span>
                              </div>
                              <p className="opacity-90">{getSafeChoiceMessage((scannedActiveProduct.analysis?.safe_choice_status || 'green') as any, scannedActiveProduct.analysis?.safe_choice_message)}</p>
                              {scannedActiveProduct.analysis?.detected_risk_components && (
                                <RiskComponentsList 
                                  components={scannedActiveProduct.analysis.detected_risk_components} 
                                  lang={lang} 
                                  onOpenEDecoder={(q) => {
                                    triggerHaptic('SUCCESS');
                                    setEDecoderInitialQuery(q || '');
                                    setIsEDecoderOpen(true);
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Real-time Nutrition Metric Grid */}
                      <div className={cn(
                        "grid gap-1.5 text-center",
                        scannedActiveProduct.caffeine100g > 0 ? "grid-cols-5" : "grid-cols-4"
                      )}>
                        <div className="p-1.5 rounded-xl bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/10">
                          <p className="text-[8px] font-black uppercase tracking-widest text-amber-500/80 mb-0.5">{lang === 'ua' ? 'Ккал' : 'Kcal'}</p>
                          <p className="text-xs font-black font-mono text-amber-500">{scaledCalories}</p>
                        </div>
                        <div className="p-1.5 rounded-xl bg-red-500/5 dark:bg-red-500/10 border border-red-500/10">
                          <p className="text-[8px] font-black uppercase tracking-widest text-red-500/80 mb-0.5">{lang === 'ua' ? 'Біл' : 'Prot'}</p>
                          <p className="text-xs font-black font-mono text-red-500">{scaledProtein}g</p>
                        </div>
                        <div className="p-1.5 rounded-xl bg-blue-500/5 dark:bg-blue-500/10 border border-blue-500/10">
                          <p className="text-[8px] font-black uppercase tracking-widest text-blue-500/80 mb-0.5">{lang === 'ua' ? 'Вуг' : 'Carb'}</p>
                          <p className="text-xs font-black font-mono text-blue-500">{scaledCarbs}g</p>
                        </div>
                        <div className="p-1.5 rounded-xl bg-pink-500/5 dark:bg-pink-500/10 border border-pink-500/10">
                          <p className="text-[8px] font-black uppercase tracking-widest text-pink-500/80 mb-0.5">{lang === 'ua' ? 'Жир' : 'Fat'}</p>
                          <p className="text-xs font-black font-mono text-pink-500">{scaledFat}g</p>
                        </div>
                        {scannedActiveProduct.caffeine100g > 0 && (
                          <div className="p-1.5 rounded-xl bg-amber-900/10 dark:bg-amber-800/20 border border-amber-700/20">
                            <p className="text-[8px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-500 mb-0.5">{lang === 'ua' ? "Коф" : "Caff"}</p>
                            <p className="text-xs font-black font-mono text-amber-700 dark:text-amber-500">{scaledCaffeine}mg</p>
                          </div>
                        )}
                      </div>

                      {/* Portion Input and Increment Adjusters */}
                      <div className="space-y-2.5">
                        <div className="flex items-center justify-between gap-4 bg-zinc-100/30 dark:bg-zinc-900/10 p-1.5 rounded-xl">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                            {lang.startsWith('ua') ? "Розмір порції (" : "Portion ("}{scannedActiveProduct.unit})
                          </span>
                          
                          {/* Numeric input with unit */}
                          <div className="flex items-center gap-1">
                            <input 
                              type="number" 
                              value={scannedActiveProduct.currentAmount}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                setScannedActiveProduct({
                                  ...scannedActiveProduct,
                                  currentAmount: val
                                });
                              }}
                              min={1}
                              max={10000}
                              className="w-16 px-1.5 py-0.5 bg-zinc-200/50 dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-800 rounded-lg text-center font-bold font-mono text-xs focus:ring-1 focus:ring-emerald-500 focus:outline-none"
                            />
                          </div>
                        </div>

                        {/* Presets List */}
                        <div className="grid grid-cols-5 gap-1">
                          {presetValues.map(p => (
                            <button
                              key={p}
                              onClick={() => {
                                sounds.playPop();
                                setScannedActiveProduct({
                                  ...scannedActiveProduct,
                                  currentAmount: p
                                });
                              }}
                              className={cn(
                                "py-1.5 rounded-lg text-[9px] font-black font-mono transition-all border text-center",
                                currentAmount === p
                                  ? "bg-emerald-500 border-emerald-500 text-white shadow-md shadow-emerald-500/20"
                                  : "bg-zinc-100/60 dark:bg-zinc-900/60 border-zinc-200/50 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-emerald-500 hover:text-emerald-500"
                              )}
                            >
                              {p}
                            </button>
                          ))}
                        </div>

                        {/* Coarse and Fine Increments Slider Buttons */}
                        <div className="grid grid-cols-4 gap-1">
                          {[-100, -20, 20, 100].map(inc => {
                            const isNeg = inc < 0;
                            return (
                              <button
                                key={inc}
                                onClick={() => {
                                  sounds.playPop();
                                  setScannedActiveProduct({
                                    ...scannedActiveProduct,
                                    currentAmount: Math.max(1, currentAmount + inc)
                                  });
                                }}
                                className={cn(
                                  "py-1.5 rounded-lg text-[9px] font-black font-mono transition-colors border",
                                  isNeg
                                    ? "bg-red-500/5 hover:bg-red-500/10 border-red-500/10 text-red-500"
                                    : "bg-emerald-500/5 hover:bg-emerald-500/10 border-emerald-500/10 text-emerald-500"
                                )}
                              >
                                {inc > 0 ? `+${inc}` : inc}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-col gap-2 pt-2.5 border-t border-zinc-200/50 dark:border-zinc-800">
                        <button
                          onClick={handleQuickLog}
                          disabled={currentAmount <= 0}
                          className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white py-2.5 rounded-2xl font-bold text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all shadow-lg shadow-emerald-500/25 btn-active-border"
                        >
                          <span>⚡</span>
                          {lang.startsWith('ua') ? "Швидко Додати" : "Quick Log"}
                        </button>

                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            onClick={handleOpenFullEditor}
                            className="bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 py-2 rounded-xl font-bold text-[9px] uppercase tracking-widest flex items-center justify-center gap-1 transition-all border border-zinc-200 dark:border-zinc-800"
                          >
                            <Settings className="w-3 h-3" />
                            {lang.startsWith('ua') ? "Детально" : "Edit Full"}
                          </button>
                          <button
                            onClick={() => {
                              sounds.playPop();
                              setScannedActiveProduct(null);
                            }}
                            className="bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-500 py-2 rounded-xl font-bold text-[9px] uppercase tracking-widest flex items-center justify-center gap-1 transition-all border border-zinc-200 dark:border-zinc-800"
                          >
                            <RefreshCw className="w-3 h-3" />
                            {lang.startsWith('ua') ? "Далі" : "Scan Next"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  };
  const renderManualEntry = () => {
    const manualWaterAmount = (manualCategory === 'drink' || /water|coffee|tea|drink|beverage|juice|soda|milk|smoothie/i.test(manualMealName)) ? (Number(manualAmount) || 0) : 0;
    const isManualWater = manualCategory === 'drink' || /water|coffee|tea|drink|beverage|juice|soda|milk|smoothie/i.test(manualMealName);
    
    const projectedTotals = {
      calories: selectedDayTotals.calories + (Number(manualCalories) || 0),
      protein: selectedDayTotals.protein + (Number(manualProtein) || 0),
      carbs: selectedDayTotals.carbs + (Number(manualCarbs) || 0),
      fat: selectedDayTotals.fat + (Number(manualFat) || 0),
      sugar: selectedDayTotals.sugar + (Number(manualSugar) || 0),
      fiber: selectedDayTotals.fiber + (Number(manualFiber) || 0),
      sodium: selectedDayTotals.sodium + (Number(manualSodium) || 0),
      water: selectedDayTotals.water + manualWaterAmount,
    };
    return (
    <motion.div
      className="space-y-8"
    >
      <div className="flex items-center gap-4 mb-2">
        <button 
          onClick={handleBack}
          className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h2 className={cn(
          "text-xl sm:text-2xl font-bold uppercase tracking-normal",
          theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
        )}>
          {skin === 'anime' ? (lang === 'ua' ? "『ЗАПИС БЕНТО ТА СТРАВ』 🍱✨" : "『BENTO & FEAST LOG』 🍱✨") : t.manualEntry}
        </h2>
      </div>

      <form onSubmit={handleManualSubmit} className="space-y-10">
        <div className={cn(
          "relative bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[32px] p-6 card-shadow overflow-hidden",
          illumination && (isManualWater ? "illumination-water" : "illumination-food")
        )}>
          {/* Atmospheric Emerald Background */}
          {illumination && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-50/30 to-emerald-100/60" />
              <motion.div 
                animate={{ y: [0, -20, 0], x: [0, 15, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-32 -left-20 w-80 h-80 bg-emerald-300/20 rounded-full blur-3xl"
              />
              <motion.div 
                animate={{ y: [0, 25, 0], x: [0, -20, 0], scale: [1, 1.15, 1] }}
                transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -bottom-20 -right-20 w-96 h-96 bg-teal-300/20 rounded-full blur-3xl"
              />
              <motion.div 
                animate={{ y: [0, -40, 0], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                className="absolute top-1/4 left-1/3 w-40 h-40 bg-emerald-200/30 rounded-full blur-3xl"
              />
            </div>
          )}

          <div className="relative z-10 space-y-6">
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 px-1">
                {t.mealName}
              </label>
              <input
                type="text"
                value={manualMealName}
                onChange={(e) => setManualMealName(e.target.value)}
                placeholder={t.mealNamePlaceholder}
                required
                className="w-full bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
              />
            </div>

            {/* Image Upload for Manual Entry */}
            <div className="space-y-2">
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">
              {t.mealImage || 'Meal Image'}
            </label>
            <div className="relative group">
              {manualImage ? (
                <div className="relative rounded-3xl overflow-hidden bg-zinc-900 border border-[var(--card-border)] card-shadow h-64 sm:h-80">
                  <img src={manualImage} alt="Manual meal" loading="lazy" decoding="async" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  <div className="absolute bottom-4 right-4 flex items-center gap-2">
                    <button 
                      type="button"
                      onClick={handleGenerateImage}
                      disabled={isGeneratingImage}
                      className="bg-white/90 backdrop-blur-md text-zinc-900 px-4 py-2 rounded-full font-bold text-xs uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all hover:bg-white disabled:opacity-50"
                    >
                      {isGeneratingImage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <motion.div
                          animate={{ 
                            scale: [1, 1.1, 1],
                            color: ["#a855f7", "#3b82f6", "#10b981", "#a855f7"],
                          }}
                          transition={{ 
                            duration: 4, 
                            repeat: Infinity, 
                            ease: "easeInOut" 
                          }}
                        >
                          <Sparkles className="w-4 h-4" />
                        </motion.div>
                      )}
                      {lang.startsWith('en') ? 'Generate' : 'Згенерувати'}
                    </button>
                    <button 
                      type="button"
                      onClick={() => setManualImage(null)}
                      className="bg-red-500/90 backdrop-blur-md text-white p-2 rounded-full shadow-lg transition-all hover:bg-red-600"
                      title={t.deleteMeal || 'Remove'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button 
                      type="button"
                      onClick={() => setImageToCrop(manualImage)}
                      className="bg-amber-500/90 backdrop-blur-md text-white p-2 rounded-full shadow-lg transition-all hover:bg-amber-600"
                      title={lang.startsWith('en') ? 'Crop' : 'Обрізати'}
                    >
                      <Crop className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="w-full h-48 rounded-2xl bg-[var(--card-bg)] border-2 border-dashed border-[var(--card-border)] flex flex-col items-center justify-center overflow-hidden transition-all group-hover:border-emerald-500/50">
                  <div className="flex items-center justify-center w-full px-6">
                    <button 
                      type="button"
                      onClick={() => document.getElementById('manual-image-upload')?.click()}
                      className="flex-1 flex items-center justify-center text-zinc-500 hover:text-emerald-500 transition-colors"
                    >
                      <Upload className="w-10 h-10" />
                    </button>
                    <div className="w-px h-12 bg-[var(--card-border)]" />
                     <button 
                      type="button"
                      onClick={() => startCamera()}
                      className="flex-1 flex items-center justify-center text-zinc-500 hover:text-emerald-500 transition-colors"
                    >
                      <Camera className="w-10 h-10" />
                    </button>
                    <div className="w-px h-12 bg-[var(--card-border)]" />
                    <button 
                      type="button"
                      onClick={handleGenerateImage}
                      disabled={isGeneratingImage}
                      className="flex-1 flex items-center justify-center text-zinc-500 hover:text-emerald-500 transition-colors disabled:opacity-50"
                    >
                      {isGeneratingImage ? (
                        <Loader2 className="w-10 h-10 animate-spin" />
                      ) : (
                        <motion.div
                          animate={{ 
                            scale: [1, 1.1, 1],
                            color: ["#a855f7", "#3b82f6", "#10b981", "#a855f7"],
                          }}
                          transition={{ 
                            duration: 4, 
                            repeat: Infinity, 
                            ease: "easeInOut" 
                          }}
                        >
                          <Sparkles className="w-10 h-10" />
                        </motion.div>
                      )}
                    </button>
                  </div>
                </div>
              )}
              <input 
                id="manual-image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = async () => {
                      try {
                        const resized = await resizeImage(reader.result as string, 512);
                        setImageToCrop(resized);
                      } catch (err) {
                        console.error("Error resizing manual image:", err);
                        toast.error(lang === 'ua' ? 'Не вдалося обробити зображення.' : 'Failed to process image.');
                      }
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 px-1">
                Category
              </label>
              <div className={cn(
                skin === 'anime' ? "grid grid-cols-3 sm:grid-cols-5 gap-2" : "flex flex-wrap gap-2"
              )}>
                {FOOD_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setManualCategory(cat.id)}
                    className={cn(
                      "transition-all card-shadow relative overflow-hidden flex",
                      skin === 'anime' 
                        ? "flex-col items-center justify-center gap-1 p-2 rounded-xl border text-[9px] font-bold uppercase tracking-widest min-h-[64px]"
                        : "items-center gap-2 px-3 py-2 rounded-xl border text-[10px] font-bold uppercase tracking-widest",
                      manualCategory === cat.id
                        ? (skin === 'anime' ? "bg-gradient-to-br from-pink-500 to-purple-600 border-pink-400 text-white shadow-[0_0_15px_rgba(236,72,153,0.6)] scale-105 z-10" : "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20")
                        : (skin === 'anime' ? "bg-pink-950/20 border-pink-400/20 text-pink-200/80 hover:border-pink-400/50 hover:scale-105" : "bg-[var(--card-bg)] border-[var(--card-border)] text-zinc-500 hover:border-emerald-500/50")
                    )}
                  >
                    {skin === 'anime' && manualCategory === cat.id && (
                      <span className="absolute top-0.5 right-1 text-[8px] text-pink-200 opacity-90 animate-pulse">✦</span>
                    )}
                    <span className={cn(skin === 'anime' ? "text-2xl drop-shadow-[0_0_8px_rgba(236,72,153,0.4)] mb-0.5" : "text-base")}>{cat.icon}</span>
                    <span className={skin === 'anime' ? "text-center leading-none" : ""}>{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 px-1">
                {t.mealType}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(['breakfast', 'lunch', 'dinner', 'snack'] as MealType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSelectedMealType(type)}
                    className={cn(
                      "py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-all border card-shadow",
                      selectedMealType === type
                        ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                        : "bg-[var(--card-bg)] border-[var(--card-border)] text-zinc-500 hover:border-emerald-500/50"
                    )}
                  >
                    {t[type]}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 px-1">
                  {t.amount}
                </label>
                <input
                  type="number"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="100"
                  className="w-full bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 px-1">
                  {t.unit}
                </label>
                <div className="flex gap-2 h-[50px]">
                  {(['gr', 'ml', 'pcs'] as const).map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setManualUnit(u)}
                      className={cn(
                        "flex-1 rounded-xl font-bold text-xs uppercase tracking-widest transition-all border card-shadow",
                        manualUnit === u
                          ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                          : "bg-[var(--card-bg)] border-[var(--card-border)] text-zinc-500 hover:border-emerald-500/50"
                      )}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={handleAutoFillNutrients}
              disabled={isAutoFillingNutrients || !manualMealName.trim()}
              className={cn(
                "w-full py-3 px-4 rounded-2xl font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all border shadow-sm",
                !manualMealName.trim()
                  ? (theme === 'light'
                      ? "bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed"
                      : "bg-[var(--card-bg)]/40 border-[var(--card-border)]/50 text-zinc-600 cursor-not-allowed")
                  : (theme === 'light'
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100/70"
                      : "bg-emerald-950/20 border-emerald-900/30 text-emerald-400 hover:bg-emerald-900/20")
              )}
            >
              {isAutoFillingNutrients ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                  <span>{lang === 'ua' ? 'Розрахунок нутрієнтів...' : 'Calculating Macros...'}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-emerald-500" />
                  <span>{lang === 'ua' ? '✨ Розрахувати макроси з ШІ' : '✨ Estimate Macros with AI'}</span>
                </>
              )}
            </motion.button>

            <div className="space-y-2">
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">
                {lang === 'ua' ? 'Нотатки до страви' : 'Meal Notes'}
              </label>
              <textarea
                value={manualMealNotes}
                onChange={(e) => setManualMealNotes(e.target.value)}
                placeholder={lang === 'ua' ? 'Додайте власні нагадування або контекст...' : 'Add custom reminders or context about this meal...'}
                className="w-full h-20 bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none shadow-sm text-sm"
              />
            </div>
          </div>

          {manualInsight && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/20 rounded-2xl flex flex-col"
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-emerald-500" />
                <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-widest">
                  {t.nutritionalInsight}
                </span>
              </div>
              <p className="text-sm text-zinc-800 dark:text-zinc-200 italic mb-1">
                {cleanInsightQuotes(manualInsight)}
              </p>
              <button
                type="button"
                onClick={() => {
                  const prompt = lang === 'ua' ? `Давай обговоримо ${manualMealName || 'мою їжу'}.` : `Let's discuss my ${manualMealName || 'meal'}.`;
                  setChatInput(prompt);
                  setView('chat');
                }}
                className="self-end flex items-center gap-1 group/ai mt-1"
              >
                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest group-hover/ai:text-emerald-500 transition-colors italic">
                  {t.askAI || 'Ask AI'}
                </span>
                <Sparkles className="w-3 h-3 text-emerald-500 group-hover/ai:scale-110 transition-transform" />
              </button>
            </motion.div>
          )}

          {manualPortionAnalysis && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 p-4 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-2xl flex flex-col"
            >
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-4 h-4 text-amber-500" />
                <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-widest">
                  {t.portionAnalysis}
                </span>
              </div>
              <p className="text-sm text-zinc-800 dark:text-zinc-200 italic">
                {cleanInsightQuotes(manualPortionAnalysis)}
              </p>
            </motion.div>
          )}

          <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-100 uppercase tracking-normal">
                {t.enterNutrients}
              </h3>
              {isAutoFillingNutrients && (
                <div className="flex items-center gap-2 text-[10px] font-bold text-emerald-500 uppercase tracking-widest">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Auto-filling...
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">
                  <Flame className="w-3 h-3 text-emerald-500" />
                  {t.calories}
                </label>
                <input
                  type="number"
                  value={manualCalories}
                  onChange={(e) => setManualCalories(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">
                  <Dumbbell className="w-3 h-3 text-blue-500" />
                  {t.protein} (g)
                </label>
                <input
                  type="number"
                  value={manualProtein}
                  onChange={(e) => setManualProtein(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">
                  <Apple className="w-3 h-3 text-red-500" />
                  {t.carbs} (g)
                </label>
                <input
                  type="number"
                  value={manualCarbs}
                  onChange={(e) => setManualCarbs(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">
                  <Droplets className="w-3 h-3 text-amber-500" />
                  {t.fat} (g)
                </label>
                <input
                  type="number"
                  value={manualFat}
                  onChange={(e) => setManualFat(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">
                  <Candy className="w-3 h-3 text-pink-500" />
                  {t.sugar} (g)
                </label>
                <input
                  type="number"
                  value={manualSugar}
                  onChange={(e) => setManualSugar(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">
                  <Wheat className="w-3 h-3 text-emerald-500" />
                  {t.fiber} (g)
                </label>
                <input
                  type="number"
                  value={manualFiber}
                  onChange={(e) => setManualFiber(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">
                  <CircleDot className="w-3 h-3 text-indigo-500" />
                  {t.sodium} (mg)
                </label>
                <input
                  type="number"
                  value={manualSodium}
                  onChange={(e) => setManualSodium(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[var(--input-bg)]/60 backdrop-blur-md border border-[var(--card-border)]/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
                />
              </div>
            </div>
          </div>
          </div>
        </div>

        <div className="flex gap-4">
          <motion.button
            whileTap={{ scale: 0.95 }}
            type="button"
            onClick={handleBack}
            className="flex-1 bg-[var(--card-bg)] border border-[var(--card-border)] py-4 rounded-2xl font-bold text-lg uppercase tracking-widest hover:bg-[var(--input-bg)] transition-all text-[var(--text)]"
          >
            {t.cancel}
          </motion.button>
          <motion.button
            animate={isManualLoggedClicked ? {
              scale: [1, 1.15, 0.85, 1.1, 0.95, 1.02, 1],
            } : { scale: 1 }}
            transition={isManualLoggedClicked ? {
              duration: 0.6,
              ease: "easeInOut"
            } : { duration: 0.2 }}
            onClick={() => {
              setIsManualLoggedClicked(true);
              setTimeout(() => setIsManualLoggedClicked(false), 600);
            }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            className="flex-[2] bg-emerald-500 py-4 rounded-2xl font-bold text-lg uppercase tracking-widest hover:bg-emerald-600 transition-all text-white shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-3 btn-active-border cursor-pointer"
          >
            <CheckCircle2 className="w-6 h-6" />
            {t.logThisMeal}
          </motion.button>
        </div>

        <DailyProgressSummary 
          totals={selectedDayTotals} 
          projectedTotals={projectedTotals} 
          goals={goals} 
          title={t.updatedProgress} 
          selectedDate={selectedDate}
          lang={lang}
          theme={theme}
          t={t}
          illumination={illumination}
          onNutrientClick={handleNutrientClick}
          viewMode={dailyProgressViewMode} skin={skin}
        />
      </form>
    </motion.div>
    );
  };
  const renderSkinsView = () => (
    <motion.div
      id="skins-section"
      className="space-y-8 scroll-mt-24"
    >
      <div className="flex items-center gap-4 px-2">
        <button 
          onClick={handleBack}
          className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <Palette className="w-6 h-6 text-emerald-500" />
          <h2 className={cn(
            "text-2xl font-bold uppercase tracking-normal",
            theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
          )}>{t.skins || 'SKINS'}</h2>
        </div>
      </div>

      <div className="space-y-6">
        {/* Anime Skin */}
        <div 
          onClick={() => handleSkinChange('anime')}
          className={cn(
            "relative overflow-hidden rounded-3xl border-2 cursor-pointer transition-all duration-300 group",
            skin === 'anime' ? "border-pink-400 shadow-[0_0_35px_rgba(244,114,182,0.55)] border-4 scale-[1.01]" : "border-[var(--card-border)] hover:border-pink-400/60 opacity-80 hover:opacity-100"
          )}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/30 via-purple-600/30 to-indigo-900/40 z-0"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1578632767115-351597cf2477?w=800&auto=format&fit=crop&q=80')] bg-cover bg-center opacity-35 mix-blend-overlay group-hover:opacity-55 transition-opacity"></div>
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-pink-400 to-transparent opacity-80"></div>
          {skin === 'anime' && (
            <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5">
              <span className="bg-gradient-to-r from-pink-500 to-purple-600 text-white text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest shadow-md flex items-center gap-1">
                <span>{lang === 'ua' ? "ОБРАНО" : "ACTIVE"}</span>
                <span>🌸</span>
              </span>
            </div>
          )}
          <div className="relative z-10 p-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-2xl font-black italic tracking-wider text-pink-400 drop-shadow-[0_2px_10px_rgba(236,72,153,0.5)]">ANIME EDITION</h3>
                <span className="text-[10px] font-bold bg-pink-500/20 text-pink-300 border border-pink-400/40 px-2 py-0.5 rounded-full uppercase">『超アニメ』</span>
              </div>
              <p className="text-sm text-pink-100/90 font-medium mt-1">Sakura Blossoms, Shonen Quests & Kawaii Spirit ✨</p>
            </div>
            {skin === 'anime' && <CheckCircle2 className="w-8 h-8 text-pink-400 drop-shadow-[0_0_10px_rgba(236,72,153,0.8)]" />}
          </div>
        </div>

        {/* Cyberpunk Skin */}
        <div 
          onClick={() => handleSkinChange('cyberpunk')}
          className={cn(
            "relative overflow-hidden rounded-3xl border-2 cursor-pointer transition-all duration-300 group",
            skin === 'cyberpunk' 
              ? (cyberpunkTone === 'cyan-magenta' ? "border-[#00f0ff] shadow-[0_0_35px_rgba(0,240,255,0.45)] border-4 scale-[1.01]" : "border-[#FCEE0A] shadow-[0_0_35px_rgba(252,238,10,0.5)] border-4 scale-[1.01]")
              : "border-[var(--card-border)] hover:border-[#FCEE0A]/60 opacity-80 hover:opacity-100"
          )}
        >
          <div className="absolute inset-0 bg-[#050510] z-0"></div>
          <div className="absolute inset-0 bg-[url('https://picsum.photos/seed/cyberpunk-bg/800/400')] bg-cover bg-center opacity-35 mix-blend-luminosity group-hover:opacity-55 transition-opacity"></div>
          <div className={cn(
            "absolute top-0 left-0 right-0 h-[2px] opacity-80",
            cyberpunkTone === 'cyan-magenta' && skin === 'cyberpunk'
              ? "bg-gradient-to-r from-transparent via-[#00f0ff] to-transparent"
              : "bg-gradient-to-r from-transparent via-[#FCEE0A] to-transparent"
          )}></div>
          {skin === 'cyberpunk' && (
            <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5">
              <span className={cn(
                "text-black text-[10px] font-black px-2.5 py-1 uppercase tracking-widest shadow-md font-mono",
                cyberpunkTone === 'cyan-magenta' ? "bg-[#00f0ff]" : "bg-[#FCEE0A]"
              )}>
                {lang === 'ua' ? "ОБРАНО // CP" : "CYBERPUNK // ACTIVE"}
              </span>
            </div>
          )}
          <div className="relative z-10 p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className={cn(
                    "text-2xl font-black tracking-widest font-mono",
                    cyberpunkTone === 'cyan-magenta' && skin === 'cyberpunk'
                      ? "text-[#00f0ff] drop-shadow-[0_0_10px_rgba(0,240,255,0.6)]"
                      : "text-[#FCEE0A] drop-shadow-[0_0_10px_rgba(252,238,10,0.6)]"
                  )}>
                    CYBERPUNK
                  </h3>
                  <span className={cn(
                    "text-[10px] font-mono font-bold border px-1.5 py-0.5 uppercase",
                    cyberpunkTone === 'cyan-magenta' && skin === 'cyberpunk'
                      ? "bg-[#ff0055]/15 text-[#ff0055] border-[#ff0055]/40"
                      : "bg-[#00ffcc]/15 text-[#00ffcc] border-[#00ffcc]/40"
                  )}>
                    RELIC OS
                  </span>
                </div>
                <p className="text-sm text-zinc-300 dark:text-zinc-400 font-medium mt-1 font-mono">
                  {cyberpunkTone === 'cyan-magenta' && skin === 'cyberpunk' 
                    ? (lang === 'ua' ? "Night City Matrix. Електричний Ціан & Неонова Маджента." : "Night City Matrix. Electric Cyan & Neon Magenta HUD.")
                    : (lang === 'ua' ? "Night City Neural Link. Неоновий Жовтий & Ціан." : "Night City Neural Link. Neon Yellow & Cyan HUD.")}
                </p>
              </div>
              {skin === 'cyberpunk' && (
                <CheckCircle2 className={cn(
                  "w-8 h-8",
                  cyberpunkTone === 'cyan-magenta' ? "text-[#00f0ff] drop-shadow-[0_0_8px_rgba(0,240,255,0.8)]" : "text-[#FCEE0A] drop-shadow-[0_0_8px_rgba(252,238,10,0.8)]"
                )} />
              )}
            </div>

            {/* Color Tone Selector */}
            <div 
              onClick={(e) => e.stopPropagation()} 
              className="mt-2 pt-3 border-t border-zinc-800/80 flex flex-wrap items-center justify-between gap-3"
            >
              <span className="text-[10px] font-mono font-bold tracking-widest text-zinc-400 uppercase">
                {lang === 'ua' ? "ПАЛІТРА КОЛЬОРІВ:" : "COLOR TONE:"}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleCyberpunkToneChange('yellow-cyan')}
                  className={cn(
                    "px-2.5 py-1.5 text-[11px] font-mono font-bold rounded flex items-center gap-1.5 transition-all cursor-pointer",
                    cyberpunkTone === 'yellow-cyan'
                      ? "bg-[#FCEE0A] text-black ring-2 ring-[#FCEE0A]/50 shadow-[0_0_12px_rgba(252,238,10,0.4)]"
                      : "bg-zinc-900/80 text-zinc-300 border border-zinc-700 hover:border-[#FCEE0A]/50"
                  )}
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FCEE0A] inline-block border border-black/30"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#00ffcc] inline-block border border-black/30 -ml-1"></span>
                  <span>{lang === 'ua' ? "Поточний (Жовтий)" : "Current (Yellow)"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleCyberpunkToneChange('cyan-magenta')}
                  className={cn(
                    "px-2.5 py-1.5 text-[11px] font-mono font-bold rounded flex items-center gap-1.5 transition-all cursor-pointer",
                    cyberpunkTone === 'cyan-magenta'
                      ? "bg-[#00f0ff] text-black ring-2 ring-[#00f0ff]/50 shadow-[0_0_12px_rgba(0,240,255,0.4)]"
                      : "bg-zinc-900/80 text-zinc-300 border border-zinc-700 hover:border-[#00f0ff]/50"
                  )}
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-[#00f0ff] inline-block border border-black/30"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff0055] inline-block border border-black/30 -ml-1"></span>
                  <span>{lang === 'ua' ? "Попередній (Ціан/Рожевий)" : "Previous (Cyan/Pink)"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* S.T.A.L.K.E.R. Skin */}
        <div 
          onClick={() => handleSkinChange('stalker')}
          className={cn(
            "relative overflow-hidden rounded-3xl border-2 cursor-pointer transition-all duration-300 group",
            skin === 'stalker' ? "border-[#4ade80] shadow-[0_0_35px_rgba(74,222,128,0.45)] border-4 scale-[1.01]" : "border-[var(--card-border)] hover:border-[#4ade80]/50 opacity-80 hover:opacity-100"
          )}
        >
          <div className="absolute inset-0 bg-black z-0"></div>
          <div className="absolute inset-0 bg-[url('https://picsum.photos/seed/stalker-bg/800/400')] bg-cover bg-center opacity-35 mix-blend-multiply group-hover:opacity-55 transition-opacity"></div>
          {skin === 'stalker' && (
            <div className="absolute top-4 right-4 z-20">
              <span className="bg-[#4ade80] text-black text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest shadow-md">{lang === 'ua' ? "ОБРАНО" : "SELECTED"}</span>
            </div>
          )}
          <div className="relative z-10 p-6 flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-black tracking-widest text-[#4ade80] drop-shadow-md font-mono">S.T.A.L.K.E.R.</h3>
              <p className="text-sm text-zinc-300 dark:text-zinc-400 font-medium mt-1 font-mono">Survive the Zone. Track your rations.</p>
            </div>
            {skin === 'stalker' && <CheckCircle2 className="w-8 h-8 text-[#4ade80]" />}
          </div>
        </div>
        
        {/* Default Skin */}
        <div 
          onClick={() => handleSkinChange('default')}
          className={cn(
            "relative overflow-hidden rounded-3xl border-2 cursor-pointer transition-all duration-300 group",
            skin === 'default' ? "border-emerald-500 shadow-[0_0_35px_rgba(16,185,129,0.35)] border-4 scale-[1.01]" : "border-[var(--card-border)] hover:border-emerald-500/50 opacity-80 hover:opacity-100"
          )}
        >
          <div className="absolute inset-0 bg-[var(--card-bg)] z-0"></div>
          {skin === 'default' && (
            <div className="absolute top-4 right-4 z-20">
              <span className="bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest shadow-md">{lang === 'ua' ? "ОБРАНО" : "SELECTED"}</span>
            </div>
          )}
          <div className="relative z-10 p-6 flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-black tracking-wide text-[var(--text)]">DEFAULT</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 font-medium mt-1">Clean & Minimal</p>
            </div>
            {skin === 'default' && <CheckCircle2 className="w-8 h-8 text-emerald-500" />}
          </div>
        </div>
      </div>
    </motion.div>
  );
  const renderFavoritesView = () => {
    const filteredFavorites = favorites.filter(meal => 
      (meal.meal_name || '').toLowerCase().includes((favoritesSearchQuery || '').toLowerCase()) ||
      meal.foods.some(f => (f.name || '').toLowerCase().includes((favoritesSearchQuery || '').toLowerCase()))
    );

    return (
      <motion.div
        id="favorites-section"
        className="space-y-6 scroll-mt-24"
      >
        <div className="flex items-center gap-4 px-2">
          <button 
            onClick={handleBack}
            className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-emerald-500" />
            <h2 className={cn(
              "text-2xl font-bold uppercase tracking-normal",
              theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
            )}>{vibe === 'casual' ? (lang !== 'ua' ? 'Besties' : 'Улюблені страви') : (lang !== 'ua' ? 'Favorites' : 'Улюблені страви')}</h2>
          </div>
        </div>

        {favorites.length > 0 && (
          <div className="px-2 relative">
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 group-focus-within:text-emerald-500 transition-colors" />
              <input
                type="text"
                value={favoritesSearchQuery}
                onChange={(e) => setFavoritesSearchQuery(e.target.value)}
                onFocus={() => setIsFavoritesSearchFocused(true)}
                onBlur={() => setTimeout(() => setIsFavoritesSearchFocused(false), 200)}
                placeholder={lang !== 'ua' ? "Search favorite meals..." : "Пошук улюблених страв..."}
                className="w-full bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl py-4 pl-12 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all card-shadow"
              />
              {favoritesSearchQuery && (
                <button 
                  onClick={() => setFavoritesSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              )}
            </div>
            {isFavoritesSearchFocused && activeFavoritesSuggestions.length > 0 && (
              <div className="absolute left-2 right-2 mt-1 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-xl z-50 overflow-hidden divide-y divide-[var(--card-border)] max-h-60 overflow-y-auto">
                {activeFavoritesSuggestions.map((item, idx) => (
                  <div
                    key={idx}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setFavoritesSearchQuery(item.text);
                      setIsFavoritesSearchFocused(false);
                      sounds.playPop();
                    }}
                    className="flex items-center justify-between px-4 py-2.5 text-xs font-medium cursor-pointer transition-colors hover:bg-[var(--input-bg)] text-zinc-300 hover:text-white"
                  >
                    <div className="flex items-center gap-2">
                      {item.type === 'food' ? (
                        <Utensils className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <ClipboardList className="w-3.5 h-3.5 text-blue-500" />
                      )}
                      <span>{item.text}</span>
                    </div>
                    <span className="text-[10px] text-zinc-500">
                      {item.frequency}x
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {favorites.length === 0 ? (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-12 text-center space-y-4 card-shadow mx-2">
            <div className="bg-[var(--card-bg)] w-16 h-16 rounded-full flex items-center justify-center mx-auto border border-[var(--card-border)]">
              <Heart className="w-8 h-8 text-zinc-500" />
            </div>
            <div>
              <p className="text-lg font-medium">{lang !== 'ua' ? 'No favorite meals yet' : 'Поки немає улюблених страв'}</p>
              <p className="text-sm text-zinc-500">{lang !== 'ua' ? 'Save your favorite meals to reuse them later!' : 'Зберігайте улюблені страви, щоб використовувати їх пізніше!'}</p>
            </div>
          </div>
        ) : filteredFavorites.length === 0 ? (
          <div className="text-center py-12 text-zinc-500 italic">
            {lang !== 'ua' ? 'No matches found' : 'Нічого не знайдено'}
          </div>
        ) : (
          <div className="space-y-10 px-2">
            {filteredFavorites.map((meal, index) => (
              <div 
                key={meal.id || index}
                className={cn(
                  "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 flex items-center justify-between gap-4 card-shadow group relative overflow-hidden transition-all",
                  illumination && "illumination-food"
                )}
              >
                <div 
                  className="flex-1 flex items-center gap-4 cursor-pointer min-w-0"
                  onClick={() => {
                    if (isChatPicking) {
                      handleMealPickForChat(meal);
                    } else {
                      setResult(meal);
                      setOriginalResult(safeClone(meal));
                      setImage(meal.image || '');
                      setView('analyze');
                      setIsLogged(false);
                    }
                  }}
                >
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-[var(--card-bg)] flex-shrink-0 border border-[var(--card-border)]">
                    {meal.image ? (
                      <img src={meal.image} alt={meal.meal_name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl bg-zinc-50 dark:bg-zinc-800">
                        {getFoodEmoji(meal.meal_name)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-grow min-w-0">
                    <div className="flex flex-col mb-1.5">
                      <div className="flex items-center gap-2 mb-0.5">
                                                {(() => {
                          const badge = getPrimaryMacroBadge(meal.total_nutrition);
                          return badge ? (
                            <motion.span 
                              initial={{ opacity: 0, scale: 0.85, y: 2 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              transition={{ duration: 0.35, ease: "easeOut" }}
                              className={cn("text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0", badge.class)}
                            >
                              {badge.label}
                            </motion.span>
                          ) : null;
                        })()}
                        {meal.meal_type && (
                          <span className={cn(
                            "text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0",
                            meal.meal_type === 'breakfast' && "bg-amber-500/10 text-amber-500 border-amber-500/20",
                            meal.meal_type === 'lunch' && "bg-blue-500/10 text-blue-500 border-blue-500/20",
                            meal.meal_type === 'dinner' && "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
                            meal.meal_type === 'snack' && "bg-rose-500/10 text-rose-500 border-rose-500/20"
                          )}>
                            {t[meal.meal_type as keyof typeof t] || meal.meal_type}
                          </span>
                        )}
                        <div className="flex items-center gap-1.5 ml-1.5">
                          {Array.from(new Set(meal.foods?.map(f => f.category?.toLowerCase()).filter(Boolean) || []))?.map((cat, idx) => (
                            <motion.span 
                              key={idx} 
                              className="text-[10px] sm:text-xs inline-block" 
                              title={String(cat)}
                              initial={{ scale: 0.3, y: 8 }}
                              animate={{ scale: 1, y: 0 }}
                              transition={{ 
                                type: "spring",
                                stiffness: 220,
                                damping: 12,
                                delay: idx * 0.08
                              }}
                              whileHover={{ scale: 1.3, rotate: 12 }}
                            >
                              {FOOD_CATEGORIES.find(c => c.id === cat)?.icon || '🍴'}
                            </motion.span>
                          ))}
                        </div>
                      </div>
                      <h3 className="font-bold text-lg leading-tight text-[var(--text)] line-clamp-2 group-hover:text-emerald-500 transition-colors">{meal.meal_name}</h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-emerald-500 font-bold text-sm">{Math.round(meal.total_nutrition?.calories_kcal || 0)} {t.kcal}</span>
                      <div className="flex items-center gap-2 text-[10px] text-zinc-500 font-medium">
                        <span className="flex items-center gap-1">
                          <div className="w-1 h-1 rounded-full bg-blue-500" />
                          {(meal.total_nutrition?.protein_g || 0).toFixed(1)}{t.proteinShort}
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-1 h-1 rounded-full bg-red-500" />
                          {(meal.total_nutrition?.carbs_g || 0).toFixed(1)}{t.carbsShort}
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-1 h-1 rounded-full bg-amber-500" />
                          {(meal.total_nutrition?.fat_g || 0).toFixed(1)}{t.fatShort || 'f'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 border-l border-zinc-100 dark:border-zinc-800 pl-3 py-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      saveToLogFromFavorites(meal);
                    }}
                    className="w-10 h-10 flex items-center justify-center bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white rounded-2xl transition-all shadow-sm active:scale-90"
                    title={lang.startsWith('en') ? "Quick Log" : "Швидкий запис"}
                  >
                    <Plus className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const realIndex = favorites.findIndex(f => f.id === meal.id);
                      removeFromFavorites(realIndex !== -1 ? realIndex : index);
                    }}
                    className="w-10 h-10 flex items-center justify-center bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white rounded-2xl transition-all shadow-sm active:scale-90"
                    title={lang.startsWith('en') ? "Remove" : "Видалити"}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    );
  };
  const renderHistorySkeleton = () => {
    return (
      <div className="space-y-6 px-2 mt-4 animate-pulse">
        {[1, 2].map((groupKey) => (
          <div key={groupKey} className="space-y-4">
            {/* Group Header Skeleton */}
            <div className="flex flex-col gap-2 pb-2 mb-[14px]">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
                <div className="w-32 h-5 bg-zinc-200 dark:bg-zinc-800 rounded" />
              </div>
              <div className="flex gap-3 mt-1">
                <div className="w-16 h-4 bg-zinc-200 dark:bg-zinc-800 rounded" />
                <div className="w-12 h-4 bg-zinc-200 dark:bg-zinc-800 rounded" />
                <div className="w-12 h-4 bg-zinc-200 dark:bg-zinc-800 rounded" />
                <div className="w-12 h-4 bg-zinc-200 dark:bg-zinc-800 rounded" />
              </div>
            </div>
            {/* Group Items Skeletons */}
            <div className="space-y-5">
              {[1, 2].map((itemKey) => (
                <div 
                  key={itemKey}
                  className={cn(
                    "relative bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4 card-shadow",
                    illumination && "illumination-food"
                  )}
                >
                  {/* Image Placeholder */}
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-zinc-200 dark:bg-zinc-800 flex-shrink-0" />
                  {/* Text Content Placeholder */}
                  <div className="flex-grow min-w-0 space-y-2">
                    <div className="w-1/2 h-4 bg-zinc-200 dark:bg-zinc-800 rounded" />
                    <div className="w-1/3 h-3 bg-zinc-150 dark:bg-zinc-800 rounded" />
                  </div>
                  {/* Calories Placeholder */}
                  <div className="w-16 h-5 bg-zinc-200 dark:bg-zinc-800 rounded flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };
  const renderGallerySkeleton = () => {
    return (
      <div className="space-y-10 px-2 animate-pulse">
        {[1, 2].map((groupKey) => (
          <div key={groupKey} className="space-y-4">
            {/* Group Header Skeleton */}
            <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 pb-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
                <div className="w-32 h-4 bg-zinc-200 dark:bg-zinc-800 rounded" />
              </div>
              <div className="flex gap-3">
                <div className="w-16 h-4 bg-zinc-200 dark:bg-zinc-800 rounded" />
                <div className="w-16 h-4 bg-zinc-200 dark:bg-zinc-800 rounded" />
              </div>
            </div>
            {/* Grid of Items Skeletons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-10">
              {[1, 2, 3, 4].map((itemKey) => (
                <div key={itemKey} className="flex flex-col gap-2">
                  <div className={cn(
                    "aspect-square rounded-2xl bg-zinc-200 dark:bg-zinc-800 card-shadow",
                    illumination && "illumination-food"
                  )} />
                  <div className="px-1 space-y-2">
                    <div className="w-2/3 h-3.5 bg-zinc-200 dark:bg-zinc-800 rounded" />
                    <div className="flex items-center justify-between">
                      <div className="w-1/3 h-3 bg-zinc-150 dark:bg-zinc-800 rounded" />
                      <div className="w-8 h-3 bg-zinc-100 dark:bg-zinc-800 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };
  const renderGalleryView = () => {
    return (
      <motion.div
        id="gallery-section"
        className="space-y-8 scroll-mt-24"
      >
        <div className="flex items-center gap-4 px-2">
          <button 
            onClick={handleBack}
            className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Image className="w-6 h-6 text-emerald-500" />
            <h2 className={cn(
              "text-2xl font-bold uppercase tracking-normal",
              theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
            )}>{t.mealGallery}</h2>
          </div>
        </div>

        <div className="px-2 space-y-10">
          <div className={cn(
            "rounded-3xl p-4 card-shadow space-y-4",
            theme === 'light' ? "bg-white border border-zinc-100" : "bg-zinc-900 border border-zinc-800",
            illumination && "illumination-food"
          )}>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between pb-1">
                <button 
                  onClick={() => setIsGalleryTagFilterOpen(!isGalleryTagFilterOpen)}
                  className="flex items-center gap-2 text-zinc-500 hover:text-emerald-500 transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  <span className="text-[10px] font-bold uppercase tracking-widest">
                    {isGalleryTagFilterOpen ? t.hideTagFilter : t.showTagFilter}
                  </span>
                  <motion.div
                    animate={{ rotate: isGalleryTagFilterOpen ? 180 : 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <ChevronDown className="w-3 h-3" />
                  </motion.div>
                </button>

                <button 
                  onClick={() => setIsGalleryFiltersOpen(!isGalleryFiltersOpen)}
                  className="flex items-center gap-2 text-zinc-500 hover:text-emerald-500 transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  <span className="text-[10px] font-bold uppercase tracking-widest">
                    {isGalleryFiltersOpen ? t.hideDateFilters : t.showDateFilters}
                  </span>
                  <motion.div
                    animate={{ rotate: isGalleryFiltersOpen ? 180 : 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <ChevronDown className="w-3 h-3" />
                  </motion.div>
                </button>
              </div>

              {(galleryMealTypeFilter !== 'all' || galleryStartDate || galleryEndDate) && (
                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      setGalleryMealTypeFilter('all');
                      setGalleryStartDate('');
                      setGalleryEndDate('');
                    }}
                    className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest hover:opacity-80 transition-opacity flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    {t.reset}
                  </button>
                </div>
              )}

              <AnimatePresence>
                {isGalleryTagFilterOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-col gap-4 pt-2">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {(['all', 'breakfast', 'lunch', 'dinner', 'snack'] as const).map((type) => (
                            <button
                              key={type}
                              onClick={() => setGalleryMealTypeFilter(type)}
                              className={cn(
                                "px-4 py-2 rounded-2xl text-xs font-bold transition-all uppercase tracking-widest whitespace-nowrap",
                                galleryMealTypeFilter === type
                                  ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                                  : theme === 'light' 
                                    ? "bg-zinc-50 text-zinc-600 hover:bg-zinc-100 border border-zinc-200/50" 
                                    : "bg-zinc-800 text-zinc-500 hover:bg-zinc-700"
                              )}
                            >
                              {type === 'all' ? t.all : t[type]}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {isGalleryFiltersOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="space-y-1">
                        <input
                          type="date"
                          value={galleryStartDate}
                          onChange={(e) => setGalleryStartDate(e.target.value)}
                          placeholder={t.startDate}
                          className={cn(
                            "w-full rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 transition-all outline-none border",
                            theme === 'light' ? "bg-zinc-50 border-zinc-200 text-zinc-900" : "bg-zinc-800 border-zinc-700 text-white"
                          )}
                        />
                      </div>
                      <div className="space-y-1">
                        <input
                          type="date"
                          value={galleryEndDate}
                          onChange={(e) => setGalleryEndDate(e.target.value)}
                          placeholder={t.endDate}
                          className={cn(
                            "w-full rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 transition-all outline-none border",
                            theme === 'light' ? "bg-zinc-50 border-zinc-200 text-zinc-900" : "bg-zinc-800 border-zinc-700 text-white"
                          )}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {isHistoryLoading ? (
            <motion.div
              key="gallery-skeleton-container"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderGallerySkeleton()}
            </motion.div>
          ) : groupedGallery.length === 0 ? (
            <motion.div
              key="empty-gallery"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className={cn(
                "bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-12 text-center space-y-4 card-shadow mx-2",
                illumination && "illumination-food"
              )}
            >
              <div className="bg-white dark:bg-zinc-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                <Search className="w-8 h-8 text-zinc-500 dark:text-zinc-400" />
              </div>
              <div>
                <p className="text-lg font-medium">
                  {galleryMealTypeFilter !== 'all' || galleryStartDate || galleryEndDate
                    ? (lang.startsWith('en') ? 'No matches found' : 'Нічого не знайдено')
                    : (lang.startsWith('en') ? 'No meals yet' : 'Ще немає страв')}
                </p>
                <p className="text-sm text-zinc-500">
                  {galleryMealTypeFilter !== 'all' || galleryStartDate || galleryEndDate
                    ? (lang.startsWith('en') ? 'Try adjusting your filters' : 'Спробуйте змінити фільтри')
                    : (lang.startsWith('en') ? 'Log your first meal to see it here!' : 'Запишіть свою першу страву, щоб побачити її тут!')}
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="gallery-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-10 px-2"
            >
              <AnimatePresence mode="popLayout">
                {groupedGallery.map(([date, { meals, totalCalories, totalWater }]) => (
                  <motion.div
                    layout
                    key={date}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.25 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 pb-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-emerald-500" />
                        <h3 className={cn(
                          "font-bold uppercase tracking-normal text-sm",
                          theme === 'light' ? "text-zinc-600" : "text-zinc-100"
                        )}>{date}</h3>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <Flame className="w-3.5 h-3.5 text-orange-500" />
                          <span className="text-[10px] font-black tracking-tight text-zinc-500">{Math.round(totalCalories)} {t.kcal}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Droplets className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-[10px] font-black tracking-tight text-zinc-500">{Math.round(totalWater)} {t.ml}</span>
                        </div>
                      </div>
                    </div>
                    <motion.div layout className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-10">
                      <AnimatePresence mode="popLayout">
                        {meals.map((item) => (
                          <motion.div
                            layout
                            key={item.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.25 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              if (isChatPicking) {
                                handleMealPickForChat(item);
                              } else {
                                if (item.image) {
                                  setFullScreenImage(item);
                                } else {
                                  setSelectedHistoryItem(item);
                                  setView('details');
                                }
                              }
                            }}
                            className="flex flex-col gap-2 group cursor-pointer"
                          >
                            <div className={cn(
                              "aspect-square rounded-2xl overflow-hidden card-shadow relative flex items-center justify-center",
                              theme === 'light' ? "bg-white border-transparent" : "bg-zinc-900 border border-zinc-800",
                              illumination && "illumination-food"
                            )}>
                              {item.image ? (
                                <img src={item.image} alt={item.meal_name} className="w-full h-full object-cover" />
                              ) : (
                                <Utensils className="w-8 h-8 text-zinc-500 dark:text-zinc-400" />
                              )}
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2 text-center">
                                 <p className="text-white text-[10px] font-bold uppercase tracking-widest line-clamp-2">{item.meal_name}</p>
                                 <div className="mt-1 flex flex-col gap-0.5">
                                   <p className="text-emerald-400 text-[10px] font-bold">{Math.round(item.total_nutrition?.calories_kcal || 0)} {t.kcal}</p>
                                   <div className="flex items-center gap-1.5 text-[8px] text-white/80 font-bold">
                                     <span>P: {Math.round(item.total_nutrition?.protein_g || 0)}g</span>
                                     <span>C: {Math.round(item.total_nutrition?.carbs_g || 0)}g</span>
                                     <span>F: {Math.round(item.total_nutrition?.fat_g || 0)}g</span>
                                   </div>
                                 </div>
                              </div>
                            </div>
                            <div className="px-1">
                              <h4 className={cn(
                                "font-bold text-xs line-clamp-1",
                                theme === 'light' ? "text-zinc-600" : "text-zinc-100"
                              )}>{item.meal_name}</h4>
                              <div className="flex items-center justify-between mt-0.5">
                                <span className="text-[10px] font-bold text-emerald-500">{Math.round(item.total_nutrition?.calories_kcal || 0)} {t.kcal}</span>
                                <span className="text-[9px] text-zinc-500">{new Date(item.timestamp).toLocaleTimeString(lang === 'ua' ? 'uk-UA' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h' })}</span>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </motion.div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {fullScreenImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center p-4"
              onClick={() => setFullScreenImage(null)}
            >
              <div className="absolute top-6 right-6 flex items-center gap-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedHistoryItem(fullScreenImage);
                    setView('details');
                    setFullScreenImage(null);
                  }}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white p-2 rounded-full shadow-lg transition-colors"
                  title={lang.startsWith('en') ? 'View Details' : 'Детальніше'}
                >
                  <Info className="w-5 h-5" />
                </button>
                <button 
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors text-white"
                  onClick={(e) => { e.stopPropagation(); setFullScreenImage(null); }}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="w-full max-w-4xl max-h-[70vh] rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <img src={fullScreenImage.image} alt={fullScreenImage.meal_name} loading="lazy" decoding="async" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
              </div>

              <div className="mt-8 text-center space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-1">
                  <h3 className="text-2xl font-bold text-white">{fullScreenImage.meal_name}</h3>
                  <p className="text-zinc-500 dark:text-zinc-400 text-sm">{formatDateTime(new Date(fullScreenImage.timestamp), dateFormat, timeFormat, lang)}</p>
                </div>
                
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => {
                      setSelectedHistoryItem(fullScreenImage);
                      setView('details');
                      setFullScreenImage(null);
                    }}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-2xl font-bold uppercase tracking-widest transition-all shadow-lg shadow-emerald-500/20"
                  >
                    {lang.startsWith('en') ? 'View Details' : 'Детальніше'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };
  const renderNutrientBreakdownView = () => {
    if (!selectedNutrient) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayMeals = history.filter(item => {
      const itemDate = new Date(item.timestamp);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate.getTime() === today.getTime();
    });

    const todayWater = waterLogs.filter(item => {
      const itemDate = new Date(item.timestamp);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate.getTime() === today.getTime();
    });

    const totalValue = selectedNutrient.key === 'water' 
      ? todayWater.reduce((sum, w) => sum + w.amount_ml, 0)
      : selectedNutrient.key === 'calories'
        ? todayMeals.reduce((sum, m) => sum + (m.total_nutrition?.calories_kcal || 0), 0)
        : todayMeals.reduce((sum, m) => sum + (Number(m.total_nutrition?.[selectedNutrient.key as keyof TotalNutrition]) || 0), 0);

    const sortedMeals = [...todayMeals]
      .map(meal => {
        const val = selectedNutrient.key === 'calories'
          ? meal.total_nutrition?.calories_kcal || 0
          : Number(meal.total_nutrition?.[selectedNutrient.key as keyof TotalNutrition]) || 0;
        return { ...meal, _sortVal: val };
      })
      .filter(m => m._sortVal > 0)
      .sort((a, b) => nutrientSortOrder === 'desc' ? b._sortVal - a._sortVal : a._sortVal - b._sortVal);

    const sortedWater = [...todayWater]
      .sort((a, b) => nutrientSortOrder === 'desc' ? b.amount_ml - a.amount_ml : a.amount_ml - b.amount_ml);

    const unit = selectedNutrient.key === 'calories' ? t.kcal :
                 selectedNutrient.key === 'water' ? t.ml :
                 selectedNutrient.key.endsWith('_mg') ? 'mg' :
                 selectedNutrient.key.endsWith('_mcg') ? 'mcg' : 'g';

    const goalValue = selectedNutrient.key === 'calories' ? goals.calories :
                      selectedNutrient.key === 'water' ? goals.water_ml :
                      selectedNutrient.key === 'protein_g' ? goals.protein :
                      selectedNutrient.key === 'carbs_g' ? goals.carbs :
                      selectedNutrient.key === 'fat_g' ? goals.fat :
                      selectedNutrient.key === 'fiber_g' ? goals.fiber_g :
                      selectedNutrient.key === 'sugar_g' ? goals.sugar_g :
                      selectedNutrient.key === 'sodium_mg' ? goals.sodium_mg :
                      selectedNutrient.key === 'potassium_mg' ? 4700 :
                      selectedNutrient.key === 'calcium_mg' ? 1300 :
                      selectedNutrient.key === 'iron_mg' ? 18 :
                      selectedNutrient.key === 'vitamin_c_mg' ? 90 :
                      selectedNutrient.key === 'vitamin_a_mcg' ? 900 :
                      selectedNutrient.key === 'vitamin_d_mcg' ? 20 :
                      selectedNutrient.key === 'vitamin_b12_mcg' ? 2.4 :
                      selectedNutrient.key === 'magnesium_mg' ? 420 :
                      selectedNutrient.key === 'zinc_mg' ? 11 : 0;

    const isNutrient = ['protein_g', 'carbs_g', 'fat_g', 'fiber_g'].includes(selectedNutrient.key);
    const isSaltSugar = ['sugar_g', 'sodium_mg', 'caffeine_mg', 'potassium_mg', 'calcium_mg', 'iron_mg', 'magnesium_mg', 'zinc_mg', 'vitamin_c_mg', 'vitamin_a_mcg', 'vitamin_d_mcg', 'vitamin_b12_mcg'].includes(selectedNutrient.key);
    const colorClass = selectedNutrient.key === 'calories' ? 'bg-emerald-500' :
                       selectedNutrient.key === 'water' ? 'bg-blue-500' :
                       selectedNutrient.key === 'protein_g' ? 'bg-purple-500' :
                       selectedNutrient.key === 'carbs_g' ? 'bg-teal-500' :
                       selectedNutrient.key === 'fat_g' ? 'bg-orange-500' :
                       selectedNutrient.key === 'fiber_g' ? 'bg-emerald-500' :
                       selectedNutrient.key === 'sugar_g' ? 'bg-white border border-zinc-200 dark:border-zinc-700' :
                       selectedNutrient.key === 'sodium_mg' ? 'bg-zinc-200' :
                       ['potassium_mg', 'calcium_mg', 'iron_mg', 'magnesium_mg', 'zinc_mg', 'vitamin_c_mg', 'vitamin_a_mcg', 'vitamin_d_mcg', 'vitamin_b12_mcg'].includes(selectedNutrient.key) ? 'bg-amber-500' : 'bg-emerald-500';

    return (
      <motion.div
        className="space-y-8"
      >
        <div className="flex items-center gap-4 px-2">
          <button 
            onClick={handleBack}
            className="p-2 hover:bg-white dark:hover:bg-zinc-800 rounded-full transition-colors border border-transparent hover:border-zinc-100 dark:hover:border-zinc-700"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-emerald-500" />
            <h2 className={cn(
              "text-2xl font-bold uppercase tracking-normal",
              theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
            )}>{selectedNutrient.label}</h2>
          </div>
        </div>

        <div className={cn(
          "bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[32px] p-8 card-shadow text-center space-y-2",
          illumination && (selectedNutrient.key === 'water' ? "illumination-water" : "illumination-food")
        )}>
          <p className="text-sm font-bold text-zinc-500 uppercase tracking-widest">{t.totalToday}</p>
          <p className="text-5xl font-black text-emerald-500">
            {Math.round(totalValue)}
            <span className="text-xl ml-1 text-zinc-500 dark:text-zinc-400">{unit}</span>
          </p>
          
          <div className="pt-6 max-w-md mx-auto">
            <ProgressBar 
              label={selectedNutrient.label}
              current={selectedNutrient.key === 'sodium_mg' ? totalValue / 1000 : totalValue}
              goal={selectedNutrient.key === 'sodium_mg' ? goalValue / 1000 : goalValue}
              colorClass={colorClass}
              unit={selectedNutrient.key === 'sodium_mg' ? 'g' : unit}
              isNutrient={isNutrient}
              isSaltSugar={isSaltSugar}
              isCarbs={selectedNutrient.key === 'carbs_g'}
              isFat={selectedNutrient.key === 'fat_g'}
              lang={lang}
            />
          </div>
        </div>

        <div className="space-y-10 px-2">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-normal">{t.mealBreakdown}</h3>
            <button 
              onClick={() => setNutrientSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-emerald-500 transition-all border border-zinc-200 dark:border-zinc-700"
            >
              {nutrientSortOrder === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
              {lang.startsWith('en') ? 'Sort by %' : 'Сортувати за %'}
            </button>
          </div>
          
          {selectedNutrient.key === 'water' && sortedWater.map(log => (
            <div key={log.id} className={cn(
              "bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-4 flex items-center justify-between card-shadow",
              illumination && "illumination-water"
            )}>
              <div className="flex items-center gap-3">
                <div className={cn(
                  "p-2 rounded-xl",
                  log.beverage_type === 'coffee' ? "bg-amber-500/10" :
                  log.beverage_type === 'tea' ? "bg-emerald-500/10" :
                  "bg-blue-500/10"
                )}>
                  {log.beverage_type === 'coffee' ? <Coffee className="w-5 h-5 text-amber-500" /> :
                   log.beverage_type === 'tea' ? <Leaf className="w-5 h-5 text-emerald-500" /> :
                   <Droplets className="w-5 h-5 text-blue-500" />}
                </div>
                <div>
                  <p className="font-bold text-sm">
                    {log.beverage_type === 'coffee' ? (lang === 'ua' ? 'Кава' : 'Coffee') :
                     log.beverage_type === 'tea' ? (lang === 'ua' ? 'Чай' : 'Tea') :
                     t.water}
                  </p>
                  <p className="text-[10px] text-zinc-500 font-medium">{new Date(log.timestamp).toLocaleTimeString(lang === 'ua' ? 'uk-UA' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h' })}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={cn(
                  "font-bold",
                  log.beverage_type === 'coffee' ? "text-amber-500" :
                  log.beverage_type === 'tea' ? "text-emerald-500" :
                  "text-blue-500"
                )}>{log.amount_ml}{t.ml}</p>
                <p className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-widest">{totalValue > 0 ? Math.round((log.amount_ml / totalValue) * 100) : 0}%</p>
              </div>
            </div>
          ))}

          {sortedMeals.map(meal => {
            if (selectedNutrient.key === 'water') return null;
            
            const val = meal._sortVal;

            return (
              <motion.div 
                key={meal.id}
                whileHover={{ scale: 1.01 }}
                onClick={() => {
                  if (!isLongPressingMeal) {
                    setSelectedHistoryItem(meal);
                    setView('details');
                  }
                }}
                onTouchStart={(e) => handleMealTouchStart(e, meal.id)}
                onTouchEnd={handleMealTouchEnd}
                onTouchMove={handleMealTouchMove}
                onMouseDown={(e) => handleMealTouchStart(e, meal.id)}
                onMouseUp={handleMealTouchEnd}
                onMouseLeave={handleMealTouchMove}
                className={cn(
                  "bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-4 flex items-center justify-between card-shadow cursor-pointer group select-none",
                  illumination && "illumination-food"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-white dark:bg-zinc-800 flex items-center justify-center">
                    {meal.image ? (
                      <img src={meal.image} alt={meal.meal_name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xl bg-zinc-50 dark:bg-zinc-800">
                        {getFoodEmoji(meal.meal_name)}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-sm group-hover:text-emerald-500 transition-colors line-clamp-1">{meal.meal_name}</p>
                    <p className="text-[10px] text-zinc-500 font-medium">{new Date(meal.timestamp).toLocaleTimeString(lang === 'ua' ? 'uk-UA' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h' })}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-emerald-500">{Math.round(val)}{unit}</p>
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-widest">{totalValue > 0 ? Math.round((val / totalValue) * 100) : 0}%</p>
                </div>
              </motion.div>
            );
          })}

          {totalValue === 0 && (
            <div className="text-center py-12 text-zinc-500 italic">
              {lang.startsWith('en') ? 'No data for this nutrient today' : 'Сьогодні немає даних для цього нутрієнта'}
            </div>
          )}
        </div>
      </motion.div>
    );
  };
  const renderSkeletonResult = () => {
    const WORDS = [
      "PROTEIN", "CARBOHYDRATES", "FAT", "CALCIUM", "ZINC", "VITAMIN A", "VITAMIN C", 
      "VITAMIN D", "VITAMIN E", "VITAMIN K", "B12", "IRON", "MAGNESIUM", "POTASSIUM", 
      "FIBER", "SUGAR", "SODIUM", "CALORIES", "MACROS", "MICROS", "AMINO ACIDS", "OMEGA-3",
      "ANTIOXIDANTS", "CHOLESTEROL", "WATER", "ENERGY", "NUTRITION", "METABOLISM",
      "01001101", "01100001", "01110100", "01110010", "01101001", "01111000"
    ];

    return (
      <div className="w-full overflow-hidden bg-black rounded-3xl p-4 font-mono text-xs select-none card-shadow relative min-h-[50px] flex flex-col justify-center items-center">
        <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black z-10 pointer-events-none" />
        
        <div className="flex flex-col gap-3 w-full opacity-90">
          <motion.div
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 0.8, ease: "linear", repeat: Infinity }}
            className="whitespace-nowrap flex gap-4"
            style={{ 
              color: '#00FF41',
              textShadow: '0 0 5px rgba(0,255,65,0.8), 0 0 10px rgba(0,255,65,0.4)'
            }}
          >
            {[...Array(2)].map((_, i) => (
              <span key={i}>
                {Array(30).fill(0).map((_, j) => WORDS[(j * 7) % WORDS.length]).join("  ")}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    );
  };
  const renderAnalysisResult = (data: MealAnalysis) => {
    const historyItem = data as HistoryEntry;
    const mealImage = historyItem.image || data.image || (data.isSuggestion ? '' : image);
    const mealTimestamp = historyItem.timestamp || Date.now();
    const isHistory = !!historyItem.id && historyItem.id !== 'temp';
    const detailIlluminationType = ((data as any).type === 'water' || (data.foods && data.foods.length === 1 && (data.foods[0].category === 'drink' || /drink|water|coffee|tea/i.test(data.meal_name || '')))) ? 'water' : 'food';

    const computedTotalAmount = (() => {
      if (data.total_amount && data.total_amount.trim() !== '') return data.total_amount;
      if (!data.foods || data.foods.length === 0) return '';
      let gCount = 0;
      let mlCount = 0;
      let otherCount = 0;
      let units = '';
      data.foods.forEach(f => {
        const amt = parseFloat(String(f.amount || '0'));
        if (!isNaN(amt) && amt > 0) {
          const unit = (f.unit || '').toLowerCase();
          if (unit === 'g' || unit === 'gr' || unit === 'grams' || unit === 'г' || unit === 'гр') {
            gCount += amt;
          } else if (unit === 'ml' || unit === 'мл') {
            mlCount += amt;
          } else {
            otherCount += amt;
            units = f.unit || 'pcs';
          }
        }
      });
      if (gCount > 0 && mlCount > 0) return `${gCount}g, ${mlCount}ml`;
      if (gCount > 0) return `${gCount}g`;
      if (mlCount > 0) return `${mlCount}ml`;
      if (otherCount > 0) return `${otherCount} ${units}`;
      return data.foods[0]?.portion || '';
    })();

    // Auto-enrich micronutrients on-the-fly if missing or 0
    if (data && data.total_nutrition) {
      const hasMicros = [
        data.total_nutrition.potassium_mg,
        data.total_nutrition.cholesterol_mg,
        data.total_nutrition.vitamin_c_mg,
        data.total_nutrition.calcium_mg,
        data.total_nutrition.iron_mg,
        data.total_nutrition.vitamin_a_mcg,
        data.total_nutrition.vitamin_d_mcg,
        data.total_nutrition.vitamin_b12_mcg,
        data.total_nutrition.magnesium_mg,
        data.total_nutrition.zinc_mg
      ].some(val => val !== undefined && val > 0);

      if (!hasMicros) {
        enrichMealMicronutrients(data);
      }
    }

    const nutrition = data.total_nutrition || { 
      calories_kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, 
      sugar_g: 0, fiber_g: 0, sodium_mg: 0,
      potassium_mg: 0, cholesterol_mg: 0, vitamin_c_mg: 0, calcium_mg: 0, iron_mg: 0, caffeine_mg: 0, water_ml: 0
    };
    const isPremiumOrPro = subscriptionTier === 'premium' || subscriptionTier === 'pro' || subscriptionTier === 'premium_pro';
    const hasExtraNutrients = [
      nutrition.potassium_mg,
      nutrition.cholesterol_mg,
      nutrition.vitamin_c_mg,
      nutrition.calcium_mg,
      nutrition.iron_mg,
      nutrition.vitamin_a_mcg,
      nutrition.vitamin_d_mcg,
      nutrition.vitamin_b12_mcg,
      nutrition.magnesium_mg,
      nutrition.zinc_mg
    ].some(val => val !== undefined && val > 0) || (Array.isArray(data.foods) && data.foods.some(f => [
      f.potassium_mg, f.cholesterol_mg, f.vitamin_c_mg, f.calcium_mg, f.iron_mg,
      f.vitamin_a_mcg, f.vitamin_d_mcg, f.vitamin_b12_mcg, f.magnesium_mg, f.zinc_mg
    ].some(v => v !== undefined && v > 0)));
    const macroData = [
      { name: t.protein, value: (nutrition.protein_g || 0) * animatedMultiplier, color: skin === 'stalker' ? '#4ade80' : skin === 'anime' ? '#ec4899' : skin === 'cyberpunk' ? '#00ffcc' : '#a855f7' },
      { name: t.carbs, value: (nutrition.carbs_g || 0) * animatedMultiplier, color: skin === 'stalker' ? '#f97316' : skin === 'anime' ? '#a855f7' : skin === 'cyberpunk' ? '#FCEE0A' : '#ef4444' },
      { name: t.fat, value: (nutrition.fat_g || 0) * animatedMultiplier, color: skin === 'stalker' ? '#ef4444' : skin === 'anime' ? '#FFB300' : skin === 'cyberpunk' ? '#ff003c' : '#f59e0b' },
    ];
    const microData = [
      { name: t.sugar, value: (nutrition.sugar_g || 0) * animatedMultiplier, color: '#ec4899' },
      { name: t.fiber, value: (nutrition.fiber_g || 0) * animatedMultiplier, color: '#10b981' },
      { name: t.sodium || 'Salt', value: ((nutrition.sodium_mg || 0) / 1000) * animatedMultiplier, color: '#3b82f6' },
      { name: t.potassium || 'Potassium', value: ((nutrition.potassium_mg || 0) / 1000) * animatedMultiplier, color: '#8b5cf6' },
      { name: t.calcium || 'Calcium', value: ((nutrition.calcium_mg || 0) / 1000) * animatedMultiplier, color: '#f59e0b' },
      { name: t.iron || 'Iron', value: ((nutrition.iron_mg || 0) / 1000) * animatedMultiplier, color: '#ef4444' },
      { name: t.caffeine || 'Caffeine', value: ((nutrition.caffeine_mg || 0) / 1000) * animatedMultiplier, color: '#92400e' },
      { name: t.vitaminC || 'Vitamin C', value: ((nutrition.vitamin_c_mg || 0) / 1000) * animatedMultiplier, color: '#f97316' },
      { name: t.cholesterol || 'Cholesterol', value: ((nutrition.cholesterol_mg || 0) / 1000) * animatedMultiplier, color: '#d946ef' },
    ].filter(d => d.value > 0);

    const chartData = detailsChartType === 'macro' ? macroData : microData;

    const renderDeficiencyIndicator = (nutrientKey: string) => {
      if (!enableHealthDeficiencyAlert) return null;
      const alert = activeDeficiencies.find(a => a.nutrientKey === nutrientKey);
      if (!alert) return null;

      const avgIntakePercent = alert.daysCount > 0 
        ? Math.round(alert.intakePercentages.reduce((sum, p) => sum + p, 0) / alert.daysCount)
        : 0;

      return (
        <div className="mt-1.5 bg-amber-500/10 dark:bg-amber-500/10 border border-amber-500/30 dark:border-amber-500/20 rounded-xl p-2.5 text-xs text-amber-800 dark:text-amber-400 space-y-1">
          <div className="flex items-center justify-between font-extrabold text-[10px] tracking-wider uppercase">
            <span className="flex items-center gap-1 text-amber-700 dark:text-amber-500">⚠️ {lang.startsWith('ua') ? 'Попередження дефіциту' : 'Deficiency Warning'}</span>
            <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-500/15 border border-amber-300 dark:border-amber-500/30 text-amber-900 dark:text-amber-300 font-mono text-[9px]">
              {alert.daysCount} {lang.startsWith('ua') ? 'дн' : 'days'}
            </span>
          </div>
          <p className="text-[10px] text-zinc-600 dark:text-zinc-400 leading-normal">
            {lang.startsWith('ua') 
              ? `Сер. споживання: ${avgIntakePercent}%. Рекомендовано: ${alert.recommendedFoods.slice(0, 3).join(', ')}.`
              : `Avg intake: ${avgIntakePercent}%. Recommended: ${alert.recommendedFoods.slice(0, 3).join(', ')}.`}
          </p>
        </div>
      );
    };

    const updateCurrentMealNutrition = (key: keyof TotalNutrition, value: number) => {
      const updateNutrition = (prevNutrition: TotalNutrition): TotalNutrition => {
        const newNutrition = { ...prevNutrition, [key]: value };
        // If protein, carbs, or fat changed, recalculate calories
        if (key === 'protein_g' || key === 'carbs_g' || key === 'fat_g') {
          newNutrition.calories_kcal = (newNutrition.protein_g * 4) + (newNutrition.carbs_g * 4) + (newNutrition.fat_g * 9);
        }
        return newNutrition;
      };

      if (isHistory) {
        const updatedHistoryItem = {
          ...historyItem,
          total_nutrition: updateNutrition(historyItem.total_nutrition)
        } as HistoryEntry;
        setSelectedHistoryItem(updatedHistoryItem);
        const newHistory = history.map(item => item.id === updatedHistoryItem.id ? updatedHistoryItem : item);
        const saved = saveMealHistory(newHistory);
        setHistory(saved);
      } else {
        setResult(prev => prev ? {
          ...prev,
          total_nutrition: updateNutrition(prev.total_nutrition)
        } : null);
      }
    };

    const drinkWater = (isHistory || isLogged) ? 0 : (data.foods || [])
      .filter(food => 
        (food.category === 'drink' || (food.water_ml && food.water_ml > 0) || /\b(water|coffee|tea|latte|cappuccino|americano|espresso|matcha|drink|beverage|juice|soda|cola|coke|pepsi|sprite|fanta|lemonade|milk|smoothie|shake|wine|beer)\b/i.test(food.name || '') || /вода|водичка|кава|еспресо|лате|латте|капучино|капучіно|американо|макіато|мока|чай|чаю|матча|матчя|напій|напою|сік|соку|молоко|коктейль|смузі|пепсі|кола|лимонад/i.test(food.name || ''))
      )
      .reduce((sum, food) => sum + (typeof food.water_ml === 'number' && food.water_ml > 0 ? food.water_ml : (Number(food.amount) || 0)), 0);

    const projectedTotals = (isHistory || isLogged) ? selectedDayTotals : {
      calories: selectedDayTotals.calories + (nutrition.calories_kcal || 0),
      protein: selectedDayTotals.protein + (nutrition.protein_g || 0),
      carbs: selectedDayTotals.carbs + (nutrition.carbs_g || 0),
      fat: selectedDayTotals.fat + (nutrition.fat_g || 0),
      sugar: selectedDayTotals.sugar + (nutrition.sugar_g || 0),
      fiber: selectedDayTotals.fiber + (nutrition.fiber_g || 0),
      sodium: selectedDayTotals.sodium + (nutrition.sodium_mg || 0),
      water: selectedDayTotals.water + drinkWater,
    };

    const mealTypeColors = {
      breakfast: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      lunch: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      dinner: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
      snack: 'bg-rose-500/10 text-rose-500 border-rose-500/20'
    };

    return (
      <motion.div 
        ref={resultRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-10 scroll-mt-16"
      >
        <div className={cn(illumination ? "space-y-[28px]" : "space-y-2")}>
          {/* Meal Image Section */}
          <div className={cn(
            "relative rounded-3xl overflow-hidden h-56 sm:h-64 border card-shadow group transition-all",
            theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-[var(--card-border)]",
            illumination && (detailIlluminationType === 'water' ? "illumination-water" : "illumination-food")
          )}>
            {mealImage ? (
              <img src={mealImage} alt={data.meal_name} loading="lazy" decoding="async" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            ) : isGeneratingImage ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-6 bg-emerald-500/5 animate-pulse">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                <span className="text-[11px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 text-center px-4">
                  {lang === 'ua' ? "Ваш апетитний прев'ю від AI готується... 🍳" : "Generating customized AI meal preview... 🍳"}
                </span>
              </div>
            ) : (
              <div className={cn(
                "w-full h-full flex flex-col items-center justify-center gap-4 p-6 transition-all",
                theme === 'light' ? "bg-white" : "bg-zinc-900"
              )}>
                <Utensils className={cn("w-20 h-20 opacity-20", theme === 'light' ? "text-zinc-900" : "text-zinc-600")} />
                {true && (
                  <div className="flex flex-wrap justify-center gap-3">
                    <button 
                      type="button"
                      onClick={() => startCamera('attach', (img) => {
                        setImageToCrop(img);
                      })}
                      className={cn(
                        "px-4 py-2 rounded-full font-bold text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all border shadow-sm",
                        theme === 'light' 
                          ? "bg-white text-zinc-900 border-zinc-200 shadow-zinc-900/5 hover:bg-zinc-50" 
                          : "bg-[var(--card-bg)] border-[var(--card-border)] text-[var(--text)] hover:bg-[var(--input-bg)]"
                      )}
                    >
                      <Camera className="w-3.5 h-3.5" />
                      {t.startCamera}
                    </button>
                    <button 
                      type="button"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = async (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = async () => {
                              try {
                                const resized = await resizeImage(reader.result as string, 512);
                                setImageToCrop(resized);
                              } catch (err) {
                                console.error("Error processing file:", err);
                                toast.error(lang === 'ua' ? 'Не вдалося обробити зображення.' : 'Failed to process image.');
                              }
                            };
                            reader.readAsDataURL(file);
                          }
                        };
                        input.click();
                      }}
                      className={cn(
                        "px-4 py-2 rounded-full font-bold text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all border shadow-sm",
                        theme === 'light' 
                          ? "bg-white text-zinc-900 border-zinc-200 shadow-zinc-900/5 hover:bg-zinc-50" 
                          : "bg-[var(--card-bg)] border-[var(--card-border)] text-[var(--text)] hover:bg-[var(--input-bg)]"
                      )}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {t.uploadPhoto}
                    </button>
                    <motion.button 
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleGenerateImage(data.meal_name, (url) => {
                          if (isHistory) {
                            const updatedItem = { ...historyItem, image: url };
                            setHistory(prev => {
                              const newHistory = prev.map(item => item.id === updatedItem.id ? updatedItem : item);
                              return saveMealHistory(newHistory);
                            });
                            if (user) syncHistoryToFirebase(updatedItem, user.uid);
                            setSelectedHistoryItem(updatedItem);
                          } else {
                            setImage(url);
                            if (selectedHistoryItem) {
                              setSelectedHistoryItem({ ...selectedHistoryItem, image: url });
                            }
                          }
                        });
                      }}
                      disabled={isGeneratingImage}
                      className={cn(
                        "px-4 py-2 rounded-full font-bold text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all border shadow-sm",
                        theme === 'light' 
                          ? "bg-white text-zinc-900 border-zinc-200 shadow-zinc-900/5 hover:bg-zinc-50" 
                          : "bg-[var(--card-bg)] border-[var(--card-border)] text-[var(--text)] hover:bg-[var(--input-bg)]"
                      )}
                    >
                      {isGeneratingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {t.generateAIImage}
                    </motion.button>
                  </div>
                )}
              </div>
            )}
            
            {/* Change Photo for unsaved analysis */}
            {!isHistory && mealImage && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 z-20">
                <button 
                  onClick={() => document.getElementById('analysis-image-upload')?.click()}
                  className="bg-white/20 backdrop-blur-md hover:bg-white/30 text-white px-4 py-2 rounded-full font-bold text-xs uppercase tracking-widest flex items-center gap-2 transition-all"
                >
                  <Upload className="w-4 h-4" />
                  {t.changePhoto}
                </button>
                <motion.button 
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleGenerateImage(data.meal_name, (url) => {
                      if (isHistory) {
                        const updatedItem = { ...historyItem, image: url };
                        setHistory(prev => {
                          const newHistory = prev.map(item => item.id === updatedItem.id ? updatedItem : item);
                          return saveMealHistory(newHistory);
                        });
                        setSelectedHistoryItem(updatedItem);
                      } else {
                        setImage(url);
                        if (selectedHistoryItem) {
                          setSelectedHistoryItem({ ...selectedHistoryItem, image: url });
                        }
                      }
                    });
                  }}
                  disabled={isGeneratingImage}
                  className="bg-white/20 backdrop-blur-md hover:bg-white/30 text-white px-4 py-2 rounded-full font-bold text-xs uppercase tracking-widest flex items-center gap-2 transition-all disabled:opacity-50"
                >
                  {isGeneratingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {t.generate || 'Generate'}
                </motion.button>
                <button 
                  onClick={() => setImageToCrop(mealImage)}
                  className="bg-white/20 backdrop-blur-md hover:bg-white/30 text-white px-4 py-2 rounded-full font-bold text-xs uppercase tracking-widest flex items-center gap-2 transition-all"
                >
                  <Crop className="w-4 h-4" />
                  {t.crop}
                </button>
              </div>
            )}
          </div>

          {/* Summary Card - Removed from here to move inside the card */}

          <div className={cn(
            "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 space-y-6 card-shadow w-full overflow-hidden",
            illumination && (detailIlluminationType === 'water' ? "illumination-water" : "illumination-food")
          )}>
            <div className="flex flex-col gap-4 w-full">
              <div className="flex flex-col gap-2 w-full">
                <div className="flex items-center gap-2 w-full overflow-hidden">
                  <span className="text-2xl flex-shrink-0">{getFoodEmoji(data.meal_name || '')}</span>
                  <div className="flex-1 overflow-x-auto no-scrollbar pb-1 -mb-1 relative flex items-center gap-2">
                    {skin === 'stalker' ? (
                      <div className="flex-1 text-base sm:text-lg font-black uppercase tracking-tighter text-emerald-500 font-mono text-glow whitespace-nowrap">
                        [OBJECT DETECTED: {data.meal_name}] -&gt; [CLASSIFICATION: {getStalkerClassification(data.meal_name || '')}]
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={data.meal_name || ''}
                        style={{ minWidth: '100%', width: `${Math.max((data.meal_name || '').length + 1, 15)}ch` }}
                        onChange={(e) => {
                          if (isHistory) {
                            const updated = { ...historyItem, meal_name: e.target.value } as HistoryEntry;
                            setSelectedHistoryItem(updated);
                            const newHistory = history.map(item => item.id === updated.id ? updated : item);
                            const saved = saveMealHistory(newHistory);
                            setHistory(saved);
                            if (user) syncHistoryToFirebase(updated, user.uid);
                          } else {
                            setResult(prev => prev ? { ...prev, meal_name: e.target.value } : null);
                          }
                        }}
                        onBlur={() => toast.success(lang === 'ua' ? 'Назву страви збережено!' : 'Meal name saved!')}
                        className={cn(
                          "text-2xl font-bold bg-transparent border-b-2 border-emerald-500 outline-none max-w-none px-1 h-10 content-center",
                          excludedFoods.includes(data.meal_name || '') && "line-through opacity-60 text-zinc-400"
                        )}
                      />
                    )}
                    {skin !== 'stalker' && excludedFoods.includes(data.meal_name || '') && (
                      <span className="text-[10px] bg-red-500/15 text-red-500 px-2 py-0.5 rounded-full font-black uppercase tracking-wider shrink-0">
                        {lang === 'ua' ? 'Виключено' : 'Excluded'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 flex-wrap mt-1 w-full">
                  <div className="flex items-center justify-end gap-2 flex-wrap ml-auto">
                    {/* Meal Type Switcher */}
                    <motion.button 
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        const mealTypes: MealType[] = ['breakfast', 'lunch', 'dinner', 'snack'];
                        const currentIndex = mealTypes.indexOf(data.meal_type as MealType);
                        const nextType = mealTypes[(currentIndex + 1) % mealTypes.length];
                        
                        const oldTags = (isHistory ? historyItem?.tags : data?.tags) || [];
                        const cleanTags = oldTags.filter(t => !mealTypes.includes(t as MealType) && t !== 'meal');
                        const nextTags = [...cleanTags, nextType];

                        if (isHistory) {
                          const updated = { 
                            ...historyItem, 
                            meal_type: nextType,
                            tags: nextTags
                          } as HistoryEntry;
                          setSelectedHistoryItem(updated);
                          const newHistory = history.map(item => item.id === updated.id ? updated : item);
                          const saved = saveMealHistory(newHistory);
                          setHistory(saved);
                          if (user) syncHistoryToFirebase(updated, user.uid);
                        } else {
                          setResult(prev => prev ? { 
                            ...prev, 
                            meal_type: nextType,
                            tags: nextTags
                          } : null);
                        }
                        toast.success(`${t.mealType} -> ${t[nextType as keyof typeof t] || nextType}`);
                      }}
                      className={cn(
                        "text-[10px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors flex items-center gap-1 cursor-pointer",
                        mealTypeColors[data.meal_type as keyof typeof mealTypeColors] || 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20'
                      )}
                    >
                      <Tag className="w-3 h-3" />
                      <span>{t[data.meal_type as keyof typeof t] || data.meal_type}</span>
                    </motion.button>

                    {/* Current Portion Size Badge */}
                    <motion.button 
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsAdjustPortionModalOpen(true)}
                      className={cn(
                        "text-[11px] font-bold px-2.5 py-1 rounded-full border flex items-center gap-1 shrink-0 transition-all hover:bg-emerald-500/10 hover:border-emerald-500/30 group cursor-pointer",
                        theme === 'light' 
                          ? 'bg-zinc-50 border-zinc-200 text-zinc-700 hover:text-emerald-600' 
                          : 'bg-zinc-800/60 border-zinc-700 text-zinc-300 hover:text-emerald-400'
                      )}
                      title={lang === 'ua' ? 'Коригувати вагу / порцію' : 'Adjust Portion & Weight'}
                    >
                      <Scale className="w-3 h-3 text-emerald-500 group-hover:scale-110 transition-transform" />
                      {data.total_amount || computedTotalAmount || (lang === 'ua' ? '1 порція' : '1 portion')}
                    </motion.button>
                  </div>

                  {/* Far Right: Exclude Icon-Only Button */}
                  <motion.button 
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const isExcluded = excludedFoods.includes(data.meal_name || '');
                      const newExclusions = isExcluded 
                        ? excludedFoods.filter(i => i !== (data.meal_name || ''))
                        : [...excludedFoods, (data.meal_name || '')];
                      setExcludedFoods(newExclusions);
                      localStorage.setItem('ai_excluded_foods', JSON.stringify(safeClone(newExclusions)));
                      toast.success(
                        isExcluded 
                          ? (lang === 'ua' ? `Страва "${data.meal_name || ''}" вилучена з винятків!` : `Meal "${data.meal_name || ''}" removed from exclusions!`)
                          : (lang === 'ua' ? `Страва "${data.meal_name || ''}" додана до винятків!` : `Meal "${data.meal_name || ''}" added to exclusions!`)
                      );
                    }}
                    className={cn(
                      "p-1.5 rounded-full border transition-all flex items-center justify-center shrink-0 cursor-pointer",
                      excludedFoods.includes(data.meal_name || '')
                        ? "bg-red-500/15 text-red-500 border-red-500/30 shadow-xs"
                        : "bg-zinc-500/10 text-zinc-400 border-zinc-500/20 hover:text-red-500 hover:border-red-500/30 hover:bg-red-500/10"
                    )}
                    title={
                      excludedFoods.includes(data.meal_name || '')
                        ? (lang === 'ua' ? 'Повернути страву в рекомендації' : 'Include meal in suggestions')
                        : (lang === 'ua' ? 'Виключити страву з рекомендацій' : 'Exclude meal from suggestions')
                    }
                  >
                    <Ban className="w-3.5 h-3.5" />
                  </motion.button>
                </div>
              </div>

              {/* Nutritional Insight Section */}
              {(() => {
                const isGeneric = !data.insight || data.insight.trim().length < 60 || isGenericInsight(data.insight);
                const ins = !isGeneric 
                  ? data.insight 
                  : buildDetailedDynamicInsight(data as MealAnalysis, lang, vibe, ignoreMealTimeLogging, onboardingGender);
                
                const isPremium = subscriptionTier !== 'free';
                const paragraphs = (() => {
                  if (!isPremium) return [ins];
                  const explicitParagraphs = ins.split(/\n+/).map(p => p.trim()).filter(Boolean);
                  if (explicitParagraphs.length >= 2) return explicitParagraphs.slice(0, 3);
                  
                  const sentences = ins.split(/(?<=[.!?])\s+/).map(s => s.trim()).filter(Boolean);
                  if (sentences.length <= 1) return [ins];
                  if (sentences.length === 2) return [sentences[0], sentences[1]];
                  if (sentences.length === 3) return sentences;
                  
                  const chunk1 = sentences.slice(0, Math.ceil(sentences.length / 3)).join(' ');
                  const chunk2 = sentences.slice(Math.ceil(sentences.length / 3), Math.ceil(sentences.length * 2 / 3)).join(' ');
                  const chunk3 = sentences.slice(Math.ceil(sentences.length * 2 / 3)).join(' ');
                  return [chunk1, chunk2, chunk3].filter(Boolean);
                })();

                return (
                  <div className={cn(
                    "mt-2 p-5 rounded-2xl flex flex-col transition-all border",
                    theme === 'light' 
                      ? "bg-emerald-50 border-emerald-100/50" 
                      : "bg-emerald-950/20 border-emerald-900/40"
                  )}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-emerald-500" />
                        <span className={cn("text-[10px] font-bold uppercase tracking-widest", theme === 'light' ? "text-emerald-700" : "text-emerald-500")}>
                          {t.nutritionalInsight}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-1">
                      {paragraphs.map((p, pIdx) => (
                        <p key={pIdx} className={cn("text-sm leading-relaxed italic", theme === 'light' ? "text-zinc-700/90" : "text-zinc-300")}>
                          {cleanInsightQuotes(p)}
                        </p>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        const prompt = lang === 'ua' ? `Давай обговоримо ${data.meal_name}.` : `Let's discuss my ${data.meal_name}.`;
                        setChatInput(prompt);
                        setView('chat');
                      }}
                      className="self-end flex items-center gap-1 group/ai mt-1"
                    >
                      <span className={cn("text-[10px] font-bold uppercase tracking-widest transition-colors italic", theme === 'light' ? "text-emerald-700/80 group-hover/ai:text-emerald-700" : "text-emerald-600 dark:text-emerald-400/80 group-hover/ai:text-emerald-400")}>
                        {t.askAI || 'Ask AI'}
                      </span>
                      <Sparkles className={cn("w-3 h-3 group-hover/ai:scale-110 transition-transform", theme === 'light' ? "text-emerald-500" : "text-emerald-500")} />
                    </button>
                  </div>
                );
              })()}

              {/* Step-by-Step Recipe Section (Conditional & Toggleable) */}
              {(data.includeRecipe || (result && result.id === data.id && includeRecipe)) ? (
                <div className={cn(
                  "mt-2 p-4 rounded-2xl flex items-center justify-between transition-all border",
                  theme === 'light' 
                    ? "bg-blue-50/80 border-blue-100" 
                    : "bg-blue-950/20 border-blue-900/40"
                )}>
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    <div className="p-2 rounded-xl bg-blue-500/10 text-blue-500 shrink-0">
                      <BookOpen className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600 dark:text-blue-400">
                        {lang === 'ua' ? 'Покроковий рецепт' : 'Step-by-step Recipe'}
                      </span>
                      <span className="text-xs font-bold text-[var(--text)] truncate">
                        {lang === 'ua' ? `Як приготувати ${data.meal_name}` : `How to prepare ${data.meal_name}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveRecipeModal({
                          isOpen: true,
                          mealName: data.meal_name,
                          ingredients: data.foods || [],
                          macros: {
                            calories: nutrition.calories_kcal,
                            protein: nutrition.protein_g,
                            carbs: nutrition.carbs_g,
                            fat: nutrition.fat_g
                          }
                        });
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-[10px] font-black uppercase tracking-wider active:scale-95 transition-all shadow-xs cursor-pointer"
                    >
                      <span>{lang === 'ua' ? 'Рецепт' : 'Recipe'}</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (isHistory) {
                          const updated = { ...historyItem, includeRecipe: false } as HistoryEntry;
                          setSelectedHistoryItem(updated);
                          const newHistory = history.map(item => item.id === updated.id ? updated : item);
                          setHistory(saveMealHistory(newHistory));
                        } else {
                          setResult(prev => prev ? { ...prev, includeRecipe: false } : null);
                        }
                        setIncludeRecipe(false);
                      }}
                      className="p-1.5 rounded-xl bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 text-xs font-bold transition-all cursor-pointer"
                      title={lang === 'ua' ? 'Прибрати рецепт' : 'Remove Recipe'}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : null}

              {(() => {
                const conf = {
                  recognition: typeof data?.confidence?.recognition === 'number' && !isNaN(data.confidence.recognition) ? data.confidence.recognition : 0.95,
                  portion: typeof data?.confidence?.portion === 'number' && !isNaN(data.confidence.portion) ? data.confidence.portion : 0.85,
                  nutrition: typeof data?.confidence?.nutrition === 'number' && !isNaN(data.confidence.nutrition) ? data.confidence.nutrition : 0.9
                };
                return (
                  <div className={cn(
                    "p-5 rounded-2xl border transition-all mt-2",
                    theme === 'light' 
                      ? "bg-emerald-50 border-emerald-100/50" 
                      : "bg-emerald-950/20 border-emerald-900/40"
                  )}>
                    <div className="flex items-center gap-2 mb-3">
                      <Activity className="w-4 h-4 text-emerald-500" />
                      <span className={cn("text-[10px] font-bold uppercase tracking-widest", theme === 'light' ? "text-emerald-700" : "text-emerald-500")}>
                        {t.aiConfidence}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex flex-col gap-1">
                        <span className={cn("text-[10px] font-bold", theme === 'light' ? "text-emerald-800/60" : "text-emerald-200/60")}>{t.recognition}</span>
                        <div className="flex items-center gap-1.5">
                          <div className={cn("h-1.5 flex-1 rounded-full overflow-hidden", theme === 'light' ? "bg-emerald-200/50" : "bg-emerald-900/50")}>
                            <div className={cn("h-full rounded-full transition-all duration-1000 ease-out", conf.recognition > 0.8 ? "bg-emerald-500" : conf.recognition > 0.5 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${conf.recognition * 100}%` }} />
                          </div>
                          <span className={cn("text-[10px] font-bold", theme === 'light' ? "text-emerald-900" : "text-emerald-50")}>{Math.round(conf.recognition * 100)}%</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={cn("text-[10px] font-bold", theme === 'light' ? "text-emerald-800/60" : "text-emerald-200/60")}>{t.portionScore}</span>
                        <div className="flex items-center gap-1.5">
                          <div className={cn("h-1.5 flex-1 rounded-full overflow-hidden", theme === 'light' ? "bg-emerald-200/50" : "bg-emerald-900/50")}>
                            <div className={cn("h-full rounded-full transition-all duration-1000 ease-out", conf.portion > 0.8 ? "bg-emerald-500" : conf.portion > 0.5 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${conf.portion * 100}%` }} />
                          </div>
                          <span className={cn("text-[10px] font-bold", theme === 'light' ? "text-emerald-900" : "text-emerald-50")}>{Math.round(conf.portion * 100)}%</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={cn("text-[10px] font-bold", theme === 'light' ? "text-emerald-800/60" : "text-emerald-200/60")}>{t.nutritionScore}</span>
                        <div className="flex items-center gap-1.5">
                          <div className={cn("h-1.5 flex-1 rounded-full overflow-hidden", theme === 'light' ? "bg-emerald-200/50" : "bg-emerald-900/50")}>
                            <div className={cn("h-full rounded-full transition-all duration-1000 ease-out", conf.nutrition > 0.8 ? "bg-emerald-500" : conf.nutrition > 0.5 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${conf.nutrition * 100}%` }} />
                          </div>
                          <span className={cn("text-[10px] font-bold", theme === 'light' ? "text-emerald-900" : "text-emerald-50")}>{Math.round(conf.nutrition * 100)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {((data.safe_choice_status) || (isPremiumOrPro && safeChoiceProfile?.active !== false)) && safeChoiceProfile?.active !== false && (
                <div 
                  onClick={() => setSafeChoiceDetailModalData(data)}
                  className={cn(
                    "p-3.5 rounded-2xl border flex items-start gap-3 transition-all duration-200 cursor-pointer group hover:scale-[1.01] hover:shadow-lg",
                    data.safe_choice_status === 'red' ? "bg-rose-500/10 border-rose-500/50 hover:bg-rose-500/15 hover:border-rose-500 shadow-rose-500/5" :
                    data.safe_choice_status === 'amber' ? "bg-amber-500/10 border-amber-500/50 hover:bg-amber-500/15 hover:border-amber-500 shadow-amber-500/5" :
                    "bg-emerald-500/10 border-emerald-500/50 hover:bg-emerald-500/15"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-xl flex-shrink-0 mt-0.5 shadow-md transition-transform group-hover:scale-110",
                    data.safe_choice_status === 'red' ? "bg-rose-500 text-white" :
                    data.safe_choice_status === 'amber' ? "bg-amber-500 text-white" :
                    "bg-emerald-500 text-white"
                  )}>
                    {data.safe_choice_status === 'red' || data.safe_choice_status === 'amber' ? 
                      <AlertCircle className="w-4 h-4" /> : 
                      <CheckCircle2 className="w-4 h-4" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h4 className={cn(
                        "font-bold uppercase tracking-widest text-xs",
                        data.safe_choice_status === 'red' ? "text-rose-500" :
                        data.safe_choice_status === 'amber' ? "text-amber-500" :
                        "text-emerald-500"
                      )}>
                        {data.safe_choice_status === 'red' ? (t.safeChoiceStop || "Safe Choice: STOP") :
                         data.safe_choice_status === 'amber' ? (t.safeChoiceReview || "Safe Choice: REVIEW") :
                         (t.safeChoicePassed || "Safe Choice: PASSED")}
                      </h4>
                      <span className={cn(
                        "text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border flex items-center gap-1 transition-all shrink-0 whitespace-nowrap",
                        data.safe_choice_status === 'red' ? "bg-rose-500/20 text-rose-600 dark:text-rose-400 border-rose-500/30 group-hover:bg-rose-500 group-hover:text-white" :
                        data.safe_choice_status === 'amber' ? "bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30 group-hover:bg-amber-500 group-hover:text-white" :
                        "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 group-hover:bg-emerald-500 group-hover:text-white"
                      )}>
                        {lang === 'ua' ? 'Детальніше' : 'Tap for details'} <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                    <p className={cn(
                      "text-[10px] leading-snug",
                      theme === 'light' ? "text-zinc-600" : "text-zinc-400"
                    )}>
                      {getSafeChoiceMessage((data.safe_choice_status || 'green') as any, data.safe_choice_message)}
                    </p>
                    {data.detected_risk_components && (
                      <RiskComponentsList 
                        components={data.detected_risk_components} 
                        lang={lang} 
                        onOpenEDecoder={(q) => {
                          triggerHaptic('SUCCESS');
                          setEDecoderInitialQuery(q || '');
                          setIsEDecoderOpen(true);
                        }}
                      />
                    )}

                  </div>
                </div>
              )}

              {/* Adjust Portion Modal */}
              <AnimatePresence>
                {isAdjustPortionModalOpen && (
                  <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setIsAdjustPortionModalOpen(false)}
                      className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    />

                    {/* Modal Container */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 20 }}
                      transition={{ type: "spring", duration: 0.4, bounce: 0.15 }}
                      className={cn(
                        "relative w-full max-w-md rounded-2xl border p-5 shadow-2xl z-10 space-y-5 max-h-[90vh] overflow-y-auto",
                        theme === 'light' 
                          ? "bg-white border-zinc-200 text-zinc-800" 
                          : "bg-zinc-900 border-[var(--card-border)] text-zinc-100"
                      )}
                    >
                      {/* Modal Header */}
                      <div className="flex items-center justify-between pb-3 border-b border-zinc-200 dark:border-zinc-800">
                        <div className="flex items-center gap-2">
                          <Scale className="w-5 h-5 text-emerald-500" />
                          <span className="text-xs font-bold uppercase tracking-wider text-[var(--text)]">
                            {lang === 'ua' ? 'Коригування ваги / порції' : 'Adjust Portion & Weight'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsAdjustPortionModalOpen(false)}
                          className="p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-400 hover:text-zinc-500 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Smart Scaling Suggestions Section */}
                      {smartScalingSuggestions.length > 0 && (
                        <div className="space-y-2.5 p-3.5 rounded-2xl bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-500/20 shadow-xs">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <Sparkles className="w-4 h-4 text-emerald-500 animate-pulse" />
                              <span className="text-[11px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                                {lang === 'ua' ? 'Розумне масштабування' : 'Smart Scaling Suggestions'}
                              </span>
                            </div>
                            <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 uppercase tracking-widest">
                              AI & History
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {smartScalingSuggestions.map((s) => {
                              const isActive = Math.abs(mealScaleMultiplier - s.multiplier) < 0.02;
                              return (
                                <button
                                  key={s.id}
                                  type="button"
                                  onClick={() => {
                                    triggerHaptic('SUCCESS');
                                    sounds.playPop();
                                    setMealScaleMultiplier(s.multiplier);
                                    setCustomWeightInput('');
                                  }}
                                  className={cn(
                                    "p-2.5 rounded-xl border text-left transition-all relative overflow-hidden group cursor-pointer flex flex-col justify-between gap-1",
                                    isActive
                                      ? "bg-emerald-500 border-emerald-500 text-white shadow-md shadow-emerald-500/20 ring-2 ring-emerald-500/30"
                                      : (theme === 'light'
                                          ? "bg-white/80 hover:bg-white border-zinc-200/80 hover:border-emerald-500/40 text-zinc-800"
                                          : "bg-zinc-800/60 hover:bg-zinc-800 border-zinc-700/80 hover:border-emerald-500/40 text-zinc-200")
                                  )}
                                >
                                  <div className="flex items-start justify-between gap-1">
                                    <span className={cn(
                                      "text-[10px] font-black uppercase tracking-wider line-clamp-1",
                                      isActive ? "text-white" : "text-zinc-800 dark:text-zinc-100"
                                    )}>
                                      {lang === 'ua' ? s.titleUa : s.titleEn}
                                    </span>
                                    <span className={cn(
                                      "text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md shrink-0",
                                      isActive
                                        ? "bg-white/20 text-white"
                                        : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                                    )}>
                                      {lang === 'ua' ? s.badgeUa : s.badgeEn}
                                    </span>
                                  </div>

                                  <div className="flex items-baseline justify-between mt-1">
                                    <span className={cn(
                                      "text-xs font-black",
                                      isActive ? "text-white" : "text-emerald-500"
                                    )}>
                                      {s.projectedCals} kcal
                                    </span>
                                    <span className={cn(
                                      "text-[10px] font-bold uppercase tracking-wider",
                                      isActive ? "text-emerald-100" : "text-zinc-400"
                                    )}>
                                      {s.multiplier}x scale
                                    </span>
                                  </div>

                                  <span className={cn(
                                    "text-[9px] font-medium line-clamp-1 block leading-tight",
                                    isActive ? "text-emerald-100" : "text-zinc-400 dark:text-zinc-500"
                                  )}>
                                    {lang === 'ua' ? s.subUa : s.subEn}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Preset Row - All active toggle buttons are branded green color */}
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest block">
                          {lang === 'ua' ? 'Швидкі коефіцієнти:' : 'Quick Scale Presets:'}
                        </label>
                        <div className="flex items-center gap-1.5 overflow-x-auto py-1 no-scrollbar sm:flex-wrap">
                          {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0].map((preset) => {
                            const isActive = Math.abs(mealScaleMultiplier - preset) < 0.01;
                            return (
                              <button
                                key={preset}
                                type="button"
                                onClick={() => {
                                  setMealScaleMultiplier(preset);
                                  setCustomWeightInput('');
                                }}
                                className={cn(
                                  "text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 rounded-xl transition-all border flex-1 text-center min-w-[50px]",
                                  isActive
                                    ? "bg-emerald-500 border-emerald-500 text-white shadow-sm font-black" 
                                    : (theme === 'light' 
                                        ? "bg-white border-zinc-200 hover:bg-zinc-50 text-zinc-700" 
                                        : "bg-zinc-800/40 border-zinc-700 hover:bg-zinc-800 text-zinc-300")
                                )}
                              >
                                {preset}x
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Direct target weight input if numeric portion is detected */}
                      {(() => {
                        const baseToUse = originalResult || data;
                        const computedBaseMax = (() => {
                          if (baseToUse.total_amount && baseToUse.total_amount.trim() !== '') return baseToUse.total_amount;
                          if (!baseToUse.foods || baseToUse.foods.length === 0) return '';
                          let gCount = 0;
                          let mlCount = 0;
                          baseToUse.foods.forEach(f => {
                            const amt = parseFloat(String(f.amount || '0'));
                            if (!isNaN(amt) && amt > 0) {
                              const unit = (f.unit || '').toLowerCase();
                              if (unit === 'g' || unit === 'gr' || unit === 'grams' || unit === 'г' || unit === 'гр') {
                                gCount += amt;
                              } else if (unit === 'ml' || unit === 'мл') {
                                mlCount += amt;
                              }
                            }
                          });
                          if (gCount > 0) return `${gCount}g`;
                          if (mlCount > 0) return `${mlCount}ml`;
                          return '';
                        })();

                        const baseMatch = computedBaseMax.match(/[\d.]+/);
                        const baseNum = baseMatch ? parseFloat(baseMatch[0]) : null;
                        const baseUnit = computedBaseMax.replace(/[\d.]+/g, '').trim() || 'g';

                        if (baseNum && baseNum > 0) {
                          return (
                            <div className="space-y-2 border-t border-dashed border-zinc-200 dark:border-zinc-800 pt-3">
                              <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest block">
                                {lang === 'ua' ? `Коригувати за вагою (${baseUnit}):` : `Scale by weight (${baseUnit}):`}
                              </label>
                              <div className="relative w-full">
                                <input
                                  type="number"
                                  min="1"
                                  value={customWeightInput}
                                  placeholder={`${Math.round(baseNum * mealScaleMultiplier)}`}
                                  onChange={(e) => {
                                    const valStr = e.target.value;
                                    setCustomWeightInput(valStr);
                                    const parsed = parseFloat(valStr);
                                    if (parsed && parsed > 0) {
                                      setMealScaleMultiplier(parsed / baseNum);
                                    } else {
                                      setMealScaleMultiplier(1.0);
                                    }
                                  }}
                                  className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl py-2 px-3 pr-8 focus:border-emerald-500 outline-none text-xs font-bold transition-colors text-[var(--text)]"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-zinc-400">
                                  {baseUnit}
                                </span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Real-time Caloric Goal Impact Progress Indicator */}
                      {(() => {
                        const calorieGoal = goals.calories || 2000;
                        const baseActiveMealCals = originalResult?.total_nutrition?.calories_kcal || nutrition.calories_kcal || 0;
                        const activeMealProjected = baseActiveMealCals * animatedMultiplier;
                        
                        // Calculate other foods eaten excluding this active meal if it was already logged
                        const otherCal = isHistory 
                          ? Math.max(0, selectedDayTotals.calories - baseActiveMealCals)
                          : selectedDayTotals.calories;

                        const projectedTotal = otherCal + activeMealProjected;
                        
                        const otherPct = (otherCal / calorieGoal) * 100;
                        const activePct = (activeMealProjected / calorieGoal) * 100;
                        
                        // Caps for rendering and visual stacking
                        const otherPctCapped = Math.min(100, otherPct);
                        const activePctCapped = Math.min(100 - otherPctCapped, activePct);
                        const overGoalPct = projectedTotal > calorieGoal ? Math.min(100, ((projectedTotal - calorieGoal) / calorieGoal) * 100) : 0;

                        return (
                          <div className="space-y-2 border-t border-dashed border-zinc-200 dark:border-zinc-800 pt-3">
                            <div className="flex items-center justify-between text-[11px] font-bold">
                              <span className="text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
                                <Activity className="w-3.5 h-3.5 text-emerald-500" />
                                {lang === 'ua' ? 'Вплив на денні калорії' : 'Impact on Daily Calories'}
                              </span>
                              <span className={cn(
                                projectedTotal > calorieGoal ? "text-rose-500 font-extrabold" : "text-emerald-500"
                              )}>
                                {Math.round(projectedTotal)} / {calorieGoal} kcal ({Math.round((projectedTotal / calorieGoal) * 100)}%)
                              </span>
                            </div>
                            
                            {/* Interactive stacked progress bar with spring width transitions */}
                            <div className="relative h-3 w-full rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden shadow-inner flex">
                              {/* Other Foods consumed segment */}
                              {otherPctCapped > 0 && (
                                <motion.div 
                                  layout
                                  animate={{ width: `${otherPctCapped}%` }}
                                  transition={{ type: "spring", stiffness: 100, damping: 15 }}
                                  className={cn(
                                    "h-full shrink-0",
                                    theme === 'light' ? "bg-zinc-400" : "bg-zinc-600"
                                  )} 
                                  title={lang === 'ua' ? `Інші страви: ${Math.round(otherCal)} ккал` : `Other meals: ${Math.round(otherCal)} kcal`}
                                />
                              )}
                              
                              {/* Selected Portion calories segment */}
                              {activePctCapped > 0 && (
                                <motion.div 
                                  layout
                                  animate={{ width: `${activePctCapped}%` }}
                                  transition={{ type: "spring", stiffness: 100, damping: 15 }}
                                  className="h-full shrink-0 bg-emerald-500 relative overflow-hidden" 
                                  title={lang === 'ua' ? `Ця страва: ${Math.round(activeMealProjected)} ккал` : `This meal: ${Math.round(activeMealProjected)} kcal`}
                                >
                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                                </motion.div>
                              )}

                              {/* Exceeded amount segment */}
                              {projectedTotal > calorieGoal && (
                                <motion.div 
                                  layout
                                  animate={{ width: `${Math.min(100 - otherPctCapped - activePctCapped, overGoalPct)}%` }}
                                  transition={{ type: "spring", stiffness: 100, damping: 15 }}
                                  className="h-full shrink-0 bg-rose-500 animate-pulse"
                                  title={lang === 'ua' ? `Перевищення ліміту: ${Math.round(projectedTotal - calorieGoal)} ккал` : `Over limit: ${Math.round(projectedTotal - calorieGoal)} kcal`}
                                />
                              )}
                            </div>

                            {/* Legend / breakdown guide */}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px] font-bold text-zinc-400">
                              <div className="flex items-center gap-1">
                                <div className={cn("w-2 h-2 rounded-full", theme === 'light' ? "bg-zinc-400" : "bg-zinc-600")} />
                                <span>{lang === 'ua' ? 'Інша їжа' : 'Other foods'} ({Math.round(otherCal)} kcal)</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span>{lang === 'ua' ? 'Ця порція' : 'This portion'} ({Math.round(activeMealProjected)} kcal)</span>
                              </div>
                              {projectedTotal > calorieGoal && (
                                <div className="flex items-center gap-1 text-rose-500">
                                  <div className="w-2 relative flex items-center justify-center">
                                    <span className="absolute animate-ping inline-flex h-2 w-2 rounded-full bg-rose-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-550"></span>
                                  </div>
                                  <span>{lang === 'ua' ? 'Перевищення' : 'Surplus'} (+{Math.round(projectedTotal - calorieGoal)} kcal)</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Interactive previews and Save Button */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-zinc-200 dark:border-zinc-800 pt-3.5">
                        <div className="flex items-center gap-2">
                          <Flame className="w-4 h-4 text-amber-500 animate-pulse" />
                          <span className="text-xs font-bold text-zinc-600 dark:text-zinc-300">
                            {lang === 'ua' ? 'Очікувані' : 'Projected'}: <strong className="text-emerald-500">{Math.round((originalResult?.total_nutrition?.calories_kcal || data.total_nutrition?.calories_kcal || 0) * animatedMultiplier)}</strong> kcal
                          </span>
                        </div>

                        {/* Save Button - Branded Green */}
                        <motion.button
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            handleScaleMealAmount(mealScaleMultiplier);
                            setIsAdjustPortionModalOpen(false);
                          }}
                          disabled={Math.abs(mealScaleMultiplier - 1.0) < 0.005 && customWeightInput === ''}
                          className={cn(
                            "w-full sm:w-auto px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all text-white",
                            Math.abs(mealScaleMultiplier - 1.0) < 0.005 && customWeightInput === ''
                              ? "bg-emerald-500/50 cursor-not-allowed opacity-60"
                              : "bg-emerald-500 hover:bg-emerald-600 shadow-md hover:shadow-emerald-500/20 active:scale-95"
                          )}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {lang === 'ua' ? 'Зберегти зміни' : 'Save Portion'}
                        </motion.button>
                      </div>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>

              <MealNotesEditor
                meal={data as HistoryEntry}
                lang={lang}
                theme={theme}
                onSave={(updatedNotes) => {
                  if (selectedHistoryItem && selectedHistoryItem.id === data.id) {
                    updateMealInHistory({
                      ...selectedHistoryItem,
                      notes: updatedNotes
                    });
                  } else if (result && result.id === data.id) {
                    setResult({
                      ...result,
                      notes: updatedNotes
                    });
                    const historyMatch = history.find(item => item.id === result.id);
                    if (historyMatch) {
                      updateMealInHistory({
                        ...historyMatch,
                        notes: updatedNotes
                      });
                    }
                  } else {
                    const historyMatch = history.find(item => item.id === data.id);
                    if (historyMatch) {
                      updateMealInHistory({
                        ...historyMatch,
                        notes: updatedNotes
                      });
                    }
                  }
                }}
              />

              {/* AI Fallback Button for Barcode Scans with missing data */}
              {data.insight?.includes('Scanned via barcode') && (nutrition.calories_kcal === 0 || data.meal_name === 'Unknown Product') && mealImage && (
                <button
                  onClick={() => {
                    setImage(mealImage);
                    handleAnalyze(false, mealImage);
                  }}
                  className="w-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all border border-emerald-500/20"
                >
                  <Sparkles className="w-4 h-4" />
                  {t.analyzeWithAI}
                </button>
              )}
            </div>
          </div>
        </div>

        {(() => {
          const tod = data.time_of_day || determineTimeOfDay(mealTimestamp);
          
          const getTodLabel = (tValue: string) => {
            if (lang === 'ua') {
              if (tValue === 'morning') return 'Ранковий фокус 🌅';
              if (tValue === 'afternoon') return 'Денний баланс ☀️';
              return 'Нічний режим 🌙';
            } else {
              if (tValue === 'morning') return 'Morning Focus 🌅';
              if (tValue === 'afternoon') return 'Afternoon Balance ☀️';
              return 'Late Night Caution 🌙';
            }
          };

          const getTodAdvice = (tValue: string) => {
            if (lang === 'ua') {
              if (tValue === 'morning') return 'Сніданок запускає метаболізм. Фокусуйтеся на складних вуглеводах і білках для довготривалої енергії без коливань глюкози.';
              if (tValue === 'afternoon') return 'Час для збалансованого обіду. Орієнтуйтеся на комбінацію білків, здорових жирів і клітковини, щоб уникнути післяобідньої втоми.';
              return 'Пізня вечеря потребує уваги. Віддайте перевагу легким білкам та овочам, обмежте важкі жири та швидкі цукри, щоб підтримати глибоку фазу сну.';
            } else {
              if (tValue === 'morning') return 'Morning meals kickstart digestion & metabolism. Prioritize complex carbohydrates and quality protein for focused, sustained energy with no sugar crashes.';
              if (tValue === 'afternoon') return 'Keep activity levels high with a balanced lunch. Focus on protein, leafy greens, fiber and healthy fats to lock down satiety and escape afternoon brain fog.';
              return 'Late night digestion slows down. Choose light, easily digestible, or protein-forward options. Steer clear of heavy fats or refined sugars to optimize growth hormone release and deep sleep quality.';
            }
          };

          const todIcon = () => {
            if (tod === 'morning') return <Sunrise className="w-5 h-5 text-amber-500 animate-pulse" />;
            if (tod === 'afternoon') return <Sun className="w-5 h-5 text-amber-500 hover:rotate-45 transition-transform" />;
            return <Moon className="w-5 h-5 text-indigo-400" />;
          };

          return (
            <div className={cn(
              "p-5 rounded-3xl flex flex-col transition-all border card-shadow",
              theme === 'light' 
                ? "bg-amber-50/70 border-amber-200/60" 
                : "bg-amber-950/10 border-amber-900/30"
            )}>
              <button 
                type="button"
                onClick={() => setIsMealTimeFocusOpen(!isMealTimeFocusOpen)}
                className="flex items-center justify-between group cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  {todIcon()}
                  <span className={cn(
                    "text-xs font-bold uppercase tracking-wider",
                    theme === 'light' ? "text-amber-800" : "text-amber-400"
                  )}>
                    {lang === 'ua' ? 'Час споживання' : 'Meal Time Focus'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-[10px] font-bold px-2.5 py-0.5 rounded-full border shrink-0",
                    theme === 'light'
                      ? "bg-amber-100/80 border-amber-200/50 text-amber-900"
                      : "bg-amber-950/80 text-amber-300 border border-amber-900/30"
                  )}>
                    {getTodLabel(tod)}
                  </span>
                  {isMealTimeFocusOpen ? (
                    <ChevronUp className={cn("w-4 h-4 transition-transform", theme === 'light' ? "text-amber-600" : "text-amber-400")} />
                  ) : (
                    <ChevronDown className={cn("w-4 h-4 transition-transform", theme === 'light' ? "text-amber-600" : "text-amber-400")} />
                  )}
                </div>
              </button>

              <AnimatePresence>
                {isMealTimeFocusOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0, marginTop: 0 }}
                    animate={{ height: 'auto', opacity: 1, marginTop: 12 }}
                    exit={{ height: 0, opacity: 0, marginTop: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-amber-200/20 dark:border-zinc-800/60 pt-3">
                      <p className={cn(
                        "text-xs leading-relaxed font-semibold mb-3",
                        theme === 'light' ? "text-zinc-800" : "text-zinc-300"
                      )}>
                        {getTodAdvice(tod)}
                      </p>
                      
                      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed border-zinc-200/50 dark:border-zinc-800/60 overflow-x-auto no-scrollbar py-1">
                        <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider mr-1 shrink-0">
                          {lang === 'ua' ? 'Змінити:' : 'Switch Slot:'}
                        </span>
                        {['morning', 'afternoon', 'late night'].map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => {
                              const updatedData = { ...data, time_of_day: opt as any };
                              
                              // 1. If we are in details view or has history item, update selectedHistoryItem state
                              if (view === 'details' || isHistory) {
                                const updated = { ...selectedHistoryItem, ...updatedData } as HistoryEntry;
                                setSelectedHistoryItem(updated);
                                if (updated.id) {
                                  const newHistory = history.map(item => item.id === updated.id ? updated : item);
                                  const saved = saveMealHistory(newHistory);
                                  setHistory(saved);
                                  if (user) syncHistoryToFirebase(updated, user.uid);
                                }
                              }
                              
                              // 2. Always update the active scan setResult state to sync any view
                              setResult(updatedData);

                              toast.success(lang === 'ua' ? `Коригування: ${getTodLabel(opt)}` : `Adjusted to ${getTodLabel(opt)}`);
                            }}
                            className={cn(
                              "text-[9px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded transition-colors shrink-0",
                              tod === opt
                                ? "bg-emerald-500 text-white shadow-sm font-bold" // ACTIVE toggle buttons are branded green color
                                : (theme === 'light' ? "bg-zinc-100/80 hover:bg-zinc-200 text-zinc-600" : "bg-zinc-800/60 hover:bg-zinc-800 text-zinc-400")
                            )}
                          >
                            {opt === 'morning' ? (lang === 'ua' ? 'Ранок' : 'Morning') :
                            opt === 'afternoon' ? (lang === 'ua' ? 'День' : 'Afternoon') :
                            (lang === 'ua' ? 'Ніч' : 'Late night')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })()}

        <div className={cn(
          "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 space-y-6 card-shadow",
          illumination && "illumination-food"
        )}>
          {/* Caffeine Limit Warning Notification Banner */}
          {(() => {
            const caffeineLimit = rawGoals.caffeine_mg || goals.caffeine_mg || 400;
            const mealCaffeine = Math.round((nutrition.caffeine_mg || 0) * animatedMultiplier);
            const baseDayCaffeine = (isHistory || isLogged)
              ? Math.max(0, selectedDayTotals.caffeine - Math.round(historyItem?.total_nutrition?.caffeine_mg || nutrition.caffeine_mg || 0))
              : selectedDayTotals.caffeine;
            const projectedDayCaffeine = baseDayCaffeine + mealCaffeine;
            const isCaffeineOverLimit = mealCaffeine > 0 && projectedDayCaffeine > caffeineLimit;

            if (!isCaffeineOverLimit) return null;

            const excessAmount = projectedDayCaffeine - caffeineLimit;

            return (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-2xl bg-amber-500/10 border-2 border-amber-500/40 flex items-start gap-3.5 text-amber-600 dark:text-amber-400 shadow-md shadow-amber-500/5"
              >
                <div className="p-2 rounded-xl bg-amber-500/20 text-amber-500 shrink-0 mt-0.5">
                  <AlertTriangle className="w-5 h-5 animate-pulse" />
                </div>
                <div className="space-y-1.5 text-xs flex-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-black uppercase tracking-wider text-amber-700 dark:text-amber-400 text-xs flex items-center gap-1.5">
                      <span>{lang === 'ua' ? "Перевищено ліміт кофеїну!" : "Caffeine Limit Exceeded!"}</span>
                    </p>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/30">
                      +{excessAmount} mg over limit
                    </span>
                  </div>
                  <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed font-medium">
                    {lang === 'ua'
                      ? `У цій страві виявлено ${mealCaffeine} мг кофеїну. Загальна кількість за день досягне ${projectedDayCaffeine} мг, що перевищує ваш встановлений ліміт (${caffeineLimit} мг).`
                      : `Detected ${mealCaffeine}mg caffeine in this meal. Your daily total will reach ${projectedDayCaffeine}mg, exceeding your set daily caffeine limit of ${caffeineLimit}mg.`}
                  </p>
                </div>
              </motion.div>
            );
          })()}

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-zinc-600 dark:text-zinc-400 font-bold uppercase text-[10px] tracking-widest">{t.nutritionalSummary}</p>
                <div className="text-right flex items-center gap-2">
                  <Flame className="w-6 h-6 text-emerald-500" />
                  <p className="text-3xl font-bold text-[var(--text)]">
                    <EditableNutrient 
                      value={nutrition.calories_kcal * animatedMultiplier} 
                      unit="" 
                      onChange={(val) => updateCurrentMealNutrition('calories_kcal', val / mealScaleMultiplier)} 
                    />
                  </p>
                  <span className="text-xs font-bold text-zinc-600 dark:text-zinc-400 self-end mb-1">kcal</span>
                </div>
              </div>
            </div>
          </div>
  
          <div className="grid grid-cols-2 gap-3 sm:gap-3.5">
              <div className={cn(
                "p-3 px-3.5 sm:p-4 rounded-2xl border flex items-center justify-between gap-2.5 transition-all relative overflow-hidden",
                theme === 'light' 
                  ? "bg-gradient-to-br from-purple-500 to-indigo-600 border-purple-400/20 shadow-lg shadow-purple-500/10" 
                  : "bg-purple-950/30 border-purple-900/40",
                illumination && "illumination-protein"
              )}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Dumbbell className={cn("w-4 h-4 flex-shrink-0", theme === 'light' ? "text-white" : "text-purple-400")} />
                  <div className={cn("text-lg sm:text-xl font-bold tracking-tight truncate", theme === 'light' ? "text-white" : "text-[var(--text)]")}>
                    <EditableNutrient 
                      value={nutrition.protein_g * animatedMultiplier} 
                      unit="g" 
                      onChange={(val) => updateCurrentMealNutrition('protein_g', val / mealScaleMultiplier)} 
                    />
                  </div>
                </div>
                <p className={cn("text-[9px] sm:text-[11px] font-black uppercase tracking-widest whitespace-nowrap leading-tight shrink-0", theme === 'light' ? "text-white/80" : "text-purple-400")}>
                  {t.protein}
                </p>
              </div>
              <div className={cn(
                "p-3 px-3.5 sm:p-4 rounded-2xl border flex items-center justify-between gap-2.5 transition-all relative overflow-hidden",
                theme === 'light'
                  ? "bg-gradient-to-br from-red-500 to-orange-600 border-red-400/20 shadow-lg shadow-red-500/10"
                  : "bg-red-950/30 border-red-900/40",
                illumination && "illumination-carbs"
              )}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Apple className={cn("w-4 h-4 flex-shrink-0", theme === 'light' ? "text-white" : "text-red-400")} />
                  <div className={cn("text-lg sm:text-xl font-bold tracking-tight truncate", theme === 'light' ? "text-white" : "text-[var(--text)]")}>
                    <EditableNutrient 
                      value={nutrition.carbs_g * animatedMultiplier} 
                      unit="g" 
                      onChange={(val) => updateCurrentMealNutrition('carbs_g', val / mealScaleMultiplier)} 
                    />
                  </div>
                </div>
                <p className={cn("text-[9px] sm:text-[11px] font-black uppercase tracking-widest whitespace-nowrap leading-tight shrink-0", theme === 'light' ? "text-white/80" : "text-red-400")}>
                  {t.carbs}
                </p>
              </div>
              <div className={cn(
                "p-3 px-3.5 sm:p-4 rounded-2xl border flex items-center justify-between gap-2.5 transition-all relative overflow-hidden",
                theme === 'light'
                  ? "bg-gradient-to-br from-amber-500 to-orange-500 border-amber-400/20 shadow-lg shadow-amber-500/10"
                  : "bg-amber-950/30 border-amber-900/40",
                illumination && "illumination-fat"
              )}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Droplets className={cn("w-4 h-4 flex-shrink-0", theme === 'light' ? "text-white" : "text-amber-400")} />
                  <div className={cn("text-lg sm:text-xl font-bold tracking-tight truncate", theme === 'light' ? "text-white" : "text-[var(--text)]")}>
                    <EditableNutrient 
                      value={nutrition.fat_g * animatedMultiplier} 
                      unit="g" 
                      onChange={(val) => updateCurrentMealNutrition('fat_g', val / mealScaleMultiplier)} 
                    />
                  </div>
                </div>
                <p className={cn("text-[9px] sm:text-[11px] font-black uppercase tracking-widest whitespace-nowrap leading-tight shrink-0", theme === 'light' ? "text-white/80" : "text-amber-400")}>
                  {t.fat}
                </p>
              </div>
              <div className={cn(
                "p-3 px-3.5 sm:p-4 rounded-2xl border flex items-center justify-between gap-2.5 transition-all relative overflow-hidden",
                theme === 'light'
                  ? "bg-gradient-to-br from-pink-500 to-rose-600 border-pink-400/20 shadow-lg shadow-pink-500/10"
                  : "bg-pink-950/30 border-pink-900/40",
                illumination && "illumination-sugar"
              )}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Candy className={cn("w-4 h-4 flex-shrink-0", theme === 'light' ? "text-white" : "text-pink-400")} />
                  <div className={cn("text-lg sm:text-xl font-bold tracking-tight truncate", theme === 'light' ? "text-white" : "text-[var(--text)]")}>
                    <EditableNutrient 
                      value={nutrition.sugar_g * animatedMultiplier} 
                      unit="g" 
                      onChange={(val) => updateCurrentMealNutrition('sugar_g', val / mealScaleMultiplier)} 
                    />
                  </div>
                </div>
                <p className={cn("text-[9px] sm:text-[11px] font-black uppercase tracking-widest whitespace-nowrap leading-tight shrink-0", theme === 'light' ? "text-white/80" : "text-pink-400")}>
                  {t.sugar}
                </p>
              </div>
              <div className={cn(
                "p-3 px-3.5 sm:p-4 rounded-2xl border flex items-center justify-between gap-2.5 transition-all relative overflow-hidden",
                theme === 'light'
                  ? "bg-gradient-to-br from-emerald-500 to-teal-600 border-emerald-400/20 shadow-lg shadow-emerald-500/10"
                  : "bg-emerald-950/30 border-emerald-900/40",
                illumination && "illumination-fiber"
              )}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Wheat className={cn("w-4 h-4 flex-shrink-0", theme === 'light' ? "text-white" : "text-emerald-400")} />
                  <div className={cn("text-lg sm:text-xl font-bold tracking-tight truncate", theme === 'light' ? "text-white" : "text-[var(--text)]")}>
                    <EditableNutrient 
                      value={nutrition.fiber_g * animatedMultiplier} 
                      unit="g" 
                      onChange={(val) => updateCurrentMealNutrition('fiber_g', val / mealScaleMultiplier)} 
                    />
                  </div>
                </div>
                <p className={cn("text-[9px] sm:text-[11px] font-black uppercase tracking-widest whitespace-nowrap leading-tight shrink-0", theme === 'light' ? "text-white/80" : "text-emerald-400")}>
                  {t.fiber}
                </p>
              </div>
              <div className={cn(
                "p-3 px-3.5 sm:p-4 rounded-2xl border flex items-center justify-between gap-2.5 transition-all relative overflow-hidden",
                theme === 'light'
                  ? "bg-gradient-to-br from-blue-500 to-cyan-600 border-blue-400/20 shadow-lg shadow-blue-500/10"
                  : "bg-blue-950/30 border-blue-900/40",
                illumination && "illumination-sodium"
              )}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <CircleDot className={cn("w-4 h-4 flex-shrink-0", theme === 'light' ? "text-white" : "text-blue-400")} />
                  <div className={cn("text-lg sm:text-xl font-bold tracking-tight truncate", theme === 'light' ? "text-white" : "text-[var(--text)]")}>
                    <EditableNutrient 
                      value={((nutrition.sodium_mg || 0) / 1000) * animatedMultiplier} 
                      unit="g" 
                      onChange={(val) => updateCurrentMealNutrition('sodium_mg', (val / mealScaleMultiplier) * 1000)} 
                    />
                  </div>
                </div>
                <p className={cn("text-[9px] sm:text-[11px] font-black uppercase tracking-widest whitespace-nowrap leading-tight shrink-0", theme === 'light' ? "text-white/80" : "text-blue-400")}>
                  {t.sodium}
                </p>
              </div>
              {((nutrition.caffeine_mg !== undefined && nutrition.caffeine_mg > 0) || (data.foods || []).some(f => (f.caffeine_mg || 0) > 0)) && (() => {
                const caffeineLimit = rawGoals.caffeine_mg || goals.caffeine_mg || 400;
                const mealCaffeine = Math.round((nutrition.caffeine_mg || 0) * animatedMultiplier);
                const baseDayCaffeine = (isHistory || isLogged)
                  ? Math.max(0, selectedDayTotals.caffeine - Math.round(historyItem?.total_nutrition?.caffeine_mg || nutrition.caffeine_mg || 0))
                  : selectedDayTotals.caffeine;
                const projectedDayCaffeine = baseDayCaffeine + mealCaffeine;
                const isOver = mealCaffeine > 0 && projectedDayCaffeine > caffeineLimit;

                return (
                  <div className={cn(
                    "p-3 px-3.5 sm:p-4 rounded-2xl border flex items-center justify-between gap-2.5 transition-all relative overflow-hidden",
                    isOver 
                      ? (theme === 'light' ? "bg-gradient-to-br from-amber-600 to-red-700 border-red-500 shadow-lg shadow-red-500/20 ring-2 ring-red-500/40" : "bg-red-950/50 border-red-800/80 ring-1 ring-red-500/60")
                      : (theme === 'light'
                          ? "bg-gradient-to-br from-orange-700 to-orange-900 border-orange-600/20 shadow-lg shadow-orange-800/10"
                          : "bg-orange-950/30 border-orange-900/40"),
                    illumination && "illumination-caffeine"
                  )}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Coffee className={cn("w-4 h-4 flex-shrink-0", theme === 'light' ? "text-white" : (isOver ? "text-red-400" : "text-orange-400"))} />
                      <div className={cn("text-lg sm:text-xl font-bold tracking-tight truncate", theme === 'light' ? "text-white" : "text-[var(--text)]")}>
                        <EditableNutrient 
                          value={(nutrition.caffeine_mg || 0) * animatedMultiplier} 
                          unit="mg" 
                          format={(v) => Math.round(v).toString()}
                          onChange={(val) => updateCurrentMealNutrition('caffeine_mg', val / mealScaleMultiplier)} 
                        />
                      </div>
                    </div>
                    <p className={cn("text-[9px] sm:text-[11px] font-black uppercase tracking-widest whitespace-nowrap leading-tight shrink-0", theme === 'light' ? "text-white/80" : (isOver ? "text-red-300" : "text-orange-400"))}>
                      {t.caffeine || 'Caffeine'}
                    </p>
                  </div>
                );
              })()}
            </div>

        {/* Nutrient Pie Chart Card */}
        <div className={cn(
          "p-4 sm:p-5 rounded-3xl border transition-all space-y-4 my-2",
          theme === 'light'
            ? "bg-white border-zinc-200/80 shadow-sm text-zinc-900"
            : "bg-zinc-900/80 border-zinc-800 text-zinc-100"
        )}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col">
              <p className="text-zinc-500 dark:text-zinc-400 font-extrabold uppercase text-[10px] tracking-widest leading-tight">
                {t.macrosPieChart || 'Macronutrient Distribution'}
              </p>
              {lang === 'ua' && (
                <p className="text-emerald-500 dark:text-emerald-400 font-black uppercase text-xs tracking-tight leading-tight mt-0.5">
                  РОЗПОДІЛ НУТРІЄНТІВ
                </p>
              )}
            </div>
            <div className={cn("flex p-1 rounded-xl border transition-all", theme === 'light' ? "bg-zinc-100 border-zinc-200" : "bg-zinc-800 border-zinc-700")}>
              <button
                type="button"
                onClick={() => setDetailsChartType('macro')}
                className={cn(
                  "px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer select-none",
                  detailsChartType === 'macro' ? "bg-emerald-500 text-white shadow-sm" : "text-zinc-500 hover:text-emerald-500"
                )}
              >
                {t.macro || 'Macro'}
              </button>
              <button
                type="button"
                onClick={() => setDetailsChartType('micro')}
                className={cn(
                  "px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer select-none",
                  detailsChartType === 'micro' ? "bg-emerald-500 text-white shadow-sm" : "text-zinc-500 hover:text-emerald-500"
                )}
              >
                {t.micro || 'Micro'}
              </button>
            </div>
          </div>

          {(() => {
            const rawChartData = detailsChartType === 'macro' ? macroData : microData;
            const validChartData = rawChartData.filter(d => (d.value || 0) > 0);
            const totalChartValue = validChartData.reduce((sum, item) => sum + (item.value || 0), 0);

            return (
              <NutrientDonutChart
                data={rawChartData}
                centerLabel={`${totalChartValue >= 10 ? Math.round(totalChartValue) : totalChartValue.toFixed(1)}g`}
                centerSublabel={detailsChartType === 'macro' ? (lang === 'ua' ? 'Всього макро' : 'Total Macros') : (lang === 'ua' ? 'Всього мікро' : 'Total Micros')}
                theme={theme}
                lang={lang}
              />
            );
          })()}
        </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="space-y-4">
          {/* Expanded Nutritional Facts Spoiler */}
          {(isPremiumOrPro || hasExtraNutrients) && (
            <div className="space-y-2 relative">
              <button 
                onClick={() => setIsNutritionFactsExpanded(!isNutritionFactsExpanded)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl hover:border-amber-500 transition-all group card-shadow relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 p-1">
                  <Sparkles className="w-8 h-8 text-amber-500/20 rotate-12" />
                </div>
                <div className="flex items-center gap-3 text-amber-700 dark:text-amber-500 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors z-10">
                  <ClipboardList className="w-5 h-5" />
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                      {t.expandedNutritionalFacts || 'Expanded Nutritional Facts'}
                    </span>
                    <span className="text-[10px] font-bold text-amber-700/80 dark:text-amber-500/70 uppercase tracking-widest flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" /> {isPremiumOrPro ? (lang === 'ua' ? "Преміум та Pro доступ" : "Premium & Pro Insight") : (lang === 'ua' ? "Преміум аналіз" : "Premium Insight")}
                    </span>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: isNutritionFactsExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="z-10"
                >
                  <ChevronDown className="w-5 h-5 text-amber-700 dark:text-amber-500 group-hover:text-amber-600 dark:group-hover:text-amber-400" />
                </motion.div>
              </button>

              <AnimatePresence initial={false}>
                {isNutritionFactsExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    {/* Nutritional Facts Panel */}
                    <div className={cn(
                      "bg-white dark:bg-zinc-950 border-2 border-amber-500/50 rounded-2xl p-4 font-sans text-zinc-900 dark:text-zinc-100 shadow-lg shadow-amber-500/10 mb-4 relative overflow-hidden",
                      illumination && "illumination-food"
                    )}>
                      <div className="absolute -top-10 -right-10 opacity-[0.04] dark:opacity-[0.02] pointer-events-none">
                        <Sparkles className="w-64 h-64 text-amber-500 rotate-12" />
                      </div>
                      
                      <div className="relative z-10">
                        <div className="flex items-center justify-between border-b-8 border-amber-500/60 dark:border-amber-500/40 pb-1 mb-1">
                          <h2 className="text-2xl font-black text-zinc-950 dark:text-white tracking-tight">
                            {t.nutritionFacts || "Nutrition Facts"}
                          </h2>
                          <button
                            type="button"
                            onClick={() => {
                              enrichMealMicronutrients(data);
                              if (result && result.id === data.id) {
                                setResult({ ...data });
                              }
                              if (selectedHistoryItem && selectedHistoryItem.id === data.id) {
                                setSelectedHistoryItem({ ...selectedHistoryItem, ...data } as HistoryEntry);
                              }
                              const historyMatch = history.find(item => item.id === data.id);
                              if (historyMatch) {
                                updateMealInHistory({ ...historyMatch, ...data });
                              }
                              sounds.playPop();
                              triggerHaptic('SUCCESS');
                              toast.success(lang === 'ua' ? 'Мікронутрієнти оновлено за базою знань!' : 'Micronutrients updated from database!');
                            }}
                            title={lang === 'ua' ? 'Перерахувати нутрієнти' : 'Recalculate nutrients'}
                            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg bg-amber-500/15 dark:bg-amber-500/20 hover:bg-amber-500/25 dark:hover:bg-amber-500/30 text-amber-800 dark:text-amber-300 border border-amber-500/40 transition-all cursor-pointer select-none active:scale-95 shadow-xs"
                          >
                            <Sparkles className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                            <span>{lang === 'ua' ? 'Оновити AI' : 'Refresh AI'}</span>
                          </button>
                        </div>
                        
                        <div className="border-b-4 border-amber-500/60 dark:border-amber-500/40 py-1 mb-2">
                          <p className="font-bold text-zinc-800 dark:text-white uppercase text-xs tracking-wider mb-1">
                            {t.amountPerServing || "Amount Per Serving"}
                          </p>
                          <div className="flex justify-between items-baseline">
                            <p className="text-4xl font-black text-zinc-950 dark:text-white">{t.calories || "Calories"}</p>
                            <p className="text-4xl font-black text-amber-600 dark:text-amber-400">
                              <EditableNutrient 
                                value={nutrition.calories_kcal * animatedMultiplier} 
                                unit="" 
                                format={(v) => Math.round(v).toString()}
                                onChange={(val) => updateCurrentMealNutrition('calories_kcal', val / mealScaleMultiplier)} 
                              />
                            </p>
                          </div>
                        </div>
                        
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between border-b border-zinc-200 dark:border-zinc-800 py-1">
                            <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.totalFat || "Total Fat"}</span> <EditableNutrient value={nutrition.fat_g * animatedMultiplier} unit="g" onChange={(val) => updateCurrentMealNutrition('fat_g', val / mealScaleMultiplier)} /></p>
                            <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round(((nutrition.fat_g * animatedMultiplier) * 9 / 2000) * 100)}%</p>
                          </div>
                          {(() => {
                            const satFat = (nutrition as any).saturated_fat_g ?? (Math.round(((nutrition.fat_g || 0) * 0.35) * 10) / 10);
                            const scaledSatFat = satFat * animatedMultiplier;
                            const dvSatFat = Math.round((scaledSatFat / 20) * 100);
                            return (
                              <div className="flex justify-between border-b border-zinc-200 dark:border-zinc-800 py-1 pl-4">
                                <p className="text-zinc-600 dark:text-zinc-400">{t.saturatedFat || "Saturated Fat"} {scaledSatFat >= 10 ? Math.round(scaledSatFat) : scaledSatFat.toFixed(1)}g</p>
                                <p className="font-bold text-amber-600/80 dark:text-amber-400/80">{dvSatFat}%</p>
                              </div>
                            );
                          })()}
                          <div className="flex justify-between border-b border-zinc-200 dark:border-zinc-800 py-1">
                            <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.sodium || "Sodium"}</span> <EditableNutrient value={nutrition.sodium_mg * animatedMultiplier} unit="mg" onChange={(val) => updateCurrentMealNutrition('sodium_mg', val / mealScaleMultiplier)} /></p>
                            <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round(((nutrition.sodium_mg * animatedMultiplier) / 2300) * 100)}%</p>
                          </div>
                          <div className="flex justify-between border-b border-zinc-200 dark:border-zinc-800 py-1">
                            <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.totalCarbohydrate || "Total Carbohydrate"}</span> <EditableNutrient value={nutrition.carbs_g * animatedMultiplier} unit="g" onChange={(val) => updateCurrentMealNutrition('carbs_g', val / mealScaleMultiplier)} /></p>
                            <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round(((nutrition.carbs_g * animatedMultiplier) * 4 / 2000) * 100)}%</p>
                          </div>
                          {((nutrition.caffeine_mg !== undefined && nutrition.caffeine_mg > 0) || data.foods.some(f => f.caffeine_mg !== undefined && f.caffeine_mg > 0) || data.foods.some(f => f.category === 'drink')) && (
                            <div className="flex justify-between border-b border-zinc-200 dark:border-zinc-800 py-1">
                              <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.caffeine || "Caffeine"}</span> <EditableNutrient value={(nutrition.caffeine_mg || 0) * animatedMultiplier} unit="mg" format={(v) => Math.round(v).toString()} onChange={(val) => updateCurrentMealNutrition('caffeine_mg', val / mealScaleMultiplier)} /></p>
                            </div>
                          )}
                          <div className="flex justify-between border-b border-zinc-200 dark:border-zinc-800 py-1 pl-4">
                            <p className="text-zinc-600 dark:text-zinc-400">{t.fiber || "Dietary Fiber"} <EditableNutrient value={nutrition.fiber_g * animatedMultiplier} unit="g" onChange={(val) => updateCurrentMealNutrition('fiber_g', val / mealScaleMultiplier)} /></p>
                            <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round(((nutrition.fiber_g * animatedMultiplier) / 28) * 100)}%</p>
                          </div>
                          <div className="flex justify-between border-b border-zinc-200 dark:border-zinc-800 py-1 pl-4">
                            <p className="text-zinc-600 dark:text-zinc-400">{t.sugar || "Total Sugars"} <EditableNutrient value={nutrition.sugar_g * animatedMultiplier} unit="g" onChange={(val) => updateCurrentMealNutrition('sugar_g', val / mealScaleMultiplier)} /></p>
                          </div>
                          {subscriptionTier === 'free' && !isPremiumOrPro ? (
                             <div className="border border-amber-500/30 rounded-xl p-3 bg-amber-50/80 dark:bg-zinc-900/60 text-center my-2 relative overflow-hidden backdrop-blur-sm shadow-xs">
                               <Lock className="w-4 h-4 text-amber-600 dark:text-amber-500 mx-auto mb-1 animate-bounce" />
                               <p className="font-bold text-[11px] text-zinc-900 dark:text-white uppercase tracking-wider">{lang === 'ua' ? "ОБМЕЖЕНИЙ ДОСТУП" : "Gated Extended Insights"}</p>
                               <p className="text-[10px] text-zinc-600 dark:text-zinc-400 mt-1 max-w-[220px] mx-auto">
                                 {lang === 'ua' 
                                   ? "Оновіть до Premium, щоб розблокувати детальний аналіз мікронутрієнтів та мінералів!" 
                                   : "Upgrade to Premium to unlock micro-nutrients & mineral analysis!"}
                               </p>
                               <button 
                                 onClick={() => {
                                   setView('goals');
                                   setIsSubscriptionExpanded(true);
                                 }}
                                 className="mt-2 px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white dark:text-black text-[10px] font-black rounded-lg transition-colors cursor-pointer select-none shadow-sm"
                               >
                                 {lang === 'ua' ? "Розблокувати Premium" : "Unlock Premium"}
                               </button>
                             </div>
                           ) : (
                             <>
                               {((nutrition.potassium_mg !== undefined) || isPremiumOrPro) && (
                             <div className="border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <div className="flex justify-between">
                                 <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.potassium || "Potassium"}</span> <EditableNutrient value={(nutrition.potassium_mg || 0) * animatedMultiplier} unit="mg" onChange={(val) => updateCurrentMealNutrition('potassium_mg', val / mealScaleMultiplier)} /></p>
                                 <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.potassium_mg || 0) * animatedMultiplier) / 4700) * 100)}%</p>
                               </div>
                               {renderDeficiencyIndicator('potassium_mg')}
                             </div>
                           )}
                           {((nutrition.cholesterol_mg !== undefined) || isPremiumOrPro) && (
                             <div className="flex justify-between border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.cholesterol || "Cholesterol"}</span> <EditableNutrient value={(nutrition.cholesterol_mg || 0) * animatedMultiplier} unit="mg" onChange={(val) => updateCurrentMealNutrition('cholesterol_mg', val / mealScaleMultiplier)} /></p>
                               <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.cholesterol_mg || 0) * animatedMultiplier) / 300) * 100)}%</p>
                             </div>
                           )}
                           {((nutrition.vitamin_c_mg !== undefined) || isPremiumOrPro) && (
                             <div className="border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <div className="flex justify-between">
                                 <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.vitaminC || "Vitamin C"}</span> <EditableNutrient value={(nutrition.vitamin_c_mg || 0) * animatedMultiplier} unit="mg" onChange={(val) => updateCurrentMealNutrition('vitamin_c_mg', val / mealScaleMultiplier)} /></p>
                                 <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.vitamin_c_mg || 0) * animatedMultiplier) / 90) * 100)}%</p>
                               </div>
                               {renderDeficiencyIndicator('vitamin_c_mg')}
                             </div>
                           )}
                           {((nutrition.calcium_mg !== undefined) || isPremiumOrPro) && (
                             <div className="border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <div className="flex justify-between">
                                 <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.calcium || "Calcium"}</span> <EditableNutrient value={(nutrition.calcium_mg || 0) * animatedMultiplier} unit="mg" onChange={(val) => updateCurrentMealNutrition('calcium_mg', val / mealScaleMultiplier)} /></p>
                                 <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.calcium_mg || 0) * animatedMultiplier) / 1300) * 100)}%</p>
                               </div>
                               {renderDeficiencyIndicator('calcium_mg')}
                             </div>
                           )}
                           {((nutrition.iron_mg !== undefined) || isPremiumOrPro) && (
                             <div className="border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <div className="flex justify-between">
                                 <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.iron || "Iron"}</span> <EditableNutrient value={(nutrition.iron_mg || 0) * animatedMultiplier} unit="mg" onChange={(val) => updateCurrentMealNutrition('iron_mg', val / mealScaleMultiplier)} /></p>
                                 <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.iron_mg || 0) * animatedMultiplier) / 18) * 100)}%</p>
                               </div>
                               {renderDeficiencyIndicator('iron_mg')}
                             </div>
                           )}
                           {((nutrition.vitamin_a_mcg !== undefined) || isPremiumOrPro) && (
                             <div className="border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <div className="flex justify-between">
                                 <p><span className="font-black text-zinc-900 dark:text-zinc-100">{lang === 'ua' ? "Вітамін A" : "Vitamin A"}</span> <EditableNutrient value={(nutrition.vitamin_a_mcg || 0) * animatedMultiplier} unit="mcg" onChange={(val) => updateCurrentMealNutrition('vitamin_a_mcg', val / mealScaleMultiplier)} /></p>
                                 <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.vitamin_a_mcg || 0) * animatedMultiplier) / 900) * 100)}%</p>
                               </div>
                               {renderDeficiencyIndicator('vitamin_a_mcg')}
                             </div>
                           )}
                           {((nutrition.vitamin_d_mcg !== undefined) || isPremiumOrPro) && (
                             <div className="border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <div className="flex justify-between">
                                 <p><span className="font-black text-zinc-900 dark:text-zinc-100">{lang === 'ua' ? "Вітамін D" : "Vitamin D"}</span> <EditableNutrient value={(nutrition.vitamin_d_mcg || 0) * animatedMultiplier} unit="mcg" onChange={(val) => updateCurrentMealNutrition('vitamin_d_mcg', val / mealScaleMultiplier)} /></p>
                                 <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.vitamin_d_mcg || 0) * animatedMultiplier) / 20) * 100)}%</p>
                               </div>
                               {renderDeficiencyIndicator('vitamin_d_mcg')}
                             </div>
                           )}
                           {((nutrition.vitamin_b12_mcg !== undefined) || isPremiumOrPro) && (
                             <div className="border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <div className="flex justify-between">
                                 <p><span className="font-black text-zinc-900 dark:text-zinc-100">{lang === 'ua' ? "Вітамін B12" : "Vitamin B12"}</span> <EditableNutrient value={(nutrition.vitamin_b12_mcg || 0) * animatedMultiplier} unit="mcg" onChange={(val) => updateCurrentMealNutrition('vitamin_b12_mcg', val / mealScaleMultiplier)} /></p>
                                 <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.vitamin_b12_mcg || 0) * animatedMultiplier) / 2.4) * 100)}%</p>
                               </div>
                               {renderDeficiencyIndicator('vitamin_b12_mcg')}
                             </div>
                           )}
                           {((nutrition.magnesium_mg !== undefined) || isPremiumOrPro) && (
                             <div className="border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <div className="flex justify-between">
                                 <p><span className="font-black text-zinc-900 dark:text-zinc-100">{lang === 'ua' ? "Магній" : "Magnesium"}</span> <EditableNutrient value={(nutrition.magnesium_mg || 0) * animatedMultiplier} unit="mg" onChange={(val) => updateCurrentMealNutrition('magnesium_mg', val / mealScaleMultiplier)} /></p>
                                 <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.magnesium_mg || 0) * animatedMultiplier) / 420) * 100)}%</p>
                               </div>
                               {renderDeficiencyIndicator('magnesium_mg')}
                             </div>
                           )}
                           {((nutrition.zinc_mg !== undefined) || isPremiumOrPro) && (
                             <div className="border-b border-zinc-200 dark:border-zinc-800 py-1">
                               <div className="flex justify-between">
                                 <p><span className="font-black text-zinc-900 dark:text-zinc-100">{lang === 'ua' ? "Цинк" : "Zinc"}</span> <EditableNutrient value={(nutrition.zinc_mg || 0) * animatedMultiplier} unit="mg" onChange={(val) => updateCurrentMealNutrition('zinc_mg', val / mealScaleMultiplier)} /></p>
                                 <p className="font-bold text-amber-600 dark:text-amber-400">{Math.round((((nutrition.zinc_mg || 0) * animatedMultiplier) / 11) * 100)}%</p>
                               </div>
                               {renderDeficiencyIndicator('zinc_mg')}
                             </div>
                           )}
                             </>
                           )}
                          <div className="flex justify-between border-b-8 border-amber-500/60 dark:border-amber-500/40 py-1">
                            <p><span className="font-black text-zinc-900 dark:text-zinc-100">{t.protein || "Protein"}</span> <EditableNutrient value={nutrition.protein_g * animatedMultiplier} unit="g" onChange={(val) => updateCurrentMealNutrition('protein_g', val / mealScaleMultiplier)} /></p>
                          </div>
                        </div>
                        <p className="text-[10px] mt-3 leading-tight text-zinc-600 dark:text-zinc-400 italic">
                          {t.dvNote || "* The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice."}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <div className="px-2 flex items-center justify-between gap-2">
            <h3 className="text-lg font-bold">{t.foodItems}</h3>
          </div>

          <div className={cn(illumination ? "space-y-[32px]" : "space-y-3")}>
            <AnimatePresence mode="popLayout">
              {data.foods.map((food, idx) => (
                <motion.div 
                  key={food.name + '-' + idx} 
                  initial={{ opacity: 0, height: 0, scale: 0.92, y: 15 }}
                  animate={{ opacity: 1, height: "auto", scale: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0, scale: 0.92, y: 15 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 140,
                    damping: 15,
                    delay: idx * 0.05
                  }}
                  className="space-y-2 overflow-visible px-4 py-4 -mx-4 -my-4"
                >
                  <div
                    className={cn(
                      "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 group transition-all card-shadow",
                      illumination ? (isFoodItemLiquid(food) ? "illumination-water" : "illumination-food") : "",
                      expandedFoodIdx === idx ? "border-emerald-500 ring-1 ring-emerald-500/20" : "hover:border-emerald-500"
                    )}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className="flex items-center gap-3 sm:gap-4 flex-1 cursor-pointer min-w-0" onClick={() => setExpandedFoodIdx(expandedFoodIdx === idx ? null : idx)}>
                        <div className={cn(
                          "p-2 sm:p-3 rounded-xl transition-colors flex-shrink-0 flex items-center justify-center min-w-[44px] min-h-[44px] border transition-all",
                          theme === 'light' ? "bg-white border-zinc-100 shadow-sm" : "bg-white dark:bg-zinc-800 border-zinc-100 dark:border-zinc-700",
                          expandedFoodIdx === idx ? "bg-emerald-500/10 border-emerald-500/20" : "group-hover:bg-emerald-500/10 group-hover:border-emerald-500/20"
                        )}>
                          <motion.div 
                            className={cn(
                              "transition-transform group-hover:scale-110",
                              expandedFoodIdx === idx ? "text-emerald-500" : "text-zinc-500 dark:text-zinc-400 group-hover:text-emerald-500"
                            )}
                            initial={{ scale: 0.3, rotate: -25 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ 
                              type: "spring",
                              stiffness: 220,
                              damping: 12,
                              delay: idx * 0.05
                            }}
                            whileHover={{ scale: 1.2, rotate: 10 }}
                          >
                            {getFoodIcon(food)}
                          </motion.div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <p className={cn(
                              "font-bold truncate text-sm sm:text-base hover:underline",
                              excludedFoods.includes(food.name) && "line-through opacity-60 text-zinc-400"
                            )}>
                              {food.name}
                            </p>
                            {excludedFoods.includes(food.name) && (
                              <span className="text-[8px] bg-red-500/15 text-red-500 px-1.5 py-0.5 rounded-full font-black uppercase tracking-wider shrink-0">
                                {lang === 'ua' ? 'Виключено' : 'Excluded'}
                              </span>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400 font-semibold whitespace-nowrap mt-0.5">
                            {typeof food.amount === 'number' && food.unit ? `${food.amount} ${food.unit}` : (food.portion || `${food.amount || 100} g`)}
                          </p>
                        </div>
                      </div>
                    </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6 border-t sm:border-t-0 border-zinc-100 dark:border-zinc-800 pt-3 sm:pt-0 flex-shrink-0">
                        <div className="flex items-center gap-3 sm:gap-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowExplanation(food.name, food.calories_kcal);
                            }}
                            className="p-2 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded-full transition-colors text-emerald-500 flex-shrink-0"
                            title={lang.startsWith('en') ? 'Why this food?' : 'Чому ця їжа?'}
                          >
                            <Info className="w-4.5 h-4.5" />
                          </button>
                          <div className="text-left sm:text-right min-w-0">
                            <div className="flex items-center sm:justify-end gap-1.5">
                              <Flame className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                              <p className="font-bold text-sm sm:text-base whitespace-nowrap">{(food.calories_kcal || 0).toFixed(0)} kcal</p>
                            </div>
                            <div className="flex items-center sm:justify-end gap-2.5 text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 font-medium whitespace-nowrap">
                              <span className="flex items-center gap-1"><Dumbbell className="w-2.5 h-2.5 opacity-70" /> {(food.protein_g || 0).toFixed(1)}g</span>
                              <span className="w-px h-2 bg-zinc-200 dark:bg-zinc-700 mx-0.5 hidden sm:block"></span>
                              <span className="flex items-center gap-1"><Apple className="w-2.5 h-2.5 opacity-70" /> {(food.carbs_g || 0).toFixed(1)}g</span>
                              <span className="w-px h-2 bg-zinc-200 dark:bg-zinc-700 mx-0.5 hidden sm:block"></span>
                              <span className="flex items-center gap-1"><Droplets className="w-2.5 h-2.5 opacity-70" /> {(food.fat_g || 0).toFixed(1)}g</span>
                            </div>
                          </div>
                        </div>
                        <motion.button
                          animate={{ rotate: expandedFoodIdx === idx ? 90 : 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedFoodIdx(expandedFoodIdx === idx ? null : idx);
                          }}
                          className={cn(
                            "p-2 rounded-full transition-all flex-shrink-0",
                            theme === 'light' ? "bg-zinc-50 hover:bg-zinc-100 border-zinc-100" : "bg-zinc-800/50 hover:bg-zinc-800 border-zinc-700",
                            expandedFoodIdx === idx ? "text-emerald-500 border-emerald-500/20" : "text-zinc-400 hover:text-emerald-500"
                          )}
                        >
                          <ChevronRight className="w-4.5 h-4.5" />
                        </motion.button>
                      </div>
                </div>

                <AnimatePresence>
                  {expandedFoodIdx === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ 
                        type: "spring", 
                        stiffness: 150, 
                        damping: 16 
                      }}
                      className="overflow-hidden"
                    >
                      <div className={cn("border rounded-[24px] p-5 mx-2 mb-2 space-y-4 shadow-sm", theme === 'light' ? "bg-zinc-50 border-zinc-200/50" : "bg-zinc-900/30 border-zinc-800")}>
                        {/* Unified AI Portion Suggester Section */}
                        <div className={cn("space-y-4 border-b pb-4", theme === 'light' ? "border-zinc-200" : "border-zinc-800")}>
                          <div className="flex flex-col sm:flex-row gap-4">
                            {/* Left: Manual Gram/Piece Input */}
                            {(typeof food.amount === 'number' && food.unit) && (
                              <div className="flex-1 space-y-1.5">
                                <p className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest pl-1">
                                  {lang === 'ua' ? 'Ручна коригування ваги' : 'Manual Weight Adjustment'}
                                </p>
                                <div className="relative group/input">
                                  <input
                                    type="number"
                                    value={food.amount === 0 ? '' : food.amount}
                                    onChange={(e) => updateFoodAmount(idx, parseFloat(e.target.value) || 0)}
                                    onFocus={(e) => e.target.select()}
                                    className={cn(
                                      "w-full border rounded-2xl px-4 py-2.5 text-sm font-bold focus:border-emerald-500 outline-none transition-all pr-12 shadow-sm text-[var(--text)]",
                                      theme === 'light' 
                                        ? "bg-white border-zinc-200 group-hover/input:border-zinc-300" 
                                        : "bg-zinc-800 border-zinc-700 group-hover/input:border-zinc-600"
                                    )}
                                    placeholder={t.amountPlaceholder}
                                  />
                                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest pointer-events-none">
                                    {food.unit}
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Right: AI Portion Parser / Natural Language Converter */}
                            <div className="flex-[1.5] space-y-1.5">
                              <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest pl-1 flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-emerald-500 animate-pulse" />
                                {lang === 'ua' ? 'Конвертер порцій штучним інтелектом' : 'AI Portion Text Parser'}
                              </p>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={customPortionText[idx] || ''}
                                  onChange={(e) => setCustomPortionText(prev => ({ ...prev, [idx]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleParsePortionText(idx, food.name);
                                    }
                                  }}
                                  placeholder={lang === 'ua' ? 'наприклад, половина тарілки, жменька' : 'e.g., half a bowl, 2 thick slices, handful'}
                                  className={cn(
                                    "flex-1 border rounded-2xl px-4 py-2.5 text-sm font-medium focus:border-emerald-500 outline-none transition-all shadow-sm text-[var(--text)]",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 placeholder:text-zinc-400"
                                      : "bg-zinc-800 border-zinc-700 placeholder:text-zinc-500"
                                  )}
                                />
                                <button
                                  type="button"
                                  disabled={parsingPortionIdx === idx || !(customPortionText[idx] || '').trim()}
                                  onClick={() => handleParsePortionText(idx, food.name)}
                                  className={cn(
                                    "px-4 rounded-2xl text-[10px] font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 shadow-sm min-h-[44px]",
                                    (customPortionText[idx] || '').trim() 
                                      ? "bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer"
                                      : (theme === 'light'
                                        ? "bg-zinc-100 text-zinc-400 border border-zinc-200 cursor-not-allowed"
                                        : "bg-zinc-800 text-zinc-600 border border-zinc-700 cursor-not-allowed")
                                  )}
                                >
                                  {parsingPortionIdx === idx ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <>
                                      <Sparkles className="w-3.5 h-3.5" />
                                      {lang === 'ua' ? 'Конвертувати' : 'Convert'}
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* AI Explanation feedback */}
                          {parseExplanation[idx] && (
                            <motion.p
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 italic bg-emerald-500/5 border border-emerald-500/10 px-3.5 py-2 rounded-xl flex items-start gap-1.5"
                            >
                              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span>{parseExplanation[idx]}</span>
                            </motion.p>
                          )}

                          {/* Quick Weight, Unit, and Portion Size Controls */}
                          <div className="space-y-3 pt-2">
                            <WeightUnitSpoiler
                              food={food}
                              idx={idx}
                              lang={lang}
                              theme={theme}
                              onUpdateFood={(foodIndex, updates) => updateFoodItem(foodIndex, updates)}
                              currentCals={Math.round(food.calories_kcal || 0)}
                              currentProtein={(food.protein_g || 0).toFixed(1)}
                            />
                            <PortionPickerSpoiler
                              food={food}
                              originalFood={originalResult?.foods?.[idx] || food}
                              idx={idx}
                              lang={lang}
                              theme={theme}
                              onUpdateFood={(foodIndex, updates) => updateFoodItem(foodIndex, updates)}
                              t={t}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-5 gap-x-4">
                          <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                            <div className="flex items-center gap-1.5 text-zinc-400">
                              <Candy className="w-3 h-3" />
                              <p className="text-[10px] font-bold uppercase tracking-widest">{t.sugar}</p>
                            </div>
                            <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.sugar_g || 0).toFixed(2)}g</p>
                          </div>
                          <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                            <div className="flex items-center gap-1.5 text-zinc-400">
                              <Wheat className="w-3 h-3" />
                              <p className="text-[10px] font-bold uppercase tracking-widest">{t.fiber}</p>
                            </div>
                            <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.fiber_g || 0).toFixed(2)}g</p>
                          </div>
                          <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                            <div className="flex items-center gap-1.5 text-zinc-400">
                              <CircleDot className="w-3 h-3" />
                              <p className="text-[10px] font-bold uppercase tracking-widest">{t.sodium}</p>
                            </div>
                            <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.sodium_mg || 0).toFixed(2)}mg</p>
                          </div>
                          {food.caffeine_mg !== undefined && food.caffeine_mg > 0 && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Coffee className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{t.caffeine || 'Caffeine'}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{Math.round(food.caffeine_mg)}mg</p>
                            </div>
                          )}
                          {food.potassium_mg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Zap className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{t.potassium}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.potassium_mg || 0).toFixed(2)}mg</p>
                            </div>
                          )}
                          {food.cholesterol_mg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Heart className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{t.cholesterol}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.cholesterol_mg || 0).toFixed(2)}mg</p>
                            </div>
                          )}
                          {food.vitamin_c_mg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Leaf className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{t.vitaminC}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.vitamin_c_mg || 0).toFixed(2)}mg</p>
                            </div>
                          )}
                          {food.calcium_mg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Milk className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{t.calcium}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.calcium_mg || 0).toFixed(2)}mg</p>
                            </div>
                          )}
                          {food.iron_mg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Dumbbell className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{t.iron}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.iron_mg || 0).toFixed(2)}mg</p>
                            </div>
                          )}
                          {food.vitamin_a_mcg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Eye className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{lang === 'ua' ? "Вітамін A" : "Vitamin A"}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.vitamin_a_mcg || 0).toFixed(1)}mcg</p>
                            </div>
                          )}
                          {food.vitamin_d_mcg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Sun className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{lang === 'ua' ? "Вітамін D" : "Vitamin D"}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.vitamin_d_mcg || 0).toFixed(1)}mcg</p>
                            </div>
                          )}
                          {food.vitamin_b12_mcg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Activity className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{lang === 'ua' ? "Вітамін B12" : "Vitamin B12"}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.vitamin_b12_mcg || 0).toFixed(2)}mcg</p>
                            </div>
                          )}
                          {food.magnesium_mg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Sparkles className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{lang === 'ua' ? "Магній" : "Magnesium"}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.magnesium_mg || 0).toFixed(1)}mg</p>
                            </div>
                          )}
                          {food.zinc_mg !== undefined && (
                            <div className={cn("space-y-1 transition-all rounded-xl", illumination && (isFoodItemLiquid(food) ? "illumination-water p-2 -m-2 border border-blue-500/20" : "illumination-food p-2 -m-2 border border-emerald-500/20"))}>
                              <div className="flex items-center gap-1.5 text-zinc-400">
                                <Shield className="w-3 h-3" />
                                <p className="text-[10px] font-bold uppercase tracking-widest">{lang === 'ua' ? "Цинк" : "Zinc"}</p>
                              </div>
                              <p className="font-bold text-zinc-700 dark:text-zinc-200 text-sm">{(food.zinc_mg || 0).toFixed(1)}mg</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        </div>

        {/* Daily Progress After This Meal */}
        <div className="space-y-10">
          {(!isHistory || view === 'details') && (
            <div className="pt-2">
              <AnimatePresence mode="wait">
                {(() => {
                  const mealId = isValidMealId(data.id) ? data.id : null;
                  const mealNameLower = (data.meal_name || '').trim().toLowerCase();
                  const isFavorite = favorites.some(f => 
                    (mealId && isValidMealId(f.id) && f.id === mealId) ||
                    (mealNameLower !== '' && (f.meal_name || '').trim().toLowerCase() === mealNameLower)
                  );
                  
                  return (
                    <div className="space-y-3">
                      <div className="flex gap-4 w-full relative">
                      <AnimatePresence mode="popLayout">
                        {isLogged && (
                          <motion.div
                            layout
                            key="countdown"
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 25 }}
                            className="flex-1 flex items-center justify-center text-emerald-500 font-bold text-sm uppercase tracking-widest"
                          >
                            Closing in {countdown}s
                          </motion.div>
                        )}

                        <motion.button
                          layout
                          key="add-favorite-new"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ type: "spring", stiffness: 200, damping: 20, mass: 1 }}
                          onClick={() => {
                            sounds.playPop();
                            if (isFavorite) {
                              const targetFav = favorites.find(f => 
                                (mealId && isValidMealId(f.id) && f.id === mealId) ||
                                (mealNameLower !== '' && (f.meal_name || '').trim().toLowerCase() === mealNameLower)
                              );
                              if (targetFav && isValidMealId(targetFav.id)) {
                                removeFromFavorites(targetFav.id);
                              } else if (mealId) {
                                removeFromFavorites(mealId);
                              } else {
                                const realIndex = favorites.findIndex(f => (f.meal_name || '').trim().toLowerCase() === mealNameLower && mealNameLower !== '');
                                if (realIndex !== -1) removeFromFavorites(realIndex);
                              }
                            } else {
                              const entryId = mealId || ((typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15) + Date.now().toString(36));
                              saveToFavorites({
                                ...data,
                                image: mealImage || data.image || undefined,
                                id: entryId,
                                timestamp: data.timestamp || Date.now(),
                                isFavorite: true
                              });
                            }
                          }}
                          className={cn(
                            "py-4 px-6 rounded-2xl font-bold text-[11px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 border", 
                            theme === 'light' 
                              ? "bg-white hover:bg-zinc-50 text-zinc-900 border-zinc-200 shadow-sm" 
                              : "bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 border-zinc-200 dark:border-zinc-700",
                            (isHistory && view === 'details') ? "w-full" : "flex-1"
                          )}
                        >
                          <Heart className={cn("w-5 h-5 text-emerald-500", isFavorite && "fill-emerald-500")} />
                          {isFavorite ? (lang === 'ua' ? 'Вже додано' : 'Already Added') : (lang === 'ua' ? 'Додати до улюблених' : 'Add to loved meals')}
                        </motion.button>

                        {!isHistory && !isLogged && (
                          <motion.button
                            layout
                            key="log-history"
                            initial={{ opacity: 0, scale: 0.95, x: 20 }}
                            animate={isDetailsLoggedClicked ? {
                              scale: [1, 1.15, 0.85, 1.1, 0.95, 1.02, 1],
                            } : { opacity: 1, scale: 1, x: 0 }}
                            exit={{ opacity: 0, scale: 0.95, x: 50 }}
                            transition={isDetailsLoggedClicked ? {
                              duration: 0.6,
                              ease: "easeInOut"
                            } : { type: "spring", stiffness: 300, damping: 25 }}
                            onClick={() => {
                              sounds.playPop();
                              setIsDetailsLoggedClicked(true);
                              setTimeout(() => setIsDetailsLoggedClicked(false), 600);
                              saveToHistory(data, mealImage!);
                            }}
                            className={cn(
                              "flex-1 bg-emerald-500 py-4 px-6 rounded-2xl font-bold text-[11px] uppercase tracking-widest hover:bg-emerald-600 transition-all text-white shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2 cursor-pointer",
                              (!isHistory && !isLogged) && "btn-active-border"
                            )}
                          >
                            <CheckCircle2 className="w-5 h-5" />
                            {t.logThisMeal}
                          </motion.button>
                        )}
                      </AnimatePresence>
                    </div>
                    </div>
                  );
                })()}
              </AnimatePresence>
            </div>
          )}
          <DailyProgressSummary 
            totals={isHistory || isLogged ? projectedTotals : selectedDayTotals} 
            projectedTotals={!isHistory && !isLogged ? projectedTotals : undefined} 
            goals={goals} 
            title={isHistory || isLogged ? t.dailyProgress : t.updatedProgress} 
            selectedDate={selectedDate}
            lang={lang}
            theme={theme}
            t={t}
            illumination={illumination}
            onNutrientClick={handleNutrientClick}
            viewMode={dailyProgressViewMode} skin={skin}
          />
        </div>
      </motion.div>
    );
  };

  const renderSplashScreen = () => {
    return (
      <div className={cn(
        "min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 relative overflow-hidden transition-colors duration-300", 
        skin === 'stalker' ? "skin-stalker bg-black text-[#4ade80]" : skin === 'anime' ? "skin-anime bg-[#0A0B10] text-white" : skin === 'cyberpunk' ? "cyberpunk bg-[#050510] text-[#00ffcc]" : "bg-[var(--bg)] text-[var(--text)]"
      )}>
        {skin === 'stalker' && <div className="skin-stalker-crt" />}
        {skin === 'anime' && <div className="skin-anime-crt" />}
        
        {/* Background Ambient Glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-25 dark:opacity-20 transform-gpu">
          <motion.div 
            animate={{ scale: [1, 1.25, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-24 -left-24 w-[420px] h-[420px] bg-[radial-gradient(circle,rgba(16,185,129,0.3)_0%,transparent_70%)] rounded-full"
          />
          <motion.div 
            animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -bottom-24 -right-24 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(52,211,153,0.25)_0%,transparent_70%)] rounded-full"
          />
        </div>

        {/* Brand Card Container with Entrance Animation */}
        <motion.div
          layoutId="brand-card-container"
          initial={{ opacity: 0, y: 22, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "relative z-10 w-full max-w-xs sm:max-w-md p-7 sm:p-10 rounded-[32px] sm:rounded-[40px]",
            "flex flex-col items-center text-center overflow-hidden transition-all duration-300",
            skin === 'stalker'
              ? "bg-black/90 border border-[#4ade80]/50 text-[#4ade80] shadow-[0_0_35px_rgba(74,222,128,0.25)]"
              : skin === 'anime'
                ? "bg-[#1c101a]/90 border border-pink-500/50 text-white shadow-[0_0_35px_rgba(236,72,153,0.3)]"
                : skin === 'cyberpunk'
                  ? "bg-[#0a0a1a]/95 border-2 border-[#FCEE0A] text-[#00ffcc] shadow-[0_0_40px_rgba(252,238,10,0.35)] rounded-none"
                  : "bg-[var(--card-bg)] border border-[var(--card-border)] text-[var(--text)] shadow-[0_20px_50px_rgba(0,0,0,0.06)] dark:shadow-[0_25px_60px_rgba(0,0,0,0.7)]"
          )}
        >
          {/* Curved Top Glow Line */}
          <div className={cn("absolute top-0 left-8 right-8 h-[2px] bg-gradient-to-r from-transparent via-[#10B981] to-transparent opacity-90 blur-[0.5px]", skin === 'cyberpunk' && "via-[#FCEE0A]")} />
          <div className={cn("absolute top-0 left-12 right-12 h-6 bg-[#10B981]/12 blur-md pointer-events-none rounded-t-full", skin === 'cyberpunk' && "bg-[#FCEE0A]/15")} />

          {/* Green Squircle App Icon */}
          <motion.div 
            layoutId="brand-logo-container"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.45, type: "spring", stiffness: 200, damping: 15 }}
            className="relative my-2"
          >
            <div className={cn("absolute -inset-3 bg-[#10B981]/25 blur-xl rounded-full pointer-events-none animate-pulse", skin === 'cyberpunk' && "bg-[#FCEE0A]/30")} />
            <motion.div layoutId="brand-logo-squircle" className={cn(
              "relative w-24 h-24 sm:w-28 sm:h-28 rounded-[28px] sm:rounded-[32px] flex items-center justify-center transition-transform duration-300 hover:scale-105",
              skin === 'stalker' 
                ? "bg-[#4ade80]/20 border-2 border-[#4ade80] text-[#4ade80] shadow-[0_0_30px_rgba(74,222,128,0.4)]" 
                : skin === 'anime'
                  ? "bg-pink-500 text-white shadow-[0_12px_32px_rgba(236,72,153,0.5)]"
                  : skin === 'cyberpunk'
                    ? "bg-[#050510] border-2 border-[#FCEE0A] text-[#FCEE0A] shadow-[0_0_30px_rgba(252,238,10,0.5)] rounded-none"
                    : "bg-[#10B981] text-white shadow-[0_12px_32px_rgba(16,185,129,0.35)]"
            )}>
              {/* Stylized Fork & Knife Logo SVG */}
              <motion.svg layoutId="brand-logo-svg" className="w-20 h-20 sm:w-24 sm:h-24 text-white drop-shadow-xs relative z-10" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Fork on Left (Ψ) */}
                <path
                  d="M 21 17 V 29 C 21 34.5 32 34.5 32 29 V 17 M 26.5 17 V 30 M 26.5 33.5 V 47"
                  stroke="currentColor"
                  strokeWidth="3.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Knife on Right (q) */}
                <path
                  d="M 42.5 17 V 47 M 42.5 17 C 49.5 17 49.5 31 42.5 31"
                  stroke="currentColor"
                  strokeWidth="3.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </motion.div>
          </motion.div>

          {/* Brand Title with Sparkle Icon */}
          <motion.div
            layoutId="brand-title-container"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="flex items-center justify-center mt-6 mb-2 px-4 max-w-full"
          >
            <div className="relative inline-flex items-center justify-center pr-6 sm:pr-7">
              <motion.h1 layoutId="brand-title-text" className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-black tracking-tight flex items-center m-0 select-none">
                <span className={cn(
                  skin === 'stalker' ? "text-[#4ade80] italic font-black font-mono" : skin === 'anime' ? "text-white italic font-black" : skin === 'cyberpunk' ? "text-[#FCEE0A] font-black font-mono tracking-widest drop-shadow-[0_0_12px_rgba(252,238,10,0.7)]" : "text-zinc-900 dark:text-white italic font-black"
                )}>
                  MAIN
                </span>
                <span className={cn(
                  "ml-1.5 font-extrabold uppercase", 
                  skin === 'stalker' ? "text-[#4ade80] font-mono" : skin === 'anime' ? "text-pink-500" : skin === 'cyberpunk' ? "text-[#00ffcc] font-mono tracking-widest drop-shadow-[0_0_10px_rgba(0,255,204,0.6)]" : "text-[#10B981]"
                )}>
                  NUTRITION
                </span>
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: [0, 1, 1, 0], scale: [0, 1, 1, 0], rotate: [-20, 20, 20, -20] }}
                transition={{ duration: 1.8, ease: "easeInOut", delay: 0.3 }}
                className={cn("absolute -top-1.5 right-0 pointer-events-none", skin === 'stalker' ? "text-[#4ade80]" : skin === 'anime' ? "text-pink-500" : skin === 'cyberpunk' ? "text-[#FCEE0A]" : "text-[#10B981]")}
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 fill-current" />
              </motion.div>
            </div>
          </motion.div>

          {/* Subtitles */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28, duration: 0.4 }}
            className="space-y-1 text-center max-w-xs sm:max-w-sm mt-1"
          >
            <p className={cn("text-xs sm:text-sm font-semibold leading-relaxed", skin === 'stalker' ? "text-[#4ade80]/90 font-mono" : skin === 'cyberpunk' ? "text-[#FCEE0A]/90 font-mono" : "text-zinc-600 dark:text-zinc-300")}>
              {lang === 'ua' ? 'Твій ШІ-помічник із харчування.' : 'Your AI-powered nutrition companion.'}
            </p>
            <p className={cn("text-xs sm:text-sm font-medium leading-relaxed", skin === 'stalker' ? "text-[#4ade80]/70 font-mono" : skin === 'cyberpunk' ? "text-[#00ffcc]/80 font-mono" : "text-zinc-500 dark:text-zinc-400")}>
              {lang === 'ua' ? 'Відстежуй свої досягнення в один клік!' : 'Track your gains with a snap!'}
            </p>
          </motion.div>
        </motion.div>
      </div>
    );
  };

  // ==========================================
  // 7. ROOT COMPONENT RENDER
  // ==========================================
  if (isAuthLoading || !isMinSplashDone) return renderSplashScreen();
  if (view === 'login') return renderLoginView();
  if (view === 'onboarding') return renderOnboardingView();
  return (
    <div className={cn(
      "min-h-screen pb-[calc(6rem+env(safe-area-inset-bottom))] overflow-x-clip transition-colors duration-300", 
      skin === 'stalker' 
        ? "skin-stalker bg-black text-[#4ade80]" 
        : skin === 'anime' 
          ? "skin-anime bg-[#0A0B10] text-white" 
          : skin === 'cyberpunk' 
            ? cn("cyberpunk text-[#00ffcc]", cyberpunkTone === 'cyan-magenta' ? "bg-[#050515] tone-cyan-magenta" : "bg-[#050510] tone-yellow-cyan") 
            : "bg-[var(--bg)] text-[var(--text)]"
    )}>
      <SkinTransitionOverlay 
        active={skinTransitionActive} 
        targetSkin={skinTransitionTarget} 
        tone={cyberpunkTone} 
        lang={lang} 
      />
      <OccasionalScreenGlitch enabled={skin === 'cyberpunk'} />
      {skin === 'anime' && <div className="skin-anime-crt"></div>}
      {skin === 'stalker' && (
        <>
          <div className="fixed inset-y-0 left-1/2 -translate-x-1/2 w-full max-w-4xl bg-[url('https://picsum.photos/seed/stalker-texture/800/800')] bg-cover opacity-20 pointer-events-none mix-blend-multiply z-0"></div>
          <div className="skin-stalker-crt"></div>
        </>
      )}
      <AnimatePresence>
        {showLiquidSplash && <LiquidSplash beverageType={splashBeverageType} />}
      </AnimatePresence>
      {renderCameraUI()}
      {imageToCrop && (
        <MealCropper
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={() => setImageToCrop(null)}
          selectedMealType={selectedMealType}
          onSelectMealType={setSelectedMealType}
          lang={lang}
        />
      )}
      {isRefreshing && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-0 left-0 right-0 z-50 flex justify-center p-4"
        >
          <div className="bg-white dark:bg-zinc-900 p-3 rounded-full shadow-lg border border-zinc-200 dark:border-zinc-800">
            <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
          </div>
        </motion.div>
      )}
      <GamificationNotifications uid={user?.uid} />
      <RecipeModal
        isOpen={activeRecipeModal.isOpen}
        onClose={() => setActiveRecipeModal(prev => ({ ...prev, isOpen: false }))}
        mealName={activeRecipeModal.mealName}
        ingredients={activeRecipeModal.ingredients}
        macros={activeRecipeModal.macros}
        lang={lang}
        theme={theme}
        onAddIngredientsToGrocery={(items) => {
          if (!items || items.length === 0) return;
          const formatted = items.map(item => ({ name: item, source_meal_id: 'recipe_modal' }));
          handleAddMultipleGroceryItems(formatted);
          toast.success(lang === 'ua' ? 'Інгредієнти додано до списку продуктів!' : 'Ingredients added to Concierge!');
        }}
      />
      <EDecoderModal
        isOpen={isEDecoderOpen}
        onClose={() => setIsEDecoderOpen(false)}
        lang={lang}
        theme={theme}
        initialQuery={eDecoderInitialQuery}
        isStalkerSkin={skin === 'stalker'}
      />
      <Suspense fallback={null}>
        {safeChoiceDetailModalData && (
          <SafeChoiceDetailModal
            isOpen={!!safeChoiceDetailModalData}
            onClose={() => setSafeChoiceDetailModalData(null)}
            data={safeChoiceDetailModalData}
            safeChoiceProfile={safeChoiceProfile}
            lang={lang}
            theme={theme}
            isStalkerSkin={skin === 'stalker'}
            onOpenEDecoder={(q) => {
              triggerHaptic('SUCCESS');
              setEDecoderInitialQuery(q || '');
              setIsEDecoderOpen(true);
            }}
            onOpenProfileConfig={() => {
              setSafeChoiceDetailModalData(null);
              setView('settings');
            }}
            onAskAI={(prompt) => {
              setSafeChoiceDetailModalData(null);
              setView('chat');
              handleChatSubmit(undefined, prompt);
            }}
          />
        )}
      </Suspense>

      <StorePushNotificationBanner 
        lang={lang} 
        theme={theme} 
      />


      <Toaster 
        position="top-center" 
        theme={theme}
        toastOptions={{ 
          style: skin === 'stalker' ? {
            background: '#000000',
            color: '#4ade80',
            border: '1px solid rgba(74, 222, 128, 0.6)',
            borderRadius: '4px',
            fontFamily: "'VT323', monospace",
            fontSize: '1.1rem',
            letterSpacing: '0.05em',
            boxShadow: '0 0 15px rgba(74, 222, 128, 0.3)',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          } : skin === 'cyberpunk' ? {
            background: '#050510',
            color: '#FCEE0A',
            border: '1px solid #FCEE0A',
            borderRadius: '0px',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.85rem',
            letterSpacing: '0.05em',
            boxShadow: '0 0 20px rgba(252, 238, 10, 0.4), inset 0 0 10px rgba(0, 255, 204, 0.1)',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          } : skin === 'anime' ? {
            background: '#0A0B10',
            color: '#ffffff',
            border: '1px solid #ec4899',
            borderRadius: '1rem',
            fontSize: '0.875rem',
            boxShadow: '0 0 15px rgba(236, 72, 153, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          } : { 
            background: theme === 'dark' ? '#18181b' : '#ffffff', 
            color: theme === 'dark' ? '#fafafa' : '#18181b', 
            border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
            borderRadius: '1rem',
            padding: '12px 20px',
            fontSize: '0.875rem',
            fontWeight: '600',
            letterSpacing: '0.025em',
            textTransform: 'uppercase',
            boxShadow: theme === 'dark' 
              ? '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
              : '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          } 
        }} 
      />
      {/* Header */}
      <header className="sticky top-0 z-50 border-x-0 !border-t-0 border-b border-[var(--card-border)] px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] transition-all duration-300 glass">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 h-10">
            <button 
              onClick={() => { setView('home'); scrollToSectionTop(undefined, true); }}
              className={cn(
                "bg-emerald-500 p-2 rounded-xl hover:bg-emerald-600 transition-all duration-300 cursor-pointer",
                (isAnalyzing || isAnalyzingText) && "shadow-[0_0_16px_rgba(16,185,129,0.7)] ring-2 ring-emerald-400 ring-offset-2 ring-offset-[var(--bg)] animate-pulse btn-active-border"
              )}
            >
              <Utensils className={cn(
                "text-white w-5 h-5 transition-transform duration-300",
                (isAnalyzing || isAnalyzingText) && "scale-110"
              )} />
            </button>
            <h1 
              onClick={() => {
                setLogoTrigger(true);
                setTimeout(() => setLogoTrigger(false), 2500);
              }}
              className="text-xl font-semibold tracking-tight overflow-visible flex items-center m-0 drop-shadow-[0_1px_1px_rgba(0,0,0,0.05)] dark:drop-shadow-none cursor-pointer"
            >
              <AnimatedMainTitle 
                text={view === 'details' ? t.details : view === 'goals' ? t.settings : view === 'home' ? t.home : view === 'edecoder' ? 'E-DECODER' : view === 'how_it_works' ? (skin === 'stalker' ? (lang === 'ua' ? 'ПРОТОКОЛ СКАНУ' : 'SCAN PROTOCOL') : (lang === 'ua' ? 'ЯК ЦЕ ПРАЦЮЄ' : 'HOW IT WORKS')) : t.aiNutritionist} 
                secondaryText="MAIN Nutrition" 
                trigger={logoTrigger}
                isLooping={isAnalyzing || isAnalyzingText}
                skin={skin}
                lang={lang}
              />
            </h1>
          </div>
          <div className="flex items-center gap-4 h-10">
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Bio-Box Ready Indicator (Very left among right-side header icons) */}
              {userBioBoxes.length > 0 && !isCameraActive && (
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('SUCCESS');
                    sfx.playUiClick();
                    setSelectedBioBoxForUnbox(userBioBoxes[0]);
                    setIsBioBoxModalOpen(true);
                  }}
                  className={cn(
                    "relative flex items-center justify-center p-1.5 sm:p-2 rounded-xl border shrink-0 hover:scale-105 active:scale-95 transition-all cursor-pointer",
                    skin === 'stalker'
                      ? "bg-[#00E676]/15 border-[#00E676]/60 text-[#4ade80] shadow-[0_0_15px_rgba(0,230,118,0.4)] animate-pulse"
                      : "bg-emerald-500/15 border-emerald-500/40 text-emerald-500 dark:text-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.35)] hover:border-emerald-400"
                  )}
                  title={
                    lang === 'ua'
                      ? `Біо-Бокс готовий до відкриття! (${userBioBoxes.length})`
                      : `Bio-Box ready to open! (${userBioBoxes.length})`
                  }
                >
                  <Gift className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 animate-bounce" style={{ animationDuration: '2.5s' }} />
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-emerald-500 text-[9px] font-black text-white flex items-center justify-center shadow-[0_0_8px_#10B981] font-mono leading-none border border-white/20">
                    {userBioBoxes.length}
                  </span>
                </button>
              )}

              {/* Streak Milestone Indicator (Unified icon button shape matching other header icons) */}
              {consecutiveDaysStreak > 0 && !isCameraActive && skin !== 'anime' && (
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('SUCCESS');
                    setView('home');
                    scrollToSectionTop('daily-streak-section', true);
                  }}
                  className={cn(
                    "relative flex items-center justify-center p-1.5 sm:p-2 rounded-xl border shrink-0 hover:scale-105 active:scale-95 transition-all cursor-pointer",
                    skin === 'stalker'
                      ? "bg-amber-500/15 border-amber-500/60 text-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.4)]"
                      : "bg-amber-500/10 border-amber-500/30 text-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.2)] hover:border-amber-400"
                  )}
                  title={lang === 'ua' ? `Поточний стрік: ${consecutiveDaysStreak} дн.` : `Current Streak: ${consecutiveDaysStreak} days`}
                >
                  <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-500 fill-amber-500 shrink-0" />
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-amber-500 text-[9px] font-black text-white flex items-center justify-center shadow-[0_0_8px_#F59E0B] font-mono leading-none border border-white/20">
                    {consecutiveDaysStreak}
                  </span>
                </button>
              )}

              {/* Gamification Badge Button */}
              {effectiveGamificationTitle && !isCameraActive && (
                <button
                  onClick={() => {
                    setView('goals');
                    scrollToSectionTop('achievements-section', true);
                  }}
                  className="flex items-center justify-center p-1.5 sm:p-2 rounded-xl border shrink-0 hover:scale-105 active:scale-95 transition-all cursor-pointer"
                  style={{
                    color: effectiveGamificationColor,
                    borderColor: `${effectiveGamificationColor}33`,
                    backgroundColor: `${effectiveGamificationColor}1A`
                  }}
                  title={`${effectiveGamificationTitle} - ${lang === 'ua' ? 'Натисніть, щоб відкрити свій профіль та бейджі' : 'Click to view profile & badges'}`}
                >
                  {highestEarnedBadge ? (
                    <highestEarnedBadge.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" style={{ color: effectiveGamificationColor }} />
                  ) : (
                    <Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" style={{ color: effectiveGamificationColor }} />
                  )}
                </button>
              )}

              {/* Membership Crown Button */}
              {!isCameraActive && (
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('SUCCESS');
                    setView('goals');
                    setIsSubscriptionExpanded(true);
                    scrollToSectionTop('subscription-card-settings', true);
                  }}
                  className={cn(
                    "flex items-center justify-center p-1.5 sm:p-2 rounded-xl border shrink-0 hover:scale-105 active:scale-95 transition-all cursor-pointer",
                    (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                      ? "pro-crown-btn-animated"
                      : subscriptionTier === 'pro'
                        ? "premium-gold-btn"
                        : "bg-emerald-500/10 text-emerald-500 border-emerald-500/30"
                  )}
                  title={lang === 'ua' ? (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro' ? 'Членство PREMIUM' : subscriptionTier === 'pro' ? 'Членство PRO' : 'Членство') : (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro' ? 'Membership PREMIUM' : subscriptionTier === 'pro' ? 'Membership PRO' : 'Membership')}
                >
                  <Crown className={cn("w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0", (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') ? "pro-crown-icon-animated" : subscriptionTier === 'pro' ? "premium-gold-icon" : "text-emerald-500")} />
                </button>
              )}

            {!((view as any) === 'login' || (view as any) === 'onboarding') && (
              <div ref={menuRef} className="relative">
                <button 
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className={cn(
                    "p-2 rounded-xl transition-all flex items-center justify-center cursor-pointer",
                    isMenuOpen 
                      ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.25)]" 
                      : "hover:bg-[var(--input-bg)] border border-transparent text-zinc-500"
                  )}
                  aria-label="Toggle Menu"
                >
                  <SpringBurgerIcon isOpen={isMenuOpen} />
                </button>
                <AnimatePresence>
                  {isMenuOpen && (
                    <>
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 cursor-default" 
                        onClick={() => setIsMenuOpen(false)}
                      />
                      <motion.div 
                        variants={burgerMenuVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="absolute right-0 mt-2 w-56 sm:w-60 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl shadow-lg z-50 p-2 overflow-hidden"
                      >
                        <motion.button 
                          variants={burgerMenuItemVariants}
                          whileHover={{ x: 4, scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => { setView('favorites'); setIsMenuOpen(false); scrollToSectionTop('favorites-section', true); }} 
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-sm font-medium whitespace-nowrap text-left cursor-pointer"
                        >
                          <Heart className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>{lang === 'ua' ? 'УЛЮБЛЕНІ' : (t.favorites ? t.favorites.toUpperCase() : 'FAVORITE MEALS')}</span>
                        </motion.button>

                        <motion.button 
                          variants={burgerMenuItemVariants}
                          whileHover={{ x: 4, scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => { setView('grocery'); setIsMenuOpen(false); scrollToSectionTop('ai-concierge-section', true); }} 
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-sm font-medium whitespace-nowrap text-left cursor-pointer"
                        >
                          <Bot className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>{lang === 'ua' ? 'КОНСЬЄРЖ' : 'CONCIERGE'}</span>
                        </motion.button>

                        <motion.button 
                          variants={burgerMenuItemVariants}
                          whileHover={{ x: 4, scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => { setView('edecoder'); setIsMenuOpen(false); scrollToSectionTop('edecoder-section', true); }} 
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-sm font-medium whitespace-nowrap text-left cursor-pointer"
                        >
                          <ShieldAlert className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>{lang === 'ua' ? 'E-DECODER' : 'E-DECODER'}</span>
                        </motion.button>

                        <motion.button 
                          variants={burgerMenuItemVariants}
                          whileHover={{ x: 4, scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => { setView('gallery'); setIsMenuOpen(false); scrollToSectionTop('gallery-section', true); }} 
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-sm font-medium whitespace-nowrap text-left cursor-pointer"
                        >
                          <Image className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>{t.mealGallery}</span>
                        </motion.button>

                        <motion.button 
                          variants={burgerMenuItemVariants}
                          whileHover={{ x: 4, scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => { setView('skins'); setIsMenuOpen(false); scrollToSectionTop('skins-section', true); }} 
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-sm font-medium whitespace-nowrap text-left cursor-pointer"
                        >
                          <Palette className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>{t.skins || 'SKINS'}</span>
                        </motion.button>

                        <motion.a 
                          href="https://mainnutrition.app/blog/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          variants={burgerMenuItemVariants}
                          whileHover={{ x: 4, scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setIsMenuOpen(false)}
                          className="w-full flex items-center justify-between px-4 py-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-sm font-medium whitespace-nowrap text-left cursor-pointer no-underline text-[var(--text)]"
                        >
                          <div className="flex items-center gap-3">
                            <BookOpen className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            <span>{lang === 'ua' ? 'БЛОГ' : 'BLOG'}</span>
                          </div>
                          <ExternalLink className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                        </motion.a>

                        <motion.button 
                          variants={burgerMenuItemVariants}
                          whileHover={{ x: 4, scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => { setView('goals'); setPreviewIlluminationColor(illuminationColor); setIsMenuOpen(false); scrollToSectionTop('my-account-section', true); }} 
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-sm font-medium whitespace-nowrap text-left cursor-pointer"
                        >
                          {effectiveAvatarUrl && !avatarImageError ? (
                            <img 
                              src={effectiveAvatarUrl} 
                              alt="Avatar" 
                              className="w-4 h-4 rounded-full object-cover shrink-0" 
                              referrerPolicy="no-referrer" 
                              onError={() => setAvatarImageError(true)} 
                            />
                          ) : (
                            <User className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          )}
                          <span>{t.settings}</span>
                        </motion.button>

                        <motion.button 
                          variants={burgerMenuItemVariants}
                          whileHover={{ x: 4, scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            triggerHaptic('SUCCESS');
                            setView('goals');
                            setIsSubscriptionExpanded(true);
                            setIsMenuOpen(false);
                            scrollToSectionTop('subscription-card-settings', true);
                          }} 
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-sm font-medium whitespace-nowrap text-left cursor-pointer"
                        >
                          <Crown className={cn("w-4 h-4 flex-shrink-0", (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') ? "text-purple-400" : subscriptionTier === 'pro' ? "text-amber-400" : (skin === 'stalker' ? "text-[#4ade80]" : "text-emerald-500"))} />
                          <span>
                            {skin === 'stalker'
                              ? (lang === 'ua' ? 'ЛІЦЕНЗІЯ PDA' : 'PDA MEMBERSHIP')
                              : (lang === 'ua' ? 'ЧЛЕНСТВО' : 'MEMBERSHIP')}
                          </span>
                        </motion.button>

                        {isAdmin && (
                          <motion.button 
                            variants={burgerMenuItemVariants}
                            whileHover={{ x: 4, scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => { setView('admin_dashboard'); setIsMenuOpen(false); scrollToSectionTop('admin-dashboard-section', true); }} 
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[var(--input-bg)] rounded-xl transition-colors text-sm font-medium whitespace-nowrap text-left cursor-pointer"
                          >
                            <LayoutTemplate className="w-4 h-4 text-purple-500 flex-shrink-0" />
                            <span>{lang === 'ua' ? (t.adminDashboard ? t.adminDashboard.toUpperCase() : 'АДМІН КОНСОЛЬ') : (t.adminDashboard ? t.adminDashboard.toUpperCase() : 'ADMIN CONSOLE')}</span>
                          </motion.button>
                        )}
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            )}
        </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {!isOnline && (
          <motion.div
            id="offline-sync-banner"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-amber-500/10 border-b border-amber-500/20 text-amber-600 dark:text-amber-500 transition-colors py-3 px-4 text-xs font-semibold"
          >
            <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <WifiOff className="w-4 h-4 text-amber-500 animate-pulse flex-shrink-0" />
                <span className="text-left leading-normal font-sans tracking-tight">
                  {lang.startsWith('ua') 
                    ? `Ви працюєте в автономному режимі. ${offlineQueueLength > 0 ? `${offlineQueueLength} елементів у черзі на синхронізацію.` : 'Зміни збережуться локально.'}`
                    : `Working offline. ${offlineQueueLength > 0 ? `${offlineQueueLength} pending changes queued for synchronization when connection is restored.` : 'Changes will be saved locally.'}`}
                </span>
              </div>
              {offlineQueueLength > 0 && (
                <div className="flex-shrink-0 text-[10px] font-bold px-2.5 py-1 bg-amber-500/20 dark:bg-amber-500/30 text-amber-600 dark:text-amber-400 rounded-lg tracking-widest font-mono">
                  {offlineQueueLength} QUEUED
                </div>
              )}
            </div>
          </motion.div>
        )}
        {isQuotaExceededState && (
          <motion.div
            id="quota-exceeded-banner"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-red-500/10 border-b border-red-500/20 text-red-600 dark:text-red-400 transition-colors py-3 px-4 text-xs font-semibold"
          >
            <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 animate-pulse flex-shrink-0" />
                <span className="text-left leading-normal font-sans tracking-tight">
                  {lang.startsWith('ua') 
                    ? "Денний ліміт хмарної бази даних вичерпано. Ваші дані зберігаються виключно на цьому пристрої (локально)."
                    : "Cloud database daily limit reached. Running in local-only mode; changes are saved securely on this device."}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={async () => {
                    toast.dismiss('quota-exceeded-toast');
                    toast.loading(
                      lang.startsWith('ua')
                        ? "Перевірка зв’язку з хмарною базою даних..."
                        : "Checking cloud database connection...",
                      { id: 'reconnect-toast' }
                    );
                    try {
                      const isRestored = await checkFirestoreConnection();
                      if (isRestored) {
                        setIsQuotaExceeded(false);
                        isQuotaExceeded = false;
                        setIsQuotaExceededState(false);
                        if (typeof window !== 'undefined') {
                          (window as any).__firestoreQuotaExceeded = false;
                        }
                        toast.success(
                          lang.startsWith('ua')
                            ? "✅ Зв’язок із хмарною базою даних відновлено!"
                            : "✅ Cloud database connection restored!",
                          { id: 'reconnect-toast', duration: 4000 }
                        );
                        await loadData(false);
                        processOfflineQueue().catch(err => console.info('[OfflineQueue] Flush note:', err));
                      } else {
                        toast.error(
                          lang.startsWith('ua')
                            ? "⚠️ Денний ліміт хмарної бази даних ще не скинуто або з’єднання недоступне. Залишаємось у локальному режимі."
                            : "⚠️ Cloud database daily quota is still exhausted. Remaining in Local Only Mode.",
                          { id: 'reconnect-toast', duration: 4000 }
                        );
                      }
                    } catch (e) {
                      console.warn('Manual reconnect failed:', e);
                      toast.error(
                        lang.startsWith('ua')
                          ? "Помилка перевірки підключення. Спробуйте пізніше."
                          : "Connection check failed. Please try again later.",
                        { id: 'reconnect-toast', duration: 3000 }
                      );
                    }
                  }}
                  className="px-2.5 py-1 text-[11px] font-bold bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-sm flex items-center gap-1 active:scale-95 cursor-pointer"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>{lang.startsWith('ua') ? "Повторити" : "Retry"}</span>
                </button>
                <div className="text-[10px] font-bold px-2.5 py-1 bg-red-500/20 dark:bg-red-500/30 text-red-600 dark:text-red-400 rounded-lg tracking-widest font-mono">
                  LOCAL ONLY
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className={cn("max-w-4xl mx-auto px-4 relative", view === 'chat' ? "pt-0 pb-0" : isCameraActive ? "pt-6 pb-0" : view === 'history' ? "pt-3 pb-[calc(8rem+env(safe-area-inset-bottom))]" : "pt-6 pb-[calc(8rem+env(safe-area-inset-bottom))]")}>
        <AnimatePresence>
          {showSyncIndicator && !isCameraActive && (view as string) !== 'login' && (view as string) !== 'onboarding' && (
            <motion.div 
              initial={{ height: 0, opacity: 0, marginTop: 0, marginBottom: 0 }}
              animate={{ height: 'auto', opacity: 1, marginTop: -8, marginBottom: 12 }}
              exit={{ height: 0, opacity: 0, marginTop: 0, marginBottom: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="overflow-hidden flex justify-center w-full"
            >
              <div 
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (typeof navigator !== 'undefined' && navigator.onLine && !isSyncing) {
                    triggerHaptic('LIGHT');
                    window.dispatchEvent(new CustomEvent('syncing-started', { detail: { manual: true } }));
                    processOfflineQueue().finally(() => {
                      loadData(true);
                    });
                  }
                }}
                className="flex items-center gap-1.5 cursor-pointer select-none opacity-85 hover:opacity-100 active:scale-95 transition-all py-0.5 px-2 rounded-full hover:bg-[var(--input-bg)]"
                title={lang === 'ua' ? (lastSyncTime ? `Останнє успішне збереження в хмарі: ${lastSyncTime.toLocaleString('uk-UA')}. Натисніть для оновлення.` : "Синхронізація з хмарою. Натисніть для оновлення.") : (lastSyncTime ? `Last successful cloud sync: ${lastSyncTime.toLocaleString('en-US')}. Click to sync now.` : "Syncing with cloud. Click to refresh.")}
              >
                <AnimatePresence mode="wait">
                  {isSyncing ? (
                    <motion.span 
                      key="syncing-spinner"
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.6, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="inline-flex shrink-0 items-center justify-center"
                    >
                      <RefreshCw className="w-2.5 h-2.5 text-emerald-500 animate-spin" />
                    </motion.span>
                  ) : !isOnline ? (
                    <motion.span 
                      key="offline-dot"
                      initial={{ scale: 0.4, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.4, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="h-1.5 w-1.5 rounded-full bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.6)] shrink-0"
                    />
                  ) : isQuotaExceededState ? (
                    <motion.span 
                      key="quota-dot"
                      initial={{ scale: 0.4, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.4, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="h-1.5 w-1.5 rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)] shrink-0"
                    />
                  ) : (
                    <motion.span 
                      key="synced-dot"
                      initial={{ scale: 0.4, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.4, opacity: 0 }}
                      transition={{ duration: 0.4, type: "spring", stiffness: 220, damping: 14 }}
                      className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)] shrink-0"
                    />
                  )}
                </AnimatePresence>
                <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-mono tracking-tight font-medium inline-flex items-center">
                  {isSyncing ? (
                    <span className="inline-flex items-center">
                      <span>
                        {skin === 'stalker'
                          ? (lang === 'ua' ? "БІО-СИНХРОНІЗАЦІЯ" : "BIO-SYNCING")
                          : (lang === 'ua' ? "Синхронізація" : "Syncing")}
                      </span>
                      <span className="inline-flex tracking-normal ml-0.5 font-bold select-none text-[11px]">
                        <motion.span
                          animate={{ opacity: [0.2, 1, 0.2], y: [0, -1.5, 0] }}
                          transition={{ duration: 0.9, repeat: Infinity, delay: 0 }}
                        >.</motion.span>
                        <motion.span
                          animate={{ opacity: [0.2, 1, 0.2], y: [0, -1.5, 0] }}
                          transition={{ duration: 0.9, repeat: Infinity, delay: 0.2 }}
                        >.</motion.span>
                        <motion.span
                          animate={{ opacity: [0.2, 1, 0.2], y: [0, -1.5, 0] }}
                          transition={{ duration: 0.9, repeat: Infinity, delay: 0.4 }}
                        >.</motion.span>
                      </span>
                    </span>
                  ) : !isOnline ? (
                    skin === 'stalker'
                      ? (lang === 'ua' ? "АВТОНОМНИЙ РЕЖИМ (PDA)" : "OFFLINE PDA MODE")
                      : (lang === 'ua' ? "Офлайн-режим" : "Offline mode")
                  ) : isQuotaExceededState ? (
                    skin === 'stalker'
                      ? (lang === 'ua' ? "ЛОКАЛЬНА ПАМ'ЯТЬ" : "LOCAL STORAGE ONLY")
                      : (lang === 'ua' ? "Локальний режим" : "Local mode")
                  ) : lastSyncTime ? (
                    skin === 'stalker'
                      ? (lang === 'ua' ? `БІО-СИНХР: ${formatSyncTime(lastSyncTime)}` : `BIO-SYNC: ${formatSyncTime(lastSyncTime)}`)
                      : (lang === 'ua' ? `Збережено: ${formatSyncTime(lastSyncTime)}` : `Synced: ${formatSyncTime(lastSyncTime)}`)
                  ) : (
                    skin === 'stalker'
                      ? (lang === 'ua' ? "БІО-СИНХРОНІЗАЦІЯ АКТИВНА" : "BIO-SYNC ACTIVE")
                      : (lang === 'ua' ? "Синхронізовано" : "Synced")
                  )}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {view === 'admin_dashboard' && (
          <div id="admin-dashboard-section" className="scroll-mt-24">
            <AdminDashboard user={user} t={t} setView={setView} setShowAdminPurgeConfirm={setShowAdminPurgeConfirm} theme={theme} lang={lang} timeFormat={timeFormat} illuminationColor={illuminationColor} setPreviewIlluminationColor={setPreviewIlluminationColor} isEveningWrapUpTestEnabled={isEveningWrapUpTestEnabled}
              setIsEveningWrapUpTestEnabled={setIsEveningWrapUpTestEnabled} setIsDismissedWrapUp={setIsDismissedWrapUp} />
          </div>
        )}
        {view === 'skins' && renderSkinsView()}
        {view === 'favorites' && renderFavoritesView()}
        {view === 'gallery' && renderGalleryView()}
        {view === 'nutrient_breakdown' && renderNutrientBreakdownView()}
        {view === 'edecoder' && (
          <div id="edecoder-section" className="scroll-mt-24">
            <EDecoderModal 
              isOpen={true} 
              isPage={true}
              onClose={() => setView('home')} 
              onBack={() => setView('home')} 
              lang={lang} 
              theme={theme} 
              isStalkerSkin={skin === 'stalker'} 
            />
          </div>
        )}
        {view === 'how_it_works' && (
          <HowItWorksPage 
            lang={lang} 
            theme={theme} 
            skin={skin} 
            onStartScan={() => setView('add_meal_options')} 
            onBack={() => setView('home')} 
          />
        )}
        <div style={{ display: view === 'home' ? 'block' : 'none' }}>
          <div>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div 
                key={selectedDate.toDateString()}
              initial={{ x: swipeDirection === 'left' ? 30 : -30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: swipeDirection === 'left' ? -30 : 30, opacity: 0 }}
              transition={{ type: "tween", ease: "easeInOut", duration: 0.15 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.15}
              style={{ willChange: "transform, opacity", backfaceVisibility: "hidden" }}
              onDragEnd={(event, info) => {
                const threshold = 50;
                const velocityThreshold = 200;
                if (info.offset.x > threshold || info.velocity.x > velocityThreshold) {
                  // Swipe Right -> Previous Day
                  setSwipeDirection('right');
                  const d = new Date(selectedDate);
                  d.setDate(d.getDate() - 1);
                  setSelectedDate(d);
                } else if (info.offset.x < -threshold || info.velocity.x < -velocityThreshold) {
                  // Swipe Left -> Next Day
                  setSwipeDirection('left');
                  const d = new Date(selectedDate);
                  d.setDate(d.getDate() + 1);
                  setSelectedDate(d);
                }
              }}
              className={cn("touch-pan-y w-full", illumination ? "space-y-8" : "space-y-6")}
            >
              {user?.isAnonymous && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "p-4 rounded-3xl border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 relative overflow-hidden card-shadow",
                    skin === 'stalker' 
                      ? "bg-black border-[#4ade80]/40 text-[#4ade80]" 
                      : "bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20 text-amber-900 dark:text-amber-100"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider">
                        {lang === 'ua' ? 'Тимчасовий гостьовий акаунт' : 'Temporary Guest Account'}
                      </h4>
                      <p className="text-[11px] opacity-90 leading-relaxed mt-1">
                        {lang === 'ua' 
                          ? 'Ви перебуваєте в гостьовому режимі. Створіть постійний акаунт, щоб зберегти свої дані назавжди та синхронізувати пристрої!' 
                          : 'You are using a temporary guest account. Create a permanent account to protect and sync your data!'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowUpgradeModal(true);
                    }}
                    className={cn(
                      "px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider shrink-0 transition-all cursor-pointer self-start sm:self-center shadow-md",
                      skin === 'stalker'
                        ? "bg-[#4ade80] hover:bg-[#22c55e] text-black"
                        : "bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/20 border-none"
                    )}
                  >
                    {lang === 'ua' ? 'Створити акаунт' : 'Upgrade Account'}
                  </button>
                </motion.div>
              )}
              {/* Daily Progress Dashboard */}
            <section className={cn(
              illumination ? "space-y-6" : "space-y-5"
            )}>
              <div className={cn(
                "flex items-center justify-between px-4 py-3.5 sm:py-4 min-h-[84px] sm:min-h-[88px] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl card-shadow transition-all relative overflow-hidden",
                illumination ? "illumination-food" : ""
              )}>
                {/* Subtle Ambient Tech Grid & Glow Orbs */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
                  <div className="absolute inset-0 bg-tech-grid opacity-20 dark:opacity-15" style={{ backgroundSize: '24px 24px' }} />
                  <div className="absolute -left-16 -top-16 w-48 h-48 bg-emerald-500/10 dark:bg-emerald-500/15 rounded-full blur-3xl" />
                  <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-teal-500/10 dark:bg-teal-500/15 rounded-full blur-3xl" />
                </div>
                {illumination && (
                  <>
                    <div className="absolute -left-20 -top-20 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] pointer-events-none" />
                  </>
                )}
                <motion.div
                  initial={justFinishedOnboarding ? { scale: 0.8, opacity: 0, y: 20 } : { x: -20, opacity: 0 }}
                  animate={{ scale: 1, x: 0, opacity: 1, y: 0 }}
                  transition={justFinishedOnboarding ? { 
                    type: "spring", 
                    stiffness: 200, 
                    damping: 15,
                    delay: 0.2
                  } : { 
                    type: "spring", 
                    stiffness: 300, 
                    damping: 25 
                  }}
                  className={cn(
                    "flex-1 min-w-0 pr-2 relative z-10",
                    illumination && "drop-shadow-[0_0_13px_rgba(16,185,129,0.35)]"
                  )}
                >
                  <CyclingGreeting greetings={allGreetings} />
                </motion.div>
                <div className="flex flex-col items-center gap-1 shrink-0 relative">
                  <div className="flex items-center gap-2">
                    <button 
                      type="button"
                      onClick={() => {
                        triggerHaptic('SELECTION');
                        setIsGoogleFitModalOpen(true);
                      }}
                      className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all cursor-pointer hover:scale-105 active:scale-95",
                        (isGoogleFitConnected || isAppleHealthConnected)
                          ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.2)]"
                          : "bg-[var(--card-bg)] border border-[var(--card-border)] text-zinc-400 dark:text-zinc-500 hover:border-emerald-500/30 hover:text-[var(--text)]"
                      )} 
                      title={
                        isAppleHealthConnected && !isGoogleFitConnected
                          ? (lang === 'ua' ? 'Apple Health (Натисніть для деталей)' : 'Apple Health (Click for details)')
                          : isGoogleFitConnected
                            ? (lang === 'ua' ? 'Google Fit (Натисніть для деталей)' : 'Google Fit (Click for details)')
                            : (lang === 'ua' ? 'Синхронізація здоров’я (Google Fit / Apple Health)' : 'Health Sync (Google Fit / Apple Health)')
                      }
                    >
                      {isAppleHealthConnected && !isGoogleFitConnected ? (
                        <Apple className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Smartphone className={cn("w-4 h-4", (isGoogleFitConnected || isAppleHealthConnected) ? "text-emerald-500" : "currentColor")} />
                      )}
                    </button>
                    <button 
                      onClick={() => {
                        if (userStatus.id === 'active') {
                          setShowStatusTooltip(!showStatusTooltip);
                          if (!showStatusTooltip) {
                            setTimeout(() => setShowStatusTooltip(false), 3000);
                          }
                        }
                      }}
                      className={cn("px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-transform active:scale-95", userStatus.bgClass, userStatus.borderClass)}
                    >
                      {userStatus.icon}
                      <span className={cn("text-[10px] font-bold uppercase tracking-widest whitespace-nowrap", userStatus.colorClass)}>
                        {userStatus.text}
                      </span>
                    </button>
                  </div>
                  
                  {/* Tooltip */}
                  <AnimatePresence>
                    {showStatusTooltip && userStatus.id === 'active' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.9 }}
                        className="absolute top-full right-0 mt-2 w-48 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-xs p-3 rounded-2xl shadow-xl z-50 pointer-events-none"
                      >
                        <div className="absolute -top-2 right-6 w-4 h-4 bg-zinc-900 dark:bg-zinc-100 rotate-45" />
                        <p className="relative z-10 font-medium leading-relaxed">
                          {lang.startsWith('en') ? 'You are actively kicking off.' : 'Ти активно починаєш.'}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className={cn(
                (selectedDate.toDateString() === new Date().toDateString())
                  ? (illumination ? "space-y-4" : "space-y-3")
                  : (illumination ? "space-y-10" : "space-y-[30px]")
              )}>
                {/* Date Switcher */}
                <div 
                  onClick={() => setView('progress')}
                  className={cn(
                    "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-full p-2 flex items-center justify-between card-shadow transition-all cursor-pointer",
                    illumination ? "illumination-food" : ""
                  )}
                >
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSwipeDirection('right');
                    const d = new Date(selectedDate);
                    d.setDate(d.getDate() - 1);
                    setSelectedDate(d);
                  }}
                  className="p-3 hover:bg-[var(--input-bg)] rounded-2xl transition-colors text-zinc-500"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                </button>
                
                <div className="flex flex-col items-center p-2 rounded-2xl transition-all active:scale-95 select-none">
                  <span className="text-sm font-bold text-[var(--text)]">
                    {(() => {
                      const dateStr = selectedDate.toLocaleDateString(lang.startsWith('en') ? 'en-US' : 'uk-UA', { 
                        weekday: 'long', 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      });
                      return lang.startsWith('en') ? dateStr : dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
                    })()}
                  </span>
                  <span 
                    onClick={(e) => {
                      e.stopPropagation();
                      const today = new Date();
                      if (today.toDateString() !== selectedDate.toDateString()) {
                        setSwipeDirection(today > selectedDate ? 'left' : 'right');
                        setSelectedDate(today);
                      }
                    }}
                    className={cn(
                      "text-[10px] font-bold uppercase tracking-tighter cursor-pointer hover:underline underline-offset-2",
                      new Date().toDateString() === selectedDate.toDateString() ? "text-emerald-500" : "text-zinc-500"
                    )}
                  >
                    {new Date().toDateString() === selectedDate.toDateString() ? 'Today' : t.goToToday}
                  </span>
                </div>

                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSwipeDirection('left');
                    const d = new Date(selectedDate);
                    d.setDate(d.getDate() + 1);
                    setSelectedDate(d);
                  }}
                  className="p-3 hover:bg-[var(--input-bg)] rounded-2xl transition-colors text-zinc-500"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <AnimatePresence mode="sync">
                {/* Daily Evening Wrap Up Card */}
                {wrapUpStatus.shouldShow && !isDismissedWrapUp && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="w-full border-none outline-none"
                  >
                  {(() => {
                  const selDateStr = selectedDate.getFullYear() + '-' + String(selectedDate.getMonth() + 1).padStart(2, '0') + '-' + String(selectedDate.getDate()).padStart(2, '0');
                  let wrapTotals = selectedDayWrapUpTotals;
                  let mCount = selectedDayCombined.filter((e: any) => e.type === 'meal').length;
                  let wMl = selectedDayTotals.water || 0;

                  if (wrapUpStatus.sessionDate !== selDateStr) {
                    const [y, m, d] = wrapUpStatus.sessionDate.split('-').map(Number);
                    const dayStart = new Date(y, m - 1, d, 0, 0, 0, 0);
                    const dayEnd = new Date(y, m - 1, d, 23, 59, 59, 999);

                    const mealEntries = history.filter(item => {
                      const itemDate = new Date(item.timestamp);
                      return itemDate >= dayStart && itemDate <= dayEnd;
                    });

                    const mealTotals = mealEntries.reduce((acc, item) => {
                      const nutrition = item.total_nutrition || { calories_kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, sugar_g: 0, fiber_g: 0, sodium_mg: 0, caffeine_mg: 0, water_ml: 0 };
                      return {
                        calories: acc.calories + (nutrition.calories_kcal || 0),
                        protein: acc.protein + (nutrition.protein_g || 0),
                        carbs: acc.carbs + (nutrition.carbs_g || 0),
                        fat: acc.fat + (nutrition.fat_g || 0),
                        sugar: acc.sugar + (nutrition.sugar_g || 0),
                        fiber: acc.fiber + (nutrition.fiber_g || 0),
                        sodium: acc.sodium + (nutrition.sodium_mg || 0),
                        caffeine: acc.caffeine + (nutrition.caffeine_mg || 0),
                        water: acc.water + (nutrition.water_ml || 0)
                      };
                    }, { calories: 0, protein: 0, carbs: 0, fat: 0, sugar: 0, fiber: 0, sodium: 0, caffeine: 0, water: 0 });

                    const waterEntries = waterLogs.filter(log => {
                      const logDate = new Date(log.timestamp);
                      return logDate >= dayStart && logDate <= dayEnd;
                    }).reduce((acc, log) => {
                      if (log.source_meal_id) return acc;
                      const nutrients = calculateWaterLogNutrients(log);
                      return {
                        water: acc.water + log.amount_ml,
                        calories: acc.calories + nutrients.cal,
                        caffeine: acc.caffeine + nutrients.caff,
                        sugar: acc.sugar + nutrients.sugar
                      };
                    }, { water: 0, calories: 0, caffeine: 0, sugar: 0 });

                    wrapTotals = {
                      calories_kcal: mealTotals.calories + waterEntries.calories,
                      protein_g: mealTotals.protein,
                      carbs_g: mealTotals.carbs,
                      fat_g: mealTotals.fat,
                      sugar_g: Number((mealTotals.sugar + waterEntries.sugar).toFixed(1)),
                      fiber_g: mealTotals.fiber,
                      sodium_mg: mealTotals.sodium,
                      water_ml: mealTotals.water + waterEntries.water,
                      caffeine_mg: mealTotals.caffeine + waterEntries.caffeine
                    };
                    mCount = mealEntries.length;
                    wMl = mealTotals.water + waterEntries.water;
                  }

                  return (
                    <DailyWrapUpCard
                      totals={wrapTotals}
                      goals={goals}
                      mealsCount={mCount}
                      waterTotalMl={wMl}
                      lang={lang}
                      vibe={vibe}
                      skin={skin}
                      subscriptionTier={subscriptionTier}
                      isEveningOverride={isEveningWrapUpTestEnabled}
                      sessionDate={wrapUpStatus.sessionDate}
                      isTransferred={wrapUpStatus.isTransferred}
                      userName={userName}
                      onDismiss={() => {
                        try {
                          localStorage.setItem(`evening_wrapup_dismissed_${wrapUpStatus.sessionDate}`, 'true');
                        } catch (e) {
                          console.warn('Failed to save wrapup dismissal', e);
                        }
                        // Sync dismissal to Firestore so all other devices hide the wrap-up card
                        if (user) {
                          const remoteKey = `eveningWrapUpDismissed_${wrapUpStatus.sessionDate}`;
                          setDocWithQueue(doc(db, 'users', user.uid), { [remoteKey]: true }, { merge: true })
                            .catch(err => console.warn('Failed to sync wrapup dismissal to Firestore:', err));
                        }
                        setIsDismissedWrapUp(true);
                        setWrapUpRefreshTick(t => t + 1);
                      }}
                    />
                  );
                })()}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* AI Meal Suggestions (Always accessible on Today view) */}
              {selectedDate.toDateString() === new Date().toDateString() && (
                enableAiSuggestions ? (
                  <div className="relative space-y-4">
                  <div 
                    className={cn(
                      "flex items-center justify-between px-2 transition-colors rounded-xl cursor-pointer",
                      theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800",
                      isAiSuggestionsCollapsed ? "py-3" : "py-1"
                    )}
                    onClick={() => {
                      const newVal = !isAiSuggestionsCollapsed;
                      setIsAiSuggestionsCollapsed(newVal);
                      localStorage.setItem('ai_suggestions_collapsed', String(newVal));
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-500" />
                      <h4 className="text-xs font-black uppercase tracking-widest text-[var(--text)] select-none">
                        {t.aiMealSuggestions}
                      </h4>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        className={cn(
                          "p-1.5 rounded-[12px] text-zinc-500 hover:text-[var(--text)] transition-colors",
                          theme === 'light' ? "bg-zinc-100 hover:bg-zinc-200" : "bg-zinc-800 hover:bg-zinc-700"
                        )}
                      >
                        {isAiSuggestionsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFetchSuggestions(undefined, true);
                        }}
                        disabled={isGeneratingSuggestions}
                        className={cn(
                          "p-1.5 rounded-[12px] text-zinc-500 hover:text-[var(--text)] transition-colors disabled:opacity-50",
                          theme === 'light' ? "bg-zinc-100 hover:bg-zinc-200" : "bg-zinc-800 hover:bg-zinc-700"
                        )}
                        title={lang.startsWith('en') ? 'Refresh Suggestions' : 'Оновити пропозиції'}
                      >
                        <RefreshCw className={cn("w-4 h-4", isGeneratingSuggestions && "animate-spin")} />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAiSuggestionsSettings(true);
                        }}
                        className={cn(
                          "p-1.5 rounded-[12px] text-zinc-500 hover:text-[var(--text)] transition-colors",
                          theme === 'light' ? "bg-zinc-100 hover:bg-zinc-200" : "bg-zinc-800 hover:bg-zinc-700"
                        )}
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <AnimatePresence initial={false}>
                    {!isAiSuggestionsCollapsed && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden px-4 py-4 -mx-4 -my-4"
                      >
                        <AnimatePresence mode="wait">
                        {isGeneratingSuggestions && filteredAiSuggestions.length === 0 ? (
                          <motion.div 
                            key="ai-suggestions-loading"
                            initial={{ opacity: 0, y: 15, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -15, scale: 0.98 }}
                            transition={{ duration: 0.3 }}
                            className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[32px] p-6 flex flex-col items-center justify-center gap-4 shadow-[0_20px_50px_rgba(0,0,0,0.08)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.3)] animate-pulse border-emerald-500/10"
                          >
                            <div className="bg-emerald-500/10 p-4 rounded-full shadow-inner">
                              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                            </div>
                            <div className="text-center">
                              <p className="text-xs font-black uppercase tracking-widest text-[var(--text)] mb-1">
                                {t.aiAnalysis}
                              </p>
                              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                                {t.aiSearchingIdeal}
                              </p>
                            </div>
                          </motion.div>
                        ) : suggestionError ? (
                          <motion.div 
                            key="ai-suggestions-error"
                            initial={{ opacity: 0, y: 15, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -15, scale: 0.98 }}
                            transition={{ duration: 0.3 }}
                            className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[32px] p-6 flex flex-col items-center justify-center gap-4 shadow-[0_15px_40px_rgba(0,0,0,0.06)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.3)]"
                          >
                            <div className="bg-amber-500/10 p-4 rounded-full">
                              <Zap className="w-8 h-8 text-amber-500" />
                            </div>
                            <div className="text-center">
                              <p className="text-xs font-black uppercase tracking-widest text-[var(--text)] mb-1">
                                {lang === 'ua' ? 'AI Тимчасово відсутній' : 'AI Busy'}
                              </p>
                              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest leading-relaxed mb-4 px-4">
                                {suggestionError === 'quota' 
                                  ? (lang === 'ua' ? 'Досягнуто ліміт запитів. Спробуйте оновити або зачекайте хвилинку.' : 'Request limit reached. Try refreshing or wait a moment.')
                                  : (lang === 'ua' ? 'Щось пішло не так при генерації. Можливо, збій мережі?' : 'Something went wrong. Network issues?')}
                              </p>
                              <button
                                onClick={() => handleFetchSuggestions(undefined, true)}
                                className={cn(
                                  "mx-auto flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20 active:scale-95 transition-all text-center",
                                  isGeneratingSuggestions && "opacity-50 pointer-events-none scale-95"
                                )}
                              >
                                {isGeneratingSuggestions ? (
                                   <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                ) : (
                                   <RotateCcw className="w-3 h-3" />
                                )}
                                {lang === 'ua' ? (isGeneratingSuggestions ? 'Оновлюємо...' : 'Оновити AI') : (isGeneratingSuggestions ? 'Refreshing...' : 'Refresh AI')}
                              </button>
                            </div>
                          </motion.div>
                        ) : filteredAiSuggestions.length > 0 ? (
                          <motion.div 
                            key={`ai-suggestions-container-${filteredAiSuggestions.map(g => g.meal_type).join('-')}-${filteredAiSuggestions.length}`}
                            initial={{ opacity: 0, y: 28, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -20, scale: 0.97 }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            className="flex flex-col gap-4 pt-1 relative"
                          >
                            {isGeneratingSuggestions && (
                              <div className="absolute inset-x-0 top-[17px] flex justify-center z-20">
                                <div className="bg-[var(--card-bg)] border border-[var(--card-border)] px-3 py-1 rounded-full shadow-[0_4px_15px_rgba(0,0,0,0.1)] flex items-center gap-2">
                                  <Loader2 className="w-3 h-3 animate-spin text-emerald-500" />
                                  <span className="text-[8px] font-bold uppercase tracking-tight text-zinc-500">
                                    {t.aiUpdating}
                                  </span>
                                </div>
                              </div>
                            )}
                            {filteredAiSuggestions.map((group, gIdx) => (
                              <motion.div 
                                key={gIdx}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: gIdx * 0.08, duration: 0.35 }}
                                className={cn(
                                  "border rounded-[32px] overflow-hidden transition-all relative group",
                                  theme === 'light' ? "bg-white border-zinc-100 shadow-[0_15px_45px_rgba(0,0,0,0.06)]" : "bg-[var(--card-bg)] border-[var(--card-border)] shadow-[0_20px_60px_rgba(0,0,0,0.3)]",
                                  illumination && "illumination-food"
                                )}
                                style={{ height: 'auto' }}
                              >
                                <div className={cn(
                                  "absolute top-0 left-0 w-1.5 h-full z-10",
                                  group.meal_type === 'breakfast' ? 'bg-amber-500' :
                                  group.meal_type === 'lunch' ? 'bg-emerald-500' :
                                  group.meal_type === 'dinner' ? 'bg-indigo-500' :
                                  'bg-pink-500'
                                )} />
                                
                                <div className="p-5 pl-8">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className={cn(
                                      "px-2.5 py-0.5 rounded-[10px] text-[9px] font-black uppercase tracking-[0.2em] shadow-sm text-white",
                                      group.meal_type === 'breakfast' ? 'bg-amber-500 shadow-amber-500/20' :
                                      group.meal_type === 'lunch' ? 'bg-emerald-500 shadow-emerald-500/20' :
                                      group.meal_type === 'dinner' ? 'bg-indigo-500 shadow-indigo-500/20' :
                                      'bg-pink-500 shadow-pink-500/20'
                                    )}>
                                      {t[group.meal_type as keyof typeof t] || group.meal_type}
                                    </span>
                                    {group.options.length > 1 && (
                                      <div className="flex items-center gap-1.5 text-[8px] font-black uppercase tracking-widest text-zinc-400">
                                        <ArrowLeftRight className="w-3 h-3" />
                                        {lang === 'ua' ? (vibe === 'casual' ? 'Свайпай' : 'Свайпайте') : 'Swipe for more'}
                                      </div>
                                    )}
                                  </div>

                                  <div 
                                    className="overflow-x-auto no-scrollbar scroll-smooth snap-x snap-mandatory flex gap-6 pb-2"
                                    onScroll={(e) => handleSuggestionScroll(gIdx, e)}
                                  >
                                    {group.options.map((option: any, oIdx: number) => {
                                      const suggestionId = `suggestion-${gIdx}-${oIdx}`;
                                      return (
                                        <div 
                                          key={oIdx} 
                                          className="w-full flex-shrink-0 snap-center space-y-4"
                                        >
                                          <div className="flex items-start justify-between">
                                            <h5 className={cn(
                                              "font-black text-[var(--text)] leading-tight text-lg tracking-tight",
                                              theme === 'light' ? "drop-shadow-[0_1px_1px_rgba(0,0,0,0.05)]" : ""
                                            )}>{option.meal_name}</h5>
                                            <span className="font-black text-emerald-500 text-lg shrink-0 whitespace-nowrap bg-emerald-500/10 px-3 py-1 rounded-[14px]">
                                              {option.expected_calories} <span className="text-[10px] uppercase tracking-widest ml-0.5 opacity-80">kcal</span>
                                            </span>
                                          </div>
                                          
                                          {adjustingSuggestionId === suggestionId ? (
                                            <div 
                                              className={cn(
                                                "p-3 rounded-[20px] border flex flex-col gap-2.5 transition-all w-full",
                                                theme === 'light' 
                                                  ? "bg-emerald-50/50 border-emerald-200/60" 
                                                  : "bg-emerald-950/10 border-emerald-900/40"
                                              )}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <div className="flex items-center gap-2 justify-between w-full">
                                                <input
                                                  type="text"
                                                  autoFocus
                                                  value={adjustmentInput}
                                                  onChange={(e) => setAdjustmentInput(e.target.value)}
                                                  placeholder={lang === 'ua' ? 'напр. Запропонуй перекус на 200 ккал...' : 'e.g. Suggest a 200 kcal snack...'}
                                                  className="w-full bg-transparent border-none text-xs text-[var(--text)] focus:outline-none placeholder:text-zinc-400 font-medium py-1"
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                      e.preventDefault();
                                                      handleFetchSuggestions(adjustmentInput, true);
                                                    }
                                                  }}
                                                />
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleFetchSuggestions(adjustmentInput, true);
                                                  }}
                                                  className="flex items-center gap-1 group/ai shrink-0"
                                                >
                                                  <span className={cn(
                                                    "text-[10px] font-bold uppercase tracking-widest transition-colors italic",
                                                    theme === 'light' 
                                                      ? "text-emerald-700/80 group-hover/ai:text-emerald-700" 
                                                      : "text-emerald-600 dark:text-emerald-400/80 group-hover/ai:text-emerald-400"
                                                  )}>
                                                    {lang === 'ua' ? 'Налаштувати' : 'Adjust'}
                                                  </span>
                                                  <Sparkles className="w-3 h-3 text-emerald-500 group-hover/ai:scale-110 transition-transform" />
                                                </button>
                                              </div>
                                              <div className="flex items-center justify-between">
                                                <span className="text-[9px] text-zinc-400 dark:text-zinc-500 italic max-w-[70%] truncate">
                                                  {lang === 'ua' ? 'Спробуйте: "перекус на 200 ккал"' : 'Try: "Suggest a 200 kcal snack"'}
                                                </span>
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAdjustingSuggestionId(null);
                                                  }}
                                                  className="text-[10px] font-bold text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 uppercase tracking-wider"
                                                >
                                                  {lang === 'ua' ? 'Скасувати' : 'Cancel'}
                                                </button>
                                              </div>
                                            </div>
                                          ) : (
                                            <p 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setAdjustingSuggestionId(suggestionId);
                                                setAdjustmentInput('');
                                              }}
                                              className="text-xs text-zinc-500 dark:text-zinc-400 font-medium leading-relaxed bg-[var(--input-bg)] p-3 rounded-[20px] line-clamp-2 min-h-[2.75rem] cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50 transition-colors"
                                              title={lang === 'ua' ? 'Натисніть, щоб налаштувати цю рекомендацію за допомогою ШІ' : 'Tap to adjust this recommendation with AI'}
                                            >
                                              {option.description}
                                            </p>
                                          )}

                                          <div className="flex items-center justify-between pt-1">
                                            <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-[var(--text)]">
                                              <span className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{option.macros?.protein ?? 0}g</span>
                                              <span className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-blue-500" />{option.macros?.carbs ?? 0}g</span>
                                              <span className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" />{option.macros?.fat ?? 0}g</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setActiveRecipeModal({
                                                    isOpen: true,
                                                    mealName: option.meal_name,
                                                    ingredients: option.ingredients || [],
                                                    macros: {
                                                      calories: option.expected_calories,
                                                      protein: option.macros?.protein ?? 0,
                                                      carbs: option.macros?.carbs ?? 0,
                                                      fat: option.macros?.fat ?? 0
                                                    }
                                                  });
                                                }}
                                                className={cn(
                                                  "p-2 rounded-xl transition-all border flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase cursor-pointer",
                                                  theme === 'light'
                                                    ? "bg-blue-50/80 border-blue-200/60 text-blue-600 hover:bg-blue-500 hover:text-white hover:border-blue-500"
                                                    : "bg-blue-950/30 border-blue-800/60 text-blue-400 hover:bg-blue-500 hover:text-white hover:border-blue-500"
                                                )}
                                                title={lang === 'ua' ? 'Переглянути покроковий рецепт' : 'View Step-by-Step Recipe'}
                                              >
                                                <BookOpen className="w-3.5 h-3.5" />
                                                <span className="hidden sm:inline">{lang === 'ua' ? 'Рецепт' : 'Recipe'}</span>
                                              </button>
                                              <button
                                                disabled={addingSuggestionId === suggestionId || isAddingToGrocery === suggestionId}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleAddMealIngredientsToGrocery({...option, id: suggestionId});
                                                }}
                                                className={cn(
                                                  "p-2 rounded-xl transition-all border",
                                                  isAddingToGrocery === suggestionId 
                                                    ? "bg-zinc-100 border-transparent"
                                                    : theme === 'light'
                                                      ? "bg-white border-zinc-100 text-zinc-400 hover:text-emerald-500 hover:border-emerald-500/30"
                                                      : "bg-zinc-800 border-zinc-700 text-zinc-500 hover:text-emerald-500 hover:border-emerald-500/30"
                                                )}
                                                title={lang === 'ua' ? 'Додати інгредієнти до списку продуктів' : 'Add ingredients to Concierge'}
                                              >
                                                {isAddingToGrocery === suggestionId ? (
                                                  <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                                                ) : (
                                                  <ShoppingCart className="w-4 h-4" />
                                                )}
                                              </button>
                                              <button
                                                disabled={addingSuggestionId === suggestionId}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleQuickAddSuggestion(option, group.meal_type, suggestionId);
                                                }}
                                                className={cn(
                                                  "relative text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-1.5 px-4 py-2 rounded-[16px] shadow-sm cursor-pointer z-10 overflow-hidden group border",
                                                  addingSuggestionId === suggestionId && "btn-active-border",
                                                  addingSuggestionId === suggestionId 
                                                    ? "border-transparent bg-transparent"
                                                    : theme === 'light' 
                                                      ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white shadow-emerald-500/10 border-transparent" 
                                                      : "bg-[var(--card-bg)] text-emerald-500 hover:bg-emerald-500 hover:text-white border-emerald-500/30 font-bold"
                                                )}
                                              >
                                                {addingSuggestionId === suggestionId && (
                                                  <>
                                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300%] aspect-square animate-[spin_3s_linear_infinite] bg-[conic-gradient(from_0deg,transparent_0_25%,#3b82f6_40%,#a855f7_50%,#3b82f6_60%,transparent_75%_100%)] opacity-100" />
                                                    <div className={cn("absolute inset-[2px] rounded-[14px] z-10", theme === 'light' ? 'bg-white' : 'bg-zinc-900')} />
                                                  </>
                                                )}
                                                <div className={cn("relative z-20 flex items-center gap-1.5", addingSuggestionId === suggestionId ? (theme === 'light' ? 'text-zinc-900' : 'text-zinc-100') : '')}>
                                                  {addingSuggestionId === suggestionId ? (
                                                    <>
                                                      <Loader2 className="w-3 h-3 animate-spin text-emerald-500" />
                                                      <span className="bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">{lang === 'ua' ? 'Додаємо...' : 'Adding...'}</span>
                                                    </>
                                                  ) : (
                                                    <>
                                                      {lang === 'ua' ? 'Додати' : 'Add'}
                                                      <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                                                    </>
                                                  )}
                                                </div>
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {group.options.length > 1 && (
                                    <div className="flex justify-center gap-1.5 mt-2">
                                      {group.options.map((_: any, dotIdx: number) => (
                                        <div 
                                          key={dotIdx}
                                          className={cn(
                                            "w-1 h-1 rounded-full transition-all duration-300",
                                            (activeMealSuggestionIndices[gIdx] || 0) === dotIdx 
                                              ? "bg-emerald-500 w-3" 
                                              : "bg-zinc-300 dark:bg-zinc-700"
                                          )}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        ) : aiMealSuggestions.length === 0 ? (
                          <motion.div 
                            key="ai-suggestions-empty"
                            initial={{ opacity: 0, y: 15, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -15, scale: 0.98 }}
                            transition={{ duration: 0.3 }}
                            className={cn(
                              "border rounded-[32px] overflow-hidden transition-all p-6 text-center flex flex-col items-center justify-center gap-4",
                              theme === 'light' ? "bg-white border-zinc-100 shadow-[0_15px_45px_rgba(0,0,0,0.06)]" : "bg-[var(--card-bg)] border-[var(--card-border)] shadow-[0_20px_60px_rgba(0,0,0,0.3)]",
                              illumination && "illumination-food"
                            )}
                          >
                            <div className="bg-emerald-500/10 p-4 rounded-full text-emerald-500 shadow-inner">
                              <Sparkles className="w-8 h-8" />
                            </div>
                            <div>
                              <h5 className="font-black text-[var(--text)] text-sm uppercase tracking-wider mb-1">
                                {t.aiMealSuggestions}
                              </h5>
                              <p className="text-xs text-zinc-500 max-w-[85%] mx-auto leading-relaxed">
                                {lang === 'ua'
                                  ? (vibe === 'casual'
                                      ? "Не знаєш, що з'їсти? Нехай ШІ накидає тобі ідеальні страви під цілі та макроси! 👊"
                                      : "Отримайте персоналізовані рекомендації страв від штучного інтелекту під ваші цілі та калорії.")
                                  : (vibe === 'casual'
                                      ? "Let AI craft custom meal suggestions based on your daily macros, body goals, and active streak! 👊"
                                      : "Get personalized meal suggestions from our AI based on your body goals and target calories.")
                                }
                              </p>
                            </div>
                            <button
                              onClick={() => handleFetchSuggestions(undefined, true)}
                              disabled={isGeneratingSuggestions}
                              className="flex items-center gap-2 px-6 py-3 bg-[var(--brand-green)] hover:bg-emerald-600 active:scale-95 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20 transition-all text-center cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
                            >
                              <Sparkles className="w-3 h-3" />
                              {lang === 'ua' ? 'Згенерувати рекомендації' : 'Generate Suggestions'}
                            </button>
                          </motion.div>
                        ) : (
                          <motion.div 
                            key="ai-suggestions-completed"
                            initial={{ opacity: 0, y: 15, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -15, scale: 0.98 }}
                            transition={{ duration: 0.3 }}
                            className={cn(
                              "border rounded-[32px] overflow-hidden transition-all p-6 text-center flex flex-col items-center justify-center gap-4",
                              theme === 'light' ? "bg-white border-zinc-100 shadow-[0_15px_45px_rgba(0,0,0,0.06)]" : "bg-[var(--card-bg)] border-[var(--card-border)] shadow-[0_20px_60px_rgba(0,0,0,0.3)]",
                              illumination && "illumination-food"
                            )}
                          >
                            <div className="bg-emerald-500/10 p-4 rounded-full text-emerald-500 shadow-inner">
                              <CheckCircle2 className="w-8 h-8" />
                            </div>
                            <div>
                              <h5 className="font-black text-[var(--text)] text-sm uppercase tracking-wider mb-1">
                                {lang === 'ua' ? "ВСІ СТРАВИ ЗАВЕРШЕНО! 🔥" : "ALL PLANNED MEALS COMPLETED! 🔥"}
                              </h5>
                              <p className="text-xs text-zinc-500 max-w-[85%] mx-auto leading-relaxed">
                                {lang === 'ua'
                                  ? (vibe === 'casual'
                                      ? (userName ? `Айо, ${userName}! Ти закрив усі заплановані прийоми їжі на сьогодні. Продовжуй у тому ж дусі! 👊` : "Айо, бро! Ти закрив усі заплановані прийоми їжі на сьогодні. Продовжуй у тому ж дусі! 👊")
                                      : (userName ? `${userName}, ви успішно зафіксували всі заплановані страви на сьогодні. Чудова робота!` : "Ви успішно зафіксували всі заплановані страви на сьогодні. Чудова робота!"))
                                  : (vibe === 'casual'
                                      ? (userName ? `Ayo, ${userName}! You completed all your planned fuel for today. Keep crushing those goals! 👊` : "Ayo, homie! You completed all your planned fuel for today. Keep crushing those goals! 👊")
                                      : (userName ? `${userName}, you have successfully logged all planned meals for today. Keep up the excellent work!` : "You have successfully logged all planned meals for today. Keep up the excellent work!"))
                                }
                              </p>
                            </div>
                            <button
                              onClick={() => handleFetchSuggestions(undefined, true)}
                              disabled={isGeneratingSuggestions}
                              className="flex items-center gap-2 px-6 py-3 bg-[var(--brand-green)] hover:bg-emerald-600 active:scale-95 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20 transition-all text-center cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
                            >
                              <RefreshCw className={cn("w-3 h-3", isGeneratingSuggestions && "animate-spin")} />
                              {lang === 'ua' ? 'Оновити рекомендації ШІ' : 'Refresh AI Suggestions'}
                            </button>
                          </motion.div>
                        )}
                        </AnimatePresence>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                ) : (
                  <div className="relative">
                    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 flex items-center justify-between card-shadow">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs font-bold uppercase tracking-wider text-[var(--text)]">
                          {t.aiMealSuggestions} ({lang === 'ua' ? 'Вимкнено' : 'Disabled'})
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setEnableAiSuggestions(true);
                          localStorage.setItem('enable_ai_suggestions', 'true');
                          syncSettings({ enableAiSuggestions: true });
                        }}
                        className="px-3.5 py-1.5 bg-emerald-500 text-emerald-950 font-black text-[10px] uppercase tracking-wider rounded-xl hover:bg-emerald-400 transition-colors cursor-pointer shadow-md shadow-emerald-500/20"
                      >
                        {lang === 'ua' ? 'Увімкнути' : 'Enable'}
                      </button>
                    </div>
                  </div>
                )
              )}
              
              {/* Deficiency Alerts */}
              {enableHealthDeficiencyAlert && activeDeficiencies.length > 0 && dismissedDeficiencyAlertsDate !== new Date().toDateString() && selectedDate.toDateString() === new Date().toDateString() && (
                <div className="relative space-y-4">
                  <div 
                    className={cn(
                      "flex items-center justify-between px-2 transition-colors rounded-xl",
                      cn("cursor-pointer", theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800"),
                      isDeficiencyAlertsCollapsed ? "py-3" : "py-1"
                    )}
                    onClick={() => {
                      const newVal = !isDeficiencyAlertsCollapsed;
                      setIsDeficiencyAlertsCollapsed(newVal);
                      localStorage.setItem('deficiency_alerts_collapsed', String(newVal));
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <h4 className="text-xs font-black uppercase tracking-widest text-[var(--text)] select-none">
                        {lang.startsWith('ua') ? 'Дефіцит-контроль' : 'Deficiency Alerts'}
                      </h4>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        className={cn(
                          "p-1.5 rounded-[12px] text-zinc-500 hover:text-[var(--text)] transition-colors",
                          theme === 'light' ? "bg-zinc-100 hover:bg-zinc-200" : "bg-zinc-800 hover:bg-zinc-700"
                        )}
                      >
                        {isDeficiencyAlertsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setDismissedDeficiencyAlertsDate(new Date().toDateString());
                          localStorage.setItem('dismissed_deficiency_alerts_date', new Date().toDateString());
                        }}
                        className={cn(
                          "p-1.5 rounded-[12px] text-zinc-500 hover:text-[var(--text)] transition-colors",
                          theme === 'light' ? "bg-zinc-100 hover:bg-zinc-200" : "bg-zinc-800 hover:bg-zinc-700"
                        )}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <AnimatePresence initial={false}>
                    {!isDeficiencyAlertsCollapsed && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden px-2 pb-2"
                      >
                        <div className="grid grid-cols-1 gap-3">
                          {activeDeficiencies.map((alert) => (
                            <motion.div
                              key={alert.nutrientKey}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              onClick={() => {
                                sounds.playPop();
                                setSelectedNutrient({ key: alert.nutrientKey as any, label: alert.nutrientName });
                               setView('nutrient_breakdown');
                              }}
                              className="p-5 bg-[var(--card-bg)] border border-amber-500/30 rounded-3xl card-shadow hover:border-amber-500/60 transition-all cursor-pointer relative overflow-hidden group"
                            >
                              {/* Background subtle glow */}
                              <div className="absolute -right-10 -bottom-10 w-24 h-24 bg-amber-500/5 rounded-full blur-2xl group-hover:bg-amber-500/10 transition-colors pointer-events-none" />
                              
                              <div className="flex items-start gap-3.5">
                                <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-500 border border-amber-500/20">
                                  <AlertCircle className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <h4 className="font-extrabold text-sm text-[var(--text)] tracking-tight">
                                      {alert.nutrientName} {lang.startsWith('ua') ? 'в дефіциті' : 'Deficiency'}
                                    </h4>
                                    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">
                                      {alert.daysCount} {lang.startsWith('ua') ? 'ДНІВ' : 'DAYS'}
                                    </span>
                                  </div>
                                  
                                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 leading-normal">
                                    {lang.startsWith('ua') 
                                      ? `Вживайте більше: ${alert.recommendedFoods.slice(0, 4).join(', ')}.` 
                                      : `Consider eating more: ${alert.recommendedFoods.slice(0, 4).join(', ')}.`}
                                  </p>

                                  {/* Mini progress representation of deficiency */}
                                  <div className="mt-3 flex items-center gap-2">
                                    <div className="flex-1 bg-zinc-100 dark:bg-zinc-800 rounded-full h-1.5 overflow-hidden">
                                      <div 
                                        className="bg-amber-500 h-full rounded-full transition-all duration-1000 ease-out" 
                                        style={{ width: `${Math.min(100, alert.intakePercentages.reduce((sum, p) => sum + p, 0) / alert.daysCount)}%` }}
                                      />
                                    </div>
                                    <span className="text-[10px] font-mono text-amber-500 font-bold">
                                      {Math.round(alert.intakePercentages.reduce((sum, p) => sum + p, 0) / alert.daysCount)}% avg
                                    </span>
                                  </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-zinc-400 group-hover:text-[var(--text)] transition-colors self-center shrink-0" />
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
              
              {/* Date Switcher */}

              <div className={cn(illumination ? "space-y-10" : "space-y-[30px]")}>

                {showFastingWidget && (
                  subscriptionTier === 'free' ? (
                    <div className="relative p-6 rounded-3xl bg-[var(--card-bg)] border border-[var(--card-border)] text-center overflow-hidden card-shadow">
                      <Lock className="w-5 h-5 text-amber-500 mx-auto mb-2 animate-bounce" />
                      <h4 className="font-extrabold text-xs sm:text-sm uppercase tracking-wider text-[var(--text)]">
                        {lang === 'ua' ? 'Етапне голодування (PRO)' : 'Fasting Stages Tracker (PRO)'}
                      </h4>
                      <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-2 max-w-[280px] mx-auto leading-relaxed">
                        {lang === 'ua' 
                          ? 'Перейдіть на PRO, щоб розблокувати інтерактивний таймер інтервального голодування та статистику!'
                          : 'Upgrade to PRO to unlock interactive intermittent fasting timer & analytics!'}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setView('goals');
                          setIsSubscriptionExpanded(true);
                        }}
                        className="mt-3 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black text-[10px] font-black rounded-xl transition-colors cursor-pointer select-none uppercase tracking-wider"
                      >
                        {lang === 'ua' ? 'Розблокувати PRO' : 'Unlock PRO'}
                      </button>
                    </div>
                  ) : (
                    <FastingTimer
                      userId={user?.uid}
                      fastingState={fastingState}
                      onSaveFastingState={handleSaveFastingState}
                      lang={lang}
                      illumination={illumination}
                    />
                  )
                )}
                
                {/* E-Decoder Quick Tool Banner */}
                <div 
                  onClick={() => {
                    triggerHaptic('SUCCESS');
                    setEDecoderInitialQuery('');
                    setIsEDecoderOpen(true);
                  }}
                  className="p-4 rounded-3xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 border border-emerald-500/30 hover:border-emerald-500/60 transition-all cursor-pointer flex items-center justify-between gap-3 shadow-xs group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-2xl bg-emerald-500/20 text-emerald-500 border border-emerald-500/30 group-hover:scale-110 transition-transform">
                      <ShieldAlert className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-[var(--text)] flex items-center gap-2">
                        {lang.startsWith('ua') ? "E-Decoder — Перевірка Добавок" : "E-Decoder Additive Inspector"}
                        <span className="text-[9px] uppercase font-black px-2 py-0.5 rounded-full bg-emerald-500 text-white">
                          NEW
                        </span>
                      </h4>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400">
                        {lang.startsWith('ua') ? "Розшифровка E100-E1500, сканер складу та аналіз ризиків" : "Decode E-numbers, scan ingredients & assess health risks"}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-400 group-hover:text-emerald-500 transition-colors shrink-0" />
                </div>

                {/* Quick Actions */}
                <div className="space-y-4">

                  <div className="grid grid-cols-2 gap-3 sm:gap-6 md:gap-10">
                    <button 
                      onClick={() => setView('add_meal_options')}
                      className={cn(
                        skin === 'anime' ? "bg-[#ec4899] p-4 sm:p-6 rounded-3xl flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 transition-all group w-full text-[#0A0B10] border border-[#ec4899] shadow-[0_0_15px_rgba(236,72,153,0.5)] relative overflow-hidden" :
                        skin === 'cyberpunk' ? "bg-[#FCEE0A] p-4 sm:p-6 rounded-none flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 transition-all group w-full text-black border-2 border-[#FCEE0A] shadow-[0_0_20px_rgba(252,238,10,0.6)] relative overflow-hidden font-mono" :
                        "bg-emerald-500 dark:bg-emerald-600 p-4 sm:p-6 rounded-3xl flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 hover:bg-emerald-600 dark:hover:bg-emerald-700 transition-all group w-full text-white", 
                        illumination ? "illumination-food" : "",
                        "btn-active-border"
                      )}
                    >
                      {skin === 'anime' && <Target className="absolute inset-0 w-[200%] h-[200%] -left-1/2 -top-1/2 opacity-20 group-hover:rotate-90 transition-transform duration-1000 ease-linear pointer-events-none" />}
                      <div className={cn("p-2 sm:p-3 rounded-2xl group-hover:scale-110 transition-transform relative z-10", skin === 'anime' ? "bg-black/20" : skin === 'cyberpunk' ? "bg-black/15 text-black rounded-none" : "bg-white/20")}>
                        <Utensils className={cn("w-6 h-6 sm:w-8 sm:h-8", skin === 'anime' ? "text-[#0A0B10]" : skin === 'cyberpunk' ? "text-black" : "text-white")} />
                      </div>
                      <span className="font-bold text-[10px] sm:text-xl uppercase tracking-widest relative z-10">{skin === 'stalker' ? (lang === 'ua' ? 'СИНХР. РАЦІОН' : 'SYNC RATION') : skin === 'cyberpunk' ? (lang === 'ua' ? 'ЗАВАНТАЖИТИ ДАНІ' : 'UPLINK NUTRITION') : skin === 'anime' ? (lang === 'ua' ? 'ВЖИТИ МАНУ' : 'CONSUME MANA') : t.addMeal}</span>
                    </button>

                    <button 
                      onClick={() => setShowWaterModal(true)}
                      className={cn(
                        skin === 'anime' ? "bg-[#a855f7] p-4 sm:p-6 rounded-3xl flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 transition-all group w-full text-[#0A0B10] border border-[#a855f7] shadow-[0_0_15px_rgba(168,85,247,0.5)] relative overflow-hidden" :
                        skin === 'cyberpunk' ? "bg-[#00ffcc] p-4 sm:p-6 rounded-none flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 transition-all group w-full text-black border-2 border-[#00ffcc] shadow-[0_0_20px_rgba(0,255,204,0.6)] relative overflow-hidden font-mono" :
                        "bg-blue-500 dark:bg-blue-600 p-4 sm:p-6 rounded-3xl flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 hover:bg-blue-600 dark:hover:bg-blue-700 transition-all group w-full text-white", 
                        illumination ? "illumination-water" : "",
                        "btn-active-border-blue"
                      )}
                    >
                      {skin === 'anime' && <Target className="absolute inset-0 w-[200%] h-[200%] -left-1/2 -top-1/2 opacity-20 group-hover:-rotate-90 transition-transform duration-1000 ease-linear pointer-events-none" />}
                      <div className={cn("p-2 sm:p-3 rounded-2xl group-hover:scale-110 transition-transform relative z-10", skin === 'anime' ? "bg-black/20" : skin === 'cyberpunk' ? "bg-black/15 text-black rounded-none" : "bg-white/20")}>
                        <Droplets className={cn("w-6 h-6 sm:w-8 sm:h-8", skin === 'anime' ? "text-[#0A0B10]" : skin === 'cyberpunk' ? "text-black" : "text-white")} />
                      </div>
                      <span className="font-bold text-[10px] sm:text-xl uppercase tracking-widest relative z-10">{skin === 'stalker' ? (lang === 'ua' ? 'СИНХР. РІДИНУ' : 'SYNC LIQUID') : skin === 'cyberpunk' ? (lang === 'ua' ? 'РІДИННИЙ UPLINK' : 'UPLINK HYDRATION') : skin === 'anime' ? (lang === 'ua' ? 'ВИПИТИ ЗІЛЛЯ' : 'DRINK POTION') : (t.addWater || 'Add Liquid')}</span>
                    </button>
                  </div>
                </div>

                {/* Quick Add Row */}
                {quickAddItems.length > 0 && (
                  <div className="space-y-2">
                    <div 
                      className="flex items-center justify-between px-2 cursor-pointer select-none group/header hover:opacity-90 transition-opacity"
                      onClick={() => {
                        sounds.playPop();
                        setIsQuickAddExpanded(!isQuickAddExpanded);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-emerald-500 animate-pulse" />
                        <h4 className="text-xs font-black uppercase tracking-widest text-[var(--text)] flex items-center gap-1.5">
                          {lang === 'ua' ? 'Додати швидко' : 'Quick Add'}
                          {isQuickAddExpanded ? (
                            <ChevronUp className="w-3.5 h-3.5 text-zinc-500 transition-colors group-hover/header:text-emerald-500" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5 text-zinc-500 transition-colors group-hover/header:text-emerald-500" />
                          )}
                        </h4>
                      </div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">
                        {lang === 'ua' ? 'В один дотик' : 'One-tap log'}
                      </span>
                    </div>

                    <AnimatePresence initial={false}>
                      {isQuickAddExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          {/* Generous py-4 to ensure card illuminations are fully visible and not clipped */}
                          <div className="py-4 px-2 overflow-x-auto no-scrollbar scroll-smooth flex gap-4">
                            {quickAddItems.map((meal) => {
                              const isLoggingThisMeal = loggingQuickAddId === meal.id;
                              const cals = meal.total_nutrition?.calories_kcal || 0;
                              const prot = meal.total_nutrition?.protein_g || 0;
                              const carbs = meal.total_nutrition?.carbs_g || 0;
                              const fat = meal.total_nutrition?.fat_g || 0;
                              
                              return (
                                <motion.div
                                  key={meal.id}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => {
                                    if (!isLoggingThisMeal) {
                                      handleQuickAddMeal(meal);
                                    }
                                  }}
                                  className={cn(
                                    "flex-shrink-0 w-44 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-3 cursor-pointer hover:border-emerald-500 transition-all card-shadow flex flex-col justify-between relative group overflow-hidden select-none",
                                    illumination && "illumination-food",
                                    isLoggingThisMeal && "btn-active-border"
                                  )}
                                >
                                  <div className="flex items-start gap-2.5 mb-2">
                                    {/* Thumbnail */}
                                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-[var(--card-bg)] flex-shrink-0 border border-[var(--card-border)] relative">
                                      {meal.image ? (
                                        <img src={meal.image} alt={meal.meal_name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                          <Utensils className="w-4 h-4 text-zinc-500" />
                                        </div>
                                      )}
                                    </div>
                                    
                                    <div className="min-w-0 flex-grow">
                                      <h5 className="font-extrabold text-[var(--text)] text-xs leading-tight line-clamp-2" title={meal.meal_name}>
                                        {meal.meal_name}
                                      </h5>
                                      <span className="font-bold text-[10px] text-emerald-500 block mt-0.5">
                                        {Math.round(cals)} kcal
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center justify-between pt-1 border-t border-[var(--card-border)] mt-1">
                                    <div className="flex items-center gap-1.5 text-[8px] font-mono font-bold text-zinc-500">
                                      <span>P:{Math.round(prot)}g</span>
                                      <span>C:{Math.round(carbs)}g</span>
                                      <span>F:{Math.round(fat)}g</span>
                                    </div>
                                    
                                    <div className="p-1 rounded-lg bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                                      {isLoggingThisMeal ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Plus className="w-3 h-3" />
                                      )}
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Water Tracking */}
                {/* WaterTrackingWidget removed */}
                
                <DailyProgressSummary 
                  totals={selectedDayTotals} 
                  goals={goals} 
                  title={new Date().toDateString() === selectedDate.toDateString() ? t.todaysProgress : t.dailyProgress} 
                  selectedDate={selectedDate}
                  lang={lang}
                  theme={theme}
                  t={t}
                  illumination={illumination}
                  onNutrientClick={handleNutrientClick}
                  viewMode={dailyProgressViewMode} skin={skin}
                  streaks={selectedDayStreaks}
                  consecutiveStreak={consecutiveDaysStreak}
                  hasIncreased={streakIncreased}
                />
              </div>
            </div>
          </section>

            {/* Recent Activity */}
            <section className={cn(illumination ? "space-y-4" : "space-y-3")}>
              <div className="flex items-center justify-between px-2">
                <h3 className="text-lg font-bold">{new Date().toDateString() === selectedDate.toDateString() ? t.recentMeals : t.mealBreakdown}</h3>
                <div className="flex items-center gap-1 text-xs text-sky-500 font-bold">
                  <Droplets className="w-4 h-4" />
                  {selectedDayTotals.water} ml
                </div>
              </div>
              {selectedDayCombined.length === 0 ? (
                <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-8 text-center card-shadow">
                  <p className="text-zinc-500 text-sm">{t.noMealsLogged}</p>
                </div>
              ) : (
                <motion.div 
                  key={`history-list-${selectedDate.toDateString()}`}
                  variants={{
                    hidden: {},
                    show: {
                      transition: {
                        staggerChildren: 0.08
                      }
                    }
                  }}
                  initial="hidden"
                  animate="show"
                  className={cn(illumination ? "space-y-[15px] sm:space-y-[10px]" : "space-y-[15px]", "sm:pb-0")}
                >
                  <AnimatePresence mode="popLayout" initial={false}>
                  {selectedDayCombined.map(item => (
                    item.type === 'meal' ? (
                      <motion.div 
                        key={item.id}
                        layout
                        variants={{
                          hidden: { opacity: 0, y: 15 },
                          show: { 
                            opacity: 1, 
                            y: 0,
                            x: 0,
                            scale: 1,
                            transition: {
                              type: "spring",
                              stiffness: 100,
                              damping: 15
                            }
                          }
                        }}
                        animate={deletingIds.has(item.id) ? { 
                          x: 350, 
                          opacity: 0, 
                          scale: 0.9, 
                          height: 0, 
                          marginTop: 0, 
                          marginBottom: 0,
                          paddingTop: 0,
                          paddingBottom: 0
                        } : "show"}
                        exit={{ 
                          x: 350, 
                          opacity: 0, 
                          scale: 0.9, 
                          height: 0, 
                          marginTop: 0, 
                          marginBottom: 0,
                          paddingTop: 0,
                          paddingBottom: 0,
                          transition: { duration: 0.3, ease: [0.32, 0, 0.67, 0] } 
                        }}
                        transition={{ duration: 0.3, layout: { type: "spring", stiffness: 350, damping: 25 } }}
                        onClick={() => {
                          if (!isLongPressingMeal) {
                            setSelectedHistoryItem(item);
                            setView('details');
                          }
                        }}
                        onTouchStart={(e) => handleMealTouchStart(e, item.id)}
                        onTouchEnd={handleMealTouchEnd}
                        onTouchMove={handleMealTouchMove}
                        onMouseDown={(e) => handleMealTouchStart(e, item.id)}
                        onMouseUp={handleMealTouchEnd}
                        onMouseLeave={handleMealTouchMove}
                        className={cn("meal-entry-card bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4 cursor-pointer hover:border-emerald-500 transition-all group card-shadow select-none relative overflow-hidden", illumination ? getIlluminationClass(item) : "")}
                      >
                        <motion.div 
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ type: "spring", stiffness: 280, damping: 20 }}
                          className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-[var(--card-bg)] flex-shrink-0 border border-[var(--card-border)]"
                        >
                          {item.image ? (
                            <img src={item.image} alt={item.meal_name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Utensils className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-500" />
                            </div>
                          )}
                        </motion.div>
                        
                        <div className="flex-grow min-w-0">
                          <div className="flex items-start justify-between mb-1 gap-3 w-full">
                            <div className="min-w-0 flex-grow">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="font-bold text-base sm:text-lg leading-tight text-[var(--text)] line-clamp-2">{item.meal_name}</h3>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  {Array.from(new Set(item.foods?.map(f => f.category?.toLowerCase()).filter(Boolean) || []))?.map((cat, idx) => (
                                    <motion.span 
                                      key={idx} 
                                      className="text-[10px] sm:text-xs inline-block" 
                                      title={String(cat)}
                                      initial={{ scale: 0.3, y: 8 }}
                                      animate={{ scale: 1, y: 0 }}
                                      transition={{ 
                                        type: "spring",
                                        stiffness: 220,
                                        damping: 12,
                                        delay: idx * 0.08
                                      }}
                                      whileHover={{ scale: 1.3, rotate: 12 }}
                                    >
                                      {FOOD_CATEGORIES.find(c => c.id === cat)?.icon || '🍴'}
                                    </motion.span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end gap-1.5 flex-shrink-0 text-right">
                              <div className="flex items-start gap-2">
                                <div className="flex flex-col items-end gap-1">
                                  {item.meal_type ? (
                                    <span className={cn(
                                      "text-[7px] sm:text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0",
                                      item.meal_type === 'breakfast' && "bg-amber-500/10 text-amber-500 border-amber-500/20",
                                      item.meal_type === 'lunch' && "bg-blue-500/10 text-blue-500 border-blue-500/20",
                                      item.meal_type === 'dinner' && "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
                                      item.meal_type === 'snack' && "bg-rose-500/10 text-rose-500 border-rose-500/20"
                                    )}>
                                      {t[item.meal_type as keyof typeof t] || item.meal_type}
                                    </span>
                                  ) : (
                                    item.tags && item.tags.length > 0 && (
                                      (Array.from(new Set(item.tags)) as string[])
                                        .filter(tag => tag !== 'water')
                                        .map(tag => (
                                          <span key={tag} className={cn(
                                            "text-[7px] sm:text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0",
                                            tag === 'breakfast' && "bg-amber-500/10 text-amber-500 border-amber-500/20",
                                            tag === 'lunch' && "bg-blue-500/10 text-blue-500 border-blue-500/20",
                                            tag === 'dinner' && "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
                                            tag === 'snack' && "bg-rose-500/10 text-rose-500 border-rose-500/20"
                                          )}>
                                            {t[tag as keyof typeof t] || tag}
                                          </span>
                                        ))
                                    )
                                  )}

                                  {(() => {
                                    const badge = getPrimaryMacroBadge(item.total_nutrition);
                                    return badge ? (
                                      <motion.span 
                                        initial={{ opacity: 0, scale: 0.85, y: 2 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        transition={{ duration: 0.35, ease: "easeOut" }}
                                        className={cn("text-[7px] sm:text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0", badge.class)}
                                      >
                                        {badge.label}
                                      </motion.span>
                                    ) : null;
                                  })()}
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5">
                                <span className="text-emerald-500 font-bold whitespace-nowrap text-xs sm:text-base flex-shrink-0">
                                  {Math.round(item.total_nutrition?.calories_kcal || 0)} {t.kcal}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-x-2 sm:gap-x-3 gap-y-1 text-[10px] sm:text-xs text-zinc-500 mb-1 sm:mb-2 flex-nowrap overflow-x-auto no-scrollbar">
                            <div className="flex items-center gap-1 shrink-0 whitespace-nowrap">
                              <Calendar className="w-3 h-3 text-emerald-500/80 shrink-0" />
                              <span>{formatDateTime(new Date(item.timestamp), dateFormat, timeFormat, lang, true)}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 whitespace-nowrap">
                              <span className="flex items-center gap-1">
                                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-blue-500 shrink-0" />
                                {(item.total_nutrition?.protein_g || 0).toFixed(2)}{t.proteinShort}
                              </span>
                              <span className="flex items-center gap-1">
                                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-red-500 shrink-0" />
                                {(item.total_nutrition?.carbs_g || 0).toFixed(2)}{t.carbsShort}
                              </span>
                              <span className="flex items-center gap-1">
                                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-amber-500 shrink-0" />
                                {(item.total_nutrition?.fat_g || 0).toFixed(2)}{t.fatShort}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div 
                        key={item.id}
                        layout
                        variants={{
                          hidden: { opacity: 0, y: 15 },
                          show: { 
                            opacity: 1, 
                            y: 0,
                            x: 0,
                            scale: 1,
                            transition: {
                              type: "spring",
                              stiffness: 100,
                              damping: 15
                            }
                          }
                        }}
                        animate={deletingIds.has(item.id) ? { 
                          x: 350, 
                          opacity: 0, 
                          scale: 0.9, 
                          height: 0, 
                          marginTop: 0, 
                          marginBottom: 0,
                          paddingTop: 0,
                          paddingBottom: 0
                        } : "show"}
                        exit={{ 
                          x: 350, 
                          opacity: 0, 
                          scale: 0.9, 
                          height: 0, 
                          marginTop: 0, 
                          marginBottom: 0,
                          paddingTop: 0,
                          paddingBottom: 0,
                          transition: { duration: 0.3, ease: [0.32, 0, 0.67, 0] } 
                        }}
                        transition={{ duration: 0.3, layout: { type: "spring", stiffness: 350, damping: 25 } }}
                        className={cn("meal-entry-card bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4 group card-shadow transition-all relative overflow-hidden", illumination ? getIlluminationClass(item) : "")}
                      >
                        <motion.div 
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ type: "spring", stiffness: 280, damping: 20 }}
                          className={cn(
                            "w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center border",
                            item.brand_name ? "bg-purple-500/10 border-purple-500/20" :
                            item.beverage_type === 'coffee' ? "bg-amber-500/10 border-amber-500/20" : 
                            item.beverage_type === 'tea' ? "bg-emerald-500/10 border-emerald-500/20" : 
                            "bg-blue-500/10 border-blue-500/20"
                          )}
                        >
                          {item.brand_id ? (
                            <BrandLogoCircle 
                              logoType={MASTER_DRINK_BRANDS.find(b => b.id === item.brand_id)?.logoType || 'generic_can'} 
                              name={item.brand_name || 'Drink'} 
                              sizePx={40} 
                            />
                          ) : item.beverage_type === 'coffee' ? <Coffee className="w-6 h-6 text-amber-500" /> :
                           item.beverage_type === 'tea' ? <Leaf className="w-6 h-6 text-emerald-500" /> :
                           <Droplets className="w-6 h-6 text-blue-500" />}
                        </motion.div>
                        <div className="flex-grow min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-1 gap-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <h3 className="font-bold text-base sm:text-lg leading-tight text-[var(--text)] line-clamp-2">
                                + {item.brand_name || (item.beverage_type === 'coffee' ? (lang === 'ua' ? 'Кава' : 'Coffee') : item.beverage_type === 'tea' ? (lang === 'ua' ? 'Чай' : 'Tea') : t.water)}
                              </h3>
                              <span className={cn(
                                "text-[7px] sm:text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0",
                                item.brand_name ? "bg-purple-500/10 text-purple-400 border-purple-500/20" : "bg-sky-500/10 text-sky-500 border-sky-500/20"
                              )}>
                                {item.brand_name ? 'Brand' : t.water}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-blue-500 font-bold whitespace-nowrap text-xs sm:text-base">+{item.amount_ml} ml</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] sm:text-xs text-zinc-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDateTime(new Date(item.timestamp), dateFormat, timeFormat, lang)}
                            </span>
                            {item.calories_kcal !== undefined && item.calories_kcal > 0 && (
                              <span className="font-bold text-amber-500">
                                {Math.round(item.calories_kcal)} kcal
                              </span>
                            )}
                            {item.caffeine_mg !== undefined && item.caffeine_mg > 0 && (
                              <span className="font-bold text-purple-400">
                                {Math.round(item.caffeine_mg)} mg caffeine
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )
                  ))}
                  </AnimatePresence>
                </motion.div>
              )}
            </section>
          </motion.div>
          </AnimatePresence>
          </div>
        </div>

        <AnimatePresence>
          {showWaterModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className={cn(
                "fixed inset-0 z-[100] flex items-center justify-center p-4",
                theme === 'light' 
                  ? "bg-black/15 backdrop-blur-md" 
                  : (illumination ? "bg-black/35 backdrop-blur-[2px]" : "bg-black/60 backdrop-blur-sm")
              )}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: "spring", stiffness: 400, damping: 26 }}
                className={cn(
                  "w-full relative text-[var(--text)] transition-all duration-300",
                  illumination ? "max-w-sm" : "max-w-md"
                )}
              >
                <div className={cn(
                  "relative border rounded-[32px] card-shadow overflow-hidden transition-all duration-300",
                  theme === 'light' 
                    ? "border-zinc-200/80 shadow-2xl shadow-zinc-200/50" 
                    : "border-[var(--card-border)]",
                  theme === 'light'
                    ? (illumination 
                        ? "bg-white/95 backdrop-blur-md p-5 sm:p-6 space-y-4 sm:space-y-6" 
                        : "bg-white p-6 sm:p-8 space-y-6 sm:space-y-8")
                    : (illumination 
                        ? "bg-zinc-900/85 backdrop-blur-md p-5 sm:p-6 space-y-4 sm:space-y-6" 
                        : "bg-[var(--card-bg)] p-6 sm:p-8 space-y-6 sm:space-y-8"),
                  illumination && (liquidType === 'water' ? "illumination-water" : "illumination-food")
                )}>
              {/* Atmospheric Water Background */}
              {illumination && (
                <div className="absolute inset-0 pointer-events-none">
                  <div className={cn(
                    "absolute inset-0 bg-gradient-to-b from-transparent transition-colors duration-500",
                    liquidType === 'water' ? "via-blue-50/30 to-blue-100/60 dark:via-blue-900/10 dark:to-blue-900/20" :
                    liquidType === 'coffee' ? "via-amber-50/30 to-amber-100/60 dark:via-amber-900/10 dark:to-amber-900/20" :
                    "via-emerald-50/30 to-emerald-100/60 dark:via-emerald-900/10 dark:to-emerald-900/20"
                  )} />
                  <motion.div 
                    animate={{ y: [0, -20, 0], x: [0, 15, 0], scale: [1, 1.1, 1] }}
                    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                    className={cn(
                      "absolute -bottom-32 -left-20 w-80 h-80 rounded-full blur-3xl transition-colors duration-500",
                      liquidType === 'water' ? "bg-blue-200/20 dark:bg-blue-300/10" :
                      liquidType === 'coffee' ? "bg-amber-200/20 dark:bg-amber-300/10" :
                      "bg-emerald-200/20 dark:bg-emerald-300/10"
                    )}
                  />
                  <motion.div 
                    animate={{ y: [0, 25, 0], x: [0, -20, 0], scale: [1, 1.15, 1] }}
                    transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className={cn(
                      "absolute -bottom-20 -right-20 w-96 h-96 rounded-full blur-3xl transition-colors duration-500",
                      liquidType === 'water' ? "bg-cyan-200/20 dark:bg-cyan-300/10" :
                      liquidType === 'coffee' ? "bg-orange-200/20 dark:bg-orange-300/10" :
                      "bg-lime-200/20 dark:bg-lime-300/10"
                    )}
                  />
                  <motion.div 
                    animate={{ y: [0, -40, 0], opacity: [0.2, 0.4, 0.2] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                    className={cn(
                      "absolute top-1/4 left-1/3 w-40 h-40 rounded-full blur-3xl transition-colors duration-500",
                      liquidType === 'water' ? "bg-blue-100/30 dark:bg-blue-200/10" :
                      liquidType === 'coffee' ? "bg-amber-100/30 dark:bg-amber-200/10" :
                      "bg-emerald-100/30 dark:bg-emerald-200/10"
                    )}
                  />
                </div>
              )}

              <div className="relative z-10 text-center space-y-2">
                <h2 className={cn(
                  "font-black uppercase tracking-tight transition-all duration-300",
                  illumination ? "text-2xl" : "text-3xl",
                  theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.05)]" : "text-[var(--text)]"
                )}>
                  {lang !== 'ua' 
                    ? `Add some liquid${userName ? `, ${userName}` : ''}` 
                    : `Додамо рідини${userName ? `, ${userName}` : ''}`}
                </h2>
                <div className="space-y-2">
                  <p className={cn(
                    "text-sm font-bold uppercase tracking-widest transition-colors duration-300",
                    theme === 'light' ? "text-zinc-500" : "text-zinc-400"
                  )}>{selectedDayTotals.water} / {goals.water_ml} ml</p>
                  <div className={cn(
                    "w-full rounded-full h-4 overflow-hidden transition-colors duration-300",
                    theme === 'light' ? "bg-zinc-200/80" : "bg-zinc-800"
                  )}>
                    <motion.div 
                      className={cn(
                        "h-full rounded-full transition-colors duration-500",
                        liquidType === 'water' ? "bg-blue-500" :
                        liquidType === 'coffee' ? "bg-amber-500" :
                        "bg-emerald-500"
                      )}
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(100, (selectedDayTotals.water / goals.water_ml) * 100)}%` }}
                      transition={{ type: "spring", stiffness: 100, damping: 20 }}
                    />
                  </div>
                </div>
              </div>

              <div className="relative z-10 space-y-6">
                {/* Segmented Beverages Tab Row */}
                <div className={cn(
                  "grid grid-cols-4 p-1.5 rounded-2xl border transition-colors duration-300 gap-1",
                  theme === 'light' 
                    ? "bg-zinc-100 border-zinc-200/50" 
                    : "bg-zinc-800/80 border-zinc-700/50"
                )}>
                  <button 
                    onClick={() => setLiquidType('water')}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 rounded-xl font-black uppercase tracking-wider text-[10px] transition-all",
                      liquidType === 'water' 
                        ? "bg-blue-500 text-white shadow-md shadow-blue-500/25" 
                        : "text-zinc-500 hover:text-blue-500"
                    )}
                  >
                    <Droplets className="w-3.5 h-3.5" />
                    {t.water || 'Water'}
                  </button>
                  <button 
                    onClick={() => setLiquidType('coffee')}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 rounded-xl font-black uppercase tracking-wider text-[10px] transition-all",
                      liquidType === 'coffee' 
                        ? "bg-amber-800 text-white shadow-md shadow-amber-800/25" 
                        : "text-zinc-500 hover:text-amber-800"
                    )}
                  >
                    <Coffee className="w-3.5 h-3.5" />
                    {lang === 'ua' ? 'Кава' : 'Coffee'}
                  </button>
                  <button 
                    onClick={() => setLiquidType('tea')}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 rounded-xl font-black uppercase tracking-wider text-[10px] transition-all",
                      liquidType === 'tea' 
                        ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/25" 
                        : "text-zinc-500 hover:text-emerald-500"
                    )}
                  >
                    <Leaf className="w-3.5 h-3.5" />
                    {lang === 'ua' ? 'Чай' : 'Tea'}
                  </button>
                  <button 
                    onClick={() => {
                      setLiquidType('custom');
                      if (!selectedBrandDrinkId && activePersonalizedDrinks.length > 0) {
                        setSelectedBrandDrinkId(activePersonalizedDrinks[0].id);
                      }
                    }}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 rounded-xl font-black uppercase tracking-wider text-[10px] transition-all",
                      liquidType === 'custom' 
                        ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-500/25" 
                        : "text-zinc-500 hover:text-purple-500"
                    )}
                  >
                    <Zap className="w-3.5 h-3.5" />
                    {lang === 'ua' ? 'Бренди' : 'Brands'}
                  </button>
                </div>

                {/* Brand Search & Filter Area */}
                {liquidType === 'custom' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-zinc-500">
                        {beverageSearchQuery.trim() || selectedCategoryFilter !== 'all' 
                          ? (lang === 'ua' ? 'Результати пошуку:' : 'Search Results:')
                          : (lang === 'ua' ? 'Напої та Бренди:' : 'Drinks & Brands:')
                        }
                      </span>
                      <button 
                        type="button" 
                        onClick={() => {
                          setShowWaterModal(false);
                          setView('goals');
                          setIsPersonalizationExpanded(true);
                          setIsDrinksBrandsExpanded(true);
                        }}
                        className="text-[10px] font-bold text-emerald-500 hover:underline flex items-center gap-1"
                      >
                        <Settings className="w-3 h-3" />
                        {lang === 'ua' ? 'Налаштувати' : 'Customize'}
                      </button>
                    </div>

                    <div className="relative">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                      <input
                        type="text"
                        placeholder={lang === 'ua' ? 'Пошук за назвою або категорією...' : 'Search by name or category...'}
                        value={beverageSearchQuery}
                        onChange={(e) => setBeverageSearchQuery(e.target.value)}
                        className={cn(
                          "w-full pl-10 pr-10 py-2 rounded-2xl text-xs font-semibold border focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all",
                          theme === 'light'
                            ? "bg-zinc-50 border-zinc-200 text-zinc-800 placeholder-zinc-400 focus:border-purple-500/50"
                            : "bg-zinc-900 border-zinc-800/80 text-zinc-100 placeholder-zinc-500 focus:border-purple-500/50"
                        )}
                      />
                      {beverageSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setBeverageSearchQuery('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {/* Quick Category Pills */}
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                      {[
                        { id: 'all', label: lang === 'ua' ? 'Всі' : 'All' },
                        { id: 'soda', label: lang === 'ua' ? 'Газовані' : 'Soda' },
                        { id: 'energy', label: lang === 'ua' ? 'Енергетики' : 'Energy' },
                        { id: 'juice', label: lang === 'ua' ? 'Сік' : 'Juice' },
                        { id: 'water', label: lang === 'ua' ? 'Вода' : 'Water' },
                        { id: 'tea', label: lang === 'ua' ? 'Чай' : 'Tea' },
                        { id: 'coffee', label: lang === 'ua' ? 'Кава' : 'Coffee' }
                      ].map((cat) => {
                        const isCatSelected = selectedCategoryFilter === cat.id;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => {
                              setSelectedCategoryFilter(cat.id);
                            }}
                            className={cn(
                              "px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all whitespace-nowrap border cursor-pointer",
                              isCatSelected
                                ? "bg-purple-500 border-purple-500 text-white shadow-sm shadow-purple-500/20 backdrop-blur-md"
                                : "glass bg-white/70 dark:bg-zinc-900/70 backdrop-blur-md border-zinc-200/80 dark:border-zinc-800/80 text-zinc-700 dark:text-zinc-300 hover:bg-white/90 dark:hover:bg-zinc-800/90 shadow-xs"
                            )}
                          >
                            {cat.label}
                          </button>
                        );
                      })}
                    </div>

                    {displayedBrandDrinks.length > 0 ? (
                      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none pt-1">
                        {displayedBrandDrinks.map((drink) => {
                          const isSelected = liquidType === 'custom' && selectedBrandDrinkId === drink.id;
                          const isEnergyRestricted = drink.isEnergyDrink && !isAge18Plus;

                          return (
                            <div
                              key={drink.id}
                              onClick={() => {
                                if (isEnergyRestricted) {
                                  setPendingEnergyDrink(drink);
                                  setShowAgeVerificationModal(true);
                                  return;
                                }
                                setSelectedBrandDrinkId(drink.id);
                                setLiquidType('custom');
                                setWaterSliderValue(drink.defaultVolumeMl);
                              }}
                              className={cn(
                                "flex flex-col items-center gap-1 p-2 rounded-2xl border transition-all cursor-pointer min-w-[72px] shrink-0 relative group",
                                isSelected 
                                  ? "bg-purple-500/10 border-purple-500/60 scale-105 shadow-md shadow-purple-500/15" 
                                  : theme === 'light'
                                    ? "bg-zinc-50 border-zinc-200/80 hover:border-zinc-300"
                                    : "bg-zinc-800/60 border-zinc-700 hover:border-zinc-600"
                              )}
                            >
                              <BrandLogoCircle logoType={drink.logoType} name={drink.name} sizePx={40} />
                              <span className="text-[10px] font-bold text-[var(--text)] text-center line-clamp-1 max-w-[76px]">
                                {drink.name}
                              </span>
                              {drink.isEnergyDrink && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-black px-1 rounded-full border border-red-600">
                                  18+
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-4 text-center text-xs text-zinc-500 font-medium">
                        {lang === 'ua' ? 'Нічого не знайдено' : 'No drinks found'}
                        {(beverageSearchQuery || selectedCategoryFilter !== 'all') && (
                          <button
                            type="button"
                            onClick={() => {
                              setBeverageSearchQuery('');
                              setSelectedCategoryFilter('all');
                            }}
                            className="ml-2 text-purple-500 font-bold underline"
                          >
                            {lang === 'ua' ? 'Скинути' : 'Reset'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Custom Brand Nutrient Preview Badge */}
                {liquidType === 'custom' && selectedBrandDrink && (
                  <div className="p-3 rounded-2xl bg-purple-500/10 border border-purple-500/20 space-y-1.5 animate-fadeIn">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BrandLogoCircle logoType={selectedBrandDrink.logoType} name={selectedBrandDrink.name} sizePx={24} />
                        <span className="text-xs font-black text-[var(--text)]">{selectedBrandDrink.name}</span>
                        {selectedBrandDrink.isEnergyDrink && (
                          <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-red-500/20 text-red-500">
                            18+ Energy
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] font-bold text-zinc-500">
                        {selectedBrandDrink.brand}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 pt-1 border-t border-purple-500/15 text-center">
                      <div>
                        <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 block">
                          {lang === 'ua' ? 'Калорії' : 'Calories'}
                        </span>
                        <span className="text-xs font-black text-amber-500">
                          {Math.round((waterSliderValue / 100) * selectedBrandDrink.caloriesPer100ml)} kcal
                        </span>
                      </div>
                      <div>
                        <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 block">
                          {lang === 'ua' ? 'Кофеїн' : 'Caffeine'}
                        </span>
                        <span className="text-xs font-black text-purple-400">
                          {Math.round((waterSliderValue / 100) * selectedBrandDrink.caffeinePer100ml)} mg
                        </span>
                      </div>
                      <div>
                        <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 block">
                          {lang === 'ua' ? 'Цукор' : 'Sugar'}
                        </span>
                        <span className="text-xs font-black text-pink-400">
                          {Math.round((waterSliderValue / 100) * selectedBrandDrink.sugarPer100ml)} g
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <span className={cn(
                      "text-3xl sm:text-4xl font-black transition-colors duration-500",
                      liquidType === 'water' ? "text-blue-500" :
                      liquidType === 'coffee' ? "text-amber-800" :
                      liquidType === 'tea' ? "text-emerald-500" :
                      "text-purple-500"
                    )}>{waterSliderValue} <span className="text-sm text-zinc-500 dark:text-zinc-400">ml</span></span>
                    <div className="flex gap-2">
                      {[250, 330, 500].map(amount => (
                        <button
                          key={amount}
                          onClick={() => setWaterSliderValue(amount)}
                          className={cn(
                            "px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border",
                            waterSliderValue === amount 
                              ? (liquidType === 'water' ? "bg-blue-500 border-blue-500 text-white shadow-lg shadow-blue-500/20" :
                                 liquidType === 'coffee' ? "bg-amber-800 border-amber-800 text-white shadow-lg shadow-amber-800/20" :
                                 liquidType === 'tea' ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20" :
                                 "bg-purple-600 border-purple-600 text-white shadow-lg shadow-purple-500/20")
                              : cn(
                                  "border font-bold transition-all",
                                  theme === 'light' 
                                    ? "bg-zinc-100 hover:bg-zinc-200 border-zinc-200 text-zinc-700" 
                                    : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200",
                                  liquidType === 'water' ? "hover:text-blue-500 hover:border-blue-500/50" :
                                  liquidType === 'coffee' ? "hover:text-amber-800 hover:border-amber-800/50" :
                                  liquidType === 'tea' ? "hover:text-emerald-500 hover:border-emerald-500/50" :
                                  "hover:text-purple-500 hover:border-purple-500/50"
                                )
                          )}
                        >
                          {amount}ml
                        </button>
                      ))}
                    </div>
                  </div>
                  <input 
                    type="range" 
                    min="50" 
                    max="2000" 
                    step="50" 
                    value={waterSliderValue} 
                    onChange={(e) => setWaterSliderValue(Number(e.target.value))}
                    className={cn(
                      "w-full h-2 rounded-lg appearance-none cursor-pointer border transition-colors duration-500",
                      theme === 'light' ? "bg-zinc-200 border-zinc-300/40" : "bg-zinc-800 border-zinc-800",
                      liquidType === 'water' ? "accent-blue-500" :
                      liquidType === 'coffee' ? "accent-amber-800" :
                      liquidType === 'tea' ? "accent-emerald-500" :
                      "accent-purple-500"
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <button 
                    onClick={() => setShowWaterModal(false)}
                    className={cn(
                      "py-3.5 rounded-2xl font-black text-sm uppercase tracking-widest transition-all border shadow-sm",
                      theme === 'light'
                        ? "bg-zinc-100 hover:bg-zinc-200 border-zinc-200 text-zinc-700 hover:text-zinc-900"
                        : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-white"
                    )}
                  >
                    {t.cancel || 'Nah'}
                  </button>
                  <button 
                    onClick={() => {
                      if (liquidType === 'custom' && selectedBrandDrink) {
                        addWaterWithBrand(waterSliderValue, selectedBrandDrink);
                      } else {
                        addWater(waterSliderValue, liquidType);
                      }
                      setShowWaterModal(false);
                    }}
                    className={cn(
                      "text-white py-3.5 rounded-2xl font-black text-sm uppercase tracking-widest transition-colors shadow-lg cursor-pointer",
                      liquidType === 'water' ? "bg-blue-500 hover:bg-blue-600 shadow-blue-500/20 btn-active-border-blue" :
                      liquidType === 'coffee' ? "bg-amber-800 hover:bg-amber-900 shadow-amber-800/20 btn-active-border-red" :
                      liquidType === 'tea' ? "bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20 btn-active-border" :
                      "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-purple-500/20 btn-active-border-purple"
                    )}
                  >
                    {vibe === 'casual' ? 'Sure' : (lang.startsWith('en') ? 'Add' : 'Додати')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Body Weight Log Modal */}
    <WeightLogModal
      isOpen={showWeightModal}
      onClose={() => setShowWeightModal(false)}
      lang={lang}
      theme={theme}
      illumination={illumination}
      baseWeight={parseFloat(onboardingWeight) || 70}
      weightInput={weightLogInput}
      setWeightInput={setWeightLogInput}
      weightDate={weightLogDate}
      setWeightDate={setWeightLogDate}
      weightNote={weightLogNote}
      setWeightNote={setWeightLogNote}
      onSave={(val, dateStr, note) => handleSaveWeightLog(val, dateStr, note)}
    />

    {/* 18+ Age Verification Modal for Energy Drinks */}
    <AnimatePresence>
      {showAgeVerificationModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="w-full max-w-sm bg-[var(--card-bg)] border border-amber-500/40 rounded-3xl p-6 shadow-2xl space-y-5 text-center relative overflow-hidden"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-500 shadow-inner">
              <Zap className="w-8 h-8 animate-pulse" />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black uppercase tracking-tight text-[var(--text)]">
                {lang === 'ua' ? '⚡ Підтвердження Віку (18+)' : '⚡ Age Verification (18+)'}
              </h3>
              <p className="text-xs text-zinc-500 leading-relaxed font-medium">
                {lang === 'ua'
                  ? `Енергетичні напої (${pendingEnergyDrink?.name || 'Red Bull, Monster, Non Stop, Burn'}) містять високий вміст кофеїну (~32мг/100мл) та таурин. Будь ласка, підтвердіть, що вам виповнилося 18 років.`
                  : `Energy drinks (${pendingEnergyDrink?.name || 'Red Bull, Monster, Non Stop, Burn'}) contain elevated caffeine levels (~32mg/100ml) and taurine. Please confirm you are 18 years or older to proceed.`}
              </p>
            </div>

            <div className="flex flex-col gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsAge18Plus(true);
                  setShowAgeVerificationModal(false);
                  if (pendingEnergyDrink) {
                    setSelectedBrandDrinkId(pendingEnergyDrink.id);
                    setLiquidType('custom');
                    setWaterSliderValue(pendingEnergyDrink.defaultVolumeMl);
                    if (!selectedDrinkIds.includes(pendingEnergyDrink.id) && selectedDrinkIds.length < 10) {
                      setSelectedDrinkIds([...selectedDrinkIds, pendingEnergyDrink.id]);
                    }
                  }
                  setPendingEnergyDrink(null);
                  toast.success(
                    lang === 'ua' ? 'Вік 18+ підтверджено!' : '18+ Age verified!',
                    { icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" /> }
                  );
                }}
                className="w-full py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-black font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-amber-500/20"
              >
                {lang === 'ua' ? 'Мені є 18 років (Підтвердити)' : 'I am 18+ (Verify & Select)'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAgeVerificationModal(false);
                  setPendingEnergyDrink(null);
                }}
                className="w-full py-2.5 rounded-2xl border border-zinc-300 dark:border-zinc-700 text-zinc-500 hover:text-zinc-900 dark:hover:text-white font-bold text-xs uppercase tracking-widest transition-all"
              >
                {t.cancel || 'Cancel'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>

    {/* Custom Beverage Creator Modal */}
    <AnimatePresence>
      {isAddCustomDrinkModalOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="w-full max-w-md bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 shadow-2xl space-y-5 relative"
          >
            <div className="flex items-center justify-between pb-2 border-b border-[var(--card-border)]">
              <div className="flex items-center gap-2">
                <Droplets className="w-5 h-5 text-emerald-500" />
                <h3 className="text-lg font-black uppercase tracking-tight text-[var(--text)]">
                  {lang === 'ua' ? 'Створити власний напій' : 'Add Custom Beverage'}
                </h3>
              </div>
              <button
                onClick={() => setIsAddCustomDrinkModalOpen(false)}
                className="p-1 rounded-full text-zinc-400 hover:text-zinc-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">
                  {lang === 'ua' ? 'Назва напою:' : 'Drink Name:'}
                </label>
                <input
                  type="text"
                  value={newDrinkName}
                  onChange={(e) => setNewDrinkName(e.target.value)}
                  placeholder={lang === 'ua' ? 'напр. Домашній Лимонад' : 'e.g. Homemade Lemonade'}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)] text-xs font-bold outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">
                  {lang === 'ua' ? 'Бренд / Виробник:' : 'Brand / Maker:'}
                </label>
                <input
                  type="text"
                  value={newDrinkBrand}
                  onChange={(e) => setNewDrinkBrand(e.target.value)}
                  placeholder={lang === 'ua' ? 'напр. Власний рецепт' : 'e.g. Custom Recipe'}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)] text-xs font-bold outline-none focus:border-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">
                    {lang === 'ua' ? 'Об\'єм за замовчуванням (мл):' : 'Default Vol (ml):'}
                  </label>
                  <input
                    type="number"
                    value={newDrinkVolume || ''}
                    placeholder="330"
                    onChange={(e) => setNewDrinkVolume(e.target.value === '' ? 0 : Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)] text-xs font-bold outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">
                    {lang === 'ua' ? 'Калорії (ккал / 100мл):' : 'Calories (kcal / 100ml):'}
                  </label>
                  <input
                    type="number"
                    value={newDrinkCalories || ''}
                    placeholder="0"
                    onChange={(e) => setNewDrinkCalories(e.target.value === '' ? 0 : Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)] text-xs font-bold outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">
                    {lang === 'ua' ? 'Цукор (г / 100мл):' : 'Sugar (g / 100ml):'}
                  </label>
                  <input
                    type="number"
                    value={newDrinkSugar || ''}
                    placeholder="0"
                    onChange={(e) => setNewDrinkSugar(e.target.value === '' ? 0 : Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)] text-xs font-bold outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">
                    {lang === 'ua' ? 'Кофеїн (мг / 100мл):' : 'Caffeine (mg / 100ml):'}
                  </label>
                  <input
                    type="number"
                    value={newDrinkCaffeine || ''}
                    placeholder="0"
                    onChange={(e) => setNewDrinkCaffeine(e.target.value === '' ? 0 : Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)] text-xs font-bold outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <span className="text-xs font-bold text-amber-500">
                  {lang === 'ua' ? 'Це енергетичний напій (18+)?' : 'Is Energy Drink (18+)?'}
                </span>
                <input
                  type="checkbox"
                  checked={newDrinkIsEnergy}
                  onChange={(e) => setNewDrinkIsEnergy(e.target.checked)}
                  className="w-4 h-4 accent-amber-500"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsAddCustomDrinkModalOpen(false)}
                className="flex-1 py-3 rounded-2xl border border-[var(--card-border)] font-bold text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
              >
                {t.cancel || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!newDrinkName.trim()) {
                    toast.error(lang === 'ua' ? 'Введіть назву напою!' : 'Enter drink name!');
                    return;
                  }
                  const customItem: CustomDrinkOption = {
                    id: `custom_${Date.now()}`,
                    name: newDrinkName.trim(),
                    brand: newDrinkBrand.trim() || 'Custom',
                    defaultVolumeMl: newDrinkVolume || 330,
                    caloriesPer100ml: newDrinkCalories || 0,
                    sugarPer100ml: newDrinkSugar || 0,
                    caffeinePer100ml: newDrinkCaffeine || 0,
                    isEnergyDrink: newDrinkIsEnergy,
                    primaryColor: newDrinkIsEnergy ? '#f59e0b' : '#10b981'
                  };
                  addCustomDrink(customItem);
                  setIsAddCustomDrinkModalOpen(false);
                  setNewDrinkName('');
                  setNewDrinkBrand('');
                  toast.success(
                    lang === 'ua' ? 'Напій успішно додано!' : 'Custom beverage added!',
                    { icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" /> }
                  );
                }}
                className="flex-1 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-black text-xs uppercase tracking-widest shadow-lg shadow-emerald-500/20"
              >
                {lang === 'ua' ? 'Зберегти' : 'Save'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>

        <AnimatePresence>
          {view === 'add_meal_options' && (
            <motion.div
              className={cn(illumination ? "space-y-8" : "space-y-6", "px-2")}
            >
              <div className="flex items-center justify-between gap-4 overflow-visible">
                <h2 className={cn(
                  "text-3xl font-black uppercase tracking-tight",
                  theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
                )}>
                  {t.addMeal}
                </h2>
                <button 
                  onClick={() => setView('favorites')}
                  className="bg-[var(--card-bg)] p-3 rounded-full hover:bg-[var(--input-bg)] transition-colors group border border-[var(--card-border)] shadow-lg card-shadow"
                  title={lang === 'ua' ? "Улюблені" : "Favorites"}
                >
                  <Heart className="w-6 h-6 text-emerald-500" />
                </button>
              </div>

              <motion.div 
                className={cn(
                  "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[40px] p-4 backdrop-blur-sm card-shadow transition-all",
                  illumination ? "space-y-[21px] illumination-food" : "space-y-4"
                )}
              >
                <motion.button 
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedHistoryItem(null);
                    setResult(null);
                    setOriginalResult(null);
                    setImage(null);
                    setError(null);
                    setView('analyze');
                  }}
                  className={cn(
                    "w-full bg-[var(--card-bg)] border border-[var(--card-border)] p-6 rounded-[32px] flex items-center gap-6 hover:border-emerald-500/30 transition-all group card-shadow",
                    illumination && "illumination-food"
                  )}
                >
                  <div className="bg-emerald-500/10 p-5 rounded-2xl border border-emerald-500/20">
                    <Camera className="w-10 h-10 text-emerald-500" />
                  </div>
                  <div className="text-left flex-1">
                    <span className="block font-black text-xl uppercase tracking-tight text-[var(--text)] leading-tight">{t.analyze}</span>
                    <p className="text-sm text-zinc-500 font-medium mt-1">{t.snapMeal}</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-zinc-400 group-hover:text-emerald-500/50 transition-colors" />
                </motion.button>

                <motion.button 
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setView('chat')}
                  className={cn(
                    "w-full bg-[var(--card-bg)] border border-[var(--card-border)] p-6 rounded-[32px] flex items-center gap-6 hover:border-emerald-500/30 transition-all group card-shadow",
                    illumination && "illumination-food"
                  )}
                >
                  <div className="bg-emerald-500/10 p-5 rounded-2xl border border-emerald-500/20">
                    <MessageSquare className="w-10 h-10 text-emerald-500" />
                  </div>
                  <div className="text-left flex-1">
                    <span className="block font-black text-xl uppercase tracking-tight text-[var(--text)] leading-tight">{skin === 'anime' ? (lang === 'ua' ? "『ШІ НУТРІ-СЕНСЕЙ』" : "『AI NUTRI-SENSEI』") : t.chatWithAI}</span>
                    <p className="text-sm text-zinc-500 font-medium mt-1">{t.chatPrompt}</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-zinc-400 group-hover:text-emerald-500/50 transition-colors" />
                </motion.button>

                <motion.button 
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { setView('manual'); }}
                  className={cn(
                    "w-full bg-[var(--card-bg)] border border-[var(--card-border)] p-6 rounded-[32px] flex items-center gap-6 hover:border-emerald-500/30 transition-all group card-shadow",
                    illumination && "illumination-food"
                  )}
                >
                  <div className="bg-emerald-500/10 p-5 rounded-2xl border border-emerald-500/20">
                    <Utensils className="w-10 h-10 text-emerald-500" />
                  </div>
                  <div className="text-left flex-1">
                    <span className="block font-black text-xl uppercase tracking-tight text-[var(--text)] leading-tight">{skin === 'anime' ? (lang === 'ua' ? "『ЗАПИС БЕНТО ТА СТРАВ』" : "『BENTO & FEAST LOG』") : t.manualEntry}</span>
                    <p className="text-sm text-zinc-500 font-medium mt-1">{t.addMealManually}</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-zinc-400 group-hover:text-emerald-500/50 transition-colors" />
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {view === 'analyze' && (
            <motion.div
              className="space-y-4 relative"
            >
            {illumination && (
              <div className={cn(
                "absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-2xl h-96 opacity-20 blur-3xl rounded-full pointer-events-none -z-10 transition-colors duration-1000",
                selectedMealType === 'breakfast' && "bg-amber-500",
                selectedMealType === 'lunch' && "bg-blue-500",
                selectedMealType === 'dinner' && "bg-indigo-500",
                selectedMealType === 'snack' && "bg-rose-500"
              )} />
            )}
            <div className="flex items-center gap-4 px-2">
              <button 
                onClick={handleBack}
                className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <h2 className={cn(
                "text-2xl font-bold uppercase tracking-normal flex items-center gap-2 flex-grow",
                theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
              )}>
                {t.analyzeMeal}
                <Sparkles className="w-5 h-5 text-emerald-500" />
              </h2>
              {(image || result) && !isAnalyzing && (
                <button 
                  onClick={() => {
                    setImage(null);
                    setResult(null);
                    setOriginalResult(null);
                    setSelectedHistoryItem(null);
                    setEditingMeal(null);
                    setError(null);
                    setIsAnalyzing(false);
                  }}
                  className={cn(
                    "text-[10px] font-bold uppercase tracking-widest transition-all flex items-center gap-1.5 px-3 py-2 rounded-full border shadow-sm",
                    theme === 'light' 
                      ? "text-emerald-600 bg-white border-emerald-100 hover:bg-emerald-50 shadow-emerald-500/10" 
                      : "text-emerald-500 bg-emerald-500/10 border-emerald-500/20 hover:text-emerald-600"
                  )}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {t.reset || 'Wipe'}
                </button>
              )}
            </div>
            {/* Upload/Camera Section */}
            {!result && (
              <section className="space-y-4">
                {!image ? (
                  <div className="relative bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[32px] p-6 card-shadow overflow-hidden">
                    {/* Atmospheric Emerald Background */}
                    {illumination && (
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-50/30 to-emerald-100/60" />
                        <motion.div 
                          animate={{ y: [0, -20, 0], x: [0, 15, 0], scale: [1, 1.1, 1] }}
                          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                          className="absolute -bottom-32 -left-20 w-80 h-80 bg-emerald-300/20 rounded-full blur-3xl"
                        />
                        <motion.div 
                          animate={{ y: [0, 25, 0], x: [0, -20, 0], scale: [1, 1.15, 1] }}
                          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                          className="absolute -bottom-20 -right-20 w-96 h-96 bg-teal-300/20 rounded-full blur-3xl"
                        />
                        <motion.div 
                          animate={{ y: [0, -40, 0], opacity: [0.3, 0.6, 0.3] }}
                          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                          className="absolute top-1/4 left-1/3 w-40 h-40 bg-emerald-200/30 rounded-full blur-3xl"
                        />
                      </div>
                    )}
                    
                    <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button 
                        type="button"
                        onClick={() => startCamera()}
                        className="border border-[var(--card-border)] rounded-3xl p-6 flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)] backdrop-blur-md hover:border-emerald-500 transition-all cursor-pointer group card-shadow hover:shadow-xl w-full"
                      >
                        <div className={cn(
                          "p-3 rounded-full transition-colors border shadow-sm",
                          theme === 'light' ? "bg-white border-emerald-100 text-emerald-500 shadow-emerald-500/5" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-500"
                        )}>
                          <Camera className="w-6 h-6" />
                        </div>
                        <div className="text-center">
                          <p className="font-black text-xl uppercase tracking-tight text-[var(--text)] leading-tight">{t.startCamera}</p>
                        </div>
                      </button>

                      <button 
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="border border-[var(--card-border)] rounded-3xl p-6 flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)] backdrop-blur-md hover:border-emerald-500 transition-all cursor-pointer group card-shadow hover:shadow-xl w-full"
                      >
                        <div className={cn(
                          "p-3 rounded-full transition-colors border shadow-sm",
                          theme === 'light' ? "bg-white border-emerald-100 text-emerald-500 shadow-emerald-500/5" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-500"
                        )}>
                          <Upload className="w-6 h-6" />
                        </div>
                        <div className="text-center">
                          <p className="font-black text-xl uppercase tracking-tight text-[var(--text)] leading-tight">{t.uploadPhoto}</p>
                        </div>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={cn(
                    "relative rounded-[32px] overflow-hidden border card-shadow transition-all group",
                    theme === 'light' ? "bg-white border-zinc-200" : "bg-zinc-900 border-[var(--card-border)]"
                  )}>
                    <img src={image} alt="Meal" loading="lazy" decoding="async" className="w-full h-64 sm:h-80 object-cover" referrerPolicy="no-referrer" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-4">
                        <button 
                          onClick={() => fileInputRef.current?.click()}
                          className="bg-white text-zinc-900 px-6 py-3 rounded-2xl font-bold uppercase tracking-widest flex items-center justify-center gap-2 shadow-2xl text-[11px] hover:scale-105 transition-all"
                        >
                          <Upload className="w-4 h-4 text-emerald-500" /> {t.uploadPhoto}
                        </button>
                        <button 
                          onClick={() => startCamera()}
                          className="bg-emerald-500 text-white px-6 py-3 rounded-2xl font-bold uppercase tracking-widest flex items-center justify-center gap-2 shadow-2xl shadow-emerald-500/40 text-[11px] hover:scale-105 transition-all"
                        >
                          <Camera className="w-4 h-4" /> {t.startCamera}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleImageUpload} 
                  accept="image/*" 
                  className="hidden" 
                />

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-500 p-4 rounded-2xl flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <Info className="w-5 h-5 flex-shrink-0" />
                      <p className="text-sm font-bold uppercase tracking-widest">{error}</p>
                    </div>
                    {(String(error || '').toLowerCase().includes("timed out") || String(error || '').toLowerCase().includes("вичерпано")) && (
                      <div className="flex flex-col sm:flex-row gap-2 mt-1">
                        <button
                          onClick={async () => {
                            try {
                              if ('serviceWorker' in navigator) {
                                const regs = await navigator.serviceWorker.getRegistrations();
                                for (const r of regs) await r.unregister();
                              }
                              if ('caches' in window) {
                                const keys = await caches.keys();
                                for (const k of keys) await caches.delete(k);
                              }
                            } catch (e) {}
                            window.location.href = window.location.origin + '?v=' + Date.now();
                          }}
                          className="flex-1 text-[11px] bg-red-600 hover:bg-red-700 text-white px-3 py-2.5 rounded-xl font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-md cursor-pointer active:scale-95"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          <span>{lang === 'ua' ? 'Очистити PWA кеш і оновити' : 'Force Update PWA & Purge Cache'}</span>
                        </button>
                        {image && (
                          <button
                            onClick={() => {
                              try {
                                const fallbackData = generateAutonomousMealFallback({
                                  mealType: selectedMealType,
                                  goals,
                                  lang,
                                  vibe,
                                  gender: onboardingGender,
                                  ignoreMealTime: ignoreMealTimeLogging
                                });
                                if (fallbackData) {
                                  const enrichedData = { ...fallbackData, meal_type: selectedMealType, includeRecipe };
                                  playScanCompleteSound();
                                  setResult(enrichedData);
                                  setOriginalResult(safeClone(enrichedData));
                                  setView('analyze');
                                }
                              } catch (e) {}
                            }}
                            className="flex-1 text-[11px] bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2.5 rounded-xl font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all border border-zinc-700 cursor-pointer active:scale-95"
                          >
                            <Utensils className="w-3.5 h-3.5" />
                            <span>{lang === 'ua' ? 'Оцінити автономно' : 'Instant Autonomous Estimate'}</span>
                          </button>
                        )}
                      </div>
                    )}
                    {String(error || '').toLowerCase().includes("camera") && (
                      <div className="flex flex-col gap-2 mt-2">
                        <p className="text-[10px] text-red-400/80 normal-case font-medium">
                          Browsers often block camera access inside previews. Opening the app in a new tab usually fixes this.
                        </p>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => { setError(null); startCamera(); }}
                            className="flex-1 text-[10px] bg-red-500 text-white px-3 py-2 rounded-lg font-bold uppercase tracking-widest hover:bg-red-600 transition-colors"
                          >
                            Try Again
                          </button>
                          <button 
                            onClick={() => window.open(window.location.href, '_blank')}
                            className="flex-1 text-[10px] bg-zinc-800 text-white px-3 py-2 rounded-lg font-bold uppercase tracking-widest hover:bg-zinc-700 transition-colors border border-zinc-700"
                          >
                            Open in New Tab
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {image && !result && !isAnalyzing && (
                  <div className="space-y-6">
                    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 card-shadow space-y-4">
                      <div className="flex items-center justify-between px-1">
                        <h3 className="text-sm font-bold uppercase tracking-normal text-zinc-500">{t.mealType}</h3>
                        <button 
                          type="button"
                          onClick={() => setIncludeRecipe(prev => !prev)}
                          className={cn(
                            "px-3 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all cursor-pointer",
                            includeRecipe 
                              ? "bg-blue-500 text-white border-blue-500 shadow-md shadow-blue-500/20" 
                              : "bg-zinc-500/10 text-zinc-400 border-zinc-500/20 hover:text-blue-500 hover:border-blue-500/30"
                          )}
                        >
                          <BookOpen className="w-3 h-3" />
                          <span>{lang === 'ua' ? 'Рецепт' : 'Recipe'}</span>
                          {includeRecipe && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((type) => {
                          const colors = {
                            breakfast: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:bg-amber-500/5',
                            lunch: 'border-blue-500/40 bg-blue-500/10 text-blue-600 dark:bg-blue-500/5',
                            dinner: 'border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:bg-indigo-500/5',
                            snack: 'border-rose-500/40 bg-rose-500/10 text-rose-600 dark:bg-rose-500/5'
                          };
                          const activeColors = {
                            breakfast: 'bg-emerald-500 border-emerald-500 text-white shadow-xl shadow-emerald-500/20',
                            lunch: 'bg-emerald-500 border-emerald-500 text-white shadow-xl shadow-emerald-500/20',
                            dinner: 'bg-emerald-500 border-emerald-500 text-white shadow-xl shadow-emerald-500/20',
                            snack: 'bg-emerald-500 border-emerald-500 text-white shadow-xl shadow-emerald-500/20'
                          };
                          
                          return (
                            <button
                              key={type}
                              onClick={() => setSelectedMealType(type)}
                              className={cn(
                                "py-3 rounded-2xl border font-bold text-xs uppercase tracking-widest transition-all card-shadow",
                                selectedMealType === type ? activeColors[type] : colors[type]
                              )}
                            >
                              {t[type]}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {error ? (
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => handleAnalyze(true)}
                          className="bg-emerald-500 text-white py-4 px-2 rounded-2xl font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-emerald-600 transition-colors flex flex-col sm:flex-row items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 text-center"
                        >
                          <RefreshCw className="w-4 h-4 shrink-0" />
                          <span>{t.retry}</span>
                        </button>
                        <button
                          onClick={() => { setView('manual'); }}
                          className="bg-[var(--input-bg)] px-2 text-zinc-500 py-4 rounded-2xl font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-zinc-200 transition-colors flex flex-col sm:flex-row items-center justify-center gap-2 border border-[var(--card-border)] text-center"
                        >
                          <Utensils className="w-4 h-4 shrink-0" />
                          <span>{skin === 'anime' ? (lang === 'ua' ? "『ЗАПИС БЕНТО』" : "『BENTO LOG』") : t.manualEntry}</span>
                        </button>
                      </div>
                    ) : (
                        <button
                          onClick={() => handleAnalyze()}
                          disabled={isAnalyzing || isAnalyzingText}
                          className={cn(
                            "w-full py-4 rounded-3xl font-black text-xs uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 disabled:cursor-not-allowed",
                            theme === 'light'
                              ? "bg-emerald-500 text-white shadow-[0_8px_30px_rgb(16,185,129,0.3)] hover:shadow-[0_8px_30px_rgb(16,185,129,0.5)] active:scale-95"
                              : "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20 hover:bg-emerald-600",
                            (isAnalyzing || isAnalyzingText) && "btn-active-border"
                          )}
                        >
                          {(isAnalyzing || isAnalyzingText) ? (
                            <>
                              <span className="animate-pulse">{t.analyzeMeal}</span>
                              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin shrink-0" />
                            </>
                          ) : (
                            <>
                              {t.analyzeMeal}
                              <Sparkles className="w-4 h-4" />
                            </>
                          )}
                        </button>
                    )}
                  </div>
                )}
              </section>
            )}

            <AnimatePresence mode="wait">
              {isAnalyzing ? (
                <motion.div 
                  ref={scannerRef}
                  key="skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6 scroll-mt-16"
                >
                  <div className="relative rounded-3xl overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] card-shadow h-64 sm:h-80">
                    <img src={image!} alt="Analyzing" className="w-full h-full object-cover opacity-50 grayscale" referrerPolicy="no-referrer" />
                    
                    {skin === 'stalker' && (
                      <div className="absolute inset-0 flex items-center justify-center z-40 pointer-events-none">
                        <div className="w-32 h-32 border-2 border-[#4ade80] rounded-full relative">
                          <div className="absolute top-0 bottom-0 left-1/2 w-[2px] bg-[#4ade80] -translate-x-1/2"></div>
                          <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-[#4ade80] -translate-y-1/2"></div>
                          <div className="absolute top-1/2 left-1/2 w-8 h-8 border border-[#4ade80] rounded-full -translate-x-1/2 -translate-y-1/2 animate-ping"></div>
                        </div>
                      </div>
                    )}
                    
                    {/* Shimmer Effect */}
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12"
                      animate={{ left: ["-100%", "200%"] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    />

                    {/* Scanning Line */}
                    <motion.div 
                      className={cn("absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-emerald-400 to-transparent z-30", illumination ? "shadow-[0_0_17px_rgba(52,211,153,1)]" : "")}
                      animate={{ top: ["0%", "100%", "0%"] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    />

                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/40 backdrop-blur-[1px] overflow-hidden z-20">
                      {/* Random Data Points */}
                      {[...Array(12)].map((_, i) => (
                        <motion.div
                          key={i}
                          className={cn("absolute w-1 h-1 bg-emerald-400 rounded-full", illumination ? "shadow-[0_0_7px_rgba(52,211,153,0.8)]" : "")}
                          initial={{ 
                            top: `${Math.random() * 80 + 10}%`, 
                            left: `${Math.random() * 80 + 10}%`,
                            opacity: 0,
                            scale: 0
                          }}
                          animate={{ 
                            opacity: [0, 0.8, 0],
                            scale: [0, 1.2, 0],
                          }}
                          transition={{ 
                            duration: 2 + Math.random() * 2, 
                            repeat: Infinity, 
                            delay: Math.random() * 2 
                          }}
                        />
                      ))}

                      <div className="relative">
                        <motion.div 
                          animate={{ rotate: 360 }}
                          transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                          className="w-28 h-28 border border-dashed border-emerald-500/30 rounded-full"
                        />
                        <motion.div 
                          animate={{ rotate: -360 }}
                          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                          className="absolute inset-2 border border-dashed border-emerald-500/20 rounded-full"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <motion.div
                            animate={{ 
                              scale: [1, 1.1, 1],
                              filter: ["drop-shadow(0 0 0px rgba(16,185,129,0))", "drop-shadow(0 0 15px rgba(16,185,129,0.6))", "drop-shadow(0 0 0px rgba(16,185,129,0))"]
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            <Search className="w-10 h-10 text-emerald-500" />
                          </motion.div>
                          
                          {/* Pulsing Scanner Line */}
                          <motion.div 
                            animate={{ 
                              top: ['10%', '90%', '10%'],
                              opacity: [0, 1, 0]
                            }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute left-0 right-0 h-0.5 bg-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.8)] z-20"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Running Green Progress Line - positioned BELOW the photo scanning section */}
                  <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 card-shadow space-y-2.5">
                    <div className="flex justify-between items-center text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-0.5">
                      <span className="text-emerald-500 font-extrabold">{Math.round(analysisProgress)}%</span>
                      <span>{t.finalizingResults}</span>
                    </div>
                    <div className="h-2.5 w-full bg-[var(--input-bg)] rounded-full overflow-hidden border border-[var(--card-border)] relative">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${analysisProgress}%` }}
                        transition={{ type: "spring", stiffness: 50, damping: 15 }}
                        className="h-full bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.8)]"
                      />
                    </div>
                  </div>

                  <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 text-center space-y-6 card-shadow">

                    {/* Status Text Section */}
                    <div className="space-y-2">
                      <motion.p 
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="text-lg font-bold text-emerald-500 uppercase tracking-widest"
                      >
                        {skin === 'stalker' ? "[SCANNING FOR BIOLOGICAL CONTAMINANTS...]" : t.analyzingMeal}
                      </motion.p>
                      {analysisTimer > 10 && (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest animate-pulse"
                        >
                          {analysisTimer > 40 
                            ? t.patiencePlea
                            : t.almostThere}
                        </motion.p>
                      )}
                      <motion.p 
                        key={analysisStep}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-zinc-500 font-bold uppercase tracking-widest"
                      >
                        {[
                          t.scanningImage, 
                          t.noodling,
                          t.detectingFood, 
                          t.identifyingItems,
                          t.daydreaming,
                          t.estimatingPortions, 
                          t.checkingDatabases,
                          t.calculatingMacros, 
                          t.balancingMacros,
                          t.generatingInsights,
                          t.finalizingResults,
                          ...(analysisTimer > 30 ? [
                            t.tripleChecking,
                            t.wrappingUp
                          ] : [])
                        ][analysisStep]}
                      </motion.p>
                      {analysisTimer > 20 && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="pt-2"
                        >
                          <p className="text-[9px] text-zinc-400 font-medium italic">
                            {lang === 'ua' 
                              ? 'Це займає більше часу, ніж зазвичай. Перевіряємо деталі...' 
                              : 'Taking a bit longer than usual. Deep-scanning for accuracy...'}
                          </p>
                        </motion.div>
                      )}
                    </div>

                    {analysisTimer > 15 && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={cancelAnalysis}
                        className="w-full py-3 bg-[var(--input-bg)] hover:bg-zinc-200 rounded-xl text-xs font-bold uppercase tracking-widest text-zinc-500 transition-all border border-[var(--card-border)]"
                      >
                        {t.cancel}
                      </motion.button>
                    )}

                    {/* Live AI Food Match Certainty Panel */}
                    <div className={cn(
                      "p-4 rounded-xl border text-left space-y-3 transition-all",
                      theme === 'light' 
                        ? "bg-zinc-50/80 border-zinc-200" 
                        : "bg-zinc-900/60 border-[var(--card-border)]"
                    )}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-emerald-500 animate-pulse" />
                          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[var(--text)]">
                            {lang === 'ua' ? 'Точність ідентифікації ШІ' : 'AI Identification Certainty'}
                          </span>
                        </div>
                        <span className={cn(
                          "text-xs font-black tracking-tight",
                          liveConfidence > 85 ? "text-emerald-500" : liveConfidence > 50 ? "text-amber-500" : "text-zinc-500"
                        )}>
                          {liveConfidence.toFixed(1)}%
                        </span>
                      </div>

                      {/* Confidence Bar */}
                      <div className="h-3 w-full bg-[var(--input-bg)] rounded-full overflow-hidden border border-[var(--card-border)] relative">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${liveConfidence}%` }}
                          transition={{ type: "spring", stiffness: 70, damping: 15 }}
                          className={cn(
                            "h-full rounded-full transition-colors duration-300",
                            liveConfidence > 85 ? "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]" : 
                            liveConfidence > 50 ? "bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]" : 
                            "bg-zinc-500"
                          )}
                        />
                        {/* Interactive scanning indicator */}
                        <motion.div 
                          animate={{ left: ["0%", "100%", "0%"] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          className="absolute top-0 bottom-0 w-8 bg-gradient-to-r from-transparent via-white/20 to-transparent z-10"
                        />
                      </div>

                      <div className="flex justify-between items-center text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                        {/* Match Status label */}
                        <div className="flex items-center gap-1.5">
                          <span className={cn(
                            "inline-block w-1.5 h-1.5 rounded-full",
                            liveConfidence > 85 ? "bg-emerald-500 animate-ping" : 
                            liveConfidence > 50 ? "bg-amber-500 animate-pulse" : 
                            "bg-zinc-500"
                          )} />
                          <span>
                            {liveConfidenceStatus}
                          </span>
                        </div>
                        <span className="text-zinc-400">
                          {liveConfidence > 85 ? (lang === 'ua' ? 'Висока точність' : 'High certainty') :
                           liveConfidence > 50 ? (lang === 'ua' ? 'Калібрування' : 'Calibrating') :
                           (lang === 'ua' ? 'Сканування' : 'Scanning')}
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-center gap-2">
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                        <motion.div
                          key={i}
                          animate={{ 
                            scale: analysisStep === i ? [1, 1.5, 1] : 1,
                            backgroundColor: analysisStep === i ? '#10b981' : (analysisStep > i ? '#10b981' : '#27272a')
                          }}
                          transition={{ duration: 0.5 }}
                          className="w-2 h-2 rounded-full"
                        />
                      ))}
                    </div>
                  </div>

                  {/* Skeleton Result Below */}
                  <div className="pointer-events-none">
                    {renderSkeletonResult()}
                  </div>
                </motion.div>
              ) : result ? (
                <motion.div 
                  key="result" 
                >
                  {renderAnalysisResult(result)}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

        {view === 'progress' && renderProgressView()}

        {view === 'manual' && renderManualEntry()}
        {view === 'chat' && renderChatView()}

        {view === 'history' && (
          <motion.div 
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
            className={cn(illumination ? "space-y-8" : "space-y-6", "relative")}
          >
            {/* Sticky Top Section containing all title, tabs, search, sort, and tag filters */}
            <div className="sticky top-[73px] z-30 bg-[var(--card-bg)]/95 backdrop-blur-xl -mx-4 px-4 border-b border-[var(--card-border)] shadow-md pt-3 pb-3 transition-colors">
              <div className="space-y-4 mb-4">
                <div className="flex items-center justify-between gap-2 px-2">
                  <h2 className={cn(
                    "text-2xl font-bold uppercase tracking-normal whitespace-nowrap shrink-0",
                    theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
                  )}>{t.mealHistory}</h2>
                  <div className="flex-1 flex items-center justify-center min-w-0 px-1">
                    <button
                      onClick={() => setView('progress')}
                      className="py-1.5 px-3.5 bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 rounded-full hover:bg-emerald-500/20 transition-all flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest shadow-lg shadow-emerald-500/5 whitespace-nowrap"
                    >
                      <TrendingUp className="w-3.5 h-3.5" />
                      {lang.startsWith('ua') ? 'ПРОГРЕС' : 'PROGRESS'}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 shrink-0 text-right">
                    {historyTab === 'logs' 
                      ? `${filteredHistory.length + filteredWaterLogs.length} ${t.entries}`
                      : `${filteredScannedProducts.length} ${lang.startsWith('ua') ? 'прод.' : 'prod.'}`
                    }
                  </p>
                </div>

                {/* Tab Selector */}
                <div className="px-2 pb-1">
                  <div className="flex glass bg-white/60 dark:bg-zinc-900/60 backdrop-blur-xl p-1 rounded-2xl border border-zinc-200/60 dark:border-zinc-800/60 w-full shadow-sm">
                    <button
                      onClick={() => {
                        sounds.playPop();
                        setHistoryTab('logs');
                      }}
                      className={cn(
                        "flex-1 py-2.5 rounded-xl font-bold uppercase text-[11px] tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer",
                        historyTab === 'logs'
                          ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20"
                          : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/40 dark:hover:bg-zinc-800/40"
                      )}
                    >
                      <ClipboardList className="w-4 h-4" />
                      {lang.startsWith('ua') ? "Лог продуктів" : "Meal Logs"}
                    </button>
                    <button
                      onClick={() => {
                        sounds.playPop();
                        setHistoryTab('scanned');
                      }}
                      className={cn(
                        "flex-1 py-2.5 rounded-xl font-bold uppercase text-[11px] tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer",
                        historyTab === 'scanned'
                          ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20"
                          : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/40 dark:hover:bg-zinc-800/40"
                      )}
                    >
                      <Barcode className="w-4 h-4" />
                      {lang.startsWith('ua') ? "Скановані продукти" : "Scanned Products"}
                    </button>
                  </div>
                </div>
              </div>

              {historyTab === 'logs' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 px-2">
                    <button
                      type="button"
                      onClick={() => {
                        sounds.playPop();
                        setIsDateRangeSpoilerOpen(!isDateRangeSpoilerOpen);
                      }}
                      className={cn(
                        "p-3 rounded-2xl border transition-all flex items-center justify-center shrink-0 cursor-pointer card-shadow",
                        (historyStartDate || historyEndDate)
                          ? "bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20"
                          : isDateRangeSpoilerOpen
                            ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40"
                            : "bg-[var(--card-bg)] text-zinc-500 dark:text-zinc-400 border-[var(--card-border)] hover:text-emerald-500 hover:border-emerald-500/40"
                      )}
                      title={lang.startsWith('ua') ? "Фільтр дат" : "Date range filter"}
                    >
                      <div className="relative flex items-center justify-center">
                        <Calendar className="w-4 h-4" />
                        {(historyStartDate || historyEndDate) && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full ring-2 ring-emerald-600 animate-pulse" />
                        )}
                      </div>
                    </button>

                    <div className="relative flex-1">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input 
                        type="text"
                        placeholder={t.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => setIsHistorySearchFocused(true)}
                        onBlur={() => setTimeout(() => setIsHistorySearchFocused(false), 200)}
                        className={cn(
                          "w-full bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all card-shadow text-sm",
                          illumination && "illumination-food"
                        )}
                      />
                      {isHistorySearchFocused && activeHistorySuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 mt-1 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-xl z-50 overflow-hidden divide-y divide-[var(--card-border)] max-h-60 overflow-y-auto">
                          {activeHistorySuggestions.map((item, idx) => (
                            <div
                              key={idx}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setSearchTerm(item.text);
                                setIsHistorySearchFocused(false);
                                sounds.playPop();
                              }}
                              className="flex items-center justify-between px-4 py-2.5 text-xs font-medium cursor-pointer transition-colors hover:bg-[var(--input-bg)] text-zinc-300 hover:text-white"
                            >
                              <div className="flex items-center gap-2">
                                {item.type === 'food' ? (
                                  <Utensils className="w-3.5 h-3.5 text-emerald-500" />
                                ) : (
                                  <ClipboardList className="w-3.5 h-3.5 text-blue-500" />
                                )}
                                <span>{item.text}</span>
                              </div>
                              <span className="text-[10px] text-zinc-500">
                                {item.frequency}x
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {isDateRangeSpoilerOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-3 sm:p-4 mx-2 card-shadow space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-emerald-500" />
                            <span className="text-xs font-bold uppercase tracking-wider text-[var(--text)]">
                              {lang.startsWith('ua') ? "Фільтр дат" : "Date Range"}
                            </span>
                          </div>
                          {(historyStartDate || historyEndDate) ? (
                            <button
                              type="button"
                              onClick={() => {
                                sounds.playPop();
                                setHistoryStartDate('');
                                setHistoryEndDate('');
                                setHistoryVisibleDaysCount(3);
                              }}
                              className="text-[10px] font-bold text-rose-500 hover:text-rose-400 bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 rounded-lg transition-all uppercase tracking-wider cursor-pointer"
                            >
                              {lang.startsWith('ua') ? "Скинути" : "Reset"}
                            </button>
                          ) : (
                            <span className="text-[10px] text-zinc-500">
                              {lang.startsWith('ua') ? "Останні 3 дні" : "Last 3 days"}
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <label className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 block">
                              {lang.startsWith('ua') ? "Від" : "From"}
                            </label>
                            <input
                              type="date"
                              value={historyStartDate}
                              onChange={(e) => {
                                sounds.playPop();
                                setHistoryStartDate(e.target.value);
                                setHistoryVisibleDaysCount(3);
                              }}
                              className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl px-2.5 py-1.5 text-xs text-[var(--text)] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 block">
                              {lang.startsWith('ua') ? "До" : "To"}
                            </label>
                            <input
                              type="date"
                              value={historyEndDate}
                              onChange={(e) => {
                                sounds.playPop();
                                setHistoryEndDate(e.target.value);
                                setHistoryVisibleDaysCount(3);
                              }}
                              className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl px-2.5 py-1.5 text-xs text-[var(--text)] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                          <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 mr-1">
                            {lang.startsWith('ua') ? "Швидко:" : "Quick:"}
                          </span>
                          {[
                            { label: lang.startsWith('ua') ? '3 дні' : '3 Days', days: 3 },
                            { label: lang.startsWith('ua') ? '7 днів' : '7 Days', days: 7 },
                            { label: lang.startsWith('ua') ? '30 днів' : '30 Days', days: 30 }
                          ].map(preset => {
                            const getLocalYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                            const todayStr = getLocalYMD(new Date());
                            const pastDate = new Date();
                            pastDate.setDate(pastDate.getDate() - (preset.days - 1));
                            const pastStr = getLocalYMD(pastDate);

                            const isActive = historyStartDate === pastStr && historyEndDate === todayStr;

                            return (
                              <button
                                key={preset.days}
                                type="button"
                                onClick={() => {
                                  sounds.playPop();
                                  setHistoryStartDate(pastStr);
                                  setHistoryEndDate(todayStr);
                                  setHistoryVisibleDaysCount(3);
                                }}
                                className={cn(
                                  "px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border cursor-pointer",
                                  isActive
                                    ? "bg-emerald-500 text-white border-emerald-500 shadow-sm"
                                    : "bg-[var(--input-bg)]/50 border-[var(--card-border)] text-zinc-400 hover:text-[var(--text)] hover:border-emerald-500/40"
                                )}
                              >
                                {preset.label}
                              </button>
                            );
                          })}

                          {(!historyStartDate && !historyEndDate) && (
                            <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-white border border-emerald-500 shadow-sm">
                              {lang.startsWith('ua') ? 'Всі дати' : 'All Dates'}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mt-3 mb-1">
                    <div className="flex items-center gap-2 px-2 overflow-x-auto no-scrollbar py-1.5 flex-nowrap">
                      {[
                        { key: 'date', label: t.sortDate },
                        { key: 'calories', label: t.sortCalories },
                        { key: 'name', label: t.sortName },
                        { key: 'tag', label: t.sortTag }
                      ].map((option) => (
                        <button
                          key={option.key}
                          onClick={() => {
                            if (sortKey === option.key) {
                              setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortKey(option.key as any);
                              setSortDirection('desc');
                            }
                          }}
                          className={cn(
                            "flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap shrink-0 cursor-pointer",
                            sortKey === option.key 
                              ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/20 border border-emerald-500" 
                              : "bg-transparent text-zinc-700 dark:text-zinc-300 border border-zinc-200/80 dark:border-zinc-800/80 hover:border-emerald-500/40 hover:text-emerald-500"
                          )}
                        >
                          {option.label}
                          {sortKey === option.key && (
                            sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                      ))}

                      {sortKey === 'tag' && (
                        <div className="flex bg-transparent p-0.5 rounded-full shrink-0 border border-zinc-200/80 dark:border-zinc-800/80">
                          <button
                            onClick={() => setTagSortMode('mealType')}
                            className={cn(
                              "px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest transition-all shrink-0 cursor-pointer",
                              tagSortMode === 'mealType' 
                                ? "bg-emerald-500 text-white shadow-sm" 
                                : "bg-transparent text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                            )}
                          >
                            {lang.startsWith('en') ? 'Meal Type' : 'Тип страви'}
                          </button>
                          <button
                            onClick={() => setTagSortMode('category')}
                            className={cn(
                              "px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest transition-all shrink-0 cursor-pointer",
                              tagSortMode === 'category' 
                                ? "bg-emerald-500 text-white shadow-sm" 
                                : "bg-transparent text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                            )}
                          >
                            {lang.startsWith('en') ? 'Category' : 'Категорія'}
                          </button>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          sounds.playPop();
                          setShowFavoritesOnly(prev => !prev);
                        }}
                        className={cn(
                          "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap border shrink-0 cursor-pointer",
                          showFavoritesOnly 
                            ? "bg-amber-500 text-white border-amber-500 shadow-md shadow-amber-500/20" 
                            : "bg-transparent text-zinc-700 dark:text-zinc-300 border border-zinc-200/80 dark:border-zinc-800/80 hover:text-amber-500 hover:border-amber-500/40"
                        )}
                      >
                        <Star className={cn("w-3 h-3", showFavoritesOnly && "fill-white text-white")} />
                        {lang.startsWith('ua') ? 'Улюблені' : 'Favorites'}
                      </button>
                      {onscreenMealItems.length > 0 && (
                        <button
                          onClick={toggleExpandAllMealItems}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap bg-transparent text-zinc-700 dark:text-zinc-300 border border-zinc-200/80 dark:border-zinc-800/80 hover:border-emerald-500/40 hover:text-emerald-500 shrink-0 cursor-pointer"
                        >
                          {areAllMealItemsExpanded ? (
                            <ChevronUp className="w-3.5 h-3.5 text-emerald-500" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5 text-emerald-500" />
                          )}
                          <span className="hidden sm:inline">
                            {areAllMealItemsExpanded ? (
                              lang.startsWith('ua') ? 'ЗГОРНУТИ ВСЕ' : 'COLLAPSE ALL'
                            ) : (
                              lang.startsWith('ua') ? 'РОЗГОРНУТИ ВСЕ' : 'EXPAND ALL'
                            )}
                          </span>
                          <span className="inline sm:hidden">
                            {lang.startsWith('ua') ? 'Все' : 'All'}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative px-2">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input 
                    type="text"
                    placeholder={lang.startsWith('ua') ? "Шукати за назвою чи штрих-кодом..." : "Search by product name or barcode..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={cn(
                      "w-full bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all card-shadow text-sm",
                      illumination && "illumination-food"
                    )}
                  />
                </div>
              )}
            </div>

            {/* List entries (no sticky) */}
            <div className="pt-0">
              {isHistoryLoading ? (
                renderHistorySkeleton()
              ) : historyTab === 'logs' ? (
                history.length === 0 ? (
                  <div className={cn(
                    "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-12 text-center space-y-4 card-shadow mx-2 transition-all mt-3",
                    illumination && "illumination-food"
                  )}>
                    <div className="bg-[var(--card-bg)] w-16 h-16 rounded-full flex items-center justify-center mx-auto border border-[var(--card-border)]">
                      <HistoryIcon className="w-8 h-8 text-zinc-500" />
                    </div>
                    <div>
                      <p className="text-lg font-medium">{t.noHistory}</p>
                      <p className="text-sm text-zinc-500">{t.historyAppear}</p>
                    </div>
                    <button 
                      onClick={() => setView('add_meal_options')}
                      className="text-emerald-500 font-bold text-sm hover:text-emerald-400 transition-colors uppercase tracking-widest"
                    >
                      {t.addMeal}
                    </button>
                  </div>
                ) : filteredHistory.length === 0 ? (
                  <div className={cn(
                    "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-12 text-center card-shadow mx-2 transition-all mt-3",
                    illumination && "illumination-food"
                  )}>
                    <p className="text-zinc-500">No results found for "{searchTerm}"</p>
                  </div>
                ) : (
                  <motion.div 
                    key={`history-list-${historyTab}-${searchTerm}-${sortKey}-${sortDirection}-${historyStartDate}-${historyEndDate}-${historyVisibleDaysCount}`}
                    initial="hidden"
                    animate="show"
                    variants={{
                      hidden: { opacity: 0 },
                      show: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.07
                        }
                      }
                    }}
                    className="space-y-6 px-2 mt-3"
                  >
                    {(sortKey === 'date' ? groupedHistory.slice(0, historyVisibleDaysCount) : groupedHistory).map(([date, group]) => {
                      const dayTimestamp = group.items[0]?.timestamp;
                      const dayDate = dayTimestamp ? new Date(dayTimestamp) : new Date();

                      return (
                      <motion.div 
                        key={date} 
                        initial="hidden"
                        animate="show"
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          show: { 
                            opacity: 1, 
                            y: 0,
                            transition: {
                              type: "spring",
                              stiffness: 100,
                              damping: 15,
                              staggerChildren: 0.05
                            }
                          }
                        }}
                        className="space-y-4"
                      >
                        <div 
                          onClick={() => {
                            if (sortKey === 'date' && dayTimestamp) {
                              sounds.playPop();
                              triggerHaptic('SELECTION');
                              setSelectedDate(dayDate);
                              setView('home');
                            }
                          }}
                          className={cn(
                            "flex flex-col gap-2 pb-2 mb-[14px] rounded-2xl p-2.5 transition-all select-none",
                            sortKey === 'date' && "cursor-pointer hover:bg-emerald-500/5 active:scale-[0.99] group border border-transparent hover:border-emerald-500/20",
                            illumination && "drop-shadow-[0_0_13px_rgba(16,185,129,0.35)]"
                          )}
                          title={sortKey === 'date' ? (lang.startsWith('ua') ? "Натисніть, щоб відкрити цей день" : "Click to view this day's history") : undefined}
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="font-bold text-[var(--text)] flex items-center gap-2 group-hover:text-emerald-500 transition-colors">
                              <Calendar className="w-4 h-4 text-emerald-500" />
                              <span>{date}</span>
                              {sortKey === 'date' && (
                                <ChevronRight className="w-3.5 h-3.5 text-zinc-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                              )}
                            </h3>
                            {sortKey === 'date' && (
                              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-500/70 group-hover:text-emerald-500 transition-colors flex items-center gap-1">
                                {lang.startsWith('ua') ? "Відкрити день →" : "View Day →"}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 text-[10px] sm:text-xs font-bold uppercase tracking-normal">
                            <span className="text-emerald-500">{Math.round(group.totals.calories)} {t.kcal}</span>
                            <span className="text-blue-500">{group.totals.protein.toFixed(2)}{t.proteinShort}</span>
                            <span className="text-red-500">{group.totals.carbs.toFixed(2)}{t.carbsShort}</span>
                            <span className="text-amber-500">{group.totals.fat.toFixed(2)}{t.fatShort}</span>
                            <span className="text-sky-500">{Math.round(group.totals.water)} ml</span>
                          </div>
                        </div>
                        <div className={cn(illumination ? "space-y-[25px] sm:space-y-5" : "space-y-5")}>
                          <AnimatePresence mode="popLayout">
                            {group.items.map((item, index) => (
                              <motion.div 
                                key={item.id} 
                                layout
                                variants={{
                                  hidden: { opacity: 0, scale: 0.95, y: 15 },
                                  show: { 
                                    opacity: 1, 
                                    scale: 1,
                                    y: 0,
                                    x: 0,
                                    transition: {
                                      type: "spring",
                                      stiffness: 350,
                                      damping: 25
                                    }
                                  }
                                }}
                                animate={deletingIds.has(item.id) ? { 
                                  x: 350, 
                                  opacity: 0, 
                                  scale: 0.9, 
                                  height: 0, 
                                  marginTop: 0, 
                                  marginBottom: 0,
                                  paddingTop: 0,
                                  paddingBottom: 0
                                } : "show"}
                                exit={{ 
                                  x: 350, 
                                  opacity: 0, 
                                  scale: 0.9, 
                                  height: 0, 
                                  marginTop: 0, 
                                  marginBottom: 0,
                                  paddingTop: 0,
                                  paddingBottom: 0,
                                  transition: { 
                                    duration: 0.3,
                                    ease: [0.32, 0, 0.67, 0]
                                  } 
                                }}
                                transition={{
                                  duration: 0.3,
                                  ease: [0.32, 0, 0.67, 0],
                                  layout: { type: "spring", stiffness: 350, damping: 25 }
                                }}
                                className="relative overflow-hidden rounded-2xl"
                              >
                                <div className="absolute inset-1 bg-red-500/90 flex items-center justify-end px-6 rounded-xl">
                                  <Trash2 className="w-6 h-6 text-white" />
                                </div>
                                <motion.div
                                  drag={isSelectionMode ? false : "x"}
                                  dragDirectionLock
                                  dragConstraints={{ left: 0, right: 0 }}
                                  dragElastic={{ left: 0.5, right: 0 }}
                                  onDragEnd={(e, info) => {
                                    if (info.offset.x < -100) {
                                      setDeletingIds(prev => new Set(prev).add(item.id));
                                      deleteHistoryItem(item.id);
                                    }
                                  }}
                                  onClick={() => {
                                    if (deletingIds.has(item.id)) return;
                                    if (!isLongPressingMeal) {
                                      if (isSelectionMode) {
                                        sounds.playPop();
                                        setSelectedMealIds(prev =>
                                          prev.includes(item.id)
                                            ? prev.filter(id => id !== item.id)
                                            : [...prev, item.id]
                                        );
                                      } else {
                                        setSelectedHistoryItem(item);
                                        setView('details');
                                      }
                                    }
                                  }}
                                  onTouchStart={(e) => handleMealTouchStart(e, item.id)}
                                  onTouchEnd={handleMealTouchEnd}
                                  onTouchMove={handleMealTouchMove}
                                  onMouseDown={(e) => handleMealTouchStart(e, item.id)}
                                  onMouseUp={handleMealTouchEnd}
                                  onMouseLeave={handleMealTouchMove}
                                  className={cn(
                                    "relative bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-3.5 sm:p-4 active:scale-[0.99] touch-manipulation flex flex-col items-stretch cursor-pointer hover:border-emerald-500 transition-all card-shadow group select-none min-h-[52px]",
                                    isSelectionMode && selectedMealIds.includes(item.id) && "border-emerald-500 bg-emerald-500/5",
                                    illumination && getIlluminationClass(item),
                                    deletingIds.has(item.id) && "opacity-50 grayscale pointer-events-none"
                                  )}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4 w-full">
                                    {isSelectionMode && (
                                      <div className="flex-shrink-0 flex items-center justify-center -mr-1">
                                        {selectedMealIds.includes(item.id) ? (
                                          <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white shadow-md shadow-emerald-500/10">
                                            <CheckCircle2 className="w-4 h-4" />
                                          </div>
                                        ) : (
                                          <div className="w-5 h-5 rounded-full border border-zinc-400 dark:border-zinc-600 bg-transparent" />
                                        )}
                                      </div>
                                    )}
                                    <motion.div 
                                      initial={{ scale: 0.85, opacity: 0.6 }}
                                      animate={{ scale: 1, opacity: 1 }}
                                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                      className={cn("w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center border border-[var(--card-border)]", item.type === 'meal' ? "bg-[var(--card-bg)]" : (item.beverage_type === 'coffee' ? "bg-amber-500/10" : item.beverage_type === 'tea' ? "bg-emerald-500/10" : "bg-blue-500/10"))}
                                    >
                                      {item.type === 'meal' ? (
                                        item.image ? (
                                          <motion.img 
                                            initial={{ scale: 1.12, opacity: 0.7 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ duration: 0.35, ease: "easeOut" }}
                                            src={item.image} 
                                            alt={item.meal_name} 
                                            loading="lazy" 
                                            decoding="async" 
                                            className="w-full h-full object-cover" 
                                          />
                                        ) : (
                                          <motion.div initial={{ scale: 0.6, rotate: -10 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 300, damping: 18 }}>
                                            <Utensils className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-500" />
                                          </motion.div>
                                        )
                                      ) : (
                                        <motion.div initial={{ scale: 0.6, rotate: -12 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 300, damping: 18 }}>
                                          {item.beverage_type === 'coffee' ? <Coffee className="w-6 h-6 text-amber-500" /> :
                                           item.beverage_type === 'tea' ? <Leaf className="w-6 h-6 text-emerald-500" /> :
                                           <Droplets className="w-6 h-6 text-blue-500" />}
                                        </motion.div>
                                      )}
                                    </motion.div>
                                    
                                    <div className="flex-grow min-w-0">
                                      <div className="flex items-start justify-between mb-1 gap-3 w-full">
                                        <div className="min-w-0 flex-grow">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="font-bold text-base sm:text-lg leading-tight text-[var(--text)] line-clamp-2">
                                              {item.type === 'meal' ? item.meal_name : `+ ${item.beverage_type === 'coffee' ? (lang === 'ua' ? 'Кава' : 'Coffee') : item.beverage_type === 'tea' ? (lang === 'ua' ? 'Чай' : 'Tea') : (lang === 'ua' ? 'Вода' : 'Water')}`}
                                            </h3>
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                              {item.type === 'meal' && Array.from(new Set(item.foods?.map(f => f.category?.toLowerCase()).filter(Boolean) || []))?.map((cat, idx) => (
                                                <motion.span 
                                                  key={idx} 
                                                  className="text-[10px] sm:text-xs inline-block" 
                                                  title={String(cat)}
                                                  initial={{ scale: 0.3, y: 8 }}
                                                  animate={{ scale: 1, y: 0 }}
                                                  transition={{ 
                                                    type: "spring",
                                                    stiffness: 220,
                                                    damping: 12,
                                                    delay: idx * 0.08
                                                  }}
                                                  whileHover={{ scale: 1.3, rotate: 12 }}
                                                >
                                                  {FOOD_CATEGORIES.find(c => c.id === cat)?.icon || '🍴'}
                                                </motion.span>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                        
                                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0 text-right">
                                          {item.type === 'meal' ? (
                                            <>
                                              <div className="flex flex-col items-end gap-1">
                                                {item.meal_type ? (
                                                  <span className={cn(
                                                    "text-[7px] sm:text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0",
                                                    item.meal_type === 'breakfast' && "bg-amber-500/10 text-amber-500 border-amber-500/20",
                                                    item.meal_type === 'lunch' && "bg-blue-500/10 text-blue-500 border-blue-500/20",
                                                    item.meal_type === 'dinner' && "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
                                                    item.meal_type === 'snack' && "bg-rose-500/10 text-rose-500 border-rose-500/20"
                                                  )}>
                                                    {t[item.meal_type as keyof typeof t] || item.meal_type}
                                                  </span>
                                                ) : (
                                                  item.tags && item.tags.length > 0 && (
                                                    (Array.from(new Set(item.tags)) as string[])
                                                      .filter(tag => tag !== 'water')
                                                      .map(tag => (
                                                        <span key={tag} className={cn(
                                                          "text-[7px] sm:text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0",
                                                          tag === 'breakfast' && "bg-amber-500/10 text-amber-500 border-amber-500/20",
                                                          tag === 'lunch' && "bg-blue-500/10 text-blue-500 border-blue-500/20",
                                                          tag === 'dinner' && "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
                                                          tag === 'snack' && "bg-rose-500/10 text-rose-500 border-rose-500/20"
                                                        )}>
                                                          {t[tag as keyof typeof t] || tag}
                                                        </span>
                                                      ))
                                                  )
                                                )}

                                                {(() => {
                                                  const badge = getPrimaryMacroBadge(item.total_nutrition);
                                                  return badge ? (
                                                    <motion.span 
                                                      initial={{ opacity: 0, scale: 0.85, y: 2 }}
                                                      animate={{ opacity: 1, scale: 1, y: 0 }}
                                                      transition={{ duration: 0.35, ease: "easeOut" }}
                                                      className={cn("text-[7px] sm:text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0", badge.class)}
                                                    >
                                                      {badge.label}
                                                    </motion.span>
                                                  ) : null;
                                                })()}
                                              </div>

                                              <div className="flex items-center gap-1.5">
                                                <span className="text-emerald-500 font-bold whitespace-nowrap text-xs sm:text-base flex-shrink-0">
                                                  {Math.round(item.total_nutrition?.calories_kcal || 0)} {t.kcal}
                                                </span>
                                              </div>
                                            </>
                                          ) : (
                                            <div className="flex items-center gap-2">
                                              <span className="text-blue-500 font-bold whitespace-nowrap text-xs sm:text-base">+{item.amount_ml} ml</span>
                                              <span className="text-[7px] sm:text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border flex-shrink-0 bg-sky-500/10 text-sky-500 border-sky-500/20">
                                                {t.water || 'Water'}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      <div className="flex items-center justify-between gap-2 text-[10px] sm:text-xs text-zinc-500 mb-1 sm:mb-2 w-full min-w-0">
                                        <div className="flex items-center gap-x-2 sm:gap-x-3 flex-nowrap overflow-x-auto no-scrollbar shrink-0">
                                          <div className="flex items-center gap-1 shrink-0 whitespace-nowrap">
                                            <Calendar className="w-3 h-3 text-emerald-500/80 shrink-0" />
                                            <span>{formatDateTime(new Date(item.timestamp), dateFormat, timeFormat, lang, true)}</span>
                                          </div>
                                          {item.type === 'meal' && (
                                            <div className="flex items-center gap-2 shrink-0 whitespace-nowrap">
                                              <span className="flex items-center gap-1">
                                                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-blue-500 shrink-0" />
                                                {(item.total_nutrition?.protein_g || 0).toFixed(2)}{t.proteinShort}
                                              </span>
                                              <span className="flex items-center gap-1">
                                                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-red-500 shrink-0" />
                                                {(item.total_nutrition?.carbs_g || 0).toFixed(2)}{t.carbsShort}
                                              </span>
                                              <span className="flex items-center gap-1">
                                                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-amber-500 shrink-0" />
                                                {(item.total_nutrition?.fat_g || 0).toFixed(2)}{t.fatShort}
                                              </span>
                                            </div>
                                          )}
                                        </div>

                                        {item.type === 'meal' && (
                                          <button 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              sounds.playPop();
                                              setExpandedMealLogIds(prev => 
                                                prev.includes(item.id) 
                                                  ? prev.filter(id => id !== item.id) 
                                                  : [...prev, item.id]
                                              );
                                            }}
                                            className="p-2 -mr-1 -my-1 text-zinc-500 hover:text-emerald-500 active:scale-110 transition-all flex-shrink-0 touch-manipulation min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg hover:bg-zinc-500/10"
                                            title={lang === 'ua' ? (expandedMealLogIds.includes(item.id) ? "Згорнути" : "Розгорнути") : (expandedMealLogIds.includes(item.id) ? "Collapse" : "Expand")}
                                          >
                                            <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", expandedMealLogIds.includes(item.id) && "rotate-180")} />
                                          </button>
                                        )}
                                      </div>
                                      </div>
                                    </div>

                                  {/* Expanded Food Items List */}
                                  <AnimatePresence>
                                    {expandedMealLogIds.includes(item.id) && item.type === 'meal' && item.foods && item.foods.length > 0 && (
                                      <motion.div 
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mt-4 pt-3 border-t border-[var(--card-border)]/50 space-y-2 w-full cursor-default"
                                        onClick={(e) => e.stopPropagation()} // don't navigate to details when clicking items
                                      >
                                        <div className="flex items-center justify-between px-1">
                                          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                            {lang === 'ua' ? 'Продукти у страві:' : 'Food Items:'}
                                          </p>

                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              sounds.playPop();
                                              copyToLog(item);
                                            }}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white border border-emerald-500/20 transition-all cursor-pointer shadow-xs active:scale-95"
                                            title={lang === 'ua' ? "Додати цю страву в журнал" : "Add this meal to log"}
                                          >
                                            <Plus className="w-3.5 h-3.5" />
                                            <span>{lang === 'ua' ? 'Додати страву' : 'Add meal'}</span>
                                          </button>
                                        </div>
                                        <div className="space-y-1.5">
                                          {item.foods.map((food, fIdx) => (
                                            <motion.div 
                                              key={fIdx} 
                                              initial={{ opacity: 0, x: -8, scale: 0.98 }}
                                              animate={{ opacity: 1, x: 0, scale: 1 }}
                                              transition={{ duration: 0.22, delay: fIdx * 0.04, ease: "easeOut" }}
                                              className="flex items-center justify-between p-2.5 bg-[var(--input-bg)] hover:bg-emerald-500/5 rounded-xl border border-[var(--card-border)]/50 transition-all"
                                            >
                                              <div className="min-w-0 flex-1 pr-2">
                                                <p className="font-semibold text-sm text-[var(--text)] truncate">
                                                  {food.name}
                                                </p>
                                                <p className="text-xs text-zinc-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                                                  <span className="font-medium text-zinc-600 dark:text-zinc-400">{food.portion || `${food.amount_g || 100} g`}</span>
                                                  {food.protein_g > 0 && (
                                                    <span className="flex items-center gap-0.5">
                                                      <span className="w-1 h-1 rounded-full bg-blue-500" />
                                                      {food.protein_g.toFixed(1)}g P
                                                    </span>
                                                  )}
                                                  {food.carbs_g > 0 && (
                                                    <span className="flex items-center gap-0.5">
                                                      <span className="w-1 h-1 rounded-full bg-red-500" />
                                                      {food.carbs_g.toFixed(1)}g C
                                                    </span>
                                                  )}
                                                  {food.fat_g > 0 && (
                                                    <span className="flex items-center gap-0.5">
                                                      <span className="w-1 h-1 rounded-full bg-amber-500" />
                                                      {food.fat_g.toFixed(1)}g F
                                                    </span>
                                                  )}
                                                </p>
                                              </div>
                                              <div className="text-right flex-shrink-0">
                                                <span className="text-sm font-bold text-emerald-500">
                                                  {Math.round(food.calories_kcal)} {t.kcal}
                                                </span>
                                              </div>
                                            </motion.div>
                                          ))}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                            </motion.div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                );
              })}
                    
                    {(groupedHistory.length > (sortKey === 'date' ? historyVisibleDaysCount : 0) || hasMoreHistory) && (
                      <div className="flex justify-center pt-4 pb-8">
                        <button
                          id="load-more-history-btn"
                          onClick={() => {
                            sounds.playPop();
                            const nextCount = historyVisibleDaysCount + 3;
                            setHistoryVisibleDaysCount(nextCount);
                            if (hasMoreHistory && nextCount >= groupedHistory.length) {
                              loadMoreHistory();
                            }
                          }}
                          disabled={loadingMoreHistory}
                          className="py-3 px-6 bg-[var(--card-bg)] text-emerald-500 border border-[var(--card-border)] rounded-full hover:bg-emerald-500/10 hover:border-emerald-500/40 transition-all font-bold text-xs uppercase tracking-widest shadow-lg flex items-center gap-2 disabled:opacity-50 active:scale-95"
                        >
                          {loadingMoreHistory ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                              {lang.startsWith('ua') ? 'ЗАВАНТАЖЕННЯ...' : 'LOADING...'}
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 text-emerald-500" />
                              {lang.startsWith('ua') ? 'ПОКАЗАТИ ЩЕ 3 ДНІ' : 'LOAD 3 MORE DAYS'}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </motion.div>
                )
              ) : (
                filteredScannedProducts.length === 0 ? (
                  <div className={cn(
                    "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-12 text-center space-y-4 card-shadow mx-2 transition-all mt-4",
                    illumination && "illumination-food"
                  )}>
                    <div className="bg-[var(--card-bg)] w-16 h-16 rounded-full flex items-center justify-center mx-auto border border-[var(--card-border)]">
                      <Barcode className="w-8 h-8 text-zinc-500 animate-pulse" />
                    </div>
                    <div>
                      <p className="text-lg font-medium">{lang.startsWith('ua') ? "Немає сканованих продуктів" : "No Scanned Products"}</p>
                      <p className="text-sm text-zinc-500">
                        {searchTerm 
                          ? (lang.startsWith('ua') ? "Нічого не знайдено за цим запитом" : "No matches found for your search")
                          : (lang.startsWith('ua') ? "Тут будуть збережені всі продукти, які ви скануєте за допомогою штрих-коду" : "Products you scan using the barcode camera will be stored here")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <motion.div 
                    initial="hidden"
                    animate="show"
                    variants={{
                      hidden: {},
                      show: {
                        transition: {
                          staggerChildren: 0.08
                        }
                      }
                    }}
                    className="space-y-5 px-2 mt-4"
                  >
                    <AnimatePresence mode="popLayout">
                      {filteredScannedProducts.map((product, index) => (
                        <motion.div
                          key={product.barcode}
                          layout
                          variants={{
                            hidden: { opacity: 0, scale: 0.95, y: 15 },
                            show: { 
                              opacity: 1, 
                              scale: 1,
                              y: 0,
                              transition: {
                                type: "spring",
                                stiffness: 350,
                                damping: 25
                              }
                            }
                          }}
                          exit={{ 
                            opacity: 0, 
                            scale: 0.95, 
                            height: 0, 
                            marginTop: 0, 
                            marginBottom: 0, 
                            paddingTop: 0, 
                            paddingBottom: 0,
                            borderWidth: 0,
                            transition: { 
                              type: "spring",
                              stiffness: 400,
                              damping: 30
                            } 
                          }}
                          transition={{
                            layout: { type: "spring", stiffness: 350, damping: 25 }
                          }}
                          className={cn(
                            "relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 flex flex-col gap-3 card-shadow hover:border-emerald-500 transition-all",
                            illumination && "illumination-food"
                          )}
                        >
                          {/* Product Identification Title Header */}
                          <div className="flex items-start gap-3">
                            <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center bg-[var(--card-bg)] border border-[var(--card-border)]">
                              {product.imageUrl ? (
                                <img src={product.imageUrl} referrerPolicy="no-referrer" alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                              ) : (
                                <Barcode className="w-6 h-6 text-emerald-500" />
                              )}
                            </div>
                            
                            <div className="flex-grow min-w-0">
                              <h3 className="font-bold text-base sm:text-lg leading-tight text-[var(--text)] line-clamp-2">
                                {product.name}
                              </h3>
                              <p className="font-mono text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1">
                                <span className="bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 rounded border border-emerald-500/20 text-[9px] font-bold">EAN</span>
                                {product.barcode}
                              </p>
                            </div>

                            <div className="text-right flex-shrink-0">
                              <span className="text-emerald-500 font-bold text-sm sm:text-base block">
                                {Math.round(product.calories100g)} {t.kcal}
                              </span>
                              <span className="text-[10px] text-zinc-500 block">{lang.startsWith('ua') ? "на 100" : "per 100"}{product.unit}</span>
                            </div>
                          </div>

                          {/* 4 Macros Grid */}
                          <div className="grid grid-cols-4 gap-2 border-t border-[var(--card-border)]/50 pt-2.5">
                            <div className="text-center rounded-lg bg-[var(--input-bg)]/40 p-1.5 border border-[var(--card-border)]/30">
                              <span className="text-[9px] uppercase font-bold tracking-wider text-zinc-500 block">{lang.startsWith('ua') ? "Біл" : "Pro"}</span>
                              <span className="text-xs font-bold text-blue-500">{(product.protein100g || 0).toFixed(1)}g</span>
                            </div>
                            <div className="text-center rounded-lg bg-[var(--input-bg)]/40 p-1.5 border border-[var(--card-border)]/30">
                              <span className="text-[9px] uppercase font-bold tracking-wider text-zinc-500 block">{lang.startsWith('ua') ? "Вуг" : "Car"}</span>
                              <span className="text-xs font-bold text-red-500">{(product.carbs100g || 0).toFixed(1)}g</span>
                            </div>
                            <div className="text-center rounded-lg bg-[var(--input-bg)]/40 p-1.5 border border-[var(--card-border)]/30">
                              <span className="text-[9px] uppercase font-bold tracking-wider text-zinc-500 block">{lang.startsWith('ua') ? "Жир" : "Fat"}</span>
                              <span className="text-xs font-bold text-amber-500">{(product.fat100g || 0).toFixed(1)}g</span>
                            </div>
                            <div className="text-center rounded-lg bg-[var(--input-bg)]/40 p-1.5 border border-[var(--card-border)]/30">
                              <span className="text-[9px] uppercase font-bold tracking-wider text-zinc-500 block">{lang.startsWith('ua') ? "Цук" : "Sug"}</span>
                              <span className="text-xs font-bold text-rose-500">{(product.sugar100g || 0).toFixed(1)}g</span>
                            </div>
                          </div>

                          {/* Extra detailed macros */}
                          <div className="flex items-center justify-between text-[10px] text-zinc-500 px-1 border-t border-[var(--card-border)]/10 pt-1.5">
                            <span className="flex items-center gap-1">
                              <strong>{lang.startsWith('ua') ? "Клітковина:" : "Fiber:"}</strong>
                              <span>{(product.fiber100g || 0).toFixed(1)}g</span>
                            </span>
                            <span className="flex items-center gap-1">
                              <strong>{lang.startsWith('ua') ? "Натрій:" : "Sodium:"}</strong>
                              <span>{Math.round(product.sodium100g || 0)}mg</span>
                            </span>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center justify-end gap-2 border-t border-[var(--card-border)]/50 pt-3 mt-1">
                            {/* Trash Icon */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                sounds.playPop();
                                setScannedProducts(prev => {
                                  const updated = prev.filter(p => p.barcode !== product.barcode);
                                  localStorage.setItem('scanned_products', JSON.stringify(safeClone(updated)));
                                  return updated;
                                });
                                // Also delete from Firestore if logged in
                                if (user) {
                                  deleteDocWithQueue(doc(db, 'users', user.uid, 'scanned_products', product.barcode)).catch(e => {
                                    console.error("Failed to delete scanned product:", e);
                                  });
                                }
                                toast.success(lang.startsWith('ua') ? "Видалено з історії сканувань" : "Removed from scanned history");
                              }}
                              className="p-2 border border-zinc-500/20 text-zinc-500 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all"
                              title={lang === 'ua' ? "Видалити продукт" : "Delete Product"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>

                            {/* Log Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                sounds.playSuccess();
                                setSelectedScannedProduct(product);
                                setScannedPortionAmount(100);
                              }}
                              className={cn(
                                "px-4 py-2 bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider rounded-xl hover:bg-emerald-400 transition-all flex items-center gap-1.5 shadow-md shadow-emerald-500/10",
                                selectedScannedProduct?.barcode === product.barcode && "btn-active-border"
                              )}
                            >
                              <Plus className="w-3.5 h-3.5" />
                              {lang.startsWith('ua') ? "Записати" : "Log Product"}
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </motion.div>
                )
              )}
            </div>

          {/* Scanned product portion adjuster overlay */}
          <AnimatePresence>
            {selectedScannedProduct && (
              <div className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-md flex items-center justify-center p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 30 }}
                  transition={{ type: "spring", duration: 0.4 }}
                  className={cn(
                    "w-full max-w-sm rounded-3xl border p-5 shadow-2xl relative overflow-hidden",
                    theme === 'light'
                      ? "bg-zinc-50/95 border-zinc-200 text-zinc-900"
                      : "bg-zinc-950/95 border-zinc-800 text-zinc-100"
                  )}
                >
                  {(() => {
                    const amount = scannedPortionAmount;
                    const ratio = amount / 100;
                    const scaledCalories = Math.round(selectedScannedProduct.calories100g * ratio);
                    const scaledProtein = Math.round(selectedScannedProduct.protein100g * ratio * 10) / 10;
                    const scaledCarbs = Math.round(selectedScannedProduct.carbs100g * ratio * 10) / 10;
                    const scaledFat = Math.round(selectedScannedProduct.fat100g * ratio * 10) / 10;
                    const scaledSugar = Math.round((selectedScannedProduct.sugar100g || 0) * ratio * 10) / 10;
                    const scaledFiber = Math.round((selectedScannedProduct.fiber100g || 0) * ratio * 10) / 10;
                    const scaledSodium = Math.round((selectedScannedProduct.sodium100g || 0) * ratio);
                    const scaledCaffeine = Math.round((selectedScannedProduct.caffeine100g || 0) * ratio);

                    const handlePortionLog = () => {
                      const finalAmount = amount || 100;
                      const finalRatio = finalAmount / 100;

                      const finalFoods = [{
                        name: selectedScannedProduct.name,
                        category: (selectedScannedProduct.unit === 'ml' ? 'drink' : 'other') as any,
                        portion: `${finalAmount}${selectedScannedProduct.unit}`,
                        amount: finalAmount,
                        unit: selectedScannedProduct.unit as any,
                        calories_kcal: selectedScannedProduct.calories100g * finalRatio,
                        protein_g: selectedScannedProduct.protein100g * finalRatio,
                        carbs_g: selectedScannedProduct.carbs100g * finalRatio,
                        fat_g: selectedScannedProduct.fat100g * finalRatio,
                        sugar_g: (selectedScannedProduct.sugar100g || 0) * finalRatio,
                        fiber_g: (selectedScannedProduct.fiber100g || 0) * finalRatio,
                        sodium_mg: (selectedScannedProduct.sodium100g || 0) * finalRatio,
                        caffeine_mg: (selectedScannedProduct.caffeine100g || 0) * finalRatio,
                      }];

                      const finalNutrition = {
                        calories_kcal: selectedScannedProduct.calories100g * finalRatio,
                        protein_g: selectedScannedProduct.protein100g * finalRatio,
                        carbs_g: selectedScannedProduct.carbs100g * finalRatio,
                        fat_g: selectedScannedProduct.fat100g * finalRatio,
                        sugar_g: (selectedScannedProduct.sugar100g || 0) * finalRatio,
                        fiber_g: (selectedScannedProduct.fiber100g || 0) * finalRatio,
                        sodium_mg: (selectedScannedProduct.sodium100g || 0) * finalRatio,
                        caffeine_mg: (selectedScannedProduct.caffeine100g || 0) * finalRatio,
                      };

                      const finalAnalysis = postProcessNutrition({
                        image: selectedScannedProduct.imageUrl || '',
                        meal_name: cleanBarcodeMealName(selectedScannedProduct.name),
                        meal_type: selectedMealType,
                        total_amount: `${finalAmount}${selectedScannedProduct.unit}`,
                        foods: finalFoods,
                        total_nutrition: finalNutrition,
                        insight: "" // Force dynamic recalculation for the portion size
                      }, lang, vibe);

                      saveToHistory(finalAnalysis, selectedScannedProduct.imageUrl);
                      sounds.playSuccess();
                      toast.success(lang.startsWith('ua') ? "Продукт успішно записано!" : "Product successfully logged!", {
                        icon: <AnimatedSuccessIcon />,
                        className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold",
                      });
                      setSelectedScannedProduct(null);
                    };

                    return (
                      <div className="space-y-5">
                        {/* Close button */}
                        <button 
                          onClick={() => { sounds.playPop(); setSelectedScannedProduct(null); }}
                          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-zinc-500/10 transition-colors text-zinc-500 hover:text-zinc-300"
                        >
                          <X className="w-5 h-5" />
                        </button>

                        {/* Product Info Summary */}
                        <div className="flex items-center gap-3 pr-6 mt-1">
                          <div className="w-12 h-12 rounded-xl border border-[var(--card-border)] overflow-hidden flex-shrink-0 flex items-center justify-center bg-[var(--card-bg)]">
                            {selectedScannedProduct.imageUrl ? (
                              <img src={selectedScannedProduct.imageUrl} referrerPolicy="no-referrer" alt={selectedScannedProduct.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                            ) : (
                              <Barcode className="w-5 h-5 text-emerald-500" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-bold text-sm tracking-tight text-[var(--text)] line-clamp-1">{selectedScannedProduct.name}</h4>
                            <p className="text-[10px] text-zinc-500 font-mono mt-0.5">{selectedScannedProduct.barcode}</p>
                          </div>
                        </div>

                        {/* Instant scaled stats view */}
                        <div className="grid grid-cols-2 gap-3 bg-[var(--input-bg)]/30 rounded-2xl p-4 border border-[var(--card-border)]/50 text-center">
                          <div>
                            <span className="text-[10px] uppercase font-bold text-zinc-500 block">{lang.startsWith('ua') ? "Калорійність" : "Calories"}</span>
                            <span className="text-xl font-black text-emerald-500">{scaledCalories} <span className="text-[10px] font-normal text-zinc-500">{t.kcal}</span></span>
                          </div>
                          <div>
                            <span className="text-[10px] uppercase font-bold text-zinc-500 block">{lang.startsWith('ua') ? "Порція" : "Size"}</span>
                            <span className="text-xl font-black text-zinc-500">{amount} <span className="text-[10px] font-normal text-zinc-500">{selectedScannedProduct.unit}</span></span>
                          </div>
                        </div>

                        {/* Quick Macros scaled */}
                        <div className="flex justify-between gap-2 text-center text-[10px] font-bold">
                          <div className="flex-1 bg-[var(--input-bg)]/20 p-2 rounded-xl border border-[var(--card-border)]/20">
                            <span className="text-zinc-500 block uppercase tracking-wider text-[8px]">{lang.startsWith('ua') ? "Біл" : "Pro"}</span>
                            <span className="text-blue-500 text-xs sm:text-sm">{scaledProtein}g</span>
                          </div>
                          <div className="flex-1 bg-[var(--input-bg)]/20 p-2 rounded-xl border border-[var(--card-border)]/20">
                            <span className="text-zinc-500 block uppercase tracking-wider text-[8px]">{lang.startsWith('ua') ? "Вуг" : "Car"}</span>
                            <span className="text-red-500 text-xs sm:text-sm">{scaledCarbs}g</span>
                          </div>
                          <div className="flex-1 bg-[var(--input-bg)]/20 p-2 rounded-xl border border-[var(--card-border)]/20">
                            <span className="text-zinc-500 block uppercase tracking-wider text-[8px]">{lang.startsWith('ua') ? "Жир" : "Fat"}</span>
                            <span className="text-amber-500 text-xs sm:text-sm">{scaledFat}g</span>
                          </div>
                          {selectedScannedProduct.caffeine100g !== undefined && selectedScannedProduct.caffeine100g > 0 && (
                            <div className="flex-1 bg-[var(--input-bg)]/20 p-2 rounded-xl border border-[var(--card-border)]/20">
                              <span className="text-zinc-500 block uppercase tracking-wider text-[8px]">{lang.startsWith('ua') ? "Коф" : "Caff"}</span>
                              <span className="text-amber-700 dark:text-amber-600 text-xs sm:text-sm font-black">{scaledCaffeine}mg</span>
                            </div>
                          )}
                        </div>

                        {/* Portion size preset options */}
                        <div className="space-y-2">
                          <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 flex justify-between items-center">
                            <span>{lang.startsWith('ua') ? "Розмір порції" : "Portion Size"} ({selectedScannedProduct.unit})</span>
                            <span className="text-emerald-500 font-bold">{amount}{selectedScannedProduct.unit}</span>
                          </label>
                          {/* Preset Buttons */}
                          <div className="flex justify-between gap-1 mt-1">
                            {[50, 100, 150, 250, 400, 500].map(pAmount => (
                              <button
                                key={pAmount}
                                type="button"
                                onClick={() => setScannedPortionAmount(pAmount)}
                                className={cn(
                                  "px-2 py-1.5 rounded-lg text-[9px] font-bold border transition-all flex-1 text-center",
                                  amount === pAmount 
                                    ? "bg-emerald-500 text-white border-emerald-500 shadow-sm" 
                                    : "bg-transparent border-zinc-500/20 text-zinc-500 hover:text-zinc-300 hover:border-zinc-500/40"
                                )}
                              >
                                {pAmount}{selectedScannedProduct.unit}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Logging Action buttons */}
                        <div className="flex items-center gap-2 pt-3 border-t border-[var(--card-border)]/40">
                          <button
                            onClick={() => { sounds.playPop(); setSelectedScannedProduct(null); }}
                            className="flex-1 py-3 border border-zinc-500/20 text-zinc-500 font-bold uppercase text-[10px] tracking-widest rounded-2xl hover:bg-zinc-500/10 transition-all"
                          >
                            {t.cancel}
                          </button>
                          <button
                            onClick={handlePortionLog}
                            className="flex-1 py-3 bg-emerald-500 text-white font-bold uppercase text-[10px] tracking-widest rounded-2xl hover:bg-emerald-400 active:scale-95 transition-all shadow-lg shadow-emerald-500/25 btn-active-border"
                          >
                            {lang.startsWith('ua') ? "Записати" : "Log Product"}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Local Storage Optimizer / Archive Management */}
          <div className={cn(
            "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-5 sm:p-6 card-shadow mx-2 transition-all space-y-4 relative overflow-hidden mt-6",
            illumination && "illumination-food"
          )}>
            <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />
            
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 text-emerald-500 rounded-2xl border border-emerald-500/20">
                  <Database className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-sm sm:text-base text-[var(--text)] uppercase tracking-wider flex items-center gap-1.5">
                    {lang.startsWith('ua') ? "Оптимізатор локальної пам'яті" : "Local Storage Optimizer"}
                  </h4>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {lang.startsWith('ua') 
                      ? "Автоматичне архівування записів, старіших за 6 місяців, зберігає додаток швидким." 
                      : "Automatically archives history older than 6 months to keep the app lightweight."}
                  </p>
                </div>
              </div>
              
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[9px] font-bold text-emerald-500 uppercase tracking-widest animate-pulse">
                <Zap className="w-3 h-3" />
                {lang.startsWith('ua') ? "Активно" : "Active"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="bg-[var(--input-bg)]/30 rounded-2xl p-3 border border-[var(--card-border)]/40 text-center">
                <span className="text-[10px] uppercase font-bold text-zinc-500 block">
                  {lang.startsWith('ua') ? "Активні записи" : "Active Logs"}
                </span>
                <span className="text-lg font-black text-[var(--text)]">{history.length}</span>
              </div>
              <div className="bg-[var(--input-bg)]/30 rounded-2xl p-3 border border-[var(--card-border)]/40 text-center">
                <span className="text-[10px] uppercase font-bold text-zinc-500 block">
                  {lang.startsWith('ua') ? "В архіві" : "Archived Logs"}
                </span>
                <span className="text-lg font-black text-emerald-500">{archivedCount}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[var(--card-border)]/30">
              <button
                onClick={async () => {
                  if (isArchiving) return;
                  setIsArchiving(true);
                  sounds.playPop();
                  try {
                    const res = await localDb.archiveOldMeals();
                    // Update history state from database
                    const updatedStr = await localDb.getItem('meal_history');
                    if (updatedStr) {
                      const parsed = JSON.parse(updatedStr);
                      if (Array.isArray(parsed)) {
                        setHistory(parsed.map(item => ({
                          ...item,
                          timestamp: ensureTimestampMillis(item.timestamp)
                        })));
                      }
                    }
                    toast.success(
                      lang.startsWith('ua')
                        ? `Успішно архівовано ${res.archivedCount} старих записів!`
                        : `Successfully archived ${res.archivedCount} older entries!`,
                      { icon: '📦' }
                    );
                  } catch (e) {
                    toast.error(lang.startsWith('ua') ? "Помилка при архівуванні" : "Failed to run manual cleanup");
                  } finally {
                    setIsArchiving(false);
                  }
                }}
                disabled={isArchiving}
                className="flex-1 min-w-[120px] py-2.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 font-bold uppercase text-[10px] tracking-widest rounded-xl transition-all border border-emerald-500/20 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <RefreshCw className={cn("w-3.5 h-3.5", isArchiving && "animate-spin")} />
                {lang.startsWith('ua') ? "Очистити зараз" : "Optimize Now"}
              </button>

              <button
                onClick={async () => {
                  sounds.playPop();
                  try {
                    const archived = await localDb.getArchivedMeals();
                    if (archived.length === 0) {
                      toast.error(lang.startsWith('ua') ? "Архів порожній" : "No archived data to export");
                      return;
                    }
                    const blob = new Blob([JSON.stringify(archived, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `nutrition_history_archive_${new Date().toISOString().slice(0,10)}.json`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    toast.success(lang.startsWith('ua') ? "Архів успішно завантажено!" : "Archive JSON file exported!", { icon: '💾' });
                  } catch (e) {
                    toast.error("Export failed");
                  }
                }}
                className="flex-1 min-w-[120px] py-2.5 bg-zinc-500/10 hover:bg-zinc-500/20 text-zinc-400 hover:text-zinc-200 font-bold uppercase text-[10px] tracking-widest rounded-xl transition-all border border-zinc-500/15 flex items-center justify-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                {lang.startsWith('ua') ? "Експорт архіву" : "Export JSON"}
              </button>

              {archivedCount > 0 && (
                <button
                  onClick={async () => {
                    sounds.playPop();
                    if (window.confirm(lang.startsWith('ua') ? "Ви впевнені, що хочете повернути всі архівні записи назад до основного логу?" : "Are you sure you want to restore all archived logs back to primary history?")) {
                      try {
                        await localDb.restoreAllArchivedMeals();
                        // Refresh state
                        const updatedStr = await localDb.getItem('meal_history');
                        if (updatedStr) {
                          const parsed = JSON.parse(updatedStr);
                          if (Array.isArray(parsed)) {
                            setHistory(parsed.map(item => ({
                              ...item,
                              timestamp: ensureTimestampMillis(item.timestamp)
                            })));
                          }
                        }
                        toast.success(lang.startsWith('ua') ? "Архівні записи успішно відновлено!" : "Archived meals successfully restored!", { icon: '♻️' });
                      } catch (err) {
                        toast.error("Restore failed");
                      }
                    }
                  }}
                  className="flex-1 min-w-[120px] py-2.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-200 font-bold uppercase text-[10px] tracking-widest rounded-xl transition-all border border-blue-500/20 flex items-center justify-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {lang.startsWith('ua') ? "Відновити" : "Restore All"}
                </button>
              )}
            </div>
          </div>
          </motion.div>
        )}

        {view === 'grocery' && (
          <div id="ai-concierge-section" className="scroll-mt-24">
            {subscriptionTier === 'free' ? (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-8 rounded-3xl bg-[var(--card-bg)] border border-[var(--card-border)] text-center space-y-4 max-w-md mx-auto my-12 card-shadow relative overflow-hidden"
              >
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto text-emerald-500">
                  <Bot className="w-8 h-8 animate-pulse" />
                </div>
                <h3 className="text-lg font-black uppercase tracking-wider text-[var(--text)]">
                  {lang === 'ua' ? 'Розумний кошик продуктів (PRO)' : 'Smart Grocery Planner (PRO)'}
                </h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed max-w-sm mx-auto">
                  {lang === 'ua'
                    ? 'Складайте списки покупок, імпортуйте інгредієнти страв та отримуйте ШІ рекомендації для заповнення дефіцитів у тарифі PRO!'
                    : 'Build smart grocery lists, sync dish ingredients, and get AI meal prep suggestions with the PRO membership!'}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setView('goals');
                    setIsSubscriptionExpanded(true);
                    scrollToSectionTop('subscription-card-settings', true);
                  }}
                  className="mt-4 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-black text-xs font-black rounded-xl uppercase tracking-widest transition-all cursor-pointer shadow-md inline-flex items-center gap-2"
                >
                  <Crown className="w-4 h-4" />
                  <span>{lang === 'ua' ? 'Розблокувати PRO' : 'Unlock PRO Plan'}</span>
                </button>
              </motion.div>
            ) : (
              <AiConcierge
                items={groceryItems}
                onToggleItem={handleToggleGroceryItem}
                onDeleteItem={handleDeleteGroceryItem}
                onClearChecked={handleClearCheckedGroceryItems}
                onAddItem={handleAddGroceryItem}
                onAddMultipleItems={handleAddMultipleGroceryItems}
                plannedMeals={filteredAiSuggestions}
                activeDeficiencies={activeDeficiencies}
                lang={lang}
                vibe={vibe}
                theme={theme}
                illumination={illumination}
                skin={skin}
                userTier={subscriptionTier}
                onOpenSubscriptionModal={() => setIsBillingModalOpen(true)}
                onFetchSuggestions={() => handleFetchSuggestions(undefined, true)}
                isGeneratingSuggestions={isGeneratingSuggestions}
              />
            )}
          </div>
        )}

        {view === 'details' && selectedHistoryItem && (
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedHistoryItem.id}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(event, info) => {
                const currentIndex = history.findIndex(item => item.id === selectedHistoryItem?.id);
                if (currentIndex === -1) return;

                if (info.offset.x > 100) {
                  // Swipe right: previous meal
                  const nextIndex = currentIndex > 0 ? currentIndex - 1 : history.length - 1;
                  setSelectedHistoryItem(history[nextIndex]);
                } else if (info.offset.x < -100) {
                  // Swipe left: next meal
                  const nextIndex = currentIndex < history.length - 1 ? currentIndex + 1 : 0;
                  setSelectedHistoryItem(history[nextIndex]);
                }
              }}
              className="space-y-6"
            >
              <div className="flex items-center gap-4 px-2 mb-2">
                <button 
                  onClick={handleBack}
                  className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <ClipboardList className="w-6 h-6 text-emerald-500 shrink-0" />
                  <h2 className={cn(
                    "text-lg sm:text-xl font-bold uppercase tracking-normal whitespace-nowrap truncate",
                    theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
                  )}>{t.details}</h2>
                </div>
                <button
                  onClick={async () => {
                    let text = `Check out my meal: ${selectedHistoryItem.meal_name || "Awesome Meal"} (${selectedHistoryItem.total_nutrition?.calories_kcal || 0} kcal) on AI Nutritionist! 🍽️\nProtein: ${selectedHistoryItem.total_nutrition?.protein_g || 0}g | Carbs: ${selectedHistoryItem.total_nutrition?.carbs_g || 0}g | Fat: ${selectedHistoryItem.total_nutrition?.fat_g || 0}g`;
                    if (navigator.share) {
                        try {
                            await navigator.share({
                                title: 'My Meal',
                                text: text,
                                url: window.location.origin
                            });
                        } catch (e: any) {
                            if (e.name !== 'AbortError') {
                                console.error('Error sharing', e);
                                navigator.clipboard.writeText(text);
                                toast.success(lang === 'ua' ? 'Страву скопійовано в буфер обміну!' : 'Meal copied to clipboard!');
                            }
                        }
                    } else {
                        navigator.clipboard.writeText(text);
                        toast.success(lang === 'ua' ? 'Страву скопійовано в буфер обміну!' : 'Meal copied to clipboard!');
                    }
                  }}
                  className="flex items-center gap-2 bg-emerald-100/50 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 px-4 py-2 rounded-full font-bold text-xs uppercase tracking-widest hover:bg-emerald-200/50 dark:hover:bg-emerald-500/30 transition-colors"
                >
                  <Share2 className="w-4 h-4" /> Share
                </button>
              </div>
              {renderAnalysisResult(selectedHistoryItem)}
            </motion.div>
          </AnimatePresence>
        )}

        {view === 'edit' && editingMeal && (
          <div className={cn(illumination ? "space-y-8" : "space-y-6")}>
            <div className="flex items-center justify-between px-2">
              <button onClick={handleBack} className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 className={cn(
                "text-xl font-bold uppercase tracking-normal",
                theme === 'light' ? "text-zinc-900 drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" : "text-[var(--text)]"
              )}>{t.editMeal}</h2>
              <div className="w-9" />
            </div>

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 card-shadow space-y-6">
              {/* Image Section for Edit */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.mealImage || 'MEAL PIC'}</label>
                <div className="relative group h-48 sm:h-64 rounded-2xl bg-[var(--input-bg)] border-2 border-dashed border-[var(--card-border)] overflow-hidden flex items-center justify-center transition-all">
                  {editingMeal.image ? (
                    <>
                      <img src={editingMeal.image} className="w-full h-full object-cover" alt="Editing meal" loading="lazy" decoding="async" referrerPolicy="no-referrer" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        <button 
                          onClick={() => setEditingMeal({ ...editingMeal, image: '' })}
                          className="bg-red-500 text-white p-3 rounded-full hover:bg-red-600 transition-all hover:scale-110 shadow-lg"
                          title={t.deleteMeal || 'Remove'}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => document.getElementById('edit-image-upload')?.click()}
                          className="bg-emerald-500 text-white p-3 rounded-full hover:bg-emerald-600 transition-all hover:scale-110 shadow-lg"
                          title={t.uploadImage}
                        >
                          <Upload className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => startCamera()}
                          className="bg-blue-500 text-white p-3 rounded-full hover:bg-blue-600 transition-all hover:scale-110 shadow-lg"
                          title={t.startCamera}
                        >
                          <Camera className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => setImageToCrop(editingMeal.image)}
                          className="bg-amber-500 text-white p-3 rounded-full hover:bg-amber-600 transition-all hover:scale-110 shadow-lg"
                          title={lang.startsWith('en') ? 'Crop' : 'Обрізати'}
                        >
                          <Crop className="w-5 h-5" />
                        </button>
                      </div>
                      {/* Mobile indicators */}
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2 sm:hidden">
                        <div className="bg-black/60 backdrop-blur-md p-1.5 rounded-full flex gap-2 border border-white/10">
                          <button onClick={() => setEditingMeal({ ...editingMeal, image: '' })} className="text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                          <button onClick={() => document.getElementById('edit-image-upload')?.click()} className="text-emerald-400 p-1"><Upload className="w-4 h-4" /></button>
                          <button onClick={() => startCamera()} className="text-blue-400 p-1"><Camera className="w-4 h-4" /></button>
                          <button onClick={() => setImageToCrop(editingMeal.image)} className="text-amber-400 p-1"><Crop className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center w-full px-6">
                      <button 
                        onClick={() => document.getElementById('edit-image-upload')?.click()}
                        className="flex-1 flex items-center justify-center text-zinc-500 hover:text-emerald-500 transition-colors"
                      >
                        <Upload className="w-10 h-10" />
                      </button>
                      <div className="w-px h-12 bg-[var(--card-border)]" />
                      <button 
                        onClick={() => startCamera()}
                        className="flex-1 flex items-center justify-center text-zinc-500 hover:text-emerald-500 transition-colors"
                      >
                        <Camera className="w-10 h-10" />
                      </button>
                      <div className="w-px h-12 bg-[var(--card-border)]" />
                      <button 
                        onClick={() => handleGenerateImage(editingMeal.meal_name, (url) => setEditingMeal({ ...editingMeal, image: url }))}
                        disabled={isGeneratingImage}
                        className="flex-1 flex items-center justify-center text-zinc-500 hover:text-emerald-500 transition-colors disabled:opacity-50"
                      >
                        {isGeneratingImage ? (
                          <Loader2 className="w-10 h-10 animate-spin" />
                        ) : (
                          <Sparkles className="w-10 h-10" />
                        )}
                      </button>
                    </div>
                  )}
                  <input 
                    id="edit-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = async () => {
                          try {
                            const resized = await resizeImage(reader.result as string, 512);
                            setImageToCrop(resized);
                          } catch (err) {
                            console.error("Error resizing edit image:", err);
                            toast.error(lang === 'ua' ? 'Не вдалося обробити зображення.' : 'Failed to process image.');
                          }
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.mealName}</label>
                  <input 
                    value={editingMeal.meal_name}
                    onChange={(e) => {
                      setEditingMeal({ ...editingMeal, meal_name: e.target.value });
                      setIsMealNameManuallyEdited(true);
                    }}
                    onBlur={() => {
                      const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                      const updatedMeal = {
                        ...editingMeal,
                        total_nutrition: updatedNutrition
                      };
                      if (updatedMeal.id !== 'temp') {
                        updateMealInHistory(updatedMeal as HistoryEntry);
                      } else {
                        setResult(updatedMeal);
                      }
                    }}
                    className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl px-4 py-3 focus:border-emerald-500 outline-none transition-colors font-bold text-[var(--text)]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.dateTime}</label>
                  <input 
                    type="datetime-local"
                    value={new Date(new Date(editingMeal.timestamp).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                    onChange={(e) => setEditingMeal({ ...editingMeal, timestamp: new Date(e.target.value).getTime() })}
                    onBlur={() => {
                      const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                      const updatedMeal = {
                        ...editingMeal,
                        total_nutrition: updatedNutrition
                      };
                      if (updatedMeal.id !== 'temp') {
                        updateMealInHistory(updatedMeal);
                      }
                    }}
                    className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl px-4 py-3 focus:border-emerald-500 outline-none transition-colors font-bold text-[var(--text)]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.mealType}</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(['breakfast', 'lunch', 'dinner', 'snack'] as MealType[]).map((type) => (
                      <button
                        key={type}
                        onClick={() => setEditingMeal({ ...editingMeal, meal_type: type })}
                        className={cn(
                          "py-2 rounded-xl border font-bold text-[10px] uppercase tracking-widest transition-all",
                          editingMeal.meal_type === type 
                            ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                            : "bg-[var(--input-bg)] border-[var(--card-border)] text-zinc-500"
                        )}
                      >
                        {t[type]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{lang === 'ua' ? 'Нотатки до страви' : 'Meal Notes'}</label>
                  <textarea 
                    value={editingMeal.notes || ''}
                    onChange={(e) => {
                      setEditingMeal({ ...editingMeal, notes: e.target.value });
                    }}
                    onBlur={() => {
                      const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                      const updatedMeal = {
                        ...editingMeal,
                        total_nutrition: updatedNutrition
                      };
                      if (updatedMeal.id !== 'temp') {
                        updateMealInHistory(updatedMeal as HistoryEntry);
                      } else {
                        setResult(updatedMeal);
                      }
                    }}
                    placeholder={lang === 'ua' ? 'Додайте власні нагадування або контекст...' : 'Add custom reminders or context about this meal...'}
                    className="w-full h-20 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl px-4 py-3 focus:border-emerald-500 outline-none transition-colors text-[var(--text)] text-sm resize-none"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col space-y-3 px-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold uppercase tracking-normal text-zinc-500">{t.foodItems}</h3>
                  </div>
                  <button 
                    onClick={() => {
                      const newFoods = [...editingMeal.foods, {
                        name: '', portion: '', amount: 100, unit: 'gr' as const, calories_kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, sugar_g: 0, fiber_g: 0, sodium_mg: 0, potassium_mg: 0, cholesterol_mg: 0, vitamin_c_mg: 0, calcium_mg: 0, iron_mg: 0
                      }];
                      setEditingMeal({ 
                        ...editingMeal, 
                        foods: newFoods,
                        meal_name: isMealNameManuallyEdited ? editingMeal.meal_name : generateMealName(newFoods)
                      });
                    }}
                    className={cn(
                      "w-full py-4 rounded-2xl transition-all flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-widest border-2 shadow-sm",
                      theme === 'light' 
                        ? "bg-white border-zinc-200 text-zinc-900 hover:bg-zinc-50 shadow-zinc-900/5 hover:translate-y-[-1px]" 
                        : "bg-[var(--input-bg)] border-[var(--card-border)] text-emerald-500 hover:bg-emerald-500/5"
                    )}
                  >
                    <Plus className="w-5 h-5" />
                    {t.addItem || 'Add Food Item'}
                  </button>
                </div>
                 <div className="space-y-4">
                  {editingMeal.foods.map((food, idx) => (
                    <div 
                      key={idx} 
                      className="p-4 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl space-y-3 relative group/item transition-all"
                    >
                      <button 
                        onClick={() => setItemToRemove(idx)}
                        className="absolute top-2 right-2 p-1.5 text-zinc-500 hover:text-red-500 transition-colors opacity-0 group-hover/item:opacity-100"
                        title={t.remove}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <div className="flex items-center gap-3">

                        <div className="w-10 h-10 bg-[var(--card-bg)] rounded-xl flex items-center justify-center text-xl shadow-sm border border-[var(--card-border)]">
                          {getFoodEmoji(food.name)}
                        </div>
                        <div className="flex-1 space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.foodName}</label>
                          <input 
                            value={food.name}
                            onChange={(e) => {
                              const newFoods = [...editingMeal.foods];
                              newFoods[idx] = { ...food, name: e.target.value };
                              setEditingMeal({ 
                                ...editingMeal, 
                                foods: newFoods, 
                                meal_name: isMealNameManuallyEdited ? editingMeal.meal_name : generateMealName(newFoods) 
                              });
                            }}
                            onBlur={() => {
                              const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                              const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                              if (updatedMeal.id !== 'temp') {
                                updateMealInHistory(updatedMeal as HistoryEntry);
                              } else {
                                setResult(updatedMeal);
                              }
                            }}
                            className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500 text-[var(--text)]"
                          />
                        </div>
                      </div>

                      {/* Weight/Amount & Unit Selector Row for Meal Review */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl p-2.5">
                        <div className="flex items-center gap-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 shrink-0">
                            {lang === 'ua' ? 'Вага / Порція:' : 'Amount / Unit:'}
                          </label>
                          <div className="relative flex items-center">
                            <input
                              type="number"
                              min="1"
                              value={food.amount !== undefined && food.amount !== null ? food.amount : 100}
                              onChange={(e) => {
                                const valStr = e.target.value;
                                const val = valStr === '' ? 0 : (parseFloat(valStr) || 0);
                                const origAmt = Number(food.amount) || 100;
                                const ratio = (origAmt > 0 && val > 0) ? val / origAmt : 1;
                                const newFoods = [...editingMeal.foods];
                                newFoods[idx] = {
                                  ...food,
                                  amount: val,
                                  portion: `${val} ${food.unit || 'g'}`,
                                  calories_kcal: val > 0 ? Math.round((food.calories_kcal || 0) * ratio) : food.calories_kcal,
                                  protein_g: val > 0 ? Number(((food.protein_g || 0) * ratio).toFixed(1)) : food.protein_g,
                                  carbs_g: val > 0 ? Number(((food.carbs_g || 0) * ratio).toFixed(1)) : food.carbs_g,
                                  fat_g: val > 0 ? Number(((food.fat_g || 0) * ratio).toFixed(1)) : food.fat_g,
                                  sugar_g: food.sugar_g ? Number(((food.sugar_g || 0) * ratio).toFixed(1)) : food.sugar_g,
                                  fiber_g: food.fiber_g ? Number(((food.fiber_g || 0) * ratio).toFixed(1)) : food.fiber_g,
                                  sodium_mg: food.sodium_mg ? Number(((food.sodium_mg || 0) * ratio).toFixed(1)) : food.sodium_mg,
                                };
                                setEditingMeal({ ...editingMeal, foods: newFoods });
                              }}
                              onBlur={() => {
                                const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                                const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                                if (updatedMeal.id !== 'temp') {
                                  updateMealInHistory(updatedMeal as HistoryEntry);
                                } else {
                                  setResult(updatedMeal);
                                }
                              }}
                              className="w-20 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg px-2 py-1 text-xs font-bold outline-none focus:border-emerald-500 text-[var(--text)] text-center"
                            />
                            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1.5 shrink-0">
                              {food.unit || 'g'}
                            </span>
                          </div>
                        </div>

                        {/* Quick Unit Selector Chips */}
                        <div className="flex items-center bg-zinc-100 dark:bg-zinc-800/80 p-0.5 rounded-lg border border-zinc-200/80 dark:border-zinc-700/80 gap-0.5 shrink-0">
                          {['g', 'ml', 'pcs', 'serving'].map((u) => {
                            const currentUnit = String(food.unit || 'g').toLowerCase().trim();
                            const isG = u === 'g' && ['g', 'gr', 'grams', 'г', 'гр'].includes(currentUnit);
                            const isPcs = u === 'pcs' && ['pcs', 'pc', 'piece', 'pieces', 'шт', 'шт.'].includes(currentUnit);
                            const isMl = u === 'ml' && ['ml', 'мл'].includes(currentUnit);
                            const isServing = u === 'serving' && ['serving', 'servings', 'порція', 'порция', 'порц'].includes(currentUnit);
                            const isActive = currentUnit === u || isG || isPcs || isMl || isServing;

                            return (
                              <button
                                key={u}
                                type="button"
                                onClick={() => {
                                  triggerHaptic('SUCCESS');
                                  sounds.playPop();
                                  let currentAmt = Number(food.amount) || 100;
                                  let newAmount = currentAmt;
                                  if (u === 'pcs') {
                                    newAmount = food.amount_pcs || (currentAmt > 15 ? 1 : currentAmt);
                                  } else if (u === 'g' || u === 'ml') {
                                    newAmount = food.amount_g || (currentAmt <= 15 ? 100 : currentAmt);
                                  } else if (u === 'serving') {
                                    newAmount = (currentUnit === 'serving') ? currentAmt : 1;
                                  }
                                  const newFoods = [...editingMeal.foods];
                                  newFoods[idx] = {
                                    ...food,
                                    unit: u as any,
                                    amount: newAmount,
                                    portion: `${newAmount} ${u}`
                                  };
                                  setEditingMeal({ ...editingMeal, foods: newFoods });
                                }}
                                className={cn(
                                  "px-2 py-1 rounded-md text-[9px] font-black uppercase tracking-wider transition-all cursor-pointer select-none",
                                  isActive
                                    ? "bg-emerald-500 text-white shadow-xs"
                                    : "text-zinc-500 dark:text-zinc-400 hover:text-emerald-500 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
                                )}
                              >
                                {u === 'serving' ? 'SERVING' : u}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.kcal}</label>
                          <input 
                            type="number"
                            min="0"
                            value={food.calories_kcal}
                            onChange={(e) => {
                              const newFoods = [...editingMeal.foods];
                              const val = e.target.value === '' ? 0 : Number(e.target.value);
                              if (val < 0) return;
                              newFoods[idx] = { ...food, calories_kcal: val };
                              setEditingMeal({ ...editingMeal, foods: newFoods });
                            }}
                            onBlur={() => {
                              const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                              const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                              if (updatedMeal.id !== 'temp') {
                                updateMealInHistory(updatedMeal as HistoryEntry);
                              } else {
                                setResult(updatedMeal);
                              }
                            }}
                            className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500 text-[var(--text)]"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.proteinShort} (g)</label>
                          <input 
                            type="number"
                            min="0"
                            value={food.protein_g}
                            onChange={(e) => {
                              const newFoods = [...editingMeal.foods];
                              const val = e.target.value === '' ? 0 : Number(e.target.value);
                              if (val < 0) return;
                              newFoods[idx] = { 
                                ...food, 
                                protein_g: val,
                                calories_kcal: Math.round((val * 4) + (food.carbs_g * 4) + (food.fat_g * 9))
                              };
                              setEditingMeal({ ...editingMeal, foods: newFoods });
                            }}
                            onBlur={() => {
                              const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                              const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                              if (updatedMeal.id !== 'temp') {
                                updateMealInHistory(updatedMeal as HistoryEntry);
                              } else {
                                setResult(updatedMeal);
                              }
                            }}
                            className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500 text-[var(--text)]"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.carbsShort} (g)</label>
                          <input 
                            type="number"
                            min="0"
                            value={food.carbs_g}
                            onChange={(e) => {
                              const newFoods = [...editingMeal.foods];
                              const val = e.target.value === '' ? 0 : Number(e.target.value);
                              if (val < 0) return;
                              newFoods[idx] = { 
                                ...food, 
                                carbs_g: val,
                                calories_kcal: Math.round((food.protein_g * 4) + (val * 4) + (food.fat_g * 9))
                              };
                              setEditingMeal({ ...editingMeal, foods: newFoods });
                            }}
                            onBlur={() => {
                              const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                              const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                              if (updatedMeal.id !== 'temp') {
                                updateMealInHistory(updatedMeal as HistoryEntry);
                              } else {
                                setResult(updatedMeal);
                              }
                            }}
                            className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500 text-[var(--text)]"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.fatShort} (g)</label>
                          <input 
                            type="number"
                            min="0"
                            value={food.fat_g}
                            onChange={(e) => {
                              const newFoods = [...editingMeal.foods];
                              const val = e.target.value === '' ? 0 : Number(e.target.value);
                              if (val < 0) return;
                              newFoods[idx] = { 
                                ...food, 
                                fat_g: val,
                                calories_kcal: Math.round((food.protein_g * 4) + (food.carbs_g * 4) + (val * 9))
                              };
                              setEditingMeal({ ...editingMeal, foods: newFoods });
                            }}
                            onBlur={() => {
                              const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                              const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                              if (updatedMeal.id !== 'temp') {
                                updateMealInHistory(updatedMeal as HistoryEntry);
                              } else {
                                setResult(updatedMeal);
                              }
                            }}
                            className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500 text-[var(--text)]"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.sugarShort || 'Sugar'} (g)</label>
                          <input 
                            type="number"
                            min="0"
                            value={food.sugar_g || 0}
                            onChange={(e) => {
                              const newFoods = [...editingMeal.foods];
                              const val = e.target.value === '' ? 0 : Number(e.target.value);
                              if (val < 0) return;
                              newFoods[idx] = { ...food, sugar_g: val };
                              setEditingMeal({ ...editingMeal, foods: newFoods });
                            }}
                            onBlur={() => {
                              const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                              const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                              if (updatedMeal.id !== 'temp') {
                                updateMealInHistory(updatedMeal as HistoryEntry);
                              } else {
                                setResult(updatedMeal);
                              }
                            }}
                            className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500 text-[var(--text)]"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.fiberShort || 'Fiber'} (g)</label>
                          <input 
                            type="number"
                            min="0"
                            value={food.fiber_g || 0}
                            onChange={(e) => {
                              const newFoods = [...editingMeal.foods];
                              const val = e.target.value === '' ? 0 : Number(e.target.value);
                              if (val < 0) return;
                              newFoods[idx] = { ...food, fiber_g: val };
                              setEditingMeal({ ...editingMeal, foods: newFoods });
                            }}
                            onBlur={() => {
                              const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                              const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                              if (updatedMeal.id !== 'temp') {
                                updateMealInHistory(updatedMeal as HistoryEntry);
                              } else {
                                setResult(updatedMeal);
                              }
                            }}
                            className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500 text-[var(--text)]"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.sodiumShort || 'Salt'} (mg)</label>
                          <input 
                            type="number"
                            min="0"
                            value={food.sodium_mg || 0}
                            onChange={(e) => {
                              const newFoods = [...editingMeal.foods];
                              const val = e.target.value === '' ? 0 : Number(e.target.value);
                              if (val < 0) return;
                              newFoods[idx] = { ...food, sodium_mg: val };
                              setEditingMeal({ ...editingMeal, foods: newFoods });
                            }}
                            onBlur={() => {
                              const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                              const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                              if (updatedMeal.id !== 'temp') {
                                updateMealInHistory(updatedMeal as HistoryEntry);
                              } else {
                                setResult(updatedMeal);
                              }
                            }}
                            className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500 text-[var(--text)]"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{t.caffeine || 'Caffeine'} (mg)</label>
                          <input 
                            type="number"
                            min="0"
                            value={food.caffeine_mg || 0}
                            onChange={(e) => {
                              const newFoods = [...editingMeal.foods];
                              const val = e.target.value === '' ? 0 : Number(e.target.value);
                              if (val < 0) return;
                              newFoods[idx] = { ...food, caffeine_mg: val };
                              setEditingMeal({ ...editingMeal, foods: newFoods });
                            }}
                            onBlur={() => {
                              const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                              const updatedMeal = { ...editingMeal, total_nutrition: updatedNutrition };
                              if (updatedMeal.id !== 'temp') {
                                updateMealInHistory(updatedMeal as HistoryEntry);
                              } else {
                                setResult(updatedMeal);
                              }
                            }}
                            className="w-full bg-[var(--input-bg)] border border-[var(--card-border)] rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500 text-[var(--text)]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Live Total Nutrition Display in Edit Mode */}
              {(() => {
                const totals = recalculateTotalNutrition(editingMeal.foods);
                return (
                  <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 space-y-3">
                    <h3 className="text-xs font-bold uppercase tracking-normal text-zinc-500">{t.totalNutrition || 'TOTAL NUTRITION'}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="flex items-center gap-2">
                        <Flame className="w-4 h-4 text-emerald-500" />
                        <div>
                          <p className="text-sm font-bold text-[var(--text)]">{totals.calories_kcal} kcal</p>
                          <p className="text-[10px] text-zinc-500 uppercase font-bold">{t.calories}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Dumbbell className="w-4 h-4 text-blue-500" />
                        <div>
                          <p className="text-sm font-bold text-[var(--text)]">{totals.protein_g}g</p>
                          <p className="text-[10px] text-zinc-500 uppercase font-bold">{t.proteinShort}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Apple className="w-4 h-4 text-red-500" />
                        <div>
                          <p className="text-sm font-bold text-[var(--text)]">{totals.carbs_g}g</p>
                          <p className="text-[10px] text-zinc-500 uppercase font-bold">{t.carbsShort}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Droplets className="w-4 h-4 text-amber-500" />
                        <div>
                          <p className="text-sm font-bold text-[var(--text)]">{totals.fat_g}g</p>
                          <p className="text-[10px] text-zinc-500 uppercase font-bold">{t.fatShort}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Candy className="w-4 h-4 text-pink-500" />
                        <div>
                          <p className="text-sm font-bold text-[var(--text)]">{totals.sugar_g}g</p>
                          <p className="text-[10px] text-zinc-500 uppercase font-bold">{t.sugarShort || 'Sugar'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Leaf className="w-4 h-4 text-emerald-500" />
                        <div>
                          <p className="text-sm font-bold text-[var(--text)]">{totals.fiber_g}g</p>
                          <p className="text-[10px] text-zinc-500 uppercase font-bold">{t.fiberShort || 'Fiber'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Waves className="w-4 h-4 text-indigo-500" />
                        <div>
                          <p className="text-sm font-bold text-[var(--text)]">{totals.sodium_mg}mg</p>
                          <p className="text-[10px] text-zinc-500 uppercase font-bold">{t.sodiumShort || 'Salt'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Coffee className="w-4 h-4 text-amber-700" />
                        <div>
                          <p className="text-sm font-bold text-[var(--text)]">{totals.caffeine_mg}mg</p>
                          <p className="text-[10px] text-zinc-500 uppercase font-bold">{t.caffeine || 'Caffeine'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="flex flex-col gap-3">
                <button 
                  onClick={() => {
                    if (!editingMeal.meal_name.trim()) {
                      toast.error(lang.startsWith('en') ? "Meal name cannot be empty." : "Назва прийому їжі не може бути порожньою.");
                      return;
                    }
                    if (editingMeal.foods.length === 0) {
                      toast.error(lang.startsWith('en') ? "Please add at least one food item." : "Будь ласка, додайте хоча б один продукт.");
                      return;
                    }
                    for (const food of editingMeal.foods) {
                      if (!food.name.trim()) {
                        toast.error(lang.startsWith('en') ? "Food item names cannot be empty." : "Назви продуктів не можуть бути порожніми.");
                        return;
                      }
                      if (!food.amount || Number(food.amount) <= 0) {
                        toast.error(lang.startsWith('en') ? "Food amounts must be greater than 0." : "Кількість продукту має бути більшою за 0.");
                        return;
                      }
                    }

                    const updatedNutrition = recalculateTotalNutrition(editingMeal.foods);
                    const updatedMeal = postProcessNutrition({
                      ...editingMeal,
                      total_nutrition: updatedNutrition,
                      insight: "" // Force dynamic recalculation for the corrected meal details
                    } as any, lang, vibe) as HistoryEntry;

                    if (updatedMeal.id === 'temp') {
                      setResult(updatedMeal);
                      setSelectedHistoryItem({
                        ...updatedMeal,
                        id: 'temp',
                        timestamp: editingMeal.timestamp || getTimestampForSelectedDate(),
                        image: editingMeal.image || image || '',
                      } as HistoryEntry);
                      setView('details');
                      toast.success(t.mealUpdatedSuccess || 'Meal updated successfully!');
                    } else {
                      updateMealInHistory(updatedMeal as HistoryEntry);
                    }
                  }}
                  className="w-full bg-emerald-500 text-white py-4 rounded-2xl font-bold text-lg uppercase tracking-widest hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-6 h-6" />
                  {t.saveChanges}
                </button>
                <button 
                  onClick={() => setView(editingMeal.id === 'temp' ? 'home' : 'details')}
                  className="w-full bg-[var(--input-bg)] text-zinc-600 py-4 rounded-2xl font-bold text-lg uppercase tracking-widest hover:bg-[var(--card-border)] transition-colors"
                >
                  {t.cancel}
                </button>
              </div>
            </div>
          </div>
        )}
        {view === 'goals' && (
          <motion.div 
            className={cn(illumination ? "space-y-8" : "space-y-6")}
          >
            <div 
              id="my-account-section"
              className={cn(
                "bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 sm:p-8 space-y-6 card-shadow transition-all scroll-mt-24",
                illumination && "illumination-food"
              )}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-50 dark:bg-emerald-900/10 p-2 rounded-xl">
                      <UserCircle className="w-6 h-6 text-emerald-500" />
                    </div>
                    <h2 className={cn(
                      "text-xl font-bold uppercase tracking-normal text-[var(--text)]"
                    )}>{lang === 'ua' ? 'МІЙ ПРОФІЛЬ' : 'MY PROFILE'}</h2>
                  </div>
                  
                  {/* Online/Offline & Sync Action */}
                  <div className="flex items-center gap-2">
                    {!isOnline ? (
                      <div className="flex items-center gap-1.5 bg-red-500/10 px-2.5 py-1.5 rounded-full border border-red-500/20" title={lang === 'ua' ? "Офлайн" : "Offline"}>
                         <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                         <span className="text-red-500 font-bold text-xs uppercase">{lang === 'ua' ? "Офлайн" : "Offline"}</span>
                         {offlineQueueLength > 0 && <span className="text-red-500 font-bold text-xs">({offlineQueueLength})</span>}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={async () => {
                          setIsSyncing(true);
                          try {
                            await loadData(true);
                            toast.success(lang === 'ua' ? 'Дані синхронізовано між пристроями!' : 'All data synced across devices!');
                          } catch (err) {
                            console.error('Manual sync failed:', err);
                            toast.error(lang === 'ua' ? 'Помилка синхронізації' : 'Sync error occurred');
                          } finally {
                            setIsSyncing(false);
                          }
                        }}
                        disabled={isSyncing}
                        className={cn(
                          "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold uppercase transition-all cursor-pointer select-none",
                          isSyncing 
                            ? "bg-yellow-500/10 border-yellow-500/20 text-yellow-500" 
                            : "bg-emerald-500/10 border-emerald-500/20 text-emerald-500 hover:bg-emerald-500/20 active:scale-95"
                        )}
                        title={lang === 'ua' ? "Синхронізувати з іншими пристроями" : "Sync across devices"}
                      >
                        <RefreshCw className={cn("w-3.5 h-3.5", isSyncing && "animate-spin")} />
                        <span>{isSyncing ? (lang === 'ua' ? "Синхронізація..." : "Syncing...") : (lang === 'ua' ? "Синхронізація" : "Sync Now")}</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className={cn(
                  "flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 p-4 sm:p-5 rounded-2xl border transition-all card-shadow",
                  theme === 'light'
                    ? "bg-white border-zinc-200 text-zinc-900"
                    : "bg-zinc-900/30 border-zinc-800/80 text-white"
                )}>
                  <div className="relative group shrink-0">
                    <input 
                      type="file" 
                      ref={avatarInputRef} 
                      onChange={handleAvatarFileChange} 
                      accept="image/*" 
                      className="hidden" 
                    />
                    <div 
                      onClick={() => avatarInputRef.current?.click()}
                      className={cn(
                        "relative group w-16 h-16 sm:w-20 sm:h-20 rounded-full cursor-pointer shrink-0 transition-all flex items-center justify-center overflow-hidden",
                        skin === 'stalker'
                          ? ((subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                              ? "p-[2.5px] bg-[#4ade80] shadow-[0_0_12px_rgba(74,222,128,0.5)]" 
                              : subscriptionTier === 'pro' 
                                ? "p-[2.5px] bg-[#f97316] shadow-[0_0_12px_rgba(249,115,22,0.5)]" 
                                : "p-[2.5px] bg-zinc-700 shadow-md")
                          : ((subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                              ? "p-[2.5px] pro-crown-badge-animated shadow-[0_0_15px_rgba(168,85,247,0.45)]"
                              : subscriptionTier === 'pro'
                                ? "p-[2.5px] bg-gradient-to-tr from-amber-500 via-amber-300 to-yellow-400 shadow-[0_0_14px_rgba(245,158,11,0.5)]"
                                : "p-[2.5px] bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]")
                      )}
                      title={effectiveAvatarUrl ? (lang === 'ua' ? "Натисніть, щоб змінити аватар" : "Click to change avatar") : (lang === 'ua' ? "Натисніть, щоб завантажити аватар" : "Click to upload avatar")}
                    >
                      <div className="w-full h-full rounded-full overflow-hidden relative flex items-center justify-center bg-zinc-900">
                        {effectiveAvatarUrl ? (
                          <img 
                            src={effectiveAvatarUrl} 
                            alt="Profile" 
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                            onError={() => setAvatarImageError(true)} 
                            className="w-full h-full object-cover shrink-0" 
                          />
                        ) : (
                          <div className={cn(
                            "w-full h-full flex flex-col items-center justify-center font-bold text-xl sm:text-2xl",
                            (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                              ? "bg-gradient-to-br from-purple-900 to-indigo-950 text-purple-200"
                              : subscriptionTier === 'pro'
                                ? "bg-gradient-to-br from-amber-950 to-zinc-900 text-amber-300"
                                : (theme === 'light' ? "bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-800" : "bg-gradient-to-br from-emerald-950 to-zinc-900 text-emerald-300")
                          )}>
                            <span>{(userName || auth.currentUser?.displayName || 'U')[0].toUpperCase()}</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white text-[10px] font-bold gap-0.5">
                          <Camera className="w-4 h-4" />
                          <span>{effectiveAvatarUrl ? (lang === 'ua' ? 'Змінити' : 'Change') : (lang === 'ua' ? 'Завантажити' : 'Upload')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-center sm:text-left flex-1 min-w-0 w-full space-y-1.5">
                    <div className="flex flex-col items-center sm:items-start gap-1">
                      {effectiveGamificationTitle && (
                        effectiveGamificationLogsCount >= 25 ? (
                          <span className="px-2.5 py-0.5 rounded-xl text-[10px] font-extrabold uppercase tracking-wider shadow-xs inline-flex items-center gap-1 shrink-0 animate-premium-gradient-solid-bg text-white border border-transparent">
                            {highestEarnedBadge && <highestEarnedBadge.icon className="w-3 h-3 text-white" />}
                            <span>{effectiveGamificationTitle}</span>
                          </span>
                        ) : (
                          <span 
                            className="px-2.5 py-0.5 rounded-xl text-[10px] font-extrabold uppercase tracking-wider border shadow-xs inline-flex items-center gap-1 shrink-0"
                            style={{
                              color: effectiveGamificationColor,
                              borderColor: `${effectiveGamificationColor}50`,
                              backgroundColor: `${effectiveGamificationColor}15`
                            }}
                          >
                            {highestEarnedBadge && <highestEarnedBadge.icon className="w-3 h-3" style={{ color: effectiveGamificationColor }} />}
                            <span>{effectiveGamificationTitle}</span>
                          </span>
                        )
                      )}
                      <h3 className={cn(
                        "text-lg sm:text-xl font-bold tracking-tight font-sans truncate",
                        theme === 'light' ? "text-zinc-800" : "text-zinc-100"
                      )}>
                        {userName || auth.currentUser?.displayName || 'User'}
                      </h3>
                    </div>
                    <div className="flex flex-col items-center sm:items-start gap-1">
                      <p className={cn(
                        "text-xs sm:text-sm font-medium tracking-normal truncate flex items-center gap-1.5 justify-center sm:justify-start",
                        theme === 'light' ? "text-zinc-500" : "text-zinc-400"
                      )}>
                        <Mail className="w-3.5 h-3.5 opacity-70 shrink-0 text-emerald-500" />
                        <span>
                          {effectiveUserEmail && (effectiveUserEmail.startsWith('guest_') || effectiveUserEmail.includes('@mainnutrition.app'))
                            ? (lang === 'ua' ? `Гостьовий акаунт (${effectiveUserEmail.split('@')[0]})` : `Guest Account (${effectiveUserEmail.split('@')[0]})`)
                            : (effectiveUserEmail || (lang === 'ua' ? 'Електронна пошта не вказана' : 'No email address'))}
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center justify-center sm:justify-start gap-2 pt-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          triggerHaptic('SUCCESS');
                          setIsSubscriptionExpanded(true);
                          scrollToSectionTop('subscription-card-settings', true);
                        }}
                        className={cn(
                          "px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider transition-all hover:scale-105 active:scale-95 cursor-pointer inline-flex items-center gap-1 shadow-sm border",
                          skin === 'stalker'
                            ? ((subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                                ? "bg-black text-[#4ade80] border-[#4ade80] hover:bg-[#4ade80]/10 font-mono" 
                                : subscriptionTier === 'pro' 
                                  ? "bg-black text-amber-500 border-amber-500 hover:bg-amber-500/10 font-mono"
                                  : "bg-black text-zinc-500 border-zinc-700 hover:bg-zinc-800 font-mono")
                            : ((subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                                ? "pro-crown-badge-animated border-transparent text-white"
                                : subscriptionTier === 'pro'
                                  ? "premium-gold-badge border-transparent text-black shadow-md"
                                  : "bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700")
                        )}
                        title={lang === 'ua' ? 'Натисніть для перегляду членства' : 'Click to view membership plans'}
                      >
                        <Crown className={cn("w-3.5 h-3.5 shrink-0 transition-transform", (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') ? "pro-crown-icon-animated" : subscriptionTier === 'pro' ? (skin === 'stalker' ? "text-amber-500 fill-amber-500/30" : "text-zinc-950 fill-zinc-950") : (skin === 'stalker' ? "text-[#4ade80]" : "text-emerald-500"))} />
                        <span>
                          {skin === 'stalker'
                            ? ((subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                                ? (lang === 'ua' ? 'ЛІЦЕНЗІЯ: ЛЕГЕНДА КПК' : 'LICENSE: LEGEND PDA')
                                : subscriptionTier === 'pro'
                                  ? (lang === 'ua' ? 'ЛІЦЕНЗІЯ: СТАЛКЕР КПК' : 'LICENSE: STALKER PDA')
                                  : (lang === 'ua' ? 'ЛІЦЕНЗІЯ: НОВАЧОК КПК' : 'LICENSE: ROOKIE PDA'))
                            : ((subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                                ? 'PREMIUM'
                                : subscriptionTier === 'pro'
                                  ? 'PRO'
                                  : 'FREE')}
                        </span>
                      </button>

                      {isTrialActive && subscriptionTier !== 'premium' && subscriptionTier !== 'premium_pro' && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 uppercase tracking-widest animate-pulse">
                          {lang === 'ua' ? 'Пробний період' : 'Trial Active'}
                        </span>
                      )}
                    </div>
                    {consecutiveDaysStreak >= 3 && (
                      <div className="hidden sm:flex flex-wrap gap-1.5 justify-center sm:justify-start pt-1">
                        {MASTERY_BADGES.filter(b => consecutiveDaysStreak >= b.days).map((badge, idx, arr) => {
                          const isHighest = idx === arr.length - 1;
                          const displayDays = isHighest ? consecutiveDaysStreak : badge.days;
                          return (
                            <button 
                              key={badge.days} 
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                if (typeof confetti === 'function') {
                                  const particleCount = Math.min(50 + badge.days * 5, 800);
                                  const spread = Math.min(60 + badge.days, 360);
                                  confetti({
                                    particleCount,
                                    spread,
                                    origin: { y: 0.6 },
                                    colors: ['#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#EAB308']
                                  });
                                }
                              }}
                              className={cn("flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-xl border transition-all hover:scale-105 active:scale-95 cursor-pointer", badge.bg, badge.border)} 
                              title={badge.name}
                            >
                              <badge.icon className={cn("w-3 h-3 sm:w-3.5 sm:h-3.5", badge.color)} />
                              <span className={cn("text-[10px] font-bold uppercase", badge.color)}>{displayDays}D</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 sm:gap-2.5 w-full sm:w-auto mt-2 sm:mt-0 shrink-0">
                    <div className={cn(
                      "text-center rounded-xl p-2 sm:p-2.5 border transition-all min-w-[70px] sm:min-w-[90px]",
                      theme === 'light'
                        ? "bg-zinc-50 border-zinc-100"
                        : "bg-zinc-950/40 border-zinc-800/60"
                    )}>
                      <div className="text-base sm:text-lg font-black text-emerald-500">{history.length}</div>
                      <div className={cn(
                        "text-[8px] sm:text-[9px] font-bold uppercase tracking-wider",
                        theme === 'light' ? "text-zinc-500" : "text-zinc-400"
                      )}>
                        {lang === 'ua' ? 'СТРАВ' : 'Meals'}
                      </div>
                    </div>
                    <div className={cn(
                      "text-center rounded-xl p-2 sm:p-2.5 border transition-all min-w-[70px] sm:min-w-[90px]",
                      theme === 'light'
                        ? "bg-zinc-50 border-zinc-100"
                        : "bg-zinc-950/40 border-zinc-800/60"
                    )}>
                      <div className="text-base sm:text-lg font-black text-amber-500">{consecutiveDaysStreak}</div>
                      <div className={cn(
                        "text-[8px] sm:text-[9px] font-bold uppercase tracking-wider",
                        theme === 'light' ? "text-zinc-500" : "text-zinc-400"
                      )}>
                        {lang === 'ua' 
                          ? (vibe === 'casual' ? 'ПОТУГ' : 'СТРІК') 
                          : 'Streak'
                        }
                      </div>
                    </div>
                    <div className={cn(
                      "text-center rounded-xl p-2 sm:p-2.5 border transition-all min-w-[70px] sm:min-w-[90px]",
                      theme === 'light'
                        ? "bg-zinc-50 border-zinc-100"
                        : "bg-zinc-950/40 border-zinc-800/60"
                    )}>
                      <div className="text-base sm:text-lg font-black text-amber-400 flex items-center justify-center gap-1">
                        <Zap className="w-3.5 h-3.5 fill-amber-400 text-amber-400 shrink-0" />
                        <span>{userTokensCount}</span>
                      </div>
                      <div className={cn(
                        "text-[8px] sm:text-[9px] font-bold uppercase tracking-wider",
                        theme === 'light' ? "text-zinc-500" : "text-zinc-400"
                      )}>
                        {lang === 'ua' ? 'ТОКЕНИ ⚡' : 'Tokens ⚡'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gamification & Achievements Section */}
                <div id="achievements-section" className="scroll-mt-24">
                  <AchievementsPanel
                    history={history}
                    waterLogs={waterLogs}
                    consecutiveDaysStreak={consecutiveDaysStreak}
                    mutualLikesCount={mutualLikesCount}
                    subscriptionTier={subscriptionTier}
                    isTrialActive={isTrialActive}
                    lang={lang}
                    theme={theme}
                    skin={skin}
                    vibe={vibe}
                    isRankExpanded={isRankExpanded}
                    setIsRankExpanded={setIsRankExpanded}
                    isBadgesExpanded={isBadgesExpanded}
                    setIsBadgesExpanded={setIsBadgesExpanded}
                    toast={toast}
                    userId={user?.uid}
                    onThemeChanged={(newTheme) => {
                      if (newTheme === 'stalker' || newTheme === 'cyberpunk' || newTheme === 'anime' || newTheme === 'default') {
                        handleSkinChange(newTheme as Skin);
                      }
                    }}
                  />
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const newName = (formData.get('userName') as string) || onboardingName || userName || '';
                saveGoals({
                  targetMode: (formData.get('targetMode') as 'daily' | 'weekly') || rawGoals.targetMode || 'daily',
                  calories: Number(formData.get('calories')) || rawGoals.calories,
                  protein: Number(formData.get('protein')) || rawGoals.protein,
                  carbs: Number(formData.get('carbs')) || rawGoals.carbs,
                  fat: Number(formData.get('fat')) || rawGoals.fat,
                  water_ml: Number(formData.get('water_ml')) || rawGoals.water_ml,
                  sugar_g: Number(formData.get('sugar_g')) || rawGoals.sugar_g,
                  fiber_g: Number(formData.get('fiber_g')) || rawGoals.fiber_g,
                  sodium_mg: Number(formData.get('sodium_mg')) || rawGoals.sodium_mg,
                  caffeine_mg: Number(formData.get('caffeine_mg')) || rawGoals.caffeine_mg,
                }, newName);
              }} className="space-y-6">
                {/* Immersive Billing & Subscription Settings Section */}
                <div className="space-y-4 scroll-mt-24" id="subscription-card-settings">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      triggerHaptic('SUCCESS');
                      setIsSubscriptionExpanded(!isSubscriptionExpanded);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        triggerHaptic('SUCCESS');
                        setIsSubscriptionExpanded(!isSubscriptionExpanded);
                      }
                    }}
                    className={cn(
                      "w-full flex items-center justify-between p-2 rounded-xl transition-colors group cursor-pointer select-none",
                      theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800"
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn("p-2 rounded-lg shrink-0", (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') ? "pro-crown-btn-animated" : subscriptionTier === 'pro' ? "premium-gold-btn" : "bg-emerald-500/10")}>
                        <Crown className={cn("w-4 h-4", (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') ? "pro-crown-icon-animated" : subscriptionTier === 'pro' ? "premium-gold-icon" : (skin === 'stalker' ? "text-[#4ade80]" : "text-emerald-500"))} />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn(
                          "text-sm font-bold uppercase tracking-widest",
                          theme === 'light' ? "text-zinc-600" : "text-zinc-300"
                        )}>
                          {skin === 'stalker' 
                            ? (lang === 'ua' ? 'ЛІЦЕНЗІЯ КПК' : 'PDA MEMBERSHIP')
                            : (lang === 'ua' ? 'ЧЛЕНСТВО' : 'MEMBERSHIP')}
                        </span>
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider",
                          (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                            ? "pro-crown-badge-animated border-transparent" 
                            : subscriptionTier === 'pro' 
                              ? "premium-gold-badge border-transparent" 
                              : "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
                        )}>
                          {(subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') ? 'PREMIUM' : subscriptionTier === 'pro' ? 'PRO' : 'FREE'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {subscriptionTier !== 'premium' && subscriptionTier !== 'premium_pro' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActivateTrialToken();
                          }}
                          className={cn(
                            "px-2.5 sm:px-3 py-1 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 shadow-xs hover:scale-105 active:scale-95",
                            skin === 'stalker'
                              ? "bg-[#ef4444] hover:bg-[#ef4444]/80 text-black font-mono"
                              : "bg-emerald-500 hover:bg-emerald-400 text-black"
                          )}
                          title={lang === 'ua' ? 'Активувати пробний період PREMIUM' : 'Activate PREMIUM Trial'}
                        >
                          <Gift className="w-3.5 h-3.5 animate-pulse shrink-0" />
                          <span>{lang === 'ua' ? 'Спробувати PREMIUM' : 'Activate Trial'}</span>
                        </button>
                      )}
                      <ChevronDown className={cn("w-5 h-5 text-zinc-500 transition-transform duration-300", isSubscriptionExpanded && "rotate-180")} />
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {isSubscriptionExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-4 pt-1 pb-4 px-1">
                          {/* 2-Day Premium Trial Banner */}
                          {subscriptionTier !== 'premium' && subscriptionTier !== 'premium_pro' && !isTrialActive && (
                            <div className={cn(
                              "p-3.5 rounded-xl border flex flex-col sm:flex-row items-center gap-3 justify-between transition-all",
                              skin === 'stalker'
                                ? "bg-black border-[#ef4444] text-[#ef4444] font-mono"
                                : "bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400"
                            )}>
                              <div className="flex items-center gap-2.5 text-center sm:text-left">
                                <Gift className="w-5 h-5 shrink-0 animate-pulse text-emerald-500" />
                                <div>
                                  <p className="text-xs font-black uppercase tracking-wider">
                                    {lang === 'ua' ? "БЕЗКОШТОВНИЙ PREMIUM НА 2 ДНІ!" : "FREE 2-DAY PREMIUM TRIAL!"}
                                  </p>
                                  <p className="text-[10px] opacity-90 leading-tight mt-0.5">
                                    {lang === 'ua' 
                                      ? "Тестуйте професійні нутрієнти, розшифровку E-добавок та HealthKit!" 
                                      : "Test professional insights, E-additives scanning & health integrations!"}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={handleActivateTrialToken}
                                className={cn(
                                  "px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shrink-0 cursor-pointer shadow-xs",
                                  skin === 'stalker'
                                    ? "bg-[#ef4444] hover:bg-[#ef4444]/80 text-black"
                                    : "bg-emerald-500 hover:bg-emerald-600 text-black"
                                )}
                              >
                                {lang === 'ua' ? "ОТРИМАТИ" : "CLAIM TOKEN"}
                              </button>
                            </div>
                          )}

                          {/* Tier Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            
                            {/* Free Tier Card */}
                            <div className={cn(
                              "p-4 rounded-xl border flex flex-col justify-between transition-all relative overflow-hidden",
                              subscriptionTier === 'free'
                                ? "bg-zinc-100/80 dark:bg-zinc-950 border-emerald-500 shadow-sm ring-1 ring-emerald-500/30"
                                : "bg-zinc-50/40 dark:bg-zinc-900/10 border-zinc-200 dark:border-zinc-800/60 opacity-85 hover:opacity-100",
                              skin === 'stalker' && "font-mono border-[#4ade80]/30 text-[#4ade80]"
                            )}>
                              <div>
                                <div className="flex items-center justify-between gap-1 mb-2">
                                  <span className="px-2 py-0.5 rounded-md bg-zinc-200/60 dark:bg-zinc-800/60 text-[9px] font-black uppercase tracking-wider text-zinc-600 dark:text-zinc-300">
                                    {skin === 'stalker' ? 'ROOKIE' : 'CORE'}
                                  </span>
                                  {subscriptionTier === 'free' && (
                                    <span className="text-[9px] font-black text-emerald-500 uppercase tracking-wider animate-pulse flex items-center gap-1">
                                      <Crown className="w-3 h-3 text-emerald-500" />
                                      {lang === 'ua' ? "АКТИВНИЙ" : "ACTIVE"}
                                    </span>
                                  )}
                                </div>
                                <h5 className="font-extrabold text-base sm:text-lg">
                                  {skin === 'stalker' ? (lang === 'ua' ? "Зелена Ряска" : "Rookie PDA") : (lang === 'ua' ? "Базовий" : "Free Tier")}
                                </h5>
                                <p className="text-xl font-black mt-1 text-[var(--text)]">
                                  {skin === 'stalker' ? "0 RU" : "$0"}
                                  <span className="text-[10px] font-normal text-zinc-500">/{lang === 'ua' ? 'міс' : 'month'}</span>
                                </p>
                                
                                <ul className="space-y-2 mt-4 text-[10px] text-zinc-500 dark:text-zinc-400">
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Базовий журнал їжі (фото, голос, текст)" : "Daily Multi-Modal Meal Logger (photo/voice/text)"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Макронутрієнти (білки, вуглеводи, жири, кава)" : "Nutrient tracking (Protein, Carbs, Fats, Caffeine)"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Базова аналітика та ШІ Бро-Коуч" : "Nutritional Chart Breakdowns & AI Bro Coach"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Базові теми та підсвічування" : "Basic skins & illumination"}</span>
                                  </li>
                                </ul>
                              </div>

                              <div className="mt-5">
                                {subscriptionTier === 'free' ? (
                                  <div className="w-full text-center py-2 bg-emerald-500/10 text-emerald-500 text-[10px] font-bold rounded-lg border border-emerald-500/20 uppercase tracking-widest flex items-center justify-center gap-1.5">
                                    <Crown className="w-3.5 h-3.5 text-emerald-500" />
                                    {lang === 'ua' ? "Поточний тариф" : "Current Plan"}
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      triggerHaptic('SUCCESS');
                                      setSubscriptionTier('free');
                                      localStorage.setItem('subscription_tier', 'free');
                                      syncSettings({ subscriptionTier: 'free' });
                                      toast.success(lang === 'ua' ? "Повернуто на безкоштовний тариф" : "Switched to Free Tier");
                                    }}
                                    className="w-full py-2 bg-zinc-200 hover:bg-zinc-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-[10px] font-black rounded-lg transition-colors uppercase tracking-widest text-[var(--text)]"
                                  >
                                    {lang === 'ua' ? "Перейти" : "Switch Plan"}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* PRO Tier Card ($9.99) */}
                            <div className={cn(
                              "p-4 rounded-xl border flex flex-col justify-between transition-all relative overflow-hidden",
                              subscriptionTier === 'pro'
                                ? "bg-zinc-100/80 dark:bg-zinc-950 border-amber-500 shadow-sm ring-1 ring-amber-500/30"
                                : "bg-zinc-50/40 dark:bg-zinc-900/10 border-zinc-200 dark:border-zinc-800/60 opacity-85 hover:opacity-100",
                              skin === 'stalker' && "font-mono border-[#4ade80]/30 text-[#4ade80]"
                            )}>
                              <div className={cn(
                                "absolute top-0 right-0 font-black text-[7px] px-2 py-0.5 rounded-bl uppercase tracking-widest flex items-center gap-1",
                                subscriptionTier === 'pro' ? "premium-gold-badge text-black" : "bg-amber-500 text-black"
                              )}>
                                <Crown className={cn("w-2.5 h-2.5", subscriptionTier === 'pro' && "premium-gold-icon")} />
                                {lang === 'ua' ? "ПОПУЛЯРНИЙ" : "POPULAR"}
                              </div>
                              <div>
                                <div className="flex items-center justify-between gap-1 mb-2">
                                  <span className={cn(
                                    "px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider",
                                    subscriptionTier === 'pro' ? "premium-gold-badge text-black" : "bg-amber-500/10 text-amber-500"
                                  )}>
                                    {skin === 'stalker' ? 'STALKER' : 'PROACTIVE'}
                                  </span>
                                  {subscriptionTier === 'pro' && (
                                    <span className="text-[9px] font-black text-amber-500 uppercase tracking-wider animate-pulse flex items-center gap-1">
                                      <Crown className="w-3 h-3 text-amber-400" />
                                      {lang === 'ua' ? "АКТИВНИЙ" : "ACTIVE"}
                                    </span>
                                  )}
                                </div>
                                <h5 className="font-extrabold text-base sm:text-lg">
                                  {skin === 'stalker' ? (lang === 'ua' ? "Ведомий Зони" : "Stalker License") : (lang === 'ua' ? "PRO" : "PRO")}
                                </h5>
                                <p className="text-xl font-black mt-1 text-[var(--text)]">
                                  {skin === 'stalker' ? "99 RU" : "$9.99"}
                                  <span className="text-[10px] font-normal text-zinc-500">/{lang === 'ua' ? 'міс' : 'month'}</span>
                                </p>
                                
                                <ul className="space-y-2 mt-4 text-[10px] text-zinc-500 dark:text-zinc-400">
                                  <li className="flex items-start gap-1.5 font-bold text-amber-500">
                                    <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Все з безкоштовного тарифу, плюс:" : "Everything in Free, plus:"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Детальний аналіз мікронутрієнтів" : "Micronutrients & vitamins breakdown"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Синхрон з HealthKit / Google Fit" : "Google Fit / Health Integration"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Розумний кошик для покупок" : "Smart Grocery Planner"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Атмосферні преміум теми оформлення" : "Atmospheric design skins & CRT filters"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Таймер етапного голодування" : "Intermittent Fasting Tracker"}</span>
                                  </li>
                                </ul>
                              </div>

                              <div className="mt-5">
                                {subscriptionTier === 'pro' ? (
                                  <div className="w-full text-center py-2 premium-gold-btn text-amber-500 text-[10px] font-bold rounded-lg border uppercase tracking-widest flex items-center justify-center gap-1.5">
                                    <Crown className="w-3.5 h-3.5 premium-gold-icon" />
                                    {lang === 'ua' ? "Поточний тариф" : "Current Plan"}
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleUpgradeSubscription('pro')}
                                    className="w-full py-2 premium-gold-badge hover:brightness-110 text-black text-[10px] font-black rounded-lg transition-all uppercase tracking-widest cursor-pointer shadow-md flex items-center justify-center gap-1.5"
                                  >
                                    <Crown className="w-3.5 h-3.5" />
                                    {lang === 'ua' ? "Активувати PRO" : "Upgrade to PRO"}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* PREMIUM Tier Card ($24.99) */}
                            <div className={cn(
                              "p-4 rounded-xl border flex flex-col justify-between transition-all relative overflow-hidden",
                              (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro')
                                ? "bg-zinc-100/80 dark:bg-zinc-950 border-purple-500 shadow-sm ring-1 ring-purple-500/30"
                                : "bg-zinc-50/40 dark:bg-zinc-900/10 border-zinc-200 dark:border-zinc-800/60 opacity-85 hover:opacity-100",
                              skin === 'stalker' && "font-mono border-[#4ade80]/30 text-[#4ade80]"
                            )}>
                              <div className={cn(
                                "absolute top-0 right-0 font-black text-[7px] px-2 py-0.5 rounded-bl uppercase tracking-widest flex items-center gap-1",
                                (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') ? "pro-crown-badge-animated text-white" : "bg-purple-500 text-white"
                              )}>
                                <Crown className={cn("w-2.5 h-2.5", (subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') && "pro-crown-icon-animated")} />
                                {lang === 'ua' ? "ЕКСКЛЮЗИВ PREMIUM" : "BIO-HACKING PREMIUM"}
                              </div>
                              <div>
                                <div className="flex items-center justify-between gap-1 mb-2">
                                  <span className="px-2 py-0.5 rounded-md bg-purple-500/10 text-[9px] font-black uppercase tracking-wider text-purple-500">
                                    {skin === 'stalker' ? 'LEGEND' : 'BIO-HACKING'}
                                  </span>
                                  {(subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') && (
                                    <span className="text-[9px] font-black text-purple-500 uppercase tracking-wider animate-pulse flex items-center gap-1">
                                      <Check className="w-3 h-3" />
                                      {lang === 'ua' ? "АКТИВНИЙ" : "ACTIVE"}
                                    </span>
                                  )}
                                </div>
                                <h5 className="font-extrabold text-base sm:text-lg">
                                  {skin === 'stalker' ? (lang === 'ua' ? "Легенда Зони" : "Legend PDA") : (lang === 'ua' ? "PREMIUM" : "PREMIUM")}
                                </h5>
                                <p className="text-xl font-black mt-1 text-[var(--text)]">
                                  {skin === 'stalker' ? "249 RU" : "$24.99"}
                                  <span className="text-[10px] font-normal text-zinc-500">/{lang === 'ua' ? 'міс' : 'month'}</span>
                                </p>
                                
                                <ul className="space-y-2 mt-4 text-[10px] text-zinc-500 dark:text-zinc-400">
                                  <li className="flex items-start gap-1.5 font-bold text-purple-500">
                                    <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Все з PRO тарифу, плюс:" : "Everything in PRO, plus:"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Персональний ШІ Нутрі-Агент" : "Personal Nutri-Agent for groceries"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Глибокий аналіз біомаркерів (Gemini Pro)" : "Deep Bio Analytics (Gemini Pro)"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Детектор токсинів E та алергенів" : "E-Numbers Decoder & Safe Choice"}</span>
                                  </li>
                                  <li className="flex items-start gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{lang === 'ua' ? "Маркери старіння AGEs та Health ROI" : "Longevity Engine & AGEs Markers"}</span>
                                  </li>
                                </ul>
                              </div>

                              <div className="mt-5">
                                {(subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') ? (
                                  <div className="w-full text-center py-2 bg-purple-500/10 text-purple-500 text-[10px] font-bold rounded-lg border border-purple-500/20 uppercase tracking-widest">
                                    {lang === 'ua' ? "Поточний тариф" : "Current Plan"}
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleUpgradeSubscription('premium')}
                                    className="w-full py-2 bg-purple-500 hover:bg-purple-600 text-white text-[10px] font-black rounded-lg transition-colors uppercase tracking-widest cursor-pointer shadow-md"
                                  >
                                    {lang === 'ua' ? "Активувати PREMIUM" : "Upgrade to PREMIUM"}
                                  </button>
                                )}
                              </div>
                            </div>

                          </div>

                          {(subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'premium_pro') && (
                            <div className="pt-3 flex justify-center">
                              <button
                                type="button"
                                onClick={() => {
                                  sounds.playPop();
                                  triggerHaptic('SUCCESS');
                                  setIsBillingModalOpen(true);
                                }}
                                className="w-full sm:w-auto px-4 py-2.5 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 border border-zinc-200 dark:border-zinc-700 cursor-pointer shadow-xs"
                              >
                                <ExternalLink className="w-3.5 h-3.5 text-emerald-500" />
                                {lang === 'ua' ? "Керувати підпискою та рахунками" : "Manage Subscription & Billing"}
                              </button>
                            </div>
                          )}

                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Personal Data Section */}
                <div className="space-y-4">
                  <button
                    type="button"
                    onClick={() => setIsPersonalDataExpanded(!isPersonalDataExpanded)}
                    className={cn(
                      "w-full flex items-center justify-between p-2 rounded-xl transition-colors group",
                      theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-emerald-500/10 p-2 rounded-lg">
                        <UserCircle className="w-4 h-4 text-emerald-500" />
                      </div>
                      <span className={cn(
                        "text-sm font-bold uppercase tracking-widest",
                        theme === 'light' ? "text-zinc-600" : "text-zinc-300"
                      )}>
                        {t.personalData || 'Personal Data'}
                      </span>
                    </div>
                    <ChevronDown className={cn("w-5 h-5 text-zinc-500 transition-transform duration-300", isPersonalDataExpanded && "rotate-180")} />
                  </button>

                  <AnimatePresence initial={false}>
                    {isPersonalDataExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-6 pt-2 pb-4 px-1">
                          {/* Name field */}
                          <div className="space-y-2 relative">
                            <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                              {lang === 'ua' ? "Ваше ім'я" : "Your Name"}
                            </label>
                            <div className="relative">
                              <User className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                              <input 
                                type="text" 
                                name="userName"
                                value={onboardingName} 
                                onChange={(e) => {
                                  setOnboardingName(e.target.value);
                                  setUserName(e.target.value);
                                }}
                                onBlur={(e) => {
                                  saveGoals(rawGoals, e.target.value, true);
                                }}
                                placeholder={lang === 'ua' ? "Введіть ваше ім'я" : "Enter your name"} 
                                className={cn(
                                  "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                  theme === 'light' 
                                    ? "bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 shadow-sm" 
                                    : "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                                )} 
                              />
                            </div>
                          </div>

                          {/* Email field (Account email) */}
                          <div className="space-y-2 relative">
                            <div className="flex items-center justify-between">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                                {lang === 'ua' ? "Електронна пошта" : "Email Address"}
                              </label>
                              <button
                                type="button"
                                onClick={() => {
                                  handleLogout();
                                }}
                                className="text-xs font-bold text-emerald-500 hover:text-emerald-400 underline transition-colors cursor-pointer flex items-center gap-1"
                              >
                                <LogOut className="w-3 h-3" />
                                <span>{lang === 'ua' ? 'Змінити акаунт / Вхід' : 'Switch Account / Sign In'}</span>
                              </button>
                            </div>
                            <div className="relative">
                              <Mail className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                              <input 
                                type="text" 
                                readOnly 
                                value={
                                  effectiveUserEmail && (effectiveUserEmail.startsWith('guest_') || effectiveUserEmail.includes('@mainnutrition.app'))
                                    ? (lang === 'ua' ? `Гостьова сесія (${effectiveUserEmail})` : `Guest Session (${effectiveUserEmail})`)
                                    : (effectiveUserEmail || (lang === 'ua' ? "Не вказано" : "Not provided"))
                                } 
                                className={cn(
                                  "w-full border rounded-xl pl-10 pr-4 py-2 outline-none cursor-default opacity-80 font-medium",
                                  theme === 'light' 
                                    ? "bg-zinc-100/80 border-zinc-200 text-zinc-600 shadow-xs" 
                                    : "bg-zinc-800/60 border-zinc-700/80 text-zinc-400"
                                )} 
                              />
                            </div>
                          </div>

                          {/* Gender field */}
                          <div className="space-y-2 relative">
                            <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                              {t.onboardingGenderLabel || 'Gender'}
                            </label>
                            <div className="flex gap-3">
                              <button
                                type="button"
                                onClick={() => {
                                  setOnboardingGender('male');
                                  saveGoals(rawGoals, undefined, true, { gender: 'male' });
                                }}
                                className={cn(
                                  "flex-1 py-2 rounded-xl border font-bold transition-all",
                                  onboardingGender === 'male' 
                                    ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                                    : theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"
                                )}
                              >
                                {t.onboardingMaleLabel || 'Male'}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setOnboardingGender('female');
                                  saveGoals(rawGoals, undefined, true, { gender: 'female' });
                                }}
                                className={cn(
                                  "flex-1 py-2 rounded-xl border font-bold transition-all",
                                  onboardingGender === 'female' 
                                    ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                                    : theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"
                                )}
                              >
                                {t.onboardingFemaleLabel || 'Female'}
                              </button>
                            </div>
                          </div>

                          {/* Metrics Grid (Weight, Height, Age) */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                                {t.onboardingWeightLabel || 'Weight (kg)'}
                              </label>
                              <div className="relative">
                                <Scale className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  type="number" 
                                  value={onboardingWeight}
                                  onChange={(e) => setOnboardingWeight(e.target.value)}
                                  onBlur={(e) => {
                                    saveGoals(rawGoals, undefined, true, { weight: e.target.value });
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )}
                                />
                              </div>
                            </div>

                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                                {t.onboardingHeightLabel || 'Height (cm)'}
                              </label>
                              <div className="relative">
                                <Activity className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  type="number" 
                                  value={onboardingHeight}
                                  onChange={(e) => setOnboardingHeight(e.target.value)}
                                  onBlur={(e) => {
                                    saveGoals(rawGoals, undefined, true, { height: e.target.value });
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )}
                                />
                              </div>
                            </div>

                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                                {t.onboardingAgeLabel || 'Age'}
                              </label>
                              <div className="relative">
                                <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  type="number" 
                                  value={onboardingAge}
                                  onChange={(e) => setOnboardingAge(e.target.value)}
                                  onBlur={(e) => {
                                    saveGoals(rawGoals, undefined, true, { age: e.target.value });
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Activity Level and Goal */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                                {t.onboardingActivityLabel || 'Activity Level'}
                              </label>
                              <div className="relative">
                                <Zap className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <select
                                  value={onboardingActivity}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value);
                                    setOnboardingActivity(val);
                                    saveGoals(rawGoals, undefined, true, { activity: val });
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-10 py-2 focus:border-emerald-500 outline-none transition-colors appearance-none",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )}
                                >
                                  <option value={1.2}>{t.activitySedentary || 'Sedentary'}</option>
                                  <option value={1.375}>{t.activityLight || 'Lightly Active'}</option>
                                  <option value={1.55}>{t.activityModerate || 'Moderately Active'}</option>
                                  <option value={1.725}>{t.activityActive || 'Very Active'}</option>
                                  <option value={1.9}>{t.activityVeryActive || 'Extra Active'}</option>
                                </select>
                                <ChevronDown className="absolute right-4 top-3 w-4 h-4 text-zinc-500 pointer-events-none" />
                              </div>
                            </div>

                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                                {t.onboardingGoalLabel || 'Goal'}
                              </label>
                              <div className="relative">
                                <Target className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <select
                                  value={onboardingGoal}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setOnboardingGoal(val as any);
                                    saveGoals(rawGoals, undefined, true, { goal: val });
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-10 py-2 focus:border-emerald-500 outline-none transition-colors appearance-none",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )}
                                >
                                  <option value="lose">{t.onboardingGoalLose || 'Lose weight'}</option>
                                  <option value="maintain">{t.onboardingGoalMaintain || 'Maintain'}</option>
                                  <option value="gain">{t.onboardingGoalGain || 'Gain weight'}</option>
                                </select>
                                <ChevronDown className="absolute right-4 top-3 w-4 h-4 text-zinc-500 pointer-events-none" />
                              </div>
                            </div>
                          </div>

                          {/* How much weight to lose (displayed conditionally) */}
                          {onboardingGoal === 'lose' && (
                            <div className="space-y-2 relative">
                              <div className="flex items-center gap-1.5">
                                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                                  {t.weightToLose || 'How much weight to lose (kg)'}
                                </label>
                                
                                {/* Tooltip */}
                                <div className="relative group">
                                  <button
                                    type="button"
                                    className="text-zinc-400 hover:text-emerald-500 transition-colors p-0.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800"
                                  >
                                    <HelpCircle className="w-4 h-4" />
                                  </button>
                                  
                                  {/* Tooltip Popup */}
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-zinc-950 text-white text-xs rounded-xl shadow-xl border border-zinc-800 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200 z-50 text-center font-medium leading-relaxed">
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-950"></div>
                                    {t.loseWeightTooltip}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="relative">
                                <Scale className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  type="number" 
                                  value={weightToLose}
                                  onChange={(e) => setWeightToLose(e.target.value)}
                                  onBlur={(e) => {
                                    saveGoals(rawGoals, undefined, true, { weightToLose: e.target.value });
                                  }}
                                  placeholder="e.g., 5"
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )}
                                />
                              </div>
                            </div>
                          )}

                          {/* Auto recalculate button for daily targets */}
                          <div className="pt-2">
                            <button
                              type="button"
                              onClick={() => {
                                // Perform target recalculation
                                const weightVal = parseFloat(onboardingWeight) || 70;
                                const heightVal = parseFloat(onboardingHeight) || 170;
                                const ageVal = parseFloat(onboardingAge) || 25;
                                const s = onboardingGender === 'male' ? 5 : -161;
                                
                                const bmr = (10 * weightVal) + (6.25 * heightVal) - (5 * ageVal) + s;
                                const tdee = bmr * onboardingActivity;
                                
                                let targetCalories = tdee;
                                if (onboardingGoal === 'lose') targetCalories -= 500;
                                if (onboardingGoal === 'gain') targetCalories += 500;
                                
                                targetCalories = Math.round(targetCalories);
                                
                                let pRatio = 0.3;
                                let cRatio = 0.4;
                                let fRatio = 0.3;

                                if (onboardingGoal === 'lose') {
                                  pRatio = 0.4;
                                  cRatio = 0.3;
                                  fRatio = 0.3;
                                } else if (onboardingGoal === 'gain') {
                                  pRatio = 0.25;
                                  cRatio = 0.5;
                                  fRatio = 0.25;
                                }

                                const p = Math.round((targetCalories * pRatio) / 4);
                                const c = Math.round((targetCalories * cRatio) / 4);
                                const f = Math.round((targetCalories * fRatio) / 9);
                                
                                const recalculatedGoals = {
                                  targetMode: rawGoals.targetMode || 'daily',
                                  calories: targetCalories,
                                  protein: p,
                                  carbs: c,
                                  fat: f,
                                  water_ml: Math.round(weightVal * 33),
                                  sugar_g: Math.round(targetCalories * 0.1 / 4),
                                  fiber_g: Math.round(targetCalories / 1000 * 14),
                                  sodium_mg: rawGoals.sodium_mg || 2300,
                                  caffeine_mg: rawGoals.caffeine_mg || 400,
                                };

                                setGoals(recalculatedGoals);
                                saveGoals(recalculatedGoals, undefined, true);
                                toast.success(lang === 'ua' ? "Цілі перераховано за новими параметрами тіла!" : "Targets recalculated based on your body metrics!");
                              }}
                              className="w-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 py-2 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 border border-emerald-500/20"
                            >
                              <RefreshCw className="w-4 h-4 animate-pulse" />
                              {lang === 'ua' ? "Розрахувати за метриками тіла" : "Recalculate from body metrics"}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Goals & Targets Section */}
                <div className="space-y-4">
                  <button
                    type="button"
                    onClick={() => setIsGoalsExpanded(!isGoalsExpanded)}
                    className={cn(
                      "w-full flex items-center justify-between p-2 rounded-xl transition-colors group",
                      theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-emerald-500/10 p-2 rounded-lg">
                        <Target className="w-4 h-4 text-emerald-500" />
                      </div>
                      <span className={cn(
                        "text-sm font-bold uppercase tracking-widest",
                        theme === 'light' ? "text-zinc-600" : "text-zinc-300"
                      )}>{t.goalsAndTargets}</span>
                    </div>
                    <ChevronDown className={cn("w-5 h-5 text-zinc-500 transition-transform duration-300", isGoalsExpanded && "rotate-180")} />
                  </button>

                  <AnimatePresence initial={false}>
                    {isGoalsExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div 
                          key={`goals-container-${rawGoals.calories}-${rawGoals.protein}-${rawGoals.carbs}-${rawGoals.fat}-${rawGoals.sugar_g}-${rawGoals.fiber_g}-${rawGoals.sodium_mg}-${rawGoals.water_ml}-${rawGoals.caffeine_mg}-${rawGoals.targetMode}`}
                          className="space-y-6 pt-2 pb-4 px-1"
                        >
                          <div className="space-y-2 relative">
                            <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{lang === 'ua' ? "Режим цілей" : "Target Mode"}</label>
                            <div className="relative">
                              <Activity className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                              <select 
                                name="targetMode" 
                                defaultValue={rawGoals.targetMode || 'daily'} 
                                onChange={(e) => {
                                  if (e.currentTarget.form) {
                                    handleGoalsFormChange(e.currentTarget.form);
                                  }
                                }}
                                className={cn(
                                  "w-full border rounded-xl pl-10 pr-10 py-2 focus:border-emerald-500 outline-none transition-colors appearance-none",
                                  theme === 'light'
                                    ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                    : "bg-zinc-800 border-zinc-700 text-white"
                                )}
                              >
                                <option value="daily">{lang === 'ua' ? "Щоденні цілі" : "Daily Targets"}</option>
                                <option value="weekly">{lang === 'ua' ? "Тижневі цілі" : "Weekly Targets"}</option>
                              </select>
                              <ChevronDown className="absolute right-4 top-3 w-4 h-4 text-zinc-500 pointer-events-none" />
                            </div>
                            <p className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">{lang === 'ua' ? "Встановлює, чи числа нижче є щоденними або щотижневими" : "Sets if the numbers below are daily or weekly"}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.calories}</label>
                              <div className="relative">
                                <Flame className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  name="calories" 
                                  type="number" 
                                  defaultValue={rawGoals.calories} 
                                  onBlur={(e) => {
                                    if (e.currentTarget.form) {
                                      handleGoalsFormChange(e.currentTarget.form);
                                    }
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )} 
                                />
                              </div>
                            </div>
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.protein} (g)</label>
                              <div className="relative">
                                <Beef className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  name="protein" 
                                  type="number" 
                                  defaultValue={rawGoals.protein} 
                                  onBlur={(e) => {
                                    if (e.currentTarget.form) {
                                      handleGoalsFormChange(e.currentTarget.form);
                                    }
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )} 
                                />
                              </div>
                            </div>
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.carbs} (g)</label>
                              <div className="relative">
                                <Wheat className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  name="carbs" 
                                  type="number" 
                                  defaultValue={rawGoals.carbs} 
                                  onBlur={(e) => {
                                    if (e.currentTarget.form) {
                                      handleGoalsFormChange(e.currentTarget.form);
                                    }
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )} 
                                />
                              </div>
                            </div>
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.fat} (g)</label>
                              <div className="relative">
                                <Milk className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  name="fat" 
                                  type="number" 
                                  defaultValue={rawGoals.fat} 
                                  onBlur={(e) => {
                                    if (e.currentTarget.form) {
                                      handleGoalsFormChange(e.currentTarget.form);
                                    }
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )} 
                                />
                              </div>
                            </div>
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.sugar} (g)</label>
                              <div className="relative">
                                <Candy className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  name="sugar_g" 
                                  type="number" 
                                  defaultValue={rawGoals.sugar_g} 
                                  onBlur={(e) => {
                                    if (e.currentTarget.form) {
                                      handleGoalsFormChange(e.currentTarget.form);
                                    }
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )} 
                                />
                              </div>
                            </div>
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.fiber} (g)</label>
                              <div className="relative">
                                <Carrot className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  name="fiber_g" 
                                  type="number" 
                                  defaultValue={rawGoals.fiber_g} 
                                  onBlur={(e) => {
                                    if (e.currentTarget.form) {
                                      handleGoalsFormChange(e.currentTarget.form);
                                    }
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )} 
                                />
                              </div>
                            </div>
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.sodium} (mg)</label>
                              <div className="relative">
                                <CircleDot className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  name="sodium_mg" 
                                  type="number" 
                                  defaultValue={rawGoals.sodium_mg} 
                                  onBlur={(e) => {
                                    if (e.currentTarget.form) {
                                      handleGoalsFormChange(e.currentTarget.form);
                                    }
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )} 
                                />
                              </div>
                            </div>
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.waterGoal} (ml)</label>
                              <div className="relative">
                                <Droplets className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  name="water_ml" 
                                  type="number" 
                                  defaultValue={rawGoals.water_ml} 
                                  onBlur={(e) => {
                                    if (e.currentTarget.form) {
                                      handleGoalsFormChange(e.currentTarget.form);
                                    }
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )} 
                                />
                              </div>
                            </div>
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.caffeineLimit || (lang === 'ua' ? "Ліміт кофеїну (мг)" : "Caffeine Limit (mg)")}</label>
                              <div className="relative">
                                <Coffee className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
                                <input 
                                  name="caffeine_mg" 
                                  type="number" 
                                  defaultValue={rawGoals.caffeine_mg} 
                                  onBlur={(e) => {
                                    if (e.currentTarget.form) {
                                      handleGoalsFormChange(e.currentTarget.form);
                                    }
                                  }}
                                  className={cn(
                                    "w-full border rounded-xl pl-10 pr-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                    theme === 'light'
                                      ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                      : "bg-zinc-800 border-zinc-700 text-white"
                                  )} 
                                />
                              </div>
                            </div>
                            <div className="space-y-2 relative">
                              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">{lang === 'ua' ? "Формат дати" : "Date Format"}</label>
                              <select 
                                value={dateFormat} 
                                onChange={(e) => {
                                  const newFormat = e.target.value as 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'D MMMM YYYY';
                                  setDateFormat(newFormat);
                                  localStorage.setItem('date_format', newFormat);
                                  syncSettings({ dateFormat: newFormat });
                                }}
                                className={cn(
                                  "w-full border rounded-xl px-4 py-2 focus:border-emerald-500 outline-none transition-colors",
                                  theme === 'light'
                                    ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                                    : "bg-zinc-800 border-zinc-700 text-white"
                                )}
                              >
                                <option value="DD/MM/YYYY">DD.MM.YYYY (25.07.2026)</option>
                                <option value="MM/DD/YYYY">MM.DD.YYYY (07.25.2026)</option>
                                <option value="YYYY-MM-DD">YYYY-MM-DD (2026-07-25)</option>
                                <option value="D MMMM YYYY">25 July 2026 ({lang === 'ua' ? '25 липня 2026' : '25 July 2026'})</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Dietary Planner & Budget Settings Section */}
                <div className="hidden sm:block space-y-4 pt-2 border-t border-[var(--card-border)]">
                  <button
                    type="button"
                    onClick={() => setIsDietaryPlanExpanded(!isDietaryPlanExpanded)}
                    className={cn(
                      "w-full flex items-center justify-between p-2 rounded-xl transition-colors group",
                      theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-emerald-500/10 p-2 rounded-lg">
                        <Utensils className="w-4 h-4 text-emerald-500" />
                      </div>
                      <span className={cn(
                        "text-sm font-bold uppercase tracking-widest",
                        theme === 'light' ? "text-zinc-600" : "text-zinc-300"
                      )}>
                        {lang === 'ua' ? 'ДІЄТА ТА БЮДЖЕТ' : 'Diet & Budget'}
                      </span>
                    </div>
                    <ChevronDown className={cn("w-5 h-5 text-zinc-500 transition-transform duration-300", isDietaryPlanExpanded && "rotate-180")} />
                  </button>

                  <AnimatePresence initial={false}>
                    {isDietaryPlanExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-4 pt-2 pb-4 px-1">
                          {/* Pre-made message / helper above the input fields */}
                          <div className={cn(
                            "p-3 rounded-xl border text-xs space-y-2 mb-2",
                            theme === 'light' ? "bg-emerald-50/50 border-emerald-100 text-emerald-800" : "bg-emerald-950/10 border-emerald-900/30 text-emerald-400"
                          )}>
                            <p className="font-bold flex items-center gap-1">
                              <Sparkles className="w-3.5 h-3.5" />
                              {lang === 'ua' ? "Заплануйте свій прийом їжі заздалегідь" : "Plan your meal ahead"}
                            </p>
                            <p className="leading-relaxed opacity-90">
                              {lang === 'ua' 
                                ? "Ви можете миттєво запитати ШІ про меню на основі ваших налаштувань:" 
                                : "You can instantly ask the AI for a curated meal plan based on your settings:"}
                            </p>
                            <div className="flex gap-2 pt-1 flex-wrap">
                              <button
                                type="button"
                                onClick={() => {
                                  setView('chat');
                                  const promptText = lang === 'ua' 
                                    ? `Порадь мені тижневі варіанти страв на основі моїх уподобань "${foodPreferences || 'не вказано'}" та бюджету "${foodBudget || 'не вказано'}".`
                                    : `Suggest me a weekly food suggestions plan based on my preferences "${foodPreferences || 'none specified'}" and budget "${foodBudget || 'none specified'}".`;
                                  setTimeout(() => {
                                    setChatInput(promptText);
                                  }, 300);
                                }}
                                className="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-[var(--brand-green)] text-black transition-colors border border-[var(--brand-green)]/30"
                              >
                                {lang === 'ua' ? "🥦 Тижневе меню" : "🥦 Weekly Plan"}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setView('chat');
                                  const promptText = lang === 'ua' 
                                    ? `Порадь мені місячні варіанти страв на основі моїх уподобань "${foodPreferences || 'не вказано'}" та бюджету "${foodBudget || 'не вказано'}".`
                                    : `Suggest me a monthly food suggestions plan based on my preferences "${foodPreferences || 'none specified'}" and budget "${foodBudget || 'none specified'}".`;
                                  setTimeout(() => {
                                    setChatInput(promptText);
                                  }, 300);
                                }}
                                className="px-2.5 py-1 rounded-lg text-[10px] font-bold bg-[var(--brand-green)] text-black transition-colors border border-[var(--brand-green)]/30"
                              >
                                {lang === 'ua' ? "📅 Місячне меню" : "📅 Monthly Plan"}
                              </button>
                            </div>
                          </div>

                          {/* Food Preferences field */}
                          <div className="space-y-2 relative">
                            <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 block">
                              <span>{lang === 'ua' ? "Харчові уподобання" : "Dietary Preferences"}</span>
                            </label>
                            <input 
                              type="text" 
                              name="foodPreferences"
                              value={foodPreferences} 
                              onChange={(e) => setFoodPreferences(e.target.value)}
                              onBlur={(e) => {
                                saveGoals(rawGoals, undefined, true, { foodPreferences: e.target.value });
                              }}
                              placeholder={lang === 'ua' ? "Напр. Вегетаріанське, високий білок" : "e.g. Vegetarian, high-protein, clean eat"} 
                              className={cn(
                                "w-full border rounded-xl px-4 py-2 focus:border-emerald-500 outline-none transition-colors placeholder:italic",
                                theme === 'light' 
                                  ? "bg-white border-zinc-200 text-zinc-900 shadow-sm" 
                                  : "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                              )} 
                            />
                          </div>

                          {/* Budget field */}
                          <div className="space-y-2 relative">
                            <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 block">
                              <span>{lang === 'ua' ? "Бюджет на харчування" : "Dietary Budget"}</span>
                            </label>
                            <input 
                              type="text" 
                              name="foodBudget"
                              value={foodBudget} 
                              onChange={(e) => setFoodBudget(e.target.value)}
                              onBlur={(e) => {
                                saveGoals(rawGoals, undefined, true, { foodBudget: e.target.value });
                              }}
                              placeholder={lang === 'ua' ? "Напр. 2000 грн на тиждень" : "e.g. $50/week, $200/month"} 
                              className={cn(
                                "w-full border rounded-xl px-4 py-2 focus:border-emerald-500 outline-none transition-colors placeholder:italic",
                                theme === 'light' 
                                  ? "bg-white border-zinc-200 text-zinc-900 shadow-sm" 
                                  : "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                              )} 
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Personalization Section */}
                <div className="space-y-4 pt-2 border-t border-[var(--card-border)]">
                  <button
                    type="button"
                    onClick={() => setIsPersonalizationExpanded(!isPersonalizationExpanded)}
                    className={cn(
                      "w-full flex items-center justify-between p-2 rounded-xl transition-colors group",
                      theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-emerald-500/10 p-2 rounded-lg">
                        <Sparkles className="w-4 h-4 text-emerald-500" />
                      </div>
                      <span className={cn(
                        "text-sm font-bold uppercase tracking-widest",
                        theme === 'light' ? "text-zinc-600" : "text-zinc-300"
                      )}>{t.personalization}</span>
                    </div>
                    <ChevronDown className={cn("w-5 h-5 text-zinc-500 transition-transform duration-300", isPersonalizationExpanded && "rotate-180")} />
                  </button>

                  <AnimatePresence initial={false}>
                    {isPersonalizationExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-4 pt-2 pb-4 px-1">

                          {/* Safe Choice Configuration */}
                          {subscriptionTier === 'free' ? (
                            <div className="p-4 rounded-2xl border border-emerald-500/10 bg-zinc-950/20 text-center relative overflow-hidden backdrop-blur-xs">
                              <Lock className="w-4 h-4 text-emerald-500 mx-auto mb-1 animate-bounce" />
                              <p className="font-extrabold text-[11px] text-white uppercase tracking-wider">{lang === 'ua' ? "АНАЛІТИКА E-КОДІВ ТА АЛЕРГЕНІВ (PRO / PREMIUM)" : "ADDITIVES & ALLERGENS (PRO / PREMIUM)"}</p>
                              <p className="text-[10px] text-zinc-400 mt-1 max-w-[280px] mx-auto">
                                {lang === 'ua' 
                                  ? "Перейдіть на тариф PRO або PREMIUM, щоб розблокувати інтелектуальну перевірку шкідливих добавок E та персоналізовані профілі безпеки харчування!" 
                                  : "Upgrade to PRO or PREMIUM to unlock advanced E-additive scanning, allergen tracking & custom dietary safe profiles!"}
                              </p>
                              <button 
                                type="button"
                                onClick={() => {
                                  setIsSubscriptionExpanded(true);
                                  scrollToSectionTop('subscription-card-settings', true);
                                }}
                                className="mt-2 px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-black text-[9px] font-black rounded-lg transition-colors cursor-pointer select-none uppercase tracking-wider"
                              >
                                {lang === 'ua' ? "Розблокувати PRO / PREMIUM" : "Unlock PRO / PREMIUM"}
                              </button>
                            </div>
                          ) : (
                            <SafeChoiceInlineConfig 
                              safeChoiceProfile={safeChoiceProfile} 
                              handleSafeChoiceSave={handleSafeChoiceSave} 
                              lang={lang}
                              theme={theme}
                              onOpenEDecoder={() => {
                                triggerHaptic('SUCCESS');
                                setEDecoderInitialQuery('');
                                setIsEDecoderOpen(true);
                              }}
                            />
                          )}

                          {/* Nutri-Agent Premium Configuration */}
                          {subscriptionTier === 'free' || subscriptionTier === 'pro' ? (
                            <div className="p-4 rounded-2xl border border-purple-500/10 bg-zinc-950/20 text-center relative overflow-hidden backdrop-blur-xs">
                              <Lock className="w-4 h-4 text-purple-500 mx-auto mb-1 animate-bounce" />
                              <p className="font-extrabold text-[11px] text-white uppercase tracking-wider">{lang === 'ua' ? "ШІ NUTRI-AGENT (PREMIUM)" : "AUTONOMOUS NUTRI-AGENT"}</p>
                              <p className="text-[10px] text-zinc-400 mt-1 max-w-[280px] mx-auto">
                                {lang === 'ua' 
                                  ? "Перейдіть на тариф PREMIUM, щоб активувати автоматичне замовлення продуктів у супермаркетах через ШІ агента!" 
                                  : "Upgrade to PREMIUM to enable autonomous supermarket order placement and grocery restocking!"}
                              </p>
                              <button 
                                type="button"
                                onClick={() => {
                                  setIsSubscriptionExpanded(true);
                                  scrollToSectionTop('subscription-card-settings', true);
                                }}
                                className="mt-2 px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white text-[9px] font-black rounded-lg transition-colors cursor-pointer select-none uppercase tracking-wider"
                              >
                                {lang === 'ua' ? "Розблокувати PREMIUM" : "Unlock PREMIUM"}
                              </button>
                            </div>
                          ) : (
                            <NutriAgentSettingsConfig 
                              lang={lang}
                              theme={theme}
                              userEmail={auth.currentUser?.email || undefined}
                            />
                          )}

                          {/* Drinks & Brands Collapsible Subsection */}
                          <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--card-border)] overflow-hidden transition-all">
                            <button
                              type="button"
                              onClick={() => setIsDrinksBrandsExpanded(!isDrinksBrandsExpanded)}
                              className={cn(
                                "w-full flex items-center justify-between p-4 sm:p-5 text-left transition-colors group",
                                theme === 'light' ? "hover:bg-zinc-50/80" : "hover:bg-zinc-800/40"
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-100 text-emerald-600" : "bg-zinc-800 text-emerald-400")}>
                                  <Droplets className="w-5 h-5 text-emerald-500" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-black uppercase tracking-wider text-[var(--text)]">
                                    {lang === 'ua' ? 'Напої та Бренди' : 'Drinks & Brands'}
                                  </h4>
                                  <p className="text-[11px] font-medium text-zinc-500">
                                    {lang === 'ua' ? 'Офіційні бренди (Pepsi, Coca-Cola, Red Bull, Monster, Pitbull) у виборі рідини' : 'Official brand logos (Pepsi, Coca-Cola, Red Bull, Monster, Pitbull) in Add Liquid'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2.5">
                                <span className="text-xs font-extrabold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                                  {selectedDrinkIds.length} / 10
                                </span>
                                <ChevronDown className={cn("w-5 h-5 text-zinc-500 transition-transform duration-300", isDrinksBrandsExpanded && "rotate-180")} />
                              </div>
                            </button>

                            <AnimatePresence initial={false}>
                              {isDrinksBrandsExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                                  className="overflow-hidden"
                                >
                                  <div className="p-4 sm:p-5 pt-0 space-y-4 border-t border-[var(--card-border)]/60">
                                    {/* Residence / Country Selector */}
                                    <div className="space-y-2 pt-3">
                                      <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 block">
                                        {lang === 'ua' ? 'Країна проживання (регіональні бренди):' : 'Residence (Regional Brands):'}
                                      </label>
                                      <div className="grid grid-cols-4 gap-2">
                                        {[
                                          { id: 'US', flag: '🇺🇸', label: 'US' },
                                          { id: 'UK', flag: '🇬🇧', label: 'UK' },
                                          { id: 'UA', flag: '🇺🇦', label: 'UA' },
                                          { id: 'GLOBAL', flag: '🌐', label: 'Global' }
                                        ].map((res) => (
                                          <button
                                            key={res.id}
                                            type="button"
                                            onClick={() => setUserResidence(res.id as any)}
                                            className={cn(
                                              "flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl border text-xs font-bold transition-all",
                                              userResidence === res.id
                                                ? "bg-[var(--brand-green)] border-[var(--brand-green)] text-black shadow-md shadow-emerald-500/20"
                                                : theme === 'light'
                                                  ? "bg-zinc-50 border-zinc-200 text-zinc-700 hover:bg-zinc-100"
                                                  : "bg-zinc-800/80 border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                                            )}
                                          >
                                            <span>{res.flag}</span>
                                            <span>{res.label}</span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* 18+ Energy Drink Policy Toggle */}
                                    <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 space-y-2">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <Zap className="w-4 h-4 text-amber-500 shrink-0" />
                                          <span className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                                            {lang === 'ua' ? 'Дозволити енергетичні напої (18+)' : 'Allow Energy Drinks (18+ Policy)'}
                                          </span>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setIsAge18Plus(!isAge18Plus)}
                                          className={cn(
                                            "w-11 h-6 rounded-full transition-colors relative shrink-0",
                                            isAge18Plus ? "bg-amber-500" : "bg-zinc-300 dark:bg-zinc-700"
                                          )}
                                        >
                                          <div className={cn(
                                            "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                                            isAge18Plus ? "left-6" : "left-1"
                                          )} />
                                        </button>
                                      </div>
                                      <p className="text-[10px] text-zinc-500 leading-relaxed">
                                        {lang === 'ua'
                                          ? 'Відповідно до правил та застережень щодо кофеїну, енергетики (Red Bull, Monster, Non Stop, Burn, Pitbull) доступні для користувачів 18+ років.'
                                          : 'In accordance with caffeine guidelines, energy drinks (Red Bull, Monster, Non Stop, Burn, Pitbull) require 18+ age verification.'}
                                      </p>
                                    </div>

                                    {/* Quick Selected Drinks Cards Grid (up to 10) */}
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                                          {lang === 'ua' ? 'Оберіть до 10 напоїв.' : 'Select up to 10 drinks.'}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => setIsAddCustomDrinkModalOpen(true)}
                                          className="text-[11px] font-extrabold text-emerald-500 hover:text-emerald-600 flex items-center gap-1"
                                        >
                                          <Plus className="w-3.5 h-3.5" />
                                          {lang === 'ua' ? 'Свій' : 'Custom'}
                                        </button>
                                      </div>

                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1">
                                        {availableBrandDrinks.map((drink) => {
                                          const isSelected = selectedDrinkIds.includes(drink.id);
                                          const isEnergyRestricted = drink.isEnergyDrink && !isAge18Plus;

                                          return (
                                            <div
                                              key={drink.id}
                                              onClick={() => {
                                                if (isSelected) {
                                                  setSelectedDrinkIds(selectedDrinkIds.filter(id => id !== drink.id));
                                                } else {
                                                  if (selectedDrinkIds.length >= 10) {
                                                    toast.error(
                                                      lang === 'ua' 
                                                        ? 'Максимум 10 напоїв! Спочатку зніміть виділення з іншого.' 
                                                        : 'Max 10 drinks selected! Deselect another first.',
                                                      { className: 'rounded-2xl font-bold' }
                                                    );
                                                    return;
                                                  }
                                                  if (isEnergyRestricted) {
                                                    setPendingEnergyDrink(drink);
                                                    setShowAgeVerificationModal(true);
                                                    return;
                                                  }
                                                  setSelectedDrinkIds([...selectedDrinkIds, drink.id]);
                                                }
                                              }}
                                              className={cn(
                                                "flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer group",
                                                isSelected
                                                  ? "bg-emerald-500/10 border-emerald-500/40 shadow-sm"
                                                  : theme === 'light'
                                                    ? "bg-zinc-50/80 border-zinc-200 hover:bg-zinc-100"
                                                    : "bg-zinc-800/50 border-zinc-700 hover:bg-zinc-800"
                                              )}
                                            >
                                              <div className="flex items-center gap-2.5 min-w-0">
                                                <BrandLogoCircle logoType={drink.logoType} name={drink.name} sizePx={36} />
                                                <div className="min-w-0">
                                                  <div className="flex items-center gap-1.5">
                                                    <span className="text-xs font-bold text-[var(--text)] truncate">{drink.name}</span>
                                                    {drink.isEnergyDrink && (
                                                      <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-red-500/20 text-red-500 shrink-0">
                                                        18+
                                                      </span>
                                                    )}
                                                  </div>
                                                  <div className="text-[10px] text-zinc-500 font-medium truncate">
                                                    {drink.defaultVolumeMl}ml • {drink.caloriesPer100ml} kcal/100ml
                                                    {drink.caffeinePer100ml > 0 && ` • ${drink.caffeinePer100ml}mg caff`}
                                                  </div>
                                                </div>
                                              </div>

                                              <div className={cn(
                                                "w-6 h-6 rounded-full flex items-center justify-center border transition-all shrink-0 ml-2",
                                                isSelected
                                                  ? "bg-emerald-500 border-emerald-500 text-white"
                                                  : "border-zinc-300 dark:border-zinc-600"
                                              )}>
                                                {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>

                  {/* Language Selection */}
                  <div className="flex items-center justify-between bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                        <Search className="w-4 h-4 text-zinc-500" />
                      </div>
                      <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">{t.language || 'Language'}</span>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        type="button"
                        onClick={() => lang !== 'en-US' && changeLang('en-US')}
                        className={cn(
                          "w-10 h-10 flex items-center justify-center rounded-xl border transition-all",
                          lang === 'en-US' 
                            ? "bg-[var(--brand-green)] border-[var(--brand-green)] text-black shadow-lg shadow-[var(--brand-green)]/20" 
                            : theme === 'light' 
                              ? "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 shadow-sm" 
                              : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"
                        )}
                        title="US"
                      >
                        <span className="text-xl">🇺🇸</span>
                      </button>
                      <button 
                        type="button"
                        onClick={() => lang !== 'en-GB' && changeLang('en-GB')}
                        className={cn(
                          "w-10 h-10 flex items-center justify-center rounded-xl border transition-all",
                          lang === 'en-GB' 
                            ? "bg-[var(--brand-green)] border-[var(--brand-green)] text-black shadow-lg shadow-[var(--brand-green)]/20" 
                            : theme === 'light' 
                              ? "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 shadow-sm" 
                              : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"
                        )}
                        title="UK"
                      >
                        <span className="text-xl">🇬🇧</span>
                      </button>
                      <button 
                        type="button"
                        onClick={() => lang !== 'ua' && changeLang('ua')}
                        className={cn(
                          "w-10 h-10 flex items-center justify-center rounded-xl border transition-all",
                          lang === 'ua' 
                            ? "bg-[var(--brand-green)] border-[var(--brand-green)] text-black shadow-lg shadow-[var(--brand-green)]/20" 
                            : theme === 'light' 
                              ? "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 shadow-sm" 
                              : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"
                        )}
                        title="UA"
                      >
                        <span className="text-xl">🇺🇦</span>
                      </button>
                    </div>
                  </div>

                  {/* Time Format Toggle */}
                  <div className="flex items-center justify-between bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                        <Calendar className="w-4 h-4 text-zinc-500" />
                      </div>
                      <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">{lang === 'ua' ? "Формат часу" : "Time Format"}</span>
                    </div>
                    <div className={cn("flex items-center border rounded-full p-1", theme === 'light' ? "bg-zinc-50 border-zinc-200" : "bg-zinc-800 border-zinc-700")}>
                      <button 
                        type="button"
                        onClick={() => toggleTimeFormat('12h')}
                        className={cn(
                          "px-3 py-1 rounded-full text-[10px] font-bold transition-all",
                          timeFormat === '12h' 
                            ? "bg-[var(--brand-green)] text-black shadow-lg shadow-[var(--brand-green)]/20" 
                            : theme === 'light' 
                              ? "text-zinc-500 hover:text-zinc-900" 
                              : "text-zinc-400 hover:text-zinc-200"
                        )}
                      >
                        12h
                      </button>
                      <button 
                        type="button"
                        onClick={() => toggleTimeFormat('24h')}
                        className={cn(
                          "px-3 py-1 rounded-full text-[10px] font-bold transition-all",
                          timeFormat === '24h' 
                            ? "bg-[var(--brand-green)] text-black shadow-lg shadow-[var(--brand-green)]/20" 
                            : theme === 'light' 
                              ? "text-zinc-500 hover:text-zinc-900" 
                              : "text-zinc-400 hover:text-zinc-200"
                        )}
                      >
                        24h
                      </button>
                    </div>
                  </div>

                  {/* Vibe Toggle */}
                  <div className="flex items-center justify-between bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn("p-2 rounded-lg flex-shrink-0", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                        <Zap className="w-4 h-4 text-zinc-500" />
                      </div>
                      <span className="text-sm font-bold uppercase tracking-widest text-zinc-500 whitespace-nowrap overflow-hidden text-ellipsis">{t.vibe}</span>
                    </div>
                    <div className={cn("flex items-center border rounded-full p-1", theme === 'light' ? "bg-zinc-50 border-zinc-200" : "bg-zinc-800 border-zinc-700")}>
                      <button 
                        type="button"
                        onClick={() => toggleVibe('standard')}
                        className={cn(
                          "px-3 py-1 rounded-full text-[10px] font-bold transition-all",
                          vibe === 'standard' 
                            ? "bg-[var(--brand-green)] text-black shadow-lg shadow-[var(--brand-green)]/20" 
                            : theme === 'light' 
                              ? "text-zinc-500 hover:text-zinc-900" 
                              : "text-zinc-400 hover:text-zinc-200"
                        )}
                      >
                        {t.standard}
                      </button>
                      <button 
                        type="button"
                        onClick={() => toggleVibe('casual')}
                        className={cn(
                          "px-3 py-1 rounded-full text-[10px] font-bold transition-all",
                          vibe === 'casual' 
                            ? "bg-[var(--brand-green)] text-black shadow-lg shadow-[var(--brand-green)]/20" 
                            : theme === 'light' 
                              ? "text-zinc-500 hover:text-zinc-900" 
                              : "text-zinc-400 hover:text-zinc-200"
                        )}
                      >
                        {t.casual}
                      </button>
                    </div>
                  </div>

                  {/* Theme Toggle */}
                  <div className="flex items-center justify-between bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn("p-2 rounded-lg flex-shrink-0", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                        <Settings className="w-4 h-4 text-zinc-500" />
                      </div>
                      <span className="text-sm font-bold uppercase tracking-widest text-zinc-500 whitespace-nowrap overflow-hidden text-ellipsis">{t.theme}</span>
                    </div>
                    <div className={cn("flex items-center border rounded-full p-1", theme === 'light' ? "bg-zinc-50 border-zinc-200" : "bg-zinc-800 border-zinc-700")}>
                      <button 
                        type="button"
                        onClick={() => {
                          setTheme('light');
                          setAutoThemeEnabled(false);
                          localStorage.setItem('auto_theme_enabled', 'false');
                          syncSettings({ theme: 'light', autoThemeEnabled: false });
                          sounds.playSave();
                          toast.success(t.settingsSaved, {
                            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                            className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                          });
                        }}
                        className={cn(
                          "px-4 py-1.5 rounded-full text-[10px] font-bold transition-all flex items-center gap-2",
                          theme === 'light' 
                            ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                            : "text-zinc-400 hover:text-zinc-200"
                        )}
                      >
                        {t.light}
                      </button>
                      <button 
                        type="button"
                        onClick={() => {
                          setTheme('dark');
                          setAutoThemeEnabled(false);
                          localStorage.setItem('auto_theme_enabled', 'false');
                          syncSettings({ theme: 'dark', autoThemeEnabled: false });
                          sounds.playSave();
                          toast.success(t.settingsSaved, {
                            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                            className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                          });
                        }}
                        className={cn(
                          "px-4 py-1.5 rounded-full text-[10px] font-bold transition-all flex items-center gap-2",
                          theme === 'dark' 
                            ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20" 
                            : "text-zinc-500 hover:text-zinc-900"
                        )}
                      >
                        {t.dark}
                      </button>
                    </div>
                  </div>

                  {/* Auto Theme (Day/Night) Toggle */}
                  <div className="flex flex-col gap-2 bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={cn("p-2 rounded-lg flex-shrink-0", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                          {theme === 'light' ? <Sun className="w-4 h-4 text-zinc-500" /> : <Moon className="w-4 h-4 text-zinc-500" />}
                        </div>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-bold uppercase tracking-widest text-zinc-500 whitespace-nowrap overflow-hidden text-ellipsis">
                            {lang === 'ua' ? "Автоматична тема" : "Auto Theme"}
                          </span>
                          <button 
                            type="button"
                            onClick={() => setShowAutoThemeTooltip(!showAutoThemeTooltip)}
                            className="text-zinc-500 hover:text-emerald-500 transition-colors"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          const newVal = !autoThemeEnabled;
                          setAutoThemeEnabled(newVal);
                          localStorage.setItem('auto_theme_enabled', String(newVal));
                          syncSettings({ autoThemeEnabled: newVal });
                          
                          sounds.playSave();
                          toast.success(lang === 'ua' ? 'Автоматичну тему оновлено!' : 'Auto theme updated!', {
                            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                            className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                          });
                        }}
                        className={cn(
                          "w-12 h-6 rounded-full transition-colors relative flex-shrink-0",
                          autoThemeEnabled ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                          autoThemeEnabled ? "left-7" : "left-1"
                        )} />
                      </button>
                    </div>
                    
                    <AnimatePresence>
                      {showAutoThemeTooltip && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <p className="text-[10px] text-zinc-500 leading-relaxed pt-2 border-t border-[var(--card-border)] mt-2">
                            {lang === 'ua' 
                              ? "Автоматично перемикає тему додатку між світлою та темною залежно від часу доби у вашому регіоні." 
                              : "Automatically switches the application between light and dark themes based on the time of day in your region."}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {autoThemeEnabled && (
                      <div className="mt-1 flex flex-col min-[380px]:flex-row min-[380px]:items-center justify-between gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-500 border-t border-[var(--card-border)] pt-2">
                        <span className="flex items-center gap-1">
                          {autoThemeLocationActive 
                            ? (lang === 'ua' ? "За вашою локацією 📍" : "By your location 📍")
                            : (lang === 'ua' ? "Стандартний розклад 🕒" : "Standard schedule 🕒")}
                        </span>
                        <span className="text-zinc-500 whitespace-nowrap flex items-center gap-1">
                          🌅 {autoThemeSunrise} | 🌇 {autoThemeSunset}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Illumination Toggle */}
                  <div className="flex flex-col gap-4 bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                          <Waves className="w-4 h-4 text-zinc-500" />
                        </div>
                        <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">{t.illumination}</span>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          const newVal = !illumination;
                          setIllumination(newVal);
                          localStorage.setItem('app_illumination', String(newVal));
                          syncSettings({ illumination: newVal, illuminationColor });
                          sounds.playSave();
                          toast.success(
                            newVal 
                              ? (lang.startsWith('ua') ? 'Ілюмінацію увімкнено!' : 'Illumination enabled!')
                              : (lang.startsWith('ua') ? 'Ілюмінацію вимкнено!' : 'Illumination disabled!'),
                            {
                              icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                              className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                            }
                          );
                        }}
                        className={cn(
                          "w-12 h-6 rounded-full transition-colors relative flex-shrink-0",
                          illumination ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                          illumination ? "left-7" : "left-1"
                        )} />
                      </button>
                    </div>

                    {illumination && (
                      <div className="flex flex-col gap-3 pt-2 border-t border-[var(--card-border)]">
                        <button 
                          type="button"
                          onClick={() => setShowColorPicker(!showColorPicker)}
                          className="flex items-center justify-between group"
                        >
                          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 group-hover:text-zinc-500 transition-colors flex items-center gap-2">
                            {t.illuminationColor || 'Illumination Color'}
                            <ChevronDown className={cn("w-3 h-3 transition-transform duration-300", showColorPicker ? "rotate-180" : "")} />
                          </span>
                          <div 
                            className="w-3 h-3 rounded-full shadow-[0_0_8px_rgba(var(--illumination-color),0.8)]"
                            style={{ backgroundColor: ILLUMINATION_COLORS.find(c => c.id === previewIlluminationColor)?.color }}
                          />
                        </button>
                        
                        {showColorPicker && (
                          <motion.div 
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex flex-col gap-4 py-2"
                          >
                            <div className="flex gap-3 mx-auto">
                              {ILLUMINATION_COLORS.map((c) => (
                                <button
                                  type="button"
                                  key={c.id}
                                  onClick={() => {
                                    setPreviewIlluminationColor(c.id);
                                    sounds.playPop();
                                  }}
                                  className={cn(
                                    "w-7 h-7 rounded-full border-2 transition-all relative group/color",
                                    previewIlluminationColor === c.id 
                                      ? "border-white scale-110" 
                                      : "border-transparent hover:scale-105"
                                  )}
                                  style={{ 
                                    backgroundColor: c.color,
                                    boxShadow: `0 0 15px ${c.color}66`
                                  }}
                                  title={c.name}
                                >
                                  {/* Neon Glow Layer */}
                                  <div 
                                    className="absolute inset-0 rounded-full opacity-50 blur-[4px] group-hover/color:opacity-80 transition-opacity"
                                    style={{ backgroundColor: c.color }}
                                  />
                                  
                                  {previewIlluminationColor === c.id && (
                                    <div className="absolute inset-0 flex items-center justify-center z-10">
                                      <div className={cn(
                                        "w-1.5 h-1.5 rounded-full shadow-[0_0_5px_white]",
                                        c.id === 'glacier' ? "bg-zinc-900" : "bg-white"
                                      )} />
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                setIlluminationColor(previewIlluminationColor);
                                localStorage.setItem('app_illumination', String(illumination));
                                localStorage.setItem('app_illumination_color', previewIlluminationColor);
                                syncSettings({ 
                                  illumination: illumination,
                                  illuminationColor: previewIlluminationColor 
                                });
                                sounds.playSave();
                                toast.success(t.settingsSaved, {
                                  icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                                  className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                                });
                                setShowColorPicker(false);
                              }}
                              className="mx-auto px-6 py-2 bg-emerald-500 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-emerald-500/20 hover:scale-105 transition-all flex items-center gap-2"
                            >
                              <Save className="w-3 h-3" />
                              {t.save || (lang === 'ua' ? 'Зберегти' : 'Save')}
                            </button>
                          </motion.div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Haptic Feedback (Vibrate on Log) Toggle */}
                  <div className="flex items-center justify-between bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn("p-2 rounded-lg flex-shrink-0", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                        <Activity className="w-4 h-4 text-zinc-500 animate-pulse" />
                      </div>
                      <span className="text-sm font-bold uppercase tracking-widest text-zinc-500 whitespace-nowrap overflow-hidden text-ellipsis">{t.vibrationOnLogging}</span>
                    </div>
                    <button 
                      type="button"
                      onClick={() => {
                        const newVal = !vibrateOnLogging;
                        setVibrateOnLogging(newVal);
                        localStorage.setItem('vibrate_on_logging', String(newVal));
                        syncSettings({ vibrateOnLogging: newVal });
                        triggerHaptic('SUCCESS', newVal);
                        sounds.playSave();
                        toast.success(t.settingsSaved, {
                          icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                          className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                        });
                      }}
                      className={cn(
                        "w-12 h-6 rounded-full transition-colors relative",
                        vibrateOnLogging ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                      )}
                    >
                      <div className={cn(
                        "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                        vibrateOnLogging ? "left-7" : "left-1"
                      )} />
                    </button>
                  </div>

                  {/* Daily Reminder Toggle */}
                  <div className="flex flex-col gap-4 bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={cn("p-2 rounded-lg flex-shrink-0", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                          <Bell className="w-4 h-4 text-zinc-500" />
                        </div>
                        <span className="text-sm font-bold uppercase tracking-widest text-zinc-500 whitespace-nowrap overflow-hidden text-ellipsis">{t.dailyReminder}</span>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          const newVal = !reminderEnabled;
                          setReminderEnabled(newVal);
                          localStorage.setItem('reminder_enabled', String(newVal));
                          syncSettings({ reminderEnabled: newVal });
                          sounds.playSave();
                          toast.success(t.settingsSaved, {
                            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                            className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                          });
                        }}
                        className={cn(
                          "w-12 h-6 rounded-full transition-colors relative",
                          reminderEnabled ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                          reminderEnabled ? "left-7" : "left-1"
                        )} />
                      </button>
                    </div>
                    
                    {reminderEnabled && (
                      <div className="flex items-center justify-between pt-2 border-t border-[var(--card-border)]">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-zinc-500" />
                          <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.reminderTime}</span>
                        </div>
                        <select 
                          value={reminderHour}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setReminderHour(val);
                            localStorage.setItem('reminder_hour', String(val));
                            syncSettings({ reminderHour: val });
                            sounds.playSave();
                            toast.success(t.settingsSaved, {
                              icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                              className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                            });
                          }}
                          className={cn(
                            "border rounded-lg px-2 py-1 text-xs font-bold outline-none focus:border-emerald-500 transition-colors",
                            theme === 'light'
                              ? "bg-white border-zinc-200 text-zinc-900 shadow-sm"
                              : "bg-zinc-800 border-zinc-700 text-white"
                          )}
                        >
                          {Array.from({ length: 24 }).map((_, i) => (
                            <option key={i} value={i}>
                              {timeFormat === '24h' 
                                ? `${i.toString().padStart(2, '0')}:00` 
                                : `${i === 0 ? 12 : i > 12 ? i - 12 : i} ${i >= 12 ? 'PM' : 'AM'}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Meal Review Toggle */}
                  <div className="flex flex-col gap-2 bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                          <CheckCircle2 className="w-4 h-4 text-zinc-500" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">{t.mealReview || 'Meal Review'}</span>
                          <button 
                            type="button"
                            onClick={() => setShowMealReviewTooltip(!showMealReviewTooltip)}
                            className="text-zinc-500 hover:text-emerald-500 transition-colors"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          const newVal = !enableMealReview;
                          setEnableMealReview(newVal);
                          localStorage.setItem('enable_meal_review', String(newVal));
                          syncSettings({ enableMealReview: newVal });
                          sounds.playSave();
                          toast.success(t.settingsSaved, {
                            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                            className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                          });
                        }}
                        className={cn(
                          "w-12 h-6 rounded-full transition-colors relative",
                          enableMealReview ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                          enableMealReview ? "left-7" : "left-1"
                        )} />
                      </button>
                    </div>
                    
                    <AnimatePresence>
                      {showMealReviewTooltip && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <p className="text-[10px] text-zinc-500 leading-relaxed pt-2 border-t border-[var(--card-border)] mt-2">
                            {t.mealReviewTooltip}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* AI Meal Suggestions Toggle */}
                  <div className="flex flex-col gap-2 bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                          <Sparkles className="w-4 h-4 text-emerald-500" />
                        </div>
                        <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">{t.aiSuggestionsToggle}</span>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          const newVal = !enableAiSuggestions;
                          setEnableAiSuggestions(newVal);
                          localStorage.setItem('enable_ai_suggestions', String(newVal));
                          syncSettings({ enableAiSuggestions: newVal });
                          sounds.playSave();
                          toast.success(t.settingsSaved, {
                            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                            className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                          });
                        }}
                        className={cn(
                          "w-12 h-6 rounded-full transition-colors relative",
                          enableAiSuggestions ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                          enableAiSuggestions ? "left-7" : "left-1"
                        )} />
                      </button>
                    </div>
                  </div>

                  {/* Deficiency Alerts Toggle */}
                  <div className="flex flex-col gap-2 bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                          <AlertCircle className="w-4 h-4 text-emerald-500" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">
                            {lang.startsWith('ua') ? 'Попередження дефіциту' : 'Deficiency Alerts'}
                          </span>
                          <button 
                            type="button"
                            onClick={() => setShowDeficiencyAlertTooltip(!showDeficiencyAlertTooltip)}
                            className="text-zinc-500 hover:text-emerald-500 transition-colors"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          const newVal = !enableHealthDeficiencyAlert;
                          setEnableHealthDeficiencyAlert(newVal);
                          localStorage.setItem('enable_health_deficiency_alert', String(newVal));
                          syncSettings({ enableHealthDeficiencyAlert: newVal });
                          sounds.playSave();
                          toast.success(t.settingsSaved, {
                            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                            className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                          });
                        }}
                        className={cn(
                          "w-12 h-6 rounded-full transition-colors relative flex-shrink-0",
                          enableHealthDeficiencyAlert ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                          enableHealthDeficiencyAlert ? "left-7" : "left-1"
                        )} />
                      </button>
                    </div>
                    
                    <AnimatePresence>
                      {showDeficiencyAlertTooltip && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <p className="text-[10px] text-zinc-500 leading-relaxed pt-2 border-t border-[var(--card-border)] mt-2">
                            {lang.startsWith('ua') ? 'Авто-повідомлення про можливий дефіцит нутрієнтів за 30 днів.' : 'Proactive alerts about potential nutrient deficiencies over 30 days.'}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Ignore Meal Time Logging Toggle */}
                  <div className="flex flex-col gap-2 bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                          <Clock className="w-4 h-4 text-emerald-500" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">{t.ignoreMealTimeLogging}</span>
                          <button 
                            type="button"
                            onClick={() => setShowIgnoreMealTimeTooltip(!showIgnoreMealTimeTooltip)}
                            className="text-zinc-500 hover:text-emerald-500 transition-colors"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          const newVal = !ignoreMealTimeLogging;
                          setIgnoreMealTimeLogging(newVal);
                          localStorage.setItem('ignore_meal_time_logging', String(newVal));
                          syncSettings({ ignoreMealTimeLogging: newVal });
                          sounds.playSave();
                          toast.success(t.settingsSaved, {
                            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                            className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                          });
                        }}
                        className={cn(
                          "w-12 h-6 rounded-full transition-colors relative flex-shrink-0",
                          ignoreMealTimeLogging ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                          ignoreMealTimeLogging ? "left-7" : "left-1"
                        )} />
                      </button>
                    </div>

                    <AnimatePresence>
                      {showIgnoreMealTimeTooltip && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <p className="text-[10px] text-zinc-500 leading-relaxed pt-2 border-t border-[var(--card-border)] mt-2">
                            {lang.startsWith('ua') 
                              ? 'Дозволяє реєструвати прийоми їжі в будь-який час доби без прив\'язки до стандартного часу сніданку, обіду чи вечері.' 
                              : 'Allows logging meals at any time of day without enforcing breakfast, lunch, or dinner timeframes.'}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Fasting Widget Toggle */}
                  <div className="flex flex-col gap-2 bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                          <Clock className="w-4 h-4 text-zinc-500" />
                        </div>
                        <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">
                          {lang.startsWith('ua') ? 'Віджет голодування' : 'Fasting Widget'}
                        </span>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          const newVal = !showFastingWidget;
                          setShowFastingWidget(newVal);
                          localStorage.setItem('show_fasting_widget', String(newVal));
                          syncSettings({ showFastingWidget: newVal });
                          sounds.playSave();
                          toast.success(
                            lang.startsWith('ua') ? 'Налаштування віджета збережено!' : 'Fasting widget settings saved!',
                            {
                              icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                              className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold",
                            }
                          );
                        }}
                        className={cn(
                          "w-12 h-6 rounded-full transition-colors relative flex-shrink-0",
                          showFastingWidget ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                          showFastingWidget ? "left-7" : "left-1"
                        )} />
                      </button>
                    </div>
                  </div>

                          {/* Sound Toggle */}
                          <div className="flex items-center justify-between bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)] relative">
                            <div className="flex items-center gap-3">
                              <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                                <Volume2 className="w-4 h-4 text-zinc-500" />
                              </div>
                              <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">{t.soundEffects}</span>
                            </div>
                            <div className="relative">
                              <button 
                                type="button"
                                onClick={() => {
                                  const nextVal = !soundEnabled;
                                  setSoundEnabled(nextVal);
                                  sounds.setEnabled(nextVal);
                                  sfx.setMuted(!nextVal);
                                  localStorage.setItem('pro_audio_enabled', String(nextVal));
                                  syncSettings({ soundEnabled: nextVal });
                                  if (nextVal) {
                                    sounds.playPop(true);
                                  }
                                }}
                                className={cn(
                                  "w-12 h-6 rounded-full transition-colors relative",
                                  soundEnabled ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                                )}
                              >
                                <div className={cn(
                                  "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                                  soundEnabled ? "left-7" : "left-1"
                                )} />
                              </button>
                            </div>
                          </div>

                          {isAdmin && (
                            <div className="flex items-center justify-between bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)] mt-4">
                              <div className="flex items-center gap-3">
                                <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                                  <Settings className="w-4 h-4 text-purple-500" />
                                </div>
                                <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">{lang === 'ua' ? "Перезапуск онбордингу" : "Restart Onboarding"}</span>
                              </div>
                              <button 
                                type="button"
                                onClick={async () => {
                                  const newVal = !showOnboarding;
                                  if (newVal) {
                                    localStorage.removeItem('onboarding_completed');
                                    if (user) {
                                      try {
                                        await setDocWithQueue(doc(db, 'users', user.uid), { onboardingCompleted: false }, { merge: true });
                                      } catch (e) {
                                        console.error(e);
                                      }
                                    }
                                    setShowOnboarding(true);
                                    setView('onboarding');
                                    toast.success(lang === 'ua' ? 'Онбординг перезапущено' : 'Onboarding restarted');
                                  } else {
                                    localStorage.setItem('onboarding_completed', 'true');
                                    if (user) {
                                      try {
                                        await setDocWithQueue(doc(db, 'users', user.uid), { onboardingCompleted: true }, { merge: true });
                                      } catch (e) {
                                        console.error(e);
                                      }
                                    }
                                    setShowOnboarding(false);
                                    toast.success(lang === 'ua' ? 'Стан онбордингу скинуто' : 'Onboarding state reset');
                                  }
                                }}
                                className={cn(
                                  "w-12 h-6 rounded-full transition-colors relative",
                                  showOnboarding ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                                )}
                              >
                                <div className={cn(
                                  "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                                  showOnboarding ? "left-7" : "left-1"
                                )} />
                              </button>
                            </div>
                          )}

                          {/* Daily Progress View Mode Selection */}
                          <div className="flex items-center justify-between bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
                            <div className="flex items-center gap-3">
                              <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
                                <LayoutTemplate className="w-4 h-4 text-zinc-500" />
                              </div>
                              <span className="text-sm font-bold uppercase tracking-widest text-zinc-500">
                                {lang === 'ua' ? 'РЕЖИМ ПРОГРЕСУ' : 'PROGRESS VIEW'}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <button 
                                type="button"
                                onClick={() => {
                                  const mode = 'horizontal';
                                  setDailyProgressViewMode(mode);
                                  localStorage.setItem('daily_progress_view_mode', mode);
                                  syncSettings({ dailyProgressViewMode: mode });
                                  sounds.playPop();
                                  triggerHaptic('SUCCESS');
                                }}
                                className={cn(
                                  "flex items-center justify-center gap-2 px-3 py-2 rounded-xl border font-bold transition-all",
                                  dailyProgressViewMode === 'horizontal' 
                                    ? "bg-[var(--brand-green)] border-[var(--brand-green)] text-black shadow-lg shadow-[var(--brand-green)]/20" 
                                    : theme === 'light' 
                                      ? "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 shadow-sm" 
                                      : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"
                                )}
                              >
                                <LayoutTemplate className="w-3.5 h-3.5" />
                              </button>
                              <button 
                                type="button"
                                onClick={() => {
                                  const mode = 'radial';
                                  setDailyProgressViewMode(mode);
                                  localStorage.setItem('daily_progress_view_mode', mode);
                                  syncSettings({ dailyProgressViewMode: mode });
                                  sounds.playPop();
                                  triggerHaptic('SUCCESS');
                                }}
                                className={cn(
                                  "flex items-center justify-center gap-2 px-3 py-2 rounded-xl border font-bold transition-all",
                                  dailyProgressViewMode === 'radial' 
                                    ? "bg-[var(--brand-green)] border-[var(--brand-green)] text-black shadow-lg shadow-[var(--brand-green)]/20" 
                                    : theme === 'light' 
                                      ? "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 shadow-sm" 
                                      : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"
                                )}
                              >
                                <CircleDot className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                           {/* Health Sync Configuration */}
                          {subscriptionTier === 'free' ? (
                            <div className="p-4 rounded-2xl border border-amber-500/10 bg-zinc-950/20 text-center relative overflow-hidden backdrop-blur-xs">
                              <Lock className="w-4 h-4 text-amber-500 mx-auto mb-1 animate-bounce" />
                              <p className="font-extrabold text-[11px] text-white uppercase tracking-wider">{lang === 'ua' ? "СИНХРО HEALTHKIT (PRO)" : "HEALTHKIT SYNC (PRO)"}</p>
                              <p className="text-[10px] text-zinc-400 mt-1 max-w-[280px] mx-auto">
                                {lang === 'ua' 
                                  ? "Оновіть до PRO, щоб синхронізувати споживання макронутрієнтів та води з Apple Health / Google Fit!" 
                                  : "Upgrade to PRO to synchronize your dietary intake & fluids with Apple Health / Google Fit!"}
                              </p>
                              <button 
                                type="button"
                                onClick={() => {
                                  setIsSubscriptionExpanded(true);
                                  scrollToSectionTop('subscription-card-settings', true);
                                }}
                                className="mt-2 px-3 py-1 bg-amber-500 hover:bg-amber-600 text-black text-[9px] font-black rounded-lg transition-colors cursor-pointer select-none uppercase tracking-wider"
                              >
                                {lang === 'ua' ? "Розблокувати PRO" : "Unlock PRO"}
                              </button>
                            </div>
                          ) : (
                            <HealthSyncConfig 
                              lang={lang} 
                              userId={auth.currentUser?.uid} 
                              theme={theme} 
                              onSyncNow={fetchGoogleFitData}
                              isSyncing={googleFitLoading}
                            />
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Permissions Section */}
                <div className="space-y-4 pt-2 border-t border-[var(--card-border)]">
                  <button
                    type="button"
                    onClick={() => setIsPermissionsExpanded(!isPermissionsExpanded)}
                    className={cn(
                      "w-full flex items-center justify-between p-2 rounded-xl transition-colors group",
                      theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-emerald-500/10 p-2 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      </div>
                      <span className={cn(
                        "text-sm font-bold uppercase tracking-widest",
                        theme === 'light' ? "text-zinc-600" : "text-zinc-300"
                      )}>{t.permissions || 'App Permissions'}</span>
                    </div>
                    <ChevronDown className={cn("w-5 h-5 text-zinc-500 transition-transform duration-300", isPermissionsExpanded && "rotate-180")} />
                  </button>

                  <AnimatePresence initial={false}>
                    {isPermissionsExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 pb-4 px-1">
                          <div className={cn(
                            "p-4 rounded-2xl border flex flex-col justify-between gap-3",
                            theme === 'light' ? "bg-white border-zinc-200 shadow-sm" : "bg-zinc-800 border-zinc-700"
                          )}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Camera className="w-4 h-4 text-zinc-500" />
                                <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.cameraPermission || 'Camera'}</span>
                              </div>
                              <div className={cn(
                                "px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-widest",
                                cameraPermissionStatus === 'granted' ? "bg-emerald-100 text-emerald-600" :
                                cameraPermissionStatus === 'denied' ? "bg-red-100 text-red-600" :
                                "bg-zinc-100 text-zinc-500"
                              )}>
                                {cameraPermissionStatus === 'granted' ? t.permissionGranted : 
                                 cameraPermissionStatus === 'denied' ? t.permissionDenied : 
                                 'Not Requested'}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={requestCameraPermission}
                              disabled={cameraPermissionStatus === 'granted'}
                              className={cn(
                                "w-full border py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-[var(--text)]",
                                theme === 'light'
                                  ? "bg-white border-zinc-200 hover:bg-zinc-50 shadow-sm"
                                  : "bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
                              )}
                            >
                              {t.requestPermission || 'Request Access'}
                            </button>
                          </div>

                          <div className={cn(
                            "p-4 rounded-2xl border flex flex-col justify-between gap-3",
                            theme === 'light' ? "bg-white border-zinc-200 shadow-sm" : "bg-zinc-800 border-zinc-700"
                          )}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Mic className="w-4 h-4 text-zinc-500" />
                                <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.micPermission || 'Microphone'}</span>
                              </div>
                              <div className={cn(
                                "px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-widest",
                                micPermissionStatus === 'granted' ? "bg-emerald-100 text-emerald-600" :
                                micPermissionStatus === 'denied' ? "bg-red-100 text-red-600" :
                                "bg-zinc-100 text-zinc-500"
                              )}>
                                {micPermissionStatus === 'granted' ? t.permissionGranted : 
                                 micPermissionStatus === 'denied' ? t.permissionDenied : 
                                 'Not Requested'}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={requestMicPermission}
                              disabled={micPermissionStatus === 'granted'}
                              className={cn(
                                "w-full border py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-[var(--text)]",
                                theme === 'light'
                                  ? "bg-white border-zinc-200 hover:bg-zinc-50 shadow-sm"
                                  : "bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
                              )}
                            >
                              {t.requestPermission || 'Request Access'}
                            </button>
                          </div>

                          <div className={cn(
                            "p-4 rounded-2xl border flex flex-col justify-between gap-3",
                            theme === 'light' ? "bg-white border-zinc-200 shadow-sm" : "bg-zinc-800 border-zinc-700"
                          )}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Bell className="w-4 h-4 text-zinc-500" />
                                <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                                  {lang === 'ua' ? "Сповіщення" : "Notifications"}
                                </span>
                              </div>
                              <div className={cn(
                                "px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-widest",
                                notifPermState === 'granted' ? "bg-emerald-100 text-emerald-600" :
                                notifPermState === 'denied' ? "bg-red-100 text-red-600" :
                                notifPermState === 'not-supported' ? "bg-amber-100 text-amber-600" :
                                "bg-zinc-100 text-zinc-500"
                              )}>
                                {notifPermState === 'granted' ? (lang === 'ua' ? "Дозволено" : "Granted") : 
                                 notifPermState === 'denied' ? (lang === 'ua' ? "Заблоковано" : "Blocked") : 
                                 notifPermState === 'not-supported' ? (lang === 'ua' ? "Н/Д" : "N/A") : 
                                 (lang === 'ua' ? "Не налаштовано" : "Not Requested")}
                              </div>
                            </div>
                            {notifPermState === 'granted' ? (
                              <button
                                type="button"
                                onClick={() => {
                                  sounds.playPop();
                                  toast.success(
                                    lang === 'ua' 
                                      ? "Тест заплановано через 3 секунди. Заблокуйте екран або згорніть додаток!" 
                                      : "Test notification scheduled in 3s. Close the app or lock your screen now!"
                                  );
                                  setTimeout(() => {
                                    const title = "MAIN Nutrition Fueling Reminder 🥗";
                                    const body = "Your device is fully configured for real-time coaching alerts!";
                                    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                                      navigator.serviceWorker.ready.then(reg => {
                                        reg.showNotification(title, {
                                          body: body,
                                          icon: '/icon-512.png',
                                          badge: '/public/icon.svg',
                                          vibrate: [100, 50, 100],
                                        } as any);
                                      }).catch(() => {
                                        new Notification(title, { body: body, icon: '/icon-512.png' });
                                      });
                                    } else {
                                      new Notification(title, { body: body, icon: '/icon-512.png' });
                                    }
                                  }, 3000);
                                }}
                                className={cn(
                                  "w-full border py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:border-emerald-500 transition-all text-[var(--text)]",
                                  theme === 'light'
                                    ? "bg-white border-zinc-200 hover:bg-zinc-50 shadow-sm"
                                    : "bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
                                )}
                              >
                                {lang === 'ua' ? "Тест (3с)" : "Test (3s)"}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!('Notification' in window)) return;
                                  try {
                                    sounds.playPop();
                                    const result = await Notification.requestPermission();
                                    setNotifPermState(result);
                                    if (user) {
                                      await setDocWithQueue(doc(db, 'users', user.uid), {
                                        notificationPermission: result,
                                        lastActiveAt: Date.now()
                                      }, { merge: true });
                                    }
                                    if (result === 'granted') {
                                      toast.success(lang === 'ua' ? "Сповіщення увімкнено успішно! 🥗" : "Notifications enabled successfully! 🥗");
                                      sounds.playConfetti();
                                    } else {
                                      toast.error(lang === 'ua' ? `Статус дозволу: ${result}` : `Permission state: ${result}`);
                                    }
                                  } catch (err) {
                                    console.error("Error requesting notifications permission:", err);
                                  }
                                }}
                                disabled={notifPermState === 'not-supported'}
                                className={cn(
                                  "w-full border py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-[var(--text)]",
                                  theme === 'light'
                                    ? "bg-white border-zinc-200 hover:bg-zinc-50 shadow-sm"
                                    : "bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
                                )}
                              >
                                {t.requestPermission || 'Request Access'}
                              </button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                  {/* Additional Section */}
                  <div className="space-y-4 pt-2 border-t border-[var(--card-border)]">
                    <button
                      type="button"
                      onClick={() => setIsAdditionalExpanded(!isAdditionalExpanded)}
                      className={cn(
                        "w-full flex items-center justify-between p-2 rounded-xl transition-colors group",
                        theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-emerald-500/10 p-2 rounded-lg">
                          <Sliders className="w-4 h-4 text-emerald-500" />
                        </div>
                        <span className={cn(
                          "text-sm font-bold uppercase tracking-widest",
                          theme === 'light' ? "text-zinc-600" : "text-zinc-300"
                        )}>
                          {lang === 'ua' ? 'Інше' : 'Additional'}
                        </span>
                      </div>
                      <ChevronDown className={cn("w-5 h-5 text-zinc-500 transition-transform duration-300", isAdditionalExpanded && "rotate-180")} />
                    </button>

                    <AnimatePresence initial={false}>
                      {isAdditionalExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-3 pt-2 pb-4 px-1">
                            {/* Submit a bug/idea Button */}
                            <button
                              type="button"
                              onClick={() => setIsBugModalOpen(true)}
                              className={cn(
                                "w-full border py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2",
                                theme === 'light'
                                  ? "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 shadow-sm"
                                  : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-white"
                              )}
                            >
                              <Bug className="w-4 h-4" />
                              {lang === 'ua' ? 'ПОМИЛКА АБО ІДЕЯ' : 'Submit a bug/idea'}
                            </button>

                            <button
                              type="button"
                              onClick={() => setShowExportConfirm(true)}
                              className={cn(
                                "w-full border py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2",
                                theme === 'light'
                                  ? "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 shadow-sm"
                                  : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-white"
                              )}
                            >
                              <Download className="w-4 h-4" />
                              {lang === 'ua' ? 'ЕКСПОРТ ДАНИХ' : 'Export Data'}
                            </button>

                            <button
                              type="button"
                              onClick={() => setShowResetConfirm(true)}
                              className={cn(
                                "w-full border py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2",
                                theme === 'light'
                                  ? "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 shadow-sm"
                                  : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-white"
                              )}
                            >
                              <Trash2 className="w-4 h-4" />
                              {t.resetHistory}
                            </button>

                            <button
                              type="button"
                              onClick={() => setShowDeleteAccountConfirm(true)}
                              className={cn(
                                "w-full border py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 cursor-pointer",
                                "bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/20 hover:border-red-500/50 shadow-sm"
                              )}
                            >
                              <ShieldAlert className="w-4 h-4 text-red-500" />
                              {lang === 'ua' ? 'ВИДАЛИТИ АКАУНТ' : 'Delete Account'}
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="pt-6 border-t border-[var(--card-border)]">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleLogout();
                      }}
                      className="w-full flex items-center justify-center gap-2 py-4 bg-zinc-500/10 hover:bg-zinc-500/20 text-zinc-600 dark:text-zinc-400 rounded-2xl font-bold uppercase tracking-widest transition-all border border-zinc-500/20 cursor-pointer hover:scale-[1.01] active:scale-[0.99]"
                    >
                      <RotateCcw className="w-5 h-5" />
                      {t.signOut || 'Sign Out'}
                    </button>
                  </div>


              </form>
              <p className="text-center text-xs text-zinc-400 mt-6">v.0.9.8(1)</p>
            </div>
          </motion.div>
        )}
      </main>

      {/* Export Confirmation Modal */}
      <AnimatePresence>
        {showExportConfirm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-8 w-full max-w-md space-y-6 shadow-2xl text-center"
            >
              <div className="bg-emerald-500/10 dark:bg-emerald-900/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto border border-emerald-500/20 dark:border-emerald-900/30">
                <Download className="w-8 h-8 text-emerald-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold">{lang === 'ua' ? 'Експортувати дані' : 'Export Data'}</h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed font-bold uppercase tracking-widest">
                  {lang === 'ua' ? 'Оберіть формат для завантаження:' : 'Select format to download:'}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button 
                  onClick={() => {
                    exportAsPDF();
                    setShowExportConfirm(false);
                  }}
                  className="flex flex-col items-center justify-center p-5 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:border-emerald-500 hover:bg-emerald-500/5 dark:hover:bg-emerald-500/5 transition-all group gap-2 text-[var(--text)] bg-transparent cursor-pointer"
                >
                  <FileText className="w-8 h-8 text-rose-500 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-bold uppercase tracking-wider">{lang === 'ua' ? 'Звіт PDF' : 'PDF Report'}</span>
                  <span className="text-[9px] text-zinc-500 font-bold tracking-tight uppercase leading-snug">
                    {lang === 'ua' ? 'Звіт для друку' : 'Printable PDF'}
                  </span>
                </button>
                <button 
                  onClick={() => {
                    exportAsCSV();
                    setShowExportConfirm(false);
                  }}
                  className="flex flex-col items-center justify-center p-5 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:border-emerald-500 hover:bg-emerald-500/5 dark:hover:bg-emerald-500/5 transition-all group gap-2 text-[var(--text)] bg-transparent cursor-pointer"
                >
                  <FileSpreadsheet className="w-8 h-8 text-emerald-500 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-bold uppercase tracking-wider">{lang === 'ua' ? 'Дані CSV' : 'CSV Spreadsheet'}</span>
                  <span className="text-[9px] text-zinc-500 font-bold tracking-tight uppercase leading-snug">
                    {lang === 'ua' ? 'Для Excel / Таблиць' : 'For Excel / Sheets'}
                  </span>
                </button>
                <button 
                  onClick={() => {
                    exportAsJSON();
                    setShowExportConfirm(false);
                  }}
                  className="flex flex-col items-center justify-center p-5 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:border-emerald-500 hover:bg-emerald-500/5 dark:hover:bg-emerald-500/5 transition-all group gap-2 text-[var(--text)] bg-transparent cursor-pointer"
                >
                  <FileJson className="w-8 h-8 text-amber-500 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-bold uppercase tracking-wider">{lang === 'ua' ? 'Дані JSON' : 'JSON Data'}</span>
                  <span className="text-[9px] text-zinc-500 font-bold tracking-tight uppercase leading-snug">
                    {lang === 'ua' ? 'Бекап / Розробка' : 'Raw Log / Backup'}
                  </span>
                </button>
              </div>
              <div className="pt-2">
                <button 
                  onClick={() => setShowExportConfirm(false)}
                  className="w-full bg-zinc-100 dark:bg-zinc-800 py-3.5 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-900 dark:text-zinc-100 cursor-pointer"
                >
                  {t.noCancel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reset Confirmation Modal */}
      <AnimatePresence>
        {itemToRemove !== null && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-8 w-full max-w-sm space-y-6 shadow-2xl text-center"
            >
              <div className="bg-red-500/10 dark:bg-red-900/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto border border-red-500/20 dark:border-red-900/30">
                <Trash2 className="w-8 h-8 text-red-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold">{t.remove}?</h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed font-bold uppercase tracking-widest">
                  {t.confirmRemoveItem}
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button 
                  onClick={() => {
                    if (editingMeal && itemToRemove !== null) {
                      const newFoods = editingMeal.foods.filter((_, i) => i !== itemToRemove);
                      setEditingMeal({ 
                        ...editingMeal, 
                        foods: newFoods,
                        meal_name: isMealNameManuallyEdited ? editingMeal.meal_name : generateMealName(newFoods)
                      });
                      setItemToRemove(null);
                    }
                  }}
                  className="w-full bg-red-600 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-red-700 transition-colors shadow-lg shadow-red-900/40 text-white"
                >
                  {t.remove}
                </button>
                <button 
                  onClick={() => setItemToRemove(null)}
                  className="w-full bg-zinc-100 dark:bg-zinc-800 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-900 dark:text-zinc-100"
                >
                  {t.noCancel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {isBugModalOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-8 w-full max-w-md space-y-6 shadow-2xl"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-500/10 p-2 rounded-xl">
                    <Bug className="w-6 h-6 text-emerald-500" />
                  </div>
                  <h2 className="text-xl font-bold uppercase tracking-widest">Submit a bug/idea</h2>
                </div>
                <button onClick={() => setIsBugModalOpen(false)} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed font-bold uppercase tracking-widest">
                  Found a bug or got an idea? Let us know! 💡👊
                </p>
                <textarea
                  value={bugDescription}
                  onChange={(e) => setBugDescription(e.target.value)}
                  placeholder={lang === 'ua' ? "Опишіть, що сталося..." : "Describe what happened..."}
                  className="w-full h-32 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-4 text-sm focus:border-emerald-500 outline-none transition-colors resize-none"
                />

                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest pl-1">Attach Screenshot (Optional)</p>
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer group">
                      <div className="flex items-center justify-center gap-2 py-4 border-2 border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl group-hover:border-emerald-500 transition-all bg-zinc-50 dark:bg-zinc-800/50">
                        {bugScreenshot ? (
                          <div className="relative w-full h-full p-2 flex items-center justify-center gap-3">
                            <img src={bugScreenshot} alt="Bug" loading="lazy" decoding="async" className="w-10 h-10 object-cover rounded-lg border border-emerald-500/50 shadow-lg shadow-emerald-500/20" />
                            <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">{lang === 'ua' ? "Зображення завантажено" : "Image Uploaded"}</span>
                          </div>
                        ) : (
                          <>
                            <Image className="w-5 h-5 text-zinc-400 group-hover:text-emerald-500" />
                            <span className="text-xs font-bold text-zinc-500 group-hover:text-emerald-500 uppercase tracking-widest">{lang === 'ua' ? "Натисніть для завантаження" : "Click to upload"}</span>
                          </>
                        )}
                      </div>
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                             const reader = new FileReader();
                             reader.onloadend = () => {
                               setBugScreenshot(reader.result as string);
                             };
                             reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                    {bugScreenshot && (
                      <button 
                        onClick={() => setBugScreenshot(null)}
                        className="p-4 bg-red-500/10 text-red-500 rounded-2xl border border-red-500/20 hover:bg-red-500/20 transition-all"
                        title={lang === 'ua' ? "Видалити фото" : "Remove image"}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  {bugScreenshot && (
                    <p className="text-[9px] text-zinc-400 font-bold uppercase tracking-widest text-center animate-pulse">
                      AI will verify the screenshot upon submission
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <button 
                  onClick={submitBugReport}
                  disabled={isVerifyingBugImage}
                  className="w-full bg-emerald-500 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/40 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isVerifyingBugImage ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.analyzing || 'Verifying...'}
                    </>
                  ) : (
                    'Submit Report'
                  )}
                </button>
                <button 
                  onClick={() => setIsBugModalOpen(false)}
                  className="w-full bg-zinc-100 dark:bg-zinc-800 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-900 dark:text-zinc-100"
                >
                  Nah
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showResetConfirm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-8 w-full max-w-sm space-y-6 shadow-2xl text-center"
            >
              <div className="bg-red-500/10 dark:bg-red-900/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto border border-red-500/20 dark:border-red-900/30">
                <Trash2 className="w-8 h-8 text-red-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold">{t.resetHistory}?</h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed font-bold uppercase tracking-widest">
                  {t.confirmReset}
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button 
                  onClick={async () => {
                    setHistory([]);
                    localStorage.removeItem('meal_history');
                    
                    if (user) {
                      try {
                        const historyCollection = collection(db, 'users', user.uid, 'history');
                        const historySnapshot = await getDocs(historyCollection);
                        const deletePromises = historySnapshot.docs.map(doc => deleteDocWithQueue(doc.ref));
                        await Promise.all(deletePromises);
                      } catch (error) {
                        handleFirestoreError(error, OperationType.DELETE, `users/${user.uid}/history`);
                      }
                    }
                    
                    setShowResetConfirm(false);
                    setView('home');
                    toast.success(t.historyResetSuccess, {
                      icon: <Trash2 className="w-5 h-5 text-red-500" />,
                      className: "rounded-2xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 font-bold",
                    });
                  }}
                  className="w-full bg-red-600 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-red-700 transition-colors shadow-lg shadow-red-900/40 text-white"
                >
                  {t.yesReset}
                </button>
                <button 
                  onClick={() => setShowResetConfirm(false)}
                  className="w-full bg-zinc-100 dark:bg-zinc-800 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-900 dark:text-zinc-100"
                >
                  {t.noCancel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showDeleteAccountConfirm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white dark:bg-zinc-900 border border-red-500/30 rounded-3xl p-8 w-full max-w-sm space-y-6 shadow-2xl text-center"
            >
              <div className="bg-red-500/10 dark:bg-red-900/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto border border-red-500/30 dark:border-red-900/40">
                <ShieldAlert className="w-8 h-8 text-red-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-50">
                  {lang === 'ua' ? 'Видалити акаунт?' : 'Delete Account?'}
                </h2>
                <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed font-bold uppercase tracking-wider">
                  {lang === 'ua' 
                    ? 'Ця дія є незворотною. Усі ваші персональні дані, історія харчування, логи води, досягнення та локальний кеш будуть назавжди видалені.'
                    : 'This action is irreversible. All your profile data, meal history, water logs, vitals, gamification progress, and local cache will be permanently wiped.'}
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button 
                  disabled={isDeletingAccount}
                  onClick={handleDeleteAccount}
                  className="w-full bg-red-600 py-4 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-red-700 transition-colors shadow-lg shadow-red-900/40 text-white flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {isDeletingAccount ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {lang === 'ua' ? 'ВИДАЛЕННЯ...' : 'DELETING...'}
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      {lang === 'ua' ? 'ТАК, ВИДАЛИТИ АКАУНТ' : 'YES, DELETE ACCOUNT'}
                    </>
                  )}
                </button>
                <button 
                  disabled={isDeletingAccount}
                  onClick={() => setShowDeleteAccountConfirm(false)}
                  className="w-full bg-zinc-100 dark:bg-zinc-800 py-4 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-900 dark:text-zinc-100 cursor-pointer disabled:opacity-50"
                >
                  {lang === 'ua' ? 'СКАСУВАТИ' : 'CANCEL'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Navigation */}
      <AnimatePresence>
        {showAdminPurgeConfirm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-8 w-full max-w-sm space-y-6 shadow-2xl text-center"
            >
              <div className="bg-red-500/10 dark:bg-red-900/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto border border-red-500/20 dark:border-red-900/30">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold">Purge Other Users?</h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed font-bold uppercase tracking-widest">
                  This will delete all user profiles and history from Firestore except Mr.Gray.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button 
                  disabled={isAdminPurging}
                  onClick={async () => {
                    setIsAdminPurging(true);
                    try {
                      const querySnapshot = await getDocs(collection(db, 'users'));
                      const deletePromises = [];
                      
                      // For each user, we should also delete their subcollections (except Mr.Gray)
                      for (const userDoc of querySnapshot.docs) {
                        const userId = userDoc.id;
                        const userData = userDoc.data();
                        if (isKimAdmin(userData.email) || userId === user?.uid) {
                          continue; // Preserve admin account
                        }
                        
                        // Delete subcollections
                        const subCols = ['history', 'water', 'grocery_list', 'scanned_products', 'meal_templates', 'days', 'notifications', 'gamification_logs', 'offline_sync_queue'];
                        for (const sub of subCols) {
                          try {
                            const subSnapshot = await getDocs(collection(db, 'users', userId, sub));
                            subSnapshot.docs.forEach(d => deletePromises.push(deleteDocWithQueue(d.ref)));
                          } catch (e) {}
                        }
                        
                        // Delete the user doc itself
                        deletePromises.push(deleteDocWithQueue(userDoc.ref));
                      }
                      
                      await Promise.all(deletePromises);
                      toast.success(lang === 'ua' ? 'Базу даних успішно очищено (крім Mr.Gray)! 👊' : 'Database purged successfully (except Mr.Gray)! 👊');
                      window.location.reload(); // Reload to refresh all states
                    } catch (error) {
                      console.error("Purge failed:", error);
                      toast.error(lang === 'ua' ? 'Помилка очищення. Перевірте консоль.' : 'Purge failed. Check console.');
                    } finally {
                      setIsAdminPurging(false);
                      setShowAdminPurgeConfirm(false);
                    }
                  }}
                  className="w-full bg-red-600 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-red-700 transition-colors shadow-lg shadow-red-900/40 text-white disabled:opacity-50"
                >
                  {isAdminPurging ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'YES, PURGE EVERYTHING'}
                </button>
                <button 
                  disabled={isAdminPurging}
                  onClick={() => setShowAdminPurgeConfirm(false)}
                  className="w-full bg-zinc-100 dark:bg-zinc-800 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-900 dark:text-zinc-100"
                >
                  {t.noCancel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAiSuggestionsSettings && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 w-full max-w-sm flex flex-col gap-6 shadow-2xl"
            >
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-black uppercase tracking-widest text-[var(--text)]">{lang === 'ua' ? 'НАЛАШТУВАННЯ ШІ' : 'AI SETTINGS'}</h2>
                <button 
                  onClick={() => setShowAiSuggestionsSettings(false)}
                  className="p-2 rounded-full bg-[var(--input-bg)] text-zinc-500 hover:text-[var(--text)] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Safe Choice Allergen Filter Toggle inside Modal */}
                <div className="flex items-center justify-between p-3.5 bg-[var(--input-bg)] rounded-2xl border border-[var(--card-border)]">
                  <div className="flex flex-col gap-0.5 pr-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-[var(--text)]">
                      {lang === 'ua' ? 'SAFE CHOICE ФІЛЬТР' : 'SAFE CHOICE FILTER'}
                    </span>
                    <span className="text-[9px] text-zinc-400 font-semibold leading-tight">
                      {lang === 'ua' 
                        ? 'Виключати алергени вашого профілю' 
                        : 'Exclude active profile allergens'}
                    </span>
                  </div>
                  <button 
                    type="button"
                    onClick={() => {
                      const newVal = !filterSafeChoiceAllergens;
                      setFilterSafeChoiceAllergens(newVal);
                      localStorage.setItem('filter_safe_choice_allergens', String(newVal));
                      syncSettings({ filterSafeChoiceAllergens: newVal });
                      sounds.playSave();
                    }}
                    className={cn(
                      "w-12 h-6 rounded-full transition-colors relative flex-shrink-0",
                      filterSafeChoiceAllergens ? "bg-[var(--brand-green)]" : "bg-zinc-300 dark:bg-zinc-700"
                    )}
                  >
                    <div className={cn(
                      "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
                      filterSafeChoiceAllergens ? "left-7" : "left-1"
                    )} />
                  </button>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xs font-black uppercase tracking-widest text-[var(--text)]">
                    {lang === 'ua' ? 'ВИНЯТКИ' : 'EXCLUSIONS'}
                  </h3>
                  <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                    {lang === 'ua' ? 'Вкажіть продукти для виключення з рекомендацій ШІ.' : 'Select foods or ingredients to exclude from AI meal suggestions.'}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {['Meat', 'Milk', 'White Wheat', 'Eggs', 'Soy', 'Nuts', 'Seafood'].map(variant => {
                      const excluded = excludedFoods.includes(variant);
                      const labelUa: Record<string, string> = {
                        'Meat': 'М\'ясо',
                        'Milk': 'Молоко',
                        'White Wheat': 'Біла пшениця',
                        'Eggs': 'Яйця',
                        'Soy': 'Соя',
                        'Nuts': 'Горіхи',
                        'Seafood': 'Морепродукти'
                      };
                      return (
                        <button
                          key={variant}
                          onClick={() => {
                            const newExclusions = excluded 
                              ? excludedFoods.filter(i => i !== variant)
                              : [...excludedFoods, variant];
                            setExcludedFoods(newExclusions);
                            localStorage.setItem('ai_excluded_foods', JSON.stringify(safeClone(newExclusions)));
                            syncSettings({ excludedFoods: newExclusions });
                            sounds.playPop();
                          }}
                          className={cn(
                            "px-4 py-2 rounded-xl text-[11px] font-bold uppercase tracking-widest transition-all",
                            excluded 
                              ? "bg-red-500/10 text-red-500 border border-red-500/20" 
                              : "bg-[var(--input-bg)] text-zinc-500 hover:text-[var(--text)] border border-transparent"
                          )}
                        >
                          {lang === 'ua' ? (labelUa[variant] || variant) : variant}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-[var(--card-border)]">
                  <h3 className="text-xs font-black uppercase tracking-widest text-[var(--text)]">
                    {lang === 'ua' ? 'УЛЮБЛЕНІ ПРОДУКТИ' : 'PREFERRED PRODUCTS'}
                  </h3>
                  <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                    {lang === 'ua' 
                      ? 'Вкажіть улюблені чи звичні інгредієнти, щоб ШІ віддавав їм перевагу при плануванні.' 
                      : 'List your preferred or usual products/ingredients for AI to prioritize in suggestions.'}
                  </p>
                  <div className="relative flex items-center bg-[var(--input-bg)] rounded-2xl border border-[var(--card-border)] px-4 py-3">
                    <input
                      type="text"
                      value={preferredIngredients}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPreferredIngredients(val);
                        localStorage.setItem('ai_preferred_ingredients', val);
                        syncSettings({ preferredIngredients: val });
                      }}
                      placeholder={lang === 'ua' ? 'напр. курка, рис, огірки, сир, вівсянка' : 'e.g., chicken, rice, eggs, cheese, oats'}
                      className="w-full bg-transparent border-none text-xs text-[var(--text)] focus:outline-none placeholder:text-zinc-400 font-medium"
                    />
                    {preferredIngredients && (
                      <button
                        onClick={() => {
                          setPreferredIngredients('');
                          localStorage.setItem('ai_preferred_ingredients', '');
                          syncSettings({ preferredIngredients: '' });
                        }}
                        className="p-1 rounded-full bg-zinc-200 dark:bg-zinc-800 text-zinc-500 hover:text-[var(--text)] transition-colors ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button 
                  onClick={() => {
                    setShowAiSuggestionsSettings(false);
                    handleFetchSuggestions(undefined, true); 
                  }}
                  className="w-full bg-emerald-500 py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/20 text-emerald-950"
                >
                  {lang === 'ua' ? 'ЗБЕРЕГТИ ТА ЗГЕНЕРУВАТИ' : 'APPLY & REGENERATE'}
                </button>
                <button 
                  onClick={() => {
                    setEnableAiSuggestions(false);
                    localStorage.setItem('enable_ai_suggestions', 'false');
                    syncSettings({ enableAiSuggestions: false });
                    setShowAiSuggestionsSettings(false);
                    toast(lang === 'ua' ? 'Рекомендації страв від ШІ закрито' : 'AI Meal Suggestions closed', { 
                      description: lang === 'ua' ? 'Функцію можна відновити в Налаштуваннях.' : 'This feature can be turned back on any time in Settings.',
                      icon: <Info className="w-5 h-5 text-blue-500" />,
                      className: "rounded-2xl border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--text)] font-bold"
                    });
                  }}
                  className="w-full bg-[var(--input-bg)] py-3 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors text-zinc-500"
                >
                  {lang === 'ua' ? 'ЗАКРИТИ СЕКЦІЮ' : 'CLOSE SECTION'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMatchOverlay && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl overflow-hidden"
          >
            <motion.div
              initial={{ scale: 0.5, rotate: -20, y: 100 }}
              animate={{ scale: 1, rotate: 0, y: 0 }}
              exit={{ scale: 1.5, opacity: 0 }}
              className="relative flex flex-col items-center text-center space-y-8"
            >
              {/* Floating Hearts Animation */}
              <div className="absolute -inset-20 pointer-events-none">
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ y: 0, x: 0, opacity: 1, scale: 0 }}
                    animate={{ 
                      y: -200 - Math.random() * 200, 
                      x: (Math.random() - 0.5) * 400,
                      opacity: 0,
                      scale: 1 + Math.random() * 2,
                      rotate: Math.random() * 360
                    }}
                    transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
                    className="absolute left-1/2 top-1/2"
                  >
                    <Heart className="w-12 h-12 text-emerald-500 fill-emerald-500 blur-[1px]" />
                  </motion.div>
                ))}
              </div>

              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="relative"
              >
                <div className="absolute inset-0 bg-emerald-500 blur-3xl opacity-50 animate-pulse" />
                <Heart className="w-32 h-32 text-emerald-500 fill-emerald-500 relative z-10 drop-shadow-[0_0_17px_rgba(16,185,129,0.5)]" />
              </motion.div>

              <div className="space-y-2 relative z-10">
                <h2 className="text-6xl font-black text-white italic tracking-tighter uppercase drop-shadow-lg">
                  {t.itsAMatch.split(' ')[0]} <span className="text-emerald-500">{t.itsAMatch.split(' ').slice(1).join(' ')}</span>
                </h2>
                <p className="text-xl font-bold text-zinc-400 uppercase tracking-widest">
                  {t.mutualLike}
                </p>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-6 bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/20 shadow-2xl"
              >
                <div className="w-16 h-16 rounded-full border-4 border-emerald-500 overflow-hidden bg-emerald-500/20 flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div className="flex gap-2">
                  <motion.div 
                    animate={{ x: [0, 10, 0] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <Heart className="w-6 h-6 text-emerald-500 fill-emerald-500" />
                  </motion.div>
                </div>
                <div className="w-16 h-16 rounded-full border-4 border-emerald-500 overflow-hidden bg-rose-500/20 flex items-center justify-center">
                  <Bot className="w-8 h-8 text-white" />
                </div>
              </motion.div>

              <button 
                onClick={() => setShowMatchOverlay(false)}
                className="relative z-10 px-8 py-4 bg-white text-zinc-900 rounded-2xl font-black uppercase tracking-widest hover:scale-105 transition-transform shadow-xl"
              >
                {t.awwAmazing}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

            <nav className="fixed bottom-0 left-0 right-0 glass border-x-0 border-b-0 border-t border-[var(--card-border)] px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] z-50">
        {isSelectionMode && view === "history" ? (
          <div className="max-w-md mx-auto flex items-center justify-between gap-2">
            <button 
              onClick={() => {
                sounds.playPop();
                setIsSelectionMode(false);
                setSelectedMealIds([]);
              }}
              className="flex-1 py-1 px-2 text-zinc-500 bg-zinc-500/10 hover:bg-zinc-500/20 rounded-full font-bold uppercase tracking-wider text-[10px] sm:text-xs transition-colors flex items-center justify-center gap-1.5 h-10"
            >
              <X className="w-4 h-4" />
              {lang.startsWith("ua") ? "Скасувати" : "CANCEL"}
            </button>
            <button
              onClick={() => {
                sounds.playPop();
                const allItemIds = groupedHistory.flatMap(([_, g]) => g.items.map(i => i.id));
                if (selectedMealIds.length === allItemIds.length) {
                  setSelectedMealIds([]);
                } else {
                  setSelectedMealIds(allItemIds);
                }
              }}
              className="flex-1 py-1 px-2 text-emerald-500 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-full font-bold uppercase tracking-wider text-[10px] sm:text-xs transition-colors flex items-center justify-center gap-1.5 h-10 border border-emerald-500/20"
            >
              <CheckCircle2 className="w-4 h-4" />
              {selectedMealIds.length === groupedHistory.flatMap(([_, g]) => g.items.map(i => i.id)).length 
                ? (lang.startsWith("ua") ? "Жодного" : "NONE")
                : (lang.startsWith("ua") ? "Всі" : "ALL")
              }
            </button>
            <button 
              disabled={selectedMealIds.length === 0}
              onClick={() => {
                sounds.playPop();
                deleteMultipleHistoryItems(selectedMealIds);
                setIsSelectionMode(false);
                setSelectedMealIds([]);
              }}
              className={cn(
                "flex-[1.5] py-1 px-2.5 rounded-full font-bold uppercase tracking-wider text-[10px] sm:text-xs transition-all flex items-center justify-center gap-1.5 h-10 text-white",
                selectedMealIds.length > 0
                  ? "bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/20"
                  : "bg-red-500/30 text-white/50 cursor-not-allowed"
              )}
            >
              <Trash2 className="w-4 h-4" />
              {lang.startsWith("ua") ? `Видал. (${selectedMealIds.length})` : `DEL (${selectedMealIds.length})`}
            </button>
          </div>
        ) : (
          <div className="max-w-md mx-auto flex items-center justify-between">
            <button 
              onClick={() => { sounds.playPop(); setView("home"); scrollToSectionTop(undefined, true); }}
              className={cn(
                "flex-1 flex flex-col items-center justify-center gap-1 transition-colors",
                view === "home" 
                  ? (skin === "cyberpunk" ? "text-[#FCEE0A] drop-shadow-[0_0_8px_rgba(252,238,10,0.8)] font-mono" : skin === "anime" ? "text-pink-400 drop-shadow-[0_0_10px_rgba(244,114,182,0.8)]" : "text-emerald-500") 
                  : (skin === "stalker" ? "text-[#4ade80]/50 hover:text-[#4ade80]" : skin === "anime" ? "text-pink-300/60 hover:text-pink-400" : skin === "cyberpunk" ? "text-[#00ffcc]/60 hover:text-[#FCEE0A] font-mono" : "text-zinc-500 hover:text-emerald-500 dark:hover:text-emerald-400")
              )}
            >
              {skin === "stalker" ? <Activity className="w-6 h-6" /> : skin === "anime" ? <Sparkles className="w-6 h-6" /> : <HomeIcon className="w-6 h-6" />}
              <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest">{skin === "stalker" ? (lang === "ua" ? "ВІТАЛИ" : "VITALS") : skin === "anime" ? (lang === "ua" ? "КВЕСТИ" : "QUESTS") : skin === "cyberpunk" ? (lang === "ua" ? "НЕЙРОЛІНК" : "NEURAL LINK") : t.home}</span>
            </button>
            
            <button 
              onClick={() => { sounds.playPop(); setView("add_meal_options"); scrollToSectionTop(undefined, true); }}
              className={cn(
                "flex-1 flex flex-col items-center justify-center gap-1 transition-colors group",
                view === "add_meal_options" || view === "analyze" || view === "chat" || view === "manual" 
                  ? (skin === "cyberpunk" ? "text-[#FCEE0A] font-mono" : skin === "anime" ? "text-pink-400" : "text-emerald-500")
                  : (skin === "stalker" ? "text-[#4ade80]/50 hover:text-[#4ade80]" : skin === "anime" ? "text-pink-300/60 hover:text-pink-400" : skin === "cyberpunk" ? "text-[#00ffcc]/60 hover:text-[#FCEE0A] font-mono" : "text-zinc-500 hover:text-emerald-500 dark:hover:text-emerald-400")
              )}
            >
              <div className={cn(
                "w-11 h-11 rounded-full flex items-center justify-center transition-all shadow-lg",
                (view === "add_meal_options" || view === "analyze" || view === "chat" || view === "manual" || isAnalyzing || isAnalyzingText) && "btn-active-border",
                view === "add_meal_options" || view === "analyze" || view === "chat" || view === "manual" 
                  ? (skin === "cyberpunk" ? "bg-[#FCEE0A] text-black shadow-[0_0_20px_rgba(252,238,10,0.8)] rounded-none scale-105" : skin === "anime" ? "bg-gradient-to-tr from-pink-500 to-purple-600 text-white shadow-[0_4px_20px_rgba(236,72,153,0.6)] border border-pink-300/50 scale-105" : "bg-emerald-500 text-white shadow-emerald-500/30 scale-105")
                  : (skin === "stalker" ? "bg-transparent border border-[#4ade80]/50 text-[#4ade80] hover:bg-[#4ade80]/10" : skin === "anime" ? "bg-gradient-to-tr from-pink-500 to-purple-600 text-white shadow-[0_4px_16px_rgba(236,72,153,0.4)] border border-pink-300/40 hover:scale-105 group-hover:scale-105" : skin === "cyberpunk" ? "bg-[#FCEE0A] text-black shadow-[0_0_15px_rgba(252,238,10,0.6)] rounded-none hover:bg-white group-hover:scale-105" : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-emerald-500/20 group-hover:bg-emerald-600 group-hover:scale-105")
              )}>
                {skin === "stalker" ? <FileText className="w-6 h-6" /> : <Plus className="w-6 h-6 stroke-[2.5]" />}
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest">{skin === "stalker" ? (lang === "ua" ? "ЗАПИС" : "LOG") : skin === "anime" ? (lang === "ua" ? "БЕНТО" : "BENTO") : skin === "cyberpunk" ? "UPLINK" : (lang === "ua" ? "Додати" : "Add")}</span>
            </button>

            <button 
              onClick={() => { sounds.playPop(); setView("history"); scrollToSectionTop(undefined, true); }}
              className={cn(
                "flex-1 flex flex-col items-center justify-center gap-1 transition-colors",
                view === "history" || view === "details" || view === "edit" 
                  ? (skin === "cyberpunk" ? "text-[#FCEE0A] drop-shadow-[0_0_8px_rgba(252,238,10,0.8)] font-mono" : skin === "anime" ? "text-pink-400 drop-shadow-[0_0_10px_rgba(244,114,182,0.8)]" : "text-emerald-500")
                  : (skin === "stalker" ? "text-[#4ade80]/50 hover:text-[#4ade80]" : skin === "anime" ? "text-pink-300/60 hover:text-pink-400" : skin === "cyberpunk" ? "text-[#00ffcc]/60 hover:text-[#FCEE0A] font-mono" : "text-zinc-500 hover:text-emerald-500 dark:hover:text-emerald-400")
              )}
            >
              {skin === "stalker" ? <Database className="w-6 h-6" /> : skin === "anime" ? <BookOpen className="w-6 h-6" /> : <HistoryIcon className="w-6 h-6" />}
              <span className="text-[10px] font-bold uppercase tracking-widest">{skin === "stalker" ? (lang === "ua" ? "СХОВИЩЕ" : "STASH") : skin === "anime" ? (lang === "ua" ? "ХРОНІКИ" : "CHRONICLES") : skin === "cyberpunk" ? (lang === "ua" ? "КІБЕРДЕК" : "CYBERDECK") : t.history}</span>
            </button>
          </div>
        )}
      </nav>

      {renderUpgradeModal()}
      {renderNoFoodModal()}
      {renderCheckoutModal()}
      <SubscriptionBillingModal
        isOpen={isBillingModalOpen}
        onClose={() => setIsBillingModalOpen(false)}
        currentTier={subscriptionTier}
        onUpgradeTier={handleUpgradeSubscription}
        onDowngradeToFree={() => {
          setSubscriptionTier('free');
          localStorage.setItem('subscription_tier', 'free');
          syncSettings({ subscriptionTier: 'free', subscriptionTrialActive: false });
        }}
        lang={lang}
        theme={theme}
        skin={skin}
        userEmail={auth.currentUser?.email || undefined}
      />
      
      <NutriAgentConfigModal
        isOpen={isNutriAgentConfigOpen}
        onClose={() => setIsNutriAgentConfigOpen(false)}
        onLaunch={(config) => {
          setNutriAgentConfig(config);
          setIsNutriAgentConfigOpen(false);
          setIsNutriAgentActive(true);
        }}
        lang={lang}
        theme={theme}
        userTier={subscriptionTier || 'premium'}
        groceryItems={groceryItems}
        initialConfig={nutriAgentConfig}
      />

      <NutriAgentProgress
        isActive={isNutriAgentActive}
        config={nutriAgentConfig}
        lang={lang}
        vibe={vibe}
        theme={theme}
        skin={skin}
        onCancel={() => setIsNutriAgentActive(false)}
        onComplete={() => {
          setIsNutriAgentActive(false);
          setIsNutriAgentApprovalOpen(true);
          toast.success(lang === 'ua' ? "Кошик Nutri-Agent готовий до перегляду!" : "Nutri-Agent cart ready for review!", { icon: '🛒' });
        }}
      />

      <NutriAgentApprovalModal
        isOpen={isNutriAgentApprovalOpen}
        onClose={() => setIsNutriAgentApprovalOpen(false)}
        config={nutriAgentConfig}
        lang={lang}
        theme={theme}
        onOrderConfirmed={(order) => {
          localDb.getItem("nutri_agent_history").then(val => {
            const list = val ? JSON.parse(val) : [];
            const newHistory = [order, ...list];
            localDb.setItem("nutri_agent_history", JSON.stringify(newHistory));
          });
        }}
      />
      {renderReviewModal()}
      {renderFoodExplanationModal()}

      {/* Google Fit / Health Sync Modal */}
      <AnimatePresence>
        {isGoogleFitModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={() => setIsGoogleFitModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-md bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-5 sm:p-6 shadow-2xl relative space-y-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between pb-2 border-b border-[var(--card-border)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500 shrink-0">
                    {isAppleHealthConnected && !isGoogleFitConnected ? (
                      <Apple className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <Smartphone className="w-5 h-5 text-emerald-500" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-[var(--text)] uppercase tracking-wider">
                      {isAppleHealthConnected && !isGoogleFitConnected
                        ? (lang === 'ua' ? 'Apple Health' : 'Apple Health')
                        : (lang === 'ua' ? 'Google Fit & Health' : 'Google Fit & Health')}
                    </h3>
                    <p className="text-xs text-zinc-500">
                      {lang === 'ua' ? "Синхронізація активності" : "Health & Activity Sync"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsGoogleFitModalOpen(false)}
                  className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors text-zinc-400 hover:text-[var(--text)] cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {subscriptionTier === 'free' ? (
                <div className="p-6 rounded-2xl border border-amber-500/20 bg-amber-500/5 text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
                    <Lock className="w-6 h-6 text-amber-500 animate-bounce" />
                  </div>
                  <h4 className="font-extrabold text-sm uppercase tracking-wider text-[var(--text)]">
                    {lang === 'ua' ? "Синхронізація з HealthKit / Fit (PRO)" : "Health Sync Integration (PRO Only)"}
                  </h4>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 max-w-[280px] mx-auto leading-relaxed">
                    {lang === 'ua'
                      ? "Оновіть до PRO, щоб синхронізувати кроки, спалені калорії та дані активності з Apple Health або Google Fit!"
                      : "Upgrade to PRO to synchronize your steps, active burn, and health data with Apple Health or Google Fit!"}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsGoogleFitModalOpen(false);
                      setIsSubscriptionExpanded(true);
                      setView('goals');
                      setTimeout(() => {
                        scrollToSectionTop('subscription-card-settings', true);
                      }, 150);
                    }}
                    className="mt-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-black text-xs font-black rounded-xl transition-all cursor-pointer uppercase tracking-wider shadow-lg shadow-amber-500/20 active:scale-95"
                  >
                    {lang === 'ua' ? "Розблокувати PRO" : "Unlock PRO"}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <GoogleFitWidget
                    isConnected={isGoogleFitConnected}
                    isAppleConnected={isAppleHealthConnected}
                    appleCalories={appleHealthTodayCalories}
                    googleFitData={googleFitData}
                    googleFitLoading={googleFitLoading}
                    onSync={fetchGoogleFitData}
                    lang={lang}
                    illumination={illumination}
                    defaultOpen={true}
                    onConnectClick={() => {
                      setIsGoogleFitModalOpen(false);
                      setView('goals');
                      setIsPersonalizationExpanded(true);
                    }}
                  />
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Context Menu for Long Press on Meal */}
      <AnimatePresence>
        {mealContextMenu && (
          <>
            <div 
              className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs" 
              onClick={() => setMealContextMenu(null)} 
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              style={{
                position: "fixed",
                left: Math.min(Math.max(16, mealContextMenu.x - 100), window.innerWidth - 220),
                top: Math.min(mealContextMenu.y + 10, window.innerHeight - 150),
              }}
              className="z-50 w-52 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-2xl overflow-hidden py-1"
            >
              <button
                onClick={() => {
                  setIsSelectionMode(true);
                  setSelectedMealIds(prev => prev.includes(mealContextMenu.id) ? prev.filter(id => id !== mealContextMenu.id) : [...prev, mealContextMenu.id]);
                  setMealContextMenu(null);
                  setTimeout(() => setIsLongPressingMeal(false), 100);
                }}
                className="w-full px-4 py-3.5 flex items-center gap-3 text-[var(--text)] hover:bg-emerald-500/10 transition-colors text-left border-b border-[var(--card-border)]/50"
              >
                <Check className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-bold uppercase tracking-widest">{lang === "ua" ? "Вибрати" : "Select"}</span>
              </button>
              <button
                onClick={(e) => {
                  deleteHistoryItem(mealContextMenu.id, e);
                  setMealContextMenu(null);
                  setTimeout(() => setIsLongPressingMeal(false), 100);
                }}
                className="w-full px-4 py-3.5 flex items-center gap-3 text-red-500 hover:bg-red-500/10 transition-colors text-left"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
                <span className="text-xs font-bold uppercase tracking-widest">{t.deleteMeal || "Delete"}</span>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Safe Choice Detailed Breakdown Modal Here */}

      {/* BIO-BOX UNBOXING MODAL */}
      <BioBoxUnboxingModal
        isOpen={isBioBoxModalOpen}
        onClose={() => {
          setIsBioBoxModalOpen(false);
          setSelectedBioBoxForUnbox(null);
          refreshBioBoxes();
        }}
        box={selectedBioBoxForUnbox || (userBioBoxes.length > 0 ? userBioBoxes[0] : null)}
        boxes={userBioBoxes}
        userId={user?.uid || auth.currentUser?.uid}
        lang={lang}
        vibe={vibe}
        tone={(vibe as string) === 'bro' ? 'bro' : 'system'}
        theme={theme}
        skin={skin}
        onBoxOpened={(_payout) => {
          refreshBioBoxes();
        }}
      />
    </div>
  );
}